import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import "./index.css";
import { createPagedSchedulePdf, createSinglePageSchedulePdf } from "./schedule-pdf";
import PlanBoardPage from "./PlanBoardPage";
import ProjectsVideowall from "./ProjectsVideowall";
import { quoteKpis } from "./quote-kpis";
import ProcurementDashboard from "./ProcurementDashboard";
import PortalPreventives from "./PortalPreventives";
import ResidentAssetsPage from "./ResidentAssetsPage";
import ResidentDashboard from "./ResidentDashboard";
import { residentDashboardData } from "./resident-dashboard-data";
import { downloadPreventiveReportPdf } from "./preventive-report-pdf";
import { applyTheme, readTheme } from "./theme-preference";
import { relativeScheduleDays, relativeTaskPosition, relativeTaskLabel, relativeTaskRange } from "./preliminary-schedule";
import { portalTicketDayKey, portalTodayKey } from "./portal-ticket-dates";
import { portalOsVolumeForDay } from "./portal-os-volume";

const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";

const navSections = [
  { label: null, items: [["grid", "Visão geral"]] },
  {
    label: "OPERAÇÃO",
    items: [
      ["headset", "Chamados"],
      ["clipboard", "Ordem de serviço"],
      ["wrench", "Visitas"],
      ["calendar", "Preventivas"],
      ["box", "Ativos do cliente"],
    ],
  },
  {
    label: "PROJETOS",
    items: [
      ["folder", "Projetos"],
      ["file", "Diário de obras"],
      ["check", "Entrega de Obra"],
    ],
  },
  {
    label: "AGENDA",
    items: [
      ["calendar", "Planejamento"],
      ["users", "Agenda dos técnicos"],
    ],
  },
  {
    label: "GESTÃO",
    items: [
      ["building", "Clientes"],
      ["file", "Orçamentos"],
      ["users", "Colaboradores"],
      ["grid", "Kanban"],
      ["grid", "Plan Board"],
      ["box", "Custos Projetos"],
      ["file", "Relatórios"],
      ["chart", "Indicadores"],
      ["trend", "Performance APS"],
      ["clock", "Ponto"],
    ],
  },
  {
    label: "FINANCEIRO",
    items: [
      ["wallet", "Resultado"],
      ["chart", "Configuração"],
    ],
  },
];

function Icon({ name, size = 18 }) {
  const paths = {
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" /></>,
    moon: <path d="M20.5 14.1A8.7 8.7 0 0 1 9.9 3.5 8.8 8.8 0 1 0 20.5 14.1Z" />,
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    settings: <><path d="M10.4 2.8h3.2l.6 2.1c.5.2 1 .4 1.5.7l2-.9 2.3 2.3-.9 2c.3.5.5 1 .7 1.5l2.1.6v3.2l-2.1.6c-.2.5-.4 1-.7 1.5l.9 2-2.3 2.3-2-.9c-.5.3-1 .5-1.5.7l-.6 2.1h-3.2l-.6-2.1c-.5-.2-1-.4-1.5-.7l-2 .9-2.3-2.3.9-2c-.3-.5-.5-1-.7-1.5l-2.1-.6v-3.2l2.1-.6c.2-.5.4-1 .7-1.5l-.9-2 2.3-2.3 2 .9c.5-.3 1-.5 1.5-.7z" /><circle cx="12" cy="12" r="3" /></>,
    folder: <path d="M3 6.5h7l2 2h9v9.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
    clipboard: (
      <>
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4V2h6v2M8 10h8M8 14h8" />
      </>
    ),
    headset: (
      <>
        <path d="M4 14v-3a8 8 0 0 1 16 0v3M4 14h3v5H5a1 1 0 0 1-1-1zM20 14h-3v5h2a1 1 0 0 0 1-1zM17 19c0 2-2 2-4 2" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20c0-4 2-6 6-6s6 2 6 6M16 5a3 3 0 0 1 0 6M17 14c3 0 4 2 4 5" />
      </>
    ),
    box: (
      <>
        <path d="m4 7 8-4 8 4-8 4zM4 7v10l8 4 8-4V7M12 11v10" />
      </>
    ),
    file: (
      <>
        <path d="M6 2h8l4 4v16H6zM14 2v5h5M9 12h6M9 16h6" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12M7 10l5 5 5-5M4 20h16" />
      </>
    ),
    "file-download": (
      <>
        <path d="M6 2h8l4 4v16H6zM14 2v5h5" />
        <path d="M12 10v7M9 14l3 3 3-3" />
      </>
    ),
    paperclip: (
      <path d="m8 12 6.6-6.6a4 4 0 0 1 5.7 5.7l-8.5 8.5a6 6 0 0 1-8.5-8.5l8-8M7 15l8-8a2 2 0 0 1 2.8 2.8l-8 8a2 2 0 0 1-2.8-2.8Z" />
    ),
    chart: (
      <>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="2.5" />
      </>
    ),
    more: (
      <>
        <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    close: <path d="M5 5l14 14M19 5 5 19" />,
    maximize: <path d="M9 4H4v5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
    minimize: <path d="M9 9H4V4M15 9h5V4M20 15h-5v5M9 15H4v5" />,
    arrow: <path d="m9 18 6-6-6-6" />,
    "chevron-right": <path d="m9 18 6-6-6-6" />,
    trash: (
      <>
        <path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" />
      </>
    ),
    edit: (
      <>
        <path d="M4 20h4L19 9l-4-4L4 16z" />
        <path d="m13 7 4 4" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    pin: (
      <>
        <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    trend: <path d="m3 17 6-6 4 4 8-9M16 6h5v5" />,
    chevron: <path d="m9 10 3 3 3-3" />,
    wallet: (
      <>
        <path d="M3 6h16a2 2 0 0 1 2 2v11H5a2 2 0 0 1-2-2zM3 7V5a2 2 0 0 1 2-2h12v3M16 11h5v4h-5a2 2 0 0 1 0-4z" />
      </>
    ),
    wrench: (
      <path d="M14 7a5 5 0 0 0-7-4l3 3-4 4-3-3a5 5 0 0 0 6 7l7 7 5-5-7-7z" />
    ),
    scan: (
      <>
        <path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4" />
        <path d="M8 9h8v6H8zM10 11h4v2h-4z" />
      </>
    ),
    camera: (
      <>
        <path d="M4 7h4l2-3h4l2 3h4v12H4z" />
        <circle cx="12" cy="13" r="3" />
      </>
    ),
    "camera-plus": (
      <>
        <path d="M3.5 8.5a2 2 0 0 1 2-2H8l1.5-2h5l1.5 2h2.5a2 2 0 0 1 2 2v3" />
        <path d="M3.5 8.5v9a2 2 0 0 0 2 2H14" />
        <circle cx="11.5" cy="12.5" r="3" />
        <path d="M19 15v6m-3-3h6" />
      </>
    ),
    building: (
      <>
        <path d="M4 21V3h11v18M15 9h5v12M8 7h3M8 11h3M8 15h3M2 21h20" />
      </>
    ),
    filter: (
      <>
        <path d="M4 5h16M7 12h10M10 19h4" />
      </>
    ),
  };
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

function AiReviewButton({ token, value, onApply, context, disabled = false }) {
  const [reviewing, setReviewing] = useState(false);
  const [message, setMessage] = useState("");
  const review = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const text = String(value || "").trim();
    if (text.length < 3) {
      setMessage("Escreva o texto antes de revisar.");
      return;
    }
    setReviewing(true);
    setMessage("");
    try {
      const response = await fetch(`${apiBase}/ai/review-text`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text, context }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "ai_review_failed");
      onApply(result.text);
      setMessage("Texto revisado. Confira antes de salvar.");
    } catch (error) {
      setMessage(
        error.message === "ai_review_not_configured"
          ? "Revisão IA ainda não configurada."
          : "Não foi possível revisar agora.",
      );
    } finally {
      setReviewing(false);
    }
  };
  return (
    <div className="ai-review-actions">
      {message && <small role="status">{message}</small>}
      <button
        type="button"
        className="ai-review-button"
        onClick={review}
        disabled={
          disabled || reviewing || String(value || "").trim().length < 3
        }
      >
        <span aria-hidden="true">✦</span>
        {reviewing ? "Revisando…" : "Revisão IA"}
      </button>
    </div>
  );
}

function ThemeToggle({ theme, onToggle, className = "" }) {
  const next = theme === "dark" ? "claro" : "escuro";
  return (
    <button
      type="button"
      className={`theme-toggle ${className}`.trim()}
      onClick={onToggle}
      title={`Ativar modo ${next}`}
      aria-label={`Ativar modo ${next}`}
      aria-pressed={theme === "light"}
    >
      <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
      <span>{theme === "dark" ? "Modo claro" : "Modo escuro"}</span>
    </button>
  );
}

function App() {
  const equipmentPathTag = window.location.pathname.match(/^\/equipment\/([^/]+)\/?$/)?.[1];
  const scannedEquipmentTag = new URLSearchParams(window.location.search).get("equipmentTag") || (equipmentPathTag ? decodeURIComponent(equipmentPathTag) : null);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [theme, setTheme] = useState(readTheme);
  const toggleTheme = () => setTheme((current) => current === "dark" ? "light" : "dark");
  useEffect(() => applyTheme(theme), [theme]);
  const [active, setActive] = useState("Visão geral");
  useEffect(() => {
    if (scannedEquipmentTag && session?.user && ["residente", "técnico residente"].includes(String(session.user.accessProfileName || "").toLocaleLowerCase("pt-BR"))) {
      setActive("Ativos do cliente");
    }
  }, [scannedEquipmentTag, session]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [range, setRange] = useState("Este mês");
  const [dashboardCustomStart, setDashboardCustomStart] = useState(() => new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(new Date()));
  const [dashboardCustomEnd, setDashboardCustomEnd] = useState(() => new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(new Date()));
  const [dashboardClientId, setDashboardClientId] = useState("");
  const [dashboardUnitId, setDashboardUnitId] = useState("");
  const operationalRequestId = useRef(0);
  const [query, setQuery] = useState("");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef(null);
  useEffect(() => {
    const dismiss = (event) => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !notificationsRef.current?.contains(event.target))) setNotificationsOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => { document.removeEventListener("pointerdown", dismiss); document.removeEventListener("keydown", dismiss); };
  }, []);
  const [projectRecords, setProjectRecords] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [residentActivities, setResidentActivities] = useState([]);
  const [residentActivitiesLoading, setResidentActivitiesLoading] = useState(false);
  const [residentActivitiesError, setResidentActivitiesError] = useState("");
  const [operationalSetup, setOperationalSetup] = useState({
    clients: [],
    units: [],
    projects: [],
    users: [],
  });
  const [dashboard, setDashboard] = useState({
    projects: 0,
    serviceOrders: 0,
    serviceOrdersWaiting: 0,
    tickets: 0,
    ticketsWaiting: 0,
    workedDays: 0,
    pendingReports: 0,
    overdueMaintenance: 0,
  });
  const [dashboardCharts, setDashboardCharts] = useState({
    clientPerformance: [],
    orderTrend: [],
    technicianRanking: [],
    clientDemand: [],
    deadline: { total: 0, completed: 0, rate: 0 },
  });
  const [modal, setModal] = useState(null);
  const [dashboardProject, setDashboardProject] = useState(null);
  const [dashboardProjectType, setDashboardProjectType] = useState("projects");
  const dashboardRangeKey =
    { Hoje: "today", "Esta semana": "week", "Este mês": "month", "Mês anterior": "previous_month", "Próximo mês": "next_month", "Este ano": "year", "Período selecionado": "custom" }[range] ||
    "month";
  const periodStart = (() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    if (dashboardRangeKey === "week") date.setDate(date.getDate() - 6);
    if (dashboardRangeKey === "month") date.setDate(1);
    if (dashboardRangeKey === "previous_month") { date.setDate(1); date.setMonth(date.getMonth() - 1); }
    if (dashboardRangeKey === "next_month") { date.setDate(1); date.setMonth(date.getMonth() + 1); }
    if (dashboardRangeKey === "year") date.setMonth(0, 1);
    if (dashboardRangeKey === "custom") return new Date(`${dashboardCustomStart}T00:00:00`);
    return date;
  })();
  const periodEnd = (() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    if (dashboardRangeKey === "next_month") { date.setDate(1); date.setMonth(date.getMonth() + 2); date.setDate(0); }
    if (dashboardRangeKey === "previous_month") { date.setDate(0); }
    if (dashboardRangeKey === "year") date.setMonth(11, 31);
    if (dashboardRangeKey === "custom") return new Date(`${dashboardCustomEnd}T23:59:59`);
    return date;
  })();
  const occursInDashboardRange = (value) => {
    if (!value) return false;
    const date = new Date(value);
    return (
      !Number.isNaN(date.getTime()) && date >= periodStart && date <= periodEnd
    );
  };
  const filteredProjects = useMemo(
    () =>
      projectRecords.filter((p) =>
        `${p.projectNumber || ""} ${p.name} ${p.client}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [query, projectRecords],
  );
  const dashboardProjects = filteredProjects.filter((project) => {
    const start = project.startDate
      ? new Date(`${project.startDate}T00:00:00`)
      : null;
    const end = project.endDate
      ? new Date(`${project.endDate}T23:59:59`)
      : null;
    return (!start || start <= periodEnd) && (!end || end >= periodStart) &&
      (!dashboardClientId || Number(project.clientId) === Number(dashboardClientId)) &&
      (!dashboardUnitId || Number(project.unitId) === Number(dashboardUnitId));
  });
  const dashboardRealProjects = dashboardProjects.filter(
    (project) => !project.preliminary,
  );
  const dashboardStudies = dashboardProjects.filter(
    (project) => project.preliminary,
  );
  const visibleDashboardProjects =
    dashboardProjectType === "studies"
      ? dashboardStudies
      : dashboardRealProjects;
  const dashboardOrders = orders.filter((order) =>
    occursInDashboardRange(order.createdAt) &&
    (!dashboardClientId || Number(order.clientId) === Number(dashboardClientId)) &&
    (!dashboardUnitId || Number(order.unitId) === Number(dashboardUnitId)),
  );
  const recentActivities = useMemo(
    () =>
      [
        ...orders.filter((order) => (!dashboardClientId || Number(order.clientId) === Number(dashboardClientId)) && (!dashboardUnitId || Number(order.unitId) === Number(dashboardUnitId))).map((order) => ({
          page: "Ordem de serviço",
          icon: "clipboard",
          tone: ["finished", "closed"].includes(order.rawStatus)
            ? "green"
            : "purple",
          title: `${order.code} · ${order.title}`,
          text: `${order.client} • ${order.unit || "Sem unidade"}`,
          occurredAt: order.createdAt,
        })),
        ...tickets.filter((ticket) => (!dashboardClientId || Number(ticket.clientId) === Number(dashboardClientId)) && (!dashboardUnitId || Number(ticket.unitId) === Number(dashboardUnitId))).map((ticket) => ({
          page: "Chamados",
          icon: "headset",
          tone: "amber",
          title: `${ticket.code} · ${ticket.title}`,
          text: `${ticket.client} • ${ticket.unit || "Sem unidade"}`,
          occurredAt: ticket.createdAt,
        })),
      ]
        .filter((item) => item.occurredAt)
        .sort((a, b) => new Date(b.occurredAt) - new Date(a.occurredAt))
        .slice(0, 6),
    [orders, tickets, dashboardClientId, dashboardUnitId],
  );

  useEffect(() => {
    const token = localStorage.getItem("aboutplan_token");
    if (!token) {
      setAuthLoading(false);
      return;
    }
    fetch(`${apiBase}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => setSession({ token, user: data.user }))
      .catch(() => localStorage.removeItem("aboutplan_token"))
      .finally(() => setAuthLoading(false));
  }, []);

  const loadOperational = async (activeSession, activeRange = range) => {
    if (!activeSession || activeSession.user.role === "customer" || activeSession.user.internalCategory === "procurement") return;
    const requestId = ++operationalRequestId.current;
    setProjectRecords([]);
    setOrders([]);
    setTickets([]);
    setOperationalSetup({ clients: [], units: [], projects: [], users: [] });
    setDashboard({
      projects: 0,
      serviceOrders: 0,
      serviceOrdersWaiting: 0,
      tickets: 0,
      ticketsWaiting: 0,
      workedDays: 0,
      pendingReports: 0,
      overdueMaintenance: 0,
    });
    setDashboardCharts({
      clientPerformance: [],
      orderTrend: [],
      technicianRanking: [],
      clientDemand: [],
      deadline: { total: 0, completed: 0, rate: 0 },
    });
    const headers = { Authorization: `Bearer ${activeSession.token}` };
    const dashboardFilters = new URLSearchParams({ range: { Hoje: "today", "Esta semana": "week", "Este mês": "month", "Mês anterior": "previous_month", "Próximo mês": "next_month", "Este ano": "year", "Período selecionado": "custom" }[activeRange] || "month" });
    if (activeRange === "Período selecionado") { dashboardFilters.set("startDate", dashboardCustomStart); dashboardFilters.set("endDate", dashboardCustomEnd); }
    if (dashboardClientId) dashboardFilters.set("clientId", dashboardClientId);
    if (dashboardUnitId) dashboardFilters.set("unitId", dashboardUnitId);
    const resident = ["residente", "técnico residente"].includes(String(activeSession.user.accessProfileName || "").toLocaleLowerCase("pt-BR"));
    const emptyResponse = (value) => ({ ok: true, json: async () => value });
    try {
      const [
        projectResponse,
        orderResponse,
        ticketResponse,
        setupResponse,
        dashboardResponse,
        chartsResponse,
      ] = await Promise.all([
        resident ? emptyResponse([]) : fetch(`${apiBase}/projects`, { headers }),
        fetch(`${apiBase}/service-orders`, { headers }),
        fetch(`${apiBase}/tickets`, { headers }),
        fetch(`${apiBase}/setup/operational`, { headers }),
        fetch(
          `${apiBase}/dashboard/summary?${dashboardFilters}`,
          { headers, cache: "no-store" },
        ),
        fetch(
          `${apiBase}/dashboard/charts?${dashboardFilters}`,
          { headers, cache: "no-store" },
        ),
      ]);
      if (
        ![
          projectResponse,
          orderResponse,
          ticketResponse,
          setupResponse,
          dashboardResponse,
          chartsResponse,
        ].every((response) => response.ok)
      )
        return;
      const [
        projectData,
        orderData,
        ticketData,
        setupData,
        dashboardData,
        chartsData,
      ] = await Promise.all([
        projectResponse.json(),
        orderResponse.json(),
        ticketResponse.json(),
        setupResponse.json(),
        dashboardResponse.json(),
        chartsResponse.json(),
      ]);
      if (requestId !== operationalRequestId.current) return;
      setOperationalSetup(setupData);
      setDashboard(dashboardData);
      setDashboardCharts(chartsData);
      setProjectRecords(
        projectData.map((item) => {
          const planned = Number(item.scheduleDays) || 0;
          const commercial = Number(item.commercialEstimatedDays) || 0;
          const preliminary = item.projectMode === "implementation_study" || Boolean(item.preliminarySchedule && !item.projectMode);
          const commercialExceeded = !preliminary && commercial > 0 && planned > commercial;
          const isCompleted = ["completed", "closed"].includes(item.status);
          const endDate = item.scheduleEndDate || item.endDate || null;
          const isOverdue =
            !isCompleted &&
            Boolean(endDate && endDate < todayIso()) &&
            Number(item.scheduleProgress || 0) < 100;
          return {
            id: item.id,
            clientId: item.clientId,
            unitId: item.unitId,
            projectNumber: item.projectNumber || "",
            projectMode: item.projectMode || (preliminary ? "implementation_study" : "project"),
            name: item.name,
            client: item.clientName || "Sem cliente",
            unit: item.unitName,
            stage: projectStatusLabel(item.status),
            rawStatus: item.status,
            completedAt: item.completedAt || null,
            progress: item.scheduleProgress || 0,
            planned,
            commercial,
            preliminary,
            commercialExceeded,
            commercialDifference: Math.max(0, planned - commercial),
            isOverdue,
            worked: item.workedDays || 0,
            responsibleUserId: item.responsibleUserId || null,
            responsibleName: item.responsibleName || "Não definido",
            canEdit: Boolean(item.canEdit),
            startDate: item.scheduleStartDate || item.startDate || null,
            endDate,
            end: endDate ? formatDate(endDate) : "A definir",
            health: isCompleted
              ? "Projeto concluído"
              : preliminary
                ? "Estudo de Implantação"
              : isOverdue
                ? "Projeto atrasado"
                : commercialExceeded
                  ? "Fora do planejado comercialmente"
                  : commercial > 0
                    ? "No prazo comercial"
                    : "Orçamento não definido",
            tone: isCompleted
              ? "green"
              : isOverdue || commercialExceeded
                ? "red"
                : commercial > 0
                  ? "green"
                  : "amber",
            initials: initials(item.clientName || "SC"),
          };
        }),
      );
      setOrders(
        orderData.map((item) => ({
          id: item.id,
          clientId: item.clientId,
          unitId: item.unitId,
          code: `OS-${String(item.id).padStart(4, "0")}`,
          title: item.title,
          description: item.description || "",
          client: item.clientName || "Sem cliente",
          unit: item.unitName,
          owner:
            item.team?.map((person) => person.name).join(", ") ||
            item.assignedTo ||
            "Não atribuído",
          date: item.scheduledAt || "Não programada",
          scheduledAt: item.scheduledAt || "",
          createdAt: item.createdAt || "",
          priority: priorityLabel(item.priority),
          status: orderStatusLabel(item.status),
          rawStatus: item.status,
          createdAt: item.createdAt || "",
          finishedAt: item.finishedAt || "",
          signature: signatureLabel(item.signaturePolicy),
          reportStatus: item.reportStatus,
          ticketId: item.ticketId || null,
          ticketOpenedByName: item.ticketOpenedByName || "",
          ticketCreatedAt: item.ticketCreatedAt || "",
          serviceOrderOpenedByName: item.openedByName || "",
        })),
      );
      setTickets(
        ticketData.map((item) => ({
          id: item.id,
          clientId: item.clientId,
          unitId: item.unitId,
          code: `CH-${String(item.id).padStart(4, "0")}`,
          title: item.title,
          description: item.description,
          photos: item.photos || [],
          client: item.clientName || "Sem cliente",
          unit: item.unitName,
          owner: item.residentTechnicianNames || "Central técnica",
          date: item.resolutionDueAt
            ? "SLA " + formatDate(item.resolutionDueAt)
            : "SLA a definir",
          priority: priorityLabel(item.priority),
          rawPriority: item.priority,
          status: item.serviceOrderId
            ? "OS aberta"
            : ticketStatusLabel(item.status),
          rawStatus: item.status,
          channel: sourceLabel(item.source),
          serviceOrderId: item.serviceOrderId,
          createdAt: item.createdAt,
          openedByName: item.openedByName || "",
          openedByType: item.openedByType || "",
        })),
      );
    } catch {}
  };

  useEffect(() => {
    if (session) loadOperational(session, range);
  }, [session, range, dashboardCustomStart, dashboardCustomEnd, dashboardClientId, dashboardUnitId]);
  useEffect(() => {
    const resident = ["residente", "técnico residente"].includes(String(session?.user?.accessProfileName || "").toLocaleLowerCase("pt-BR"));
    if (!resident || active !== "Visão geral") return undefined;
    const controller = new AbortController();
    setResidentActivitiesLoading(true);
    setResidentActivitiesError("");
    fetch(`${apiBase}/maintenance/activities`, { headers: { Authorization: `Bearer ${session.token}` }, signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("Não foi possível carregar as preventivas.")))
      .then(setResidentActivities)
      .catch((cause) => { if (cause.name !== "AbortError") setResidentActivitiesError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setResidentActivitiesLoading(false); });
    return () => controller.abort();
  }, [session, active]);

  const login = async (credentials) => {
    const response = await fetch(`${apiBase}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });
    if (!response.ok) throw new Error("E-mail ou senha inválidos.");
    const data = await response.json();
    localStorage.setItem("aboutplan_token", data.token);
    setSession(data);
  };
  const logout = async () => {
    if (session?.token)
      fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.token}` },
      }).catch(() => {});
    localStorage.removeItem("aboutplan_token");
    setSession(null);
  };

  if (authLoading)
    return (
      <div className="auth-loading">
        <img
          className="brand-icon"
          src="/aboutplan-icon-transparent.png"
          alt="AboutPlan"
        />
        <span>Carregando AboutPlan…</span>
      </div>
    );
  if (!session) return <><LoginPage onLogin={login} /><ThemeToggle theme={theme} onToggle={toggleTheme} className="theme-toggle-login" /></>;
  const currentUser = session.user;
  const currentUserFirstName =
    String(currentUser.name || "")
      .trim()
      .split(/\s+/)[0] || "usuário";
  const overdueProjectCount = dashboardProjects.filter(
    (project) => project.isOverdue,
  ).length;
  const permissions = {
    enterProjectCosts:
      currentUser.role === "administrator" ||
      currentUser.permissions.includes("enter_project_costs") ||
      currentUser.permissions.includes("finance.entries"),
    viewProjectResult:
      currentUser.role === "administrator" ||
      currentUser.permissions.includes("view_project_result") ||
      currentUser.permissions.includes("finance.results"),
    viewServiceOrders:
      currentUser.role === "administrator" ||
      currentUser.permissions.includes("view_service_orders") ||
      currentUser.permissions.includes("service_orders.view") ||
      currentUser.permissions.includes("service_orders.fill") ||
      currentUser.permissions.includes("service_orders.manage"),
    fillServiceOrders:
      currentUser.role === "administrator" ||
      currentUser.permissions.includes("service_orders.fill") ||
      currentUser.permissions.includes("service_orders.manage"),
    viewProjects:
      currentUser.role === "administrator" ||
      currentUser.permissions.includes("view_projects") ||
      currentUser.permissions.includes("projects.view") ||
      currentUser.permissions.includes("projects.manage"),
    fillDailyWork:
      currentUser.role === "administrator" ||
      currentUser.permissions.includes("fill_daily_work") ||
      currentUser.permissions.includes("daily_work.fill"),
    keys: currentUser.permissions,
    isAdministrator: currentUser.role === "administrator",
  };
  const viewPermission = {
    "Visão geral": "dashboard.view",
    Clientes: "clients.view",
    Orçamentos: "quotes.view",
    Projetos: "projects.view",
    "Diário de obras": "daily_work.fill",
    "Entrega de Obra": "projects.view",
    Visitas: "maintenance.view",
    Preventivas: "maintenance.view",
    "Ativos do cliente": "maintenance.view",
    "Ordem de serviço": "service_orders.view",
    Chamados: "tickets.view",
    Planejamento: "planning.view",
    "Agenda dos técnicos": "planning.view",
    Colaboradores: "collaborators.view",
    Kanban: "kanban.view",
    "Plan Board": "plan_board.view",
    "Custos Projetos": "materials.view",
    Relatórios: "reports.view",
    Indicadores: "indicators.view",
    "Performance APS": "performance.view",
    Ponto: "time.team.view",
    Resultado: "finance.results",
    Configuração: "settings.manage",
  };
  const managePermission = {
    Projetos: "projects.manage",
    Visitas: "maintenance.manage",
    Preventivas: "maintenance.manage",
    "Ordem de serviço": "service_orders.manage",
    Chamados: "tickets.manage",
    Colaboradores: "collaborators.manage",
    Orçamentos: "quotes.manage",
    Kanban: "kanban.manage",
    "Custos Projetos": "materials.manage",
    Resultado: "finance.results",
  };
  const isResidentProfile =
    ["residente", "técnico residente"].includes(
      String(currentUser.accessProfileName || "").toLocaleLowerCase("pt-BR"),
    );
  const residentSummary = residentDashboardData(orders, residentActivities, range, new Date(), { start: dashboardCustomStart, end: dashboardCustomEnd });
  const canOpen = (label) =>
    label === "Ativos do cliente"
      ? isResidentProfile
      : label === "Visão geral" && currentUser.internalCategory === "procurement"
      ? permissions.keys.includes("quotes.view") || permissions.keys.includes("quotes.manage") || permissions.isAdministrator
      : (label === "Agenda dos técnicos" || label === "Visitas") && isResidentProfile
      ? false
      : permissions.isAdministrator ||
        permissions.keys.includes(viewPermission[label]) ||
        permissions.keys.includes(managePermission[label]);
  const notificationAlerts = [
    { page: "Ordem de serviço", label: "OS aguardando atendimento", count: dashboard.serviceOrdersWaiting, icon: "clipboard" },
    { page: "Chamados", label: "Chamados aguardando", count: dashboard.ticketsWaiting, icon: "headset" },
    { page: "Preventivas", label: "Preventivas atrasadas", count: dashboard.overdueMaintenance, icon: "calendar" },
    { page: "Relatórios", label: "Relatórios pendentes", count: dashboard.pendingReports, icon: "file" },
  ].filter((item) => canOpen(item.page) && Number(item.count) > 0);
  const notificationRecent = recentActivities.filter((item) => canOpen(item.page));
  const openNotificationPage = (page) => {
    setActive(page);
    setNotificationsOpen(false);
  };
  const saveOperationalRecord = async (type, payload) => {
    const endpoint =
      type === "projeto"
        ? "projects"
        : type === "chamado"
          ? "tickets"
          : "service-orders";
    const response = await fetch(`${apiBase}/${endpoint}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error("Não foi possível salvar o registro.");
    await loadOperational(session);
    setModal(null);
  };
  if (currentUser.role === "customer")
    return (
      <CustomerPortal
        token={session.token}
        user={currentUser}
        onLogout={logout}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    );

  return (
    <div className={`app-shell${active === "Plan Board" ? " plan-board-shell" : ""}`}>
      <aside className="sidebar">
        <div className="brand">
          <img
            className="brand-horizontal"
            src={theme === "dark" ? "/aboutplan-logo-white-hq.png" : "/aboutplan-horizontal-hq.png"}
            alt="AboutPlan — Gestão de Operações"
          />
          <img
            className="brand-compact"
            src="/aboutplan-icon-transparent.png"
            alt="AboutPlan"
          />
        </div>
        <nav>
          {navSections.map((section) => {
            const items = section.items.filter(([, label]) => canOpen(label));
            return items.length ? (
              <div className="nav-section" key={section.label || "main"}>
                {section.label && (
                  <span className="nav-section-label">{section.label}</span>
                )}
                {items.map(([icon, label]) => (
                  <button
                    key={label}
                    className={active === label ? "active" : ""}
                    onClick={() => setActive(label)}
                  >
                    <Icon name={icon} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            ) : null;
          })}
        </nav>
        <div className="sidebar-bottom">
          <button
            className="profile"
            onClick={logout}
            title="Sair do AboutPlan"
            aria-label={`Sair da conta de ${currentUser.name}`}
          >
            <div className="avatar">{initials(currentUser.name)}</div>
            <div className="profile-copy">
              <strong>{currentUser.name}</strong>
              <span>
                <i aria-hidden="true" />
                {roleLabel(currentUser.role)}
              </span>
            </div>
            <div className="profile-exit" aria-hidden="true">
              <small>Sair</small>
              <Icon name="chevron" size={14} />
            </div>
          </button>
        </div>
      </aside>

      <main>
        <div className="mobile-workspace-header">
          <img src={theme === "dark" ? "/aboutplan-logo-white-hq.png" : "/aboutplan-horizontal-hq.png"} alt="AboutPlan" />
          <div className="mobile-workspace-header-actions">
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            <button type="button" className="mobile-user-button" onClick={() => setMobileMenuOpen(true)} aria-label={`Abrir menu de ${currentUser.name}`}>
              <span className="avatar">{initials(currentUser.name)}</span>
              <span className="mobile-user-name">{currentUser.name}</span>
            </button>
          </div>
        </div>
        <header>
          <div>
            <h1>{active === "Visão geral" && currentUser.internalCategory === "procurement" ? "Painel de Suprimentos" : active}</h1>
            <p>
              {active === "Visão geral" && currentUser.internalCategory === "procurement"
                ? "Indicadores e acompanhamento dos orçamentos."
                : active === "Ponto"
                ? "Gestão de jornada, marcações e conformidade da equipe."
                : active === "Visitas"
                  ? "Planeje atendimentos em campo, mobilize a equipe e acompanhe cada execução."
                  : active === "Ativos do cliente"
                    ? "Consulte os equipamentos e o histórico das unidades vinculadas."
                  : active === "Preventivas"
                    ? "Gerencie planos, prazos e conformidade das manutenções programadas."
                : "Acompanhe projetos, equipes e atendimentos em um só lugar."}
            </p>
          </div>
          <div className="header-actions">
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            {active !== "Plan Board" && <label className="search">
              <Icon name="search" size={17} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  active === "Ponto"
                    ? "Buscar colaborador ou unidade"
                    : "Buscar nº do projeto, projeto ou cliente"
                }
              />
            </label>}
            <div className="notification-menu" ref={notificationsRef}>
              <button className="icon-button" type="button" aria-label="Abrir notificações" aria-expanded={notificationsOpen} aria-controls="notification-panel" onClick={() => setNotificationsOpen((value) => !value)}>
                <Icon name="bell" />
                {notificationAlerts.length > 0 && <i aria-hidden="true" />}
              </button>
              {notificationsOpen && <div className="notification-panel" id="notification-panel" role="dialog" aria-label="Notificações">
                <div className="notification-panel-title"><div><strong>Notificações</strong><span>Pendências e atividades recentes</span></div><button type="button" onClick={() => setNotificationsOpen(false)} aria-label="Fechar notificações">×</button></div>
                {notificationAlerts.length > 0 && <div className="notification-panel-section"><small>PENDÊNCIAS</small>{notificationAlerts.map((item) => <button type="button" key={item.page} onClick={() => openNotificationPage(item.page)}><span className="notification-panel-icon"><Icon name={item.icon} size={16} /></span><span>{item.label}</span><b>{item.count}</b></button>)}</div>}
                <div className="notification-panel-section"><small>ATIVIDADE RECENTE</small>{notificationRecent.map((item, index) => <button type="button" key={`${item.page}-${index}`} onClick={() => openNotificationPage(item.page)}><span className="notification-panel-icon"><Icon name={item.icon} size={16} /></span><span className="notification-panel-item"><strong>{item.title}</strong><em>{item.text} · {formatDate(item.occurredAt)}</em></span></button>)}{!notificationRecent.length && <p className="notification-panel-empty">Nenhuma atividade recente disponível.</p>}</div>
              </div>}
            </div>
          </div>
        </header>

        <section className="content">
          {active === "Visão geral" && currentUser.internalCategory === "procurement" ? (
            <ProcurementDashboard token={session.token} onOpenQuotes={() => setActive("Orçamentos")} />
          ) : active === "Visão geral" ? (
            <>
              <div className="section-heading">
                <div>
                  <h2>Olá, {currentUserFirstName}</h2>
                  <p>Veja o que precisa da sua atenção hoje.</p>
                </div>
                <div className="dashboard-heading-controls">
                {(currentUser.role === "administrator" || currentUser.role === "supervisor") && (
                  <div className="dashboard-scope-filters" aria-label="Filtrar visão geral por cliente e unidade">
                  <label>Cliente
                    <select value={dashboardClientId} onChange={(event) => { setDashboardClientId(event.target.value); setDashboardUnitId(""); }}>
                      <option value="">Todos os clientes</option>
                      {operationalSetup.clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
                    </select>
                  </label>
                  <label>Unidade
                    <select value={dashboardUnitId} onChange={(event) => setDashboardUnitId(event.target.value)}>
                      <option value="">Todas as unidades</option>
                      {operationalSetup.units.filter((unit) => !dashboardClientId || Number(unit.clientId) === Number(dashboardClientId)).map((unit) => <option key={unit.id} value={unit.id}>{dashboardClientId ? unit.name : `${operationalSetup.clients.find((client) => Number(client.id) === Number(unit.clientId))?.name || "Cliente"} · ${unit.name}`}</option>)}
                    </select>
                  </label>
                  </div>
                )}
                <label className="dashboard-period-filter">Período
                  <select value={range} onChange={(e) => setRange(e.target.value)}>
                    <option>Este mês</option>
                    <option>Mês anterior</option>
                    {isResidentProfile && <option>Próximo mês</option>}
                    {isResidentProfile && <option>Este ano</option>}
                    <option>Esta semana</option>
                    <option>Hoje</option>
                    <option>Período selecionado</option>
                  </select>
                </label>
                {range === "Período selecionado" && <div className="dashboard-custom-dates" aria-label="Intervalo personalizado">
                  <label>Data inicial<input type="date" value={dashboardCustomStart} onChange={(event) => { const value = event.target.value; if (!value) return; setDashboardCustomStart(value); if (value > dashboardCustomEnd) setDashboardCustomEnd(value); }} /></label>
                  <label>Data final<input type="date" value={dashboardCustomEnd} onChange={(event) => { const value = event.target.value; if (!value) return; setDashboardCustomEnd(value); if (value < dashboardCustomStart) setDashboardCustomStart(value); }} /></label>
                </div>}
                </div>
              </div>

              <div className={`metrics${isResidentProfile ? " resident-metrics" : ""}`}>
                {isResidentProfile ? <>
                  <Metric icon="calendar" tone="blue" label="Preventivas programadas" value={residentActivitiesLoading || residentActivitiesError ? "—" : residentSummary.preventive.planned} detail={range.toLowerCase()} />
                  <Metric icon="clipboard" tone="purple" label="OS criadas" value={residentSummary.orders.created} detail={range.toLowerCase()} />
                  <Metric icon="check" tone="green" label="OS concluídas" value={residentSummary.orders.completed} detail={range.toLowerCase()} />
                  <Metric icon="headset" tone="amber" label="Chamados abertos" value={dashboard.tickets} detail={`${dashboard.ticketsWaiting} aguardando abertura de OS`} warning={dashboard.ticketsWaiting > 0} />
                  <Metric icon="trend" tone={residentSummary.preventive.score == null ? "blue" : residentSummary.preventive.score >= 8 ? "green" : residentSummary.preventive.score >= 5 ? "amber" : "red"} label="Saúde preventiva" value={residentActivitiesLoading || residentActivitiesError || residentSummary.preventive.score == null ? "—" : `${residentSummary.preventive.score.toLocaleString("pt-BR")}/10`} detail={residentActivitiesError || `${residentSummary.preventive.assessed} atividade(s) avaliadas`} />
                </> : <>
                <Metric
                  icon="folder"
                  tone="blue"
                  label="Projetos em andamento"
                  value={dashboardRealProjects.length}
                  detail={
                    overdueProjectCount
                      ? `${overdueProjectCount} ${overdueProjectCount === 1 ? "obra atrasada" : "obras atrasadas"}`
                      : "Nenhuma obra atrasada"
                  }
                />
                <Metric
                  icon="clipboard"
                  tone="purple"
                  label="Ordens de serviço"
                  value={dashboard.serviceOrders}
                  detail={`${dashboard.serviceOrdersWaiting} aguardando atendimento`}
                />
                <Metric
                  icon="headset"
                  tone="amber"
                  label="Chamados abertos"
                  value={dashboard.tickets}
                  detail={`${dashboard.ticketsWaiting} aguardando abertura de OS`}
                  warning={dashboard.ticketsWaiting > 0}
                />
                <Metric
                  icon="trend"
                  tone="green"
                  label="Taxa de conclusão das OS"
                  value={`${dashboardOrders.length ? Math.round((dashboardOrders.filter((order) => ["finished", "closed"].includes(order.rawStatus)).length / dashboardOrders.length) * 100) : 0}%`}
                  detail={`${dashboardOrders.filter((order) => ["finished", "closed"].includes(order.rawStatus)).length} concluídas de ${dashboardOrders.length}`}
                />
                <Metric
                  icon="file"
                  tone="amber"
                  label="Relatórios pendentes"
                  value={dashboard.pendingReports}
                  detail="obra, OS e manutenção"
                  warning={dashboard.pendingReports > 0}
                />
                </>}
              </div>

              {isResidentProfile ? <ResidentDashboard orders={orders} activities={residentActivities} range={range} loading={residentActivitiesLoading} error={residentActivitiesError} /> : <>
              <div className="grid-main">
                <section className="panel projects-panel">
                  <div className="panel-title dashboard-projects-title">
                    <div>
                      <h3>
                        {dashboardProjectType === "studies"
                          ? "Estudos de Implantação"
                          : "Projetos em andamento"}
                      </h3>
                      <p>
                        {dashboardProjectType === "studies"
                          ? "Cronogramas preliminares aguardando efetivação"
                          : "Prazo planejado comparado à execução real"}
                      </p>
                    </div>
                    <button type="button" onClick={() => setActive("Projetos")}>
                      Ver todos
                      <Icon name="arrow" size={15} />
                    </button>
                  </div>
                  <div
                    className="dashboard-project-filters"
                    role="group"
                    aria-label="Filtrar projetos por tipo"
                  >
                    <button
                      type="button"
                      className={dashboardProjectType === "projects" ? "active" : ""}
                      aria-pressed={dashboardProjectType === "projects"}
                      onClick={() => setDashboardProjectType("projects")}
                    >
                      <Icon name="folder" size={14} />
                      Projetos
                      <b>{dashboardRealProjects.length}</b>
                    </button>
                    <button
                      type="button"
                      className={dashboardProjectType === "studies" ? "active" : ""}
                      aria-pressed={dashboardProjectType === "studies"}
                      onClick={() => setDashboardProjectType("studies")}
                    >
                      <Icon name="file" size={14} />
                      Estudos de Implantação
                      <b>{dashboardStudies.length}</b>
                    </button>
                  </div>
                  <div className="project-table">
                    <div className="table-head">
                      <span>Projeto</span>
                      <span>Progresso</span>
                      <span>Prazo consumido</span>
                      <span>Entrega</span>
                      <span>Status</span>
                      <span></span>
                    </div>
                    {visibleDashboardProjects.map((p) => (
                      <ProjectRow
                        key={p.id || p.name}
                        project={p}
                        onOpen={() => setDashboardProject(p)}
                      />
                    ))}
                    {!visibleDashboardProjects.length && (
                      <div className="empty">
                        {dashboardProjectType === "studies"
                          ? "Nenhum estudo de implantação encontrado no período."
                          : "Nenhum projeto em andamento encontrado no período."}
                      </div>
                    )}
                  </div>
                </section>

                <section className="panel activity-panel">
                  <PanelTitle
                    title="Atividade recente"
                    action="Ver histórico"
                  />
                  <div className="activity-list">
                    {recentActivities.map((a) => (
                      <div className="activity" key={a.title}>
                        <div className={`activity-icon ${a.tone}`}>
                          <Icon name={a.icon} size={17} />
                        </div>
                        <div>
                          <strong>{a.title}</strong>
                          <p>{a.text}</p>
                          <time>{formatDateTime(a.occurredAt)}</time>
                        </div>
                      </div>
                    ))}
                    {!recentActivities.length && (
                      <div className="empty">Nenhuma atividade recente no seu escopo.</div>
                    )}
                  </div>
                </section>
              </div>

              <PowerBiCharts data={dashboardCharts} />
              <OperationalRankings data={dashboardCharts} />
              </>}
            </>
          ) : (
            <WorkspacePage
              active={active}
              query={query}
              projects={filteredProjects}
              orders={orders}
              tickets={tickets}
              operationalSetup={operationalSetup}
              permissions={permissions}
              role={currentUser.role}
              resident={isResidentProfile}
              internalCategory={currentUser.internalCategory}
              userId={currentUser.id}
              token={session.token}
              pendingOrderId={pendingOrderId}
              onOrderOpened={() => setPendingOrderId(null)}
              onRefresh={() => loadOperational(session)}
              onCreate={(type) => setModal(type)}
              onOpenOrder={(orderId) => { setPendingOrderId(Number(orderId)); loadOperational(session); setActive("Ordem de serviço"); }}
              initialAssetTag={scannedEquipmentTag}
              theme={theme}
              onToggleTheme={toggleTheme}
            />
          )}
        </section>
      </main>
      <nav className="mobile-workspace-nav" aria-label="Navegação principal">
        {[["grid", "Visão geral", "Início"], ["headset", "Chamados", "Chamados"], ["clipboard", "Ordem de serviço", "OS"], ["calendar", "Preventivas", "Preventivas"]].filter(([, label]) => canOpen(label)).map(([icon, label, shortLabel]) => (
          <button type="button" key={label} className={active === label ? "active" : ""} onClick={() => { setActive(label); setMobileMenuOpen(false); }} aria-label={label} aria-current={active === label ? "page" : undefined}>
            <Icon name={icon} size={21} /><span>{shortLabel}</span>
          </button>
        ))}
        <button type="button" className={mobileMenuOpen ? "active" : ""} onClick={() => setMobileMenuOpen(true)} aria-label="Mais opções"><Icon name="settings" size={21} /><span>Mais</span></button>
      </nav>
      {mobileMenuOpen && <div className="mobile-workspace-menu-backdrop" onClick={() => setMobileMenuOpen(false)}>
        <div className="mobile-workspace-menu" role="dialog" aria-modal="true" aria-label="Menu" onClick={(event) => event.stopPropagation()}>
          <div className="mobile-workspace-menu-head"><div><span className="avatar">{initials(currentUser.name)}</span><div><strong>{currentUser.name}</strong><small>{roleLabel(currentUser.role)}</small></div></div><button type="button" onClick={() => setMobileMenuOpen(false)} aria-label="Fechar menu">×</button></div>
          <div className="mobile-workspace-menu-links">{navSections.flatMap((section) => section.items).filter(([, label]) => canOpen(label)).map(([icon, label]) => <button type="button" key={label} className={active === label ? "active" : ""} onClick={() => { setActive(label); setMobileMenuOpen(false); }}><Icon name={icon} size={19} />{label}</button>)}</div>
          <button type="button" className="mobile-workspace-signout" onClick={logout}>Sair da conta</button>
        </div>
      </div>}
      {modal && (
        <CreateModal
          type={modal}
          setup={operationalSetup}
          resident={isResidentProfile}
          token={session.token}
          onClose={() => setModal(null)}
          onSave={(payload) => saveOperationalRecord(modal, payload)}
        />
      )}
      {dashboardProject && (
        <ProjectWorkModal
          project={dashboardProject}
          permissions={permissions}
          role={currentUser.role}
          token={session.token}
          onRefresh={() => loadOperational(session)}
          onClose={() => {
            setDashboardProject(null);
            loadOperational(session);
          }}
          onSaved={() => {
            setDashboardProject(null);
            loadOperational(session);
          }}
        />
      )}
    </div>
  );
}

function Metric({ icon, tone, label, value, detail, warning }) {
  return (
    <article className={`metric tone-${tone}${warning ? " has-warning" : ""}`}>
      <div className={`metric-icon ${tone}`}>
        <Icon name={icon} />
      </div>
      <div className="metric-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <small className={warning ? "warning" : ""}>
          {warning && "● "}
          {detail}
        </small>
      </div>
      <button>
        <Icon name="arrow" size={17} />
      </button>
    </article>
  );
}
function PanelTitle({ title, subtitle, action }) {
  return (
    <div className="panel-title">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action && (
        <button>
          {action}
          <Icon name="arrow" size={15} />
        </button>
      )}
    </div>
  );
}
function ProjectRow({ project: p, onOpen }) {
  const exceeded =
    Number(p.planned) > 0 && Number(p.worked) > Number(p.planned);
  return (
    <div className="project-row">
      <div className="project-name">
        <span className={`client-logo ${p.tone}`}>{p.initials}</span>
        <div>
          <strong>{p.name}</strong>
          <small>
            {p.client} • {p.stage}
          </small>
        </div>
      </div>
      <div className="progress">
        <div title={`${p.name}: ${p.progress}% concluído`}>
          <i style={{ width: `${p.progress}%` }}></i>
        </div>
        <b>{p.progress}%</b>
      </div>
      <div
        className={`days${exceeded ? " exceeded" : ""}`}
        title={exceeded ? "Dias executados acima do tempo estimado" : ""}
      >
        <b>
          {p.worked}/{p.planned}d
        </b>
      </div>
      <span>{p.end}</span>
      <span className={`status ${p.tone}`}>● {p.health}</span>
      <button
        type="button"
        className="row-action"
        onClick={onOpen}
        aria-label={`Abrir projeto ${p.name}`}
        title={`Abrir projeto ${p.name}`}
      >
        <Icon name="arrow" size={16} />
      </button>
    </div>
  );
}

function PowerBiCharts({ data }) {
  const clients = data.clientPerformance || [];
  const trend = data.orderTrend || [];
  const rate = data.deadline?.rate || 0;
  const portfolioProgress = clients.length
    ? Math.round(
        clients.reduce(
          (sum, item) =>
            sum + Number(item.progress || 0) * Number(item.projects || 0),
          0,
        ) /
          Math.max(
            1,
            clients.reduce((sum, item) => sum + Number(item.projects || 0), 0),
          ),
      )
    : 0;
  const portfolioExpected = clients.length
    ? Math.round(
        clients.reduce(
          (sum, item) =>
            sum +
            Number(item.expectedProgress || 0) * Number(item.projects || 0),
          0,
        ) /
          Math.max(
            1,
            clients.reduce((sum, item) => sum + Number(item.projects || 0), 0),
          ),
      )
    : 0;
  const measuredProjects = clients.reduce(
    (sum, item) => sum + Number(item.projects || 0),
    0,
  );
  const scheduleVariance = portfolioProgress - portfolioExpected;
  const aheadProjects = clients.reduce(
    (sum, item) => sum + Number(item.ahead || 0),
    0,
  );
  const onTrackProjects = clients.reduce(
    (sum, item) => sum + Number(item.onTrack || 0),
    0,
  );
  const delayedProjects = clients.reduce(
    (sum, item) => sum + Number(item.delayed || 0),
    0,
  );
  const scheduleStatus =
    scheduleVariance > 15
      ? "ahead"
      : scheduleVariance < -5
        ? "delayed"
        : "on-track";
  const scheduleStatusLabel =
    scheduleStatus === "ahead"
      ? "Ótimo"
      : scheduleStatus === "delayed"
        ? "Atenção"
        : "Normal";
  const trendMax = Math.max(
    5,
    ...trend.flatMap((item) => [item.created, item.completed]),
  );
  const axisLabelStep = Math.max(1, Math.ceil((trend.length - 1) / 6));
  const chartPoints = (key) =>
    trend.map((item, index) => ({
      x: 55 + index * (510 / Math.max(trend.length - 1, 1)),
      y: 145 - ((Number(item[key]) || 0) / trendMax) * 110,
    }));
  const yTicks = [trendMax, trendMax * (2 / 3), trendMax / 3, 0].map((value) =>
    Math.round(value),
  );
  const smoothPath = (source) => {
    if (!source.length) return "";
    if (source.length === 1) return `M ${source[0].x} ${source[0].y}`;
    return source.slice(0, -1).reduce((path, point, index) => {
      const previous = source[index - 1] || point;
      const next = source[index + 1];
      const afterNext = source[index + 2] || next;
      const firstControlX = point.x + (next.x - previous.x) / 6;
      const firstControlY = point.y + (next.y - previous.y) / 6;
      const secondControlX = next.x - (afterNext.x - point.x) / 6;
      const secondControlY = next.y - (afterNext.y - point.y) / 6;
      return `${path} C ${firstControlX} ${firstControlY}, ${secondControlX} ${secondControlY}, ${next.x} ${next.y}`;
    }, `M ${source[0].x} ${source[0].y}`);
  };
  const createdPoints = chartPoints("created");
  const completedPoints = chartPoints("completed");
  const areaPath = (source) =>
    source.length
      ? `${smoothPath(source)} L ${source[source.length - 1].x} 145 L ${source[0].x} 145 Z`
      : "";
  return (
    <div className="power-charts">
      <section className="panel project-performance-chart">
        <PanelTitle
          title="Desempenho dos projetos"
          subtitle="Avanço realizado comparado ao avanço esperado pelo calendário"
        />
        <div className="performance-modern-summary">
          <div>
            <span>Avanço realizado</span>
            <strong>{portfolioProgress}%</strong>
            <small>{measuredProjects} obra(s) com cronograma mensurável</small>
          </div>
          <div>
            <span>Avanço esperado</span>
            <strong>{portfolioExpected}%</strong>
            <small>Conforme início e conclusão planejada</small>
          </div>
          <div className={`schedule-summary schedule-${scheduleStatus}`}>
            <span>Desempenho</span>
            <strong>{scheduleStatusLabel}</strong>
            <small>
              {delayedProjects} atrasada(s) · {onTrackProjects} em dia ·{" "}
              {aheadProjects} adiantada(s)
            </small>
          </div>
        </div>
        <div className="performance-modern-chart">
          <div className="performance-modern-axis">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
          {clients.map((item) => {
            const progress = Math.max(
              0,
              Math.min(100, Number(item.progress) || 0),
            );
            const expected = Math.max(
              0,
              Math.min(100, Number(item.expectedProgress) || 0),
            );
            const variance = progress - expected;
            const status =
              variance > 5 ? "ahead" : variance < -5 ? "delayed" : "on-track";
            const statusLabel =
              status === "ahead"
                ? "Adiantado"
                : status === "delayed"
                  ? "Atrasado"
                  : "Dentro do prazo";
            return (
              <article key={item.client} className={`schedule-${status}`}>
                <div>
                  <strong>{item.client}</strong>
                  <span>{progress}%</span>
                </div>
                <div className="performance-modern-track">
                  <i style={{ width: `${progress}%` }}></i>
                  <em
                    style={{ left: `${expected}%` }}
                    title={`Avanço esperado ${expected}%`}
                  ></em>
                </div>
                <small>
                  {statusLabel} · esperado {expected}% ·{" "}
                  {variance > 0 ? "+" : ""}
                  {variance}%
                </small>
              </article>
            );
          })}
          {!clients.length && (
            <div className="empty">
              Nenhuma obra com dias e cronograma mensurável neste período.
            </div>
          )}
        </div>
      </section>
      <section className="panel order-history">
        <PanelTitle
          title="OS concluídas × criadas"
          subtitle="Movimentação no período selecionado"
        />
        <div className="power-legend">
          <span>
            <i className="actual"></i>Criadas
          </span>
          <span>
            <i className="completed"></i>Concluídas
          </span>
        </div>
        <svg
          viewBox="0 0 600 205"
          role="img"
          aria-label="Histórico de ordens de serviço"
        >
          <defs>
            <linearGradient id="createdArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3478f6" stopOpacity="0.34" />
              <stop offset="100%" stopColor="#69c7ff" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="completedArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#24d2bd" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#6ee7d4" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="createdStroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#55d8ff" />
              <stop offset="55%" stopColor="#3478f6" />
              <stop offset="100%" stopColor="#665cff" />
            </linearGradient>
            <linearGradient id="completedStroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#63e6d4" />
              <stop offset="100%" stopColor="#16a979" />
            </linearGradient>
            <filter id="softGlow" x="-20%" y="-30%" width="140%" height="160%">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g className="grid-lines">
            {[35, 72, 109, 145].map((y, index) => (
              <g key={y}>
                <line x1="55" x2="565" y1={y} y2={y} />
                <text className="axis-value" x="47" y={y + 3}>
                  {yTicks[index]}
                </text>
              </g>
            ))}
          </g>
          <text
            className="axis-title"
            x="12"
            y="90"
            transform="rotate(-90 12 90)"
          >
            Quantidade de OS
          </text>
          {trend.length === 1 && (
            <g className="single-day-orders">
              {[
                { key: "created", label: "Criadas", x: 235 },
                { key: "completed", label: "Concluídas", x: 385 },
              ].map(({ key, label, x }) => {
                const count = Number(trend[0][key]) || 0;
                const height = (count / trendMax) * 110;
                return (
                  <g key={key} className={`single-day-${key}`}>
                    <rect x={x - 22} y={145 - height} width="44" height={Math.max(height, 2)} rx="7" />
                    <text className="single-day-count" x={x} y={Math.max(30, 136 - height)}>{count}</text>
                    <text className="single-day-label" x={x} y="161">{label}</text>
                  </g>
                );
              })}
              <text className="axis-day" x="310" y="179">
                {shortChartDate(trend[0].day)}
              </text>
            </g>
          )}
          {trend.length > 1 && (
            <>
              <path
                className="chart-area created-area"
                d={areaPath(createdPoints)}
              />
              <path
                className="chart-area completed-area"
                d={areaPath(completedPoints)}
              />
              <path
                className="chart-line created-line"
                d={smoothPath(createdPoints)}
              />
              <path
                className="chart-line completed-line"
                d={smoothPath(completedPoints)}
              />
              {trend.map((item, index) => {
                const x = 55 + index * (510 / Math.max(trend.length - 1, 1));
                return (
                  <g key={item.day}>
                    {item.created > 0 && (
                      <circle className="created-dot" cx={x} cy={145 - (item.created / trendMax) * 110} r="2.5">
                        <title>{`${shortChartDate(item.day)} — Criadas: ${item.created}`}</title>
                      </circle>
                    )}
                    {item.completed > 0 && (
                      <circle className="completed-dot" cx={x} cy={145 - (item.completed / trendMax) * 110} r="2.5">
                        <title>{`${shortChartDate(item.day)} — Concluídas: ${item.completed}`}</title>
                      </circle>
                    )}
                    {(index % axisLabelStep === 0 || index === trend.length - 1) && (
                      <text className="axis-day" x={x} y="174">
                        {item.day.slice(8, 10)}/{item.day.slice(5, 7)}
                      </text>
                    )}
                  </g>
                );
              })}
            </>
          )}
          <text className="axis-horizontal-title" x="310" y="199">Data</text>
        </svg>
        {!trend.length && (
          <div className="empty">Ainda não há histórico de OS.</div>
        )}
      </section>
      <section className="panel deadline-gauge">
        <PanelTitle
          title="Conclusão de projetos"
          subtitle="Percentual concluído no ano atual"
        />
        <div className="gauge-wrap">
          <svg viewBox="0 0 220 125">
            <defs>
              <linearGradient id="deadlineGauge" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#56d9ff" />
                <stop offset="52%" stopColor="#3478f6" />
                <stop offset="100%" stopColor="#665cff" />
              </linearGradient>
            </defs>
            <path className="gauge-bg" d="M25 110 A85 85 0 0 1 195 110" />
            <path
              className="gauge-value"
              pathLength="100"
              strokeDasharray={`${rate} 100`}
              d="M25 110 A85 85 0 0 1 195 110"
            />
          </svg>
          <div className="gauge-label">
            <strong>{rate}%</strong>
            <span>Concluído</span>
          </div>
          <small className="gauge-zero">0%</small>
          <small className="gauge-hundred">100%</small>
        </div>
        <p>
          {data.deadline?.completed || 0} de {data.deadline?.total || 0}{" "}
          projetos concluídos no ano
        </p>
      </section>
    </div>
  );
}

function OperationalRankings({ data }) {
  const technicians = data.technicianRanking || [];
  const demands = data.clientDemand || [];
  const maxTech = Math.max(1, ...technicians.map((item) => item.attended));
  const maxDemand = Math.max(1, ...demands.map((item) => item.total));
  return (
    <div className="operational-rankings">
      <section className="panel ranking-panel">
        <PanelTitle
          title="Top 5 — Atendimento de OS"
          subtitle="Profissionais com maior volume de ordens atribuídas"
        />
        <div className="ranking-list">
          {technicians.map((item, index) => (
            <div key={item.name}>
              <b>{index + 1}</b>
              <span className="avatar small">{initials(item.name)}</span>
              <div>
                <strong>{item.name}</strong>
                <div
                  className="ranking-bar"
                  title={`${item.name}: ${item.attended} OS atribuídas, ${item.completed} concluídas`}
                >
                  <i
                    style={{ width: `${(item.attended / maxTech) * 100}%` }}
                  ></i>
                </div>
                <small>
                  {item.completed} concluídas
                  <b>
                    {item.attended
                      ? Math.round((item.completed / item.attended) * 100)
                      : 0}
                    %
                  </b>
                </small>
              </div>
              <em>
                {item.attended}
                <small> OS</small>
              </em>
            </div>
          ))}
          {!technicians.length && (
            <div className="empty">
              As OS ainda não possuem responsáveis atribuídos.
            </div>
          )}
        </div>
      </section>
      <section className="panel demand-panel">
        <PanelTitle
          title="Clientes com maior demanda"
          subtitle="OS e chamados ativos que exigem atenção"
        />
        <div className="demand-list">
          {demands.map((item, index) => (
            <div key={item.name}>
              <div>
                <b>{index + 1}</b>
                <strong>{item.name}</strong>
                <span>{item.total} demandas ativas</span>
              </div>
              <div
                className="demand-track"
                title={`${item.name}: ${item.serviceOrders} OS e ${item.tickets} chamados ativos`}
              >
                <i
                  className="os"
                  style={{
                    width: `${(item.serviceOrders / maxDemand) * 100}%`,
                  }}
                ></i>
                <i
                  className="ticket"
                  style={{ width: `${(item.tickets / maxDemand) * 100}%` }}
                ></i>
              </div>
              <small>
                <span>
                  <i className="os"></i>
                  {item.serviceOrders} OS
                </span>
                <span>
                  <i className="ticket"></i>
                  {item.tickets} chamados
                </span>
              </small>
            </div>
          ))}
          {!demands.length && (
            <div className="empty">Nenhuma demanda operacional ativa.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function ExecutiveCharts({ projects, orders, tickets, dashboard }) {
  const completed = orders.filter((item) =>
    ["finished", "closed"].includes(item.rawStatus),
  ).length;
  const running = orders.filter((item) =>
    ["analysis", "scheduled", "in_progress"].includes(item.rawStatus),
  ).length;
  const waiting = Math.max(0, orders.length - completed - running);
  const healthy = projects.filter((item) => item.tone === "green").length;
  const attention = projects.filter((item) => item.tone === "amber").length;
  const critical = projects.filter((item) => item.tone === "red").length;
  const healthTotal = Math.max(projects.length, 1);
  const healthScore = Math.round(
    ((healthy + attention * 0.5) / healthTotal) * 100,
  );
  const clients = Object.values(
    projects.reduce((acc, item) => {
      acc[item.client] ??= { name: item.client, total: 0, progress: 0 };
      acc[item.client].total++;
      acc[item.client].progress += Number(item.progress) || 0;
      return acc;
    }, {}),
  )
    .map((item) => ({
      ...item,
      progress: Math.round(item.progress / item.total),
    }))
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 6);
  const maxClient = Math.max(...clients.map((item) => item.total), 1);
  return (
    <div className="executive-dashboard">
      <section className="panel executive-health">
        <PanelTitle
          title="Saúde da operação"
          subtitle="Visão consolidada dos projetos ativos"
        />
        <div className="health-content">
          <div
            className="donut"
            style={{ "--score": `${healthScore * 3.6}deg` }}
          >
            <div>
              <strong>{healthScore}%</strong>
              <span>índice saudável</span>
            </div>
          </div>
          <div className="health-legend">
            <span>
              <i className="good"></i>
              <b>{healthy}</b> No prazo
            </span>
            <span>
              <i className="attention"></i>
              <b>{attention}</b> Atenção
            </span>
            <span>
              <i className="critical"></i>
              <b>{critical}</b> Críticos
            </span>
          </div>
        </div>
      </section>
      <section className="panel status-chart">
        <PanelTitle
          title="Fluxo das ordens de serviço"
          subtitle="Distribuição atual por situação"
        />
        <div className="status-stack">
          <i
            className="complete"
            style={{
              width: `${orders.length ? (completed / orders.length) * 100 : 0}%`,
            }}
          ></i>
          <i
            className="running"
            style={{
              width: `${orders.length ? (running / orders.length) * 100 : 0}%`,
            }}
          ></i>
          <i
            className="waiting"
            style={{
              width: `${orders.length ? (waiting / orders.length) * 100 : 0}%`,
            }}
          ></i>
        </div>
        <div className="status-chart-legend">
          <div>
            <i className="complete"></i>
            <span>Concluídas</span>
            <strong>{completed}</strong>
          </div>
          <div>
            <i className="running"></i>
            <span>Em andamento</span>
            <strong>{running}</strong>
          </div>
          <div>
            <i className="waiting"></i>
            <span>Aguardando</span>
            <strong>{waiting}</strong>
          </div>
        </div>
        <div className="attention-strip">
          <Icon name="headset" size={17} />
          <span>
            <b>{tickets.length}</b> chamados abertos
          </span>
          <span>
            <b>{dashboard.pendingReports || 0}</b> relatórios pendentes
          </span>
        </div>
      </section>
      <section className="panel client-chart">
        <PanelTitle
          title="Carteira por cliente"
          subtitle="Volume de projetos e avanço médio"
        />
        <div className="client-columns">
          {clients.map((item) => (
            <div key={item.name}>
              <div className="column-area">
                <i
                  style={{
                    height: `${Math.max(12, (item.total / maxClient) * 100)}%`,
                  }}
                >
                  <b>{item.total}</b>
                </i>
              </div>
              <strong>{item.name}</strong>
              <span>{item.progress}% avanço médio</span>
            </div>
          ))}
          {!clients.length && (
            <div className="empty">Nenhum projeto ativo.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function WorkspacePage({
  active,
  query,
  projects: projectList,
  orders,
  tickets,
  operationalSetup,
  permissions,
  role,
  resident,
  internalCategory,
  userId,
  token,
  onRefresh,
  onCreate,
  onOpenOrder,
  pendingOrderId,
  onOrderOpened,
  initialAssetTag,
  theme,
  onToggleTheme,
}) {
  if (active === "Projetos")
    return (
      <ProjectsPage
        projects={projectList}
        permissions={permissions}
        role={role}
        token={token}
        onRefresh={onRefresh}
        onCreate={() => onCreate("projeto")}
      />
    );
  if (active === "Diário de obras")
    return (
      <DailyWorkPage
        projects={projectList}
        role={role}
        token={token}
        onRefresh={onRefresh}
      />
    );
  if (active === "Entrega de Obra")
    return (
      <ProjectDeliveryPage
        projects={projectList}
        token={token}
        canManage={
          permissions.isAdministrator ||
          permissions.keys.includes("projects.manage")
        }
      />
    );
  if (active === "Clientes") return <ClientsPage token={token} />;
  if (active === "Orçamentos")
    return (
      <QuotesPage
        token={token}
        canDelete={permissions.isAdministrator}
        canManage={
          permissions.isAdministrator ||
          permissions.keys.includes("quotes.manage")
        }
      />
    );
  if (active === "Kanban")
    return (
      <ProjectKanbanPage
        token={token}
        canManage={
          permissions.isAdministrator ||
          permissions.keys.includes("kanban.manage")
        }
        canConfigure={internalCategory === "director"}
      />
    );
  if (active === "Plan Board")
    return <PlanBoardPage token={token} user={{ id: userId, role, internalCategory }} permissions={permissions.keys} theme={theme} onToggleTheme={onToggleTheme} />;
  if (active === "Ordem de serviço")
    return (
      <ServiceOrdersPage
        items={orders}
        query={query}
        token={token}
        onRefresh={onRefresh}
        canFill={permissions.fillServiceOrders}
        canDelete={permissions.isAdministrator}
        resident={resident}
        pendingOrderId={pendingOrderId}
        onOrderOpened={onOrderOpened}
      />
    );
  if (active === "Chamados")
    return (
      <RecordsPage
        type="chamado"
        title="Chamados"
        subtitle="Registre o problema e abra uma OS para iniciar o atendimento."
        items={tickets}
        operationalSetup={operationalSetup}
        resident={resident}
        query={query}
        token={token}
        onRefresh={onRefresh}
        onCreate={() => onCreate("chamado")}
      />
    );
  if (active === "Visitas")
    return <MaintenancePage token={token} query={query} module="visits" />;
  if (active === "Preventivas")
    return <MaintenancePage token={token} query={query} module="preventives" resident={resident} />;
  if (active === "Ativos do cliente" && resident)
    return <ResidentAssetsPage token={token} onOpenOrder={onOpenOrder} initialAssetTag={initialAssetTag} />;
  if (active === "Relatórios") return <ReportsPage token={token} />;
  if (active === "Planejamento") return <PlanningPage token={token} />;
  if (active === "Agenda dos técnicos")
    return <TechnicianAgendaPage token={token} />;
  if (active === "Colaboradores")
    return (
      <CollaboratorsPage
        token={token}
        role={role}
        canManage={
          permissions.isAdministrator ||
          permissions.keys.includes("collaborators.manage")
        }
      />
    );
  if (active === "Custos Projetos") return <MaterialsPageV2 token={token} />;
  if (active === "Indicadores") return <IndicatorsPage token={token} />;
  if (active === "Performance APS")
    return (
      <PerformancePage
        token={token}
        canManage={
          permissions.isAdministrator ||
          permissions.keys.includes("performance.manage")
        }
      />
    );
  if (active === "Ponto")
    return (
      <TimeClockPage
        token={token}
        canReview={
          permissions.isAdministrator ||
          permissions.keys.includes("time.adjustments.review")
        }
        canAudit={
          permissions.isAdministrator || permissions.keys.includes("time.audit")
        }
        canManage={
          permissions.isAdministrator ||
          permissions.keys.includes("time.manage")
        }
      />
    );
  if (active === "Resultado")
    return <FinancialPage permissions={permissions} token={token} />;
  if (active === "Configuração")
    return <SettingsPage token={token} currentUserId={userId} role={role} />;
  return <ComingSoon title={active} />;
}

function TimeClockPage({ token, canReview, canAudit, canManage }) {
  const [tab, setTab] = useState("Visão Geral");
  const [data, setData] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [mirrorMonth, setMirrorMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [exportingMirror, setExportingMirror] = useState(false);
  const [managerAdjustment, setManagerAdjustment] = useState({
    requestType: "missing_punch",
    requestedEventType: "entry",
    requestedOccurredAt: `${new Date().toISOString().slice(0, 10)}T08:00`,
    originalPunchId: "",
    reason: "",
  });
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [selectedWorkday, setSelectedWorkday] = useState(null);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().slice(0, 10),
    technicianId: "",
    clientId: "",
    unitId: "",
  });
  const headers = { Authorization: `Bearer ${token}` };
  const queryString = () =>
    new URLSearchParams(
      Object.entries(filters).filter(([, value]) => value),
    ).toString();
  const load = async () => {
    const response = await fetch(`${apiBase}/time/dashboard?${queryString()}`, {
      headers,
    });
    if (!response.ok) throw new Error("Não foi possível carregar o ponto.");
    setData(await response.json());
    if (canReview) {
      const pending = await fetch(`${apiBase}/time/adjustments`, { headers });
      if (pending.ok) setAdjustments(await pending.json());
    }
  };
  useEffect(() => {
    load().catch(() => setData(null));
  }, [filters.date, filters.technicianId, filters.clientId, filters.unitId]);
  const review = async (id, status) => {
    const notes = window.prompt(
      status === "approved"
        ? "Justificativa da aprovação"
        : "Motivo da rejeição",
    );
    if (!notes) return;
    const response = await fetch(`${apiBase}/time/adjustments/${id}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ status, reviewNotes: notes }),
    });
    if (response.ok) load();
  };
  const createManagerAdjustment = async (event) => {
    event.preventDefault();
    if (!filters.technicianId || managerAdjustment.reason.trim().length < 8) {
      window.alert(
        "Selecione o colaborador e informe uma observação detalhada.",
      );
      return;
    }
    setSavingAdjustment(true);
    try {
      const response = await fetch(`${apiBase}/time/adjustments/manager`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...managerAdjustment,
          technicianId: Number(filters.technicianId),
          workDate: filters.date,
          requestedOccurredAt: new Date(
            managerAdjustment.requestedOccurredAt,
          ).toISOString(),
          originalPunchId:
            managerAdjustment.requestType === "correction"
              ? Number(managerAdjustment.originalPunchId)
              : null,
        }),
      });
      if (!response.ok) throw new Error("adjustment_error");
      setManagerAdjustment({
        requestType: "missing_punch",
        requestedEventType: "entry",
        requestedOccurredAt: `${filters.date}T08:00`,
        originalPunchId: "",
        reason: "",
      });
      await load();
    } catch (_) {
      window.alert("Não foi possível gravar o ajuste. Confira os campos.");
    } finally {
      setSavingAdjustment(false);
    }
  };
  const tabs = [
    "Visão Geral",
    "Equipe Hoje",
    "Marcações",
    "Ajustes",
    "Espelhos",
    "Banco de Horas",
    "Escalas",
    "Afastamentos",
    "Fechamentos",
    "Relatórios",
    "Fiscalização",
    "Auditoria",
    "Configurações",
  ];
  const eventLabel = {
    entry: "Entrada",
    break_start: "Início do intervalo",
    break_end: "Retorno",
    exit: "Saída",
  };
  const time = (value) =>
    new Date(value).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  const hours = (seconds) =>
    `${String(Math.floor((seconds || 0) / 3600)).padStart(2, "0")}:${String(Math.floor(((seconds || 0) % 3600) / 60)).padStart(2, "0")}`;
  const monthOptions = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  const mirrorYears = Array.from(
    { length: new Date().getFullYear() - 2023 + 2 },
    (_, index) => 2024 + index,
  );
  const exportMonthlyMirror = async () => {
    if (!filters.technicianId) {
      window.alert("Selecione um colaborador para gerar o ponto mensal.");
      return;
    }
    setExportingMirror(true);
    try {
      const response = await fetch(
        `${apiBase}/time/monthly?technicianId=${filters.technicianId}&month=${mirrorMonth}`,
        { headers },
      );
      if (!response.ok) throw new Error("monthly_report_error");
      const report = await response.json();
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const width = 210;
      let reportLogo = null;
      try {
        reportLogo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
      } catch {}
      let y = 0;
      const addHeader = () => {
        doc.setFillColor(21, 25, 26);
        doc.rect(0, 0, width, 31, "F");
        if (reportLogo) {
          doc.addImage(reportLogo, "PNG", 15, 7, 48, 10.74, undefined, "SLOW");
        }
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text("ESPELHO DE PONTO", width - 15, 13, { align: "right" });
        doc.setFontSize(8);
        doc.setTextColor(117, 103, 71);
        doc.text("Ponto mensal do colaborador", width - 15, 20, {
          align: "right",
        });
        doc.setTextColor(255, 255, 255);
        doc.text(mirrorMonth.split("-").reverse().join("/"), width - 15, 26, {
          align: "right",
        });
        y = 40;
      };
      const ensureSpace = (needed = 18) => {
        if (y + needed < 282) return;
        doc.addPage();
        addHeader();
      };
      const text = (value, x, lineY, options = {}) =>
        doc.text(String(value ?? "—"), x, lineY, options);
      addHeader();
      doc.setTextColor(31, 43, 65);
      doc.setFontSize(13);
      doc.text(report.technician.name, 15, y);
      doc.setFontSize(8);
      doc.setTextColor(104, 118, 141);
      doc.text(
        `${report.technician.functionName} • ${report.technician.email || "E-mail não informado"}`,
        15,
        y + 6,
      );
      y += 15;
      const summaries = [
        ["Dias registrados", report.summary.daysWithPunches],
        ["Jornadas completas", report.summary.completeDays],
        ["Jornadas incompletas", report.summary.incompleteDays],
        ["Total trabalhado", hours(report.summary.totalWorkedSeconds)],
        ["Marcações", report.summary.totalPunches],
        ["Ajustes aprovados", report.summary.approvedAdjustments],
      ];
      summaries.forEach(([label, value], index) => {
        const column = index % 3;
        const row = Math.floor(index / 3);
        const x = 15 + column * 61;
        const cardY = y + row * 19;
        doc.setFillColor(246, 248, 252);
        doc.roundedRect(x, cardY, 56, 15, 2, 2, "F");
        doc.setFontSize(6.5);
        doc.setTextColor(116, 130, 153);
        text(label, x + 4, cardY + 5);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(31, 43, 65);
        text(value, x + 4, cardY + 11.5);
        doc.setFont("helvetica", "normal");
      });
      y += 45;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Resumo diário", 15, y);
      y += 7;
      report.days.forEach((day) => {
        ensureSpace(22 + day.punches.length * 5);
        doc.setFillColor(
          day.complete ? 237 : 255,
          day.complete ? 248 : 247,
          day.complete ? 243 : 232,
        );
        doc.roundedRect(15, y, 180, 9, 1.5, 1.5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(38, 51, 74);
        text(day.date.split("-").reverse().join("/"), 19, y + 5.8);
        text(hours(day.workedSeconds), 150, y + 5.8);
        text(day.complete ? "Completa" : "Incompleta", 191, y + 5.8, {
          align: "right",
        });
        y += 12;
        day.punches.forEach((punch) => {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6.5);
          doc.setTextColor(90, 104, 126);
          text(
            punch.adjusted ? `AJ-${punch.adjustmentId}` : `NSR ${punch.nsr}`,
            19,
            y,
          );
          text(eventLabel[punch.eventType], 43, y);
          text(time(punch.occurredAt), 77, y);
          text(
            `${punch.clientName || "Sem cliente"}${punch.unitName ? ` • ${punch.unitName}` : ""}`,
            96,
            y,
          );
          text(
            punch.adjusted
              ? "Ajuste gerencial"
              : punch.connectivity === "offline"
                ? "Offline sincronizado"
                : "Online",
            191,
            y,
            { align: "right" },
          );
          y += 5;
          doc.setTextColor(132, 143, 160);
          text(
            punch.adjusted
              ? `Retificação: ${punch.adjustmentReason} • gestor: ${punch.reviewedByName || "identificado na auditoria"}`
              : `GPS: ${punch.latitude == null ? "não obtido" : `${Number(punch.latitude).toFixed(5)}, ${Number(punch.longitude).toFixed(5)}`} • aparelho: ${new Date(punch.deviceOccurredAt).toLocaleString("pt-BR")} • servidor: ${new Date(punch.serverReceivedAt).toLocaleString("pt-BR")}`,
            43,
            y,
          );
          y += 5;
        });
        y += 4;
      });
      if (report.approvedAdjustments.length) {
        ensureSpace(25);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(31, 43, 65);
        doc.text("Ajustes aprovados no período", 15, y);
        y += 7;
        report.approvedAdjustments.forEach((item) => {
          ensureSpace(14);
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          text(
            `${item.workDate.split("-").reverse().join("/")} • ${eventLabel[item.requestedEventType]} • ${new Date(item.requestedOccurredAt).toLocaleString("pt-BR")}`,
            18,
            y,
          );
          doc.setFont("helvetica", "normal");
          doc.setTextColor(102, 116, 137);
          const lines = doc.splitTextToSize(
            `Motivo: ${item.reason} • Parecer: ${item.reviewNotes || "—"}`,
            170,
          );
          doc.text(lines, 18, y + 5);
          y += 6 + lines.length * 4;
        });
      }
      const pages = doc.getNumberOfPages();
      for (let page = 1; page <= pages; page += 1) {
        doc.setPage(page);
        doc.setFontSize(6.5);
        doc.setTextColor(125, 137, 156);
        doc.text(
          `Gerado em ${new Date(report.generatedAt).toLocaleString("pt-BR")} • Marcações originais imutáveis • Fuso ${report.integrity.timezone}`,
          15,
          290,
        );
        doc.text(`Página ${page}/${pages}`, 195, 290, { align: "right" });
      }
      doc.save(
        `ponto-${report.technician.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${mirrorMonth}.pdf`,
      );
    } catch (_) {
      window.alert("Não foi possível gerar o ponto mensal.");
    } finally {
      setExportingMirror(false);
    }
  };
  if (!data) return <div className="loading">Carregando Ponto...</div>;
  const kpis = [
    ["Colaboradores com registro", data.kpis.registered, "blue"],
    ["Trabalhando agora", data.kpis.working, "green"],
    ["Em intervalo", data.kpis.onBreak, "amber"],
    ["Jornada finalizada", data.kpis.finished, "violet"],
    ["Necessita verificação", data.kpis.needsVerification, "red"],
    ["Ajustes pendentes", data.kpis.pendingAdjustments, "orange"],
  ];
  const operational = ["Visão Geral", "Equipe Hoje", "Marcações"].includes(tab);
  return (
    <div className="time-page">
      <section className="time-hero">
        <div>
          <span>GESTÃO DE JORNADA</span>
          <h2>Ponto</h2>
          <p>
            Marcações, exceções e conformidade operacional em uma visão segura.
          </p>
        </div>
        <div>
          <strong>{filters.date.split("-").reverse().join("/")}</strong>
          <small>Data analisada</small>
        </div>
      </section>
      <nav className="time-tabs">
        {tabs.map((item) => (
          <button
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}
      </nav>
      <section className="panel time-filters">
        <label>
          Data
          <input
            type="date"
            value={filters.date}
            onChange={(event) =>
              setFilters({ ...filters, date: event.target.value })
            }
          />
        </label>
        <label>
          Colaborador
          <select
            value={filters.technicianId}
            onChange={(event) =>
              setFilters({ ...filters, technicianId: event.target.value })
            }
          >
            <option value="">Todos</option>
            {data.technicians.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Cliente
          <select
            value={filters.clientId}
            onChange={(event) =>
              setFilters({
                ...filters,
                clientId: event.target.value,
                unitId: "",
              })
            }
          >
            <option value="">Todos</option>
            {data.clients.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Unidade
          <select
            value={filters.unitId}
            onChange={(event) =>
              setFilters({ ...filters, unitId: event.target.value })
            }
          >
            <option value="">Todas</option>
            {data.units
              .filter(
                (item) =>
                  !filters.clientId ||
                  String(item.clientId) === String(filters.clientId),
              )
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
      </section>
      {tab === "Visão Geral" && (
        <section className="time-kpis">
          {kpis.map(([label, value, tone]) => (
            <article className="panel" key={label}>
              <i className={tone}></i>
              <span>{label}</span>
              <strong>{value}</strong>
              <small>no período selecionado</small>
            </article>
          ))}
        </section>
      )}
      {operational && (
        <section className="panel time-team">
          <header>
            <div>
              <h3>
                {tab === "Marcações" ? "Marcações consideradas" : "Equipe hoje"}
              </h3>
              <p>
                {tab === "Marcações"
                  ? "Marcações originais e retificações identificadas separadamente."
                  : "Clique em um colaborador para consultar a jornada e os locais das marcações."}
              </p>
            </div>
            <span>{data.team.length} colaborador(es)</span>
          </header>
          {tab === "Marcações" ? (
            <div className="time-table">
              <div className="time-row head">
                <span>NSR</span>
                <span>Colaborador</span>
                <span>Evento</span>
                <span>Horário</span>
                <span>Cliente / Unidade</span>
                <span>Origem</span>
              </div>
              {data.recent.map((item) => (
                <div
                  className={`time-row ${item.adjusted ? "adjusted" : ""}`}
                  key={item.id}
                  title={item.adjustmentReason || ""}
                >
                  <b>{item.adjusted ? `AJ-${item.adjustmentId}` : item.nsr}</b>
                  <strong>{item.technicianName}</strong>
                  <span>{eventLabel[item.eventType]}</span>
                  <span>{time(item.occurredAt)}</span>
                  <span>
                    {item.adjusted
                      ? item.adjustmentReason
                      : `${item.clientName || "—"} ${item.unitName ? `• ${item.unitName}` : ""}`}
                  </span>
                  <em>
                    {item.adjusted
                      ? "Ajuste do gestor"
                      : item.connectivity === "offline"
                        ? "Offline sincronizado"
                        : "Online"}
                  </em>
                </div>
              ))}
            </div>
          ) : (
            <div className="time-team-grid">
              {data.team.map((item) => (
                <button
                  type="button"
                  key={item.technicianId}
                  onClick={() => setSelectedWorkday(item)}
                >
                  <div>
                    <b className={item.photoDataUrl ? "photo" : ""}>
                      {item.photoDataUrl ? (
                        <img src={item.photoDataUrl} alt={item.name} />
                      ) : (
                        item.name.slice(0, 2).toUpperCase()
                      )}
                    </b>
                    <span>
                      <strong>{item.name}</strong>
                      <small>
                        {item.clientName || "Sem local associado"}{" "}
                        {item.unitName ? `• ${item.unitName}` : ""}
                      </small>
                    </span>
                    <Icon name="arrow" size={16} />
                  </div>
                  <em className={item.status}>
                    {item.status === "finished"
                      ? "Finalizada"
                      : item.status === "break"
                        ? "Em intervalo"
                        : "Trabalhando"}
                  </em>
                  <footer>
                    <span>
                      Horas registradas <b>{hours(item.workedSeconds)}</b>
                    </span>
                    <span>
                      Última marcação <b>{time(item.lastPunch.occurredAt)}</b>
                    </span>
                  </footer>
                </button>
              ))}
            </div>
          )}
        </section>
      )}
      {tab === "Ajustes" && (
        <>
          <section className="panel time-manager-adjustment">
            <header>
              <div>
                <span>AJUSTE GERENCIAL</span>
                <h3>Registrar ajuste de ponto</h3>
                <p>
                  A marcação original não será alterada. A retificação e sua
                  observação ficarão permanentemente auditadas.
                </p>
              </div>
              <Icon name="edit" size={25} />
            </header>
            {canReview ? (
              <form onSubmit={createManagerAdjustment}>
                <label>
                  Colaborador
                  <select
                    required
                    value={filters.technicianId}
                    onChange={(event) =>
                      setFilters({
                        ...filters,
                        technicianId: event.target.value,
                      })
                    }
                  >
                    <option value="">Selecione</option>
                    {data.technicians.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Data
                  <input
                    type="date"
                    value={filters.date}
                    onChange={(event) => {
                      setFilters({ ...filters, date: event.target.value });
                      setManagerAdjustment({
                        ...managerAdjustment,
                        requestedOccurredAt: `${event.target.value}T08:00`,
                      });
                    }}
                  />
                </label>
                <label>
                  Tipo
                  <select
                    value={managerAdjustment.requestType}
                    onChange={(event) =>
                      setManagerAdjustment({
                        ...managerAdjustment,
                        requestType: event.target.value,
                        originalPunchId: "",
                      })
                    }
                  >
                    <option value="missing_punch">
                      Incluir marcação esquecida
                    </option>
                    <option value="correction">Corrigir uma marcação</option>
                  </select>
                </label>
                {managerAdjustment.requestType === "correction" && (
                  <label>
                    Marcação original
                    <select
                      required
                      value={managerAdjustment.originalPunchId}
                      onChange={(event) => {
                        const original = data.originalPunches.find(
                          (item) => String(item.id) === event.target.value,
                        );
                        setManagerAdjustment({
                          ...managerAdjustment,
                          originalPunchId: event.target.value,
                          requestedEventType: original?.eventType || "entry",
                        });
                      }}
                    >
                      <option value="">Selecione</option>
                      {data.originalPunches
                        .filter(
                          (item) =>
                            !filters.technicianId ||
                            String(item.technicianId) ===
                              String(filters.technicianId),
                        )
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            NSR {item.nsr} • {eventLabel[item.eventType]} •{" "}
                            {time(item.occurredAt)}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
                <label>
                  Evento
                  <select
                    value={managerAdjustment.requestedEventType}
                    onChange={(event) =>
                      setManagerAdjustment({
                        ...managerAdjustment,
                        requestedEventType: event.target.value,
                      })
                    }
                  >
                    {Object.entries(eventLabel).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Data e horário ajustados
                  <input
                    required
                    type="datetime-local"
                    value={managerAdjustment.requestedOccurredAt}
                    onChange={(event) =>
                      setManagerAdjustment({
                        ...managerAdjustment,
                        requestedOccurredAt: event.target.value,
                      })
                    }
                  />
                </label>
                <label className="reason">
                  Observação obrigatória
                  <textarea
                    required
                    minLength="8"
                    placeholder="Explique claramente por que o ponto foi ajustado"
                    value={managerAdjustment.reason}
                    onChange={(event) =>
                      setManagerAdjustment({
                        ...managerAdjustment,
                        reason: event.target.value,
                      })
                    }
                  />
                </label>
                <button className="primary-button" disabled={savingAdjustment}>
                  {savingAdjustment ? "Gravando..." : "Gravar ajuste"}
                </button>
              </form>
            ) : (
              <div className="empty">
                Seu perfil pode consultar ajustes, mas não pode criá-los.
              </div>
            )}
          </section>
          <section className="panel time-team">
            <header>
              <div>
                <h3>Histórico de ajustes</h3>
                <p>
                  Solicitações e retificações com responsável, motivo e
                  situação.
                </p>
              </div>
            </header>
            <div className="time-adjustments">
              {adjustments.length ? (
                adjustments.map((item) => (
                  <article key={item.id}>
                    <div>
                      <strong>{item.technicianName}</strong>
                      <small>
                        {item.workDate.split("-").reverse().join("/")} •{" "}
                        {eventLabel[item.requestedEventType]} • por{" "}
                        {item.requesterName}
                      </small>
                      <p>{item.reason}</p>
                      {item.reviewNotes && (
                        <small>
                          Parecer: {item.reviewNotes} •{" "}
                          {item.reviewerName || "Gestor"}
                        </small>
                      )}
                    </div>
                    <em className={item.status}>
                      {item.status === "pending"
                        ? "Pendente"
                        : item.status === "approved"
                          ? "Aprovado"
                          : "Rejeitado"}
                    </em>
                    {canReview && item.status === "pending" && (
                      <div>
                        <button onClick={() => review(item.id, "rejected")}>
                          Rejeitar
                        </button>
                        <button
                          className="primary-button"
                          onClick={() => review(item.id, "approved")}
                        >
                          Aprovar
                        </button>
                      </div>
                    )}
                  </article>
                ))
              ) : (
                <div className="empty">Nenhum ajuste encontrado.</div>
              )}
            </div>
          </section>
        </>
      )}
      {tab === "Espelhos" && (
        <section className="panel time-mirror">
          <header>
            <div>
              <span>DOCUMENTO MENSAL</span>
              <h3>Ponto mensal do colaborador</h3>
              <p>
                Relatório completo com jornadas, NSR, locais, sincronizações,
                marcações e ajustes aprovados.
              </p>
            </div>
            <Icon name="file" size={30} />
          </header>
          <div>
            <label>
              Colaborador
              <select
                value={filters.technicianId}
                onChange={(event) =>
                  setFilters({ ...filters, technicianId: event.target.value })
                }
              >
                <option value="">Selecione um colaborador</option>
                {data.technicians.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className="time-competence-picker">
              <legend>Competência</legend>
              <label>
                Mês
                <select
                  value={Number(mirrorMonth.slice(5, 7))}
                  onChange={(event) =>
                    setMirrorMonth(
                      `${mirrorMonth.slice(0, 4)}-${String(event.target.value).padStart(2, "0")}`,
                    )
                  }
                >
                  {monthOptions.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Ano
                <select
                  value={mirrorMonth.slice(0, 4)}
                  onChange={(event) =>
                    setMirrorMonth(
                      `${event.target.value}-${mirrorMonth.slice(5, 7)}`,
                    )
                  }
                >
                  {mirrorYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
            </fieldset>
            <button
              className="primary-button"
              disabled={!filters.technicianId || exportingMirror}
              onClick={exportMonthlyMirror}
            >
              <Icon name="download" size={17} />
              {exportingMirror ? "Gerando PDF..." : "Baixar ponto mensal"}
            </button>
          </div>
          <footer>
            <Icon name="check" size={16} />
            <span>
              O PDF identifica marcações originais e ajustes separadamente,
              preservando a rastreabilidade do registro.
            </span>
          </footer>
        </section>
      )}
      {tab === "Auditoria" && !canAudit && (
        <div className="panel time-foundation">
          <Icon name="lock" size={28} />
          <h3>Acesso restrito</h3>
          <p>A auditoria exige permissão específica.</p>
        </div>
      )}
      {[
        "Banco de Horas",
        "Escalas",
        "Afastamentos",
        "Fechamentos",
        "Relatórios",
        "Fiscalização",
        "Configurações",
      ].includes(tab) && (
        <TimeOperationsModule
          tab={tab}
          token={token}
          canManage={canManage}
          technicians={data.technicians}
          selectedTechnicianId={filters.technicianId}
          onOpenMirrors={() => setTab("Espelhos")}
        />
      )}
      {tab === "Auditoria" && canAudit && <TimeAudit token={token} />}
      {selectedWorkday && (
        <div
          className="modal-backdrop"
          onMouseDown={() => setSelectedWorkday(null)}
        >
          <div
            className="modal time-day-detail"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-head">
              <div>
                <span>JORNADA DO DIA</span>
                <h2>{selectedWorkday.name}</h2>
                <p>
                  {filters.date.split("-").reverse().join("/")} •{" "}
                  {selectedWorkday.clientName || "Sem cliente"}
                  {selectedWorkday.unitName
                    ? ` • ${selectedWorkday.unitName}`
                    : ""}
                </p>
              </div>
              <button onClick={() => setSelectedWorkday(null)}>×</button>
            </div>
            <section className="time-day-summary">
              <article>
                <span>Horas registradas</span>
                <strong>{hours(selectedWorkday.workedSeconds)}</strong>
              </article>
              <article>
                <span>Marcações</span>
                <strong>{selectedWorkday.punches.length}</strong>
              </article>
              <article>
                <span>Situação</span>
                <strong>
                  {selectedWorkday.status === "finished"
                    ? "Finalizada"
                    : selectedWorkday.status === "break"
                      ? "Em intervalo"
                      : "Trabalhando"}
                </strong>
              </article>
            </section>
            <div className="time-day-timeline">
              {selectedWorkday.punches.map((punch, index) => {
                const hasLocation =
                  punch.latitude != null && punch.longitude != null;
                const latitude = Number(punch.latitude);
                const longitude = Number(punch.longitude);
                const mapUrl = hasLocation
                  ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.004}%2C${latitude - 0.003}%2C${longitude + 0.004}%2C${latitude + 0.003}&layer=mapnik&marker=${latitude}%2C${longitude}`
                  : "";
                return (
                  <article
                    className={punch.adjusted ? "adjusted" : ""}
                    key={punch.id}
                  >
                    <i>
                      <Icon
                        name={punch.adjusted ? "edit" : "clock"}
                        size={16}
                      />
                    </i>
                    <div>
                      <header>
                        <div>
                          <strong>{eventLabel[punch.eventType]}</strong>
                          <small>
                            {punch.adjusted
                              ? `AJ-${punch.adjustmentId}`
                              : `NSR ${punch.nsr}`}{" "}
                            • {time(punch.occurredAt)}
                          </small>
                        </div>
                        <em>
                          {punch.adjusted
                            ? "Ajuste gerencial"
                            : punch.connectivity === "offline"
                              ? "Offline sincronizado"
                              : "Online"}
                        </em>
                      </header>
                      <section>
                        <div>
                          <span>Cliente / Unidade</span>
                          <b>
                            {punch.clientName ||
                              selectedWorkday.clientName ||
                              "Não associado"}
                            {punch.unitName || selectedWorkday.unitName
                              ? ` • ${punch.unitName || selectedWorkday.unitName}`
                              : ""}
                          </b>
                        </div>
                        <div>
                          <span>Localização</span>
                          <b>
                            {hasLocation
                              ? "Registrada no mapa"
                              : punch.adjusted
                                ? "Não se aplica ao ajuste"
                                : "Não obtida"}
                          </b>
                        </div>
                        <div>
                          <span>Precisão</span>
                          <b>
                            {punch.accuracyMeters != null
                              ? `${Math.round(Number(punch.accuracyMeters))} m`
                              : "—"}
                          </b>
                        </div>
                        <div>
                          <span>Horário recebido</span>
                          <b>
                            {punch.serverReceivedAt
                              ? new Date(punch.serverReceivedAt).toLocaleString(
                                  "pt-BR",
                                )
                              : "—"}
                          </b>
                        </div>
                      </section>
                      {punch.adjustmentReason && (
                        <p>
                          <strong>Motivo do ajuste:</strong>{" "}
                          {punch.adjustmentReason}
                        </p>
                      )}
                      {hasLocation && (
                        <div className="time-punch-map">
                          <iframe
                            title={`Local da ${eventLabel[punch.eventType]} de ${selectedWorkday.name}`}
                            src={mapUrl}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                          <footer>
                            <span>
                              <Icon name="pin" size={15} /> Local da marcação •
                              precisão de{" "}
                              {Math.round(Number(punch.accuracyMeters || 0))} m
                            </span>
                            <a
                              href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Ampliar no mapa <Icon name="arrow" size={13} />
                            </a>
                          </footer>
                        </div>
                      )}
                    </div>
                    {index < selectedWorkday.punches.length - 1 && (
                      <span className="line" />
                    )}
                  </article>
                );
              })}
            </div>
            <div className="modal-actions">
              <button onClick={() => setSelectedWorkday(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimeOperationsModule({
  tab,
  token,
  canManage,
  technicians,
  selectedTechnicianId,
  onOpenMirrors,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  const availableYears = Array.from(
    { length: new Date().getFullYear() - 2023 + 2 },
    (_, index) => 2024 + index,
  );
  const [month, setMonth] = useState(today.slice(0, 7));
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reportView, setReportView] = useState("mirror");
  const reportStart = `${month}-01`;
  const reportEnd = `${month}-${String(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate()).padStart(2, "0")}`;
  const [exportingReport, setExportingReport] = useState(false);
  const [form, setForm] = useState({
    technicianId: "",
    name: "Jornada padrão",
    dailyHours: 8,
    effectiveFrom: today,
    leaveType: "Férias",
    startsOn: today,
    endsOn: today,
    notes: "",
    periodStart: `${today.slice(0, 7)}-01`,
    periodEnd: today,
    fileType: "AFD",
  });
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = async () => {
    const response = await fetch(`${apiBase}/time/management?month=${month}`, {
      headers,
    });
    if (!response.ok) throw new Error("management_load_error");
    setData(await response.json());
  };
  useEffect(() => {
    load().catch(() => setData(null));
  }, [month, tab]);
  const submit = async (path, payload) => {
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("save_error");
      await load();
    } catch (_) {
      window.alert("Não foi possível gravar. Confira todos os campos.");
    } finally {
      setSaving(false);
    }
  };
  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/time/settings`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data.settings),
      });
      if (!response.ok) throw new Error("settings_error");
      await load();
    } catch (_) {
      window.alert("Não foi possível salvar as configurações.");
    } finally {
      setSaving(false);
    }
  };
  const duration = (seconds) => {
    const sign = Number(seconds) < 0 ? "-" : "";
    const value = Math.abs(Number(seconds) || 0);
    return `${sign}${String(Math.floor(value / 3600)).padStart(2, "0")}:${String(Math.floor((value % 3600) / 60)).padStart(2, "0")}`;
  };
  const reportClock = (value) =>
    value
      ? new Date(value).toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";
  const loadPeriodReport = async (individual = false) => {
    if (individual && !selectedTechnicianId) {
      window.alert("Selecione um colaborador no filtro superior.");
      return null;
    }
    const query = new URLSearchParams({ start: reportStart, end: reportEnd });
    if (individual) query.set("technicianId", selectedTechnicianId);
    const response = await fetch(`${apiBase}/time/reports/period?${query}`, {
      headers,
    });
    if (!response.ok) throw new Error("period_report_error");
    return response.json();
  };
  const reportPdfHeader = async (doc, title, report) => {
    doc.setFillColor(21, 25, 26);
    doc.rect(0, 0, 297, 29, "F");
    try {
      const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
      doc.addImage(logo, "PNG", 12, 7, 52, 11.64, undefined, "SLOW");
    } catch {}
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(title, 285, 12, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(201, 211, 228);
    doc.text(
      `DE ${report.period.start.split("-").reverse().join("/")} ATÉ ${report.period.end.split("-").reverse().join("/")}`,
      285,
      20,
      { align: "right" },
    );
    doc.setDrawColor(187, 161, 108);
    doc.setLineWidth(0.7);
    doc.line(12, 28, 285, 28);
  };
  const exportTimeCard = async () => {
    setExportingReport(true);
    try {
      const report = await loadPeriodReport(false);
      if (!report) return;
      if (!report.people.length) {
        window.alert("Não há colaboradores no período selecionado.");
        return;
      }
      const doc = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "landscape",
      });
      const week = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
      const colors = {
        normal: [48, 112, 242],
        night: [112, 78, 238],
        extraDay: [245, 158, 11],
        extraNight: [236, 72, 153],
        extra100: [244, 63, 94],
        expected: [6, 182, 212],
      };
      const footer = (page, total) => {
        doc.setDrawColor(224, 230, 239);
        doc.line(12, 201, 285, 201);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(91, 104, 125);
        doc.text(
          `Página ${page} de ${total}  |  Emitido em ${new Date(report.generatedAt).toLocaleString("pt-BR")}`,
          12,
          206,
        );
        doc.text("Cálculo baseado na escala vigente", 285, 206, {
          align: "right",
        });
      };
      const metricCard = (label, value, left, color) => {
        doc.setFillColor(248, 250, 253);
        doc.roundedRect(left, 49, 38, 19, 2.5, 2.5, "F");
        doc.setFillColor(...color);
        doc.roundedRect(left, 49, 2, 19, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.2);
        doc.setTextColor(83, 99, 126);
        doc.text(label.toUpperCase(), left + 6, 56);
        doc.setFontSize(12);
        doc.setTextColor(24, 37, 61);
        doc.text(value, left + 6, 65);
      };
      const drawMonthlyHoursChart = (person, top, height) => {
        const chartLeft = 18,
          chartRight = 283,
          chartTop = top + 9,
          chartBottom = top + height - 10;
        const chartHeight = chartBottom - chartTop;
        const dailyHours = person.days.map((day) => ({
          day,
          expected: Number(day.expectedSeconds || 0) / 3600,
          parts: [
            Number(day.daytimeNormalSeconds || 0) / 3600,
            Number(day.normalNightSeconds || 0) / 3600,
            Number(day.overtime60DaySeconds || 0) / 3600,
            Number(day.overtime60NightSeconds || 0) / 3600,
            Number(day.overtime100Seconds || 0) / 3600,
          ],
        }));
        const peak = Math.max(
          8,
          ...dailyHours.map((item) =>
            Math.max(
              item.expected,
              item.parts.reduce((sum, value) => sum + value, 0),
            ),
          ),
        );
        const scaleMax = Math.ceil(peak / 2) * 2;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(25, 39, 64);
        doc.text("HORAS TRABALHADAS POR DIA", 12, top + 3);
        let legendX = 82;
        [
          ["Normal", colors.normal],
          ["Extra dia", colors.extraDay],
          ["Extra noite", colors.extraNight],
          ["Noturno", colors.night],
          ["Falta", colors.extra100],
          ["Previsto", colors.expected],
        ].forEach(([label, color]) => {
          doc.setFillColor(...color);
          doc.rect(legendX, top, 3.2, 3.2, "F");
          doc.setFont("helvetica", "normal");
          doc.setFontSize(5.5);
          doc.setTextColor(91, 104, 125);
          doc.text(label, legendX + 4.5, top + 2.8);
          legendX += 31;
        });
        [0, 0.25, 0.5, 0.75, 1].forEach((ratio) => {
          const gridY = chartBottom - chartHeight * ratio;
          doc.setDrawColor(222, 229, 239);
          doc.setLineWidth(0.2);
          doc.line(chartLeft, gridY, chartRight, gridY);
          doc.setFontSize(5.5);
          doc.setTextColor(112, 125, 146);
          doc.text(
            `${Math.round(scaleMax * ratio)}h`,
            chartLeft - 2,
            gridY + 1,
            { align: "right" },
          );
        });
        const slot = (chartRight - chartLeft) / Math.max(1, dailyHours.length);
        const barWidth = Math.min(6.5, slot * 0.68);
        dailyHours.forEach((item, index) => {
          const center = chartLeft + slot * index + slot / 2;
          let currentBottom = chartBottom;
          [
            colors.normal,
            colors.night,
            colors.extraDay,
            colors.extraNight,
            colors.extra100,
          ].forEach((color, partIndex) => {
            const partHeight = (chartHeight * item.parts[partIndex]) / scaleMax;
            if (partHeight > 0) {
              const softColor = color.map((channel) =>
                Math.round(channel + (255 - channel) * 0.72),
              );
              doc.setFillColor(...softColor);
              doc.roundedRect(
                center - barWidth / 2 - 0.45,
                currentBottom - partHeight - 0.45,
                barWidth + 0.9,
                partHeight + 0.9,
                0.9,
                0.9,
                "F",
              );
              doc.setFillColor(...color);
              doc.roundedRect(
                center - barWidth / 2,
                currentBottom - partHeight,
                barWidth,
                partHeight,
                0.6,
                0.6,
                "F",
              );
              currentBottom -= partHeight;
            }
          });
          if (!item.day.expectedSeconds && !item.parts.some(Boolean)) {
            doc.setFillColor(232, 236, 242);
            doc.rect(
              center - barWidth / 2,
              chartBottom - chartHeight * 0.92,
              barWidth,
              chartHeight * 0.92,
              "F",
            );
          }
          if (item.day.expectedSeconds && !item.parts.some(Boolean)) {
            const missingHeight =
              (chartHeight * Math.min(item.expected, scaleMax)) / scaleMax;
            doc.setFillColor(255, 226, 230);
            doc.roundedRect(
              center - barWidth / 2,
              chartBottom - missingHeight,
              barWidth,
              missingHeight,
              0.6,
              0.6,
              "F",
            );
            doc.setDrawColor(...colors.extra100);
            doc.setLineWidth(0.45);
            doc.line(
              center - barWidth / 2,
              chartBottom - missingHeight,
              center + barWidth / 2,
              chartBottom - missingHeight,
            );
          }
          doc.setFontSize(4.8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(69, 82, 104);
          doc.text(
            String(Number(item.day.date.slice(-2))),
            center,
            chartBottom + 4,
            { align: "center" },
          );
          doc.setFontSize(4);
          doc.setFont("helvetica", "normal");
          doc.text(week[item.day.weekDay], center, chartBottom + 7.5, {
            align: "center",
          });
        });
        doc.setDrawColor(...colors.expected);
        doc.setLineWidth(0.28);
        dailyHours.forEach((item, index) => {
          if (index === dailyHours.length - 1) return;
          const next = dailyHours[index + 1];
          const x1 = chartLeft + slot * index + slot / 2;
          const x2 = chartLeft + slot * (index + 1) + slot / 2;
          const y1 =
            chartBottom -
            (chartHeight * Math.min(item.expected, scaleMax)) / scaleMax;
          const y2 =
            chartBottom -
            (chartHeight * Math.min(next.expected, scaleMax)) / scaleMax;
          doc.line(x1, y1, x2, y2);
        });
      };
      for (
        let personIndex = 0;
        personIndex < report.people.length;
        personIndex += 1
      ) {
        const person = report.people[personIndex];
        if (personIndex) doc.addPage();
        const totalPages = report.people.length * 2;
        const pageBase = personIndex * 2;
        await reportPdfHeader(doc, "Cartão de Ponto", report);
        doc.setTextColor(31, 43, 65);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text(person.name.toUpperCase(), 12, 37);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(79, 96, 124);
        doc.text(
          `${person.functionName} • COL-${String(person.id).padStart(4, "0")} • ${report.company.name}`,
          12,
          43,
        );
        const schedule =
          person.days.find((day) => day.schedule)?.schedule || {};
        const expected = Number(person.totals.expectedSeconds) || 0;
        const worked = Number(person.totals.workedSeconds) || 0;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(117, 103, 71);
        doc.text(
          `Escala: ${person.scheduleNames.join(", ") || "Não informada"}`,
          12,
          47,
        );
        metricCard("Horas previstas", duration(expected), 12, colors.normal);
        metricCard("Horas trabalhadas", duration(worked), 54, [22, 183, 139]);
        metricCard(
          "Horas noturnas",
          duration(person.totals.nightSeconds),
          96,
          colors.night,
        );
        metricCard(
          "Horas extras",
          duration(person.totals.overtimeSeconds),
          138,
          colors.extraDay,
        );
        const compliance = expected
          ? Math.min(100, Math.round((worked / expected) * 100))
          : 0;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(29, 43, 66);
        doc.text(`CUMPRIMENTO DA JORNADA  ${compliance}%`, 12, 74);
        doc.setFillColor(229, 235, 244);
        doc.roundedRect(12, 77, 78, 4, 2, 2, "F");
        doc.setFillColor(157, 133, 84);
        doc.roundedRect(
          12,
          77,
          Math.max(2, (78 * compliance) / 100),
          4,
          2,
          2,
          "F",
        );
        doc.text("COMPOSIÇÃO DAS HORAS", 96, 74);
        const composition = [
          [person.totals.daytimeNormalSeconds, colors.normal],
          [person.totals.normalNightSeconds, colors.night],
          [person.totals.overtimeSeconds, colors.extraDay],
          [person.totals.missingSeconds, colors.extra100],
        ];
        const compositionTotal = Math.max(
          1,
          composition.reduce((sum, item) => sum + Number(item[0] || 0), 0),
        );
        let compositionX = 96;
        composition.forEach(([seconds, color]) => {
          const width = (80 * Number(seconds || 0)) / compositionTotal;
          if (width > 0) {
            doc.setFillColor(...color);
            doc.rect(compositionX, 77, width, 4, "F");
            compositionX += width;
          }
        });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(5.3);
        doc.setTextColor(83, 99, 126);
        doc.text("Diurnas", 96, 85);
        doc.text("Noturnas", 117, 85);
        doc.text("Extras", 141, 85);
        doc.text("Faltas", 163, 85);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(27, 41, 64);
        doc.text("ESCALA SEMANAL", 190, 35);
        const scheduleRows = [
          [
            "SEG",
            schedule.entry,
            schedule.breakStart || schedule.break_start,
            schedule.breakEnd || schedule.break_end,
            schedule.exit,
          ],
          [
            "TER",
            schedule.entry,
            schedule.breakStart || schedule.break_start,
            schedule.breakEnd || schedule.break_end,
            schedule.exit,
          ],
          [
            "QUA",
            schedule.entry,
            schedule.breakStart || schedule.break_start,
            schedule.breakEnd || schedule.break_end,
            schedule.exit,
          ],
          [
            "QUI",
            schedule.entry,
            schedule.breakStart || schedule.break_start,
            schedule.breakEnd || schedule.break_end,
            schedule.exit,
          ],
          [
            "SEX",
            schedule.entry,
            schedule.breakStart || schedule.break_start,
            schedule.breakEnd || schedule.break_end,
            schedule.exit,
          ],
          ["SÁB", null, null, null, null],
          ["DOM", null, null, null, null],
        ];
        const scaleX = [190, 211, 232, 253, 276];
        doc.setFontSize(5.3);
        ["DIA", "ENTRADA", "SAÍDA 1", "ENTRADA 2", "SAÍDA 2"].forEach(
          (label, i) =>
            doc.text(label, scaleX[i], 41, { align: i ? "center" : "left" }),
        );
        let scaleY = 47;
        scheduleRows.forEach((row) => {
          doc.setFillColor(scaleY % 2 ? 248 : 242, 246, 251);
          doc.rect(188, scaleY - 3.7, 96, 5, "F");
          row.forEach((value, i) => {
            doc.setFont("helvetica", i === 0 ? "bold" : "normal");
            doc.setFontSize(5.8);
            doc.setTextColor(28, 43, 67);
            doc.text(value || "—", scaleX[i], scaleY, {
              align: i ? "center" : "left",
            });
          });
          scaleY += 5.2;
        });
        drawMonthlyHoursChart(person, 91, 56);
        const occurrences = person.days
          .filter(
            (day) =>
              day.missingSeconds ||
              day.overtimeSeconds ||
              (!day.complete &&
                (day.entry || day.break_start || day.break_end || day.exit)),
          )
          .slice(0, 6);
        doc.setFillColor(248, 250, 253);
        doc.roundedRect(12, 151, 174, 43, 2, 2, "F");
        doc.roundedRect(191, 151, 94, 43, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(28, 42, 65);
        doc.text("OCORRÊNCIAS DO PERÍODO", 17, 158);
        doc.text("RESUMO DO PERÍODO", 196, 158);
        let oy = 164;
        doc.setFontSize(6);
        occurrences.forEach((day) => {
          const detail = day.missingSeconds
            ? `Falta/atraso ${duration(day.missingSeconds)}`
            : day.overtimeSeconds
              ? `Jornada excedente ${duration(day.overtimeSeconds)}`
              : "Batida incompleta";
          doc.text(day.date.split("-").reverse().join("/"), 17, oy);
          doc.text(detail, 45, oy);
          oy += 5;
        });
        if (!occurrences.length) {
          doc.setFont("helvetica", "normal");
          doc.text("Nenhuma ocorrência no período.", 17, 166);
        }
        const summary = [
          ["Normais previstas", expected],
          ["Trabalhadas", worked],
          ["Saldo", worked - expected],
          ["Extra 60% dia", person.totals.overtime60DaySeconds],
          ["Extra 60% noite", person.totals.overtime60NightSeconds],
          ["Extra 100%", person.totals.overtime100Seconds],
          ["Faltas", person.totals.missingSeconds],
        ];
        let sy = 164;
        summary.forEach(([label, value]) => {
          doc.setFont("helvetica", "normal");
          doc.text(label, 196, sy);
          doc.setFont("helvetica", "bold");
          doc.text(duration(value), 279, sy, { align: "right" });
          sy += 4.2;
        });
        footer(pageBase + 1, totalPages);

        doc.addPage();
        await reportPdfHeader(doc, "Cartão de Ponto", report);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(25, 39, 64);
        doc.text("ESPELHO DE PONTO — DETALHADO", 12, 35);
        doc.setFontSize(8);
        doc.text(person.name.toUpperCase(), 98, 35);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(5.8);
        doc.setTextColor(91, 106, 130);
        doc.text(
          `${person.functionName} • COL-${String(person.id).padStart(4, "0")} • ${report.company.name}`,
          98,
          40,
        );
        doc.text(
          `Período: ${report.period.start.split("-").reverse().join("/")} a ${report.period.end.split("-").reverse().join("/")} • Escala: ${person.scheduleNames.join(", ") || "Não informada"}`,
          285,
          40,
          { align: "right" },
        );
        const tableX = [
          12, 34, 49, 70, 92, 114, 137, 160, 181, 202, 224, 247, 285,
        ];
        const tableHeads = [
          "DATA",
          "DIA",
          "ENTRADA",
          "SAÍDA ALMOÇO",
          "RETORNO",
          "SAÍDA",
          "TRABALHADO",
          "EXTRA DIA",
          "EXTRA NOITE",
          "NOTURNO",
          "FALTAS",
          "SALDO",
          "SITUAÇÃO",
        ];
        doc.setFillColor(21, 25, 26);
        doc.roundedRect(10, 44, 277, 8, 1.5, 1.5, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(5.3);
        tableHeads.forEach((head, index) =>
          doc.text(head, tableX[index], 49, {
            align: index === 12 ? "right" : "left",
          }),
        );
        let y = 56;
        person.days.forEach((day) => {
          const hasPunch =
            day.entry || day.break_start || day.break_end || day.exit;
          const weekend = day.weekDay === 0 || day.weekDay === 6;
          if (day.holidayName) doc.setFillColor(255, 239, 241);
          else if (weekend) doc.setFillColor(250, 246, 238);
          else doc.setFillColor(255, 255, 255);
          doc.rect(10, y - 3, 277, 4.05, "F");
          doc.setTextColor(30, 43, 64);
          doc.setFont(
            "helvetica",
            weekend || day.holidayName ? "bold" : "normal",
          );
          doc.setFontSize(5.8);
          const status = day.holidayName
            ? `Feriado — ${day.holidayName}`
            : day.leaveType ||
              (!day.expectedSeconds && !hasPunch
                ? "Folga"
                : day.complete
                  ? "Completo"
                  : "Incompleto");
          const balance =
            Number(day.workedSeconds || 0) - Number(day.expectedSeconds || 0);
          const values = [
            day.date.split("-").reverse().join("/"),
            week[day.weekDay],
            reportClock(day.entry),
            reportClock(day.break_start),
            reportClock(day.break_end),
            reportClock(day.exit),
            duration(day.workedSeconds),
            duration(day.overtime60DaySeconds),
            duration(day.overtime60NightSeconds),
            duration(day.normalNightSeconds),
            duration(day.missingSeconds),
            duration(balance),
            status,
          ];
          values.forEach((value, index) =>
            doc.text(
              String(value).slice(0, index === 12 ? 24 : 15),
              tableX[index],
              y,
              { align: index === 12 ? "right" : "left" },
            ),
          );
          doc.setDrawColor(223, 229, 238);
          doc.line(10, y + 1.05, 287, y + 1.05);
          y += 4.05;
        });
        doc.setFillColor(239, 244, 251);
        doc.roundedRect(10, 190, 277, 9, 1.5, 1.5, "F");
        doc.setFont("helvetica", "bold");
        doc.setTextColor(28, 42, 65);
        doc.setFontSize(6.2);
        doc.text("TOTAIS", 13, 195.5);
        [
          ["PREVISTAS", expected],
          ["TRABALHADAS", worked],
          ["EXTRA DIA", person.totals.overtime60DaySeconds],
          ["EXTRA NOITE", person.totals.overtime60NightSeconds],
          ["EXTRA 100%", person.totals.overtime100Seconds],
          ["FALTAS", person.totals.missingSeconds],
          ["SALDO", worked - expected],
        ].forEach(([label, value], index) => {
          const left = 39 + index * 34;
          doc.setFontSize(4.6);
          doc.text(label, left, 194);
          doc.setFontSize(7);
          doc.text(duration(value), left, 197.5);
        });
        footer(pageBase + 2, totalPages);
      }
      doc.save(
        `cartoes-ponto-equipe-${report.period.start}-${report.period.end}.pdf`,
      );
    } catch (_) {
      window.alert("Não foi possível gerar o Cartão de Ponto.");
    } finally {
      setExportingReport(false);
    }
  };
  const exportPeriodStatement = async () => {
    setExportingReport(true);
    try {
      const report = await loadPeriodReport(false);
      if (!report?.people?.length) {
        window.alert("Não há colaboradores no período selecionado.");
        return;
      }
      const doc = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "landscape",
      });
      await reportPdfHeader(doc, "Extrato por Período", report);
      const x = [10, 67, 112, 136, 159, 182, 205, 228, 251, 286];
      const heads = [
        "NOME DA EMPRESA",
        "NOME DO COLABORADOR",
        "DIURNAS NORMAIS",
        "TOTAL TRABALHADO",
        "NOTURNAS NORMAIS",
        "FALTAS E ATRASOS",
        "EXTRA 60% DIURNA",
        "EXTRA 60% NOTURNA",
        "EXTRA 100%",
        "EXTRAS TOTAL",
      ];
      const total = (key) =>
        report.people.reduce(
          (sum, person) => sum + Number(person.totals[key] || 0),
          0,
        );
      const expected = total("expectedSeconds");
      const worked = total("workedSeconds");
      const compliance = expected
        ? Math.min(100, Math.round((worked / expected) * 100))
        : 0;
      const card = (label, value, left, color) => {
        doc.setFillColor(248, 250, 253);
        doc.roundedRect(left, 35, 50, 17, 2.2, 2.2, "F");
        doc.setFillColor(...color);
        doc.roundedRect(left, 35, 2, 17, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5.2);
        doc.setTextColor(112, 126, 149);
        doc.text(label.toUpperCase(), left + 6, 40.5);
        doc.setTextColor(27, 40, 65);
        doc.setFontSize(10.5);
        doc.text(value, left + 6, 48);
      };
      card("Colaboradores", String(report.people.length), 10, [52, 116, 242]);
      card("Horas trabalhadas", duration(worked), 64, [34, 185, 140]);
      card(
        "Horas noturnas",
        duration(total("nightSeconds")),
        118,
        [125, 92, 246],
      );
      card(
        "Horas extras",
        duration(total("overtimeSeconds")),
        172,
        [239, 160, 45],
      );
      card(
        "Faltas e atrasos",
        duration(total("missingSeconds")),
        226,
        [238, 86, 101],
      );
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.4);
      doc.setTextColor(61, 77, 103);
      doc.text(`CUMPRIMENTO DA JORNADA  ${compliance}%`, 12, 59);
      doc.setFillColor(232, 237, 245);
      doc.roundedRect(12, 61, 92, 4, 2, 2, "F");
      doc.setFillColor(
        compliance >= 100 ? 35 : 52,
        compliance >= 100 ? 185 : 116,
        compliance >= 100 ? 140 : 242,
      );
      doc.roundedRect(
        12,
        61,
        Math.max(2, (92 * compliance) / 100),
        4,
        2,
        2,
        "F",
      );
      doc.text("COMPOSIÇÃO CONSOLIDADA DAS HORAS", 119, 59);
      const composition = [
        [total("daytimeNormalSeconds"), [52, 116, 242], "Diurnas"],
        [total("nightSeconds"), [125, 92, 246], "Noturnas"],
        [total("overtimeSeconds"), [239, 160, 45], "Extras"],
        [total("missingSeconds"), [238, 86, 101], "Faltas"],
      ];
      const compositionTotal = Math.max(
        1,
        composition.reduce((sum, item) => sum + item[0], 0),
      );
      let barX = 119;
      composition.forEach(([seconds, color]) => {
        const width = (92 * seconds) / compositionTotal;
        if (width > 0) {
          doc.setFillColor(...color);
          doc.rect(barX, 61, width, 4, "F");
          barX += width;
        }
      });
      composition.forEach(([, color, label], index) => {
        doc.setFillColor(...color);
        doc.circle(120 + index * 23, 69.5, 1.1, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(4.7);
        doc.setTextColor(102, 116, 139);
        doc.text(label, 123 + index * 23, 70.2);
      });
      let y = 82;
      const drawTableHeader = () => {
        doc.setFillColor(239, 243, 249);
        doc.rect(10, y - 6, 277, 9, "F");
        doc.setTextColor(47, 60, 82);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5.1);
        heads.forEach((head, index) =>
          doc.text(
            doc.splitTextToSize(head, index < 2 ? 52 : 20),
            x[index],
            y - 1,
            { align: index === 9 ? "right" : "left" },
          ),
        );
        y += 8;
      };
      drawTableHeader();
      doc.setFont("helvetica", "normal");
      report.people.forEach((person) => {
        if (y > 190) {
          doc.addPage();
          y = 39;
          drawTableHeader();
        }
        const values = [
          report.company.name,
          person.name,
          duration(person.totals.daytimeNormalSeconds),
          duration(person.totals.workedSeconds),
          duration(person.totals.normalNightSeconds),
          duration(person.totals.missingSeconds),
          duration(person.totals.overtime60DaySeconds),
          duration(person.totals.overtime60NightSeconds),
          duration(person.totals.overtime100Seconds),
          duration(person.totals.overtimeSeconds),
        ];
        doc.setFontSize(5.3);
        values.forEach((value, index) =>
          doc.text(String(value), x[index], y, {
            align: index === 9 ? "right" : "left",
            maxWidth: index === 0 ? 53 : index === 1 ? 41 : 20,
          }),
        );
        doc.setDrawColor(210, 217, 228);
        doc.line(10, y + 1.5, 287, y + 1.5);
        y += 6;
      });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.5);
      doc.text(`TOTAL: ${report.people.length} COLABORADOR(ES)`, 12, y + 1);
      [
        "daytimeNormalSeconds",
        "workedSeconds",
        "normalNightSeconds",
        "missingSeconds",
        "overtime60DaySeconds",
        "overtime60NightSeconds",
        "overtime100Seconds",
        "overtimeSeconds",
      ].forEach((key, index) =>
        doc.text(duration(total(key)), x[index + 2], y + 1, {
          align: index === 7 ? "right" : "left",
        }),
      );
      doc.setFont("helvetica", "normal");
      doc.setTextColor(96, 110, 132);
      doc.text(
        "Tolerância: 10 min na entrada e saída • Extra 60% diurna: fim da escala até 22h • Extra 60% noturna: 22h às 6h • Extra 100%: feriados nacionais.",
        12,
        202,
      );
      doc.text(
        `Valores calculados conforme a escala vigente • Emitido em ${new Date(report.generatedAt).toLocaleString("pt-BR")}`,
        12,
        207,
      );
      doc.save(`extrato-ponto-${report.period.start}-${report.period.end}.pdf`);
    } catch (_) {
      window.alert("Não foi possível gerar o Extrato por Período.");
    } finally {
      setExportingReport(false);
    }
  };
  const exportClosureReport = async (closure) => {
    if (!selectedTechnicianId) {
      window.alert(
        "Selecione um colaborador no filtro superior para gerar o relatório.",
      );
      return;
    }
    try {
      const response = await fetch(
        `${apiBase}/time/closure-report?closureId=${closure.id}&technicianId=${selectedTechnicianId}`,
        { headers },
      );
      if (!response.ok) throw new Error("closure_report_error");
      const report = await response.json();
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const labels = [
        ["Data", 15],
        ["Entrada", 48],
        ["Almoço", 76],
        ["Retorno", 104],
        ["Saída", 132],
        ["Total", 160],
        ["Situação", 184],
      ];
      const clock = (value) =>
        value
          ? new Date(value).toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—";
      doc.setFillColor(21, 25, 26);
      doc.rect(0, 0, 210, 34, "F");
      try {
        const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
        doc.addImage(logo, "PNG", 15, 7, 48, 10.74, undefined, "SLOW");
      } catch {}
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("FECHAMENTO DE PONTO", 195, 13, { align: "right" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(201, 211, 228);
      doc.text("Relatório mensal do colaborador", 195, 21, {
        align: "right",
      });
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(`Versão v${report.closure.version}`, 195, 28, {
        align: "right",
      });
      doc.setTextColor(31, 43, 65);
      doc.setFontSize(13);
      doc.text(report.technician.name, 15, 44);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(103, 117, 139);
      doc.text(
        `${report.technician.functionName} • COL-${String(report.technician.id).padStart(4, "0")} • ${report.technician.email || "E-mail não informado"}`,
        15,
        50,
      );
      doc.setFontSize(7);
      doc.text(
        `Período: ${report.closure.periodStart.split("-").reverse().join("/")} a ${report.closure.periodEnd.split("-").reverse().join("/")} • Unidade residente: ${report.technician.residentUnits || "Não vinculada"}`,
        15,
        56,
      );
      doc.setFontSize(6.5);
      doc.text("Sábados", 15, 63);
      doc.setFillColor(232, 241, 255);
      doc.rect(27, 59.5, 8, 4.5, "F");
      doc.text("Domingos", 40, 63);
      doc.setFillColor(255, 239, 220);
      doc.rect(54, 59.5, 8, 4.5, "F");
      let y = 72;
      const drawHead = () => {
        doc.setFillColor(242, 246, 251);
        doc.rect(12, y - 5, 186, 9, "F");
        doc.setFont("helvetica", "bold");
        doc.setTextColor(92, 108, 132);
        doc.setFontSize(6.5);
        labels.forEach(([label, x]) =>
          doc.text(label, x, y, { align: x === 184 ? "center" : "left" }),
        );
        y += 8;
      };
      drawHead();
      report.days.forEach((day) => {
        if (y > 278) {
          doc.addPage();
          y = 18;
          drawHead();
        }
        const weekDay = new Date(`${day.date}T12:00:00`).getDay();
        const saturday = weekDay === 6;
        const sunday = weekDay === 0;
        const weekend = saturday || sunday;
        const hasPunch = Boolean(
          day.entry || day.break_start || day.break_end || day.exit,
        );
        if (day.holidayName) {
          doc.setFillColor(255, 239, 241);
          doc.rect(12, y - 5, 186, 8, "F");
        } else if (saturday) {
          doc.setFillColor(242, 247, 255);
          doc.rect(12, y - 5, 186, 8, "F");
        } else if (sunday) {
          doc.setFillColor(255, 247, 237);
          doc.rect(12, y - 5, 186, 8, "F");
        }
        doc.setFont(
          "helvetica",
          weekend || day.holidayName ? "bold" : "normal",
        );
        doc.setFontSize(7.5);
        doc.setTextColor(
          sunday ? 176 : saturday ? 45 : 42,
          sunday ? 104 : saturday ? 100 : 55,
          sunday ? 32 : saturday ? 180 : 78,
        );
        doc.text(
          `${day.date.split("-").reverse().join("/")} ${saturday ? "• Sáb" : sunday ? "• Dom" : ""}`,
          15,
          y,
        );
        doc.setFont("helvetica", "normal");
        doc.setTextColor(42, 55, 78);
        doc.text(clock(day.entry), 48, y);
        doc.text(clock(day.break_start), 76, y);
        doc.text(clock(day.break_end), 104, y);
        doc.text(clock(day.exit), 132, y);
        doc.setFont("helvetica", "bold");
        doc.text(duration(day.workedSeconds), 160, y);
        doc.setFontSize(6.5);
        doc.text(
          day.holidayName
            ? `Feriado • ${day.holidayName}`
            : weekend && !hasPunch
              ? "Fim de semana"
              : day.complete
                ? day.adjusted
                  ? "Completo • ajuste"
                  : "Completo"
                : "Incompleto",
          184,
          y,
          { align: "center" },
        );
        doc.setDrawColor(230, 235, 242);
        doc.line(12, y + 3, 198, y + 3);
        y += 8;
      });
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.setFillColor(232, 241, 255);
      doc.roundedRect(125, y + 2, 73, 17, 2, 2, "F");
      doc.setFontSize(8);
      doc.setTextColor(67, 84, 109);
      doc.text("TOTAL DO PERÍODO", 130, y + 9);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(117, 103, 71);
      doc.text(duration(report.totalWorkedSeconds), 193, y + 13, {
        align: "right",
      });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(122, 135, 155);
      doc.text(
        `Fechado por ${report.closure.closedByName || "gestor"} • Gerado em ${new Date(report.generatedAt).toLocaleString("pt-BR")}`,
        15,
        290,
      );
      doc.save(
        `fechamento-${report.technician.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${report.closure.periodStart}-${report.closure.periodEnd}.pdf`,
      );
    } catch (_) {
      window.alert("Não foi possível gerar o relatório do fechamento.");
    }
  };
  if (!data)
    return <div className="panel time-foundation">Carregando {tab}...</div>;
  const periodFields = (
    <>
      <label>
        Início
        <input
          type="date"
          value={form.periodStart}
          onChange={(event) =>
            setForm({ ...form, periodStart: event.target.value })
          }
        />
      </label>
      <label>
        Fim
        <input
          type="date"
          value={form.periodEnd}
          onChange={(event) =>
            setForm({ ...form, periodEnd: event.target.value })
          }
        />
      </label>
    </>
  );
  return (
    <section className="panel time-module">
      <header>
        <div>
          <span>GESTÃO DE JORNADA</span>
          <h3>{tab}</h3>
          <p>Dados reais, rastreáveis e vinculados aos colaboradores.</p>
        </div>
        <fieldset className="time-competence-picker compact">
          <legend>Competência</legend>
          <label>
            Mês
            <select
              value={Number(month.slice(5, 7))}
              onChange={(event) =>
                setMonth(
                  `${month.slice(0, 4)}-${String(event.target.value).padStart(2, "0")}`,
                )
              }
            >
              {monthNames.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ano
            <select
              value={month.slice(0, 4)}
              onChange={(event) =>
                setMonth(`${event.target.value}-${month.slice(5, 7)}`)
              }
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
      </header>
      {tab === "Banco de Horas" && (
        <div className="time-module-table">
          <div className="head">
            <span>Colaborador</span>
            <span>Dias</span>
            <span>Previsto</span>
            <span>Trabalhado</span>
            <span>Saldo</span>
          </div>
          {data.bank.map((item) => (
            <div key={item.technicianId}>
              <strong>{item.name}</strong>
              <span>{item.days}</span>
              <span>{duration(item.expectedSeconds)}</span>
              <span>{duration(item.workedSeconds)}</span>
              <em className={item.balanceSeconds < 0 ? "negative" : "positive"}>
                {duration(item.balanceSeconds)}
              </em>
            </div>
          ))}
        </div>
      )}
      {tab === "Escalas" && (
        <>
          <div className="time-module-table">
            <div className="head">
              <span>Escala</span>
              <span>Colaborador</span>
              <span>Horas/dia</span>
              <span>Vigência</span>
              <span>Situação</span>
            </div>
            {data.schedules.map((item) => {
              let rules = {};
              try {
                rules = JSON.parse(item.rulesJson || "{}");
              } catch {}
              return (
                <div key={`${item.id}-${item.technicianId || 0}`}>
                  <strong>{item.name}</strong>
                  <span>{item.technicianName || "Não atribuída"}</span>
                  <span>{rules.dailyHours || "—"}</span>
                  <span>
                    {item.effectiveFrom.split("-").reverse().join("/")}
                  </span>
                  <em className={item.active ? "positive" : "negative"}>
                    {item.active ? "Ativa" : "Inativa"}
                  </em>
                </div>
              );
            })}
          </div>
          {canManage && (
            <form
              className="time-module-form"
              onSubmit={(event) => {
                event.preventDefault();
                submit("/time/schedules", {
                  technicianId: Number(form.technicianId),
                  name: form.name,
                  dailyHours: Number(form.dailyHours),
                  effectiveFrom: form.effectiveFrom,
                });
              }}
            >
              <label>
                Colaborador
                <select
                  required
                  value={form.technicianId}
                  onChange={(event) =>
                    setForm({ ...form, technicianId: event.target.value })
                  }
                >
                  <option value="">Selecione</option>
                  {technicians.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Nome da escala
                <input
                  required
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                />
              </label>
              <label>
                Horas por dia
                <input
                  required
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={form.dailyHours}
                  onChange={(event) =>
                    setForm({ ...form, dailyHours: event.target.value })
                  }
                />
              </label>
              <label>
                Início da vigência
                <input
                  required
                  type="date"
                  value={form.effectiveFrom}
                  onChange={(event) =>
                    setForm({ ...form, effectiveFrom: event.target.value })
                  }
                />
              </label>
              <button className="primary-button" disabled={saving}>
                Cadastrar escala
              </button>
            </form>
          )}
        </>
      )}
      {tab === "Afastamentos" && (
        <>
          <div className="time-module-table">
            <div className="head">
              <span>Colaborador</span>
              <span>Tipo</span>
              <span>Início</span>
              <span>Fim</span>
              <span>Situação</span>
            </div>
            {data.leaves.map((item) => (
              <div key={item.id}>
                <strong>{item.technicianName}</strong>
                <span>{item.leaveType}</span>
                <span>{item.startsOn.split("-").reverse().join("/")}</span>
                <span>{item.endsOn.split("-").reverse().join("/")}</span>
                <em className="positive">
                  {item.status === "active" ? "Ativo" : item.status}
                </em>
              </div>
            ))}
          </div>
          {canManage && (
            <form
              className="time-module-form"
              onSubmit={(event) => {
                event.preventDefault();
                submit("/time/leaves", {
                  technicianId: Number(form.technicianId),
                  leaveType: form.leaveType,
                  startsOn: form.startsOn,
                  endsOn: form.endsOn,
                  notes: form.notes,
                });
              }}
            >
              <label>
                Colaborador
                <select
                  required
                  value={form.technicianId}
                  onChange={(event) =>
                    setForm({ ...form, technicianId: event.target.value })
                  }
                >
                  <option value="">Selecione</option>
                  {technicians.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Tipo
                <select
                  value={form.leaveType}
                  onChange={(event) =>
                    setForm({ ...form, leaveType: event.target.value })
                  }
                >
                  <option>Férias</option>
                  <option>Atestado</option>
                  <option>Licença</option>
                  <option>Folga</option>
                  <option>Outro</option>
                </select>
              </label>
              <label>
                Início
                <input
                  type="date"
                  value={form.startsOn}
                  onChange={(event) =>
                    setForm({ ...form, startsOn: event.target.value })
                  }
                />
              </label>
              <label>
                Fim
                <input
                  type="date"
                  value={form.endsOn}
                  onChange={(event) =>
                    setForm({ ...form, endsOn: event.target.value })
                  }
                />
              </label>
              <button className="primary-button" disabled={saving}>
                Registrar afastamento
              </button>
            </form>
          )}
        </>
      )}
      {tab === "Fechamentos" && (
        <>
          <div className="time-module-table">
            <div className="head">
              <span>Período</span>
              <span>Versão</span>
              <span>Situação</span>
              <span>Fechado por</span>
              <span>Data / Relatório</span>
            </div>
            {data.closures.map((item) => (
              <div key={item.id}>
                <strong>
                  {item.periodStart.split("-").reverse().join("/")} a{" "}
                  {item.periodEnd.split("-").reverse().join("/")}
                </strong>
                <span>v{item.version}</span>
                <em className="positive">
                  {item.status === "closed" ? "Fechado" : item.status}
                </em>
                <span>{item.closedByName || "—"}</span>
                <span className="time-closure-report">
                  {item.closedAt
                    ? new Date(item.closedAt).toLocaleString("pt-BR")
                    : "—"}
                  <button
                    type="button"
                    onClick={() => exportClosureReport(item)}
                  >
                    <Icon name="download" size={14} /> Baixar PDF
                  </button>
                </span>
              </div>
            ))}
          </div>
          {canManage && (
            <form
              className="time-module-form compact"
              onSubmit={(event) => {
                event.preventDefault();
                if (
                  window.confirm(
                    "Fechar este período? As marcações originais permanecerão imutáveis.",
                  )
                )
                  submit("/time/closures", {
                    periodStart: form.periodStart,
                    periodEnd: form.periodEnd,
                  });
              }}
            >
              {periodFields}
              <button className="primary-button" disabled={saving}>
                Conferir e fechar período
              </button>
            </form>
          )}
        </>
      )}
      {tab === "Relatórios" && (
        <div className="time-reports-workspace">
          <nav className="time-report-tabs">
            {[
              ["mirror", "Espelho", "file"],
              ["card", "Cartão de Ponto", "calendar"],
              ["statement", "Extrato por Período", "chart"],
            ].map(([value, label, icon]) => (
              <button
                type="button"
                className={reportView === value ? "selected" : ""}
                onClick={() => setReportView(value)}
                key={value}
              >
                <Icon name={icon} size={17} /> {label}
              </button>
            ))}
          </nav>
          <div className="time-report-hub">
            <article>
              <span>Marcações</span>
              <strong>{data.summary.punches}</strong>
            </article>
            <article>
              <span>Horas trabalhadas</span>
              <strong>{duration(data.summary.workedSeconds)}</strong>
            </article>
            <article>
              <span>Horas previstas pela escala</span>
              <strong>{duration(data.summary.expectedSeconds)}</strong>
            </article>
          </div>
          {reportView === "mirror" ? (
            <section className="time-report-action-card">
              <div>
                <Icon name="file" size={24} />
                <span>
                  <strong>Espelho mensal</strong>
                  <small>
                    Modelo atual detalhado por colaborador, marcações e ajustes.
                  </small>
                </span>
              </div>
              <button
                className="primary-button time-report-download"
                onClick={onOpenMirrors}
              >
                <Icon name="file" size={18} /> Gerar Espelho
              </button>
            </section>
          ) : (
            <section className="time-report-generator">
              <div>
                <strong>
                  {reportView === "card"
                    ? "Cartões de Ponto da equipe"
                    : "Extratos individuais por período"}
                </strong>
                <small>
                  {reportView === "card"
                    ? `Competência completa: ${monthNames[Number(month.slice(5, 7)) - 1]} de ${month.slice(0, 4)}. Duas páginas por colaborador.`
                    : `Competência completa: ${monthNames[Number(month.slice(5, 7)) - 1]} de ${month.slice(0, 4)}. Um colaborador por página.`}
                </small>
              </div>
              <button
                className="primary-button time-report-download"
                disabled={exportingReport}
                onClick={
                  reportView === "card" ? exportTimeCard : exportPeriodStatement
                }
              >
                <Icon name="download" size={17} />{" "}
                {exportingReport ? "Gerando…" : "Exportar equipe"}
              </button>
            </section>
          )}
          <p className="time-report-rule-note">
            <Icon name="check" size={15} /> Todos os totais consideram a escala
            vigente do colaborador em cada dia. Novas escalas passam a ser
            usadas automaticamente conforme sua vigência.
          </p>
        </div>
      )}
      {tab === "Fiscalização" && (
        <>
          <div className="time-module-table">
            <div className="head">
              <span>Arquivo</span>
              <span>Período</span>
              <span>Versão</span>
              <span>Situação</span>
              <span>Integridade</span>
            </div>
            {data.regulatory.map((item) => (
              <div key={item.id}>
                <strong>{item.fileType}</strong>
                <span>
                  {item.periodStart.split("-").reverse().join("/")} a{" "}
                  {item.periodEnd.split("-").reverse().join("/")}
                </span>
                <span>{item.schemaVersion}</span>
                <em className="positive">{item.status}</em>
                <span title={item.integrityHash}>
                  {item.integrityHash?.slice(0, 12) || "—"}
                </span>
              </div>
            ))}
          </div>
          {canManage && (
            <form
              className="time-module-form compact"
              onSubmit={(event) => {
                event.preventDefault();
                submit("/time/regulatory", {
                  fileType: form.fileType,
                  periodStart: form.periodStart,
                  periodEnd: form.periodEnd,
                });
              }}
            >
              <label>
                Formato
                <select
                  value={form.fileType}
                  onChange={(event) =>
                    setForm({ ...form, fileType: event.target.value })
                  }
                >
                  <option>AFD</option>
                  <option>AEJ</option>
                  <option>ACJEF</option>
                </select>
              </label>
              {periodFields}
              <button className="primary-button" disabled={saving}>
                Gerar arquivo de fiscalização
              </button>
            </form>
          )}
        </>
      )}
      {tab === "Configurações" && (
        <div className="time-settings-form">
          <label>
            <input
              type="checkbox"
              checked={Boolean(data.settings.locationRequired)}
              onChange={(event) =>
                setData({
                  ...data,
                  settings: {
                    ...data.settings,
                    locationRequired: event.target.checked,
                  },
                })
              }
            />{" "}
            Exigir localização nas marcações
          </label>
          <label>
            <input
              type="checkbox"
              checked={Boolean(data.settings.photoRequired)}
              onChange={(event) =>
                setData({
                  ...data,
                  settings: {
                    ...data.settings,
                    photoRequired: event.target.checked,
                  },
                })
              }
            />{" "}
            Exigir foto nas marcações
          </label>
          <label>
            Raio de geofence (metros)
            <input
              type="number"
              min="25"
              max="5000"
              value={data.settings.geofenceRadiusMeters}
              onChange={(event) =>
                setData({
                  ...data,
                  settings: {
                    ...data.settings,
                    geofenceRadiusMeters: Number(event.target.value),
                  },
                })
              }
            />
          </label>
          {canManage ? (
            <button
              className="primary-button"
              disabled={saving}
              onClick={saveSettings}
            >
              Salvar configurações
            </button>
          ) : (
            <p>Seu perfil possui acesso somente para consulta.</p>
          )}
        </div>
      )}
    </section>
  );
}

function TimeAudit({ token }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch(`${apiBase}/time/audit`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : []))
      .then(setItems);
  }, [token]);
  return (
    <section className="panel time-team">
      <header>
        <div>
          <h3>Trilha de auditoria</h3>
          <p>
            Eventos anexados em ordem cronológica, sem alteração do registro
            original.
          </p>
        </div>
      </header>
      <div className="time-adjustments">
        {items.map((item) => (
          <article key={item.id}>
            <div>
              <strong>{item.actorName || "Sistema"}</strong>
              <small>{new Date(item.createdAt).toLocaleString("pt-BR")}</small>
              <p>
                {item.action} • {item.entityType} #{item.entityId}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProjectDeliveryPage({ projects, token, canManage }) {
  const [projectId, setProjectId] = useState(projects[0]?.id || "");
  const [delivery, setDelivery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [revisionReason, setRevisionReason] = useState("");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const readApiResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json"))
      throw new Error(
        "O backend do AboutPlan não está disponível ou precisa ser reiniciado.",
      );
    return response.json();
  };
  const load = async (id) => {
    if (!id) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/projects/${id}/delivery`, {
        headers,
      });
      const data = await readApiResponse(response);
      if (!response.ok)
        throw new Error(data.error || "Não foi possível carregar a entrega.");
      setDelivery(data);
    } catch (e) {
      setError(
        e.message === "Failed to fetch"
          ? "Não foi possível conectar ao backend do AboutPlan."
          : e.message,
      );
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!projectId && projects[0]?.id) setProjectId(projects[0].id);
  }, [projects, projectId]);
  useEffect(() => {
    load(projectId);
  }, [projectId]);
  const data = delivery?.data || {};
  const setData = (patch) =>
    setDelivery((current) => ({
      ...current,
      data: { ...current.data, ...patch },
    }));
  const setIdentification = (field, value) =>
    setData({
      identification: { ...(data.identification || {}), [field]: value },
    });
  const patchRow = (key, index, field, value) =>
    setData({
      [key]: (data[key] || []).map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    });
  const addRow = (key, row) => setData({ [key]: [...(data[key] || []), row] });
  const removeRow = (key, index) =>
    setData({ [key]: (data[key] || []).filter((_, i) => i !== index) });
  const persist = async (action) => {
    if (!projectId || !delivery) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/projects/${projectId}/delivery`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ data, action, revisionReason }),
        },
      );
      const result = await readApiResponse(response);
      if (!response.ok)
        throw new Error(
          result.error === "revision_reason_required"
            ? "Informe o motivo da nova revisão."
            : result.error === "invalid_project_delivery_photos"
              ? "Revise as fotos: use JPG, PNG ou WebP de até 4 MB, no máximo 5 por item e 20 MB no documento."
            : result.error || "Não foi possível salvar.",
        );
      setDelivery(result);
      setRevisionReason("");
    } catch (e) {
      setError(
        e.message === "Failed to fetch"
          ? "Não foi possível conectar ao backend do AboutPlan."
          : e.message,
      );
    } finally {
      setBusy(false);
    }
  };
  const exportPdf = async () => {
    if (!delivery) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    let y = 22;
    const left = 18;
    const page = () => {
      doc.addPage();
      y = 20;
    };
    const line = (text, size = 10, bold = false) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      const rows = doc.splitTextToSize(String(text || "—"), 174);
      if (y + rows.length * 5 > 280) page();
      doc.text(rows, left, y);
      y += rows.length * 5 + 2;
    };
    const title = (text) => {
      if (y > 260) page();
      y += 3;
      doc.setTextColor(32, 49, 77);
      line(text, 14, true);
      doc.setDrawColor(223, 229, 239);
      doc.line(left, y, 192, y);
      y += 6;
    };
    doc.setFillColor(24, 42, 70);
    doc.rect(0, 0, 210, 45, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(21);
    doc.setFont("helvetica", "bold");
    doc.text("ABOUTPLAN", left, 22);
    doc.setFontSize(11);
    doc.text(
      `ENTREGA DE OBRA • REV.${String(delivery.revision || 0).padStart(2, "0")}`,
      left,
      34,
    );
    y = 58;
    doc.setTextColor(32, 49, 77);
    line(data.identification?.projectName, 20, true);
    line(
      `Projeto nº ${data.identification?.projectNumber || "não informado"} • ${data.identification?.clientName} • ${data.identification?.unitName}`,
      10,
    );
    title("Identificação");
    [
      ["Responsável Aboutsec", data.identification?.aboutsecResponsible],
      ["Responsável do cliente", data.identification?.clientResponsible],
      ["Início", formatDate(data.identification?.startDate)],
      ["Conclusão planejada", formatDate(data.identification?.plannedEndDate)],
      ["Conclusão real", formatDate(data.identification?.actualEndDate)],
      ["Situação", data.identification?.deliveryStatus],
    ].forEach(([a, b]) => line(`${a}: ${b || "—"}`));
    title("Resumo executivo");
    line(data.executiveSummary || "Não informado.");
    title("Escopo executado");
    (data.scope || []).forEach((item) =>
      line(
        `${item.item} — ${item.description} | Previsto: ${item.plannedQuantity} | Executado: ${item.executedQuantity} | ${item.situation}${item.observation ? ` | ${item.observation}` : ""}`,
      ),
    );
    if (data.changes?.length) {
      title("Alterações do projeto");
      data.changes.forEach((item) =>
        line(
          `${formatDate(item.date)} • ${item.type}: ${item.description} (${item.origin || "origem não informada"})`,
        ),
      );
    }
    if (data.interferencesEnabled && data.interferences?.length) {
      title("Interferências");
      data.interferences.forEach((item) =>
        line(
          `${formatDate(item.date)} • ${item.type}: ${item.description} • impacto ${item.impactedTime || "não informado"}`,
        ),
      );
    }
    title("Pendências");
    if (!(data.pendingItems || []).length) line("Entrega sem pendências.");
    else
      data.pendingItems.forEach((item) =>
        line(
          `${item.item} • ${item.status} • responsável: ${item.responsible || "—"} • prazo: ${formatDate(item.expectedDate)}`,
        ),
      );
    if (data.tests?.length) {
      title("Testes e validações");
      data.tests.forEach((item) =>
        line(
          `${item.item}: ${item.status}${item.observation ? ` • ${item.observation}` : ""}`,
        ),
      );
    }
    if (data.documentationEnabled) {
      title("Documentação entregue");
      (data.documents || []).forEach((item) =>
        line(`${item.type}: ${item.description || item.url || "Anexo"}`),
      );
    }
    if (data.trainingEnabled) {
      title("Treinamento");
      line(
        `Data: ${formatDate(data.training?.date)} • Instrutor: ${data.training?.instructor || "—"} • Participantes: ${data.training?.participants || "—"}`,
      );
      line(data.training?.content);
    }
    title("Aceite");
    line(`Situação: ${data.acceptance?.status || "Não registrado"}`);
    line(
      `Responsável: ${data.acceptance?.responsibleName || "—"} • ${data.acceptance?.responsibleRole || ""}`,
    );
    line(data.acceptance?.notes);
    doc.save(
      `Entrega_de_Obra_${data.identification?.projectNumber || projectId}_REV${String(delivery.revision || 0).padStart(2, "0")}.pdf`,
    );
  };
  const exportPdfModern = async () => {
    if (!delivery) return;
    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    });
    const navy = [23, 35, 60];
    const blue = [65, 105, 170];
    const ink = [32, 49, 77];
    const muted = [104, 119, 143];
    const pale = [244, 247, 251];
    const revision = `REV.${String(delivery.revision || 0).padStart(2, "0")}`;
    const identification = data.identification || {};
    const safe = (value) => String(value || "—");
    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    let logo = null;
    try {
      logo = await loadImage("/aboutplan-logo-white-hq.png");
    } catch (e) {
      logo = null;
    }
    const addLogo = (x, y, maxWidth, maxHeight) => {
      if (logo) {
        const ratio = logo.naturalWidth / logo.naturalHeight;
        let width = maxWidth;
        let height = width / ratio;
        if (height > maxHeight) {
          height = maxHeight;
          width = height * ratio;
        }
        doc.addImage(logo, "PNG", x, y, width, height, undefined, "SLOW");
      } else {
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text("ABOUTPLAN", x, y + 8);
      }
    };
    const addFooter = async () => {
      const total = doc.getNumberOfPages();
      for (let pageNumber = 2; pageNumber <= total; pageNumber++) {
        doc.setPage(pageNumber);
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 272, 210, 25, "F");
        doc.setDrawColor(224, 229, 238);
        doc.line(16, 277, 194, 277);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        const footerReference = doc.splitTextToSize(
          `AboutPlan • ${identification.projectNumber || identification.projectName || "Projeto"}`,
          65,
        )[0];
        doc.text(footerReference, 16, 288);
        await addAboutsecPdfLogo(doc, 157, 282, 25, 10, "dark");
        doc.text(`${pageNumber - 1}/${total - 1}`, 194, 288, {
          align: "right",
        });
      }
    };
    doc.setFillColor(...navy);
    doc.rect(0, 0, 210, 297, "F");
    addLogo(22, 24, 118, 31);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(197, 207, 223);
    doc.setCharSpace(0.12);
    doc.text("Powered by Aboutsec", 22, 61);
    doc.setCharSpace(0);
    doc.setDrawColor(197, 119, 73);
    doc.setLineWidth(1.2);
    doc.line(22, 70, 62, 70);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(197, 119, 73);
    doc.text("DOCUMENTO DE CONCLUSÃO", 22, 87);
    doc.setFontSize(29);
    doc.setTextColor(255, 255, 255);
    doc.text("ENTREGA DE OBRA", 22, 105);
    const projectTitle = doc.splitTextToSize(
      safe(identification.projectName),
      160,
    );
    doc.setFontSize(18);
    doc.text(projectTitle, 22, 124);
    const titleBottom = 124 + projectTitle.length * 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(197, 207, 223);
    doc.text(
      `Projeto nº ${identification.projectNumber || "não informado"}`,
      22,
      titleBottom + 8,
    );
    doc.text(
      `${identification.clientName || "Cliente não informado"} • ${identification.unitName || "Unidade não informada"}`,
      22,
      titleBottom + 15,
    );
    doc.setFillColor(34, 55, 86);
    doc.roundedRect(22, 218, 166, 42, 3, 3, "F");
    doc.setFontSize(8);
    doc.setTextColor(164, 179, 202);
    doc.text("SITUAÇÃO DA ENTREGA", 30, 230);
    doc.text("CONCLUSÃO REAL", 112, 230);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(safe(identification.deliveryStatus), 30, 240);
    doc.text(formatDate(identification.actualEndDate) || "—", 112, 240);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(197, 207, 223);
    doc.text(
      `${revision} • Emitido em ${new Date().toLocaleDateString("pt-BR")}`,
      30,
      252,
    );
    doc.setFontSize(8);
    doc.text("GESTÃO DE OPERAÇÕES", 22, 280);

    let y = 35;
    const newPage = () => {
      doc.addPage();
      doc.setFillColor(...navy);
      doc.rect(0, 0, 210, 22, "F");
      addLogo(16, 5, 70, 12);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text("ENTREGA DE OBRA", 194, 13, { align: "right" });
      y = 34;
    };
    const ensure = (height) => {
      if (y + height > 268) newPage();
    };
    const section = (title) => {
      ensure(18);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(...ink);
      doc.text(title, 16, y);
      y += 4;
      doc.setDrawColor(...blue);
      doc.setLineWidth(0.7);
      doc.line(16, y, 194, y);
      y += 8;
    };
    const paragraph = (text) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...ink);
      const lines = doc.splitTextToSize(safe(text), 178);
      ensure(lines.length * 5 + 4);
      doc.text(lines, 16, y);
      y += lines.length * 5 + 4;
    };
    const infoGrid = (items) => {
      const width = 86;
      for (let index = 0; index < items.length; index += 2) {
        ensure(25);
        items.slice(index, index + 2).forEach(([label, value], column) => {
          const x = 16 + column * 92;
          doc.setFillColor(...pale);
          doc.roundedRect(x, y, width, 20, 2, 2, "F");
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...muted);
          doc.text(label.toUpperCase(), x + 5, y + 7);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9.5);
          doc.setTextColor(...ink);
          const lines = doc.splitTextToSize(safe(value), width - 10);
          doc.text(lines.slice(0, 2), x + 5, y + 14);
        });
        y += 24;
      }
    };
    const list = (rows, render, empty = "Nenhum registro informado.") => {
      if (!rows?.length) {
        paragraph(empty);
        return;
      }
      rows.forEach((row, index) => {
        const text = render(row, index);
        const lines = doc.splitTextToSize(text, 164);
        const height = Math.max(17, lines.length * 4.5 + 9);
        ensure(height + 3);
        doc.setFillColor(
          index % 2 ? 248 : 244,
          index % 2 ? 250 : 247,
          index % 2 ? 253 : 251,
        );
        doc.roundedRect(16, y, 178, height, 2, 2, "F");
        doc.setFillColor(...blue);
        doc.circle(22, y + 8, 2.4, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.8);
        doc.setTextColor(...ink);
        doc.text(lines, 28, y + 7);
        y += height + 3;
      });
    };
    const photoRecords = [
      ...(data.changes || []).flatMap((item) =>
        (item.photos || []).map((photo) => ({
          ...photo,
          group: "ALTERAÇÃO DO PROJETO",
          reference: item.description || item.type || "Alteração registrada",
        })),
      ),
      ...(data.interferences || []).flatMap((item) =>
        (item.photos || []).map((photo) => ({
          ...photo,
          group: "INTERFERÊNCIA",
          reference: item.description || item.type || "Interferência registrada",
        })),
      ),
      ...(data.tests || []).flatMap((item) =>
        (item.photos || []).map((photo) => ({
          ...photo,
          group: "TESTE E VALIDAÇÃO",
          reference: item.item || "Teste registrado",
        })),
      ),
      ...(data.evidencePhotos || []).map((photo) => ({
        ...photo,
        group: "EVIDÊNCIA GERAL",
        reference: "Registro fotográfico da conclusão",
      })),
    ].filter((photo) => String(photo.dataUrl || "").startsWith("data:image/"));
    const addPhotoAppendix = () => {
      photoRecords.forEach((photo, index) => {
        const slot = index % 4;
        if (slot === 0) {
          newPage();
          section("Registros fotográficos");
        }
        const column = slot % 2;
        const row = Math.floor(slot / 2);
        const gap = 8;
        const cardWidth = 85;
        const cardHeight = 101;
        const cardX = 16 + column * (cardWidth + gap);
        const cardY = 49 + row * 108;
        const padding = 4;
        doc.setFillColor(250, 251, 253);
        doc.setDrawColor(224, 229, 238);
        doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.2);
        doc.setTextColor(...ink);
        doc.text(photo.group, cardX + padding, cardY + 7);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...muted);
        const description = doc
          .splitTextToSize(photo.caption || photo.reference, cardWidth - 8)
          .slice(0, 2);
        doc.text(description, cardX + padding, cardY + 12, {
          lineHeightFactor: 1.2,
        });
        try {
          const properties = doc.getImageProperties(photo.dataUrl);
          const imageTop = cardY + 22;
          const maxWidth = cardWidth - padding * 2;
          const maxHeight = cardHeight - 26;
          const ratio = properties.width / properties.height;
          let width = maxWidth;
          let height = width / ratio;
          if (height > maxHeight) {
            height = maxHeight;
            width = height * ratio;
          }
          doc.setFillColor(255, 255, 255);
          doc.rect(cardX + padding, imageTop, maxWidth, maxHeight, "F");
          doc.addImage(
            photo.dataUrl,
            properties.fileType || "JPEG",
            cardX + (cardWidth - width) / 2,
            imageTop + (maxHeight - height) / 2,
            width,
            height,
            undefined,
            "FAST",
          );
        } catch {
          doc.setTextColor(174, 94, 98);
          doc.text("Imagem indisponível.", cardX + padding, cardY + 30);
        }
      });
    };
    newPage();
    section("1. Identificação do projeto");
    infoGrid([
      ["Projeto", identification.projectName],
      ["Número do projeto", identification.projectNumber],
      ["Cliente", identification.clientName],
      ["Unidade", identification.unitName],
      ["Responsável Aboutsec", identification.aboutsecResponsible],
      ["Responsável do cliente", identification.clientResponsible],
      ["Início", formatDate(identification.startDate)],
      ["Conclusão planejada", formatDate(identification.plannedEndDate)],
      ["Conclusão real", formatDate(identification.actualEndDate)],
      ["Situação", identification.deliveryStatus],
    ]);
    section("2. Resumo executivo");
    paragraph(data.executiveSummary || "Resumo executivo não informado.");
    section("3. Escopo executado");
    list(
      data.scope,
      (item) =>
        `${item.item || "Item"} — ${item.description || "Sem descrição"}\nPrevisto: ${item.plannedQuantity || "—"}  |  Executado: ${item.executedQuantity || "—"}  |  Situação: ${item.situation || "—"}${item.observation ? `\nObservação: ${item.observation}` : ""}`,
      "Nenhum item de escopo registrado.",
    );
    if (data.changes?.length) {
      section("4. Alterações do projeto");
      list(
        data.changes,
        (item) =>
          `${formatDate(item.date)} • ${item.type || "Alteração"}\n${item.description || "Sem descrição"}${item.origin ? `\nOrigem: ${item.origin}` : ""}`,
      );
    }
    if (data.interferencesEnabled) {
      section("5. Interferências");
      list(
        data.interferences,
        (item) =>
          `${formatDate(item.date)} • ${item.type || "Interferência"}\n${item.description || "Sem descrição"}${item.impactedTime ? `\nTempo impactado: ${item.impactedTime}` : ""}`,
        "Nenhuma interferência registrada.",
      );
    }
    section("6. Pendências");
    list(
      data.pendingItems,
      (item) =>
        `${item.item || "Pendência"} • ${item.status || "Sem status"} • Prioridade: ${item.priority || "—"}\nResponsável: ${item.responsible || "—"} • Prazo: ${formatDate(item.expectedDate) || "—"}${item.observation ? `\n${item.observation}` : ""}`,
      "Entrega declarada sem pendências.",
    );
    section("7. Testes e validações");
    list(
      data.tests,
      (item) =>
        `${item.item || "Teste"} • ${item.status || "Sem status"}${item.observation ? `\n${item.observation}` : ""}`,
      "Nenhum teste registrado.",
    );
    if (data.documentationEnabled) {
      section("9. Documentação entregue");
      list(
        data.documents,
        (item) =>
          `${item.type || "Documento"} — ${item.description || item.url || "Sem descrição"}`,
        "Nenhum documento registrado.",
      );
    }
    if (data.trainingEnabled) {
      section("10. Treinamento");
      infoGrid([
        ["Data", formatDate(data.training?.date)],
        ["Instrutor", data.training?.instructor],
        ["Participantes", data.training?.participants],
      ]);
      paragraph(data.training?.content || "Conteúdo não informado.");
    }
    section("11. Aceite da entrega");
    infoGrid([
      ["Situação", data.acceptance?.status || "Não registrado"],
      ["Responsável", data.acceptance?.responsibleName],
      ["Cargo / função", data.acceptance?.responsibleRole],
      [
        "Data e horário",
        `${formatDate(data.acceptance?.date) || "—"} ${data.acceptance?.time || ""}`,
      ],
    ]);
    paragraph(data.acceptance?.notes || "Sem observações adicionais.");
    ensure(38);
    doc.setFillColor(...pale);
    doc.roundedRect(16, y, 178, 31, 2, 2, "F");
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text("ASSINATURA / REFERÊNCIA DE ACEITE", 22, y + 8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text(safe(data.acceptance?.signature), 22, y + 18);
    doc.setDrawColor(186, 197, 214);
    doc.line(112, y + 22, 187, y + 22);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("Assinatura do responsável pelo aceite", 125, y + 27);
    y += 35;
    addPhotoAppendix();
    await addFooter();
    doc.setPage(1);
    await addAboutsecPdfLogo(doc, 179, 270, 18, 20, "white");
    doc.save(
      `Entrega_de_Obra_${identification.projectNumber || projectId}_${revision}.pdf`,
    );
  };
  const input = (value, onChange, type = "text", placeholder = "") => (
    <input
      type={type}
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  const RowActions = ({ section, index }) =>
    canManage ? (
      <button
        type="button"
        className="delivery-remove"
        onClick={() => removeRow(section, index)}
      >
        <Icon name="trash" size={15} /> Remover
      </button>
    ) : null;
  const deliveryPhotoCount = [
    ...(data.evidencePhotos || []),
    ...(data.changes || []).flatMap((item) => item.photos || []),
    ...(data.interferences || []).flatMap((item) => item.photos || []),
    ...(data.tests || []).flatMap((item) => item.photos || []),
  ].length;
  const deliveryReadiness = [
    { label: "Escopo", ready: Boolean(data.scope?.length) },
    { label: "Testes", ready: Boolean(data.tests?.length) },
    {
      label: "Documentação",
      ready: !data.documentationEnabled || Boolean(data.documents?.length),
    },
    {
      label: "Aceite",
      ready: Boolean(data.acceptance?.responsibleName || data.acceptance?.status),
    },
  ];
  const deliveryReadyCount = deliveryReadiness.filter((item) => item.ready).length;
  return (
    <div className="delivery-page">
      <div className="page-heading page-hero compact delivery-hero">
        <div>
          <span>GESTÃO DE PROJETOS</span>
          <h1>Entrega de Obra</h1>
          <p>Consolidação, aceite e encerramento formal dos projetos.</p>
        </div>
        <div className="delivery-project-select">
          <label>Projeto</label>
          <div className="delivery-project-control">
            <Icon name="folder" size={17} />
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">Selecione um projeto</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.projectNumber ? `${p.projectNumber} — ` : ""}
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {busy && !delivery && <div className="empty">Carregando entrega…</div>}
      {delivery && (
        <>
          <div className="delivery-overview">
            <div className="delivery-kpis">
              <article>
                <i><Icon name="file" size={17} /></i>
                <div><span>Revisão</span><strong>REV.{String(delivery.revision || 0).padStart(2, "0")}</strong><small>{delivery.status === "finalized" ? "documento finalizado" : "em elaboração"}</small></div>
              </article>
              <article>
                <i><Icon name="clipboard" size={17} /></i>
                <div><span>Escopo</span><strong>{data.scope?.length || 0}</strong><small>itens registrados</small></div>
              </article>
              <article className={data.pendingItems?.length ? "attention" : ""}>
                <i><Icon name="clock" size={17} /></i>
                <div><span>Pendências</span><strong>{data.pendingItems?.length || 0}</strong><small>itens para acompanhar</small></div>
              </article>
              <article>
                <i><Icon name="camera" size={17} /></i>
                <div><span>Evidências</span><strong>{deliveryPhotoCount}</strong><small>registros fotográficos</small></div>
              </article>
            </div>
            <section className="delivery-readiness" aria-label="Completude da entrega">
              <header>
                <div><strong>Completude documental</strong><small>Itens essenciais da entrega</small></div>
                <b>{deliveryReadyCount}/4</b>
              </header>
              <div className="delivery-readiness-bar"><span style={{ width: `${deliveryReadyCount * 25}%` }} /></div>
              <div className="delivery-readiness-items">
                {deliveryReadiness.map((item) => (
                  <span className={item.ready ? "ready" : ""} key={item.label}>
                    <Icon name={item.ready ? "check" : "clock"} size={11} /> {item.label}
                  </span>
                ))}
              </div>
            </section>
          </div>
          <div className="delivery-toolbar">
            <div>
              <strong>{data.identification?.projectName}</strong>
              <span>
                {delivery.status === "finalized"
                  ? `Finalizada • REV.${String(delivery.revision).padStart(2, "0")}`
                  : "Rascunho em elaboração"}
              </span>
            </div>
            <div>
              {delivery.status === "finalized" &&
                canManage &&
                input(
                  revisionReason,
                  setRevisionReason,
                  "text",
                  "Motivo da nova revisão",
                )}
              <button className="secondary-button" onClick={exportPdfModern}>
                <Icon name="download" /> Exportar PDF
              </button>
              {canManage && (
                <button
                  className="secondary-button"
                  disabled={busy}
                  onClick={() => persist("save")}
                >
                  Salvar rascunho
                </button>
              )}
              {canManage && (
                <button
                  className="primary-button"
                  disabled={busy}
                  onClick={() => persist("finalize")}
                >
                  <Icon name="check" />{" "}
                  {delivery.status === "finalized"
                    ? "Gerar revisão"
                    : "Finalizar entrega"}
                </button>
              )}
            </div>
          </div>
          <fieldset disabled={!canManage} className="delivery-form">
            <DeliverySection
              title="1. Identificação da obra"
              subtitle="Dados principais consolidados do projeto."
            >
              <div className="delivery-grid three">
                {[
                  ["Cliente", "clientName"],
                  ["Unidade", "unitName"],
                  ["Projeto", "projectName"],
                  ["Projeto número", "projectNumber"],
                  ["Responsável Aboutsec", "aboutsecResponsible"],
                  ["Responsável do cliente", "clientResponsible"],
                ].map(([label, key]) => (
                  <label key={key}>
                    {label}
                    {input(data.identification?.[key], (v) =>
                      setIdentification(key, v),
                    )}
                  </label>
                ))}
                <label>
                  Data de início
                  {input(
                    data.identification?.startDate,
                    (v) => setIdentification("startDate", v),
                    "date",
                  )}
                </label>
                <label>
                  Conclusão planejada
                  {input(
                    data.identification?.plannedEndDate,
                    (v) => setIdentification("plannedEndDate", v),
                    "date",
                  )}
                </label>
                <label>
                  Conclusão real
                  {input(
                    data.identification?.actualEndDate,
                    (v) => setIdentification("actualEndDate", v),
                    "date",
                  )}
                </label>
                <label>
                  Situação
                  <select
                    value={data.identification?.deliveryStatus || ""}
                    onChange={(e) =>
                      setIdentification("deliveryStatus", e.target.value)
                    }
                  >
                    <option>Entregue sem pendências</option>
                    <option>Entregue com pendências</option>
                  </select>
                </label>
              </div>
            </DeliverySection>
            <DeliverySection title="2. Resumo executivo e linha do tempo">
              <label>
                Resumo executivo
                <textarea
                  value={data.executiveSummary || ""}
                  onChange={(e) =>
                    setData({ executiveSummary: e.target.value })
                  }
                />
                <AiReviewButton
                  token={token}
                  value={data.executiveSummary || ""}
                  onApply={(value) => setData({ executiveSummary: value })}
                  context="resumo executivo de entrega de obra"
                  disabled={!canManage}
                />
              </label>
              <DeliveryRows
                rows={data.timeline}
                columns={[
                  ["Data", "date", "date"],
                  ["Descrição", "description"],
                  ["Equipe", "team"],
                ]}
                patch={(i, f, v) => patchRow("timeline", i, f, v)}
                actions={(i) => <RowActions section="timeline" index={i} />}
              />
              <button
                type="button"
                className="add-inline"
                onClick={() =>
                  addRow("timeline", {
                    date: "",
                    description: "",
                    team: "",
                    visibleToClient: true,
                  })
                }
              >
                + Adicionar evento
              </button>
            </DeliverySection>
            <DeliverySection title="3. Escopo executado">
              <DeliveryRows
                rows={data.scope}
                columns={[
                  ["Item", "item"],
                  ["Descrição", "description"],
                  ["Qtd. prevista", "plannedQuantity", "number"],
                  ["Qtd. executada", "executedQuantity", "number"],
                  [
                    "Situação",
                    "situation",
                    "select",
                    ["Concluído", "Parcial", "Não executado", "Não aplicável"],
                  ],
                  ["Observação", "observation"],
                ]}
                patch={(i, f, v) => patchRow("scope", i, f, v)}
                actions={(i) => <RowActions section="scope" index={i} />}
              />
              <button
                type="button"
                className="add-inline"
                onClick={() =>
                  addRow("scope", {
                    item: "",
                    description: "",
                    plannedQuantity: 1,
                    executedQuantity: 0,
                    situation: "Não executado",
                    observation: "",
                  })
                }
              >
                + Adicionar item
              </button>
            </DeliverySection>
            <DeliverySection title="4. Alterações do projeto">
              <DeliveryRows
                rows={data.changes}
                columns={[
                  ["Data", "date", "date"],
                  [
                    "Tipo",
                    "type",
                    "select",
                    [
                      "Serviço adicional",
                      "Solicitação do cliente",
                      "Alteração técnica",
                      "Alteração de local de equipamento",
                      "Infraestrutura",
                      "Outro",
                    ],
                  ],
                  ["Descrição", "description"],
                  ["Origem / solicitante", "origin"],
                  ["Observação", "observation"],
                ]}
                patch={(i, f, v) => patchRow("changes", i, f, v)}
                renderAfter={(row, index) => (
                  <DeliveryPhotoField
                    photos={row.photos || []}
                    onChange={(photos) => patchRow("changes", index, "photos", photos)}
                    disabled={!canManage || busy}
                  />
                )}
                actions={(i) => <RowActions section="changes" index={i} />}
              />
              <button
                type="button"
                className="add-inline"
                onClick={() =>
                  addRow("changes", {
                    date: "",
                    type: "Serviço adicional",
                    description: "",
                    origin: "",
                    observation: "",
                    photos: [],
                  })
                }
              >
                + Registrar alteração
              </button>
            </DeliverySection>
            <DeliverySection
              title="5. Interferências"
              optional
              checked={data.interferencesEnabled}
              onToggle={(value) => setData({ interferencesEnabled: value })}
            >
              {data.interferencesEnabled && (
                <>
                  <DeliveryRows
                    rows={data.interferences}
                    columns={[
                      ["Data", "date", "date"],
                      ["Tipo", "type"],
                      ["Descrição", "description"],
                      ["Início", "start"],
                      ["Fim", "end"],
                      ["Tempo impactado", "impactedTime"],
                      ["Origem", "origin"],
                    ]}
                    patch={(i, f, v) => patchRow("interferences", i, f, v)}
                    renderAfter={(row, index) => (
                      <DeliveryPhotoField
                        photos={row.photos || []}
                        onChange={(photos) => patchRow("interferences", index, "photos", photos)}
                        disabled={!canManage || busy}
                      />
                    )}
                    actions={(i) => (
                      <RowActions section="interferences" index={i} />
                    )}
                  />
                  <button
                    type="button"
                    className="add-inline"
                    onClick={() =>
                      addRow("interferences", {
                        date: "",
                        type: "",
                        description: "",
                        start: "",
                        end: "",
                        impactedTime: "",
                        origin: "",
                        photos: [],
                      })
                    }
                  >
                    + Adicionar interferência
                  </button>
                </>
              )}
            </DeliverySection>
            <DeliverySection title="6. Pendências">
              <DeliveryRows
                rows={data.pendingItems}
                columns={[
                  ["Item", "item"],
                  ["Categoria", "category"],
                  ["Responsável", "responsible"],
                  ["Prazo", "expectedDate", "date"],
                  [
                    "Prioridade",
                    "priority",
                    "select",
                    ["Baixa", "Média", "Alta"],
                  ],
                  [
                    "Status",
                    "status",
                    "select",
                    ["Aberta", "Em andamento", "Concluída"],
                  ],
                  ["Observação", "observation"],
                ]}
                patch={(i, f, v) => patchRow("pendingItems", i, f, v)}
                actions={(i) => <RowActions section="pendingItems" index={i} />}
              />
              <button
                type="button"
                className="add-inline"
                onClick={() =>
                  addRow("pendingItems", {
                    item: "",
                    category: "",
                    responsible: "",
                    expectedDate: "",
                    priority: "Média",
                    status: "Aberta",
                    observation: "",
                  })
                }
              >
                + Adicionar pendência
              </button>
            </DeliverySection>
            <DeliverySection title="7. Testes e validações">
              <DeliveryRows
                rows={data.tests}
                columns={[
                  ["Teste", "item"],
                  [
                    "Status",
                    "status",
                    "select",
                    ["Aprovado", "Reprovado", "Não aplicável"],
                  ],
                  ["Observação", "observation"],
                ]}
                patch={(i, f, v) => patchRow("tests", i, f, v)}
                renderAfter={(row, index) => (
                  <DeliveryPhotoField
                    photos={row.photos || []}
                    onChange={(photos) => patchRow("tests", index, "photos", photos)}
                    disabled={!canManage || busy}
                  />
                )}
                actions={(i) => <RowActions section="tests" index={i} />}
              />
              <button
                type="button"
                className="add-inline"
                onClick={() =>
                  addRow("tests", {
                    item: "",
                    status: "Aprovado",
                    observation: "",
                    photos: [],
                  })
                }
              >
                + Adicionar teste
              </button>
            </DeliverySection>
            <DeliverySection title="8. Evidências">
              <DeliveryPhotoField
                photos={data.evidencePhotos || []}
                onChange={(evidencePhotos) => setData({ evidencePhotos })}
                disabled={!canManage || busy}
                title="Fotos gerais da entrega"
              />
            </DeliverySection>
            <DeliverySection
              title="9. Documentação entregue"
              optional
              checked={data.documentationEnabled}
              onToggle={(value) => setData({ documentationEnabled: value })}
            >
              {data.documentationEnabled && (
                <>
                  <DeliveryRows
                    rows={data.documents}
                    columns={[
                      ["Tipo de documento", "type"],
                      ["Descrição", "description"],
                      ["Arquivo / URL", "url"],
                    ]}
                    patch={(i, f, v) => patchRow("documents", i, f, v)}
                    actions={(i) => (
                      <RowActions section="documents" index={i} />
                    )}
                  />
                  <button
                    type="button"
                    className="add-inline"
                    onClick={() =>
                      addRow("documents", {
                        type: "",
                        description: "",
                        url: "",
                      })
                    }
                  >
                    + Adicionar documento
                  </button>
                </>
              )}
            </DeliverySection>
            <DeliverySection
              title="10. Treinamento"
              optional
              checked={data.trainingEnabled}
              onToggle={(value) => setData({ trainingEnabled: value })}
            >
              {data.trainingEnabled && (
                <div className="delivery-grid three">
                  <label>
                    Data
                    {input(
                      data.training?.date,
                      (v) =>
                        setData({ training: { ...data.training, date: v } }),
                      "date",
                    )}
                  </label>
                  <label>
                    Instrutor
                    {input(data.training?.instructor, (v) =>
                      setData({
                        training: { ...data.training, instructor: v },
                      }),
                    )}
                  </label>
                  <label>
                    Participantes
                    {input(data.training?.participants, (v) =>
                      setData({
                        training: { ...data.training, participants: v },
                      }),
                    )}
                  </label>
                  <label className="span-all">
                    Conteúdo
                    <textarea
                      value={data.training?.content || ""}
                      onChange={(e) =>
                        setData({
                          training: {
                            ...data.training,
                            content: e.target.value,
                          },
                        })
                      }
                    />
                    <AiReviewButton
                      token={token}
                      value={data.training?.content || ""}
                      onApply={(value) =>
                        setData({
                          training: { ...data.training, content: value },
                        })
                      }
                      context="conteúdo de treinamento entregue em obra"
                      disabled={!canManage}
                    />
                  </label>
                </div>
              )}
            </DeliverySection>
            <DeliverySection title="11. Aceite da entrega">
              <div className="delivery-grid three">
                <label>
                  Situação
                  <select
                    value={data.acceptance?.status || ""}
                    onChange={(e) =>
                      setData({
                        acceptance: {
                          ...data.acceptance,
                          status: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="">Selecione</option>
                    <option>Aprovado</option>
                    <option>Aprovado com pendências</option>
                    <option>Reprovado</option>
                  </select>
                </label>
                <label>
                  Responsável
                  {input(data.acceptance?.responsibleName, (v) =>
                    setData({
                      acceptance: { ...data.acceptance, responsibleName: v },
                    }),
                  )}
                </label>
                <label>
                  Cargo / função
                  {input(data.acceptance?.responsibleRole, (v) =>
                    setData({
                      acceptance: { ...data.acceptance, responsibleRole: v },
                    }),
                  )}
                </label>
                <label>
                  Data
                  {input(
                    data.acceptance?.date,
                    (v) =>
                      setData({ acceptance: { ...data.acceptance, date: v } }),
                    "date",
                  )}
                </label>
                <label>
                  Horário
                  {input(
                    data.acceptance?.time,
                    (v) =>
                      setData({ acceptance: { ...data.acceptance, time: v } }),
                    "time",
                  )}
                </label>
                <label>
                  Assinatura / referência
                  {input(data.acceptance?.signature, (v) =>
                    setData({
                      acceptance: { ...data.acceptance, signature: v },
                    }),
                  )}
                </label>
                <label className="span-all">
                  Observações
                  <textarea
                    value={data.acceptance?.notes || ""}
                    onChange={(e) =>
                      setData({
                        acceptance: {
                          ...data.acceptance,
                          notes: e.target.value,
                        },
                      })
                    }
                  />
                  <AiReviewButton
                    token={token}
                    value={data.acceptance?.notes || ""}
                    onApply={(value) =>
                      setData({
                        acceptance: { ...data.acceptance, notes: value },
                      })
                    }
                    context="observações do aceite de entrega de obra"
                    disabled={!canManage}
                  />
                </label>
              </div>
            </DeliverySection>
            <DeliverySection
              title="Informações internas"
              subtitle="Não são exibidas ao cliente nem exportadas no PDF."
            >
              <textarea
                value={data.internalNotes || ""}
                onChange={(e) => setData({ internalNotes: e.target.value })}
              />
              <AiReviewButton
                token={token}
                value={data.internalNotes || ""}
                onApply={(value) => setData({ internalNotes: value })}
                context="observações técnicas internas de entrega de obra"
                disabled={!canManage}
              />
            </DeliverySection>
          </fieldset>
        </>
      )}
    </div>
  );
}

function DeliverySection({
  title,
  subtitle,
  optional,
  checked,
  onToggle,
  children,
}) {
  const iconName = title.startsWith("1.")
    ? "building"
    : title.startsWith("2.")
      ? "trend"
      : title.startsWith("3.")
        ? "clipboard"
        : title.startsWith("4.")
          ? "edit"
          : title.startsWith("5.")
            ? "wrench"
            : title.startsWith("6.")
              ? "clock"
              : title.startsWith("7.") || title.startsWith("11.")
                ? "check"
                : title.startsWith("8.")
                  ? "camera"
                  : title.startsWith("9.")
                    ? "file"
                    : title.startsWith("10.")
                      ? "users"
                      : "file";
  return (
    <section className="delivery-section">
      <div className="delivery-section-head">
        <span className="delivery-section-icon" aria-hidden="true">
          <Icon name={iconName} size={17} />
        </span>
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {optional && (
          <label className="delivery-switch">
            <input
              type="checkbox"
              checked={Boolean(checked)}
              onChange={(e) => onToggle(e.target.checked)}
            />
            <span>{checked ? "Incluir na entrega" : "Não incluir"}</span>
          </label>
        )}
      </div>
      {(!optional || checked) && (
        <div className="delivery-section-body">{children}</div>
      )}
    </section>
  );
}
function DeliveryPhotoField({
  photos,
  onChange,
  disabled,
  title = "Fotos da evidência",
}) {
  return (
    <div className="delivery-photo-field">
      <TicketPhotoPicker
        photos={photos}
        onChange={onChange}
        title={title}
        description="Até 5 fotos JPG, PNG ou WebP de até 4 MB. A observação de cada foto é opcional."
        disabled={disabled}
        allowComments
        hideFileNames
      />
    </div>
  );
}
function DeliveryRows({ rows = [], columns, patch, actions, renderAfter }) {
  return (
    <div className="delivery-rows">
      {rows.map((row, index) => (
        <div className="delivery-row" key={row.sourceId || index}>
          {columns.map(([label, field, type = "text", options]) => (
            <label key={field}>
              {label}
              {type === "select" ? (
                <select
                  value={row[field] || ""}
                  onChange={(e) => patch(index, field, e.target.value)}
                >
                  {options.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              ) : type === "checkbox" ? (
                <input
                  type="checkbox"
                  checked={Boolean(row[field])}
                  onChange={(e) => patch(index, field, e.target.checked)}
                />
              ) : (
                <input
                  type={type}
                  value={row[field] ?? ""}
                  onChange={(e) =>
                    patch(
                      index,
                      field,
                      type === "number"
                        ? Number(e.target.value)
                        : e.target.value,
                    )
                  }
                />
              )}
            </label>
          ))}
          {renderAfter?.(row, index)}
          {actions(index)}
        </div>
      ))}
    </div>
  );
}

const quoteStatusOptions = [
  ["new", "Nova solicitação"],
  ["under_review", "Em análise"],
  ["inspection_pending", "Vistoria a agendar"],
  ["inspection_scheduled", "Vistoria agendada"],
  ["waiting_distributor", "Aguardando valores do distribuidor"],
  ["preparing", "Preparando proposta"],
  ["sent", "Proposta enviada"],
  ["accepted", "Aceita"],
  ["declined", "Recusada"],
  ["cancelled", "Cancelada"],
];
const quoteCategoryOptions = [
  "Projeto",
  "Vistoria",
  "Visita Técnica",
  "Consultoria",
  "Outros",
];
const quoteStatusLabel = (status) =>
  quoteStatusOptions.find(([value]) => value === status)?.[1] || status;
const quoteStatusTone = (status) =>
  ({
    accepted: "green",
    sent: "green",
    declined: "red",
    cancelled: "gray",
    waiting_distributor: "amber",
    inspection_scheduled: "purple",
  })[status] || "blue";
const quoteDisplayDate = (value) =>
  value
    ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString(
        "pt-BR",
      )
    : "—";

function QuotePhotoGallery({ photos = [] }) {
  if (!photos.length) return null;
  return (
    <section className="quote-photo-gallery">
      <h3>Fotos anexadas</h3>
      <div>
        {photos.map((photo, index) => (
          <a key={photo.id || index} href={photo.dataUrl} download={`solicitacao-foto-${index + 1}.${photo.mimeType === "image/png" ? "png" : photo.mimeType === "image/webp" ? "webp" : "jpg"}`} title={`Baixar foto ${index + 1}`}>
            <img src={photo.dataUrl} alt={`Foto ${index + 1} da solicitação`} />
            <span>Foto {index + 1}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

function QuoteAttachments({ files = [], onChange, disabled = false, onBusyChange }) {
  const [error, setError] = useState("");
  const [reading, setReading] = useState(false);
  const addFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    setError("");
    if (files.length + selected.length > 5) return setError("Adicione no máximo 5 arquivos.");
    if (selected.some((file) => !/\.(pdf|xls|xlsx|csv|dwg|dxf)$/i.test(file.name) || file.size > 5 * 1024 * 1024 || !file.size)) {
      return setError("Use PDF, Excel, CSV, DWG ou DXF com até 5 MB por arquivo.");
    }
    setReading(true);
    onBusyChange?.(true);
    try {
      const added = await Promise.all(selected.map((file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ fileName: file.name, dataUrl: reader.result });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      })));
      onChange([...files, ...added]);
    } catch {
      setError("Não foi possível ler o arquivo. Tente novamente.");
    } finally {
      setReading(false);
      onBusyChange?.(false);
    }
  };
  return (
    <section className="quote-attachments">
      <strong>Anexos da solicitação</strong>
      {onChange && <>
        <small>Até 5 arquivos PDF, Excel, CSV, DWG ou DXF de até 5 MB cada.</small>
        <label className="ticket-photo-add">
          <Icon name="plus" size={15} /> Adicionar arquivos
          <input type="file" accept=".pdf,.xls,.xlsx,.csv,.dwg,.dxf" multiple disabled={disabled || reading} onChange={addFiles} />
        </label>
      </>}
      {reading && <small role="status">Preparando arquivos…</small>}
      {files.length > 0 && <ul>{files.map((file, index) => <li key={`${file.fileName}-${index}`}>
        {onChange ? <span>{file.fileName}</span> : <a href={file.dataUrl} download={file.fileName}>{file.fileName}</a>}
        {onChange && <button type="button" disabled={disabled || reading} onClick={() => onChange(files.filter((_, position) => position !== index))} aria-label={`Remover ${file.fileName}`}>×</button>}
      </li>)}</ul>}
      {error && <small className="text-danger">{error}</small>}
    </section>
  );
}

function QuotesPage({ token, canManage, canDelete }) {
  const [data, setData] = useState({
    items: [],
    assignees: [],
    clients: [],
    units: [],
    contacts: [],
  });
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [error, setError] = useState("");
  const [markingSentId, setMarkingSentId] = useState(null);
  const load = async () => {
    const response = await fetch(
      `${apiBase}/quotes${filter ? `?status=${filter}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!response.ok) {
      setError("Não foi possível carregar os orçamentos.");
      return;
    }
    setData(await response.json());
  };
  useEffect(() => {
    load();
  }, [filter]);
  const filteredUnits = data.units.filter(
    (item) => !clientFilter || String(item.clientId) === String(clientFilter),
  );
  const items = data.items.filter(
    (item) =>
      (!clientFilter || String(item.clientId) === String(clientFilter)) &&
      (!unitFilter || String(item.unitId) === String(unitFilter)) &&
      `${item.title} ${item.clientName} ${item.unitName} ${item.location} ORC-${String(item.id).padStart(5, "0")}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
  );
  const open = async (item) => {
    const response = await fetch(`${apiBase}/quotes/${item.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) setSelected(await response.json());
  };
  const markSent = async (item) => {
    setMarkingSentId(item.id);
    setError("");
    try {
      const response = await fetch(`${apiBase}/quotes/${item.id}/mark-sent`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("mark_sent_failed");
      await load();
    } catch {
      setError("Não foi possível marcar a proposta como enviada.");
    } finally {
      setMarkingSentId(null);
    }
  };
  const active = data.items.filter(
    (item) =>
      !["sent", "accepted", "declined", "cancelled"].includes(item.status),
  );
  const overdue = active.filter(
    (item) =>
      item.promisedProposalDate && item.promisedProposalDate < todayIso(),
  ).length;
  const sent = data.items.filter(
    (item) =>
      item.proposalSentAt ||
      ["sent", "accepted", "declined"].includes(item.status),
  ).length;
  const quoteStages = [
    {
      label: "Entrada e análise",
      tone: "slate",
      value: data.items.filter((item) =>
        ["new", "under_review"].includes(item.status),
      ).length,
    },
    {
      label: "Vistoria",
      tone: "violet",
      value: data.items.filter((item) =>
        ["inspection_pending", "inspection_scheduled"].includes(item.status),
      ).length,
    },
    {
      label: "Preparação",
      tone: "amber",
      value: data.items.filter((item) =>
        ["waiting_distributor", "preparing"].includes(item.status),
      ).length,
    },
    { label: "Propostas enviadas", tone: "sage", value: sent },
  ];
  return (
    <div className="quotes-page page-stack">
      <section className="page-hero compact">
        <div>
          <span>GESTÃO COMERCIAL</span>
          <h1>Orçamentos</h1>
          <p>
            Solicitações recebidas pelo portal do cliente, prazos, vistorias e
            propostas.
          </p>
        </div>
        {canManage && (
          <button className="primary quote-create-button" onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} />
            Nova solicitação
          </button>
        )}
      </section>
      {error && <div className="empty error-text">{error}</div>}
      <div className="quote-overview">
        <div className="quote-kpis">
          <article>
            <i><Icon name="clipboard" size={17} /></i>
            <div><span>Total</span><strong>{data.items.length}</strong><small>solicitações</small></div>
          </article>
          <article>
            <i><Icon name="trend" size={17} /></i>
            <div><span>Em andamento</span><strong>{active.length}</strong><small>em tratamento</small></div>
          </article>
          <article className={overdue ? "attention" : ""}>
            <i><Icon name="clock" size={17} /></i>
            <div><span>Prazos vencidos</span><strong>{overdue}</strong><small>exigem atenção</small></div>
          </article>
          <article>
            <i><Icon name="file-download" size={17} /></i>
            <div><span>Propostas enviadas</span><strong>{sent}</strong><small>com proposta</small></div>
          </article>
        </div>
        <section className="quote-stage-chart" aria-label="Distribuição das solicitações por etapa">
          <header>
            <div><strong>Distribuição por etapa</strong><small>Visão rápida do fluxo comercial</small></div>
            <b>{data.items.length}</b>
          </header>
          <div>
            {quoteStages.map((stage) => (
              <span className={`quote-stage ${stage.tone}`} key={stage.label}>
                <label><small>{stage.label}</small><b>{stage.value}</b></label>
                <i><em style={{ width: `${(stage.value / Math.max(data.items.length, 1)) * 100}%` }} /></i>
              </span>
            ))}
          </div>
        </section>
      </div>
      <section className="panel quote-list-panel">
        <div className="quote-toolbar">
          <label className="quote-filter-field quote-search-field">
            <span>Busca</span>
            <div className="search-box">
              <Icon name="search" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Número, assunto, cliente, unidade ou local"
              />
            </div>
          </label>
          <label className="quote-filter-field">
            <span>Cliente</span>
            <div className="quote-select-control">
              <Icon name="building" size={16} />
              <select
                value={clientFilter}
                onChange={(event) => {
                  setClientFilter(event.target.value);
                  setUnitFilter("");
                }}
              >
                <option value="">Todos os clientes</option>
                {data.clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
          </label>
          <label className="quote-filter-field">
            <span>Unidade</span>
            <div className={`quote-select-control ${!clientFilter ? "disabled" : ""}`}>
              <Icon name="pin" size={16} />
              <select
                disabled={!clientFilter}
                value={unitFilter}
                onChange={(event) => setUnitFilter(event.target.value)}
              >
                <option value="">Todas as unidades</option>
                {filteredUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>{unit.name}</option>
                ))}
              </select>
            </div>
          </label>
          <label className="quote-filter-field quote-status-field">
            <span>Situação</span>
            <div className="quote-select-control">
              <Icon name="filter" size={16} />
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="">Todos os status</option>
                {quoteStatusOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </label>
        </div>
        <div className="quote-table">
          <div className="quote-row quote-head">
            <span>Solicitação</span>
            <span>Cliente / unidade</span>
            <span>Prazo assumido</span>
            <span>Status</span>
          </div>
          {items.map((item) => (
            <div
              className={`quote-row quote-data-row${item.proposalSentAt || ["sent", "accepted", "declined"].includes(item.status) ? " sent" : ""}`}
              key={item.id}
              onClick={() => open(item)}
              onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); open(item); } }}
              role="button"
              tabIndex={0}
            >
              <span>
                <b>ORC-{String(item.id).padStart(5, "0")}</b>
                <strong>{item.title}</strong>
                <small>{item.location}</small>
              </span>
              <span>
                <strong>{item.clientName}</strong>
                <small>{item.unitName}</small>
              </span>
              <span
                className={
                  item.promisedProposalDate < todayIso() &&
                  !["sent", "accepted", "declined", "cancelled"].includes(
                    item.status,
                  )
                    ? "overdue"
                    : ""
                }
              >
                {quoteDisplayDate(item.promisedProposalDate)}
              </span>
              <span className="quote-row-status">
                <i className={`status-pill ${quoteStatusTone(item.status)}`}>
                  {quoteStatusLabel(item.status)}
                </i>
                {canManage && !item.proposalSentAt && !["sent", "accepted", "declined", "cancelled"].includes(item.status) && (
                  <button type="button" className="quote-mark-sent" disabled={markingSentId === item.id} onClick={(event) => { event.stopPropagation(); markSent(item); }}>
                    {markingSentId === item.id ? "Marcando…" : "Marcar como enviada"}
                  </button>
                )}
              </span>
            </div>
          ))}
          {!items.length && (
            <div className="empty">Nenhuma solicitação encontrada.</div>
          )}
        </div>
      </section>
      {selected && (
        <QuoteManagementModal
          item={selected}
          assignees={data.assignees}
          token={token}
          canManage={canManage}
          canDelete={canDelete}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
      {creating && (
        <CreateQuoteModal
          token={token}
          setup={data}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function CreateQuoteModal({ token, setup, onClose, onSaved }) {
  const [form, setForm] = useState({
    clientId: "",
    unitId: "",
    requestedByContactId: "",
    title: "",
    location: "",
    category: "Projeto",
    description: "",
    photos: [],
    attachments: [],
  });
  const [saving, setSaving] = useState(false);
  const [readingPhotos, setReadingPhotos] = useState(false);
  const [readingAttachments, setReadingAttachments] = useState(false);
  const [error, setError] = useState("");
  const units = (setup.units || []).filter(
    (item) => String(item.clientId) === String(form.clientId),
  );
  const contacts = (setup.contacts || []).filter(
    (item) =>
      String(item.clientId) === String(form.clientId) &&
      String(item.unitId) === String(form.unitId),
  );
  const submit = async (event) => {
    event.preventDefault();
    if (readingPhotos || readingAttachments) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/quotes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (response.ok) onSaved();
      else setError("Confira os campos e as fotos selecionadas.");
    } catch {
      setError("Não foi possível enviar a solicitação. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <form className="modal portal-quote-modal internal-quote-create-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <span>NOVA SOLICITAÇÃO</span>
            <h2>Registrar pedido do cliente</h2>
            <p>O pedido ficará disponível no fluxo comercial e no portal do solicitante.</p>
          </div>
          <button type="button" onClick={onClose}>×</button>
        </div>
        <div className="form-grid">
          <label>
            Cliente
            <select required value={form.clientId} onChange={(event) => setForm({...form,clientId:event.target.value,unitId:"",requestedByContactId:""})}>
              <option value="">Selecione</option>
              {(setup.clients || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>
            Unidade
            <select required disabled={!form.clientId} value={form.unitId} onChange={(event) => setForm({...form,unitId:event.target.value,requestedByContactId:""})}>
              <option value="">Selecione</option>
              {units.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label className="wide">
            Solicitante do cliente
            <select required disabled={!form.unitId} value={form.requestedByContactId} onChange={(event) => setForm({...form,requestedByContactId:event.target.value})}>
              <option value="">Selecione</option>
              {contacts.map((item) => <option key={item.id} value={item.id}>{item.name}{item.email ? ` — ${item.email}` : ""}</option>)}
            </select>
            {form.unitId && !contacts.length && <small>Esta unidade não possui responsável com acesso a Orçamentos no portal.</small>}
          </label>
          <label className="wide">
            Título da solicitação
            <input required value={form.title} onChange={(event) => setForm({...form,title:event.target.value})} placeholder="Ex.: Ampliação do sistema de CFTV" />
          </label>
          <label>
            Local do serviço ou entrega
            <input required value={form.location} onChange={(event) => setForm({...form,location:event.target.value})} placeholder="Ex.: Galpão 2" />
          </label>
          <label>
            Categoria
            <select required value={form.category} onChange={(event) => setForm({...form,category:event.target.value})}>
              {quoteCategoryOptions.map((category) => <option key={category}>{category}</option>)}
            </select>
          </label>
          <label className="wide quote-description-field">
            Descrição da solicitação
            <textarea required rows={6} value={form.description} onChange={(event) => setForm({...form,description:event.target.value})} placeholder="Informe escopo, quantidades, condições do local e demais detalhes." />
          </label>
        </div>
        <TicketPhotoPicker photos={form.photos} onChange={(photos) => setForm((current) => ({...current,photos}))} title="Fotos da solicitação" description="Até 5 fotos JPG, PNG ou WebP de até 4 MB cada." disabled={saving || readingPhotos} onBusyChange={setReadingPhotos} hideFileNames />
        <QuoteAttachments files={form.attachments} onChange={(attachments) => setForm((current) => ({...current,attachments}))} disabled={saving || readingAttachments} onBusyChange={setReadingAttachments} />
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Cancelar</button>
          <button className="primary" disabled={saving || readingPhotos || readingAttachments}>{saving ? "Registrando…" : "Criar solicitação"}</button>
        </div>
      </form>
    </div>
  );
}

function QuoteManagementModal({
  item,
  assignees,
  token,
  canManage,
  canDelete,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    status: item.status,
    promisedProposalDate: item.promisedProposalDate || "",
    inspectionScheduledAt: item.inspectionScheduledAt
      ? String(item.inspectionScheduledAt).slice(0, 16)
      : "",
    assignedUserId: item.assignedUserId || "",
    proposalNumber: item.proposalNumber || "",
    internalNotes: item.internalNotes || "",
    publicNote: "",
  });
  const [proposal, setProposal] = useState(null),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const fileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError("A proposta deve ser um arquivo PDF.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setProposal({
        proposalDataUrl: reader.result,
        proposalFileName: file.name,
      });
    reader.readAsDataURL(file);
  };
  const persist = async (overrides) => {
    setSaving(true);
    setError("");
    const response = await fetch(`${apiBase}/quotes/${item.id}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...form,
        assignedUserId: form.assignedUserId || null,
        ...proposal,
        ...overrides,
      }),
    });
    if (response.ok) onSaved();
    else {
      const detail = await response.json().catch(() => ({}));
      setError(
        detail.error === "invalid_quote_pdf"
          ? "PDF inválido ou maior que o limite permitido."
          : "Não foi possível atualizar o orçamento.",
      );
      setSaving(false);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    await persist({});
  };
  const markAsSent = async () => {
    await persist({
      status: "sent",
      publicNote: form.publicNote.trim() || "Proposta enviada ao cliente",
    });
  };
  const deleteRequest = async () => {
    const number = `ORC-${String(item.id).padStart(5, "0")}`;
    if (!window.confirm(`Excluir definitivamente a solicitação ${number} (${item.title})? O histórico, as fotos e a proposta anexada serão removidos.`)) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/quotes/${item.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("delete_failed");
      onSaved();
    } catch {
      setError("Não foi possível excluir a solicitação.");
      setSaving(false);
    }
  };
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form className="modal quote-modal" onSubmit={save}>
        <div className="modal-head">
          <div>
            <span>ORC-{String(item.id).padStart(5, "0")}</span>
            <h2>{item.title}</h2>
            <p>
              {item.clientName} • {item.unitName}
            </p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="quote-modal-body">
          <div className="quote-request-summary">
            <h3>Solicitação do cliente</h3>
            <dl>
              <div>
                <dt>Local</dt>
                <dd>{item.location}</dd>
              </div>
              <div className="wide">
                <dt>Descrição da solicitação</dt>
                <dd>{item.description}</dd>
              </div>
              <div>
                <dt>Solicitante</dt>
                <dd>{item.requestedByName || "—"}</dd>
              </div>
              <div>
                <dt>Recebida em</dt>
                <dd>{new Date(item.createdAt).toLocaleString("pt-BR")}</dd>
              </div>
              {item.proposalSentAt && (
                <div>
                  <dt>Proposta enviada em</dt>
                  <dd>
                    {new Date(item.proposalSentAt).toLocaleString("pt-BR")}
                  </dd>
                </div>
              )}
            </dl>
            <QuotePhotoGallery photos={item.photos} />
            <QuoteAttachments files={item.attachments} />
          </div>
          <div className="quote-management-form">
            <h3>Gestão do orçamento</h3>
            <div className="form-grid">
              <label>
                Status
                <select
                  disabled={!canManage}
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {quoteStatusOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Prazo de entrega da proposta
                <input
                  disabled={!canManage}
                  type="date"
                  value={form.promisedProposalDate}
                  onChange={(e) =>
                    setForm({ ...form, promisedProposalDate: e.target.value })
                  }
                />
              </label>
              <label>
                Vistoria agendada
                <input
                  disabled={!canManage}
                  type="datetime-local"
                  value={form.inspectionScheduledAt}
                  onChange={(e) =>
                    setForm({ ...form, inspectionScheduledAt: e.target.value })
                  }
                />
              </label>
              <label>
                Responsável
                <select
                  disabled={!canManage}
                  value={form.assignedUserId}
                  onChange={(e) =>
                    setForm({ ...form, assignedUserId: e.target.value })
                  }
                >
                  <option value="">Não definido</option>
                  {assignees.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Número da proposta
                <input
                  disabled={!canManage}
                  value={form.proposalNumber}
                  onChange={(e) =>
                    setForm({ ...form, proposalNumber: e.target.value })
                  }
                />
              </label>
              <label>
                Proposta em PDF
                <input
                  disabled={!canManage}
                  type="file"
                  accept="application/pdf"
                  onChange={fileChange}
                />
                <small>
                  {proposal?.proposalFileName ||
                    item.proposalFileName ||
                    "Nenhum PDF anexado"}
                </small>
              </label>
              <label className="wide">
                Atualização visível ao cliente
                <textarea
                  disabled={!canManage}
                  value={form.publicNote}
                  onChange={(e) =>
                    setForm({ ...form, publicNote: e.target.value })
                  }
                  placeholder="Ex.: vistoria confirmada para a data combinada"
                />
              </label>
              <label className="wide">
                Observações internas
                <textarea
                  disabled={!canManage}
                  value={form.internalNotes}
                  onChange={(e) =>
                    setForm({ ...form, internalNotes: e.target.value })
                  }
                />
              </label>
            </div>
            {item.proposalDataUrl && (
              <a
                className="secondary"
                href={item.proposalDataUrl}
                download={item.proposalFileName || "proposta.pdf"}
              >
                Baixar proposta atual
              </a>
            )}
          </div>
          <div className="quote-history">
            <h3>Histórico</h3>
            {item.history?.map((event) => (
              <div key={event.id}>
                <i />
                <span>
                  <strong>
                    {event.statusTo
                      ? quoteStatusLabel(event.statusTo)
                      : "Atualização"}
                  </strong>
                  {event.note && <p>{event.note}</p>}
                  <small>
                    {event.authorName} •{" "}
                    {new Date(event.createdAt).toLocaleString("pt-BR")}
                  </small>
                </span>
              </div>
            ))}
          </div>
        </div>
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          {canDelete && (
            <button type="button" className="quote-delete-action" disabled={saving} onClick={deleteRequest}>
              Excluir solicitação
            </button>
          )}
          <button type="button" onClick={onClose}>
            Fechar
          </button>
          {canManage &&
            !["sent", "accepted", "declined", "cancelled"].includes(
              item.status,
            ) && (
              <button
                type="button"
                className="quote-close-action"
                disabled={saving}
                onClick={markAsSent}
              >
                <Icon name="check" size={16} />
                Registrar proposta enviada e dar baixa
              </button>
            )}
          {canManage && (
            <button className="primary" disabled={saving}>
              {saving ? "Salvando…" : "Salvar alterações"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function ClientsPage({ token }) {
  const [clients, setClients] = useState([]);
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [stateFilter, setStateFilter] = useState("Todos");
  const [sortOrder, setSortOrder] = useState("name_asc");
  const [selectedId, setSelectedId] = useState(null);
  const [access, setAccess] = useState(null);
  const [view, setView] = useState("list");
  const [tab, setTab] = useState("general");
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  const loadClients = () =>
    fetch(`${apiBase}/clients`, { headers })
      .then((r) => r.json())
      .then(setClients)
      .catch(() => setError("Não foi possível carregar os clientes."));
  const loadAccess = (id) => {
    setAccess(null);
    return fetch(`${apiBase}/clients/${id}/access-management`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        setAccess(data);
        setSelectedId(id);
      })
      .catch(() => setError("Não foi possível abrir o cadastro."));
  };
  useEffect(() => {
    loadClients();
  }, []);
  const openClient = (id) => {
    setView("detail");
    setTab("general");
    loadAccess(id);
  };
  const savedClient = (id) => {
    loadClients();
    openClient(id);
  };
  const deletedClient = () => {
    setAccess(null);
    setView("list");
    loadClients();
  };
  const normalizeSearch = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const states = [
    ...new Set(clients.map((client) => client.state).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const visibleClients = clients
    .filter((client) => {
      const searchable = normalizeSearch(
        [
          client.name,
          client.tradeName,
          client.legalName,
          client.document,
          client.email,
          client.phone,
          client.city,
          client.state,
        ].join(" "),
      );
      return (
        searchable.includes(normalizeSearch(directoryQuery)) &&
        (statusFilter === "Todos" ||
          (statusFilter === "Ativos"
            ? client.status !== "inactive"
            : client.status === "inactive")) &&
        (stateFilter === "Todos" || client.state === stateFilter)
      );
    })
    .sort((a, b) => {
      const nameA = a.tradeName || a.name || "";
      const nameB = b.tradeName || b.name || "";
      if (sortOrder === "name_desc") return nameB.localeCompare(nameA, "pt-BR");
      if (sortOrder === "recent") return Number(b.id) - Number(a.id);
      return nameA.localeCompare(nameB, "pt-BR");
    });
  const clearDirectoryFilters = () => {
    setDirectoryQuery("");
    setStatusFilter("Todos");
    setStateFilter("Todos");
    setSortOrder("name_asc");
  };
  if (view === "form")
    return (
      <ClientFullForm
        token={token}
        client={access?.client || null}
        onCancel={() => setView(access ? "detail" : "list")}
        onSaved={savedClient}
        onDeleted={deletedClient}
      />
    );
  if (view === "detail")
    return (
      <>
        <div className="client-detail-heading">
          <button
            onClick={() => {
              setView("list");
              setAccess(null);
            }}
          >
            ‹ Voltar para clientes
          </button>
          {access && (
            <div>
              <div>
                <span className="client-logo blue">
                  {initials(access.client.name)}
                </span>
                <div>
                  <h2>{access.client.tradeName || access.client.name}</h2>
                  <p>
                    {formatDocument(access.client.document)} •{" "}
                    {access.units.length} unidades • {access.contacts.length}{" "}
                    responsáveis
                  </p>
                </div>
              </div>
              <button
                className="client-edit-button"
                onClick={() => setView("form")}
              >
                <Icon name="edit" size={16} />
                Editar dados gerais
              </button>
            </div>
          )}
        </div>
        {!access ? (
          <div className="panel empty">Carregando ficha do cliente…</div>
        ) : (
          <>
            <div className="client-detail-tabs">
              <button
                className={tab === "general" ? "active" : ""}
                onClick={() => setTab("general")}
              >
                Dados gerais
              </button>
              <button
                className={tab === "units" ? "active" : ""}
                onClick={() => setTab("units")}
              >
                Unidades <b>{access.units.length}</b>
              </button>
              <button
                className={tab === "contacts" ? "active" : ""}
                onClick={() => setTab("contacts")}
              >
                Responsáveis <b>{access.contacts.length}</b>
              </button>
            </div>
            {tab === "general" && <ClientGeneralView client={access.client} />}{" "}
            {tab === "units" && (
              <section className="panel client-entities">
                <div className="entity-title">
                  <div>
                    <h3>Unidades do cliente</h3>
                    <p>
                      Clique em uma unidade para visualizar e editar seus dados.
                    </p>
                  </div>
                  <button
                    className="primary"
                    onClick={() => setEditor({ type: "unit" })}
                  >
                    <Icon name="plus" size={15} /> Nova unidade
                  </button>
                </div>
                <div className="unit-card-grid">
                  {access.units.map((unit) => (
                    <button
                      key={unit.id}
                      onClick={() => setEditor({ type: "unit", record: unit })}
                    >
                      <div className="metric-icon blue">
                        <Icon name="building" />
                      </div>
                      <div>
                        <strong>{unit.name}</strong>
                        <span>{unit.code || "Sem código interno"}</span>
                        <small>
                          {[unit.address, unit.city, unit.state]
                            .filter(Boolean)
                            .join(" • ") || "Endereço não informado"}
                        </small>
                      </div>
                      <span
                        className={`record-status ${unit.active ? "done" : "new"}`}
                      >
                        {unit.active ? "Ativa" : "Inativa"}
                      </span>
                      {Boolean(unit.isPrimary) && (
                        <span className="record-status primary-unit-status">
                          Principal
                        </span>
                      )}
                      <span className="unit-edit-label">
                        <Icon name="edit" size={14} /> Editar
                      </span>
                    </button>
                  ))}
                  {!access.units.length && (
                    <div className="empty">Nenhuma unidade cadastrada.</div>
                  )}
                </div>
              </section>
            )}{" "}
            {tab === "contacts" && (
              <section className="panel client-entities">
                <div className="entity-title">
                  <div>
                    <h3>Responsáveis e acessos</h3>
                    <p>Contatos, unidades permitidas e permissões do portal.</p>
                  </div>
                  <button
                    className="primary"
                    onClick={() => setEditor({ type: "contact" })}
                  >
                    <Icon name="plus" size={15} /> Novo responsável
                  </button>
                </div>
                <div className="contact-head">
                  <span>Responsável</span>
                  <span>Unidades permitidas</span>
                  <span>Atributos do portal</span>
                  <span>Status</span>
                </div>
                {access.contacts.map((contact) => (
                  <button
                    className="contact-row editable-contact"
                    key={contact.id}
                    title="Editar responsável"
                    onClick={() =>
                      setEditor({ type: "contact", record: contact })
                    }
                  >
                    <div>
                      <span className="avatar small">
                        {initials(contact.name)}
                      </span>
                      <div>
                        <strong>{contact.name}</strong>
                        <small>
                          {contact.email} •{" "}
                          {contact.jobTitle || "Função não informada"}
                        </small>
                      </div>
                    </div>
                    <span>
                      {contact.unitIds
                        .map(
                          (id) =>
                            access.units.find((unit) => unit.id === id)?.name,
                        )
                        .filter(Boolean)
                        .join(", ") || "Nenhuma unidade"}
                    </span>
                    <span>
                      {contact.permissions
                        .map(portalPermissionLabel)
                        .join(", ") || "Sem permissões"}
                    </span>
                    <span
                      className={`record-status ${contact.portalEnabled ? "done" : "new"}`}
                    >
                      {contact.portalEnabled ? "Portal ativo" : "Sem acesso"}
                    </span>
                  </button>
                ))}
                {!access.contacts.length && (
                  <div className="empty">Nenhum responsável cadastrado.</div>
                )}
              </section>
            )}{" "}
          </>
        )}
        {editor && access && (
          <ClientEntityModal
            {...editor}
            client={access.client}
            units={access.units}
            technicians={access.technicians}
            residentTechnicians={access.residentTechnicians}
            token={token}
            onClose={() => setEditor(null)}
            onSaved={() => {
              setEditor(null);
              loadAccess(selectedId);
            }}
          />
        )}
      </>
    );
  return (
    <>
      <PageHeading
        title="Clientes"
        subtitle="Cadastros, unidades, responsáveis e acessos ao portal."
        button="Novo cliente"
        onCreate={() => {
          setAccess(null);
          setView("form");
        }}
      />
      {error && <div className="login-error">{error}</div>}
      <section className="panel client-directory-filters">
        <label className="client-directory-search">
          Buscar cliente
          <span>
            <Icon name="search" size={17} />
            <input
              value={directoryQuery}
              onChange={(event) => setDirectoryQuery(event.target.value)}
              placeholder="Nome, razão social, CNPJ, cidade, e-mail..."
            />
          </span>
        </label>
        <label>
          Situação
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option>Todos</option>
            <option>Ativos</option>
            <option>Inativos</option>
          </select>
        </label>
        <label>
          Estado
          <select
            value={stateFilter}
            onChange={(event) => setStateFilter(event.target.value)}
          >
            <option>Todos</option>
            {states.map((state) => (
              <option key={state}>{state}</option>
            ))}
          </select>
        </label>
        <label>
          Ordenar
          <select
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          >
            <option value="name_asc">Nome A–Z</option>
            <option value="name_desc">Nome Z–A</option>
            <option value="recent">Mais recentes</option>
          </select>
        </label>
        <div>
          <span>{visibleClients.length} cliente(s) encontrado(s)</span>
          <button type="button" onClick={clearDirectoryFilters}>
            Limpar filtros
          </button>
        </div>
      </section>
      <div className="client-directory">
        {visibleClients.map((client) => (
          <button
            className="panel"
            key={client.id}
            onClick={() => openClient(client.id)}
          >
            <span className="client-logo blue">{initials(client.name)}</span>
            <div>
              <strong>{client.tradeName || client.name}</strong>
              <small>{client.legalName || "Razão social não informada"}</small>
              <span>
                {formatDocument(client.document) || "CNPJ não informado"}
              </span>
            </div>
            <Icon name="arrow" />
          </button>
        ))}
        {!visibleClients.length && (
          <div className="panel empty">
            {clients.length
              ? "Nenhum cliente corresponde aos filtros selecionados."
              : "Nenhum cliente cadastrado."}
          </div>
        )}
      </div>
    </>
  );
}

function ResidentTechniciansPanel({ access, token, onSaved }) {
  const [selected, setSelected] = useState(
    (access.residentTechnicians || []).map((item) => item.id),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [startedVisitId, setStartedVisitId] = useState(null);
  const toggle = (id) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/clients/${access.client.id}/resident-technicians`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ technicianIds: selected }),
        },
      );
      if (!response.ok) throw new Error();
      await onSaved();
    } catch {
      setError("Não foi possível salvar os técnicos residentes.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="panel client-entities">
      <div className="entity-title">
        <div>
          <h3>Técnicos / OS residentes</h3>
          <p>
            Esses colaboradores atendem exclusivamente este cliente e visualizam
            todas as OS vinculadas a ele.
          </p>
        </div>
        <button className="primary" disabled={saving} onClick={save}>
          {saving ? "Salvando…" : "Salvar vínculos"}
        </button>
      </div>
      {error && <div className="login-error">{error}</div>}
      <div className="daily-technician-list">
        {(access.technicians || []).map((item) => (
          <label key={item.id}>
            <input
              type="checkbox"
              checked={selected.includes(item.id)}
              onChange={() => toggle(item.id)}
            />
            <span>
              <strong>{item.name}</strong>
              <small>
                {item.role || "Técnico"} • {item.email || "Sem e-mail"}
              </small>
            </span>
          </label>
        ))}
        {!access.technicians?.length && (
          <div className="empty">Nenhum colaborador ativo disponível.</div>
        )}
      </div>
    </section>
  );
}

function ClientGeneralView({ client }) {
  const address = [
    client.street,
    client.addressNumber,
    client.complement,
    client.district,
    client.city,
    client.state,
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <div className="client-general-grid">
      <section className="panel detail-card">
        <h3>
          <Icon name="building" size={17} /> Dados principais
        </h3>
        <dl>
          <div>
            <dt>Razão social</dt>
            <dd>{client.legalName || client.name}</dd>
          </div>
          <div>
            <dt>Nome fantasia</dt>
            <dd>{client.tradeName || "Não informado"}</dd>
          </div>
          <div>
            <dt>CNPJ</dt>
            <dd>{formatDocument(client.document)}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{client.status === "inactive" ? "Inativo" : "Ativo"}</dd>
          </div>
          <div>
            <dt>Identificação por NFC</dt>
            <dd>{client.nfcEnabled ? "Ativada" : "Desativada"}</dd>
          </div>
        </dl>
      </section>
      <section className="panel detail-card">
        <h3>
          <Icon name="users" size={17} /> Contato principal
        </h3>
        <dl>
          <div>
            <dt>Responsável</dt>
            <dd>{client.contact || "Não informado"}</dd>
          </div>
          <div>
            <dt>E-mail</dt>
            <dd>{client.email || "Não informado"}</dd>
          </div>
          <div>
            <dt>Telefone</dt>
            <dd>{client.phone || "Não informado"}</dd>
          </div>
          <div>
            <dt>Site</dt>
            <dd>{client.website || "Não informado"}</dd>
          </div>
        </dl>
      </section>
      <section className="panel detail-card full">
        <h3>
          <Icon name="building" size={17} /> Endereço principal
        </h3>
        <p>{address || client.address || "Endereço não informado"}</p>
        <small>
          {client.zipCode ? `CEP ${client.zipCode}` : ""}{" "}
          {client.country || "Brasil"}
        </small>
      </section>
      {client.notes && (
        <section className="panel detail-card full">
          <h3>
            <Icon name="file" size={17} /> Observações
          </h3>
          <p>{client.notes}</p>
        </section>
      )}
    </div>
  );
}

function ClientFullForm({ token, client, onCancel, onSaved, onDeleted }) {
  const empty = {
    name: "",
    legalName: "",
    tradeName: "",
    document: "",
    contact: "",
    email: "",
    phone: "",
    website: "",
    zipCode: "",
    street: "",
    addressNumber: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    country: "Brasil",
    notes: "",
    status: "active",
    nfcEnabled: 0,
  };
  const [form, setForm] = useState({ ...empty, ...client });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const deleteClient = async () => {
    setDeleting(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/clients/${client.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error === "client_in_use"
          ? "Este cliente possui unidades ou registros vinculados. Remova os vínculos antes de excluir ou altere o status para Inativo."
          : data.error === "permission_denied"
            ? "Você não tem permissão para excluir clientes."
            : "Não foi possível excluir o cliente.");
      }
      onDeleted();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/clients${client ? `/${client.id}` : ""}`,
        {
          method: client ? "PATCH" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        },
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.error === "document_already_registered"
            ? "Este CNPJ já está cadastrado."
            : "Não foi possível salvar o cliente.",
        );
      }
      const data = client ? { id: client.id } : await response.json();
      onSaved(data.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form className="client-full-form" onSubmit={submit}>
      <div className="client-form-top">
        <div>
          <button type="button" onClick={onCancel}>
            ‹ Clientes
          </button>
          <h2>{client ? "Editar cliente" : "Novo cliente"}</h2>
          <p>Cadastre os dados do cliente e suas informações principais.</p>
        </div>
        <div className="client-steps">
          <span className="active">
            <b>1</b> Dados gerais
          </span>
          <i>›</i>
          <span>
            <b>2</b> Unidades
          </span>
          <i>›</i>
          <span>
            <b>3</b> Responsáveis
          </span>
        </div>
      </div>
      <div className="client-form-columns">
        <section className="panel form-section">
          <h3>
            <Icon name="building" size={17} /> Dados principais
          </h3>
          <div className="form-grid">
            <label>
              Nome de exibição *
              <input
                name="name"
                value={form.name}
                onChange={change}
                required
                placeholder="Nome usado no sistema"
              />
            </label>
            <label>
              Razão social *
              <input
                name="legalName"
                value={form.legalName}
                onChange={change}
                required
                placeholder="Razão social completa"
              />
            </label>
            <label>
              Nome fantasia
              <input
                name="tradeName"
                value={form.tradeName}
                onChange={change}
              />
            </label>
            <label>
              CNPJ *
              <input
                name="document"
                value={form.document}
                onChange={change}
                required
                placeholder="00.000.000/0000-00"
              />
            </label>
            <label>
              Site
              <input name="website" value={form.website} onChange={change} />
            </label>
            <label>
              Status
              <select name="status" value={form.status} onChange={change}>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </label>
            <div className="client-nfc-setting">
              <div>
                <strong>Identificação por NFC</strong>
                <small>Permite vincular e ler etiquetas NFC dos ativos deste cliente no Android.</small>
              </div>
              <button
                type="button"
                className={`client-nfc-toggle${form.nfcEnabled ? " is-active" : ""}`}
                role="switch"
                aria-checked={Boolean(form.nfcEnabled)}
                aria-label="Identificação por NFC"
                onClick={() => setForm({ ...form, nfcEnabled: form.nfcEnabled ? 0 : 1 })}
              >
                {form.nfcEnabled ? "Ativado" : "Desativado"}
              </button>
            </div>
          </div>
          <label>
            Descrição / observações
            <textarea
              name="notes"
              value={form.notes}
              onChange={change}
              maxLength="500"
              placeholder="Informações adicionais sobre o cliente"
            />
          </label>
        </section>
        <section className="panel form-section">
          <h3>
            <Icon name="users" size={17} /> Contato principal
          </h3>
          <div className="form-grid">
            <label>
              Nome do contato
              <input
                name="contact"
                value={form.contact || ""}
                onChange={change}
              />
            </label>
            <label>
              E-mail
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={change}
              />
            </label>
            <label>
              Telefone
              <input name="phone" value={form.phone} onChange={change} />
            </label>
          </div>
          <h3 className="address-title">
            <Icon name="building" size={17} /> Endereço principal
          </h3>
          <div className="form-grid address-grid">
            <label>
              CEP
              <input name="zipCode" value={form.zipCode} onChange={change} />
            </label>
            <label className="wide">
              Endereço
              <input name="street" value={form.street} onChange={change} />
            </label>
            <label>
              Número
              <input
                name="addressNumber"
                value={form.addressNumber}
                onChange={change}
              />
            </label>
            <label>
              Complemento
              <input
                name="complement"
                value={form.complement}
                onChange={change}
              />
            </label>
            <label>
              Bairro
              <input name="district" value={form.district} onChange={change} />
            </label>
            <label>
              Cidade
              <input name="city" value={form.city} onChange={change} />
            </label>
            <label>
              Estado
              <input name="state" value={form.state} onChange={change} />
            </label>
            <label>
              País
              <input name="country" value={form.country} onChange={change} />
            </label>
          </div>
        </section>
      </div>
      {error && <div className="login-error">{error}</div>}
      {confirmDelete && (
        <div className="client-delete-confirm" role="alertdialog" aria-label="Confirmar exclusão do cliente">
          <div>
            <strong>Excluir {client.name}?</strong>
            <span>Esta ação é permanente. Clientes com unidades ou histórico vinculado não podem ser excluídos.</span>
          </div>
          <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting}>Voltar</button>
          <button type="button" className="client-delete-confirm-button" onClick={deleteClient} disabled={deleting}>
            <Icon name="trash" size={16} /> {deleting ? "Excluindo…" : "Confirmar exclusão"}
          </button>
        </div>
      )}
      <div className="client-form-actions">
        {client && <button type="button" className="client-delete-trigger" onClick={() => setConfirmDelete(true)} disabled={saving || deleting}>
          <Icon name="trash" size={16} /> Excluir cliente
        </button>}
        <div className="client-form-actions-main">
        <button type="button" className="client-cancel-button" onClick={onCancel} disabled={saving || deleting}>
          <Icon name="close" size={16} /> Cancelar
        </button>
        <button className="primary client-save-button" disabled={saving || deleting}>
          <Icon name="check" size={16} />
          {saving
            ? "Salvando…"
            : client
              ? "Salvar alterações"
              : "Salvar e acessar unidades"}
        </button>
        </div>
      </div>
    </form>
  );
}

function ClientEntityModal({
  type,
  record,
  client,
  units,
  technicians = [],
  residentTechnicians = [],
  token,
  onClose,
  onSaved,
}) {
  const editing = Boolean(record);
  const isUnit = type === "unit";
  const unitEmpty = {
    name: "",
    code: "",
    document: "",
    email: "",
    phone: "",
    zipCode: "",
    address: "",
    city: "",
    state: "",
    active: 1,
    isPrimary: false,
    residentTechnicianIds: [],
  };
  const contactEmpty = {
    name: "",
    email: "",
    phone: "",
    jobTitle: "",
    portalEnabled: true,
    password: "",
    unitIds: [],
    permissions: ["open_tickets", "view_service_orders", "request_quotes"],
  };
  const [form, setForm] = useState(
    isUnit
      ? {
          ...unitEmpty,
          ...record,
          residentTechnicianIds: residentTechnicians
            .filter((item) => Number(item.unitId) === Number(record?.id))
            .map((item) => item.id),
        }
      : {
          ...contactEmpty,
          ...record,
          portalEnabled: Boolean(record?.portalEnabled),
        },
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const change = (e) =>
    setForm({
      ...form,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  const toggle = (field, value) =>
    setForm({
      ...form,
      [field]: form[field].includes(value)
        ? form[field].filter((item) => item !== value)
        : [...form[field], value],
    });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const base = `${apiBase}/clients/${client.id}/${isUnit ? "units" : "contacts"}`;
    try {
      const response = await fetch(`${base}${editing ? `/${record.id}` : ""}`, {
        method: editing ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.error === "email_already_registered"
            ? "Este e-mail já está cadastrado."
            : "Não foi possível salvar.",
        );
      }
      if (isUnit) {
        const saved = editing ? null : await response.json();
        const unitId = record?.id || saved?.id;
        const residentResponse = await fetch(
          `${apiBase}/clients/${client.id}/units/${unitId}/resident-technicians`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              technicianIds: form.residentTechnicianIds || [],
            }),
          },
        );
        if (!residentResponse.ok) {
          const detail = await residentResponse.json().catch(() => ({}));
          throw new Error(
            detail.error === "resident_already_linked_to_another_unit"
              ? `Este técnico já é residente da unidade ${detail.unitName}.`
              : "A unidade foi salva, mas não foi possível vincular os residentes.",
          );
        }
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const removeUnit = async () => {
    if (!isUnit || !editing || deleting) return;
    const confirmed = window.confirm(
      `Excluir a unidade “${record.name}”? Esta ação não poderá ser desfeita.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/clients/${client.id}/units/${record.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        if (detail.error === "unit_in_use") {
          throw new Error(
            "Esta unidade possui projetos, atendimentos, equipamentos ou acessos vinculados. Remova os vínculos ou deixe a unidade inativa.",
          );
        }
        if (detail.error === "permission_denied") {
          throw new Error(
            "Seu perfil não possui permissão para excluir unidades.",
          );
        }
        throw new Error("Não foi possível excluir a unidade.");
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };
  const removeContact = async () => {
    if (isUnit || !editing || deleting) return;
    const confirmed = window.confirm(
      `Excluir o responsável “${record.name}”? O acesso dele ao portal será encerrado.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/clients/${client.id}/contacts/${record.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok)
        throw new Error("Não foi possível excluir o responsável.");
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };
  const permissions = [
    ["view_projects", "Projetos"],
    ["open_tickets", "Chamados"],
    ["view_service_orders", "Ordens de serviço"],
    ["request_quotes", "Orçamentos"],
    ["view_reports", "Relatórios"],
    ["view_maintenance", "Preventivas"],
    ["view_assets", "Ativos nas preventivas"],
  ];
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving && !deleting)
          onClose();
      }}
    >
      <form className="modal access-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>
              {editing ? "Editar" : "Nova"} {isUnit ? "unidade" : "responsável"}
            </h2>
            <p>{client.tradeName || client.name}</p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        {isUnit ? (
          <>
            <label>
              Nome da unidade *
              <input name="name" value={form.name} onChange={change} required />
            </label>
            <div className="form-grid">
              <label>
                Código interno
                <input name="code" value={form.code || ""} onChange={change} />
              </label>
              <label>
                CNPJ
                <input
                  name="document"
                  value={form.document || ""}
                  onChange={change}
                />
              </label>
              <label>
                E-mail
                <input
                  type="email"
                  name="email"
                  value={form.email || ""}
                  onChange={change}
                />
              </label>
              <label>
                Telefone
                <input
                  name="phone"
                  value={form.phone || ""}
                  onChange={change}
                />
              </label>
              <label>
                CEP
                <input
                  name="zipCode"
                  value={form.zipCode || ""}
                  onChange={change}
                />
              </label>
              <label>
                Endereço
                <input
                  name="address"
                  value={form.address || ""}
                  onChange={change}
                />
              </label>
              <label>
                Cidade
                <input name="city" value={form.city || ""} onChange={change} />
              </label>
              <label>
                Estado
                <input
                  name="state"
                  value={form.state || ""}
                  onChange={change}
                />
              </label>
            </div>
            <fieldset className="resident-unit-selector">
              <legend>Técnicos residentes da unidade</legend>
              <p>
                Residentes ficam exclusivos desta unidade e recebem
                automaticamente as preventivas e OS corretivas locais.
              </p>
              <div className="daily-technician-list">
                {technicians.map((item) => (
                  <label key={item.id}>
                    <input
                      type="checkbox"
                      checked={(form.residentTechnicianIds || []).includes(
                        item.id,
                      )}
                      onChange={() => toggle("residentTechnicianIds", item.id)}
                    />
                    <span>
                      <strong>{item.name}</strong>
                      <small>{item.role || "Técnico"}</small>
                    </span>
                  </label>
                ))}
                {!technicians.length && (
                  <div className="empty">
                    Nenhum colaborador ativo cadastrado.
                  </div>
                )}
              </div>
            </fieldset>
            {editing && (
              <label className="portal-toggle">
                <input
                  type="checkbox"
                  name="active"
                  checked={Boolean(form.active)}
                  onChange={change}
                />
                <span>Unidade ativa</span>
              </label>
            )}
            <label className="portal-toggle">
              <input
                type="checkbox"
                name="isPrimary"
                checked={Boolean(form.isPrimary)}
                onChange={change}
                disabled={
                  !Boolean(form.active) ||
                  (editing && Boolean(record?.isPrimary))
                }
              />
              <span>Unidade principal no portal</span>
              <small>
                {editing && Boolean(record?.isPrimary)
                  ? "Para trocar, marque outra unidade como principal."
                  : "Será selecionada primeiro para os responsáveis autorizados."}
              </small>
            </label>
          </>
        ) : (
          <>
            <div className="form-grid">
              <label>
                Nome *
                <input
                  name="name"
                  value={form.name}
                  onChange={change}
                  required
                />
              </label>
              <label>
                E-mail *
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={change}
                  required
                />
              </label>
              <label>
                Cargo / função
                <input
                  name="jobTitle"
                  value={form.jobTitle || ""}
                  onChange={change}
                />
              </label>
              <label>
                Telefone
                <input
                  name="phone"
                  value={form.phone || ""}
                  onChange={change}
                />
              </label>
            </div>
            <label className="portal-toggle">
              <input
                type="checkbox"
                name="portalEnabled"
                checked={form.portalEnabled}
                onChange={change}
              />
              <span>Permitir acesso ao portal</span>
            </label>
            {form.portalEnabled && (
              <label>
                {editing ? "Nova senha (opcional)" : "Senha inicial *"}
                <input
                  type="password"
                  name="password"
                  value={form.password || ""}
                  onChange={change}
                  required={!editing}
                />
              </label>
            )}
            <fieldset>
              <legend>Unidades permitidas</legend>
              <div className="choice-grid">
                {units.map((unit) => (
                  <label key={unit.id}>
                    <input
                      type="checkbox"
                      checked={form.unitIds.includes(unit.id)}
                      onChange={() => toggle("unitIds", unit.id)}
                    />
                    <span>{unit.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>Permissões do portal</legend>
              <div className="choice-grid">
                {permissions.map(([value, label]) => (
                  <label key={value}>
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(value)}
                      onChange={() => toggle("permissions", value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          {isUnit && editing && (
            <button
              type="button"
              className="unit-delete-button"
              onClick={removeUnit}
              disabled={saving || deleting}
            >
              <Icon name="trash" size={14} />
              {deleting ? "Excluindo…" : "Excluir unidade"}
            </button>
          )}
          {!isUnit && editing && (
            <button
              type="button"
              className="unit-delete-button"
              onClick={removeContact}
              disabled={saving || deleting}
            >
              <Icon name="trash" size={14} />
              {deleting ? "Excluindo…" : "Excluir responsável"}
            </button>
          )}
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving || deleting}>
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ClientsPageLegacy({ token }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [access, setAccess] = useState(null);
  const [modal, setModal] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  const loadClients = () =>
    fetch(`${apiBase}/clients`, { headers })
      .then((r) => r.json())
      .then((data) => {
        setClients(data);
        if (!selected && data[0]) setSelected(data[0]);
      })
      .catch(() => setError("Não foi possível carregar os clientes."));
  const loadAccess = (client) => {
    if (!client) return;
    setAccess(null);
    fetch(`${apiBase}/clients/${client.id}/access-management`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setAccess)
      .catch(() =>
        setError("Não foi possível carregar unidades e responsáveis."),
      );
  };
  useEffect(() => {
    loadClients();
  }, []); // carregamento inicial
  useEffect(() => {
    loadAccess(selected);
  }, [selected]);
  useEffect(() => {
    const edit = () => {
      if (access) setModal("edit-client");
    };
    window.addEventListener("aboutplan:edit-client", edit);
    return () => window.removeEventListener("aboutplan:edit-client", edit);
  }, [access]);
  return (
    <>
      <PageHeading
        title="Clientes e acessos"
        subtitle="Dados cadastrais, unidades, responsáveis e permissões do portal."
        button="Novo cliente"
        onCreate={() => setModal("client")}
      />
      <div className="clients-layout">
        <aside className="client-list panel">
          <div className="client-list-title">
            <strong>Clientes</strong>
            <span>{clients.length}</span>
          </div>
          {clients.map((client) => (
            <button
              className={selected?.id === client.id ? "selected" : ""}
              onClick={() => setSelected(client)}
              key={client.id}
            >
              <span className="client-logo blue">{initials(client.name)}</span>
              <div>
                <strong>{client.name}</strong>
                <small>
                  {formatDocument(client.document) || "Documento não informado"}
                </small>
              </div>
              <Icon name="arrow" size={15} />
            </button>
          ))}
        </aside>
        <section className="client-access panel">
          {error && <div className="empty error-text">{error}</div>}
          {!access && !error && (
            <div className="empty">Carregando cadastro…</div>
          )}
          {access && (
            <>
              <div className="client-access-head">
                <div>
                  <h2>{access.client.tradeName || access.client.name}</h2>
                  <p>
                    {access.client.legalName || "Razão social não informada"} •{" "}
                    {formatDocument(access.client.document)}
                  </p>
                </div>
                <div>
                  <button
                    className="secondary-button"
                    onClick={() => setModal("unit")}
                  >
                    <Icon name="plus" size={14} /> Unidade
                  </button>
                  <button
                    className="primary"
                    onClick={() => setModal("contact")}
                  >
                    <Icon name="plus" size={14} /> Responsável
                  </button>
                </div>
              </div>
              <div className="client-data-strip">
                <div>
                  <span>Contato geral</span>
                  <b>
                    {access.client.email ||
                      access.client.contact ||
                      "Não informado"}
                  </b>
                  <small>{access.client.phone || "Sem telefone"}</small>
                </div>
                <div>
                  <span>Endereço principal</span>
                  <b>
                    {[access.client.street, access.client.addressNumber]
                      .filter(Boolean)
                      .join(", ") ||
                      access.client.address ||
                      "Não informado"}
                  </b>
                  <small>
                    {[access.client.city, access.client.state]
                      .filter(Boolean)
                      .join(" / ")}
                  </small>
                </div>
                <div>
                  <span>Unidades</span>
                  <b>{access.units.length}</b>
                  <small>cadastradas</small>
                </div>
              </div>
              <div className="unit-summary">
                <Icon name="building" />
                <div>
                  <strong>{access.units.length} unidades cadastradas</strong>
                  <p>
                    Cada responsável acessa somente as unidades explicitamente
                    selecionadas.
                  </p>
                </div>
              </div>
              <div className="contact-head">
                <span>Responsável</span>
                <span>Unidades permitidas</span>
                <span>Atributos do portal</span>
                <span>Status</span>
              </div>
              {access.contacts.map((contact) => (
                <div className="contact-row" key={contact.id}>
                  <div>
                    <span className="avatar small">
                      {initials(contact.name)}
                    </span>
                    <div>
                      <strong>{contact.name}</strong>
                      <small>
                        {contact.email} •{" "}
                        {contact.jobTitle || "Função não informada"}
                      </small>
                    </div>
                  </div>
                  <span>
                    {contact.unitIds
                      .map(
                        (id) =>
                          access.units.find((unit) => unit.id === id)?.name,
                      )
                      .filter(Boolean)
                      .join(", ") || "Nenhuma unidade"}
                  </span>
                  <span>
                    {contact.permissions
                      .map(portalPermissionLabel)
                      .join(", ") || "Sem permissões"}
                  </span>
                  <span
                    className={`record-status ${contact.portalEnabled ? "done" : "new"}`}
                  >
                    {contact.portalEnabled ? "Portal ativo" : "Sem acesso"}
                  </span>
                </div>
              ))}
              {!access.contacts.length && (
                <div className="empty">
                  Nenhum responsável com acesso cadastrado.
                </div>
              )}
            </>
          )}
        </section>
      </div>
      {modal && (
        <ClientAccessModal
          type={modal}
          client={access?.client}
          units={access?.units || []}
          token={token}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            if (modal === "client") loadClients();
            else loadAccess(selected);
          }}
        />
      )}
    </>
  );
}

function ClientAccessModal({ type, client, units, token, onClose, onSaved }) {
  const editingClient = type === "edit-client";
  const isClient = type === "client" || editingClient;
  const isUnit = type === "unit";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const emptyClient = {
    name: "",
    legalName: "",
    tradeName: "",
    document: "",
    email: "",
    phone: "",
    website: "",
    zipCode: "",
    street: "",
    addressNumber: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    country: "Brasil",
    notes: "",
  };
  const [form, setForm] = useState(
    isClient
      ? editingClient
        ? { ...emptyClient, ...client }
        : emptyClient
      : isUnit
        ? {
            name: "",
            code: "",
            document: "",
            email: "",
            phone: "",
            zipCode: "",
            address: "",
            city: "",
            state: "",
          }
        : {
            name: "",
            email: "",
            phone: "",
            jobTitle: "",
            portalEnabled: true,
            password: "",
            unitIds: [],
            permissions: [
              "open_tickets",
              "view_service_orders",
              "request_quotes",
            ],
          },
  );
  const change = (e) =>
    setForm({
      ...form,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  const toggle = (field, value) =>
    setForm({
      ...form,
      [field]: form[field].includes(value)
        ? form[field].filter((item) => item !== value)
        : [...form[field], value],
    });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const endpoint = isClient
        ? `${apiBase}/clients${editingClient ? `/${client.id}` : ""}`
        : `${apiBase}/clients/${client.id}/${isUnit ? "units" : "contacts"}`;
      const response = await fetch(endpoint, {
        method: editingClient ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.error === "email_already_registered"
            ? "Este e-mail já está cadastrado."
            : data.error === "document_already_registered"
              ? "Este CNPJ já está cadastrado."
              : "Não foi possível salvar.",
        );
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const permissionOptions = [
    ["open_tickets", "Abrir chamados"],
    ["view_service_orders", "Acompanhar ordens de serviço"],
    ["request_quotes", "Solicitar e acompanhar orçamentos"],
  ];
  return (
    <div className="modal-backdrop">
      <form className="modal access-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>
              {isClient
                ? "Novo cliente"
                : isUnit
                  ? "Nova unidade"
                  : "Novo responsável"}
            </h2>
            <p>{client?.name || "Cadastro empresarial"}</p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        {isClient ? (
          <>
            <div className="form-grid">
              <label>
                Nome de exibição
                <input
                  name="name"
                  value={form.name}
                  onChange={change}
                  required
                />
              </label>
              <label>
                CNPJ
                <input
                  name="document"
                  value={form.document}
                  onChange={change}
                  placeholder="00.000.000/0000-00"
                  required
                />
              </label>
              <label>
                Razão social
                <input
                  name="legalName"
                  value={form.legalName}
                  onChange={change}
                />
              </label>
              <label>
                Nome fantasia
                <input
                  name="tradeName"
                  value={form.tradeName}
                  onChange={change}
                />
              </label>
              <label>
                E-mail geral
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={change}
                />
              </label>
              <label>
                Telefone
                <input name="phone" value={form.phone} onChange={change} />
              </label>
              <label>
                Site
                <input name="website" value={form.website} onChange={change} />
              </label>
              <label>
                CEP
                <input name="zipCode" value={form.zipCode} onChange={change} />
              </label>
            </div>
            <fieldset>
              <legend>Endereço principal</legend>
              <div className="form-grid">
                <label>
                  Logradouro
                  <input name="street" value={form.street} onChange={change} />
                </label>
                <label>
                  Número
                  <input
                    name="addressNumber"
                    value={form.addressNumber}
                    onChange={change}
                  />
                </label>
                <label>
                  Complemento
                  <input
                    name="complement"
                    value={form.complement}
                    onChange={change}
                  />
                </label>
                <label>
                  Bairro
                  <input
                    name="district"
                    value={form.district}
                    onChange={change}
                  />
                </label>
                <label>
                  Cidade
                  <input name="city" value={form.city} onChange={change} />
                </label>
                <label>
                  Estado
                  <input name="state" value={form.state} onChange={change} />
                </label>
              </div>
            </fieldset>
            <label>
              Observações
              <textarea name="notes" value={form.notes} onChange={change} />
            </label>
          </>
        ) : isUnit ? (
          <>
            <label>
              Nome da unidade
              <input name="name" value={form.name} onChange={change} required />
            </label>
            <div className="form-grid">
              <label>
                Código interno
                <input name="code" value={form.code} onChange={change} />
              </label>
              <label>
                CNPJ da unidade
                <input
                  name="document"
                  value={form.document}
                  onChange={change}
                />
              </label>
              <label>
                E-mail
                <input name="email" value={form.email} onChange={change} />
              </label>
              <label>
                Telefone
                <input name="phone" value={form.phone} onChange={change} />
              </label>
              <label>
                CEP
                <input name="zipCode" value={form.zipCode} onChange={change} />
              </label>
              <label>
                Endereço
                <input name="address" value={form.address} onChange={change} />
              </label>
              <label>
                Cidade
                <input name="city" value={form.city} onChange={change} />
              </label>
              <label>
                Estado
                <input name="state" value={form.state} onChange={change} />
              </label>
            </div>
          </>
        ) : (
          <>
            <div className="form-grid">
              <label>
                Nome
                <input
                  name="name"
                  value={form.name}
                  onChange={change}
                  required
                />
              </label>
              <label>
                E-mail
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={change}
                  required
                />
              </label>
              <label>
                Função/cargo
                <input
                  name="jobTitle"
                  value={form.jobTitle}
                  onChange={change}
                />
              </label>
              <label>
                Telefone
                <input name="phone" value={form.phone} onChange={change} />
              </label>
            </div>
            <label className="portal-toggle">
              <input
                type="checkbox"
                name="portalEnabled"
                checked={form.portalEnabled}
                onChange={change}
              />
              <span>Permitir acesso ao portal</span>
            </label>
            {form.portalEnabled && (
              <label>
                Senha inicial
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={change}
                  required
                />
              </label>
            )}
            <fieldset>
              <legend>Unidades que poderá acessar</legend>
              <div className="choice-grid">
                {units.map((unit) => (
                  <label key={unit.id}>
                    <input
                      type="checkbox"
                      checked={form.unitIds.includes(unit.id)}
                      onChange={() => toggle("unitIds", unit.id)}
                    />
                    <span>{unit.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>Atributos e permissões</legend>
              <div className="choice-grid">
                {permissionOptions.map(([value, label]) => (
                  <label key={value}>
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(value)}
                      onChange={() => toggle("permissions", value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving}>
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function portalPermissionLabel(permission) {
  return (
    {
      view_projects: "Projetos",
      open_tickets: "Chamados",
      view_service_orders: "OS",
      view_reports: "Relatórios",
      view_maintenance: "Manutenções",
      request_quotes: "Orçamentos",
    }[permission] || permission
  );
}

const maintenancePdfDate = (value) =>
  value
    ? new Date(`${String(value).slice(0, 10)}T12:00:00Z`).toLocaleDateString(
        "pt-BR",
      )
    : "—";
const maintenanceFileName = (value) =>
  String(value || "programacao")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_-]+/gi, "_");
async function maintenancePdfHeader(pdf, title, subtitle, page) {
  pdf.setFillColor(21, 25, 26);
  pdf.rect(0, 0, 297, 29, "F");
  try {
    const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
    const properties = pdf.getImageProperties(logo);
    const ratio = properties.width / properties.height;
    const width = 61;
    const height = width / ratio;
    pdf.addImage(logo, "PNG", 12, 7, width, height, undefined, "SLOW");
  } catch {}
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(title, 285, 11, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(201, 211, 228);
  pdf.text(subtitle || "", 285, 17, { align: "right", maxWidth: 170 });
  pdf.text(`Página ${page}`, 285, 23, { align: "right" });
  pdf.setDrawColor(187, 161, 108);
  pdf.setLineWidth(0.7);
  pdf.line(12, 28, 285, 28);
}
async function exportMaintenanceProgramPdf(items, subtitle) {
  if (!items.length)
    return window.alert(
      "Não há atividades nos filtros selecionados para exportar.",
    );
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  let page = 1;
  let y = 39;
  const columns = [
    ["Cliente / unidade", 12, 54],
    ["Atividade", 68, 58],
    ["Plano / frequência", 128, 55],
    ["Data", 185, 30],
    ["Status", 217, 28],
    ["Responsável", 247, 38],
  ];
  const drawHeader = async () => {
    await maintenancePdfHeader(
      pdf,
      "PROGRAMAÇÃO DE PREVENTIVAS",
      subtitle,
      page,
    );
    pdf.setFillColor(241, 245, 251);
    pdf.rect(10, 32, 277, 8, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(81, 94, 119);
    columns.forEach(([label, x]) => pdf.text(label, x, 37));
    y = 44;
  };
  await drawHeader();
  for (const item of items) {
    if (y > 194) {
      pdf.addPage();
      page++;
      await drawHeader();
    }
    const values = [
      `${item.clientName || "Sem cliente"}${item.unitName ? ` / ${item.unitName}` : ""}`,
      item.assetName ||
        item.checklistName ||
        item.planName ||
        "Atividade preventiva",
      `${item.planName || "Sem plano"} / ${frequencyLabel({ frequencyUnit: "month", frequencyInterval: item.frequencyInterval || 1 })}`,
      maintenancePdfDate(item.plannedDate),
      maintenanceGroup(item),
      item.assignedTo || "Não atribuído",
    ];
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.2);
    pdf.setTextColor(27, 38, 61);
    values.forEach((value, index) => {
      const [, x, width] = columns[index];
      pdf.text(pdf.splitTextToSize(String(value), width), x, y);
    });
    pdf.setDrawColor(226, 231, 239);
    pdf.line(10, y + 8, 287, y + 8);
    y += 12;
  }
  await addAboutsecPdfFooters(pdf);
  pdf.save(
    `Programacao_Preventivas_${maintenanceFileName(items[0]?.clientName || "Geral")}.pdf`,
  );
}
async function exportMaintenanceGanttPdf(items, subtitle) {
  if (!items.length)
    return window.alert(
      "Não há atividades nos filtros selecionados para exportar.",
    );
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const dated = items
    .filter((item) => item.plannedDate)
    .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
  if (!dated.length)
    return window.alert(
      "As atividades selecionadas não possuem datas planejadas.",
    );
  const groups = [
    ...dated
      .reduce((map, item) => {
        const key = `${item.planName || "Plano"}|${item.clientName || ""}|${item.unitName || ""}`;
        if (!map.has(key))
          map.set(key, {
            name: item.planName || "Plano preventivo",
            client: item.clientName || "Sem cliente",
            unit: item.unitName || "",
            items: [],
            start: item.planStartDate || item.plannedDate,
            end: item.planEndDate || item.plannedDate,
          });
        const group = map.get(key);
        group.items.push(item);
        if ((item.planStartDate || item.plannedDate) < group.start)
          group.start = item.planStartDate || item.plannedDate;
        if ((item.planEndDate || item.plannedDate) > group.end)
          group.end = item.planEndDate || item.plannedDate;
        return map;
      }, new Map())
      .values(),
  ];
  const parse = (value) => new Date(`${String(value).slice(0, 10)}T12:00:00Z`);
  const addDays = (date, days) => new Date(date.getTime() + days * 86400000);
  const boundaries = groups
    .flatMap((group) => [group.start, group.end])
    .filter(Boolean)
    .sort();
  const first = parse(boundaries[0]);
  const last = parse(boundaries[boundaries.length - 1]);
  const chunkDays = 31;
  let chunkStart = new Date(first);
  let page = 0;
  while (chunkStart <= last) {
    const chunkEnd = new Date(
      Math.min(addDays(chunkStart, chunkDays - 1).getTime(), last.getTime()),
    );
    let rowStart = 0;
    while (rowStart < groups.length) {
      if (page > 0) pdf.addPage();
      page++;
      await maintenancePdfHeader(
        pdf,
        "GANTT DE MANUTENÇÕES PREVENTIVAS",
        `${subtitle} • ${maintenancePdfDate(chunkStart.toISOString())} a ${maintenancePdfDate(chunkEnd.toISOString())}`,
        page,
      );
      const nameWidth = 72;
      const chartX = 85;
      const chartWidth = 200;
      const days = Math.round((chunkEnd - chunkStart) / 86400000) + 1;
      const dayWidth = chartWidth / days;
      let y = 39;
      pdf.setFillColor(241, 245, 251);
      pdf.rect(10, 32, 277, 12, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(81, 94, 119);
      pdf.text("PLANO / CLIENTE / UNIDADE", 12, 39);
      for (let day = 0; day < days; day++) {
        const date = addDays(chunkStart, day);
        const weekend = [0, 6].includes(date.getUTCDay());
        if (weekend) {
          pdf.setFillColor(232, 243, 255);
          pdf.rect(chartX + day * dayWidth, 32, dayWidth, 162, "F");
        }
        if (days <= 16 || day % 2 === 0) {
          pdf.setFontSize(days > 24 ? 5 : 6);
          pdf.text(
            String(date.getUTCDate()).padStart(2, "0"),
            chartX + (day + 0.5) * dayWidth,
            37,
            { align: "center" },
          );
          pdf.text(
            ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"][date.getUTCDay()],
            chartX + (day + 0.5) * dayWidth,
            41,
            { align: "center" },
          );
        }
        pdf.setDrawColor(224, 230, 239);
        pdf.line(chartX + day * dayWidth, 32, chartX + day * dayWidth, 194);
      }
      const pageGroups = groups.slice(rowStart, rowStart + 11);
      for (const group of pageGroups) {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(10, y + 6, 277, 13, "F");
        pdf.setTextColor(24, 35, 58);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        pdf.text(pdf.splitTextToSize(group.name, nameWidth - 5)[0], 12, y + 11);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(104, 116, 139);
        pdf.setFontSize(6);
        pdf.text(
          pdf.splitTextToSize(
            `${group.client}${group.unit ? ` • ${group.unit}` : ""}`,
            nameWidth - 5,
          )[0],
          12,
          y + 16,
        );
        const rangeStart = new Date(
          Math.max(parse(group.start).getTime(), chunkStart.getTime()),
        );
        const rangeEnd = new Date(
          Math.min(parse(group.end).getTime(), chunkEnd.getTime()),
        );
        if (rangeStart <= rangeEnd) {
          const startOffset = Math.round((rangeStart - chunkStart) / 86400000);
          const endOffset = Math.round((rangeEnd - chunkStart) / 86400000);
          pdf.setFillColor(214, 225, 247);
          pdf.roundedRect(
            chartX + startOffset * dayWidth + 0.3,
            y + 10,
            Math.max(1.5, (endOffset - startOffset + 1) * dayWidth - 0.6),
            4,
            0.8,
            0.8,
            "F",
          );
        }
        for (const item of group.items) {
          const date = parse(item.plannedDate);
          if (date < chunkStart || date > chunkEnd) continue;
          const offset = Math.round((date - chunkStart) / 86400000);
          const x = chartX + offset * dayWidth + 0.35;
          const color =
            maintenanceGroup(item) === "Realizadas"
              ? [37, 169, 112]
              : maintenanceGroup(item) === "Atrasadas"
                ? [235, 78, 91]
                : maintenanceGroup(item) === "Próximas"
                  ? [239, 164, 43]
                  : [45, 108, 246];
          pdf.setFillColor(...color);
          pdf.roundedRect(
            x,
            y + 9,
            Math.max(1.5, dayWidth - 0.7),
            6,
            1,
            1,
            "F",
          );
        }
        pdf.setDrawColor(226, 231, 239);
        pdf.line(10, y + 19, 287, y + 19);
        y += 14;
      }
      rowStart += pageGroups.length;
    }
    chunkStart = addDays(chunkEnd, 1);
  }
  await addAboutsecPdfFooters(pdf);
  pdf.save(
    `Gantt_Preventivas_${maintenanceFileName(dated[0]?.clientName || "Geral")}.pdf`,
  );
}

async function exportMaintenanceGanttExecutivePdf(items, subtitle) {
  const dated = items.filter((item) => item.plannedDate);
  if (!dated.length)
    return window.alert("Não há atividades com data nos filtros selecionados.");
  const parse = (value) => new Date(`${String(value).slice(0, 10)}T12:00:00Z`);
  const monthStart = (date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));
  const addMonths = (date, value) =>
    new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + value, 1, 12),
    );
  const plans = [
    ...dated
      .reduce((map, item) => {
        const key = `${item.clientName || ""}|${item.unitName || ""}|${item.planName || ""}`;
        if (!map.has(key))
          map.set(key, {
            client: item.clientName || "Sem cliente",
            unit: item.unitName || "Sem unidade",
            name: item.planName || "Plano preventivo",
            frequency:
              item.frequencyInterval === 1
                ? "Mensal"
                : `A cada ${item.frequencyInterval || 1} meses`,
            start: item.planStartDate || item.plannedDate,
            end: item.planEndDate || item.plannedDate,
            items: [],
          });
        const plan = map.get(key);
        plan.items.push(item);
        if (item.plannedDate < plan.start) plan.start = item.plannedDate;
        if (item.plannedDate > plan.end) plan.end = item.plannedDate;
        return map;
      }, new Map())
      .values(),
  ].sort(
    (a, b) =>
      a.client.localeCompare(b.client, "pt-BR") ||
      a.start.localeCompare(b.start),
  );
  const boundaries = plans.flatMap((plan) => [plan.start, plan.end]).sort();
  const globalStart = monthStart(parse(boundaries[0]));
  const globalLast = parse(boundaries[boundaries.length - 1]);
  const segments = [];
  for (
    let start = new Date(globalStart);
    start <= globalLast;
    start = addMonths(start, 12)
  ) {
    const next = addMonths(start, 12);
    segments.push({
      start,
      end: new Date(Math.min(next.getTime() - 86400000, globalLast.getTime())),
    });
  }
  const rows = [];
  let lastClient = "";
  for (const plan of plans) {
    if (plan.client !== lastClient) {
      rows.push({ type: "client", name: plan.client });
      lastClient = plan.client;
    }
    rows.push({ type: "plan", ...plan });
  }
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  let page = 0;
  const months = [
    "JAN",
    "FEV",
    "MAR",
    "ABR",
    "MAI",
    "JUN",
    "JUL",
    "AGO",
    "SET",
    "OUT",
    "NOV",
    "DEZ",
  ];
  for (const segment of segments) {
    for (let offset = 0; offset < rows.length; offset += 13) {
      if (page) pdf.addPage();
      page++;
      await maintenancePdfHeader(
        pdf,
        "PLANO DE MANUTENÇÕES PREVENTIVAS",
        `${subtitle} • ${maintenancePdfDate(segment.start.toISOString())} a ${maintenancePdfDate(segment.end.toISOString())}`,
        page,
      );
      const chartX = 94,
        chartW = 191,
        labelX = 12,
        labelW = 78,
        headerY = 34,
        rowTop = 54,
        rowH = 10;
      const segmentEndExclusive = addMonths(segment.start, 12);
      const totalMs = segmentEndExclusive - segment.start;
      const xFor = (date) =>
        chartX +
        Math.max(0, Math.min(1, (date - segment.start) / totalMs)) * chartW;
      pdf.setFillColor(246, 248, 252);
      pdf.rect(10, headerY, 277, 17, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(58, 71, 96);
      pdf.setFontSize(7);
      pdf.text("CLIENTE / PLANO / PERÍODO", labelX, 44);
      for (let m = 0; m < 12; m++) {
        const start = addMonths(segment.start, m),
          end = addMonths(segment.start, m + 1);
        const x = xFor(start),
          width = xFor(end) - x;
        pdf.setDrawColor(215, 222, 233);
        pdf.rect(x, headerY, width, 17);
        pdf.setFontSize(6);
        pdf.text(String(start.getUTCFullYear()), x + width / 2, 39, {
          align: "center",
        });
        pdf.text(months[start.getUTCMonth()], x + width / 2, 47, {
          align: "center",
        });
      }
      const today = new Date();
      if (today >= segment.start && today < segmentEndExclusive) {
        const x = xFor(today);
        pdf.setDrawColor(31, 41, 60);
        pdf.setLineWidth(0.35);
        pdf.line(
          x,
          headerY,
          x,
          rowTop + Math.min(13, rows.length - offset) * rowH,
        );
        pdf.setFontSize(5.5);
        pdf.text("HOJE", x + 1, 33);
      }
      const pageRows = rows.slice(offset, offset + 13);
      pageRows.forEach((row, index) => {
        const y = rowTop + index * rowH;
        if (row.type === "client") {
          pdf.setFillColor(232, 239, 250);
          pdf.rect(10, y, 277, rowH, "F");
          pdf.setTextColor(29, 49, 82);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(7.5);
          pdf.text(row.name.toUpperCase(), labelX, y + 6.4);
          return;
        }
        pdf.setFillColor(index % 2 ? 252 : 255, index % 2 ? 253 : 255, 255);
        pdf.rect(10, y, 277, rowH, "F");
        pdf.setTextColor(27, 38, 61);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6.8);
        pdf.text(
          pdf.splitTextToSize(`   ${row.name}`, labelW)[0],
          labelX,
          y + 4.5,
        );
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(119, 130, 150);
        pdf.setFontSize(5.3);
        pdf.text(
          `${row.unit} • ${maintenancePdfDate(row.start)} – ${maintenancePdfDate(row.end)} • ${row.frequency}`,
          labelX + 3,
          y + 8,
        );
        const start = new Date(
          Math.max(parse(row.start).getTime(), segment.start.getTime()),
        );
        const end = new Date(
          Math.min(parse(row.end).getTime(), segmentEndExclusive.getTime() - 1),
        );
        if (start <= end) {
          const bx = xFor(start),
            bw = Math.max(2, xFor(end) - bx);
          pdf.setFillColor(195, 220, 247);
          pdf.roundedRect(bx, y + 3, bw, 4, 1.6, 1.6, "F");
          pdf.setDrawColor(187, 161, 108);
          pdf.setLineWidth(0.35);
          pdf.roundedRect(bx, y + 3, bw, 4, 1.6, 1.6, "S");
        }
        row.items.forEach((item) => {
          const date = parse(item.plannedDate);
          if (date < segment.start || date >= segmentEndExclusive) return;
          const x = xFor(date);
          const group = maintenanceGroup(item);
          const color =
            group === "Realizadas"
              ? [34, 169, 111]
              : group === "Atrasadas"
                ? [232, 72, 85]
                : group === "Próximas"
                  ? [239, 158, 36]
                  : [37, 111, 232];
          pdf.setFillColor(...color);
          pdf.circle(x, y + 5, 1.65, "F");
          pdf.setTextColor(...color);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(4.7);
          pdf.text(String(date.getUTCDate()).padStart(2, "0"), x, y + 9, {
            align: "center",
          });
        });
        pdf.setDrawColor(232, 236, 242);
        pdf.line(10, y + rowH, 287, y + rowH);
      });
      const legendY = Math.min(191, rowTop + pageRows.length * rowH + 7);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(5.8);
      pdf.setTextColor(105, 116, 136);
      pdf.text(
        "A barra representa a vigência do plano. Os pontos indicam as visitas:",
        12,
        legendY,
      );
      [
        ["Programada", [37, 111, 232]],
        ["Próxima", [239, 158, 36]],
        ["Atrasada", [232, 72, 85]],
        ["Realizada", [34, 169, 111]],
      ].forEach(([label, color], i) => {
        const x = 118 + i * 35;
        pdf.setFillColor(...color);
        pdf.circle(x, legendY - 1.5, 1.4, "F");
        pdf.text(label, x + 3, legendY);
      });
    }
  }
  await addAboutsecPdfFooters(pdf);
  pdf.save(
    `Gantt_Preventivas_${maintenanceFileName(dated[0]?.clientName || "Geral")}.pdf`,
  );
}

const monthlyMaintenanceResultLabel = (value) =>
  ({
    conforming: "Conforme",
    non_conforming: "Não conforme",
    not_applicable: "Não aplicável",
    removed_for_maintenance: "Retirado para manutenção",
    damaged: "Danificado",
    replaced: "Substituído",
  })[value] ||
  value ||
  "—";

async function exportMonthlyMaintenancePdf(report, subtitle) {
  if (!report?.inspections?.length)
    return window.alert("Não há equipamentos inspecionados neste período.");
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const s = report.summary,
    activityMap = new Map(report.activities.map((item) => [item.id, item]));
  const issuedAt = new Date().toLocaleString("pt-BR"),
    formatReportDate = (value) => String(value || "").split("-").reverse().join("/"),
    period = `${formatReportDate(report.dateFrom)} a ${formatReportDate(report.dateTo)}`;
  pdf.setFillColor(21, 25, 26);
  pdf.rect(0, 0, 297, 210, "F");
  try {
    const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png"),
      properties = pdf.getImageProperties(logo),
      width = 78,
      height = width / (properties.width / properties.height);
    pdf.addImage(logo, "PNG", 18, 18, width, height, undefined, "SLOW");
  } catch {}
  pdf.setDrawColor(187, 161, 108);
  pdf.setLineWidth(1.2);
  pdf.line(18, 55, 279, 55);
  pdf.setTextColor(117, 103, 71);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("GESTÃO DE MANUTENÇÃO PREVENTIVA", 18, 79);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(29);
  pdf.text("Relatório técnico", 18, 98);
  pdf.text("de preventivas", 18, 113);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(205, 215, 231);
  pdf.text(
    "Consolidação executiva, calendário de visitas e comprovação técnica dos equipamentos inspecionados.",
    18,
    126,
    { maxWidth: 165 },
  );
  pdf.setFillColor(36, 55, 91);
  pdf.roundedRect(194, 73, 85, 69, 5, 5, "F");
  pdf.setFontSize(7);
  pdf.setTextColor(117, 103, 71);
  pdf.text("PERÍODO", 204, 86);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(255, 255, 255);
  pdf.text(period || "—", 204, 98);
  pdf.setFontSize(7);
  pdf.setTextColor(117, 103, 71);
  pdf.text("ESCOPO SELECIONADO", 204, 111);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(225, 232, 243);
  pdf.text(
    pdf
      .splitTextToSize(subtitle || "Todos os clientes • Todas as unidades", 65)
      .slice(0, 3),
    204,
    119,
  );
  pdf.setFontSize(7);
  pdf.setTextColor(166, 184, 214);
  pdf.text(`Emitido em ${issuedAt}`, 18, 190);
  pdf.text("Documento gerencial e comprobatório", 245, 190, { align: "right" });
  pdf.addPage();
  await maintenancePdfHeader(
    pdf,
    "RELATÓRIO DE PREVENTIVAS",
    subtitle,
    2,
  );
  pdf.setTextColor(25, 38, 62);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.text("Resumo executivo do período", 14, 47);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(105, 117, 138);
  pdf.text(
    "Comprovação consolidada das visitas preventivas, equipamentos inspecionados e resultados técnicos.",
    14,
    54,
  );
  const kpis = [
    ["VISITAS REALIZADAS", s.activities, [52, 116, 242]],
    ["EQUIPAMENTOS", s.equipment, [103, 82, 237]],
    ["CONFORMES", s.conform, [36, 169, 119]],
    ["NÃO CONFORMES / CRÍTICOS", s.nonConform + s.critical, [229, 83, 96]],
    ["COM EVIDÊNCIAS", s.withPhotos, [239, 158, 36]],
  ];
  kpis.forEach(([label, value, color], index) => {
    const x = 14 + index * 54.2;
    pdf.setFillColor(246, 248, 252);
    pdf.roundedRect(x, 63, 49, 25, 4, 4, "F");
    pdf.setFillColor(...color);
    pdf.roundedRect(x, 63, 2.3, 25, 2, 2, "F");
    pdf.setTextColor(104, 117, 139);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    pdf.text(label, x + 7, 71, { maxWidth: 39 });
    pdf.setTextColor(25, 38, 62);
    pdf.setFontSize(16);
    pdf.text(String(value), x + 7, 83);
  });
  const rangeStart = new Date(`${report.dateFrom}T00:00:00Z`),
    rangeEnd = new Date(`${report.dateTo}T00:00:00Z`),
    rangeDays = Math.max(1, Math.round((rangeEnd - rangeStart) / 86400000) + 1);
  const dayTotals = new Map();
  report.activities.forEach((item) => {
    const raw = item.completedAt || item.plannedDate;
    if (!raw) return;
    const key = String(raw).slice(0, 10);
    dayTotals.set(key, (dayTotals.get(key) || 0) + 1);
  });
  const weeklyTotals = [0, 0, 0, 0, 0, 0];
  dayTotals.forEach((value, key) => {
    const offset = Math.max(0, Math.round((new Date(`${key}T00:00:00Z`) - rangeStart) / 86400000));
    const bucket = Math.min(5, Math.floor((offset / rangeDays) * 6));
    weeklyTotals[bucket] += value;
  });
  pdf.setFillColor(249, 251, 254);
  pdf.roundedRect(14, 99, 128, 55, 4, 4, "F");
  pdf.setTextColor(25, 38, 62);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Visitas por semana", 21, 110);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6.5);
  pdf.setTextColor(111, 124, 145);
  pdf.text("Distribuição das execuções ao longo do período", 21, 116);
  const maxWeek = Math.max(1, ...weeklyTotals),
    baseY = 144;
  weeklyTotals.forEach((value, index) => {
    const x = 24 + index * 18.5,
      height = (value / maxWeek) * 20;
    pdf.setFillColor(231, 237, 247);
    pdf.roundedRect(x, 123, 10, 21, 3, 3, "F");
    if (value) {
      pdf.setFillColor(60, 205, 218);
      pdf.roundedRect(x, baseY - height, 10, height, 3, 3, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(25, 38, 62);
      pdf.text(String(value), x + 5, baseY - height - 2, { align: "center" });
    }
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5.5);
    pdf.setTextColor(105, 118, 140);
    pdf.text(`S${index + 1}`, x + 5, 150, { align: "center" });
  });
  const resultData = [
      ["Conformes", s.conform, [36, 169, 119]],
      ["Não conformes", s.nonConform, [239, 158, 36]],
      ["Críticos", s.critical, [229, 83, 96]],
      [
        "Demais",
        Math.max(0, s.equipment - s.conform - s.nonConform - s.critical),
        [152, 164, 184],
      ],
    ],
    resultTotal = Math.max(1, s.equipment);
  pdf.setFillColor(249, 251, 254);
  pdf.roundedRect(148, 99, 132, 55, 4, 4, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(25, 38, 62);
  pdf.text("Qualidade das inspeções", 155, 110);
  let resultOffset = 0;
  resultData.forEach(([, value, color]) => {
    if (!value) return;
    pdf.setDrawColor(...color);
    pdf.setLineWidth(7);
    const start = -90 + (resultOffset / resultTotal) * 360,
      end = -90 + ((resultOffset + value) / resultTotal) * 360;
    pdf.setDrawColor(...color);
    for (let angle = start; angle < end; angle += 2) {
      const a = (angle * Math.PI) / 180,
        b = (Math.min(angle + 2, end) * Math.PI) / 180;
      pdf.line(
        177 + Math.cos(a) * 14,
        128 + Math.sin(a) * 14,
        177 + Math.cos(b) * 14,
        128 + Math.sin(b) * 14,
      );
    }
    resultOffset += value;
  });
  pdf.setLineWidth(0.2);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(25, 38, 62);
  pdf.text(`${Math.round((s.conform / resultTotal) * 100)}%`, 177, 127, {
    align: "center",
  });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(5.5);
  pdf.setTextColor(105, 118, 140);
  pdf.text("conformes", 177, 132, { align: "center" });
  resultData.forEach(([label, value, color], index) => {
    const x = 202,
      y = 118 + index * 8;
    pdf.setFillColor(...color);
    pdf.circle(x, y - 1.5, 1.5, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    pdf.setTextColor(76, 90, 114);
    pdf.text(label, x + 5, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(25, 38, 62);
    pdf.text(String(value), 269, y, { align: "right" });
  });
  pdf.setFillColor(243, 247, 252);
  pdf.roundedRect(14, 163, 266, 25, 4, 4, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(25, 38, 62);
  pdf.text("Leitura do período", 21, 173);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(91, 105, 130);
  pdf.text(
    `Foram realizadas ${s.activities} visita(s), com ${s.equipment} equipamento(s) inspecionado(s) e ${s.conform} resultado(s) conforme(s).`,
    21,
    181,
  );
  pdf.text(
    "O calendário completo e a distribuição das execuções por data são apresentados na página seguinte.",
    21,
    186,
  );
  pdf.addPage();
  await maintenancePdfHeader(
    pdf,
    "CALENDÁRIO DE EXECUÇÕES",
    subtitle,
    3,
  );
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(25, 38, 62);
  pdf.text(`Preventivas realizadas de ${period}`, 14, 43);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(101, 115, 139);
  pdf.text(
    "Relação cronológica das visitas concluídas dentro do intervalo selecionado.",
    14,
    50,
  );
  const executionDays = [...dayTotals.entries()].sort(([a], [b]) => a.localeCompare(b));
  executionDays.slice(0, 18).forEach(([date, total], index) => {
    const col = index % 2,
      row = Math.floor(index / 2),
      x = 14 + col * 136,
      yCard = 58 + row * 14;
    const descriptions = report.activities
      .filter((item) => String(item.completedAt || item.plannedDate).slice(0, 10) === date)
      .map((item) => item.groupName || item.planName || item.clientName)
      .filter(Boolean);
    pdf.setFillColor(246, 250, 253);
    pdf.roundedRect(x, yCard, 130, 11, 2, 2, "F");
    pdf.setFillColor(43, 181, 135);
    pdf.roundedRect(x, yCard, 2.5, 11, 1, 1, "F");
    pdf.setTextColor(37, 51, 76);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.text(formatReportDate(date), x + 7, yCard + 4.5);
    pdf.setTextColor(35, 143, 105);
    pdf.text(`${total} visita${total > 1 ? "s" : ""}`, x + 124, yCard + 4.5, { align: "right" });
    pdf.setTextColor(105, 118, 140);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5.5);
    pdf.text(pdf.splitTextToSize([...new Set(descriptions)].join(" • ") || "Preventiva concluída", 116).slice(0, 1), x + 7, yCard + 8.5);
  });
  if (!executionDays.length) {
    pdf.setTextColor(105, 118, 140);
    pdf.setFontSize(9);
    pdf.text("Nenhuma execução registrada no período.", 14, 68);
  } else if (executionDays.length > 18) {
    pdf.setTextColor(105, 118, 140);
    pdf.setFontSize(7);
    pdf.text(`Outras ${executionDays.length - 18} data(s) constam no detalhamento técnico.`, 14, 190);
  }
  pdf.addPage();
  let page = 4,
    y = 51;
  const columns = [
    ["Data", 12, 19],
    ["Cliente / unidade", 32, 35],
    ["Grupo", 68, 25],
    ["Equipamento", 94, 42],
    ["Tag", 137, 32],
    ["Status", 170, 34],
    ["Ações / evidências", 205, 45],
    ["Técnico", 251, 34],
  ];
  const drawPage = async () => {
    await maintenancePdfHeader(
      pdf,
      "RELATÓRIO DE PREVENTIVAS",
      subtitle,
      page,
    );
    pdf.setFillColor(21, 25, 26);
    pdf.rect(10, 35, 277, 8, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(6.5);
    columns.forEach(([label, x]) => pdf.text(label, x, 40));
    y = 49;
  };
  await drawPage();
  for (const item of report.inspections) {
    if (y > 194) {
      pdf.addPage();
      page++;
      await drawPage();
    }
    const activity = activityMap.get(item.activityId) || {};
    const values = [
      maintenancePdfDate(item.inspectedAt || activity.completedAt),
      `${activity.clientName || "—"} / ${activity.unitName || "—"}`,
      activity.groupName || "—",
      `${item.equipmentName || "—"}${item.area ? ` • ${item.area}` : ""}`,
      item.tag || "—",
      monthlyMaintenanceResultLabel(item.result),
      `${item.actions?.length ? item.actions.map((action) => equipmentActions.find(([key]) => key === action)?.[1] || action).join(", ") : "Sem ação"} • ${item.photoCount || 0} foto(s)`,
      activity.teamNames || activity.assignedTo || "—",
    ];
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    pdf.setTextColor(28, 39, 61);
    values.forEach((value, index) => {
      const [, x, width] = columns[index];
      pdf.text(pdf.splitTextToSize(String(value), width).slice(0, 2), x, y);
    });
    pdf.setDrawColor(227, 232, 240);
    pdf.line(10, y + 9, 287, y + 9);
    y += 12;
  }
  await addAboutsecPdfFooters(pdf, true);
  pdf.save(
    `Relatorio_Preventivas_${report.dateFrom}_${report.dateTo}_${maintenanceFileName(report.activities[0]?.clientName || "Geral")}.pdf`,
  );
}

function MaintenanceMonthlyReport({ token, context }) {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 8)}01`);
  const [dateTo, setDateTo] = useState(today);
  const [clientId, setClientId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const units = context.units.filter(
    (item) => !clientId || item.clientId === Number(clientId),
  );
  useEffect(() => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    const params = new URLSearchParams({ dateFrom, dateTo });
    if (clientId) params.set("clientId", clientId);
    if (unitId) params.set("unitId", unitId);
    setLoading(true);
    fetch(`${apiBase}/maintenance/monthly-report?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then((data) => {
        setReport(data);
        setError("");
      })
      .catch(() => setError("Não foi possível carregar o relatório do período."))
      .finally(() => setLoading(false));
  }, [token, dateFrom, dateTo, clientId, unitId]);
  const activityDays = new Map();
  (report?.activities || []).forEach((item) => {
    const day = String(item.completedAt || item.plannedDate).slice(0, 10);
    if (!activityDays.has(day)) activityDays.set(day, []);
    activityDays.get(day).push(item);
  });
  const s = report?.summary || {
    activities: 0,
    equipment: 0,
    conform: 0,
    nonConform: 0,
    critical: 0,
    withPhotos: 0,
    qrVerified: 0,
  };
  const total = Math.max(1, s.equipment);
  const formatPeriodDate = (value) => value.split("-").reverse().join("/");
  const periodLabel = `${formatPeriodDate(dateFrom)} a ${formatPeriodDate(dateTo)}`;
  const subtitle = `${context.clients.find((item) => item.id === Number(clientId))?.name || "Todos os clientes"} • ${units.find((item) => item.id === Number(unitId))?.name || "Todas as unidades"} • ${periodLabel}`;
  return (
    <div className="maintenance-monthly-report">
      <section className="panel maintenance-report-filters">
        <fieldset className="maintenance-period-filter">
          <legend>Período</legend>
          <label>
            Data inicial
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label>
            Data final
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </fieldset>
        <label>
          Cliente
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setUnitId("");
            }}
          >
            <option value="">Todos</option>
            {context.clients.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Unidade
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            <option value="">Todas</option>
            {units.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="primary"
          disabled={!report?.inspections?.length}
          onClick={() => exportMonthlyMaintenancePdf(report, subtitle)}
        >
          <Icon name="download" size={16} /> Exportar relatório PDF
        </button>
      </section>
      {dateFrom > dateTo && <div className="empty error-text">A data final deve ser igual ou posterior à data inicial.</div>}
      {loading && <div className="empty">Consolidando inspeções do período…</div>}
      {error && <div className="empty error-text">{error}</div>}
      {!loading && !error && report && (
        <>
          <div className="maintenance-report-kpis">
            <SummaryBox
              tone="blue"
              label="Preventivas realizadas"
              value={s.activities}
              detail="Visitas concluídas no período"
            />
            <SummaryBox
              tone="green"
              label="Equipamentos inspecionados"
              value={s.equipment}
              detail={`${s.conform} conformes`}
            />
            <SummaryBox
              tone="red"
              label="Pontos de atenção"
              value={s.nonConform + s.critical}
              detail="Não conformes, danificados ou retirados"
            />
            <SummaryBox
              tone="purple"
              label="Evidências registradas"
              value={s.withPhotos}
              detail={`${s.qrVerified} equipamentos validados por QR`}
            />
          </div>
          <div className="maintenance-report-analytics">
            <section className="panel maintenance-report-chart">
              <PanelTitle
                title="Conformidade dos equipamentos"
                subtitle="Distribuição das inspeções do período"
              />
              <div
                className="maintenance-report-donut"
                style={{
                  background: `conic-gradient(#2bb587 0 ${(s.conform / total) * 100}%,#ef5967 ${(s.conform / total) * 100}% ${((s.conform + s.nonConform + s.critical) / total) * 100}%,#9b7cf4 ${((s.conform + s.nonConform + s.critical) / total) * 100}% 100%)`,
                }}
              >
                <span>
                  <b>{Math.round((s.conform / total) * 100)}%</b>conformes
                </span>
              </div>
              <div className="maintenance-report-legend">
                <span>
                  <i className="green" />
                  Conformes <b>{s.conform}</b>
                </span>
                <span>
                  <i className="red" />
                  Atenção <b>{s.nonConform + s.critical}</b>
                </span>
                <span>
                  <i className="purple" />
                  Não aplicáveis <b>{s.notApplicable}</b>
                </span>
              </div>
            </section>
            <section className="panel maintenance-report-calendar">
              <PanelTitle
                title="Execuções no período"
                subtitle={periodLabel}
              />
              <div className="maintenance-period-executions">
                {[...activityDays.entries()].map(([day, items]) => (
                  <article key={day}>
                    <time>{formatPeriodDate(day)}</time>
                    <b>{items.length} visita(s)</b>
                    <span>{[...new Set(items.map((item) => item.unitName).filter(Boolean))].join(" • ") || "Unidade não informada"}</span>
                  </article>
                ))}
                {!activityDays.size && <div className="empty">Nenhuma execução registrada no período.</div>}
              </div>
            </section>
          </div>
          <section className="panel maintenance-report-table">
            <PanelTitle
              title="Equipamentos atendidos"
              subtitle={`${s.equipment} registros comprobatórios no período`}
            />
            <div className="maintenance-report-head">
              <span>Data</span>
              <span>Cliente / unidade</span>
              <span>Grupo</span>
              <span>Equipamento</span>
              <span>Identificação</span>
              <span>Status</span>
              <span>Ações e evidências</span>
              <span>Técnico</span>
            </div>
            {report.inspections.map((item) => {
              const activity =
                report.activities.find((row) => row.id === item.activityId) ||
                {};
              return (
                <div className="maintenance-report-row" key={item.id}>
                  <time>
                    {maintenancePdfDate(
                      item.inspectedAt || activity.completedAt,
                    )}
                  </time>
                  <span>
                    <b>{activity.clientName || "—"}</b>
                    <small>{activity.unitName || "—"}</small>
                  </span>
                  <span>
                    <b>{activity.groupName || "—"}</b>
                    <small>{activity.planName || ""}</small>
                  </span>
                  <span>
                    <b>{item.equipmentName}</b>
                    <small>
                      {[item.brand, item.model, item.area, item.location]
                        .filter(Boolean)
                        .join(" • ")}
                    </small>
                  </span>
                  <code>{item.tag || "—"}</code>
                  <i className={`result-${item.result}`}>
                    {monthlyMaintenanceResultLabel(item.result)}
                  </i>
                  <span>
                    <b>{item.actions.length} ação(ões)</b>
                    <small>
                      {item.photoCount} foto(s)
                      {item.qrVerifiedAt ? " • QR validado" : ""}
                    </small>
                  </span>
                  <span>
                    {activity.teamNames || activity.assignedTo || "—"}
                  </span>
                </div>
              );
            })}
            {!report.inspections.length && (
              <div className="empty">
                Nenhum equipamento inspecionado neste período.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

async function exportAssetLifeReport(detail, qrData) {
  const { asset, inspections = [], events = [] } = detail;
  const pdf = new jsPDF({ unit: "mm", format: "a4", compress: true });
  await maintenancePdfHeader(
    pdf,
    "HISTÓRICO DO EQUIPAMENTO",
    `${asset.clientName || "—"} • ${asset.unitName || "—"} • ${asset.groupName || "—"}`,
    1,
  );
  pdf.setTextColor(26, 39, 64);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(asset.name || "Equipamento", 14, 39);
  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    [
      `ID: ${asset.tag || asset.id}`,
      `Tipo / sistema: ${asset.assetType || "—"} / ${asset.systemType || "—"}`,
      `Marca / modelo: ${[asset.brand, asset.model].filter(Boolean).join(" • ") || "—"}`,
      `Área / local: ${[asset.area, asset.location].filter(Boolean).join(" • ") || "—"}`,
      `Status atual: ${asset.status || "—"}`,
    ],
    14,
    47,
  );
  if (qrData) pdf.addImage(qrData, "PNG", 172, 33, 24, 24);
  const conform = inspections.filter(
    (item) => item.result === "conforming",
  ).length;
  pdf.setFillColor(244, 247, 252);
  pdf.roundedRect(14, 67, 182, 17, 2, 2, "F");
  pdf.setFont("helvetica", "bold");
  pdf.text(`Manutenções: ${inspections.length}`, 20, 76);
  pdf.setTextColor(27, 143, 99);
  pdf.text(`Conformes: ${conform}`, 68, 76);
  pdf.setTextColor(202, 68, 79);
  pdf.text(`Pontos de atenção: ${inspections.length - conform}`, 112, 76);
  pdf.setTextColor(38, 57, 86);
  pdf.text(`Eventos: ${events.length}`, 167, 76);
  let y = 94;
  pdf.setFillColor(24, 39, 68);
  pdf.rect(14, y - 6, 182, 7, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.text("DATA", 17, y - 1);
  pdf.text("PLANO / RESULTADO", 43, y - 1);
  pdf.text("AÇÕES E OBSERVAÇÕES", 100, y - 1);
  pdf.text("TÉCNICO", 168, y - 1);
  y += 6;
  for (const item of inspections) {
    if (y > 275) {
      pdf.addPage();
      y = 24;
    }
    pdf.setTextColor(35, 49, 73);
    pdf.setFont("helvetica", "normal");
    pdf.text(maintenancePdfDate(item.inspectedAt), 17, y);
    pdf.text(
      pdf
        .splitTextToSize(
          `${item.planName || "—"} • ${monthlyMaintenanceResultLabel(item.result)}`,
          52,
        )
        .slice(0, 2),
      43,
      y,
    );
    pdf.text(
      pdf
        .splitTextToSize(
          `${(item.actions || []).map((action) => equipmentActions.find(([key]) => key === action)?.[1] || action).join(", ") || "Sem ação"}${item.notes ? ` • ${item.notes}` : ""}`,
          62,
        )
        .slice(0, 3),
      100,
      y,
    );
    pdf.text(
      pdf
        .splitTextToSize(item.teamNames || item.assignedTo || "—", 25)
        .slice(0, 2),
      168,
      y,
    );
    pdf.setDrawColor(226, 232, 240);
    pdf.line(14, y + 10, 196, y + 10);
    y += 14;
  }
  await addAboutsecPdfFooters(pdf);
  pdf.save(`Historico_${maintenanceFileName(asset.tag || asset.name)}.pdf`);
}

function AssetHistoryModal({ token, asset, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null),
    [qr, setQr] = useState(""),
    [editing, setEditing] = useState(false);
  const loadDetail = () =>
    fetch(`${apiBase}/maintenance/assets-overview/${asset.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setDetail);
  useEffect(() => {
    loadDetail();
    QRCode.toDataURL(
      `${window.location.origin}/equipment/${encodeURIComponent(asset.tag || asset.id)}`,
      { width: 360, margin: 1 },
    ).then(setQr);
  }, [token, asset]);
  if (!detail)
    return (
      <div className="modal-backdrop">
        <div className="modal asset-life-modal">
          <div className="empty">Carregando histórico…</div>
        </div>
      </div>
    );
  if (editing)
    return (
      <EquipmentForm
        record={detail.asset}
        client={{ id: detail.asset.clientId, name: detail.asset.clientName }}
        unit={{ id: detail.asset.unitId, name: detail.asset.unitName }}
        token={token}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          loadDetail();
          onUpdated?.();
        }}
      />
    );
  const conform = detail.inspections.filter(
      (item) => item.result === "conforming",
    ).length,
    total = detail.inspections.length;
  const fields = [
    ["Status", detail.asset.status],
    [
      "Sistema / tipo",
      [detail.asset.systemType, detail.asset.assetType]
        .filter(Boolean)
        .join(" • "),
    ],
    [
      "Marca / modelo",
      [detail.asset.brand, detail.asset.model].filter(Boolean).join(" • "),
    ],
    ["Número de série", detail.asset.serialNumber],
    ["IP / Hostname", detail.asset.ipHostname],
    ["Capacidade", detail.asset.capacity],
    [
      "Área / local",
      [detail.asset.area, detail.asset.location].filter(Boolean).join(" • "),
    ],
    ["Ambiente", detail.asset.environment],
    ["Criticidade", detail.asset.criticality],
    ["Instalação", maintenancePdfDate(detail.asset.installationDate)],
    ["Garantia até", maintenancePdfDate(detail.asset.warrantyUntil)],
    [
      "Item / grupo",
      `${detail.asset.itemNumber || "—"} • ${detail.asset.groupName || `Grupo ${detail.asset.groupNumber}`}`,
    ],
  ];
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal asset-life-modal">
        <div className="modal-head">
          <div>
            <small>{detail.asset.tag}</small>
            <h2>{detail.asset.name}</h2>
            <p>
              {detail.asset.clientName} • {detail.asset.unitName} •{" "}
              {detail.asset.groupName}
            </p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <div className="asset-life-summary">
          <div>
            <b>{total}</b>
            <span>Manutenções</span>
          </div>
          <div>
            <b>{total ? Math.round((conform / total) * 100) : 0}%</b>
            <span>Conformidade</span>
          </div>
          <div>
            <b>
              {detail.inspections.reduce(
                (sum, item) => sum + (item.photoCount || 0),
                0,
              )}
            </b>
            <span>Evidências</span>
          </div>
          <img src={qr} alt={`QR Code ${detail.asset.tag}`} />
        </div>
        <div className="asset-life-details">
          {fields.map(([label, value]) => (
            <span key={label}>
              <b>{label}</b>
              {value || "—"}
            </span>
          ))}
          {detail.asset.details && (
            <span className="wide">
              <b>Informações adicionais</b>
              {detail.asset.details}
            </span>
          )}
        </div>
        <PanelTitle
          title="Histórico de manutenção"
          subtitle="Resultados, ações realizadas, evidências e responsáveis"
        />
        <div className="asset-life-history">
          {detail.inspections.map((item) => (
            <article key={item.id}>
              <time>{maintenancePdfDate(item.inspectedAt)}</time>
              <div>
                <b>{item.planName}</b>
                <small>
                  {(item.actions || [])
                    .map(
                      (action) =>
                        equipmentActions.find(([key]) => key === action)?.[1] ||
                        action,
                    )
                    .join(" • ") || "Sem ação registrada"}
                  {item.notes ? ` • ${item.notes}` : ""}
                </small>
              </div>
              <i className={`result-${item.result}`}>
                {monthlyMaintenanceResultLabel(item.result)}
              </i>
              <span>{item.teamNames || item.assignedTo || "—"}</span>
            </article>
          ))}
          {!detail.inspections.length && (
            <div className="empty">Nenhuma manutenção registrada.</div>
          )}
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>Fechar</button>
          <button onClick={() => setEditing(true)}>
            <Icon name="edit" size={16} /> Editar informações
          </button>
          <button
            className="primary"
            onClick={() => exportAssetLifeReport(detail, qr)}
          >
            <Icon name="download" size={16} /> Exportar relatório
          </button>
        </div>
      </div>
    </div>
  );
}

async function exportAssetsOverviewPdf({
  items,
  systemTotals,
  maintenanceTotals,
  filters,
}) {
  if (!items.length)
    return window.alert("Não há itens nos filtros selecionados para exportar.");
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const scopeFilters = filters.filter(Boolean),
    scopeSummary =
      scopeFilters.join(" • ") ||
      "Todos os clientes • Todas as unidades • Todos os registros";
  const selectedClient =
    scopeFilters
      .find((item) => item.startsWith("Cliente:"))
      ?.replace("Cliente: ", "") || "Todos";
  const selectedUnit =
    scopeFilters
      .find((item) => item.startsWith("Unidade:"))
      ?.replace("Unidade: ", "") || "Todas";
  const clientScope =
    selectedClient === "Todos" ? "Todos os clientes" : selectedClient;
  const unitScope =
    selectedUnit === "Todas" ? "Todas as unidades" : selectedUnit;
  const issuedAt = new Date().toLocaleString("pt-BR");
  pdf.setFillColor(21, 25, 26);
  pdf.rect(0, 0, 297, 210, "F");
  try {
    const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png"),
      properties = pdf.getImageProperties(logo),
      width = 78,
      height = width / (properties.width / properties.height);
    pdf.addImage(logo, "PNG", 18, 18, width, height, undefined, "SLOW");
  } catch {}
  pdf.setDrawColor(187, 161, 108);
  pdf.setLineWidth(1.2);
  pdf.line(18, 55, 279, 55);
  pdf.setTextColor(117, 103, 71);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("GESTÃO DO INVENTÁRIO TÉCNICO", 18, 79);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(29);
  pdf.text("Equipamentos", 18, 98);
  pdf.text("e softwares", 18, 113);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(205, 215, 231);
  pdf.text(
    "Inventário consolidado, situação operacional e programação preventiva dos ativos cadastrados.",
    18,
    126,
    { maxWidth: 160 },
  );
  pdf.setFillColor(36, 55, 91);
  pdf.roundedRect(194, 73, 85, 72, 5, 5, "F");
  pdf.setFontSize(7);
  pdf.setTextColor(117, 103, 71);
  pdf.text("CLIENTE / UNIDADE", 204, 86);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(255, 255, 255);
  pdf.text(
    pdf.splitTextToSize(`${clientScope} • ${unitScope}`, 65).slice(0, 3),
    204,
    96,
  );
  pdf.setFontSize(7);
  pdf.setTextColor(117, 103, 71);
  pdf.text("ITENS CADASTRADOS", 204, 119);
  pdf.setFontSize(18);
  pdf.setTextColor(255, 255, 255);
  pdf.text(String(items.length), 204, 132);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(166, 184, 214);
  pdf.text(`Emitido em ${issuedAt}`, 18, 190);
  pdf.text("Documento gerencial do inventário", 245, 190, { align: "right" });
  pdf.addPage();
  await maintenancePdfHeader(
    pdf,
    "INVENTÁRIO TÉCNICO",
    `${clientScope} • ${unitScope} • Equipamentos e softwares`,
    2,
  );
  pdf.setTextColor(27, 39, 64);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(21);
  pdf.text("Visão executiva do inventário", 14, 48);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(104, 117, 140);
  pdf.text(
    `Emitido em ${new Date().toLocaleString("pt-BR")} • ${items.length} item(ns) no período selecionado`,
    14,
    55,
  );
  const kpis = [
    ["ITENS CADASTRADOS", items.length, [52, 116, 242]],
    [
      "ATIVOS",
      items.filter((i) => i.status === "Ativo").length,
      [40, 180, 135],
    ],
    [
      "SOFTWARES",
      items.filter(
        (i) =>
          String(i.assetType).toUpperCase() === "SOF" ||
          String(i.systemType).toLowerCase() === "software",
      ).length,
      [111, 82, 237],
    ],
    [
      "ATENÇÃO PREVENTIVA",
      maintenanceTotals
        .filter((i) => ["Próxima", "Atrasada"].includes(i.name))
        .reduce((s, i) => s + i.value, 0),
      [239, 163, 43],
    ],
  ];
  kpis.forEach(([label, value, color], index) => {
    const x = 14 + index * 69;
    pdf.setFillColor(246, 248, 252);
    pdf.roundedRect(x, 64, 63, 28, 4, 4, "F");
    pdf.setFillColor(...color);
    pdf.roundedRect(x, 64, 2.5, 28, 2, 2, "F");
    pdf.setTextColor(105, 118, 141);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.text(label, x + 8, 73);
    pdf.setTextColor(26, 39, 64);
    pdf.setFontSize(18);
    pdf.text(String(value), x + 8, 86);
  });
  pdf.setFontSize(11);
  pdf.setTextColor(27, 39, 64);
  pdf.text("Distribuição por sistema", 14, 108);
  pdf.text("Situação das manutenções", 155, 108);
  const max = Math.max(1, ...systemTotals.map((i) => i.value));
  systemTotals.slice(0, 6).forEach((item, index) => {
    const y = 118 + index * 10;
    pdf.setFontSize(7.5);
    pdf.setTextColor(74, 88, 112);
    pdf.text(String(item.name).slice(0, 28), 14, y);
    pdf.setFillColor(232, 238, 247);
    pdf.roundedRect(56, y - 4, 80, 4, 2, 2, "F");
    pdf.setFillColor(
      index % 2 ? 40 : 52,
      index % 2 ? 180 : 116,
      index % 2 ? 135 : 242,
    );
    pdf.roundedRect(
      56,
      y - 4,
      Math.max(2, (item.value / max) * 80),
      4,
      2,
      2,
      "F",
    );
    pdf.setFont("helvetica", "bold");
    pdf.text(String(item.value), 140, y, { align: "right" });
  });
  const colors = [
    [40, 180, 135],
    [239, 163, 43],
    [229, 92, 104],
    [183, 193, 208],
  ];
  maintenanceTotals.forEach((item, index) => {
    const y = 118 + index * 15;
    pdf.setFillColor(...colors[index]);
    pdf.circle(160, y - 2, 2, "F");
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(74, 88, 112);
    pdf.setFontSize(8);
    pdf.text(item.name, 166, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(27, 39, 64);
    pdf.text(String(item.value), 225, y, { align: "right" });
    pdf.setFillColor(235, 240, 247);
    pdf.roundedRect(232, y - 5, 48, 4, 2, 2, "F");
    if (item.value) {
      pdf.setFillColor(...colors[index]);
      pdf.roundedRect(
        232,
        y - 5,
        Math.max(2, (item.value / items.length) * 48),
        4,
        2,
        2,
        "F",
      );
    }
  });
  pdf.setFillColor(242, 246, 252);
  pdf.roundedRect(14, 177, 266, 16, 3, 3, "F");
  pdf.setFontSize(6.8);
  pdf.setTextColor(82, 96, 120);
  pdf.setFont("helvetica", "bold");
  pdf.text("FILTROS APLICADOS", 19, 183);
  pdf.setFont("helvetica", "normal");
  pdf.text(pdf.splitTextToSize(scopeSummary, 252).slice(0, 2), 19, 188);
  pdf.addPage();
  await maintenancePdfHeader(
    pdf,
    "INVENTÁRIO TÉCNICO",
    `${clientScope} • ${unitScope} • Relação detalhada`,
    3,
  );
  const columns = [
    ["GRUPO", 12],
    ["ID", 47],
    ["ITEM", 88],
    ["CLIENTE / UNIDADE", 148],
    ["ÚLTIMA", 202],
    ["PRÓXIMA", 228],
    ["STATUS", 257],
  ];
  const tableHead = () => {
    pdf.setFillColor(241, 245, 251);
    pdf.rect(10, 33, 277, 10, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    pdf.setTextColor(83, 97, 121);
    columns.forEach(([label, x]) => pdf.text(label, x, 39));
  };
  tableHead();
  let y = 50;
  const statusColor = (value) => {
    const key = String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    if (
      key.includes("danific") ||
      key.includes("inativo") ||
      key.includes("baixado") ||
      key.includes("descart")
    )
      return [211, 69, 82];
    if (key.includes("manuten") || key.includes("retirado"))
      return [224, 142, 32];
    if (key.includes("substit")) return [111, 82, 237];
    if (key.includes("reserva")) return [52, 116, 242];
    if (key === "ativo") return [31, 139, 96];
    return [89, 105, 132];
  };
  for (const item of items) {
    if (y > 188) {
      pdf.addPage();
      await maintenancePdfHeader(
        pdf,
        "INVENTÁRIO TÉCNICO",
        "Continuação da relação detalhada",
        pdf.getNumberOfPages(),
      );
      tableHead();
      y = 50;
    }
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(31, 44, 69);
    [
      [item.groupName || `Grupo ${item.groupNumber}`, 12, 32],
      [item.tag || `#${item.id}`, 47, 38],
      [item.name, 88, 56],
      [`${item.clientName || "—"} / ${item.unitName || "—"}`, 148, 50],
      [maintenancePdfDate(item.lastMaintenanceDate), 202, 23],
      [maintenancePdfDate(item.nextMaintenanceDate), 228, 23],
    ].forEach(([value, x, width]) =>
      pdf.text(pdf.splitTextToSize(String(value), width)[0], x, y),
    );
    pdf.setTextColor(...statusColor(item.status));
    pdf.setFont("helvetica", "bold");
    pdf.text(pdf.splitTextToSize(String(item.status || "—"), 27)[0], 257, y);
    pdf.setDrawColor(226, 232, 241);
    pdf.line(10, y + 5, 287, y + 5);
    y += 11;
  }
  await addAboutsecPdfFooters(pdf, true);
  pdf.save(
    `Inventario_Tecnico_${maintenanceFileName(clientScope)}_${maintenanceFileName(unitScope)}.pdf`,
  );
}

function AssetsAndSoftware({ token, context }) {
  const [items, setItems] = useState([]),
    [selected, setSelected] = useState(null),
    [search, setSearch] = useState(""),
    [clientId, setClientId] = useState(""),
    [unitId, setUnitId] = useState(""),
    [status, setStatus] = useState("Todos"),
    [group, setGroup] = useState(""),
    [system, setSystem] = useState(""),
    [maintenance, setMaintenance] = useState("Todos");
  const loadItems = () =>
    fetch(`${apiBase}/maintenance/assets-overview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setItems);
  useEffect(() => {
    loadItems();
  }, [token]);
  const units = context.units.filter(
    (item) => !clientId || item.clientId === Number(clientId),
  );
  const maintenanceState = (item) => {
    const next = item.nextMaintenanceDate
      ? new Date(`${String(item.nextMaintenanceDate).slice(0, 10)}T12:00:00`)
      : null;
    if (!next) return "Sem programação";
    const days = Math.ceil((next - new Date()) / 86400000);
    if (days < 0) return "Atrasada";
    if (days <= 30) return "Próxima";
    return "Em dia";
  };
  const visible = items.filter(
    (item) =>
      (!clientId || item.clientId === Number(clientId)) &&
      (!unitId || item.unitId === Number(unitId)) &&
      (status === "Todos" || item.status === status) &&
      (!group || (item.groupName || `Grupo ${item.groupNumber}`) === group) &&
      (!system || (item.systemType || "Não informado") === system) &&
      (maintenance === "Todos" || maintenanceState(item) === maintenance) &&
      `${item.name} ${item.tag} ${item.assetType} ${item.systemType} ${item.brand} ${item.model} ${item.groupName} ${item.clientName} ${item.unitName}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const software = visible.filter(
    (item) =>
      String(item.assetType).toUpperCase() === "SOF" ||
      String(item.systemType).toLowerCase() === "software",
  ).length;
  const groups = [
      ...new Set(
        items.map((item) => item.groupName || `Grupo ${item.groupNumber}`),
      ),
    ].sort((a, b) => a.localeCompare(b, "pt-BR")),
    systems = [
      ...new Set(items.map((item) => item.systemType || "Não informado")),
    ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const systemTotals = systems
      .map((name) => ({
        name,
        value: visible.filter(
          (item) => (item.systemType || "Não informado") === name,
        ).length,
      }))
      .filter((item) => item.value)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6),
    maxSystem = Math.max(1, ...systemTotals.map((item) => item.value));
  const maintenanceTotals = [
    "Em dia",
    "Próxima",
    "Atrasada",
    "Sem programação",
  ].map((name) => ({
    name,
    value: visible.filter((item) => maintenanceState(item) === name).length,
  }));
  const clearFilters = () => {
    setSearch("");
    setClientId("");
    setUnitId("");
    setStatus("Todos");
    setGroup("");
    setSystem("");
    setMaintenance("Todos");
  };
  const appliedFilters = [
    `Cliente: ${context.clients.find((item) => item.id === Number(clientId))?.name || "Todos"}`,
    `Unidade: ${context.units.find((item) => item.id === Number(unitId))?.name || "Todas"}`,
    `Grupo: ${group || "Todos"}`,
    `Sistema: ${system || "Todos"}`,
    `Status: ${status}`,
    `Manutenção: ${maintenance}`,
    `Busca: ${search || "sem termo"}`,
  ];
  const statusClass = (value) =>
    String(value || "status")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="assets-overview">
      <section className="panel assets-overview-filters-wrap">
        <div className="assets-filter-title">
          <div>
            <strong>Filtros do inventário</strong>
            <small>
              Refine por identificação, vínculo e condição operacional.
            </small>
          </div>
          <span>{visible.length} item(ns) encontrado(s)</span>
        </div>
        <div className="assets-overview-filters">
          <label className="assets-search">
            Buscar
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ID, item, marca, modelo, cliente ou unidade"
            />
          </label>
          <label>
            Cliente
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setUnitId("");
              }}
            >
              <option value="">Todos</option>
              {context.clients.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Unidade
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">Todas</option>
              {units.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Grupo
            <select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">Todos</option>
              {groups.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label>
            Sistema
            <select value={system} onChange={(e) => setSystem(e.target.value)}>
              <option value="">Todos</option>
              {systems.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label>
            Status do item
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option>Todos</option>
              {[
                ...new Set(items.map((item) => item.status).filter(Boolean)),
              ].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label>
            Manutenção
            <select
              value={maintenance}
              onChange={(e) => setMaintenance(e.target.value)}
            >
              {[
                "Todos",
                "Em dia",
                "Próxima",
                "Atrasada",
                "Sem programação",
              ].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <div className="assets-filter-actions">
            <button type="button" onClick={clearFilters}>
              Limpar
            </button>
            <button
              type="button"
              className="primary"
              onClick={() =>
                exportAssetsOverviewPdf({
                  items: visible,
                  systemTotals,
                  maintenanceTotals,
                  filters: appliedFilters,
                })
              }
            >
              <Icon name="download" size={15} /> Exportar PDF
            </button>
          </div>
        </div>
      </section>
      <div className="assets-overview-kpis">
        <SummaryBox
          tone="blue"
          label="Itens cadastrados"
          value={visible.length}
          detail="Equipamentos e softwares"
        />
        <SummaryBox
          tone="green"
          label="Ativos"
          value={visible.filter((item) => item.status === "Ativo").length}
          detail="Disponíveis na operação"
        />
        <SummaryBox
          tone="purple"
          label="Softwares"
          value={software}
          detail="Licenças e soluções cadastradas"
        />
        <SummaryBox
          tone="amber"
          label="Atenção preventiva"
          value={
            visible.filter((item) =>
              ["Próxima", "Atrasada"].includes(maintenanceState(item)),
            ).length
          }
          detail="Próximos ou fora do prazo"
        />
      </div>
      <div className="assets-overview-charts">
        <section className="panel assets-chart-card">
          <PanelTitle
            title="Distribuição por sistema"
            subtitle="Composição do inventário filtrado"
          />
          <div className="assets-system-bars">
            {systemTotals.map((item, index) => (
              <div key={item.name}>
                <span>
                  <b>{item.name}</b>
                  <strong>{item.value}</strong>
                </span>
                <div>
                  <i
                    style={{
                      width: `${(item.value / maxSystem) * 100}%`,
                      "--asset-index": index,
                    }}
                  />
                </div>
              </div>
            ))}
            {!systemTotals.length && (
              <div className="empty">Sem dados para o período selecionado.</div>
            )}
          </div>
        </section>
        <section className="panel assets-chart-card">
          <PanelTitle
            title="Situação das manutenções"
            subtitle="Leitura preventiva dos próximos vencimentos"
          />
          <div className="assets-maintenance-chart">
            <div
              className="assets-maintenance-donut"
              style={{
                "--on-time": `${visible.length ? (maintenanceTotals[0].value / visible.length) * 100 : 0}%`,
                "--soon": `${visible.length ? (maintenanceTotals[1].value / visible.length) * 100 : 0}%`,
                "--late": `${visible.length ? (maintenanceTotals[2].value / visible.length) * 100 : 0}%`,
              }}
            >
              <span>
                <b>{visible.length}</b>
                <small>itens</small>
              </span>
            </div>
            <ul>
              {maintenanceTotals.map((item) => (
                <li
                  key={item.name}
                  className={item.name.toLowerCase().replace(" ", "-")}
                >
                  <span>{item.name}</span>
                  <b>{item.value}</b>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
      <section className="panel assets-overview-table">
        <PanelTitle
          title="Equipamentos e softwares"
          subtitle="Organizados por Cliente → Unidade e grupo"
        />
        <div className="assets-overview-head">
          <span>Grupo</span>
          <span>ID</span>
          <span>Item</span>
          <span>Cliente / unidade</span>
          <span>Última manutenção</span>
          <span>Próxima manutenção</span>
          <span>Status</span>
        </div>
        {visible.map((item) => (
          <button
            className="assets-overview-row"
            key={item.id}
            onClick={() => setSelected(item)}
          >
            <span>
              <b>{item.groupName || `Grupo ${item.groupNumber}`}</b>
              <small>Grupo {item.groupNumber}</small>
            </span>
            <code>{item.tag || `#${item.id}`}</code>
            <span>
              <b>{item.name}</b>
              <small>
                {[item.systemType, item.brand, item.model]
                  .filter(Boolean)
                  .join(" • ")}
              </small>
            </span>
            <span>
              <b>{item.clientName || "—"}</b>
              <small>{item.unitName || "—"}</small>
            </span>
            <time>{maintenancePdfDate(item.lastMaintenanceDate)}</time>
            <time>{maintenancePdfDate(item.nextMaintenanceDate)}</time>
            <i className={`status-${statusClass(item.status)}`}>
              {item.status}
            </i>
          </button>
        ))}
        {!visible.length && (
          <div className="empty">Nenhum item encontrado.</div>
        )}
      </section>
      {selected && (
        <AssetHistoryModal
          token={token}
          asset={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function SpotPreventive({ token, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const timeNow = new Date().toTimeString().slice(0, 5);
  const [setup, setSetup] = useState({
    clients: [],
    units: [],
    checklists: [],
    technicians: [],
    systems: [],
  });
  const [clientId, setClientId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [assets, setAssets] = useState([]);
  const [assetIds, setAssetIds] = useState([]);
  const [technicianIds, setTechnicianIds] = useState([]);
  const [systemType, setSystemType] = useState("");
  const [checklistTemplateId, setChecklistTemplateId] = useState("");
  const [date, setDate] = useState(today);
  const [startTime, setStartTime] = useState(timeNow);
  const [endTime, setEndTime] = useState(timeNow);
  const [query, setQuery] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [showEquipment, setShowEquipment] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const client = setup.clients.find(
    (item) => String(item.id) === String(clientId),
  );
  const unit = setup.units.find((item) => String(item.id) === String(unitId));
  const loadAssets = (selectedUnitId) => {
    if (!selectedUnitId) return setAssets([]);
    fetch(`${apiBase}/maintenance/inventory?unitId=${selectedUnitId}`, {
      headers,
    })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) =>
        setAssets((data.equipment || []).filter((item) => item.active)),
      )
      .catch(() =>
        setError("Não foi possível carregar os equipamentos desta unidade."),
      );
  };
  useEffect(() => {
    fetch(`${apiBase}/maintenance/spot/setup`, { headers })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => {
        setSetup(data);
      })
      .catch(() => setError("Não foi possível preparar a Preventiva Spot."))
      .finally(() => setLoading(false));
  }, [token]);
  const visibleAssets = assets.filter(
    (item) =>
      (!systemType || item.systemType === systemType) &&
      `${item.tag} ${item.name} ${item.assetType} ${item.systemType} ${item.area} ${item.location} Grupo ${item.groupNumber}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  useEffect(() => {
    const compatible = setup.checklists.find(
      (item) =>
        String(item.assetType || "").toLowerCase() === systemType.toLowerCase(),
    );
    setChecklistTemplateId(compatible?.id || setup.checklists?.[0]?.id || "");
    setAssetIds([]);
  }, [systemType, setup.checklists]);
  const toggleAsset = (id) =>
    setAssetIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  const toggleTechnician = (id) =>
    setTechnicianIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  const create = async () => {
    if (
      !clientId ||
      !unitId ||
      !systemType ||
      !assetIds.length ||
      !technicianIds.length ||
      !checklistTemplateId ||
      !date ||
      !startTime ||
      !endTime
    ) {
      setError(
        "Selecione cliente, unidade, sistema, equipamentos, equipe, data e horários.",
      );
      return;
    }
    if (endTime < startTime)
      return setError("O horário final deve ser posterior ao início.");
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/maintenance/spot`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId,
          unitId,
          systemType,
          assetIds,
          technicianIds,
          checklistTemplateId,
          date,
          startTime,
          endTime,
          notes,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error);
      setCreated(result);
      onSaved?.();
    } catch {
      setError(
        "Não foi possível iniciar a Preventiva Spot. Revise os dados selecionados.",
      );
    } finally {
      setSaving(false);
    }
  };
  if (loading)
    return <div className="empty">Preparando a Preventiva Spot…</div>;
  return (
    <div className="spot-preventive-page">
      <section className="spot-hero">
        <div>
          <span>ATENDIMENTO PREVENTIVO SOB DEMANDA</span>
          <h2>Preventiva Spot</h2>
          <p>
            Registre uma visita não programada, selecione somente os itens
            disponíveis e execute a inspeção completa.
          </p>
        </div>
        <div>
          <b>{assetIds.length}</b>
          <span>itens selecionados</span>
        </div>
      </section>
      <section className="panel spot-context-grid">
        <label>
          Cliente*
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setUnitId("");
              setAssets([]);
              setAssetIds([]);
            }}
          >
            <option value="">Selecione</option>
            {setup.clients.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Unidade*
          <select
            value={unitId}
            disabled={!clientId}
            onChange={(e) => {
              setUnitId(e.target.value);
              setAssetIds([]);
              loadAssets(e.target.value);
            }}
          >
            <option value="">Selecione</option>
            {setup.units
              .filter((item) => String(item.clientId) === String(clientId))
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          Sistema da preventiva*
          <select
            value={systemType}
            onChange={(e) => setSystemType(e.target.value)}
          >
            <option value="">Selecione</option>
            {setup.systems.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
        <label>
          Data*
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <label>
          Início*
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </label>
        <label>
          Fim*
          <input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </label>
      </section>
      <section className="panel spot-team-panel">
        <div className="spot-panel-title">
          <div>
            <span>1</span>
            <div>
              <h3>Equipe da visita</h3>
              <p>
                Selecione o responsável principal primeiro e os demais
                participantes.
              </p>
            </div>
          </div>
        </div>
        <div className="spot-technician-list">
          {setup.technicians.map((person) => (
            <label
              className={technicianIds.includes(person.id) ? "selected" : ""}
              key={person.id}
            >
              <input
                type="checkbox"
                checked={technicianIds.includes(person.id)}
                onChange={() => toggleTechnician(person.id)}
              />
              <span>{initials(person.name)}</span>
              <div>
                <b>{person.name}</b>
                <small>{person.functionName || "Técnico"}</small>
              </div>
            </label>
          ))}
        </div>
      </section>
      <section className="panel spot-assets-panel">
        <div className="spot-assets-toolbar">
          <div className="spot-panel-title">
            <div>
              <span>2</span>
              <div>
                <h3>Equipamentos disponíveis</h3>
                <p>Selecione somente os itens atendidos nesta visita.</p>
              </div>
            </div>
          </div>
          <div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar TAG, equipamento, grupo, área ou local"
            />
            <button
              className="secondary-button"
              disabled={!unit}
              onClick={() => setShowEquipment(true)}
            >
              <Icon name="plus" size={15} /> Cadastrar equipamento
            </button>
          </div>
        </div>
        {unit && assets.length > 0 && (
          <div className="spot-select-actions">
            <button
              onClick={() => setAssetIds(visibleAssets.map((item) => item.id))}
            >
              Selecionar exibidos
            </button>
            <button onClick={() => setAssetIds([])}>Limpar seleção</button>
            <span>{visibleAssets.length} itens encontrados</span>
          </div>
        )}
        <div className="spot-asset-list">
          {visibleAssets.map((item) => (
            <label
              className={assetIds.includes(item.id) ? "selected" : ""}
              key={item.id}
            >
              <input
                type="checkbox"
                checked={assetIds.includes(item.id)}
                onChange={() => toggleAsset(item.id)}
              />
              <span>
                <Icon name="box" size={16} />
              </span>
              <div>
                <b>{item.name}</b>
                <small>{item.tag}</small>
                <small>
                  Grupo {item.groupNumber} •{" "}
                  {[item.area, item.location].filter(Boolean).join(" — ")}
                </small>
              </div>
              <em>{item.systemType || item.assetType}</em>
            </label>
          ))}
        </div>
        {!unit && (
          <div className="inventory-empty">
            Selecione o cliente e a unidade para visualizar os equipamentos.
          </div>
        )}
        {unit && !assets.length && (
          <div className="inventory-empty">
            Nenhum equipamento ativo nesta unidade. Cadastre o primeiro item
            para iniciar.
          </div>
        )}
      </section>
      <section className="panel spot-finalize">
        <label>
          Observação geral da visita
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Contexto, solicitação do cliente ou informação importante para a equipe…"
          />
          <AiReviewButton
            token={token}
            value={notes}
            onApply={setNotes}
            context="observação geral de preventiva spot"
          />
        </label>
        <div>
          <p>
            A atividade será criada em andamento e abrirá a inspeção individual
            dos equipamentos.
          </p>
          <button className="primary" disabled={saving} onClick={create}>
            {saving ? "Criando…" : "Iniciar Preventiva Spot"}
          </button>
        </div>
      </section>
      {error && <div className="login-error">{error}</div>}
      {showEquipment && client && unit && (
        <EquipmentForm
          client={client}
          unit={unit}
          token={token}
          onClose={() => setShowEquipment(false)}
          onSaved={() => {
            setShowEquipment(false);
            loadAssets(unit.id);
          }}
        />
      )}
      {created && (
        <ChecklistModal
          activity={created}
          token={token}
          onClose={() => setCreated(null)}
          onSaved={() => {
            setCreated(null);
            setAssetIds([]);
            onSaved?.();
          }}
        />
      )}
    </div>
  );
}

const visitNatureLabel = {
  preventive: "Preventiva",
  corrective: "Corretiva",
  inspection: "Inspeção",
  other: "Outra atividade",
};

const visitStatusLabel = {
  draft: "Rascunho",
  in_progress: "Em andamento",
  completed: "Concluída",
  cancelled: "Cancelada",
};

function FieldVisitSpot({ token, onSaved }) {
  const now = new Date();
  const [setup, setSetup] = useState({
    units: [],
    technicians: [],
    assets: [],
  });
  const [unitId, setUnitId] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [technicianIds, setTechnicianIds] = useState([]);
  const [showAdditionalTechnicians, setShowAdditionalTechnicians] = useState(false);
  const [visitDate, setVisitDate] = useState(now.toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState(now.toTimeString().slice(0, 5));
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [startedVisitId, setStartedVisitId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    fetch(`${apiBase}/visits/setup`, { headers })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then(setSetup)
      .catch(() => setError("Não foi possível preparar a nova visita."));
  }, [token]);
  const unit = setup.units.find((item) => String(item.id) === String(unitId));
  const technicians = setup.technicians.filter(
    (item) =>
      !item.residentUnitId || String(item.residentUnitId) === String(unitId),
  );
  const residentTechnicians = setup.technicians.filter(
    (item) => String(item.residentUnitId) === String(unitId),
  );
  const responsibleOptions = residentTechnicians.length
    ? residentTechnicians
    : technicians;
  const additionalTechnicians = technicians.filter(
    (item) =>
      !item.residentUnitId &&
      String(item.id) !== String(responsibleId),
  );
  const assets = setup.assets.filter(
    (item) =>
      String(item.unitId) === String(unitId) &&
      `${item.tag} ${item.name} ${item.systemType} ${item.area} ${item.location}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const addAsset = (asset) => {
    if (items.some((item) => Number(item.assetId) === Number(asset.id))) return;
    setItems((current) => [
      ...current,
      {
        key: `asset-${asset.id}`,
        assetId: asset.id,
        assetName: asset.name,
        tag: asset.tag,
        nature: "preventive",
        title: asset.name,
        description: "",
      },
    ]);
  };
  const addManual = () =>
    setItems((current) => [
      ...current,
      {
        key: `manual-${Date.now()}`,
        assetId: null,
        assetName: "Atividade sem equipamento",
        tag: "Manual",
        nature: "corrective",
        title: "Manutenção corretiva",
        description: "",
      },
    ]);
  const updateItem = (key, field, value) =>
    setItems((current) =>
      current.map((item) =>
        item.key === key ? { ...item, [field]: value } : item,
      ),
    );
  const create = async () => {
    if (!unitId || !responsibleId || !visitDate || !startTime || !items.length)
      return setError(
        "Selecione a unidade, o responsável e ao menos um item de atendimento.",
      );
    if (items.some((item) => !item.title.trim()))
      return setError("Informe o título de todos os itens da visita.");
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/visits/sync`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          unitId: Number(unitId),
          origin: "spot",
          visitDate,
          checkin: `${visitDate}T${startTime}:00`,
          responsibleTechnicianId: Number(responsibleId),
          technicianIds: technicianIds.map(Number),
          notes,
          activities: items.map((item) => ({
            nature: item.nature,
            source: "web_spot",
            assetId: item.assetId,
            title: item.title,
            description: item.description,
            status: "in_progress",
          })),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error);
      const createdVisit = result.visit || result;
      if (!createdVisit?.id) throw new Error("visit_response_invalid");
      setStartedVisitId(createdVisit.id);
      setItems([]);
      setNotes("");
      onSaved?.();
    } catch (requestError) {
      const messages = {
        preventive_checklist_required:
          "Não há um roteiro preventivo compatível com um dos equipamentos selecionados.",
        visit_asset_scope_invalid:
          "Um dos equipamentos não pertence à unidade selecionada.",
        visit_responsible_invalid: "Selecione um técnico responsável ativo.",
        resident_technician_unit_mismatch:
          "O técnico residente selecionado pertence a outra unidade.",
      };
      setError(
        messages[requestError.message] ||
          "Não foi possível iniciar a visita. Revise os dados e tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="field-visit-page">
      <section className="spot-hero visit-hero">
        <div>
          <span>NOVA VISITA TÉCNICA</span>
          <h2>Planeje o atendimento em campo</h2>
          <p>
            Reúna preventivas, corretivas e inspeções realizadas na mesma ida ao
            cliente.
          </p>
          <div className="visit-progress">
            <span className={unitId ? "done" : "active"}><b>1</b> Local e agenda</span>
            <span className={responsibleId ? "done" : unitId ? "active" : ""}><b>2</b> Equipe</span>
            <span className={items.length ? "done" : responsibleId ? "active" : ""}><b>3</b> Itens</span>
          </div>
        </div>
        <div>
          <b>{items.length}</b>
          <span>{items.length === 1 ? "item selecionado" : "itens selecionados"}</span>
        </div>
      </section>
      <div className="visit-setup-grid">
      <section className="panel field-visit-context">
        <div className="visit-section-heading">
          <span>1</span>
          <div><b>Local e agendamento</b><small>Defina onde e quando o atendimento começa.</small></div>
        </div>
        <label>
          Cliente / unidade*
          <select
            value={unitId}
            onChange={(e) => {
              const nextUnitId = e.target.value;
              const residents = setup.technicians.filter(
                (item) => String(item.residentUnitId) === String(nextUnitId),
              );
              setUnitId(nextUnitId);
              setItems([]);
              setResponsibleId(residents[0]?.id || "");
              setTechnicianIds(residents.map((item) => item.id));
              setShowAdditionalTechnicians(false);
            }}
          >
            <option value="">Selecione</option>
            {setup.units.map((item) => (
              <option key={item.id} value={item.id}>
                {item.clientName} • {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Responsável principal*
          <select
            value={responsibleId}
            disabled={!unitId}
            onChange={(e) => setResponsibleId(e.target.value)}
          >
            <option value="">Selecione</option>
            {responsibleOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}{residentTechnicians.length ? " • Residente" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Data*
          <input
            type="date"
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value)}
          />
        </label>
        <label>
          Check-in*
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </label>
      </section>
      <section className="panel field-visit-team">
        <div className="visit-section-heading">
          <span>2</span>
          <div><b>Equipe participante</b><small>O responsável conduz e encerra a visita.</small></div>
          <em>{new Set([...technicianIds, ...(responsibleId ? [responsibleId] : [])]).size} selecionado(s)</em>
        </div>
        <div className="spot-technician-list">
          {residentTechnicians.map((person) => (
              <label className="selected resident" key={person.id}>
                <input type="checkbox" checked readOnly />
                <span>{initials(person.name)}</span>
                <div>
                  <b>{person.name}</b>
                  <small>{String(person.id) === String(responsibleId) ? "Residente • Responsável principal" : "Técnico residente"}</small>
                </div>
              </label>
            ))}
          {additionalTechnicians
            .filter((item) => technicianIds.includes(item.id))
            .map((person) => (
              <label
                className="selected additional"
                key={person.id}
              >
                <input
                  type="checkbox"
                  checked={technicianIds.includes(person.id)}
                  onChange={() =>
                    setTechnicianIds((current) =>
                      current.includes(person.id)
                        ? current.filter((id) => id !== person.id)
                        : [...current, person.id],
                    )
                  }
                />
                <span>{initials(person.name)}</span>
                <div>
                  <b>{person.name}</b>
                  <small>{person.functionName || "Técnico"}</small>
                </div>
              </label>
            ))}
        </div>
        {unitId && (
          <button
            type="button"
            className="secondary-button visit-add-technician"
            onClick={() => setShowAdditionalTechnicians((current) => !current)}
            aria-expanded={showAdditionalTechnicians}
          >
            <span className="visit-add-technician-icon"><Icon name={showAdditionalTechnicians ? "chevron" : "plus"} size={15} /></span>
            <span className="visit-add-technician-copy">
              <b>{showAdditionalTechnicians ? "Fechar participantes" : "Adicionar participante"}</b>
              <small>{showAdditionalTechnicians ? "Ocultar profissionais disponíveis" : "Incluir apoio eventual nesta visita"}</small>
            </span>
            <Icon name="chevron-right" size={15} />
          </button>
        )}
        {showAdditionalTechnicians && (
          <div className="visit-additional-picker">
            <div><b>Participantes eventuais</b><span>Selecione profissionais para apoiar esta visita.</span></div>
            {additionalTechnicians
              .filter((person) => !technicianIds.includes(person.id))
              .map((person) => (
              <label key={person.id} className={technicianIds.includes(person.id) ? "selected" : ""}>
                <input
                  type="checkbox"
                  checked={technicianIds.includes(person.id)}
                  onChange={() => setTechnicianIds((current) =>
                    current.includes(person.id)
                      ? current.filter((id) => id !== person.id)
                      : [...current, person.id],
                  )}
                />
                <span>{initials(person.name)}</span>
                <div><b>{person.name}</b><small>{person.functionName || "Técnico"}</small></div>
              </label>
            ))}
            {!additionalTechnicians.filter((person) => !technicianIds.includes(person.id)).length && <div className="empty">Todos os profissionais disponíveis já foram selecionados.</div>}
          </div>
        )}
      </section>
      </div>
      <section className="panel field-visit-assets">
        <div className="field-visit-assets-head">
          <div>
            <div className="visit-section-heading">
              <span>3</span>
              <div><b>Itens da visita</b><small>Monte o escopo do atendimento.</small></div>
            </div>
            <p>
              Selecione equipamentos e defina individualmente se o atendimento é
              preventivo, corretivo ou inspeção.
            </p>
          </div>
          <div>
            <label className="field-visit-search">
              <Icon name="search" size={16} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar equipamento, TAG, sistema ou local"
              />
            </label>
            <button className="secondary-button visit-manual-item" onClick={addManual}>
              <span><Icon name="plus" size={15} /></span>
              <span><b>Item manual</b><small>Atividade sem equipamento</small></span>
            </button>
          </div>
        </div>
        {unit && (
          <div className="field-visit-asset-picker">
            <div className="field-visit-asset-list-head">
              <span>Equipamento</span><span>Identificação</span><span>Sistema / local</span><span>Ação</span>
            </div>
            {assets.map((asset) => (
              <button
                key={asset.id}
                className={
                  items.some((item) => item.assetId === asset.id)
                    ? "selected"
                    : ""
                }
                onClick={() => addAsset(asset)}
              >
                <span className="asset-list-icon"><Icon name="box" size={15} /></span>
                <span className="asset-list-name"><b>{asset.name}</b><small>{asset.assetType || "Equipamento"}</small></span>
                <code>{asset.tag}</code>
                <span className="asset-list-location"><b>{asset.systemType || "Sistema não informado"}</b><small>{[asset.area, asset.location].filter(Boolean).join(" • ") || "Local não informado"}</small></span>
                <span className="asset-list-action">
                  <Icon name={items.some((item) => item.assetId === asset.id) ? "check" : "plus"} size={14} />
                  {items.some((item) => item.assetId === asset.id) ? "Adicionado" : "Adicionar"}
                </span>
              </button>
            ))}
          </div>
        )}
        {!unit && (
          <div className="visit-assets-empty">
            <span><Icon name="box" size={25} /></span>
            <b>Escolha o local da visita</b>
            <small>Os equipamentos vinculados à unidade aparecerão aqui para seleção.</small>
          </div>
        )}
        <div className="field-visit-items">
          {items.map((item, index) => (
            <article key={item.key}>
              <span>{index + 1}</span>
              <div>
                <b>{item.assetName}</b>
                <small>{item.tag}</small>
              </div>
              <label>
                Natureza
                <select
                  value={item.nature}
                  onChange={(e) =>
                    updateItem(item.key, "nature", e.target.value)
                  }
                >
                  {Object.entries(visitNatureLabel).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Título
                <input
                  value={item.title}
                  onChange={(e) =>
                    updateItem(item.key, "title", e.target.value)
                  }
                />
              </label>
              <label>
                Observação
                <input
                  value={item.description}
                  onChange={(e) =>
                    updateItem(item.key, "description", e.target.value)
                  }
                  placeholder="Diagnóstico ou objetivo inicial"
                />
              </label>
              <button
                className="icon-button danger"
                title="Remover item"
                onClick={() =>
                  setItems((current) =>
                    current.filter((entry) => entry.key !== item.key),
                  )
                }
              >
                ×
              </button>
            </article>
          ))}
        </div>
      </section>
      <section className="panel field-visit-footer">
        <label>
          Observação geral
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Contexto da visita ou orientação para a equipe"
          />
          <AiReviewButton
            token={token}
            value={notes}
            onApply={setNotes}
            context="observação geral de visita técnica em campo"
          />
        </label>
        <div className="visit-start-card">
          <span className="visit-start-icon"><Icon name="arrow" size={20} /></span>
          <div><small>PRONTO PARA INICIAR</small><strong>
            {unit
              ? `${unit.clientName} • ${unit.name}`
              : "Selecione o local da visita"}
          </strong></div>
          <p>
            A visita ficará em andamento até a conclusão de todos os itens.
          </p>
          <ul>
            <li className={unitId ? "done" : ""}>Local definido</li>
            <li className={responsibleId ? "done" : ""}>Responsável definido</li>
            <li className={items.length ? "done" : ""}>Ao menos um item</li>
          </ul>
          <button
            className="primary-button"
            disabled={saving || !unitId || !responsibleId || !items.length}
            onClick={create}
          >
            <Icon name="arrow" size={16} /> {saving ? "Iniciando…" : "Iniciar visita"}
          </button>
        </div>
      </section>
      {error && <div className="login-error">{error}</div>}
      {startedVisitId && (
        <VisitDetailsModal
          token={token}
          visitId={startedVisitId}
          onClose={() => setStartedVisitId(null)}
          onChanged={onSaved}
        />
      )}
    </div>
  );
}

async function exportVisitPdf(visit) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const margin = 16;
  const contentWidth = 178;
  const navy = [23, 35, 60];
  const muted = [99, 113, 135];
  const line = [218, 225, 235];
  const clean = (value) => String(value ?? "").trim();
  const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png").catch(
    () => null,
  );
  const addHeader = () => {
    pdf.setFillColor(...navy);
    pdf.rect(0, 0, 210, 28, "F");
    if (logo) {
      const properties = pdf.getImageProperties(logo);
      const scale = Math.min(52 / properties.width, 17 / properties.height);
      const width = properties.width * scale;
      const height = properties.height * scale;
      pdf.addImage(logo, "PNG", margin, 5 + (17 - height) / 2, width, height);
    } else {
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.text("ABOUTPLAN", margin, 16);
    }
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(255, 255, 255);
    pdf.text("RELATÓRIO DE VISITA", 194, 12, { align: "right" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(clean(visit.code), 194, 19, { align: "right" });
    pdf.setDrawColor(...line);
    pdf.line(margin, 278, 194, 278);
    pdf.setTextColor(...muted);
    pdf.setFontSize(7);
    pdf.text(clean(visit.code), margin, 288);
    pdf.text(`Página ${pdf.getNumberOfPages()}`, 104, 288, { align: "center" });
  };
  let y = 36;
  const nextPage = () => {
    pdf.addPage();
    addHeader();
    y = 36;
  };
  const ensureSpace = (height) => {
    if (y + height > 270) nextPage();
  };
  const section = (title) => {
    ensureSpace(15);
    pdf.setFillColor(244, 247, 251);
    pdf.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(...navy);
    pdf.text(title.toUpperCase(), margin + 4, y + 6);
    y += 14;
  };
  const field = (label, value) => {
    const text = clean(value) || "—";
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(text, contentWidth - 4);
    const fieldHeight = 10 + lines.length * 5.2;
    if (fieldHeight <= 230) ensureSpace(fieldHeight);
    else ensureSpace(11);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(...muted);
    pdf.text(label.toUpperCase(), margin, y);
    y += 5.5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...navy);
    for (const lineText of lines) {
      ensureSpace(5.2);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(...navy);
      pdf.text(lineText, margin, y);
      y += 5.2;
    }
    y += 2.5;
  };
  const identificationRow = (entries) => {
    const gap = 5;
    const width = entries.length === 1 ? contentWidth : (contentWidth - gap) / 2;
    const cards = entries.map(([label, value]) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      return { label, lines: pdf.splitTextToSize(clean(value) || "—", width - 12) };
    });
    const height = Math.max(24, 15 + Math.max(...cards.map((card) => card.lines.length)) * 5.3);
    ensureSpace(height + 5);
    cards.forEach((card, index) => {
      const x = margin + index * (width + gap);
      pdf.setFillColor(248, 250, 253);
      pdf.setDrawColor(...line);
      pdf.roundedRect(x, y, width, height, 2, 2, "FD");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(...muted);
      pdf.text(card.label.toUpperCase(), x + 6, y + 7);
      pdf.setFontSize(10);
      pdf.setTextColor(...navy);
      card.lines.forEach((lineText, lineIndex) =>
        pdf.text(lineText, x + 6, y + 15 + lineIndex * 5.3),
      );
    });
    y += height + 5;
  };
  const photoAsJpeg = (source) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve({
          data: canvas.toDataURL("image/jpeg", 0.85),
          width: canvas.width,
          height: canvas.height,
        });
      };
      image.onerror = reject;
      image.src = source;
    });
  // Capa institucional: a marca sempre conserva a proporção original.
  pdf.setFillColor(...navy);
  pdf.rect(0, 0, 210, 297, "F");
  if (logo) {
    const properties = pdf.getImageProperties(logo);
    const scale = Math.min(106 / properties.width, 29 / properties.height);
    pdf.addImage(logo, "PNG", margin, 22, properties.width * scale, properties.height * scale);
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  pdf.setTextColor(185, 198, 216);
  pdf.setCharSpace(1.1);
  pdf.text("POWERED BY ABOUTSEC", margin, 60);
  pdf.setCharSpace(0);
  pdf.setDrawColor(197, 119, 73);
  pdf.setLineWidth(1.2);
  pdf.line(margin, 68, 194, 68);
  pdf.setTextColor(197, 119, 73);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("RELATÓRIO TÉCNICO", margin, 87);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(27);
  pdf.text("Visita técnica", margin, 103);
  pdf.setFontSize(13);
  const coverClient = pdf.splitTextToSize(clean(visit.clientName) || "Cliente não informado", contentWidth);
  pdf.text(coverClient, margin, 133);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.text(pdf.splitTextToSize(clean(visit.unitName) || "Unidade não informada", contentWidth), margin, 145 + coverClient.length * 5);
  pdf.setFillColor(34, 48, 74);
  pdf.roundedRect(margin, 207, contentWidth, 43, 3, 3, "F");
  pdf.setFontSize(8);
  pdf.setTextColor(174, 188, 208);
  pdf.text("IDENTIFICAÇÃO", margin + 6, 219);
  pdf.text("DATA DA VISITA", margin + 92, 219);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(clean(visit.code), margin + 6, 229);
  pdf.text(formatDate(visit.visitDate), margin + 92, 229);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(`${(visit.activities || []).length} itens técnicos  •  ${(visit.evidences || []).filter((photo) => String(photo.mimeType || "").startsWith("image/")).length} fotos`, margin + 6, 242);
  pdf.addPage();
  addHeader();
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(197, 119, 73);
  pdf.text("DADOS DA VISITA", margin, 41);
  pdf.setFontSize(17);
  pdf.setTextColor(...navy);
  pdf.text("Identificação", margin, 51);
  pdf.setDrawColor(197, 119, 73);
  pdf.setLineWidth(0.8);
  pdf.line(margin, 57, 194, 57);
  y = 65;
  identificationRow([["Cliente", visit.clientName], ["Unidade", visit.unitName]]);
  identificationRow([
    ["Data da visita", formatDate(visit.visitDate)],
    ["Origem", visit.origin === "contract" ? "Contrato" : visit.origin === "emergency" ? "Emergencial" : "Visita Spot"],
  ]);
  identificationRow([["Situação", visitStatusLabel[visit.status] || visit.status], ["Responsável", visit.responsibleName]]);
  if (visit.checkin || visit.checkout)
    identificationRow([["Início", visit.checkin ? formatDateTime(visit.checkin) : "—"], ["Encerramento", visit.checkout ? formatDateTime(visit.checkout) : "—"]]);
  identificationRow([["Equipe", (visit.team || []).map((person) => person.name).join(", ")]]);
  if (visit.notes) {
    ensureSpace(30);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(197, 119, 73);
    pdf.text("CONTEXTO DA VISITA", margin, y + 4);
    pdf.setFontSize(17);
    pdf.setTextColor(...navy);
    pdf.text("Observação geral", margin, y + 14);
    pdf.setDrawColor(197, 119, 73);
    pdf.setLineWidth(0.8);
    pdf.line(margin, y + 20, 194, y + 20);
    y += 29;
    field("Registro da visita", visit.notes);
  }
  nextPage();
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(197, 119, 73);
  pdf.text("RELATÓRIO TÉCNICO", margin, 41);
  pdf.setFontSize(17);
  pdf.setTextColor(...navy);
  pdf.text("Atendimentos realizados", margin, 51);
  pdf.setDrawColor(197, 119, 73);
  pdf.setLineWidth(0.8);
  pdf.line(margin, 57, 194, 57);
  y = 66;
  for (const [index, activity] of (visit.activities || []).entries()) {
    ensureSpace(16);
    pdf.setDrawColor(...line);
    pdf.line(margin, y, 194, y);
    y += 5;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...navy);
    const titleLines = pdf.splitTextToSize(
      `${index + 1}. ${clean(activity.title)}`,
      contentWidth,
    );
    for (const title of titleLines) {
      ensureSpace(6);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(...navy);
      pdf.text(title, margin, y);
      y += 5;
    }
    y += 5;
    field("Natureza", visitNatureLabel[activity.nature] || activity.nature);
    if (activity.assetName || activity.tag)
      field(
        "Equipamento",
        [activity.assetName, activity.tag].filter(Boolean).join(" • "),
      );
    if (activity.serviceOrderCode || activity.ticketCode)
      field("Referência", activity.serviceOrderCode || activity.ticketCode);
    field(
      "Status técnico",
      equipmentResults.find(([value]) => value === activity.outcome)?.[1] ||
        activity.outcome,
    );
    const actions = (activity.actions || []).map(
      (value) => equipmentActions.find(([code]) => code === value)?.[1] || value,
    );
    if (actions.length) field("Ações realizadas", actions.join(", "));
    if (activity.notes) field("Observação do item", activity.notes);
    for (const [photoIndex, photo] of (visit.evidences || []).filter((entry) => Number(entry.visitActivityId) === Number(activity.id) && entry.caption).entries()) field(`Observação da foto ${photoIndex + 1}`, photo.caption);
  }
  const photos = (visit.evidences || []).filter((photo) =>
    String(photo.mimeType || "").startsWith("image/"),
  );
  for (let pageStart = 0; pageStart < photos.length; pageStart += 4) {
    nextPage();
    section(`Registro fotográfico • ${pageStart + 1}–${Math.min(pageStart + 4, photos.length)} de ${photos.length}`);
    for (const [slot, photo] of photos.slice(pageStart, pageStart + 4).entries()) {
      const col = slot % 2;
      const row = Math.floor(slot / 2);
      const cardX = margin + col * 91;
      const cardY = 54 + row * 109;
      const cardWidth = 87;
      const cardHeight = 104;
      pdf.setFillColor(247, 249, 252);
      pdf.setDrawColor(...line);
      pdf.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, "FD");
      const activity = (visit.activities || []).find((item) => Number(item.id) === Number(photo.visitActivityId));
      pdf.setTextColor(...muted);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      pdf.text(`FOTO ${pageStart + slot + 1}`, cardX + 4, cardY + 7);
      const title = pdf.splitTextToSize(clean(activity?.title) || "Registro da visita", cardWidth - 8).slice(0, 2);
      pdf.setTextColor(...navy);
      pdf.setFontSize(8);
      pdf.text(title, cardX + 4, cardY + 13);
      const imageTop = cardY + 18;
      const imageHeight = 64;
      try {
        const image = await photoAsJpeg(photo.dataUrl);
        const ratio = Math.min((cardWidth - 8) / image.width, imageHeight / image.height);
        const width = image.width * ratio;
        const height = image.height * ratio;
        pdf.addImage(image.data, "JPEG", cardX + (cardWidth - width) / 2, imageTop + (imageHeight - height) / 2, width, height, undefined, "FAST");
      } catch {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text("Imagem indisponível", cardX + 4, imageTop + 31);
      }
      if (photo.caption) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(...navy);
        const lines = pdf.splitTextToSize(clean(photo.caption), cardWidth - 8);
        pdf.text(lines.slice(0, 5), cardX + 4, cardY + 86);
        if (lines.length > 5) {
          pdf.setFontSize(6);
          pdf.setTextColor(...muted);
          pdf.text("Observação completa no item técnico", cardX + 4, cardY + 101);
        }
      }
    }
  }
  for (let page = 1; page <= pdf.getNumberOfPages(); page++) {
    pdf.setPage(page);
    if (page === 1) await addAboutsecPdfLogo(pdf, 179, 270, 18, 20, "white");
    else await addAboutsecPdfLogo(pdf, 164, 282, 30, 9, "dark");
  }
  pdf.save(
    `Visita_${clean(visit.code).replace(/[^a-z0-9_-]/gi, "_")}_${clean(visit.visitDate).slice(0, 10)}.pdf`,
  );
}

function VisitDetailsModal({ token, visitId, onClose, onChanged }) {
  const [visit, setVisit] = useState(null);
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingReport, setEditingReport] = useState(false);
  const [reportNotes, setReportNotes] = useState("");
  const [reportVisitDate, setReportVisitDate] = useState("");
  const [evidenceCaptions, setEvidenceCaptions] = useState({});
  const [removedEvidenceIds, setRemovedEvidenceIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [exporting, setExporting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const closeModal = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setDismissed(true);
  };
  useEffect(() => {
    setDismissed(false);
    setSuccess("");
  }, [visitId]);
  useEffect(() => {
    if (!dismissed) return undefined;
    onClose?.();
    return undefined;
  }, [dismissed, onClose]);
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !saving) closeModal(event);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [saving]);
  const applyVisit = (data) => {
    setVisit(data);
    setReportNotes(data.notes || "");
    setReportVisitDate(String(data.visitDate || "").slice(0, 10));
    setEvidenceCaptions(Object.fromEntries((data.evidences || []).map((photo) => [photo.id, photo.caption || ""])));
    setRemovedEvidenceIds([]);
    setForms(
      Object.fromEntries(
        (data.activities || []).map((item) => [
          item.id,
          {
            outcome: item.outcome || "",
            actions: Array.isArray(item.actions) ? item.actions : [],
            notes: item.notes || "",
            photos: [],
          },
        ]),
      ),
    );
  };
  const load = () =>
    fetch(`${apiBase}/visits/${visitId}`, { headers })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then(applyVisit)
      .catch(() => setError("Não foi possível carregar a visita."));
  useEffect(() => {
    load();
  }, [visitId, token]);
  const setForm = (activityId, field, value) =>
    setForms((current) => ({
      ...current,
      [activityId]: { ...(current[activityId] || {}), [field]: value },
    }));
  const toggleAction = (activityId, action) => {
    const selected = forms[activityId]?.actions || [];
    setForm(
      activityId,
      "actions",
      selected.includes(action)
        ? selected.filter((item) => item !== action)
        : [...selected, action],
    );
  };
  const addPhotos = (activityId, files) => {
    const existingCount = (visit?.evidences || []).filter(
      (item) => Number(item.visitActivityId) === Number(activityId) && !removedEvidenceIds.includes(item.id),
    ).length;
    const current = forms[activityId]?.photos || [];
    const available = Math.max(0, 3 - existingCount - current.length);
    const selected = Array.from(files || []).slice(0, available);
    if (
      selected.some(
        (file) =>
          !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
          file.size > 4 * 1024 * 1024,
      )
    ) {
      setError("Use JPG, PNG ou WebP com até 4 MB por foto.");
      return;
    }
    if (!selected.length) {
      if (available <= 0) setError("Cada item permite até 3 fotos.");
      return;
    }
    Promise.all(
      selected.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                mimeType: file.type,
                dataUrl: reader.result,
                caption: "",
              });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }),
      ),
    )
      .then((photos) => setForm(activityId, "photos", [...current, ...photos]))
      .catch(() => setError("Não foi possível preparar uma das fotos."));
  };
  const saveAndCloseVisit = async () => {
    const incomplete = (visit?.activities || []).filter(
      (activity) => !forms[activity.id]?.outcome,
    );
    if (incomplete.length) {
      setError(
        `Selecione o status técnico de: ${incomplete.map((item) => item.title).join(", ")}.`,
      );
      return;
    }
    setError("");
    setSaving(true);
    try {
      for (const activity of visit.activities) {
        const form = forms[activity.id] || {};
        const response = await fetch(
          `${apiBase}/visits/${visitId}/activities/${activity.id}`,
          {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({
              status: "completed",
              outcome: form.outcome,
              actions: form.actions || [],
              notes: form.notes || null,
            }),
          },
        );
        if (!response.ok) throw new Error("visit_activity_save_failed");
        for (const photo of form.photos || []) {
          const evidenceResponse = await fetch(
            `${apiBase}/visits/${visitId}/evidences`,
            {
              method: "POST",
              headers: { ...headers, "Content-Type": "application/json" },
              body: JSON.stringify({ visitActivityId: activity.id, ...photo }),
            },
          );
          if (!evidenceResponse.ok) throw new Error("invalid_visit_evidence");
        }
      }
      const response = await fetch(`${apiBase}/visits/${visitId}/close`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ checkout: new Date().toISOString() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      applyVisit({ ...data, canEditReport: true });
      setSuccess("Visita salva e encerrada. O PDF já pode ser exportado.");
      await onChanged?.();
    } catch (cause) {
      setError(
        cause.message === "visit_has_unfinished_activities"
          ? "Ainda existem itens incompletos na visita."
          : "Não foi possível salvar e encerrar a visita. Revise os dados e tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };
  const saveReport = async () => {
    setError("");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(reportVisitDate)) {
      setError("Informe uma data válida para a visita.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/visits/${visitId}/report`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          notes: reportNotes,
          visitDate: reportVisitDate,
          activities: visit.activities.map((item) => ({ id: item.id, outcome: forms[item.id]?.outcome, actions: forms[item.id]?.actions || [], notes: forms[item.id]?.notes || "" })),
          evidenceCaptions: (visit.evidences || []).filter((photo) => !removedEvidenceIds.includes(photo.id)).map((photo) => ({ id: photo.id, caption: evidenceCaptions[photo.id] || "" })),
          removedEvidenceIds,
          newEvidences: visit.activities.flatMap((item) => (forms[item.id]?.photos || []).map((photo) => ({ visitActivityId: item.id, ...photo }))),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "save_failed");
      applyVisit(data);
      setEditingReport(false);
      setSuccess("Relatório atualizado. O PDF já contém as correções.");
      await onChanged?.();
    } catch {
      setError("Não foi possível salvar o relatório. Revise os dados e tente novamente.");
    } finally {
      setSaving(false);
    }
  };
  const exportPdf = async () => {
    if (!visit || exporting) return;
    setError("");
    setExporting(true);
    try {
      await exportVisitPdf(visit);
    } catch {
      setError("Não foi possível exportar o PDF da visita.");
    } finally {
      setExporting(false);
    }
  };
  const editable =
    visit && ((visit.status !== "completed" && visit.status !== "cancelled") || editingReport);
  if (dismissed) return null;
  const modal = (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) closeModal(event);
      }}
    >
      <section
        className="modal visit-details-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {visit ? (
          <>
            <header>
              <div>
                <span>{visit.code}</span>
                <h2>
                  {visit.clientName} • {visit.unitName}
                </h2>
                <p>
                  {formatDate(editingReport ? reportVisitDate : visit.visitDate)} • Responsável:{" "}
                  {visit.responsibleName || "Não atribuído"}
                </p>
              </div>
              <button
                type="button"
                aria-label="Fechar visita"
                onClick={closeModal}
              >
                ×
              </button>
            </header>
            <div className="visit-detail-summary">
              <span>
                <small>Situação</small>
                <b>{visitStatusLabel[visit.status] || visit.status}</b>
              </span>
              <span>
                <small>Origem</small>
                <b>
                  {visit.origin === "contract"
                    ? "Contrato"
                    : visit.origin === "emergency"
                      ? "Emergencial"
                      : "Spot"}
                </b>
              </span>
              <span>
                <small>Itens</small>
                <b>{visit.activities.length}</b>
              </span>
              <span>
                <small>Equipe</small>
                <b>{visit.team.map((item) => item.name).join(", ") || "—"}</b>
              </span>
            </div>
            {editingReport && (
              <label className="visit-report-date-field">
                <span>Data da visita</span>
                <input
                  type="date"
                  required
                  value={reportVisitDate}
                  onChange={(event) => setReportVisitDate(event.target.value)}
                  disabled={saving}
                />
                <small>A alteração atualiza a data da visita no histórico e no PDF.</small>
              </label>
            )}
            <div className="visit-detail-items">
              <div className="visit-detail-section-head">
                <div>
                  <span>EXECUÇÃO TÉCNICA</span>
                  <h3>Itens de atendimento</h3>
                  <p>
                    {editingReport
                      ? "Corrija os dados técnicos e as fotos; as alterações serão aplicadas ao relatório."
                      : "Preencha todos os equipamentos. Status, ações, observações e fotos serão salvos juntos ao encerrar a visita."}
                  </p>
                </div>
              </div>
              {visit.activities.map((item, index) => {
                const form = forms[item.id] || {};
                const evidences = (visit.evidences || []).filter(
                  (entry) => Number(entry.visitActivityId) === Number(item.id) && !removedEvidenceIds.includes(entry.id),
                );
                return (
                  <article
                    className={`visit-activity-editor ${form.outcome ? "ready" : ""}`}
                    key={item.id}
                  >
                    <div className="visit-activity-head">
                      <span>{index + 1}</span>
                      <div>
                        <i className={`nature-${item.nature}`}>
                          {visitNatureLabel[item.nature]}
                        </i>
                        <b>{item.title}</b>
                        <small>
                          {item.assetName || "Sem equipamento"}
                          {item.tag ? ` • ${item.tag}` : ""}
                        </small>
                      </div>
                      <div className="visit-activity-reference">
                        <strong>
                          {item.serviceOrderCode ||
                            item.ticketCode ||
                            "Item da visita"}
                        </strong>
                        <small>
                          {form.outcome ? "Preenchido" : "Pendente"}
                        </small>
                      </div>
                    </div>
                    <div className="visit-activity-body">
                      <div className="visit-field-title">Status técnico</div>
                      <div className="equipment-result-buttons">
                        {equipmentResults.map(([value, label]) => (
                          <button
                            type="button"
                            disabled={!editable || saving}
                            className={form.outcome === value ? value : ""}
                            onClick={() => setForm(item.id, "outcome", value)}
                            key={value}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="equipment-actions">
                        <b>Ações realizadas</b>
                        <div>
                          {equipmentActions.map(([value, label]) => (
                            <label
                              className={
                                (form.actions || []).includes(value)
                                  ? "selected"
                                  : ""
                              }
                              key={value}
                            >
                              <input
                                disabled={!editable || saving}
                                type="checkbox"
                                checked={(form.actions || []).includes(value)}
                                onChange={() => toggleAction(item.id, value)}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <label className="visit-activity-notes">
                        <b>Observação do item</b>
                        <textarea
                          disabled={!editable || saving}
                          placeholder="Descreva diagnóstico, condição encontrada, testes ou orientações…"
                          value={form.notes || ""}
                          onChange={(event) =>
                            setForm(item.id, "notes", event.target.value)
                          }
                        />
                        <AiReviewButton
                          token={token}
                          value={form.notes || ""}
                          onApply={(value) => setForm(item.id, "notes", value)}
                          context={`observação técnica do item ${item.title || item.assetName || "da visita"}`}
                          disabled={!editable || saving}
                        />
                      </label>
                      <div className="equipment-evidence">
                        <label
                          className={!editable || saving ? "disabled" : ""}
                        >
                          <Icon name="camera" size={15} />
                          Adicionar fotos
                          <input
                            disabled={!editable || saving}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={(event) => {
                              addPhotos(item.id, event.target.files);
                              event.target.value = "";
                            }}
                          />
                        </label>
                        <span>
                          {evidences.length + (form.photos || []).length}/3
                          evidência(s)
                        </span>
                        <div>
                          {evidences.map((photo, photoIndex) => (
                            <figure className="visit-photo-card" key={`saved-${photo.id}`}>
                              <img
                                src={photo.dataUrl}
                                alt={`Evidência ${photoIndex + 1} do item ${item.title}`}
                              />
                              {editingReport && <button type="button" aria-label="Remover foto" onClick={() => setRemovedEvidenceIds((ids) => [...ids, photo.id])}>×</button>}
                              {editingReport ? <label className="visit-photo-caption">Observação da foto (opcional)<input maxLength={500} value={evidenceCaptions[photo.id] || ""} onChange={(event) => setEvidenceCaptions((current) => ({ ...current, [photo.id]: event.target.value }))} /></label> : photo.caption && <figcaption>{photo.caption}</figcaption>}
                            </figure>
                          ))}
                          {(form.photos || []).map((photo, photoIndex) => (
                            <figure className="visit-photo-card" key={`new-${photoIndex}`}>
                              <img
                                src={photo.dataUrl}
                                alt={`Nova evidência ${photoIndex + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setForm(
                                    item.id,
                                    "photos",
                                    form.photos.filter(
                                      (_, currentIndex) =>
                                        currentIndex !== photoIndex,
                                    ),
                                  )
                                }
                              >
                                ×
                              </button>
                              <label className="visit-photo-caption">
                                Observação da foto (opcional)
                                <input
                                  disabled={!editable || saving}
                                  maxLength={500}
                                  value={photo.caption || ""}
                                  onChange={(event) =>
                                    setForm(
                                      item.id,
                                      "photos",
                                      form.photos.map((entry, index) =>
                                        index === photoIndex
                                          ? { ...entry, caption: event.target.value }
                                          : entry,
                                      ),
                                    )
                                  }
                                  placeholder="Descreva o que a foto mostra"
                                />
                              </label>
                            </figure>
                          ))}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {(visit.notes || editingReport) && (
              <div className="visit-detail-notes">
                <b>Observação da visita</b>
                {editingReport ? <textarea maxLength={5000} value={reportNotes} onChange={(event) => setReportNotes(event.target.value)} /> : <p>{visit.notes}</p>}
              </div>
            )}
            {error && <div className="login-error">{error}</div>}
            {success && <div className="visit-save-success" role="status">{success}</div>}
            <footer className="visit-final-actions">
              {!editingReport && (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={saving}
                  onClick={closeModal}
                >
                  Fechar
                </button>
              )}
              {visit.status === "completed" && visit.canEditReport && (editingReport ? <>
                <button type="button" className="secondary-button" disabled={saving} onClick={() => { applyVisit(visit); setEditingReport(false); setError(""); }}>Cancelar edição</button>
                <button type="button" className="primary-button" disabled={saving} onClick={saveReport}>{saving ? "Salvando relatório…" : "Salvar relatório"}</button>
              </> : <button type="button" className="secondary-button" onClick={() => { setEditingReport(true); setSuccess(""); }}>Editar relatório</button>)}
              {visit.status === "completed" && !editingReport && (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={saving || exporting}
                  onClick={exportPdf}
                >
                  <Icon name="download" size={16} />
                  {exporting ? "Gerando PDF…" : "Exportar visita em PDF"}
                </button>
              )}
              {editable && visit.status !== "completed" && (
                <button
                  type="button"
                  className="primary-button"
                  disabled={saving}
                  onClick={saveAndCloseVisit}
                >
                  <Icon name="check" size={16} />
                  {saving ? "Salvando visita…" : "Salvar e encerrar visita"}
                </button>
              )}
            </footer>
          </>
        ) : (
          <div className="empty">{error || "Carregando visita…"}</div>
        )}
      </section>
    </div>
  );
  return typeof document === "undefined"
    ? modal
    : createPortal(modal, document.body);
}

function VisitsRegistry({ token, mode }) {
  const [visits, setVisits] = useState([]);
  const [setup, setSetup] = useState({ units: [] });
  const [unitId, setUnitId] = useState("");
  const [nature, setNature] = useState("all");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  const load = () => {
    const parameters = new URLSearchParams();
    if (mode === "progress") parameters.set("status", "in_progress");
    if (mode === "history") parameters.set("status", "completed");
    if (unitId) parameters.set("unitId", unitId);
    if (from) parameters.set("from", from);
    if (to) parameters.set("to", to);
    Promise.all([
      fetch(`${apiBase}/visits?${parameters}`, { headers }),
      fetch(`${apiBase}/visits/setup`, { headers }),
    ])
      .then(async ([visitsResponse, setupResponse]) => {
        if (!visitsResponse.ok || !setupResponse.ok) throw new Error();
        setVisits(await visitsResponse.json());
        setSetup(await setupResponse.json());
        setError("");
      })
      .catch(() => setError("Não foi possível carregar as visitas."));
  };
  useEffect(load, [token, mode, unitId, from, to]);
  const visible = visits.filter((item) => {
    const natureMatches =
      nature === "all" ||
      (nature === "mixed"
        ? item.preventiveCount > 0 && item.correctiveCount > 0
        : nature === "preventive"
          ? item.preventiveCount > 0
          : item.correctiveCount > 0);
    return (
      natureMatches &&
      `${item.code} ${item.clientName} ${item.unitName} ${item.responsibleName}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  });
  const totals = visible.reduce(
    (summary, visit) => ({
      items: summary.items + Number(visit.activityCount || 0),
      preventive: summary.preventive + Number(visit.preventiveCount || 0),
      corrective: summary.corrective + Number(visit.correctiveCount || 0),
    }),
    { items: 0, preventive: 0, corrective: 0 },
  );
  const clearFilters = () => {
    setSearch("");
    setUnitId("");
    setNature("all");
    setFrom("");
    setTo("");
  };
  return (
    <div className="visits-registry">
      <section className="visits-registry-hero">
        <div>
          <span>
            {mode === "progress"
              ? "OPERAÇÃO EM CAMPO"
              : "RASTREABILIDADE OPERACIONAL"}
          </span>
          <h2>
            {mode === "progress"
              ? "Visitas em andamento"
              : "Histórico de visitas"}
          </h2>
          <p>
            {mode === "progress"
              ? "Acompanhe as idas ao cliente, a equipe responsável e todos os atendimentos reunidos na visita."
              : "Consulte as visitas encerradas e todo o histórico de preventivas, corretivas e inspeções."}
          </p>
        </div>
        <div>
          <strong>{visible.length}</strong>
          <small>
            {visible.length === 1 ? "visita no período" : "visitas no período"}
          </small>
        </div>
      </section>
      <section className="visits-registry-kpis">
        <article>
          <Icon name="calendar" size={19} />
          <span>
            <small>Visitas</small>
            <b>{visible.length}</b>
            <em>{mode === "progress" ? "Abertas agora" : "Concluídas"}</em>
          </span>
        </article>
        <article>
          <Icon name="clipboard" size={19} />
          <span>
            <small>Atendimentos</small>
            <b>{totals.items}</b>
            <em>Itens vinculados</em>
          </span>
        </article>
        <article>
          <Icon name="wrench" size={19} />
          <span>
            <small>Preventivas</small>
            <b>{totals.preventive}</b>
            <em>Inspeções planejadas</em>
          </span>
        </article>
        <article>
          <Icon name="headset" size={19} />
          <span>
            <small>Corretivas</small>
            <b>{totals.corrective}</b>
            <em>Demandas técnicas</em>
          </span>
        </article>
      </section>
      <section className="panel visits-registry-filters">
        <label className="visit-search">
          Buscar
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nº da visita, cliente, unidade ou técnico"
          />
        </label>
        <label>
          Cliente / unidade
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            <option value="">Todas</option>
            {setup.units.map((item) => (
              <option key={item.id} value={item.id}>
                {item.clientName} • {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tipo
          <select value={nature} onChange={(e) => setNature(e.target.value)}>
            <option value="all">Todos</option>
            <option value="preventive">Com preventiva</option>
            <option value="corrective">Com corretiva</option>
            <option value="mixed">Visita mista</option>
          </select>
        </label>
        <label>
          De
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label>
          Até
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <button className="secondary-button" onClick={clearFilters}>
          Limpar filtros
        </button>
      </section>
      <section className="panel visits-registry-table">
        <div className="visits-registry-table-head">
          <span>Visita</span>
          <span>Cliente / unidade</span>
          <span>Data</span>
          <span>Atendimentos</span>
          <span>Responsável</span>
          <span>Situação</span>
          <span />
        </div>
        {visible.map((visit) => (
          <button
            className="visits-registry-row"
            key={visit.id}
            onClick={() => setSelected(visit.id)}
          >
            <span className="visit-code-cell">
              <b>{visit.code}</b>
              <small>
                {visit.origin === "contract"
                  ? "Contrato"
                  : visit.origin === "emergency"
                    ? "Emergencial"
                    : "Spot"}
              </small>
            </span>
            <span>
              <b>{visit.clientName}</b>
              <small>{visit.unitName}</small>
            </span>
            <time>{formatDate(visit.visitDate)}</time>
            <span className="visit-nature-badges">
              {visit.preventiveCount > 0 && (
                <i className="nature-preventive">
                  {visit.preventiveCount} preventiva(s)
                </i>
              )}
              {visit.correctiveCount > 0 && (
                <i className="nature-corrective">
                  {visit.correctiveCount} corretiva(s)
                </i>
              )}
              {!visit.preventiveCount && !visit.correctiveCount && (
                <i>{visit.activityCount} item(ns)</i>
              )}
            </span>
            <span>{visit.responsibleName || "Não atribuído"}</span>
            <em className={`visit-status visit-status-${visit.status}`}>
              {visitStatusLabel[visit.status] || visit.status}
            </em>
            <Icon name="chevron-right" size={17} />
          </button>
        ))}
        {!visible.length && (
          <div className="visits-registry-empty">
            <Icon name="calendar" size={25} />
            <b>Nenhuma visita encontrada</b>
            <span>Ajuste os filtros para consultar outro período.</span>
          </div>
        )}
        {error && <div className="empty error-text">{error}</div>}
      </section>
      {selected && (
        <VisitDetailsModal
          token={token}
          visitId={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function MaintenancePage({ token, query, module = "preventives", resident = false }) {
  const [mobilePreventiveSearch, setMobilePreventiveSearch] = useState("");
  const [mobilePreventiveFiltersOpen, setMobilePreventiveFiltersOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [inventoryContext, setInventoryContext] = useState({
    clients: [],
    units: [],
  });
  const [filter, setFilter] = useState("Todos");
  const [clientFilter, setClientFilter] = useState("Todos");
  const [unitFilter, setUnitFilter] = useState("Todas");
  const [planFilter, setPlanFilter] = useState("Todos");
  const [responsibleFilter, setResponsibleFilter] = useState("Todos");
  const [dateFilter, setDateFilter] = useState("Todos");
  const [monthFilter, setMonthFilter] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [showPlan, setShowPlan] = useState(false);
  const [guidedSetup, setGuidedSetup] = useState(false);
  const preventiveModule = module === "preventives";
  const [section, setSection] = useState(
    preventiveModule ? (resident ? "calendar" : "agenda") : "spot",
  );
  const load = () => {
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    Promise.all([
      fetch(`${apiBase}/maintenance/activities`, { headers }),
      fetch(`${apiBase}/maintenance/plans`, { headers }),
      fetch(`${apiBase}/maintenance/inventory/context`, { headers }),
    ])
      .then(async ([a, p, c]) => {
        if (!a.ok || !p.ok || !c.ok) throw new Error();
        setActivities(await a.json());
        setPlans(await p.json());
        setInventoryContext(await c.json());
        setError("");
      })
      .catch(() =>
        setError(
          "Não foi possível carregar as preventivas, clientes e unidades.",
        ),
      )
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, [token]);
  useEffect(() => {
    setSection(preventiveModule ? (resident ? "calendar" : "agenda") : "spot");
    setView(preventiveModule && resident ? "calendar" : "list");
  }, [preventiveModule, resident]);
  const clients = inventoryContext.clients.map((item) => item.name).sort();
  const selectedClientId = inventoryContext.clients.find(
    (item) => item.name === clientFilter,
  )?.id;
  const units = inventoryContext.units
    .filter(
      (item) => clientFilter === "Todos" || item.clientId === selectedClientId,
    )
    .map((item) => item.name)
    .sort();
  const scopedFilterActivities = activities.filter(
    (item) =>
      (clientFilter === "Todos" || item.clientName === clientFilter) &&
      (unitFilter === "Todas" || item.unitName === unitFilter),
  );
  const planNames = [
    ...new Set(scopedFilterActivities.map((item) => item.planName).filter(Boolean)),
  ].sort();
  const responsibles = [
    ...new Set(scopedFilterActivities.map((item) => item.assignedTo).filter(Boolean)),
  ].sort();
  const matchesDate = (item) => {
    const date = (item.plannedDate || item.scheduledDate || "").slice(0, 10);
    if (dateFilter === "Mês") return date.slice(0, 7) === monthFilter;
    if (dateFilter === "Período")
      return (
        (!periodStart || date >= periodStart) &&
        (!periodEnd || date <= periodEnd)
      );
    return true;
  };
  const matchesFilters = (item) =>
    `${item.planName} ${item.clientName} ${item.assetName} ${item.checklistName}`
      .toLowerCase()
      .includes(query.toLowerCase()) &&
    (clientFilter === "Todos" || item.clientName === clientFilter) &&
    (unitFilter === "Todas" || item.unitName === unitFilter) &&
    (planFilter === "Todos" || item.planName === planFilter) &&
    (responsibleFilter === "Todos" || item.assignedTo === responsibleFilter) &&
    (!resident || !mobilePreventiveSearch || `${item.planName} ${item.clientName} ${item.unitName} ${item.assetName} ${item.assignedTo}`.toLowerCase().includes(mobilePreventiveSearch.toLowerCase())) &&
    matchesDate(item);
  const filteredActivities = activities.filter(matchesFilters);
  const visible = filteredActivities.filter(
    (item) => filter === "Todos" || maintenanceGroup(item) === filter,
  );
  const ganttActivities = filteredActivities.filter(
    (item) =>
      !["completed", "completed_with_issue", "cancelled"].includes(item.status),
  );
  const count = (group) =>
    filteredActivities.filter((item) => maintenanceGroup(item) === group)
      .length;
  const clearMaintenanceFilters = () => {
    setClientFilter("Todos");
    setUnitFilter("Todas");
    setPlanFilter("Todos");
    setResponsibleFilter("Todos");
    setDateFilter("Todos");
    setPeriodStart("");
    setPeriodEnd("");
    setFilter("Todos");
  };
  const exportSubtitle =
    [
      clientFilter !== "Todos" && clientFilter,
      unitFilter !== "Todas" && unitFilter,
      planFilter !== "Todos" && planFilter,
      dateFilter === "Mês" && monthFilter,
      dateFilter === "Período" &&
        `${periodStart || "início"} a ${periodEnd || "fim"}`,
    ]
      .filter(Boolean)
      .join(" • ") || "Todos os clientes e períodos";
  const downloadReport = async (activity) => {
    if (downloadingReportId) return;
    setDownloadingReportId(activity.id);
    setError("");
    try {
      await downloadPreventiveReportPdf({ token, activityId: activity.id, apiBase });
    } catch {
      setError("Não foi possível gerar o relatório desta preventiva.");
    } finally {
      setDownloadingReportId(null);
    }
  };
  return (
    <>
      {!preventiveModule && (
        <PageHeading
          title="Visitas"
          subtitle="Registre visitas avulsas, acompanhe execuções em campo e consulte o histórico operacional."
        />
      )}
      <div className={`maintenance-tabs-row ${resident && preventiveModule ? "resident-preventive-tabs" : ""}`}>
        <div className="maintenance-section-tabs">
        {preventiveModule ? (
          <>
            {!resident && <button
              className={section === "agenda" ? "active" : ""}
              onClick={() => {
                setSection("agenda");
                setView("list");
              }}
            >
              <Icon name="grid" size={17} /> Visão geral
            </button>}
            <button
              className={section === "calendar" ? "active" : ""}
              onClick={() => {
                setSection("calendar");
                setView("calendar");
              }}
            >
              <Icon name="calendar" size={17} /> Calendário
            </button>
            {resident && <button className={section === "agenda" ? "active" : ""} onClick={() => { setSection("agenda"); setView("list"); }}><Icon name="clipboard" size={17} /> Lista</button>}
            <button
              className={section === "gantt" ? "active" : ""}
              onClick={() => {
                setSection("gantt");
                setView("list");
                setFilter("Todos");
              }}
            >
              <Icon name="chart" size={17} /> Gantt
            </button>
            <button
              className={section === "compliance" ? "active" : ""}
              onClick={() => setSection("compliance")}
            >
              <Icon name="check" size={17} /> Conformidade
            </button>
            {!resident && <button
              className={section === "registry" ? "active" : ""}
              onClick={() => setSection("registry")}
            >
              <Icon name="box" size={17} /> Cadastros
            </button>}
            <button
              className={section === "monthly-report" ? "active" : ""}
              onClick={() => setSection("monthly-report")}
            >
              <Icon name="chart" size={17} /> Relatório mensal
            </button>
          </>
        ) : (
          <>
            <button
              className={section === "spot" ? "active" : ""}
              onClick={() => setSection("spot")}
            >
              <Icon name="plus" size={17} /> Nova visita
            </button>
            <button
              className={section === "in-progress" ? "active" : ""}
              onClick={() => setSection("in-progress")}
            >
              <Icon name="clock" size={17} /> Em andamento
            </button>
            <button
              className={section === "history" ? "active" : ""}
              onClick={() => setSection("history")}
            >
              <Icon name="clipboard" size={17} /> Histórico
            </button>
          </>
        )}
        </div>
        {preventiveModule && !resident && (
          <button
            type="button"
            className="primary maintenance-program-button"
            onClick={() => {
              setGuidedSetup(true);
              setSection("registry");
              setView("list");
            }}
          >
            <Icon name="plus" size={16} /> Programar preventiva
          </button>
        )}
      </div>
      {section === "spot" ? (
        <FieldVisitSpot token={token} onSaved={load} />
      ) : section === "in-progress" ? (
        <VisitsRegistry token={token} mode="progress" />
      ) : section === "history" ? (
        <VisitsRegistry token={token} mode="history" />
      ) : section === "registry" && !resident ? (
        <EquipmentInventory token={token} onScheduleSaved={load} guidedSetup={guidedSetup} onAdvancedPlan={() => setShowPlan(true)} />
      ) : section === "compliance" ? (
        <>
          <PreventiveComplianceDashboard
            activities={filteredActivities}
            plans={plans}
            onOpen={setSelected}
          />
          {selected && (
            <ChecklistModal
              activity={selected}
              token={token}
              resident={resident}
              onClose={() => setSelected(null)}
              onStatusChanged={load}
              onSaved={() => {
                setSelected(null);
                load();
              }}
            />
          )}
        </>
      ) : section === "inventory" && !resident ? (
        <div className="programmed-activities-subview">
          <button
            className="secondary-button"
            onClick={() => setSection("agenda")}
          >
            <Icon name="arrow" size={15} /> Voltar para Programadas
          </button>
          <EquipmentInventory token={token} onScheduleSaved={load} guidedSetup={guidedSetup} onAdvancedPlan={() => setShowPlan(true)} />
        </div>
      ) : section === "monthly-report" ? (
        <MaintenanceMonthlyReport token={token} context={inventoryContext} />
      ) : section === "assets" ? (
        <AssetsAndSoftware token={token} context={inventoryContext} />
      ) : (
        <>
          {section === "agenda" && !resident && (
          <section className="panel programmed-activities-callout">
            <div className="programmed-activities-icon" aria-hidden="true">
              <Icon name="wrench" size={21} />
            </div>
            <div>
              <strong>Atividades e equipamentos das programadas</strong>
              <span>
                Cadastre itens, organize grupos e prepare os planos preventivos
                recorrentes.
              </span>
            </div>
            <button
              className="secondary-button programmed-activities-action"
              onClick={() => setSection("inventory")}
            >
              <span className="programmed-activities-action-icon" aria-hidden="true">
                <Icon name="plus" size={15} />
              </span>
              Adicionar atividades
            </button>
          </section>
          )}
          {section !== "gantt" && <div className={`maintenance-summary ${resident ? "resident-preventive-summary" : ""}`}>
            <SummaryBox
              tone="blue"
              label="Programadas"
              value={count("Programadas")}
              detail="Preventivas futuras"
              active={filter === "Programadas"}
              onClick={() => setFilter("Programadas")}
            />
            <SummaryBox
              tone="amber"
              label="Próximas do prazo"
              value={count("Próximas")}
              detail="Nos próximos 7 dias"
              active={filter === "Próximas"}
              onClick={() => setFilter("Próximas")}
            />
            <SummaryBox
              tone="red"
              label="Atrasadas"
              value={count("Atrasadas")}
              detail="Precisam de atenção"
              active={filter === "Atrasadas"}
              onClick={() => setFilter("Atrasadas")}
            />
            <SummaryBox
              tone="amber"
              label="Aguardando liberação da área"
              value={count("Aguardando liberação")}
              detail="Acesso pendente do cliente"
              active={filter === "Aguardando liberação"}
              onClick={() => setFilter("Aguardando liberação")}
            />
            <SummaryBox
              tone="green"
              label="Realizadas"
              value={count("Realizadas")}
              detail="Execuções registradas"
              active={filter === "Realizadas"}
              onClick={() => setFilter("Realizadas")}
            />
          </div>}
          {resident && preventiveModule && <div className="resident-preventive-mobile-tools">
            <label className="resident-preventive-search"><Icon name="search" size={17} /><input type="search" aria-label="Buscar preventivas por cliente, unidade ou atividade" placeholder="Buscar preventivas" value={mobilePreventiveSearch} onChange={(event) => setMobilePreventiveSearch(event.target.value)} /></label>
            <button type="button" className={mobilePreventiveFiltersOpen ? "active" : ""} onClick={() => setMobilePreventiveFiltersOpen((open) => !open)}><Icon name="filter" size={17} /> Filtros</button>
          </div>}
          <section className={`panel preventive-filters ${resident ? `resident-preventive-filters ${mobilePreventiveFiltersOpen ? "mobile-open" : ""}` : ""}`}>
            <label>
              Cliente
              <select
                value={clientFilter}
                onChange={(e) => {
                  setClientFilter(e.target.value);
                  setUnitFilter("Todas");
                  setPlanFilter("Todos");
                  setResponsibleFilter("Todos");
                }}
              >
                <option>Todos</option>
                {clients.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              Unidade
              <select
                value={unitFilter}
                onChange={(e) => {
                  setUnitFilter(e.target.value);
                  setPlanFilter("Todos");
                  setResponsibleFilter("Todos");
                }}
              >
                <option>Todas</option>
                {units.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              Plano / atividade
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
              >
                <option>Todos</option>
                {planNames.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              Responsável
              <select
                value={responsibleFilter}
                onChange={(e) => setResponsibleFilter(e.target.value)}
              >
                <option>Todos</option>
                {responsibles.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <div className="preventive-date-filter">
              <label>
                Data
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option>Todos</option>
                  <option>Mês</option>
                  <option>Período</option>
                </select>
              </label>
              {dateFilter === "Mês" && (
                <label>
                  Mês
                  <input
                    type="month"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                  />
                </label>
              )}
              {dateFilter === "Período" && (
                <>
                  <label>
                    De
                    <input
                      type="date"
                      value={periodStart}
                      max={periodEnd || undefined}
                      onChange={(e) => setPeriodStart(e.target.value)}
                    />
                  </label>
                  <label>
                    Até
                    <input
                      type="date"
                      value={periodEnd}
                      min={periodStart || undefined}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                    />
                  </label>
                </>
              )}
            </div>
            <div>
              <span>{visible.length} atividades encontradas</span>
              <button type="button" onClick={clearMaintenanceFilters}>
                Limpar filtros
              </button>
            </div>
          </section>
          <section className={`panel maintenance-panel preventive-table ${resident ? "resident-preventive-panel" : ""}`}>
            <div className="maintenance-toolbar">
              {section !== "gantt" && <div>
                {[
                  "Todos",
                  "Programadas",
                  "Próximas",
                  "Atrasadas",
                  "Aguardando liberação",
                  "Em execução",
                  "Realizadas",
                ].map((item) => (
                  <button
                    key={item}
                    className={filter === item ? "selected" : ""}
                    onClick={() => setFilter(item)}
                  >
                    {item}
                    <b>
                      {item === "Todos"
                        ? filteredActivities.length
                        : count(item)}
                    </b>
                  </button>
                ))}
              </div>}
              <div className="maintenance-export-actions">
                <button
                  className="secondary-button"
                  onClick={() =>
                    exportMaintenanceProgramPdf(
                      section === "gantt" ? ganttActivities : visible,
                      exportSubtitle,
                    )
                  }
                >
                  <Icon name="file" size={15} /> Imprimir programação
                </button>
                <button
                  className="secondary-button"
                  onClick={() =>
                    exportMaintenanceGanttExecutivePdf(
                      section === "gantt" ? ganttActivities : visible,
                      exportSubtitle,
                    )
                  }
                >
                  <Icon name="chart" size={15} /> Exportar Gantt
                </button>
                {!preventiveModule && (
                  <button
                    className="secondary-button"
                    onClick={() =>
                      setView(view === "list" ? "calendar" : "list")
                    }
                  >
                    <Icon
                      name={view === "list" ? "calendar" : "clipboard"}
                      size={15}
                    />{" "}
                    {view === "list" ? "Calendário" : "Lista"}
                  </button>
                )}
              </div>
            </div>
            {loading && <div className="empty">Carregando preventivas…</div>}
            {error && <div className="empty error-text">{error}</div>}
            {!loading &&
              !error &&
              (section === "gantt" ? (
                <MaintenanceGanttView
                  activities={ganttActivities}
                  onOpen={setSelected}
                />
              ) : section === "calendar" || view === "calendar" ? (
                <MaintenanceCalendar
                  activities={visible}
                  onOpen={setSelected}
                  resident={resident}
                />
              ) : (
                <>
                  <div className="preventive-head">
                    <span>Cliente / unidade</span>
                    <span>Atividade preventiva</span>
                    <span>Plano / frequência</span>
                    <span>
                      {filter === "Realizadas"
                        ? "Data realizada"
                        : "Próxima execução"}
                    </span>
                    <span>Status</span>
                    <span>Responsável</span>
                    <span></span>
                  </div>
                  {visible.map((activity) => (
                    <PreventiveRow
                      key={activity.id}
                      activity={activity}
                      plan={plans.find((plan) => plan.id === activity.planId)}
                      showCompletedDate={filter === "Realizadas"}
                      onOpen={setSelected}
                      onDownload={downloadReport}
                      reportBusy={downloadingReportId === activity.id}
                    />
                  ))}
                  {!visible.length && (
                    <div className="empty">
                      Nenhuma preventiva encontrada para os filtros
                      selecionados.
                    </div>
                  )}
                </>
              ))}
          </section>
          {section === "agenda" && !resident && (
          <section className="panel maintenance-plans">
            <PanelTitle
              title="Planos preventivos ativos"
              subtitle="Recorrências que geram automaticamente a agenda"
              action="Gerenciar planos"
            />
            <div className="plan-grid">
              {plans
                .filter(
                  (plan) =>
                    clientFilter === "Todos" ||
                    plan.clientName === clientFilter,
                )
                .map((plan) => (
                  <div className="plan-item" key={plan.id}>
                    <div className="metric-icon purple">
                      <Icon name="wrench" />
                    </div>
                    <div>
                      <strong>{plan.name}</strong>
                      <p>
                        {plan.clientName} • {plan.assetName}
                      </p>
                      <span>
                        {frequencyLabel(plan)} • Próxima:{" "}
                        {formatDate(plan.nextDate)}
                      </span>
                    </div>
                    <button>
                      <Icon name="arrow" size={16} />
                    </button>
                  </div>
                ))}
            </div>
          </section>
          )}
          {selected && (
            <ChecklistModal
              activity={selected}
              token={token}
              resident={resident}
              onClose={() => setSelected(null)}
              onStatusChanged={load}
              onSaved={() => {
                setSelected(null);
                load();
              }}
            />
          )}
        </>
      )}
      {showPlan && !resident && (
        <MaintenancePlanModal
          token={token}
          onClose={() => setShowPlan(false)}
          onSaved={() => {
            setShowPlan(false);
            load();
          }}
        />
      )}
    </>
  );
}

function MaintenanceGanttView({ activities, onOpen }) {
  const dated = activities
    .filter((item) => item.plannedDate || item.scheduledDate)
    .map((item) => ({
      ...item,
      ganttDate: String(item.plannedDate || item.scheduledDate).slice(0, 10),
    }))
    .sort((a, b) => a.ganttDate.localeCompare(b.ganttDate));
  if (!dated.length)
    return (
      <div className="preventive-gantt-empty">
        <Icon name="chart" size={28} />
        <b>Nenhuma atividade prevista no período</b>
        <span>Ajuste Cliente, Unidade ou Data para montar o cronograma.</span>
      </div>
    );
  const dayMs = 86400000,
    parseDay = (value) => new Date(`${value}T12:00:00Z`),
    first = parseDay(dated[0].ganttDate),
    last = parseDay(dated[dated.length - 1].ganttDate),
    totalDays = Math.max(1, Math.round((last - first) / dayMs) + 1),
    timelineWidth = Math.min(2600, Math.max(820, totalDays * 18)),
    position = (value) =>
      (Math.round((parseDay(value) - first) / dayMs) / Math.max(1, totalDays - 1)) * 100;
  const monthTicks = [];
  const cursor = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1, 12));
  while (cursor <= last) {
    const value = cursor.toISOString().slice(0, 10);
    monthTicks.push({
      value,
      label: cursor.toLocaleDateString("pt-BR", { month: "short", year: "numeric", timeZone: "UTC" }),
      left: Math.max(0, position(value)),
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return (
    <div className="preventive-gantt-view">
      <div className="preventive-gantt-intro">
        <div>
          <b>Cronograma de preventivas</b>
          <span>{dated.length} atividade(s) prevista(s) entre {maintenancePdfDate(dated[0].ganttDate)} e {maintenancePdfDate(dated[dated.length - 1].ganttDate)}</span>
        </div>
        <div className="preventive-gantt-legend">
          <span><i className="on-time" /> Programada</span>
          <span><i className="due-soon" /> Próxima</span>
          <span><i className="overdue" /> Atrasada</span>
        </div>
      </div>
      <div className="preventive-gantt-scroll">
        <div className="preventive-gantt-canvas" style={{ "--gantt-width": `${timelineWidth}px` }}>
          <div className="preventive-gantt-header">
            <strong>Cliente, unidade e atividade</strong>
            <div className="preventive-gantt-timeline">
              {monthTicks.map((tick) => (
                <span key={tick.value} style={{ left: `${tick.left}%` }}>{tick.label}</span>
              ))}
            </div>
          </div>
          {dated.map((item) => (
            <button className="preventive-gantt-row" key={item.id} onClick={() => onOpen(item)}>
              <span className="preventive-gantt-label">
                <b>{item.planName || item.assetName || "Atividade preventiva"}</b>
                <small>{item.clientName || "Sem cliente"} • {item.unitName || "Sem unidade"}</small>
              </span>
              <span className="preventive-gantt-track">
                {monthTicks.map((tick) => <i className="grid-line" key={tick.value} style={{ left: `${tick.left}%` }} />)}
                <i
                  className={`preventive-gantt-marker ${item.deadlineStatus || "on_time"}`}
                  style={{ left: `${position(item.ganttDate)}%` }}
                >
                  <b>{maintenancePdfDate(item.ganttDate)}</b>
                </i>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreventiveComplianceDashboard({ activities, plans, onOpen }) {
  const classified = activities.map((item) => ({
    ...item,
    complianceGroup: maintenanceGroup(item),
  }));
  const overdue = classified.filter(
    (item) => item.complianceGroup === "Atrasadas",
  );
  const upcoming = classified.filter(
    (item) => item.complianceGroup === "Próximas",
  );
  const onTrack = classified.filter((item) =>
    ["Programadas", "Em execução", "Realizadas"].includes(
      item.complianceGroup,
    ),
  );
  const activePlans = plans.filter((item) => item.status === "active").length;
  const complianceRate = classified.length
    ? Math.round(((classified.length - overdue.length) / classified.length) * 100)
    : 100;
  const scopes = Object.values(
    classified.reduce((result, item) => {
      const client = item.clientName || "Cliente não informado";
      const unit = item.unitName || "Unidade não informada";
      const key = `${client}::${unit}`;
      if (!result[key])
        result[key] = {
          key,
          client,
          unit,
          total: 0,
          onTrack: 0,
          upcoming: 0,
          overdue: 0,
        };
      result[key].total++;
      if (item.complianceGroup === "Atrasadas") result[key].overdue++;
      else if (item.complianceGroup === "Próximas") result[key].upcoming++;
      else result[key].onTrack++;
      return result;
    }, {}),
  ).sort((a, b) => b.overdue - a.overdue || a.client.localeCompare(b.client));
  const attention = [...overdue, ...upcoming].sort((a, b) =>
    String(a.plannedDate || "").localeCompare(String(b.plannedDate || "")),
  );
  return (
    <div className="preventive-compliance-dashboard">
      <section className="preventive-compliance-hero">
        <div>
          <span>ÍNDICE DE CONFORMIDADE</span>
          <strong>{complianceRate}%</strong>
          <p>
            {overdue.length
              ? `${overdue.length} atividade(s) vencida(s) exigem regularização.`
              : "Nenhuma preventiva atrasada no recorte selecionado."}
          </p>
        </div>
        <div
          className="preventive-compliance-ring"
          style={{ "--compliance": `${complianceRate * 3.6}deg` }}
        >
          <b>{complianceRate}%</b>
          <small>em conformidade</small>
        </div>
      </section>
      <div className="preventive-compliance-kpis">
        <SummaryBox
          tone="green"
          label="Em dia"
          value={onTrack.length}
          detail="Programadas, executando ou concluídas"
        />
        <SummaryBox
          tone="amber"
          label="Próximas"
          value={upcoming.length}
          detail="Vencem nos próximos 7 dias"
        />
        <SummaryBox
          tone="red"
          label="Atrasadas"
          value={overdue.length}
          detail="Fora da data prevista"
        />
        <SummaryBox
          tone="purple"
          label="Planos ativos"
          value={activePlans}
          detail="Recorrências em gestão"
        />
      </div>
      <div className="preventive-compliance-grid">
        <section className="panel preventive-scope-table">
          <div className="preventive-compliance-title">
            <div>
              <span>CONFORMIDADE POR ESCOPO</span>
              <h3>Clientes e unidades</h3>
            </div>
            <small>{scopes.length} escopo(s) monitorado(s)</small>
          </div>
          <div className="preventive-scope-head">
            <span>Cliente / unidade</span>
            <span>Em dia</span>
            <span>Próximas</span>
            <span>Atrasadas</span>
            <span>Índice</span>
          </div>
          {scopes.map((scope) => {
            const rate = scope.total
              ? Math.round(((scope.total - scope.overdue) / scope.total) * 100)
              : 100;
            return (
              <div className="preventive-scope-row" key={scope.key}>
                <div>
                  <strong>{scope.client}</strong>
                  <small>{scope.unit}</small>
                </div>
                <b className="compliance-good">{scope.onTrack}</b>
                <b className="compliance-soon">{scope.upcoming}</b>
                <b className="compliance-late">{scope.overdue}</b>
                <div className="preventive-rate">
                  <span><i style={{ width: `${rate}%` }} /></span>
                  <b>{rate}%</b>
                </div>
              </div>
            );
          })}
          {!scopes.length && (
            <div className="empty">Nenhuma preventiva no recorte atual.</div>
          )}
        </section>
        <section className="panel preventive-attention-list">
          <div className="preventive-compliance-title">
            <div>
              <span>PRIORIDADES</span>
              <h3>Próximas ações</h3>
            </div>
          </div>
          {attention.slice(0, 7).map((item) => (
            <button key={item.id} onClick={() => onOpen(item)}>
              <i className={maintenanceTone(item.complianceGroup)} />
              <span>
                <strong>{item.planName}</strong>
                <small>{item.clientName} • {item.unitName}</small>
              </span>
              <time>{formatDate(item.plannedDate)}</time>
              <Icon name="arrow" size={14} />
            </button>
          ))}
          {!attention.length && (
            <div className="preventive-compliance-ok">
              <Icon name="check" size={20} />
              <strong>Agenda regular</strong>
              <span>Não existem atividades próximas ou atrasadas.</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function PreventiveRow({ activity, plan, showCompletedDate = false, onOpen, onDownload, reportBusy = false }) {
  const group = maintenanceGroup(activity);
  const isSpot = activity.activityType === "spot";
  const executionDate = showCompletedDate
    ? activity.completedAt || activity.plannedDate
    : isSpot
      ? null
      : activity.plannedDate;
  return (
    <div className="preventive-row" role="button" tabIndex={0} onClick={() => onOpen(activity)} onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onOpen(activity); } }}>
      <div className="preventive-row-client">
        <span className="client-logo blue">
          {initials(activity.clientName || "CL")}
        </span>
        <div>
          <strong>{activity.clientName || "Sem cliente"}</strong>
          <small>{activity.unitName || "Sem unidade"}</small>
        </div>
      </div>
      <div className="preventive-row-activity">
        <strong>{activity.assetName || activity.planName}</strong>
        <small>{activity.checklistName || "Checklist não definido"}</small>
      </div>
      <div className="preventive-row-plan">
        <strong>{activity.planName}</strong>
        <small>
          {plan ? frequencyLabel(plan) : "Recorrência não informada"}
        </small>
      </div>
      <span className="preventive-row-date">
        {executionDate ? formatDate(executionDate) : ""}
        {!showCompletedDate && !isSpot && (
          <small>{activity.estimatedHours || 0}h previstas</small>
        )}
      </span>
      <span className={`preventive-row-status maintenance-status ${maintenanceTone(group)}`}>
        ● {group}
      </span>
      <span className="preventive-row-assignee">{activity.assignedTo || "Não atribuído"}</span>
      {["completed", "completed_with_issue"].includes(activity.status) ? (
        <button type="button" className="preventive-row-action preventive-download-button" title="Baixar relatório da preventiva em PDF" aria-label={`Baixar relatório da preventiva MP-${String(activity.id).padStart(4, "0")}`} disabled={reportBusy} onClick={(event) => { event.stopPropagation(); onDownload(activity); }}>
          <Icon name="download" size={16} />
        </button>
      ) : <span className="preventive-row-action"><Icon name="arrow" size={16} /></span>}
    </div>
  );
}

const equipmentTypes = [
  ["CAM", "Câmeras"],
  ["ALA", "Alarme"],
  ["SCA", "Controle de acesso"],
  ["AUT", "Automação"],
  ["SER", "Servidor"],
  ["WOR", "Workstation"],
  ["RAC", "Rack"],
  ["SWI", "Switch"],
  ["NOB", "Nobreak"],
  ["QUA", "Quadro"],
  ["TEL", "Tela"],
  ["MON", "Monitor"],
  ["ROT", "Roteador"],
  ["LEI", "Leitor"],
  ["CTL", "Controladora"],
  ["CNV", "Conversor"],
  ["HD", "HD"],
  ["STO", "Storage"],
  ["PTZ", "PTZ"],
  ["AUD", "Áudio"],
  ["ANT", "Antena"],
  ["RAD", "Radar"],
  ["CAI", "Caixa"],
  ["SOF", "Software"],
  ["GER", "Gerenciadora"],
  ["CAT", "Catraca"],
  ["TOR", "Torniquete"],
  ["CAN", "Cancela"],
  ["POR", "Portal"],
];
const excelDate = (value) => {
  if (!value) return "";
  if (typeof value === "number") {
    const date = XLSX.SSF.parse_date_code(value);
    return date
      ? `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}`
      : "";
  }
  const text = String(value).trim();
  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : text;
};

function EquipmentInventory({ token, onScheduleSaved, guidedSetup = false, onAdvancedPlan }) {
  const [context, setContext] = useState({
    clients: [],
    units: [],
    types: [],
    users: [],
  });
  const [client, setClient] = useState(null);
  const [unit, setUnit] = useState(null);
  const [inventory, setInventory] = useState({
    equipment: [],
    groups: [],
    occupiedVisits: [],
    residentTechnicians: [],
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showQrLabels, setShowQrLabels] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [equipmentFilters, setEquipmentFilters] = useState({
    group: "Todos",
    tag: "",
    equipment: "",
    area: "Todas",
    status: "Todos",
    location: "Todos",
    system: "Todos",
  });
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    fetch(`${apiBase}/maintenance/inventory/context`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setContext)
      .catch(() => setMessage("Não foi possível carregar clientes e unidades."))
      .finally(() => setLoading(false));
  }, [token]);
  const loadInventory = (selected) =>
    fetch(`${apiBase}/maintenance/inventory?unitId=${selected.id}`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        setInventory(data);
        setSelectedEquipment([]);
      })
      .catch(() => setMessage("Não foi possível carregar o inventário."));
  const chooseClient = (item) => {
    setClient(item);
    setUnit(null);
    setInventory({ equipment: [], groups: [], occupiedVisits: [], residentTechnicians: [] });
    setSelectedEquipment([]);
    setMessage("");
  };
  const chooseUnit = (item) => {
    setUnit(item);
    setSelectedEquipment([]);
    setMessage("");
    setEquipmentFilters({
      group: "Todos",
      tag: "",
      equipment: "",
      area: "Todas",
      status: "Todos",
      location: "Todos",
      system: "Todos",
    });
    loadInventory(item);
  };
  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/Modelo_Importacao_Equipamentos_AboutPlan.xlsx";
    link.download = "Modelo_Importacao_Equipamentos_AboutPlan.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !client || !unit) return;
    setImporting(true);
    setMessage("");
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array", cellDates: false });
      const sheet =
        workbook.Sheets["Equipamentos"] ||
        workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const items = rows
        .filter((row) => row["Item*"] || row.Item)
        .map((row) => ({
          itemNumber: row["Item*"] || row.Item,
          groupNumber: row["Grupo*"] || row.Grupo,
          type: String(row["Tipo*"] || row.Tipo || "")
            .trim()
            .toUpperCase(),
          name: row["Nome / descrição*"] || row["Nome / descrição"] || row.Nome,
          brand: row.Marca,
          model: row.Modelo,
          serialNumber: row["Número de série"],
          ipHostname: row["IP / Hostname"],
          capacity: row.Capacidade,
          installationDate: excelDate(row["Data de instalação"]),
          warrantyUntil: excelDate(row["Garantia até"]),
          area: row["Área*"] || row["Área"],
          location: row["Local*"] || row.Local,
          environment: row["Identificação do ambiente"],
          criticality: row["Criticidade*"] || row.Criticidade || "Média",
          status: row["Status*"] || row.Status || "Ativo",
          details: row["Informações adicionais"],
        }));
      const response = await fetch(`${apiBase}/maintenance/inventory/import`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: client.id, unitId: unit.id, items }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.rows
            ? `Confira as linhas: ${result.rows.join(", ")}`
            : "Planilha inválida. Confira os campos obrigatórios.",
        );
      setMessage(
        `${result.created} equipamento(s) importado(s) com TAG gerada automaticamente.`,
      );
      loadInventory(unit);
    } catch (error) {
      setMessage(error.message || "Não foi possível importar a planilha.");
    } finally {
      setImporting(false);
    }
  };
  const units = context.units.filter((item) => item.clientId === client?.id);
  const distinct = (field) =>
    [
      ...new Set(
        inventory.equipment.map((item) => item[field]).filter(Boolean),
      ),
    ].sort((a, b) =>
      String(a).localeCompare(String(b), "pt-BR", { numeric: true }),
    );
  const filteredEquipment = inventory.equipment.filter(
    (item) =>
      (equipmentFilters.group === "Todos" ||
        String(item.groupNumber) === equipmentFilters.group) &&
      String(item.tag || "")
        .toLowerCase()
        .includes(equipmentFilters.tag.toLowerCase()) &&
      `${item.name || ""} ${item.assetType || ""} ${item.brand || ""} ${item.model || ""}`
        .toLowerCase()
        .includes(equipmentFilters.equipment.toLowerCase()) &&
      (equipmentFilters.area === "Todas" ||
        item.area === equipmentFilters.area) &&
      (equipmentFilters.status === "Todos" ||
        item.status === equipmentFilters.status) &&
      (equipmentFilters.location === "Todos" ||
        item.location === equipmentFilters.location) &&
      (equipmentFilters.system === "Todos" ||
        item.systemType === equipmentFilters.system),
  );
  const exportEquipment = () => {
    if (!unit || !filteredEquipment.length) return;
    const rows = filteredEquipment.map((item) => ({
      Item: String(item.itemNumber ?? ""),
      TAG: String(item.tag ?? ""),
      Equipamento: item.name || "",
      Grupo: String(item.groupNumber ?? ""),
      Área: item.area || "",
      Sistema: item.systemType || "",
      Status: item.status || "",
    }));
    const sheet = XLSX.utils.json_to_sheet(rows, {
      header: [
        "Item", "TAG", "Equipamento", "Grupo", "Área", "Sistema", "Status",
      ],
    });
    sheet["!cols"] = [
      { wch: 12 }, { wch: 28 }, { wch: 48 }, { wch: 12 },
      { wch: 30 }, { wch: 28 }, { wch: 18 },
    ];
    sheet["!autofilter"] = { ref: sheet["!ref"] };
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Equipamentos");
    const fileName = `Inventario_${client.name}_${unit.name}`
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .slice(0, 100);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };
  const setEquipmentFilter = (field, value) =>
    setEquipmentFilters((current) => ({ ...current, [field]: value }));
  const clearEquipmentFilters = () =>
    setEquipmentFilters({
      group: "Todos",
      tag: "",
      equipment: "",
      area: "Todas",
      status: "Todos",
      location: "Todos",
      system: "Todos",
    });
  const visibleIds = filteredEquipment.map((item) => item.id);
  const allVisibleSelected =
    Boolean(visibleIds.length) &&
    visibleIds.every((id) => selectedEquipment.includes(id));
  const toggleEquipment = (id) =>
    setSelectedEquipment((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  const toggleVisible = () =>
    setSelectedEquipment((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : [...new Set([...current, ...visibleIds])],
    );
  const deleteSelected = async () => {
    if (!selectedEquipment.length || !client || !unit) return;
    const count = selectedEquipment.length;
    if (
      !window.confirm(
        `Excluir definitivamente ${count} equipamento(s) selecionado(s)? Itens com histórico de manutenção serão preservados e não poderão ser excluídos.`,
      )
    )
      return;
    setDeleting(true);
    setMessage("");
    try {
      const response = await fetch(`${apiBase}/maintenance/inventory`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: client.id,
          unitId: unit.id,
          ids: selectedEquipment,
        }),
      });
      const contentType = response.headers.get("content-type") || "";
      const result = contentType.includes("application/json")
        ? await response.json()
        : null;
      if (!response.ok) {
        if (result?.error === "equipment_has_maintenance_history")
          throw new Error(
            `Não foi possível excluir: ${result.protected.map((item) => item.tag).join(", ")} possui(em) plano, atividade ou histórico de manutenção.`,
          );
        if (response.status === 404 || !result)
          throw new Error(
            "A rota de exclusão ainda não está ativa. Reinicie o backend do AboutPlan e tente novamente.",
          );
        throw new Error(
          "Não foi possível excluir os equipamentos selecionados.",
        );
      }
      if (!result)
        throw new Error(
          "O backend retornou uma resposta inválida. Reinicie o serviço e tente novamente.",
        );
      setMessage(`${result.deleted} equipamento(s) excluído(s) com sucesso.`);
      await loadInventory(unit);
    } catch (error) {
      setMessage(
        error.message ||
          "Não foi possível excluir os equipamentos selecionados.",
      );
    } finally {
      setDeleting(false);
    }
  };
  if (loading)
    return (
      <section className="panel inventory-empty">
        Carregando cadastro de equipamentos…
      </section>
    );
  return (
    <div className="equipment-inventory">
      <section className={`preventive-setup-guide${guidedSetup ? " emphasized" : ""}`} aria-label="Etapas para programar preventivas">
        <div className="preventive-setup-guide-heading">
          <div><small>CONFIGURAÇÃO ASSISTIDA</small><h2>Programar uma preventiva</h2><p>Escolha onde será feita, cadastre os equipamentos e defina as visitas. O sistema cria a agenda automaticamente.</p></div>
          {onAdvancedPlan && <button type="button" className="preventive-advanced-link" onClick={onAdvancedPlan}>Plano individual avançado <Icon name="arrow" size={14} /></button>}
        </div>
        <div className="preventive-setup-steps">
          <span className={client ? "done" : "current"}><b>{client ? <Icon name="check" size={14} /> : "1"}</b> Cliente</span>
          <span className={unit ? "done" : client ? "current" : ""}><b>{unit ? <Icon name="check" size={14} /> : "2"}</b> Unidade</span>
          <span className={inventory.equipment.length ? "done" : unit ? "current" : ""}><b>{inventory.equipment.length ? <Icon name="check" size={14} /> : "3"}</b> Equipamentos e grupo</span>
          <span className={inventory.groups.some(group => group.activePlanId) ? "done" : inventory.equipment.length ? "current" : ""}><b>{inventory.groups.some(group => group.activePlanId) ? <Icon name="check" size={14} /> : "4"}</b> Programação</span>
        </div>
        {unit && <div className="preventive-setup-next"><span>{inventory.equipment.length ? `${inventory.equipment.length} equipamento(s) em ${inventory.groups.length} grupo(s). ${inventory.groups.filter(group => !group.activePlanId).length} grupo(s) aguardam programação.` : "Esta unidade ainda não tem equipamentos cadastrados."}</span><button type="button" className="primary" onClick={() => inventory.equipment.length ? setShowScheduler(true) : setShowForm(true)}><Icon name={inventory.equipment.length ? "calendar" : "plus"} size={16} /> {inventory.equipment.length ? "Programar grupos" : "Cadastrar primeiro equipamento"}</button></div>}
      </section>
      <div className="inventory-breadcrumb">
        <button
          onClick={() => {
            setClient(null);
            setUnit(null);
          }}
        >
          Clientes
        </button>
        {client && (
          <>
            <span>›</span>
            <button onClick={() => setUnit(null)}>{client.name}</button>
          </>
        )}
        {unit && (
          <>
            <span>›</span>
            <strong>{unit.name}</strong>
          </>
        )}
      </div>
      {!client ? (
        <>
          <div className="inventory-title">
            <div>
              <h2>Selecione o cliente</h2>
              <p>
                Escolha o cliente para acessar suas unidades e o inventário de
                equipamentos.
              </p>
            </div>
          </div>
          {context.clients.length > 8 && <input className="inventory-client-search" type="search" value={clientSearch} onChange={(event) => setClientSearch(event.target.value)} placeholder="Buscar cliente pelo nome" aria-label="Buscar cliente" />}
          <div className="inventory-client-grid">
            {context.clients.filter(item => item.name.toLocaleLowerCase("pt-BR").includes(clientSearch.trim().toLocaleLowerCase("pt-BR"))).map((item) => (
              <button key={item.id} onClick={() => chooseClient(item)}>
                <span className="client-logo blue">{initials(item.name)}</span>
                <div>
                  <strong>{item.name}</strong>
                  <small>
                    {item.unitCount} unidade(s) • {item.equipmentCount}{" "}
                    equipamento(s)
                  </small>
                </div>
                <Icon name="arrow" size={17} />
              </button>
            ))}
          </div>
        </>
      ) : !unit ? (
        <>
          <div className="inventory-title">
            <div>
              <h2>Unidades de {client.name}</h2>
              <p>
                O cadastro e a importação serão vinculados à unidade
                selecionada.
              </p>
            </div>
          </div>
          <div className="inventory-client-grid unit-selection">
            {units.map((item) => (
              <button key={item.id} onClick={() => chooseUnit(item)}>
                <span className="client-logo green">
                  <Icon name="building" size={17} />
                </span>
                <div>
                  <strong>{item.name}</strong>
                  <small>
                    {[item.city, item.state].filter(Boolean).join(" – ") ||
                      item.code ||
                      "Endereço não informado"}{" "}
                    • {item.equipmentCount} equipamento(s)
                  </small>
                </div>
                <Icon name="arrow" size={17} />
              </button>
            ))}
            {!units.length && (
              <div className="panel inventory-empty">
                Este cliente ainda não possui unidades cadastradas.
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="inventory-title inventory-unit-head">
            <div>
              <h2>Inventário — {unit.name}</h2>
              <p>
                {client.name} • Cadastre individualmente ou importe a planilha
                preenchida.
              </p>
            </div>
            <div className="inventory-actions">
              <button onClick={() => setShowScheduler(true)}>
                <Icon name="calendar" size={16} /> Agendar atividades
                recorrentes
              </button>
              <button onClick={() => setShowQrLabels(true)}>
                <Icon name="box" size={16} /> Etiquetas QR Code
              </button>
              <button onClick={downloadTemplate}>
                <Icon name="file" size={16} /> Baixar modelo
              </button>
              <button
                onClick={exportEquipment}
                disabled={!filteredEquipment.length}
                title="Exportar os equipamentos exibidos com os filtros atuais"
              >
                <Icon name="file" size={16} /> Exportar Excel
              </button>
              <label className={importing ? "disabled" : ""}>
                <Icon name="file" size={16} />
                {importing ? "Importando…" : "Importar planilha"}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={importFile}
                  disabled={importing}
                />
              </label>
              <button className="primary" onClick={() => setShowForm(true)}>
                <Icon name="plus" size={16} /> Cadastrar item
              </button>
            </div>
          </div>
          {message && (
            <div
              className={`inventory-message ${message.includes("importado") ? "success" : ""}`}
            >
              {message}
            </div>
          )}
          <div className="inventory-stats">
            <div>
              <b>{inventory.equipment.length}</b>
              <span>Equipamentos</span>
            </div>
            <div>
              <b>{inventory.groups.length}</b>
              <span>Grupos</span>
            </div>
            <div>
              <b>
                {
                  new Set(inventory.equipment.map((item) => item.systemType))
                    .size
                }
              </b>
              <span>Sistemas</span>
            </div>
            <div>
              <b>
                {
                  inventory.equipment.filter((item) => item.status === "Ativo")
                    .length
                }
              </b>
              <span>Ativos</span>
            </div>
          </div>
          <section className="panel inventory-filters">
            <div className="inventory-filter-grid">
              <label>
                Grupo
                <select
                  value={equipmentFilters.group}
                  onChange={(e) => setEquipmentFilter("group", e.target.value)}
                >
                  <option>Todos</option>
                  {distinct("groupNumber").map((value) => (
                    <option key={value} value={value}>
                      Grupo {value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                TAG
                <input
                  placeholder="Buscar pela TAG"
                  value={equipmentFilters.tag}
                  onChange={(e) => setEquipmentFilter("tag", e.target.value)}
                />
              </label>
              <label>
                Equipamento
                <input
                  placeholder="Nome, tipo, marca ou modelo"
                  value={equipmentFilters.equipment}
                  onChange={(e) =>
                    setEquipmentFilter("equipment", e.target.value)
                  }
                />
              </label>
              <label>
                Área
                <select
                  value={equipmentFilters.area}
                  onChange={(e) => setEquipmentFilter("area", e.target.value)}
                >
                  <option>Todas</option>
                  {distinct("area").map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select
                  value={equipmentFilters.status}
                  onChange={(e) => setEquipmentFilter("status", e.target.value)}
                >
                  <option>Todos</option>
                  {distinct("status").map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Local
                <select
                  value={equipmentFilters.location}
                  onChange={(e) =>
                    setEquipmentFilter("location", e.target.value)
                  }
                >
                  <option>Todos</option>
                  {distinct("location").map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Sistema
                <select
                  value={equipmentFilters.system}
                  onChange={(e) => setEquipmentFilter("system", e.target.value)}
                >
                  <option>Todos</option>
                  {distinct("systemType").map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="inventory-filter-result">
              <span>
                Exibindo <b>{filteredEquipment.length}</b> de{" "}
                {inventory.equipment.length} equipamentos
              </span>
              <button onClick={clearEquipmentFilters}>Limpar filtros</button>
            </div>
          </section>
          {selectedEquipment.length > 0 && (
            <div className="inventory-selection-bar">
              <div>
                <strong>
                  {selectedEquipment.length} item(ns) selecionado(s)
                </strong>
                <span>
                  Você pode continuar selecionando mesmo usando os filtros.
                </span>
              </div>
              <button onClick={() => setSelectedEquipment([])}>
                Limpar seleção
              </button>
              <button
                className="inventory-delete-selected"
                onClick={deleteSelected}
                disabled={deleting}
              >
                <Icon name="trash" size={15} />
                {deleting ? "Excluindo…" : "Excluir selecionados"}
              </button>
            </div>
          )}
          <section className="panel inventory-table">
            <div className="inventory-table-head">
              <label className="inventory-check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleVisible}
                  disabled={!visibleIds.length}
                  aria-label="Selecionar todos os equipamentos filtrados"
                />
              </label>
              <span>Item / TAG</span>
              <span>Equipamento</span>
              <span>Grupo</span>
              <span>Área / local</span>
              <span>Sistema</span>
              <span>Status</span>
              <span></span>
            </div>
            {filteredEquipment.map((item) => (
              <div
                className={`inventory-row ${selectedEquipment.includes(item.id) ? "selected" : ""}`}
                key={item.id}
              >
                <label className="inventory-check">
                  <input
                    type="checkbox"
                    checked={selectedEquipment.includes(item.id)}
                    onChange={() => toggleEquipment(item.id)}
                    aria-label={`Selecionar ${item.tag}`}
                  />
                </label>
                <div>
                  <b>#{item.itemNumber}</b>
                  <small>{item.tag}</small>
                </div>
                <div>
                  <strong>{item.name}</strong>
                  <small>
                    {item.assetType} •{" "}
                    {[item.brand, item.model].filter(Boolean).join(" ") ||
                      "Sem fabricante informado"}
                  </small>
                </div>
                <span>Grupo {item.groupNumber}</span>
                <div>
                  <strong>{item.area}</strong>
                  <small>{item.location}</small>
                </div>
                <span>{item.systemType}</span>
                <i className={item.status === "Ativo" ? "active" : ""}>
                  {item.status}
                </i>
                <button
                  className="inventory-edit"
                  onClick={() => setEditingEquipment(item)}
                >
                  Editar
                </button>
              </div>
            ))}
            {!inventory.equipment.length ? (
              <div className="inventory-empty">
                Nenhum equipamento cadastrado nesta unidade. Use “Cadastrar
                item” ou “Importar planilha”.
              </div>
            ) : (
              !filteredEquipment.length && (
                <div className="inventory-empty">
                  Nenhum equipamento encontrado com os filtros selecionados.
                </div>
              )
            )}
          </section>
          {showForm && (
            <EquipmentForm
              client={client}
              unit={unit}
              groups={inventory.groups}
              equipment={inventory.equipment}
              token={token}
              onClose={() => setShowForm(false)}
              onSaved={() => {
                setShowForm(false);
                setMessage(
                  "Equipamento cadastrado e TAG gerada automaticamente.",
                );
                loadInventory(unit);
              }}
            />
          )}
          {editingEquipment && (
            <EquipmentForm
              record={editingEquipment}
              client={client}
              unit={unit}
              groups={inventory.groups}
              equipment={inventory.equipment}
              token={token}
              onClose={() => setEditingEquipment(null)}
              onSaved={() => {
                setEditingEquipment(null);
                setMessage("Equipamento atualizado com sucesso.");
                loadInventory(unit);
              }}
            />
          )}
          {showScheduler && (
            <RecurringScheduleModal
              groups={inventory.groups}
              occupiedVisits={inventory.occupiedVisits || []}
              holidayDates={inventory.holidayDates || []}
              residentTechnicians={inventory.residentTechnicians || []}
              users={context.users || []}
              token={token}
              client={client}
              unit={unit}
              onClose={() => setShowScheduler(false)}
              onSaved={(result, keepOpen = false) => {
                if (!keepOpen) setShowScheduler(false);
                setMessage(
                  result.deleted !== undefined
                    ? `Programação excluída. ${result.deleted} atividade(s) futura(s) removida(s).`
                    : `${result.generated} atividade(s) recorrente(s) agendada(s), sem conflito com outras visitas.`,
                );
                const refresh = loadInventory(unit);
                onScheduleSaved?.();
                return refresh;
              }}
            />
          )}
          {showQrLabels && (
            <QrLabelGenerator
              equipment={inventory.equipment}
              client={client}
              unit={unit}
              onClose={() => setShowQrLabels(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

function EquipmentForm({ record, client, unit, groups = [], equipment = [], token, onClose, onSaved }) {
  const suggestedGroup = Number(groups.find(group => !group.activePlanId)?.groupNumber || groups[0]?.groupNumber || 1);
  const nextItemNumber = (groupNumber) => Math.max(0, ...equipment.filter(item => Number(item.groupNumber) === Number(groupNumber)).map(item => Number(item.itemNumber) || 0)) + 1;
  const empty = {
    itemNumber: nextItemNumber(suggestedGroup),
    groupNumber: suggestedGroup,
    type: "CAM",
    name: "",
    brand: "",
    model: "",
    serialNumber: "",
    ipHostname: "",
    capacity: "",
    installationDate: "",
    warrantyUntil: "",
    area: "",
    location: "",
    environment: "",
    criticality: "Média",
    status: "Ativo",
    details: "",
  };
  const [form, setForm] = useState(
    record ? { ...empty, ...record, type: record.assetType } : empty,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (field, value) =>
    setForm((current) => ({ ...current, [field]: value, ...(field === "groupNumber" && !record ? { itemNumber: nextItemNumber(value) } : {}) }));
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        record
          ? `${apiBase}/maintenance/inventory/${record.id}`
          : `${apiBase}/maintenance/inventory`,
        {
          method: record ? "PATCH" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...form,
            clientId: client.id,
            unitId: unit.id,
          }),
        },
      );
      if (!response.ok) throw new Error();
      onSaved();
    } catch {
      setError("Preencha Item, Grupo, Tipo, Nome, Área e Local.");
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal equipment-modal" onSubmit={save}>
        <div className="modal-head">
          <div>
            <h2>{record ? "Editar equipamento" : "Novo equipamento"}</h2>
            <p>
              {client.name} • {unit.name} •{" "}
              {record
                ? `TAG atual: ${record.tag}`
                : "A TAG será criada automaticamente."}
            </p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="equipment-form-grid">
          <label>
            Item no grupo*
            <input
              type="number"
              min="1"
              value={form.itemNumber}
              onChange={(e) => update("itemNumber", e.target.value)}
              required
            />
          </label>
          <label>
            Grupo de atendimento*
            <select value={groups.some(group => Number(group.groupNumber) === Number(form.groupNumber)) ? String(form.groupNumber) : "new"} onChange={(e) => update("groupNumber", e.target.value === "new" ? Math.max(0, ...groups.map(group => Number(group.groupNumber) || 0)) + 1 : e.target.value)}>
              {groups.map(group => <option key={group.id} value={group.groupNumber}>{group.name || `Grupo ${group.groupNumber}`} · {group.equipmentCount} item(ns)</option>)}
              <option value="new">+ Criar novo grupo</option>
            </select>
            {!groups.some(group => Number(group.groupNumber) === Number(form.groupNumber)) && <small className="equipment-group-hint">Novo grupo {form.groupNumber} · criado ao salvar</small>}
          </label>
          <label>
            Tipo*
            <select
              value={form.type}
              onChange={(e) => update("type", e.target.value)}
            >
              {equipmentTypes.map(([code, name]) => (
                <option key={code} value={code}>
                  {code} — {name}
                </option>
              ))}
            </select>
          </label>
          <label className="wide">
            Nome / descrição*
            <input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
            />
          </label>
          <label>
            Área*
            <input value={form.area || ""} onChange={(e) => update("area", e.target.value)} required placeholder="Ex.: Recepção" />
          </label>
          <label>
            Local*
            <input value={form.location || ""} onChange={(e) => update("location", e.target.value)} required placeholder="Ex.: Balcão principal" />
          </label>
          <details className="equipment-extra-fields wide" open={record ? true : undefined}>
            <summary>Dados complementares do equipamento <Icon name="chevron" size={15} /></summary>
            <div className="equipment-form-grid">
          <label>
            Marca
            <input
              value={form.brand || ""}
              onChange={(e) => update("brand", e.target.value)}
            />
          </label>
          <label>
            Modelo
            <input
              value={form.model || ""}
              onChange={(e) => update("model", e.target.value)}
            />
          </label>
          <label>
            Número de série
            <input
              value={form.serialNumber || ""}
              onChange={(e) => update("serialNumber", e.target.value)}
            />
          </label>
          <label>
            IP / Hostname
            <input
              value={form.ipHostname || ""}
              onChange={(e) => update("ipHostname", e.target.value)}
            />
          </label>
          <label>
            Capacidade
            <input
              value={form.capacity || ""}
              onChange={(e) => update("capacity", e.target.value)}
            />
          </label>
          <label>
            Identificação do ambiente
            <input
              value={form.environment || ""}
              onChange={(e) => update("environment", e.target.value)}
            />
          </label>
          <label>
            Data de instalação
            <input
              type="date"
              value={form.installationDate || ""}
              onChange={(e) => update("installationDate", e.target.value)}
            />
          </label>
          <label>
            Garantia até
            <input
              type="date"
              value={form.warrantyUntil || ""}
              onChange={(e) => update("warrantyUntil", e.target.value)}
            />
          </label>
          <label>
            Criticidade
            <select
              value={form.criticality}
              onChange={(e) => update("criticality", e.target.value)}
            >
              <option>Baixa</option>
              <option>Média</option>
              <option>Alta</option>
              <option>Crítica</option>
            </select>
          </label>
          <label>
            Status
            <select
              value={form.status}
              onChange={(e) => update("status", e.target.value)}
            >
              <option>Ativo</option>
              <option>Inativo</option>
              <option>Em manutenção</option>
              <option>Descartado</option>
            </select>
          </label>
          <label className="wide">
            Informações adicionais
            <textarea
              value={form.details || ""}
              onChange={(e) => update("details", e.target.value)}
            />
          </label>
            </div>
          </details>
        </div>
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving}>
            {saving
              ? "Salvando…"
              : record
                ? "Salvar alterações"
                : "Salvar equipamento"}
          </button>
        </div>
      </form>
    </div>
  );
}

function RecurringScheduleModal({
  groups,
  occupiedVisits = [],
  holidayDates = [],
  residentTechnicians = [],
  users,
  token,
  client,
  unit,
  onClose,
  onSaved,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const nextYear = new Date();
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  const groupForm = (group) => {
    let days = [2, 3];
    try {
      days = Array.isArray(group?.preferredWeekdays)
        ? group.preferredWeekdays
        : JSON.parse(group?.preferredWeekdays || "[2,3]");
    } catch {}
    const contractType = group?.contractType || "scheduled_visits";
    const assignedTo = contractType === "resident"
      ? residentTechnicians.find((person) => person.name === group?.assignedTo)?.name || residentTechnicians[0]?.name || ""
      : group?.assignedTo || "";
    return {
      groupId: String(group?.id || ""),
      name: group?.name || `Grupo ${group?.groupNumber || ""}`,
      contractType,
      startDate: group?.startDate || today,
      endDate: group?.endDate || nextYear.toISOString().slice(0, 10),
      monthInterval: Number(group?.frequencyInterval) || 1,
      visitsPerCycle: Number(group?.visitsPerCycle) || 1,
      preferredWeekdays: days.map(Number).filter((day) => day >= 2 && day <= 5),
      assignedTo,
    };
  };
  const [form, setForm] = useState(() => groupForm(groups.find(group => !group.activePlanId) || groups[0]));
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [completedGroupIds, setCompletedGroupIds] = useState([]);
  const [selectedCancellationIds, setSelectedCancellationIds] = useState([]);
  const [cancelledGroupIds, setCancelledGroupIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const update = (field, value) => {
    setSuccess("");
    setForm((current) => ({ ...current, [field]: value }));
  };
  const selectedGroup = groups.find((item) => String(item.id) === form.groupId);
  const weekdays = [
    [2, "Terça"],
    [3, "Quarta"],
    [4, "Quinta"],
    [5, "Sexta"],
  ];
  const toggleDay = (day) =>
    update(
      "preferredWeekdays",
      form.preferredWeekdays.includes(day)
        ? form.preferredWeekdays.filter((item) => item !== day)
        : [...form.preferredWeekdays, day].sort(),
    );
  const chooseGroup = (value) => {
    const selected = groups.find((item) => String(item.id) === value);
    setForm(groupForm(selected));
    setSelectedGroupIds([]);
    setError("");
    setSuccess("");
  };
  const availableGroups = groups.filter((item) => !item.activePlanId && !completedGroupIds.includes(String(item.id)));
  const scheduledGroups = groups.filter((item) => item.activePlanId && !cancelledGroupIds.includes(String(item.id)));
  const toggleGroup = (id) => {
    setSelectedGroupIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
    setSuccess("");
  };
  const chooseContractType = (contractType) => {
    setSuccess("");
    setForm((current) => ({
      ...current,
      contractType,
      assignedTo: contractType === "resident"
        ? residentTechnicians.find((person) => person.name === current.assignedTo)?.name || residentTechnicians[0]?.name || ""
        : "",
    }));
  };
  const preview = useMemo(() => {
    if (!form.startDate || !form.endDate) return [];
    const result = [];
    const start = new Date(`${form.startDate}T12:00:00Z`);
    const end = new Date(`${form.endDate}T12:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
    const allowed =
      form.contractType === "resident"
        ? [2, 3, 4, 5]
        : form.preferredWeekdays.filter((day) => day >= 2 && day <= 5);
    const occupied = new Set(
      occupiedVisits
        .filter((item) => String(item.groupId) !== form.groupId &&
          (Number(item.unitId) === Number(unit.id) ||
            (form.contractType === "scheduled_visits" && item.contractType === "scheduled_visits")))
        .map((item) => item.plannedDate),
    );
    const addMonthsFrom = (date, months) => {
      const month = date.getUTCMonth() + months;
      const lastDay = new Date(Date.UTC(date.getUTCFullYear(), month + 1, 0, 12)).getUTCDate();
      return new Date(Date.UTC(date.getUTCFullYear(), month, Math.min(date.getUTCDate(), lastDay), 12));
    };
    const addMonths = (months) => addMonthsFrom(start, months);
    const eligible = [];
    for (const cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
      const value = cursor.toISOString().slice(0, 10);
      if (allowed.includes(cursor.getUTCDay()) && !holidayDates.includes(value)) eligible.push(new Date(cursor));
    }
    const roundSize = Math.max(1, groups.filter((item) => item.active !== 0 && Number(item.equipmentCount) > 0).length) * Math.max(1, Number(form.visitsPerCycle));
    const boundary = (index) => {
      if (index === 0) return start;
      const interval = Number(form.monthInterval);
      const calendarStart = addMonths(Math.max(0, index * interval - (interval > 1 ? 1 : 0)));
      const capacityStart = eligible[index * roundSize] || new Date(end.getTime() + 86400000);
      return new Date(Math.max(calendarStart.getTime(), capacityStart.getTime()));
    };
    let previousCycleLastDate = null;
    for (let cycleIndex = 0; result.length < 8; cycleIndex++) {
      const interval = Number(form.monthInterval);
      const nominalStart = addMonths(cycleIndex * interval);
      if (nominalStart > end || (cycleIndex > 0 && nominalStart.getTime() === end.getTime())) break;
      const cycleStart = boundary(cycleIndex);
      const nextCycleStart = boundary(cycleIndex + 1);
      const cycleEnd = new Date(Math.min(end.getTime(), nextCycleStart.getTime() - 86400000));
      let remaining = Number(form.visitsPerCycle);
      const earliestRenewal = previousCycleLastDate ? addMonthsFrom(previousCycleLastDate, Math.max(1, interval - 1)) : cycleStart;
      const cursor = new Date(Math.max(cycleStart.getTime(), earliestRenewal.getTime()));
      while (cursor <= cycleEnd && remaining > 0 && result.length < 8) {
        const value = cursor.toISOString().slice(0, 10);
        if (allowed.includes(cursor.getUTCDay()) && !occupied.has(value) && !holidayDates.includes(value)) {
          result.push(value);
          occupied.add(value);
          remaining--;
          if (remaining === 0) previousCycleLastDate = new Date(cursor);
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    }
    return result.slice(0, 8);
  }, [form, groups, occupiedVisits, holidayDates, unit.id]);
  const scheduleError = (result) => {
    if (result.error === "schedule_has_insufficient_dates" || result.error === "schedule_has_no_available_dates")
      return `Há ${result.available || 0} data(s) livre(s) para ${result.expected || "as"} visita(s) previstas. Amplie o período, reduza a frequência ou escolha mais dias preferenciais.`;
    if (result.error === "resident_technician_required") return "Vincule um técnico residente a esta unidade antes de programar visitas residentes.";
    if (result.error === "resident_technician_unit_mismatch") return "O responsável selecionado não é residente desta unidade. Atualize a seleção.";
    if (result.error === "checklist_required") return "Cadastre um roteiro de inspeção ativo antes de programar visitas.";
    if (result.error === "invalid_group_schedule") return "Confira as datas de início e fim da programação.";
    return "Não foi possível gerar a programação. Confira os dados e tente novamente.";
  };
  const save = async (keepOpen = false) => {
    const batchGroups = selectedGroupIds.length
      ? groups.filter((item) => selectedGroupIds.includes(String(item.id)) && !item.activePlanId && !completedGroupIds.includes(String(item.id)))
      : [];
    if (
      (!form.groupId && !batchGroups.length) ||
      !form.name ||
      !form.startDate ||
      !form.endDate ||
      (form.contractType === "resident" && !residentTechnicians.length) ||
      (!form.preferredWeekdays.length && form.contractType !== "resident")
    ) {
      setError(form.contractType === "resident" && !residentTechnicians.length
        ? "Vincule um técnico residente a esta unidade antes de programar visitas residentes."
        : "Preencha o grupo, período e ao menos um dia preferencial.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    const completed = [];
    try {
      if (batchGroups.length) {
        let generated = 0;
        for (const group of batchGroups) {
          const response = await fetch(`${apiBase}/maintenance/inventory/groups/${group.id}/schedule`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ ...form, groupId: String(group.id), name: group.name || `Grupo ${group.groupNumber}` }),
          });
          if (!response.ok) {
            const result = await response.json().catch(() => ({}));
            throw new Error(`${completed.length} grupo(s) programado(s). Grupo ${group.groupNumber}: ${scheduleError(result)}`);
          }
          const result = await response.json();
          generated += result.generated;
          completed.push(String(group.id));
        }
        await onSaved({ generated }, keepOpen);
        setCompletedGroupIds((current) => [...current, ...completed]);
        setSelectedGroupIds([]);
        if (keepOpen) {
          setSuccess(`${completed.length} grupo(s) e ${generated} visita(s) programados. As datas foram reservadas separadamente para cada grupo.`);
          setSaving(false);
        }
        return;
      }
      const response = await fetch(
        `${apiBase}/maintenance/inventory/groups/${form.groupId}/schedule`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        },
      );
      const result = await response.json();
      if (!response.ok) throw new Error(scheduleError(result));
      await onSaved(result, keepOpen);
      if (keepOpen) {
        setSuccess(`${result.generated} visita(s) programada(s) para ${selectedGroup?.name || `Grupo ${selectedGroup?.groupNumber}`}. Você pode escolher outro grupo.`);
        setSaving(false);
      }
    } catch (error) {
      if (completed.length) {
        setCompletedGroupIds((current) => [...current, ...completed]);
        setSelectedGroupIds((current) => current.filter((id) => !completed.includes(id)));
        await onSaved({ generated: 0 }, true);
      }
      setError(error.message);
      setSaving(false);
    }
  };
  const removeSchedules = async (ids = selectedCancellationIds) => {
    const targets = scheduledGroups.filter((item) => ids.includes(String(item.id)));
    if (!targets.length) return;
    const futureCount = targets.reduce((total, item) => total + Number(item.futureActivityCount || 0), 0);
    if (!window.confirm(`Cancelar a programação de ${targets.length} grupo(s) e remover ${futureCount} visita(s) futuras? As visitas já realizadas serão preservadas.`)) return;
    setSaving(true);
    setError("");
    setSuccess("");
    const completed = [];
    let deleted = 0;
    try {
      for (const group of targets) {
        const response = await fetch(
          `${apiBase}/maintenance/inventory/groups/${group.id}/schedule`,
          { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) throw new Error(`Falha ao cancelar o Grupo ${group.groupNumber}.`);
        const result = await response.json();
        deleted += Number(result.deleted || 0);
        completed.push(String(group.id));
      }
      setCancelledGroupIds((current) => [...current, ...completed]);
      setCompletedGroupIds((current) => current.filter((id) => !completed.includes(id)));
      setSelectedCancellationIds([]);
      await onSaved({ deleted }, true);
      setSuccess(`${completed.length} programação(ões) cancelada(s). ${deleted} visita(s) futuras removida(s); histórico preservado.`);
    } catch (cause) {
      if (completed.length) {
        setCancelledGroupIds((current) => [...current, ...completed]);
        setCompletedGroupIds((current) => current.filter((id) => !completed.includes(id)));
        setSelectedCancellationIds((current) => current.filter((id) => !completed.includes(id)));
        await onSaved({ deleted }, true);
      }
      setError(`${completed.length} grupo(s) cancelado(s). ${cause.message || "Não foi possível cancelar as programações."}`);
    } finally {
      setSaving(false);
    }
  };
  if (!groups.length)
    return (
      <div className="modal-backdrop">
        <div className="modal schedule-group-modal">
          <div className="modal-head">
            <div>
              <h2>Programar visitas preventivas</h2>
              <p>
                {client.name} • {unit.name}
              </p>
            </div>
            <button onClick={onClose}>×</button>
          </div>
          <div className="inventory-empty">
            Cadastre ao menos um equipamento nesta unidade. O grupo será criado automaticamente ao salvar.
          </div>
          <div className="modal-actions">
            <button onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    );
  const cycleText =
    Number(form.monthInterval) === 1
      ? "por mês"
      : `a cada ${form.monthInterval} meses`;
  return (
    <div className="modal-backdrop">
      <div className="modal schedule-group-modal">
        <div className="modal-head">
          <div>
            <h2>Programar visitas preventivas</h2>
            <p>
              {client.name} • {unit.name} · Escolha o grupo e as datas
            </p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <div className="schedule-group-body">
          <section className="schedule-group-main">
            <div className="schedule-simple-heading"><b>1. O que será atendido?</b><span>Equipamentos do mesmo grupo entram juntos em cada visita.</span></div>
            <div className="schedule-form-grid">
              <label>
                Grupo de equipamentos*
                <select
                  value={form.groupId}
                  onChange={(e) => chooseGroup(e.target.value)}
                >
                  {groups.map((item) => (
                    <option key={item.id} value={item.id}>
                      Grupo {item.groupNumber} — {item.name || "Sem nome"} (
                      {item.equipmentCount} itens)
                      {item.activePlanId ? " • programado" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Primeira data possível*
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update("startDate", e.target.value)}
                />
              </label>
              <label>
                Programar até*
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => update("endDate", e.target.value)}
                />
              </label>
            </div>
            <div className="schedule-group-selection">
              <div className="schedule-group-selection-head">
                <b>Programar vários grupos com estas características</b>
                <button type="button" disabled={!availableGroups.length || saving} onClick={() => {
                  setSelectedGroupIds(availableGroups.length === selectedGroupIds.length ? [] : availableGroups.map((item) => String(item.id)));
                  setSuccess("");
                }}>{availableGroups.length === selectedGroupIds.length && availableGroups.length ? "Limpar seleção" : "Selecionar todos"}</button>
              </div>
              <div className="schedule-group-selection-list">
                {groups.map((item) => <label key={item.id} className={item.activePlanId || completedGroupIds.includes(String(item.id)) ? "is-programmed" : ""}>
                  <input type="checkbox" disabled={!!item.activePlanId || completedGroupIds.includes(String(item.id)) || saving} checked={selectedGroupIds.includes(String(item.id))} onChange={() => toggleGroup(String(item.id))} />
                  <span>Grupo {item.groupNumber} · {item.name || "Sem nome"} ({item.equipmentCount} itens){item.activePlanId || completedGroupIds.includes(String(item.id)) ? " · programado" : ""}</span>
                </label>)}
              </div>
              <small>{selectedGroupIds.length ? `${selectedGroupIds.length} grupo(s) selecionado(s). Cada grupo receberá datas distintas na mesma unidade.` : "Selecione grupos sem programação ativa ou use o seletor acima para editar um grupo."}</small>
            </div>
            <div className="schedule-simple-heading"><b>2. Como serão as visitas?</b><span>As datas aparecem na prévia antes de salvar.</span></div>
            <h3>Modalidade de atendimento</h3>
            <div className="contract-type-cards">
              <button
                className={form.contractType === "resident" ? "active" : ""}
                onClick={() => chooseContractType("resident")}
              >
                <Icon name="users" size={18} />
                <div>
                  <b>Técnico / OS residente</b>
                  <span>Datas flexíveis entre segunda e sexta-feira.</span>
                </div>
              </button>
              <button
                className={
                  form.contractType === "scheduled_visits" ? "active" : ""
                }
                onClick={() => chooseContractType("scheduled_visits")}
              >
                <Icon name="calendar" size={18} />
                <div>
                  <b>Visitas programadas</b>
                  <span>
                    Visitas em dias preferenciais definidos no contrato.
                  </span>
                </div>
              </button>
            </div>
            <div className="recurrence-grid">
              <label>
                Recorrência
                <select
                  value={form.monthInterval}
                  onChange={(e) =>
                    update("monthInterval", Number(e.target.value))
                  }
                >
                  <option value="1">Todo mês</option>
                  <option value="2">A cada 2 meses</option>
                  <option value="3">A cada 3 meses</option>
                  <option value="4">A cada 4 meses</option>
                  <option value="5">A cada 5 meses</option>
                  <option value="6">A cada 6 meses</option>
                </select>
              </label>
              <label>
                Visitas por ciclo
                <select
                  value={form.visitsPerCycle}
                  onChange={(e) =>
                    update("visitsPerCycle", Number(e.target.value))
                  }
                >
                  <option value="1">1 visita</option>
                  <option value="2">2 visitas</option>
                  <option value="3">3 visitas</option>
                  <option value="4">4 visitas</option>
                </select>
              </label>
            </div>
            {form.contractType === "resident" ? (
              <div className="resident-rule">
                <Icon name="calendar" size={18} />
                <div>
                  <b>Agendamento flexível</b>
                  <span>
                    Em cada ciclo, as datas serão distribuídas automaticamente
                    de terça a sexta-feira, sem feriados.
                  </span>
                </div>
              </div>
            ) : (
              <>
                <h3>Dias preferenciais</h3>
                <div className="weekday-buttons">
                  {weekdays.map(([value, label]) => (
                    <button
                      key={value}
                      className={
                        form.preferredWeekdays.includes(value) ? "active" : ""
                      }
                      onClick={() => toggleDay(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="schedule-help">
                  O sistema verifica todas as visitas já agendadas e escolhe
                  automaticamente outra data disponível, evitando duas equipes
                  comprometidas no mesmo dia.
                </p>
              </>
            )}
            <details className="schedule-advanced-settings">
              <summary>Nome e responsável da programação <Icon name="chevron" size={15} /></summary>
              <div className="recurrence-grid">
                <label>Nome da programação<input value={form.name} onChange={(e) => update("name", e.target.value)} /></label>
                {form.contractType === "resident" ? (
                  <label>Responsável residente
                    {residentTechnicians.length === 1 ? (
                      <input value={residentTechnicians[0].name} readOnly />
                    ) : (
                      <select value={form.assignedTo} onChange={(e) => update("assignedTo", e.target.value)} disabled={!residentTechnicians.length}>
                        {!residentTechnicians.length && <option value="">Nenhum residente vinculado</option>}
                        {residentTechnicians.map((person) => <option key={person.id} value={person.name}>{person.name}</option>)}
                      </select>
                    )}
                    {!residentTechnicians.length && <small>Cadastre o residente em Clientes → Unidades.</small>}
                  </label>
                ) : (
                  <label>Responsável<select value={form.assignedTo} onChange={(e) => update("assignedTo", e.target.value)}><option value="">Definir depois</option>{users.map(user => <option key={user.id} value={user.name}>{user.name}</option>)}</select></label>
                )}
              </div>
            </details>
            {scheduledGroups.length > 0 && (
              <details className="schedule-cancel-section">
                <summary>Cancelar agendamentos realizados ({scheduledGroups.length}) <Icon name="chevron" size={15} /></summary>
                <p>Selecione as programações a cancelar. Visitas já realizadas permanecem no histórico.</p>
                <div className="schedule-group-selection-head">
                  <b>{selectedCancellationIds.length} grupo(s) selecionado(s)</b>
                  <button type="button" disabled={saving} onClick={() => setSelectedCancellationIds(
                    selectedCancellationIds.length === scheduledGroups.length
                      ? []
                      : scheduledGroups.map((item) => String(item.id)),
                  )}>{selectedCancellationIds.length === scheduledGroups.length ? "Limpar seleção" : "Selecionar todos"}</button>
                </div>
                <div className="schedule-group-selection-list">
                  {scheduledGroups.map((item) => (
                    <label key={item.id}>
                      <input type="checkbox" disabled={saving} checked={selectedCancellationIds.includes(String(item.id))} onChange={() => {
                        setSelectedCancellationIds((current) => current.includes(String(item.id))
                          ? current.filter((id) => id !== String(item.id))
                          : [...current, String(item.id)]);
                        setError("");
                      }} />
                      <span>Grupo {item.groupNumber} · {item.futureActivityCount || 0} visita(s) futuras</span>
                    </label>
                  ))}
                </div>
                <button type="button" className="danger-button" disabled={saving || !selectedCancellationIds.length} onClick={() => removeSchedules()}>
                  <Icon name="trash" size={15} /> Cancelar selecionados
                </button>
              </details>
            )}
          </section>
          <aside className="schedule-preview">
            <span>PRÉVIA DAS PRÓXIMAS DATAS</span>
            <h3>{selectedGroupIds.length ? `${selectedGroupIds.length} grupos selecionados` : selectedGroup?.name || `Grupo ${selectedGroup?.groupNumber}`}</h3>
            <p>{selectedGroupIds.length ? "Datas ilustrativas; cada grupo terá dias distintos ao salvar." : `${selectedGroup?.equipmentCount || 0} equipamento(s) neste grupo`}</p>
            <p>
              {form.visitsPerCycle} visita(s) {cycleText}
              {form.contractType === "resident"
                ? " • dias úteis"
                : " • dias preferenciais"}
            </p>
            <div>
              {preview.map((date, index) => (
                <div key={date}>
                  <b>{index + 1}</b>
                  <span>{formatDate(date)}</span>
                </div>
              ))}
              {!preview.length && (
                <small>Selecione um período e dias válidos.</small>
              )}
            </div>
            {preview.length > 7 && (
              <small>Exibindo as primeiras datas da programação.</small>
            )}
          </aside>
        </div>
        {error && <div className="login-error" role="alert">{error}</div>}
        {success && <div className="schedule-apply-success" role="status">{success}</div>}
        <div className="modal-actions schedule-management-actions">
          <div>
            {!selectedGroupIds.length && selectedGroup?.activePlanId && (
              <button
                className="danger-button"
                disabled={saving}
                onClick={() => removeSchedules([String(selectedGroup.id)])}
              >
                <Icon name="trash" size={15} /> Cancelar programação
              </button>
            )}
            <span>
              {selectedGroupIds.length ? `${selectedGroupIds.length} grupo(s) selecionado(s)` : selectedGroup?.activePlanId
                ? `${selectedGroup.futureActivityCount || 0} atividade(s) futura(s) nesta programação`
                : "Este grupo ainda não possui programação ativa"}
            </span>
          </div>
          <button onClick={onClose}>Fechar</button>
          <button disabled={saving} onClick={() => save(true)}>Aplicar e continuar</button>
          <button className="primary" disabled={saving} onClick={() => save(false)}>
            {saving
              ? "Salvando…"
              : selectedGroup?.activePlanId
                ? "Salvar alterações"
                : "Confirmar e criar agenda"}
          </button>
        </div>
      </div>
    </div>
  );
}

const imageAsDataUrl = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext("2d").drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = reject;
    image.src = src;
  });
async function addAboutsecPdfLogo(
  pdf,
  x,
  y,
  maxWidth,
  maxHeight,
  variant = "white",
) {
  try {
    const source =
      variant === "dark"
        ? "/aboutsec-logo-dark.png"
        : "/aboutsec-logo-white.png";
    const logo = await imageAsDataUrl(source);
    const properties = pdf.getImageProperties(logo);
    const ratio = properties.width / properties.height;
    let width = maxWidth,
      height = width / ratio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }
    pdf.addImage(
      logo,
      "PNG",
      x + (maxWidth - width) / 2,
      y + (maxHeight - height) / 2,
      width,
      height,
      undefined,
      "SLOW",
    );
  } catch {}
}

async function addAboutsecPdfFooters(pdf, firstPageDark = false) {
  const total = pdf.getNumberOfPages();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  for (let page = 1; page <= total; page++) {
    pdf.setPage(page);
    const dark = firstPageDark && page === 1;
    if (dark)
      await addAboutsecPdfLogo(
        pdf,
        pageWidth - 31,
        pageHeight - 27,
        18,
        20,
        "white",
      );
    else
      await addAboutsecPdfLogo(
        pdf,
        pageWidth - 44,
        pageHeight - 15,
        30,
        9,
        "dark",
      );
  }
}

function ReportBrandLogos() {
  return (
    <span className="report-brand-lockup">
      <img src="/aboutplan-logo-white-hq.png" alt="AboutPlan" />
    </span>
  );
}

function QrLabelGenerator({ equipment, client, unit, onClose }) {
  const [step, setStep] = useState(1);
  const [scope, setScope] = useState("all");
  const [itemId, setItemId] = useState(String(equipment[0]?.id || ""));
  const [group, setGroup] = useState(String(equipment[0]?.groupNumber || ""));
  const [groups, setGroups] = useState([]);
  const [size, setSize] = useState(3);
  const [logo, setLogo] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const groupOptions = [
    ...new Set(
      equipment.map((item) => String(item.groupNumber)).filter(Boolean),
    ),
  ].sort((a, b) => Number(a) - Number(b));
  const selected =
    scope === "item"
      ? equipment.filter((item) => String(item.id) === itemId)
      : scope === "group"
        ? equipment.filter((item) => String(item.groupNumber) === group)
        : scope === "groups"
          ? equipment.filter((item) =>
              groups.includes(String(item.groupNumber)),
            )
          : equipment;
  const uploadLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };
  const toggleGroup = (value) =>
    setGroups((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  const generate = async () => {
    if (!selected.length) {
      setError("Selecione ao menos um equipamento.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const logoData =
        logo || (await imageAsDataUrl("/aboutplan-horizontal-transparent.png"));
      const qrMm = Number(size) * 10;
      const labelWidth = qrMm + 8;
      const labelHeight = qrMm + 17;
      const margin = 8;
      const usableWidth = 210 - margin * 2;
      const usableHeight = 297 - margin * 2;
      const columns = Math.max(1, Math.floor(usableWidth / labelWidth));
      const rows = Math.max(1, Math.floor(usableHeight / labelHeight));
      const perPage = columns * rows;
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      for (let index = 0; index < selected.length; index++) {
        if (index > 0 && index % perPage === 0) pdf.addPage();
        const pageIndex = index % perPage;
        const row = Math.floor(pageIndex / columns);
        const column = pageIndex % columns;
        const x = margin + column * labelWidth;
        const y = margin + row * labelHeight;
        pdf.setDrawColor(150);
        pdf.setLineWidth(0.15);
        pdf.setLineDashPattern([1, 1], 0);
        pdf.rect(x, y, labelWidth, labelHeight);
        pdf.setLineDashPattern([], 0);
        const logoProperties = pdf.getImageProperties(logoData);
        const logoMaxWidth = qrMm;
        const logoMaxHeight = 6;
        const logoRatio = logoProperties.width / logoProperties.height;
        let logoWidth = Math.min(logoMaxWidth, logoMaxHeight * logoRatio);
        let logoHeight = logoWidth / logoRatio;
        if (logoHeight > logoMaxHeight) {
          logoHeight = logoMaxHeight;
          logoWidth = logoHeight * logoRatio;
        }
        pdf.addImage(
          logoData,
          "PNG",
          x + (labelWidth - logoWidth) / 2,
          y + 1,
          logoWidth,
          logoHeight,
          undefined,
          "FAST",
        );
        const target = `${window.location.origin}/equipment/${encodeURIComponent(selected[index].tag)}`;
        const qrData = await QRCode.toDataURL(target, {
          errorCorrectionLevel: "H",
          width: 1200,
          margin: 1,
          color: { dark: "#000000", light: "#FFFFFF" },
        });
        pdf.addImage(
          qrData,
          "PNG",
          x + 4,
          y + 8,
          qrMm,
          qrMm,
          undefined,
          "FAST",
        );
        pdf.setTextColor(15, 24, 42);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(size === 1 ? 4.5 : size === 2 ? 6 : 7.5);
        const tag = selected[index].tag;
        const maxTextWidth = labelWidth - 3;
        const tagWidth = pdf.getTextWidth(tag);
        if (tagWidth > maxTextWidth)
          pdf.setFontSize(
            Math.max(4, (size === 1 ? 4.5 : 7.5) * (maxTextWidth / tagWidth)),
          );
        pdf.text(tag, x + labelWidth / 2, y + qrMm + 13, { align: "center" });
      }
      pdf.save(`Etiquetas_QR_${client.name}_${unit.name}.pdf`);
    } catch {
      setError("Não foi possível gerar o PDF. Confira o arquivo de logo.");
    } finally {
      setGenerating(false);
    }
  };
  const next = () => {
    if (step === 1 && !selected.length) {
      setError("Selecione ao menos um equipamento.");
      return;
    }
    setError("");
    setStep((current) => Math.min(3, current + 1));
  };
  return (
    <div className="modal-backdrop">
      <div className="modal qr-label-modal qr-wizard">
        <div className="modal-head">
          <div>
            <h2>Etiquetas QR Code</h2>
            <p>
              {client.name} • {unit.name} • PDF A4 para gráfica
            </p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <div className="qr-stepper">
          {[
            ["1", "Equipamentos"],
            ["2", "Formato"],
            ["3", "Revisar e gerar"],
          ].map(([number, label]) => (
            <button
              key={number}
              className={
                step === Number(number)
                  ? "active"
                  : step > Number(number)
                    ? "done"
                    : ""
              }
              onClick={() => Number(number) < step && setStep(Number(number))}
            >
              <b>{step > Number(number) ? "✓" : number}</b>
              <span>{label}</span>
            </button>
          ))}
        </div>
        <div className="qr-wizard-body">
          {step === 1 && (
            <section className="qr-step-panel">
              <div className="qr-step-title">
                <div>
                  <span>ETAPA 1 DE 3</span>
                  <h3>Quais etiquetas deseja gerar?</h3>
                  <p>
                    Selecione um item, um ou mais grupos, ou todo o inventário.
                  </p>
                </div>
                <strong>{selected.length} selecionada(s)</strong>
              </div>
              <div className="qr-scope-cards">
                {[
                  [
                    "all",
                    "Todos os equipamentos",
                    "Gerar uma etiqueta para cada item do inventário",
                  ],
                  [
                    "item",
                    "Um item",
                    "Escolher apenas um equipamento pela TAG",
                  ],
                  ["group", "Um grupo", "Todos os itens de um único grupo"],
                  ["groups", "Vários grupos", "Combinar dois ou mais grupos"],
                ].map(([value, label, description]) => (
                  <button
                    key={value}
                    className={scope === value ? "active" : ""}
                    onClick={() => setScope(value)}
                  >
                    <i>{scope === value ? "✓" : ""}</i>
                    <div>
                      <b>{label}</b>
                      <span>{description}</span>
                    </div>
                  </button>
                ))}
              </div>
              {scope === "item" && (
                <label className="qr-select">
                  Equipamento
                  <select
                    value={itemId}
                    onChange={(e) => setItemId(e.target.value)}
                  >
                    {equipment.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.tag} — {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {scope === "group" && (
                <label className="qr-select">
                  Grupo
                  <select
                    value={group}
                    onChange={(e) => setGroup(e.target.value)}
                  >
                    {groupOptions.map((value) => (
                      <option key={value} value={value}>
                        Grupo {value}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {scope === "groups" && (
                <div className="qr-group-pills">
                  {groupOptions.map((value) => (
                    <button
                      key={value}
                      className={groups.includes(value) ? "active" : ""}
                      onClick={() => toggleGroup(value)}
                    >
                      Grupo {value}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}
          {step === 2 && (
            <section className="qr-step-panel">
              <div className="qr-step-title">
                <div>
                  <span>ETAPA 2 DE 3</span>
                  <h3>Escolha o tamanho do QR Code</h3>
                  <p>A medida corresponde ao QR impresso em tamanho real.</p>
                </div>
              </div>
              <div className="qr-size-cards">
                {[1, 2, 3, 4].map((value) => (
                  <button
                    key={value}
                    className={size === value ? "active" : ""}
                    onClick={() => setSize(value)}
                  >
                    <i
                      style={{ width: 20 + value * 9, height: 20 + value * 9 }}
                    ></i>
                    <div>
                      <b>
                        {value} × {value} cm
                      </b>
                      <span>
                        {value === 1
                          ? "Compacto"
                          : value === 2
                            ? "Pequeno"
                            : value === 3
                              ? "Padrão recomendado"
                              : "Grande e fácil leitura"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="qr-format-note">
                A etiqueta completa será um pouco maior para acomodar o logo, a
                TAG e as marcações de corte.
              </div>
            </section>
          )}
          {step === 3 && (
            <section className="qr-step-panel">
              <div className="qr-step-title">
                <div>
                  <span>ETAPA 3 DE 3</span>
                  <h3>Logo e confirmação</h3>
                  <p>
                    Revise as informações antes de criar o arquivo para
                    impressão.
                  </p>
                </div>
              </div>
              <div className="qr-review-grid">
                <div className="qr-logo-card">
                  <span>Logo da etiqueta</span>
                  <div>
                    {logo ? (
                      <img src={logo} alt="Logo selecionado" />
                    ) : (
                      <img
                        src="/aboutplan-horizontal-transparent.png"
                        alt="Logo AboutPlan"
                      />
                    )}
                  </div>
                  <label>
                    Trocar logo
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={uploadLogo}
                    />
                  </label>
                  <small>PNG transparente oferece melhor resultado.</small>
                </div>
                <div className="qr-review-summary">
                  <div>
                    <span>Etiquetas</span>
                    <b>{selected.length}</b>
                  </div>
                  <div>
                    <span>QR Code</span>
                    <b>
                      {size} × {size} cm
                    </b>
                  </div>
                  <div>
                    <span>Papel</span>
                    <b>A4</b>
                  </div>
                  <div>
                    <span>Acabamento</span>
                    <b>Marcação de corte</b>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
        {error && <div className="login-error qr-wizard-error">{error}</div>}
        <div className="modal-actions qr-wizard-actions">
          <button
            onClick={
              step === 1 ? onClose : () => setStep((current) => current - 1)
            }
          >
            {step === 1 ? "Cancelar" : "Voltar"}
          </button>
          {step < 3 ? (
            <button className="primary" onClick={next}>
              Continuar
            </button>
          ) : (
            <button
              className="primary"
              disabled={generating || !selected.length}
              onClick={generate}
            >
              {generating ? "Gerando PDF…" : "Gerar PDF A4"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MaintenancePageLegacy({ token, query }) {
  const [activities, setActivities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState("Todos");
  const [clientFilter, setClientFilter] = useState("Todos");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [showPlan, setShowPlan] = useState(false);
  const load = () => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${apiBase}/maintenance/activities`, { headers }),
      fetch(`${apiBase}/maintenance/plans`, { headers }),
    ])
      .then(async ([activityResponse, planResponse]) => {
        if (!activityResponse.ok || !planResponse.ok) throw new Error();
        setActivities(await activityResponse.json());
        setPlans(await planResponse.json());
      })
      .catch(() => setError("Não foi possível carregar as manutenções."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, [token]);
  const maintenanceClients = [
    ...new Set(
      activities.map((activity) => activity.clientName).filter(Boolean),
    ),
  ].sort();
  const visible = activities.filter(
    (activity) =>
      `${activity.planName} ${activity.clientName} ${activity.assetName}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (filter === "Todos" || maintenanceGroup(activity) === filter) &&
      (clientFilter === "Todos" || activity.clientName === clientFilter),
  );
  const count = (group) =>
    activities.filter(
      (activity) =>
        maintenanceGroup(activity) === group &&
        (clientFilter === "Todos" || activity.clientName === clientFilter),
    ).length;
  return (
    <>
      <PageHeading
        title="Manutenções"
        subtitle="Preventivas recorrentes, checklists e corretivas originadas por inconformidades."
        button="Novo plano"
        onCreate={() => setShowPlan(true)}
      />
      <div className="module-filter">
        <div>
          <Icon name="building" size={16} />
          <span>Cliente</span>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option>Todos</option>
            {maintenanceClients.map((client) => (
              <option key={client}>{client}</option>
            ))}
          </select>
        </div>
        <small>
          Exibindo {visible.length} de {activities.length} atividades
        </small>
      </div>
      <div className="maintenance-summary">
        <SummaryBox
          tone="blue"
          label="Programadas"
          value={count("Programadas")}
          detail={`${plans.length} plano(s) ativo(s)`}
        />
        <SummaryBox
          tone="amber"
          label="Próximas do prazo"
          value={count("Próximas")}
          detail="Nos próximos 7 dias"
        />
        <SummaryBox
          tone="red"
          label="Atrasadas"
          value={count("Atrasadas")}
          detail="Precisam de atenção"
        />
        <SummaryBox
          tone="green"
          label="Realizadas"
          value={count("Realizadas")}
          detail="No período consultado"
        />
      </div>
      <section className="panel maintenance-panel">
        <div className="maintenance-toolbar">
          <div>
            {[
              "Todos",
              "Programadas",
              "Próximas",
              "Atrasadas",
              "Realizadas",
            ].map((item) => (
              <button
                key={item}
                className={filter === item ? "selected" : ""}
                onClick={() => setFilter(item)}
              >
                {item}
                {item !== "Todos" && <b>{count(item)}</b>}
              </button>
            ))}
          </div>
          <button
            className="secondary-button"
            onClick={() => setView(view === "list" ? "calendar" : "list")}
          >
            <Icon name={view === "list" ? "calendar" : "clipboard"} size={15} />{" "}
            {view === "list" ? "Calendário" : "Lista"}
          </button>
        </div>
        {loading && <div className="empty">Carregando manutenções…</div>}
        {error && <div className="empty error-text">{error}</div>}
        {!loading &&
          !error &&
          (view === "calendar" ? (
            <MaintenanceCalendar activities={visible} onOpen={setSelected} />
          ) : (
            <>
              <div className="maintenance-head">
                <span>Manutenção</span>
                <span>Equipamento</span>
                <span>Data prevista</span>
                <span>Responsável</span>
                <span>Situação</span>
                <span></span>
              </div>
              {visible.map((activity) => (
                <MaintenanceRow
                  key={activity.id}
                  activity={activity}
                  onOpen={setSelected}
                />
              ))}
              {!visible.length && (
                <div className="empty">
                  Nenhuma manutenção encontrada para o cliente selecionado.
                </div>
              )}
            </>
          ))}
      </section>
      <section className="panel maintenance-plans">
        <PanelTitle
          title="Planos ativos"
          subtitle="Recorrências que geram as atividades preventivas"
          action="Gerenciar planos"
        />
        <div className="plan-grid">
          {plans
            .filter(
              (plan) =>
                clientFilter === "Todos" || plan.clientName === clientFilter,
            )
            .map((plan) => (
              <div className="plan-item" key={plan.id}>
                <div className="metric-icon purple">
                  <Icon name="wrench" />
                </div>
                <div>
                  <strong>{plan.name}</strong>
                  <p>
                    {plan.clientName} • {plan.assetName}
                  </p>
                  <span>
                    {frequencyLabel(plan)} • Próxima:{" "}
                    {formatDate(plan.nextDate)}
                  </span>
                </div>
                <button>
                  <Icon name="arrow" size={16} />
                </button>
              </div>
            ))}
        </div>
      </section>
      {selected && (
        <ChecklistModal
          activity={selected}
          token={token}
          onClose={() => setSelected(null)}
          onStatusChanged={load}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}{" "}
      {showPlan && (
        <MaintenancePlanModal
          token={token}
          onClose={() => setShowPlan(false)}
          onSaved={() => {
            setShowPlan(false);
            load();
          }}
        />
      )}
    </>
  );
}

function SummaryBox({ tone, label, value, detail, active = false, onClick }) {
  return (
    <button
      type="button"
      className={`maintenance-stat tone-${tone}${active ? " active" : ""}`}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
    >
      <span className={`stat-dot ${tone}`}></span>
      <div>
        <span>{label}</span>
        <b>{value}</b>
        <small>{detail}</small>
      </div>
    </button>
  );
}
function MaintenanceRow({ activity, onOpen }) {
  const group = maintenanceGroup(activity);
  return (
    <div className="maintenance-row">
      <div>
        <b>MP-{String(activity.id).padStart(4, "0")}</b>
        <strong>{activity.planName}</strong>
        <small>
          {activity.clientName} • {activity.unitName}
        </small>
      </div>
      <div>
        <strong>{activity.assetName}</strong>
        <small>
          TAG: {activity.assetTag || "—"} • {activity.checklistName}
        </small>
      </div>
      <span>
        {formatDate(activity.plannedDate)}
        <small>{activity.estimatedHours}h previstas</small>
      </span>
      <span>{activity.assignedTo || "Não atribuído"}</span>
      <span className={`maintenance-status ${maintenanceTone(group)}`}>
        ● {group}
      </span>
      <button onClick={() => onOpen(activity)}>
        <Icon name="arrow" size={16} />
      </button>
    </div>
  );
}
function maintenanceGroup(activity) {
  if (
    activity.status === "completed" ||
    activity.status === "completed_with_issue"
  )
    return "Realizadas";
  if (activity.status === "in_progress") return "Em execução";
  if (activity.status === "waiting_area_release") return "Aguardando liberação";
  if (activity.deadlineStatus === "overdue") return "Atrasadas";
  if (activity.deadlineStatus === "due_soon") return "Próximas";
  return "Programadas";
}
function maintenanceTone(group) {
  return {
    Realizadas: "done",
    Atrasadas: "late",
    Próximas: "soon",
    "Em execução": "doing",
    "Aguardando liberação": "waiting",
    Programadas: "planned",
  }[group];
}
function formatDate(value) {
  if (!value) return "A definir";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value.slice(0, 10)}T12:00:00Z`));
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}
function frequencyLabel(plan) {
  const units = { day: "dia", week: "semana", month: "mês", year: "ano" };
  return plan.frequencyInterval === 1
    ? `Todo ${units[plan.frequencyUnit]}`
    : `A cada ${plan.frequencyInterval} ${units[plan.frequencyUnit]}s`;
}

function PlanningPage({ token }) {
  const today = new Date();
  const initialMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [items, setItems] = useState([]);
  const [type, setType] = useState("Todos");
  const [client, setClient] = useState("Todos");
  const [month, setMonth] = useState(initialMonth);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch(`${apiBase}/planning?month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then(setItems)
      .catch(() => setError("Não foi possível carregar o calendário."));
  }, [month, token]);
  const clients = [
    ...new Set(items.map((item) => item.clientName).filter(Boolean)),
  ].sort();
  const visible = items.filter(
    (item) =>
      (type === "Todos" || item.itemType === type) &&
      (client === "Todos" || item.clientName === client),
  );
  const [year, monthNumber] = month.split("-").map(Number);
  const monthStart = new Date(year, monthNumber - 1, 1);
  const monthEnd = new Date(year, monthNumber, 0);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const days = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
  const keyFor = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const eventsByDay = visible.reduce((map, item) => {
    if (!item.plannedDate) return map;
    const key = String(item.plannedDate).slice(0, 10);
    (map[key] ??= []).push(item);
    return map;
  }, {});
  const changeMonth = (delta) => {
    const date = new Date(year, monthNumber - 1 + delta, 1);
    setMonth(
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
    );
  };
  const monthLabel = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(monthStart);
  const exportCalendarPdf = async () => {
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const width = 297;
    pdf.setFillColor(21, 25, 26);
    pdf.rect(0, 0, width, 31, "F");
    try {
      const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
      pdf.addImage(logo, "PNG", 11, 5, 48, 17);
    } catch {}
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.text(`PLANEJAMENTO • ${monthLabel.toUpperCase()}`, 286, 12, {
      align: "right",
    });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    const filterText =
      [
        type !== "Todos" && `Tipo: ${type}`,
        client !== "Todos" && `Cliente: ${client}`,
      ]
        .filter(Boolean)
        .join(" • ") || "Todos os tipos e clientes";
    pdf.text(filterText, 286, 19, { align: "right" });
    const calendarX = 10,
      calendarY = 42,
      calendarW = 277,
      headerH = 9,
      rowH = 24,
      cellW = calendarW / 7;
    const weekdays = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
    weekdays.forEach((label, index) => {
      pdf.setFillColor(
        index === 0 || index === 6 ? 235 : 244,
        index === 0 || index === 6 ? 243 : 247,
        index === 0 || index === 6 ? 255 : 251,
      );
      pdf.rect(calendarX + index * cellW, calendarY, cellW, headerH, "F");
      pdf.setTextColor(
        index === 0 || index === 6 ? 47 : 90,
        index === 0 || index === 6 ? 105 : 103,
        index === 0 || index === 6 ? 214 : 124,
      );
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6);
      pdf.text(label, calendarX + (index + 0.5) * cellW, calendarY + 6, {
        align: "center",
      });
    });
    const colors = {
      Projeto: [52, 116, 242],
      OS: [233, 155, 38],
      Preventiva: [35, 166, 111],
      Chamado: [139, 92, 246],
      Feriado: [226, 78, 94],
    };
    days.forEach((date, index) => {
      const column = index % 7,
        row = Math.floor(index / 7),
        x = calendarX + column * cellW,
        y = calendarY + headerH + row * rowH;
      const outside = date.getMonth() !== monthNumber - 1;
      pdf.setFillColor(
        outside ? 249 : column === 0 || column === 6 ? 246 : 255,
        outside ? 250 : column === 0 || column === 6 ? 249 : 255,
        outside ? 252 : 255,
      );
      pdf.rect(x, y, cellW, rowH, "F");
      pdf.setDrawColor(223, 229, 238);
      pdf.rect(x, y, cellW, rowH, "S");
      pdf.setTextColor(
        outside ? 174 : 35,
        outside ? 181 : 47,
        outside ? 193 : 68,
      );
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6);
      pdf.text(String(date.getDate()), x + 2, y + 4);
      if (outside) return;
      const eventItems = eventsByDay[keyFor(date)] || [];
      eventItems.slice(0, 4).forEach((item, eventIndex) => {
        const color = colors[item.itemType] || [90, 103, 124],
          eventY = y + 6 + eventIndex * 4;
        pdf.setFillColor(...color);
        pdf.roundedRect(x + 2, eventY, 1.5, 2.6, 0.4, 0.4, "F");
        pdf.setTextColor(42, 53, 72);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(4.4);
        const title = `${item.itemType}: ${item.title}`;
        pdf.text(pdf.splitTextToSize(title, cellW - 6)[0], x + 5, eventY + 2.1);
      });
      if (eventItems.length > 4) {
        pdf.setTextColor(94, 107, 128);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(4.2);
        pdf.text(`+${eventItems.length - 4} evento(s)`, x + 5, y + 22);
      }
    });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(5.5);
    let legendX = 11;
    [
      ["Projeto", "Projetos"],
      ["OS", "OS corretivas"],
      ["Preventiva", "Preventivas"],
      ["Chamado", "Chamados"],
      ["Feriado", "Feriados"],
    ].forEach(([value, label]) => {
      pdf.setFillColor(...colors[value]);
      pdf.roundedRect(legendX, 199, 3, 3, 0.5, 0.5, "F");
      pdf.setTextColor(82, 94, 114);
      pdf.text(label, legendX + 5, 201.5);
      legendX += 35;
    });
    await addAboutsecPdfFooters(pdf);
    const safeMonth = month.replace("-", "_");
    pdf.save(`Planejamento_${safeMonth}_${type}_${client}.pdf`);
  };
  return (
    <>
      <PageHeading
        title="Planejamento"
        subtitle="Calendário mensal unificado de projetos, ordens de serviço, chamados e preventivas."
      />
      {error && <div className="form-error">{error}</div>}
      <section className="panel planning-toolbar">
        <label>
          Tipo
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
          >
            <option>Todos</option>
            <option>Projeto</option>
            <option>OS</option>
            <option>Chamado</option>
            <option>Preventiva</option>
            <option>Feriado</option>
          </select>
        </label>
        <label>
          Cliente
          <select
            value={client}
            onChange={(event) => setClient(event.target.value)}
          >
            <option>Todos</option>
            {clients.map((name) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </label>
        <label>
          Mês
          <input
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </label>
        <div className="planning-filter-result">
          <b>
            {
              visible.filter(
                (item) => String(item.plannedDate || "").slice(0, 7) === month,
              ).length
            }
          </b>
          <span>agendamentos no mês</span>
          <button
            onClick={() => {
              setType("Todos");
              setClient("Todos");
              setMonth(initialMonth);
            }}
          >
            Limpar filtros
          </button>
          <button
            className="planning-export-button"
            onClick={exportCalendarPdf}
          >
            <Icon name="file" size={15} />
            Exportar calendário
          </button>
        </div>
      </section>
      <section className="panel planning-calendar">
        <div className="planning-calendar-title">
          <button onClick={() => changeMonth(-1)} aria-label="Mês anterior">
            ‹
          </button>
          <h3>{monthLabel}</h3>
          <button onClick={() => changeMonth(1)} aria-label="Próximo mês">
            ›
          </button>
        </div>
        <div className="planning-weekdays">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="planning-month-grid">
          {days.map((date) => {
            const dayKey = keyFor(date);
            const events = eventsByDay[dayKey] || [];
            const outside = date.getMonth() !== monthNumber - 1;
            const isToday = dayKey === keyFor(today);
            return (
              <article
                key={dayKey}
                className={`${outside ? "outside " : ""}${date.getDay() === 0 || date.getDay() === 6 ? "weekend " : ""}${events.some((item) => item.itemType === "Feriado") ? "holiday " : ""}${isToday ? "today" : ""}`}
              >
                <time>{date.getDate()}</time>
                <div>
                  {events.map((item) => (
                    <div
                      key={`${item.itemType}-${item.id}`}
                      className={`planning-event type-${item.itemType.toLowerCase()}`}
                      title={`${item.title} — ${item.clientName || "Sem cliente"}`}
                    >
                      <b>{item.itemType}</b>
                      <span>{item.title}</span>
                      <small>
                        {item.clientName || "Sem cliente"}
                        {item.unitName ? ` • ${item.unitName}` : ""}
                      </small>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
        <div className="planning-legend">
          {[
            ["Projeto", "Projeto"],
            ["OS", "OS corretiva"],
            ["Preventiva", "OS preventiva"],
            ["Chamado", "Chamado / prazo"],
            ["Feriado", "Feriado"],
          ].map(([value, label]) => (
            <span key={value}>
              <i className={`type-${value.toLowerCase()}`}></i>
              {label}
            </span>
          ))}
        </div>
      </section>
    </>
  );
}

function TechnicianAgendaPage({ token }) {
  const today = new Date();
  const initialMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(initialMonth);
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [data, setData] = useState({ days: {}, residentCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    setLoading(true);
    fetch(`${apiBase}/planning/technicians/month?month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then((result) => {
        setData(result);
        setError("");
      })
      .catch(() => setError("Não foi possível carregar a agenda da equipe."))
      .finally(() => setLoading(false));
  }, [month, token]);
  const [year, monthNumber] = month.split("-").map(Number);
  const monthStart = new Date(year, monthNumber - 1, 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const days = Array.from({ length: 42 }, (_, index) => {
    const value = new Date(gridStart);
    value.setDate(gridStart.getDate() + index);
    return value;
  });
  const keyFor = (value) =>
    `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  const selected = data.days?.[selectedDate] || {
    allocated: [],
    available: [],
  };
  const changeMonth = (delta) => {
    const next = new Date(year, monthNumber - 1 + delta, 1);
    const nextMonth = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
    setMonth(nextMonth);
    setSelectedDate(`${nextMonth}-01`);
  };
  const monthLabel = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(monthStart);
  const selectedLabel = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${selectedDate}T12:00:00Z`));
  const initialsFor = (name) =>
    String(name || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  return (
    <>
      <PageHeading
        title="Agenda dos técnicos"
        subtitle="Alocação diária da equipe em ordens de serviço, preventivas e projetos."
      />
      {error && <div className="form-error">{error}</div>}
      <section className="panel planning-calendar technician-calendar">
        <div className="planning-calendar-title">
          <button onClick={() => changeMonth(-1)} aria-label="Mês anterior">
            ‹
          </button>
          <h3>{monthLabel}</h3>
          <button onClick={() => changeMonth(1)} aria-label="Próximo mês">
            ›
          </button>
        </div>
        <div className="planning-weekdays">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="planning-month-grid">
          {days.map((day) => {
            const dayKey = keyFor(day);
            const dayData = data.days?.[dayKey] || {
              allocated: [],
              available: [],
            };
            const outside = day.getMonth() !== monthNumber - 1;
            const active = dayKey === selectedDate;
            return (
              <article
                key={dayKey}
                role="button"
                tabIndex={outside ? -1 : 0}
                className={`${outside ? "outside " : ""}${day.getDay() === 0 || day.getDay() === 6 ? "weekend " : ""}${dayData.holiday ? "holiday " : ""}${active ? "selected " : ""}`}
                onClick={() => !outside && setSelectedDate(dayKey)}
                onKeyDown={(event) =>
                  event.key === "Enter" && !outside && setSelectedDate(dayKey)
                }
              >
                <time>{day.getDate()}</time>
                {!outside && dayData.holiday && (
                  <div className="calendar-holiday-label">
                    <b>Feriado</b>
                    <span>{dayData.holiday.name}</span>
                  </div>
                )}
                {!outside && (
                  <div>
                    {dayData.allocated.slice(0, 3).map((technician) => (
                      <div
                        className="technician-calendar-person"
                        key={technician.id}
                      >
                        <b>{initialsFor(technician.name)}</b>
                        <span>{technician.name}</span>
                        <small>{technician.assignments.length}</small>
                      </div>
                    ))}
                    {dayData.allocated.length > 3 && (
                      <em>+{dayData.allocated.length - 3} técnico(s)</em>
                    )}
                    {!dayData.allocated.length && (
                      <small className="technician-calendar-empty">
                        Sem alocações
                      </small>
                    )}
                  </div>
                )}
                {!outside && (
                  <footer>
                    <span>{dayData.available.length} disponível(is)</span>
                  </footer>
                )}
              </article>
            );
          })}
        </div>
      </section>
      <section className="technician-agenda-hero compact">
        <div>
          <span>DIA SELECIONADO</span>
          <h2>{selectedLabel}</h2>
          <p>Clique em outro dia do calendário para consultar a equipe.</p>
        </div>
      </section>
      <section className="technician-agenda-kpis">
        <article>
          <i className="blue"></i>
          <span>Técnicos alocados</span>
          <b>{selected.allocated.length}</b>
        </article>
        <article>
          <i className="green"></i>
          <span>Disponíveis</span>
          <b>{selected.available.length}</b>
          <small>Residentes não entram neste total</small>
        </article>
        <article>
          <i className="violet"></i>
          <span>Atividades do dia</span>
          <b>
            {selected.allocated.reduce(
              (sum, item) => sum + item.assignments.length,
              0,
            )}
          </b>
        </article>
      </section>
      <section className="panel technician-agenda-panel">
        <PanelTitle
          title="Alocação da equipe"
          subtitle={
            loading
              ? "Atualizando agenda…"
              : `${selected.allocated.length} técnico(s) com atividade`
          }
        />
        {loading ? (
          <div className="loading-bar"></div>
        ) : (
          <div className="technician-agenda-grid">
            {selected.allocated.map((technician) => (
              <article key={technician.id}>
                <header>
                  {technician.photoDataUrl ? (
                    <img src={technician.photoDataUrl} alt="" />
                  ) : (
                    <i>{initialsFor(technician.name)}</i>
                  )}
                  <div>
                    <strong>{technician.name}</strong>
                    <small>{technician.functionName}</small>
                  </div>
                  <em>{technician.assignments.length} atividade(s)</em>
                </header>
                <div>
                  {technician.assignments.map((item) => (
                    <div
                      className={`technician-agenda-assignment type-${item.itemType.toLowerCase()}`}
                      key={`${item.itemType}-${item.itemId}`}
                    >
                      <span>{item.itemType}</span>
                      <strong>{item.title}</strong>
                      <small>
                        {item.clientName || "Sem cliente"}
                        {item.unitName ? ` • ${item.unitName}` : ""}
                      </small>
                    </div>
                  ))}
                </div>
              </article>
            ))}
            {!selected.allocated.length && !loading && (
              <div className="empty">Nenhum técnico alocado nesta data.</div>
            )}
          </div>
        )}
      </section>
      <section className="panel technician-available-panel">
        <PanelTitle
          title="Técnicos disponíveis"
          subtitle={`Sem atividade em ${formatDate(selectedDate)} • técnicos residentes excluídos`}
        />
        <div>
          {selected.available.map((technician) => (
            <article key={technician.id}>
              {technician.photoDataUrl ? (
                <img src={technician.photoDataUrl} alt="" />
              ) : (
                <i>{initialsFor(technician.name)}</i>
              )}
              <span>
                <strong>{technician.name}</strong>
                <small>{technician.functionName}</small>
              </span>
              <em>Disponível</em>
            </article>
          ))}
          {!selected.available.length && !loading && (
            <div className="empty">Nenhum técnico disponível nesta data.</div>
          )}
        </div>
      </section>
    </>
  );
}

function collaboratorStatusLabel(status) {
  return (
    {
      active: "Ativo",
      away: "Afastado",
      vacation: "Férias",
      terminated: "Desligado",
    }[status] || status
  );
}
function portraitPhotoDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("image/"))
      return reject(new Error("Selecione uma imagem JPG, PNG ou WebP."));
    if (file.size > 8 * 1024 * 1024)
      return reject(new Error("A imagem original deve ter no máximo 8 MB."));
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      const target = document.createElement("canvas");
      target.width = 450;
      target.height = 600;
      const context = target.getContext("2d");
      const ratio = Math.max(
        target.width / image.width,
        target.height / image.height,
      );
      const width = image.width * ratio;
      const height = image.height * ratio;
      context.drawImage(
        image,
        (target.width - width) / 2,
        (target.height - height) / 2,
        width,
        height,
      );
      URL.revokeObjectURL(url);
      resolve(target.toDataURL("image/jpeg", 0.84));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler essa imagem."));
    };
    image.src = url;
  });
}
function CollaboratorsPage({ token, canManage }) {
  const blank = {
    name: "",
    email: "",
    password: "",
    phone: "",
    jobFunctionId: "",
    accessProfileId: "",
    status: "active",
    notes: "",
    photoDataUrl: null,
  };
  const [items, setItems] = useState([]);
  const [configuration, setConfiguration] = useState({
    functions: [],
    profiles: [],
  });
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [listQuery, setListQuery] = useState("");
  const [functionFilter, setFunctionFilter] = useState("Todos");
  const [profileFilter, setProfileFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = () =>
    fetch(`${apiBase}/technicians`, { headers })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        setItems(await response.json());
      })
      .catch(() => setError("Não foi possível carregar os colaboradores."));
  useEffect(() => {
    load();
    fetch(`${apiBase}/collaborators/configuration`, { headers })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => {
        setConfiguration(data);
        setForm((current) => ({
          ...current,
          jobFunctionId:
            current.jobFunctionId || String(data.functions[0]?.id || ""),
          accessProfileId:
            current.accessProfileId || String(data.profiles[0]?.id || ""),
        }));
      })
      .catch(() =>
        setError("Configure funções e perfis de acesso em Configurações."),
      );
  }, [token]);
  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setError("");
  };
  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      email: item.email || "",
      password: "",
      phone: item.phone || "",
      jobFunctionId: String(item.jobFunctionId || ""),
      accessProfileId: String(item.accessProfileId || ""),
      status: item.status || "active",
      notes: item.notes || "",
      photoDataUrl: item.photoDataUrl || null,
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const choosePhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const photoDataUrl = await portraitPhotoDataUrl(file);
      setForm((current) => ({ ...current, photoDataUrl }));
      setError("");
    } catch (photoError) {
      setError(photoError.message);
    } finally {
      event.target.value = "";
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const response = await fetch(
      `${apiBase}/technicians${editingId ? `/${editingId}` : ""}`,
      {
        method: editingId ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(form),
      },
    );
    const data = await response.json().catch(() => ({}));
    setSaving(false);
    if (response.ok) {
      reset();
      load();
      return;
    }
    const messages = {
      collaborator_email_required:
        "Informe o e-mail usado para acessar o sistema.",
      invalid_collaborator_identity: "Informe nome e e-mail válidos.",
      password_too_short: "A senha deve possuir pelo menos 8 caracteres.",
      password_required_for_access:
        "Defina uma senha para criar o acesso deste colaborador.",
      email_already_registered: "Este e-mail já pertence a outro usuário.",
      invalid_collaborator_profile: "Status ou perfil de acesso inválido.",
      invalid_collaborator_photo:
        "A foto precisa ser JPG, PNG ou WebP e respeitar o limite de tamanho.",
    };
    setError(messages[data.error] || "Não foi possível salvar o colaborador.");
  };
  const remove = async (item) => {
    if (
      !window.confirm(
        `Excluir ${item.name}? Se houver histórico, será necessário marcá-lo como Desligado.`,
      )
    )
      return;
    setError("");
    const response = await fetch(`${apiBase}/technicians/${item.id}`, {
      method: "DELETE",
      headers,
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      if (editingId === item.id) reset();
      load();
      return;
    }
    setError(
      data.error === "collaborator_has_history"
        ? "Este colaborador possui projetos ou relatórios vinculados. Altere o status para Desligado para preservar o histórico."
        : "Não foi possível excluir o colaborador.",
    );
  };
  const counts = {
    active: items.filter((item) => item.status === "active").length,
    unavailable: items.filter((item) =>
      ["away", "vacation"].includes(item.status),
    ).length,
    terminated: items.filter((item) => item.status === "terminated").length,
  };
  const normalizeListSearch = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const visibleItems = items
    .filter((item) => {
      const functionName = item.functionName || item.role || "Sem função";
      const profileName = item.accessProfileName || "Sem perfil";
      return (
        normalizeListSearch(
          `${item.name} ${item.email} ${item.phone} ${functionName} ${profileName}`,
        ).includes(normalizeListSearch(listQuery)) &&
        (functionFilter === "Todos" || functionName === functionFilter) &&
        (profileFilter === "Todos" || profileName === profileFilter) &&
        (statusFilter === "Todos" || item.status === statusFilter)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  const clearListFilters = () => {
    setListQuery("");
    setFunctionFilter("Todos");
    setProfileFilter("Todos");
    setStatusFilter("Todos");
  };
  return (
    <>
      <PageHeading
        title="Colaboradores"
        subtitle="Gerencie profissionais internos, acesso ao sistema e disponibilidade operacional."
      />
      <div className="collaborator-stats">
        <article className="panel">
          <span>Total</span>
          <b>{items.length}</b>
        </article>
        <article className="panel">
          <span>Ativos</span>
          <b>{counts.active}</b>
        </article>
        <article className="panel">
          <span>Afastados ou em férias</span>
          <b>{counts.unavailable}</b>
        </article>
        <article className="panel">
          <span>Desligados</span>
          <b>{counts.terminated}</b>
        </article>
      </div>
      {error && <div className="login-error collaborator-error">{error}</div>}
      <div className="collaborator-layout">
        <form className="panel inline-form collaborator-form" onSubmit={submit}>
          <div className="collaborator-form-title">
            <div>
              <h3>{editingId ? "Editar colaborador" : "Novo colaborador"}</h3>
              <p>Funções e perfis são administrados em Configurações.</p>
            </div>
            {editingId && (
              <button type="button" onClick={reset}>
                Cancelar
              </button>
            )}
          </div>
          <div className="collaborator-photo-field">
            <div className="collaborator-photo-preview">
              {form.photoDataUrl ? (
                <img src={form.photoDataUrl} alt="Foto 3x4 do colaborador" />
              ) : (
                <span>
                  <Icon name="users" size={25} />
                  <small>Foto 3×4</small>
                </span>
              )}
            </div>
            <div>
              <strong>Foto do colaborador</strong>
              <small>
                JPG, PNG ou WebP. O enquadramento será ajustado automaticamente
                para 3×4.
              </small>
              {canManage && (
                <div className="collaborator-photo-actions">
                  <label className="photo-upload-button">
                    {form.photoDataUrl ? "Trocar foto" : "Adicionar foto"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={choosePhoto}
                    />
                  </label>
                  {form.photoDataUrl && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, photoDataUrl: null })}
                    >
                      Remover
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <label>
            Nome completo*
            <input
              value={form.name}
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
              required
              disabled={!canManage}
            />
          </label>
          <label>
            E-mail de acesso*
            <input
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
              required
              disabled={!canManage}
            />
          </label>
          <label>
            {editingId ? "Nova senha (opcional)" : "Senha inicial*"}
            <input
              type="password"
              minLength="8"
              required={!editingId}
              value={form.password}
              onChange={(event) =>
                setForm({ ...form, password: event.target.value })
              }
              disabled={!canManage}
            />
          </label>
          <div className="form-grid">
            <label>
              Telefone
              <input
                value={form.phone}
                onChange={(event) =>
                  setForm({ ...form, phone: event.target.value })
                }
                disabled={!canManage}
              />
            </label>
            <label>
              Função
              <select
                required
                value={form.jobFunctionId}
                onChange={(event) => {
                  const selectedFunction = configuration.functions.find(
                    (item) => String(item.id) === event.target.value,
                  );
                  const residentProfile = configuration.profiles.find(
                    (item) => item.name.toLocaleLowerCase("pt-BR") === "técnico residente",
                  );
                  setForm({
                    ...form,
                    jobFunctionId: event.target.value,
                    accessProfileId: selectedFunction?.name === "Técnico residente" && residentProfile
                      ? String(residentProfile.id)
                      : form.accessProfileId,
                  });
                }}
                disabled={!canManage}
              >
                <option value="">Selecione</option>
                {configuration.functions.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {configuration.functions.some((item) => String(item.id) === form.jobFunctionId && item.name === "Técnico residente") && (
                <small>Após salvar, vincule o colaborador em Clientes → Unidade → Técnicos residentes.</small>
              )}
            </label>
            <label>
              Perfil de acesso
              <select
                required
                value={form.accessProfileId}
                onChange={(event) =>
                  setForm({ ...form, accessProfileId: event.target.value })
                }
                disabled={!canManage}
              >
                <option value="">Selecione</option>
                {configuration.profiles.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(event) =>
                  setForm({ ...form, status: event.target.value })
                }
                disabled={!canManage}
              >
                <option value="active">Ativo</option>
                <option value="away">Afastado</option>
                <option value="vacation">Férias</option>
                <option value="terminated">Desligado</option>
              </select>
            </label>
          </div>
          <label>
            Observações
            <textarea
              value={form.notes}
              onChange={(event) =>
                setForm({ ...form, notes: event.target.value })
              }
              disabled={!canManage}
            />
          </label>
          <div className="collaborator-access-note">
            <Icon name="check" size={16} />
            <span>
              <strong>Permissão de menor privilégio</strong>
              <small>
                O colaborador acessará exclusivamente o que estiver selecionado
                no perfil escolhido.
              </small>
            </span>
          </div>
          {canManage ? (
            <button className="primary" disabled={saving}>
              {saving
                ? "Salvando…"
                : editingId
                  ? "Salvar alterações"
                  : "Cadastrar colaborador"}
            </button>
          ) : (
            <small>Seu perfil não permite alterar este cadastro.</small>
          )}
        </form>
        <section className="panel collaborator-list">
          <PanelTitle
            title="Colaboradores cadastrados"
            subtitle={`${visibleItems.length} de ${items.length} colaborador(es)`}
          />
          <div className="collaborator-list-filters">
            <label className="collaborator-list-search">
              Buscar
              <span>
                <Icon name="search" size={16} />
                <input
                  value={listQuery}
                  onChange={(event) => setListQuery(event.target.value)}
                  placeholder="Nome, e-mail ou telefone"
                />
              </span>
            </label>
            <label>
              Função
              <select
                value={functionFilter}
                onChange={(event) => setFunctionFilter(event.target.value)}
              >
                <option>Todos</option>
                {configuration.functions.map((item) => (
                  <option key={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
            <label>
              Perfil
              <select
                value={profileFilter}
                onChange={(event) => setProfileFilter(event.target.value)}
              >
                <option>Todos</option>
                {configuration.profiles.map((item) => (
                  <option key={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="Todos">Todos</option>
                <option value="active">Ativo</option>
                <option value="away">Afastado</option>
                <option value="vacation">Férias</option>
                <option value="terminated">Desligado</option>
              </select>
            </label>
            <button type="button" onClick={clearListFilters}>
              Limpar filtros
            </button>
          </div>
          {visibleItems.map((item) => (
            <article className="collaborator-row" key={item.id}>
              {item.photoDataUrl ? (
                <img
                  className="collaborator-list-photo"
                  src={item.photoDataUrl}
                  alt=""
                />
              ) : (
                <span className="avatar">{initials(item.name)}</span>
              )}
              <div>
                <strong>{item.name}</strong>
                <small>
                  {item.email || "Acesso ainda não configurado"} •{" "}
                  {item.phone || "Sem telefone"}
                </small>
                <em>
                  {item.functionName || item.role || "Sem função"} • Acesso:{" "}
                  {item.accessProfileName || "Sem perfil"}
                </em>
              </div>
              <span
                className={`collaborator-status ${item.status || "active"}`}
              >
                {collaboratorStatusLabel(item.status || "active")}
              </span>
              {canManage && (
                <div className="collaborator-row-actions">
                  <button onClick={() => edit(item)}>Editar</button>
                  <button className="danger" onClick={() => remove(item)}>
                    Excluir
                  </button>
                </div>
              )}
            </article>
          ))}
          {!visibleItems.length && (
            <div className="empty">
              {items.length
                ? "Nenhum colaborador corresponde aos filtros selecionados."
                : "Nenhum colaborador cadastrado."}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function TeamsPageLegacy({ token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Técnico",
  });
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = () =>
    fetch(`${apiBase}/technicians`, { headers })
      .then((r) => r.json())
      .then(setItems);
  useEffect(() => {
    load();
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    const r = await fetch(`${apiBase}/technicians`, {
      method: "POST",
      headers,
      body: JSON.stringify(form),
    });
    if (r.ok) {
      setForm({ name: "", email: "", phone: "", role: "Técnico" });
      load();
    }
  };
  return (
    <>
      <PageHeading
        title="Equipes e técnicos"
        subtitle="Cadastre os profissionais disponíveis para planejamento e execução."
      />
      <div className="split-page">
        <form className="panel inline-form" onSubmit={submit}>
          <h3>Novo profissional</h3>
          <label>
            Nome
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label>
            E-mail
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <div className="form-grid">
            <label>
              Telefone
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label>
              Função
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option>Técnico</option>
                <option>Analista</option>
                <option>Supervisor</option>
                <option>Gestor</option>
              </select>
            </label>
          </div>
          <button className="primary">Cadastrar</button>
        </form>
        <section className="panel team-list">
          <PanelTitle
            title="Profissionais cadastrados"
            subtitle={`${items.length} no total`}
          />
          {items.map((item) => (
            <div className="team-row" key={item.id}>
              <span className="avatar">{initials(item.name)}</span>
              <div>
                <strong>{item.name}</strong>
                <small>
                  {item.email || "Sem e-mail"} • {item.phone || "Sem telefone"}
                </small>
              </div>
              <span className="record-status done">
                {item.role || "Técnico"}
              </span>
            </div>
          ))}
          {!items.length && (
            <div className="empty">Nenhum profissional cadastrado.</div>
          )}
        </section>
      </div>
    </>
  );
}

function MaterialsPage({ token }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    projectId: "",
    item: "",
    unit: "un",
    plannedQuantity: 0,
    deliveredQuantity: 0,
    usedQuantity: 0,
    unitCost: 0,
  });
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = () =>
    Promise.all([
      fetch(`${apiBase}/materials`, { headers }).then((r) => r.json()),
      fetch(`${apiBase}/setup/operational`, { headers }).then((r) => r.json()),
    ]).then(([m, s]) => {
      setItems(m);
      setProjects(s.projects);
    });
  useEffect(() => {
    load();
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    const r = await fetch(`${apiBase}/materials`, {
      method: "POST",
      headers,
      body: JSON.stringify({ ...form, projectId: Number(form.projectId) }),
    });
    if (r.ok) {
      setForm({
        ...form,
        item: "",
        plannedQuantity: 0,
        deliveredQuantity: 0,
        usedQuantity: 0,
      });
      load();
    }
  };
  return (
    <>
      <PageHeading
        title="Materiais por projeto"
        subtitle="Previsto, entregue, utilizado e saldo sem exposição do resultado financeiro."
      />
      <form className="panel material-form" onSubmit={submit}>
        <select
          value={form.projectId}
          onChange={(e) => setForm({ ...form, projectId: e.target.value })}
          required
        >
          <option value="">Projeto</option>
          {projects.map((p) => (
            <option value={p.id} key={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          value={form.item}
          onChange={(e) => setForm({ ...form, item: e.target.value })}
          placeholder="Material"
          required
        />
        <input
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
          placeholder="Unidade"
        />
        <input
          type="number"
          value={form.plannedQuantity}
          onChange={(e) =>
            setForm({ ...form, plannedQuantity: Number(e.target.value) })
          }
          placeholder="Previsto"
        />
        <button className="primary">
          <Icon name="plus" />
          Adicionar
        </button>
      </form>
      <section className="panel material-table">
        <div className="material-head">
          <span>Projeto/material</span>
          <span>Previsto</span>
          <span>Entregue</span>
          <span>Utilizado</span>
          <span>Saldo</span>
        </div>
        {items.map((item) => (
          <div className="material-row" key={item.id}>
            <div>
              <strong>{item.item}</strong>
              <small>
                {item.projectName} • {item.clientName}
              </small>
            </div>
            <span>
              {item.plannedQuantity} {item.unit}
            </span>
            <span>
              {item.deliveredQuantity} {item.unit}
            </span>
            <span>
              {item.usedQuantity} {item.unit}
            </span>
            <b>
              {item.balance} {item.unit}
            </b>
          </div>
        ))}
        {!items.length && <div className="empty">Nenhum material lançado.</div>}
      </section>
    </>
  );
}

function MaterialsPageV2({ token }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const empty = {
    item: "",
    itemType: "material",
    billingScope: "included",
    brand: "",
    model: "",
    plannedQuantity: 1,
    unitCost: 0,
  };
  const [form, setForm] = useState(empty);
  const [projectValue, setProjectValue] = useState("");
  const [serviceValue, setServiceValue] = useState("");
  const [editingItemId, setEditingItemId] = useState(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectClient, setProjectClient] = useState("Todos");
  const [projectUnit, setProjectUnit] = useState("Todas");
  const [projectStatus, setProjectStatus] = useState("Todos");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = async () => {
    const safeJson = async (path) => {
      try {
        const response = await fetch(`${apiBase}/${path}`, { headers });
        if (!response.ok) throw new Error();
        return await response.json();
      } catch {
        return null;
      }
    };
    const [materialData, costProjectData, setup] = await Promise.all([
      safeJson("materials"),
      safeJson("materials/projects"),
      safeJson("setup/operational"),
    ]);
    const loadedItems = Array.isArray(materialData) ? materialData : [];
    const operationalProjects = Array.isArray(setup?.projects)
      ? setup.projects
      : [];
    const clients = Array.isArray(setup?.clients) ? setup.clients : [];
    const units = Array.isArray(setup?.units) ? setup.units : [];
    const fallbackProjects = operationalProjects.map((project) => {
      const projectItems = loadedItems.filter(
        (item) => Number(item.projectId) === Number(project.id),
      );
      return {
        ...project,
        clientName:
          clients.find(
            (client) => Number(client.id) === Number(project.clientId),
          )?.name || "Sem cliente",
        unitName:
          units.find((unit) => Number(unit.id) === Number(project.unitId))
            ?.name || "",
        contractValue: 0,
        administrativeExpensePercent: 15,
        taxPercent: 22,
        bonusPercent: null,
        itemCount: projectItems.length,
        itemsTotal: projectItems.reduce(
          (sum, item) => sum + Number(item.totalValue || 0),
          0,
        ),
      };
    });
    setItems(loadedItems);
    const costProjects =
      Array.isArray(costProjectData) && costProjectData.length
        ? costProjectData.map((project) => ({
            ...operationalProjects.find(
              (item) => Number(item.id) === Number(project.id),
            ),
            ...project,
          }))
        : fallbackProjects;
    setProjects(costProjects);
  };
  useEffect(() => {
    load();
  }, [token]);
  const openProject = (project) => {
    setProjectId(project.id);
    setProjectValue(
      Number(project.contractValue) > 0 ? Number(project.contractValue) : "",
    );
    setServiceValue(
      Number(project.serviceValue) > 0 ? Number(project.serviceValue) : "",
    );
    setEditingItemId(null);
    setMessage("");
    setForm(empty);
  };
  const selected = projects.find((project) => project.id === projectId);
  const projectClients = [
    ...new Set(projects.map((project) => project.clientName).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const projectUnits = [
    ...new Set(
      projects
        .filter(
          (project) =>
            projectClient === "Todos" || project.clientName === projectClient,
        )
        .map((project) => project.unitName)
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const normalizedProjectSearch = projectSearch
    .trim()
    .toLocaleLowerCase("pt-BR");
  const filteredProjects = projects.filter(
    (project) =>
      (!normalizedProjectSearch ||
        [
          project.projectNumber,
          project.name,
          project.clientName,
          project.unitName,
        ].some((value) =>
          String(value || "")
            .toLocaleLowerCase("pt-BR")
            .includes(normalizedProjectSearch),
        )) &&
      (projectClient === "Todos" || project.clientName === projectClient) &&
      (projectUnit === "Todas" || project.unitName === projectUnit) &&
      (projectStatus === "Todos" ||
        (projectStatus === "Concluídos"
          ? project.status === "completed"
          : project.status !== "completed")),
  );
  const projectItems = items.filter(
    (item) => Number(item.projectId) === Number(projectId),
  );
  const itemsTotal = projectItems.reduce(
    (sum, item) => sum + Number(item.totalValue || 0),
    0,
  );
  const additionalItemsTotal = projectItems
    .filter((item) => item.billingScope === "additional")
    .reduce((sum, item) => sum + Number(item.totalValue || 0), 0);
  const editingItem = projectItems.find((item) => item.id === editingItemId);
  const editingAutomaticExcess =
    editingItem?.systemKey === "excess_project_days";
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    const response = await fetch(
      `${apiBase}/materials${editingItemId ? `/${editingItemId}` : ""}`,
      {
        method: editingItemId ? "PATCH" : "POST",
        headers,
        body: JSON.stringify({ ...form, projectId, unit: "un" }),
      },
    );
    if (response.ok) {
      setForm(empty);
      setEditingItemId(null);
      setMessage(
        editingItemId
          ? "Item atualizado com sucesso."
          : "Item cadastrado com sucesso.",
      );
      await load();
    } else setMessage("Não foi possível salvar o item.");
    setSaving(false);
  };
  const editItem = (item) => {
    setEditingItemId(item.id);
    setForm({
      item: item.item || "",
      itemType: item.itemType || "material",
      billingScope: item.billingScope || "included",
      brand: item.brand || "",
      model: item.model || "",
      plannedQuantity: Number(item.plannedQuantity) || 1,
      unitCost: Number(item.unitCost) || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteItem = async (item) => {
    if (item.systemKey === "excess_project_days") {
      setMessage(
        "Este item é calculado automaticamente e não pode ser excluído.",
      );
      return;
    }
    if (
      !window.confirm(
        `Excluir “${item.item}”? Esta ação não poderá ser desfeita.`,
      )
    )
      return;
    setSaving(true);
    const response = await fetch(`${apiBase}/materials/${item.id}`, {
      method: "DELETE",
      headers,
    });
    setMessage(
      response.ok
        ? "Item excluído com sucesso."
        : "Não foi possível excluir o item.",
    );
    if (response.ok) {
      if (editingItemId === item.id) {
        setEditingItemId(null);
        setForm(empty);
      }
      await load();
    }
    setSaving(false);
  };
  const saveProjectValue = async (event) => {
    event.preventDefault();
    if (projectValue === "") return;
    setSaving(true);
    const response = await fetch(
      `${apiBase}/materials/projects/${projectId}/settings`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          contractValue: Number(projectValue),
          serviceValue: Number(serviceValue) || 0,
        }),
      },
    );
    setMessage(
      response.ok
        ? "Valores do projeto salvos."
        : "Não foi possível salvar os valores do projeto. Verifique se o backend foi reiniciado.",
    );
    if (response.ok) await load();
    setSaving(false);
  };
  const importSpreadsheet = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSaving(true);
    setMessage("");
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]],
        { header: 1, defval: "" },
      );
      const normalize = (value) =>
        String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "");
      const columns = (rows[0] || []).map(normalize);
      const index = (name) => columns.indexOf(name);
      const typeIndex = index("TIPO"),
        descriptionIndex = index("DESCRICAO"),
        brandIndex = index("MARCA"),
        modelIndex = index("MODELO"),
        quantityIndex = index("QTD"),
        unitValueIndex = columns.findIndex(
          (column) => column === "VUNIT" || column === "VUNITARIO",
        );
      if (
        [
          descriptionIndex,
          brandIndex,
          modelIndex,
          quantityIndex,
          unitValueIndex,
        ].some((value) => value < 0)
      )
        throw new Error("template");
      const imported = rows
        .slice(1)
        .filter(
          (row) =>
            String(row[descriptionIndex] || "").trim() &&
            normalize(row[0]) !== "VALORTOTAL",
        )
        .map((row) => ({
          item: String(row[descriptionIndex]).trim(),
          itemType:
            typeIndex >= 0 && normalize(row[typeIndex]).startsWith("SERV")
              ? "service"
              : "material",
          brand: String(row[brandIndex] || "").trim(),
          model: String(row[modelIndex] || "").trim(),
          plannedQuantity: Number(row[quantityIndex]),
          unitCost: Number(row[unitValueIndex]),
        }));
      if (!imported.length) throw new Error("empty");
      const response = await fetch(`${apiBase}/materials/import`, {
        method: "POST",
        headers,
        body: JSON.stringify({ projectId, items: imported }),
      });
      if (!response.ok) throw new Error("api");
      setMessage(`${imported.length} item(ns) importado(s) com sucesso.`);
      await load();
    } catch {
      setMessage(
        "Planilha inválida. Use DESCRIÇÃO, MARCA, MODELO, QTD e V.UNIT; a coluna TIPO é opcional.",
      );
    } finally {
      setSaving(false);
    }
  };
  if (!selected)
    return (
      <>
        <div className="access-banner">
          <Icon name="wallet" />
          <div>
            <strong>Custos operacionais dos projetos</strong>
            <p>
              Selecione uma obra para lançar materiais, equipamentos e serviços.
            </p>
          </div>
        </div>
        {message && <div className="login-error">{message}</div>}
        <div className="cost-project-intro">
          <div>
            <h2>Selecione um projeto</h2>
            <p>
              Abra uma obra para visualizar e cadastrar seus itens de custo.
            </p>
          </div>
          <span>
            {filteredProjects.length} de {projects.length} projeto(s)
          </span>
        </div>
        <section className="panel result-project-filters cost-project-filters">
          <label>
            <span>Buscar projeto</span>
            <div>
              <Icon name="search" size={16} />
              <input
                value={projectSearch}
                onChange={(event) => setProjectSearch(event.target.value)}
                placeholder="Número, nome, cliente ou unidade"
              />
            </div>
          </label>
          <label>
            <span>Cliente</span>
            <select
              value={projectClient}
              onChange={(event) => {
                setProjectClient(event.target.value);
                setProjectUnit("Todas");
              }}
            >
              <option>Todos</option>
              {projectClients.map((client) => (
                <option key={client}>{client}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Unidade</span>
            <select
              value={projectUnit}
              onChange={(event) => setProjectUnit(event.target.value)}
            >
              <option>Todas</option>
              {projectUnits.map((unit) => (
                <option key={unit}>{unit}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Situação</span>
            <select
              value={projectStatus}
              onChange={(event) => setProjectStatus(event.target.value)}
            >
              <option>Todos</option>
              <option>Em andamento</option>
              <option>Concluídos</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => {
              setProjectSearch("");
              setProjectClient("Todos");
              setProjectUnit("Todas");
              setProjectStatus("Todos");
            }}
          >
            Limpar filtros
          </button>
        </section>
        <div className="cost-project-grid">
          {filteredProjects.map((project) => (
            <button
              className="panel cost-project-card"
              onClick={() => openProject(project)}
              key={project.id}
            >
              <div>
                <span className="client-logo green">
                  {initials(project.clientName || project.name)}
                </span>
                <em>
                  {project.status === "completed"
                    ? "Concluído"
                    : "Em andamento"}
                </em>
              </div>
              <b className="cost-project-number">
                {project.projectNumber || "Projeto sem número"}
              </b>
              <h3>{project.name}</h3>
              <p>
                {project.clientName || "Sem cliente"}
                {project.unitName ? ` • ${project.unitName}` : ""}
              </p>
              <div>
                <span>
                  Itens cadastrados<b>{project.itemCount || 0}</b>
                </span>
                <span>
                  Total dos itens<b>{formatCurrency(project.itemsTotal)}</b>
                </span>
              </div>
              <strong>
                Abrir custos <Icon name="arrow" size={15} />
              </strong>
            </button>
          ))}
        </div>
        {!filteredProjects.length && (
          <div className="panel empty">
            {projects.length
              ? "Nenhum projeto corresponde aos filtros selecionados."
              : "Nenhum projeto disponível."}
          </div>
        )}
      </>
    );
  return (
    <div className="project-cost-page">
      <div className="project-cost-toolbar">
        <button onClick={() => setProjectId(null)}>
          ‹ Voltar aos projetos
        </button>
        <div>
          <h2>{selected.name}</h2>
          <p>
            {selected.clientName}
            {selected.unitName ? ` • ${selected.unitName}` : ""}
          </p>
        </div>
        <label className="secondary-button spreadsheet-action">
          <span className="spreadsheet-action-icon">
            <Icon name="download" size={18} />
          </span>
          <span>
            <b>Importar planilha</b>
            <small>XLSX com materiais e serviços</small>
          </span>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={importSpreadsheet}
            disabled={saving}
          />
        </label>
      </div>
      {message && (
        <div
          className={
            message.includes("sucesso") || message.includes("salvo")
              ? "scope-note"
              : "login-error"
          }
        >
          {message}
        </div>
      )}
      <form className="panel project-cost-value" onSubmit={saveProjectValue}>
        <label>
          Valor do projeto
          <MoneyInput
            required
            value={projectValue}
            onChange={setProjectValue}
            placeholder="0,00"
          />
        </label>
        <label>
          Valor dos serviços
          <MoneyInput
            value={serviceValue}
            onChange={setServiceValue}
            placeholder="0,00"
          />
        </label>
        <button className="primary" disabled={saving || projectValue === ""}>
          Salvar valores
        </button>
        <p>
          Impostos, despesas administrativas, premiação, saldo e lucro ficam
          disponíveis exclusivamente na área Resultado.
        </p>
      </form>
      <form
        className={`panel project-cost-form ${editingItemId ? "editing" : ""}`}
        onSubmit={submit}
      >
        <div className="project-cost-form-heading">
          <h3>
            {editingAutomaticExcess
              ? "Editar custo do dia excedente"
              : editingItemId
                ? "Editar item"
                : "Novo item"}
          </h3>
          {editingItemId && (
            <button
              type="button"
              onClick={() => {
                setEditingItemId(null);
                setForm(empty);
              }}
            >
              Cancelar edição
            </button>
          )}
        </div>
        {editingAutomaticExcess && (
          <p className="automatic-cost-note">
            Item automático: descrição e quantidade acompanham o cronograma.
            Somente o custo diário pode ser alterado.
          </p>
        )}
        <div>
          <label>
            Classificação*
            <select
              disabled={editingAutomaticExcess}
              value={form.billingScope}
              onChange={(event) =>
                setForm({ ...form, billingScope: event.target.value })
              }
            >
              <option value="included">Dentro do projeto</option>
              <option value="additional">Adicional</option>
            </select>
          </label>
          <label>
            Tipo*
            <select
              disabled={editingAutomaticExcess}
              value={form.itemType}
              onChange={(event) =>
                setForm({ ...form, itemType: event.target.value })
              }
            >
              <option value="material">Material / equipamento</option>
              <option value="service">Serviço</option>
            </select>
          </label>
          <label>
            Descrição*
            <input
              disabled={editingAutomaticExcess}
              required
              value={form.item}
              onChange={(event) =>
                setForm({ ...form, item: event.target.value })
              }
            />
          </label>
          <label>
            Marca
            <input
              disabled={editingAutomaticExcess}
              value={form.brand}
              onChange={(event) =>
                setForm({ ...form, brand: event.target.value })
              }
            />
          </label>
          <label>
            Modelo
            <input
              disabled={editingAutomaticExcess}
              value={form.model}
              onChange={(event) =>
                setForm({ ...form, model: event.target.value })
              }
            />
          </label>
          <label>
            Quantidade*
            <input
              disabled={editingAutomaticExcess}
              required
              type="number"
              min="0.01"
              step="0.01"
              value={form.plannedQuantity}
              onChange={(event) =>
                setForm({
                  ...form,
                  plannedQuantity: Number(event.target.value),
                })
              }
            />
          </label>
          <label>
            Valor unitário*
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={form.unitCost}
              onChange={(event) =>
                setForm({ ...form, unitCost: Number(event.target.value) })
              }
            />
          </label>
          <label>
            Total
            <input
              readOnly
              value={formatCurrency(form.plannedQuantity * form.unitCost)}
            />
          </label>
          <button className="primary" disabled={saving}>
            <Icon name={editingItemId ? "check" : "plus"} size={16} />
            {editingItemId ? "Salvar alteração" : "Adicionar item"}
          </button>
        </div>
      </form>
      <section className="panel project-cost-table categorized actionable">
        <div className="project-cost-head">
          <span>Item / descrição</span>
          <span>Classificação</span>
          <span>Tipo</span>
          <span>Marca</span>
          <span>Modelo</span>
          <span>Quantidade</span>
          <span>Valor unitário</span>
          <span>Total</span>
          <span>Ações</span>
        </div>
        {projectItems.map((item, index) => (
          <div
            className={`project-cost-row ${editingItemId === item.id ? "editing" : ""} ${item.systemKey === "excess_project_days" ? "automatic" : ""}`}
            key={item.id}
          >
            <div>
              <b>{index + 1}</b>
              <strong>
                {item.item}
                {item.systemKey === "excess_project_days" && (
                  <small>Automático • protegido</small>
                )}
              </strong>
            </div>
            <span
              className={`cost-scope ${item.billingScope === "additional" ? "additional" : ""}`}
            >
              {item.billingScope === "additional"
                ? "Adicional"
                : "Dentro do projeto"}
            </span>
            <span
              className={`cost-type ${item.itemType === "service" ? "service" : ""}`}
            >
              {item.itemType === "service"
                ? "Serviço"
                : "Material / equipamento"}
            </span>
            <span>{item.brand || "—"}</span>
            <span>{item.model || "—"}</span>
            <span>
              {item.plannedQuantity} {item.unit}
            </span>
            <span>{formatCurrency(item.unitCost)}</span>
            <strong>{formatCurrency(item.totalValue)}</strong>
            <div className="project-cost-actions">
              <button
                type="button"
                onClick={() => editItem(item)}
                title={
                  item.systemKey === "excess_project_days"
                    ? "Editar somente o custo diário"
                    : "Editar item"
                }
              >
                <Icon name="edit" size={15} />
              </button>
              {item.systemKey !== "excess_project_days" && (
                <button
                  type="button"
                  className="danger"
                  onClick={() => deleteItem(item)}
                  title="Excluir item"
                >
                  <Icon name="trash" size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
        {!projectItems.length && (
          <div className="empty">Nenhum item cadastrado neste projeto.</div>
        )}
        <div className="project-cost-subtotal">
          <span>
            Itens adicionais <b>{formatCurrency(additionalItemsTotal)}</b>
          </span>
          <span>
            Subtotal dos itens <b>{formatCurrency(itemsTotal)}</b>
          </span>
        </div>
      </section>
    </div>
  );
}

function CatalogModal({ item, token, onClose, onSaved, catalog, onEdit }) {
  const [form, setForm] = useState(
    item || {
      name: "",
      itemType: "material",
      unit: "un",
      defaultValue: 0,
      description: "",
    },
  );
  const save = async (e) => {
    e.preventDefault();
    const response = await fetch(
      `${apiBase}/resource-catalog${item ? `/${item.id}` : ""}`,
      {
        method: item ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      },
    );
    if (response.ok) onSaved();
  };
  return (
    <div className="modal-backdrop">
      <section className="catalog-dialog">
        <form className="modal catalog-form" onSubmit={save}>
          <div className="modal-head">
            <div>
              <h2>{item ? "Editar item salvo" : "Novo item do catálogo"}</h2>
              <p>
                Materiais e serviços disponíveis no preenchimento automático.
              </p>
            </div>
            <button type="button" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="form-grid">
            <label>
              Tipo
              <select
                value={form.itemType}
                onChange={(e) => setForm({ ...form, itemType: e.target.value })}
              >
                <option value="material">Material</option>
                <option value="service">Serviço</option>
              </select>
            </label>
            <label>
              Nome
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </label>
            <label>
              Unidade
              <input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              />
            </label>
            <label>
              Valor padrão
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.defaultValue}
                onChange={(e) =>
                  setForm({ ...form, defaultValue: Number(e.target.value) })
                }
              />
            </label>
          </div>
          <label>
            Descrição
            <textarea
              value={form.description || ""}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </label>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Cancelar
            </button>
            <button className="primary">Salvar item</button>
          </div>
        </form>
        {!item && (
          <aside className="catalog-list">
            <h3>Itens salvos</h3>
            {catalog.map((saved) => (
              <button key={saved.id} onClick={() => onEdit(saved)}>
                <div>
                  <strong>{saved.name}</strong>
                  <small>
                    {saved.itemType === "service" ? "Serviço" : "Material"} •{" "}
                    {saved.unit}
                  </small>
                </div>
                <b>{formatCurrency(saved.defaultValue)}</b>
                <Icon name="arrow" size={15} />
              </button>
            ))}
            {!catalog.length && <p>Nenhum item salvo.</p>}
          </aside>
        )}
      </section>
    </div>
  );
}

function PerformancePage({ token, canManage }) {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [functionId, setFunctionId] = useState("");
  const [collaboratorId, setCollaboratorId] = useState("");
  const [settings, setSettings] = useState(null);
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (functionId) params.set("functionId", functionId);
    fetch(`${apiBase}/performance/overview?${params}`, { headers })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error);
        return body;
      })
      .then(setData)
      .catch(() =>
        setError("Não foi possível carregar o AboutPlan Performance Score."),
      )
      .finally(() => setLoading(false));
  };
  useEffect(load, [token, functionId]);
  useEffect(() => {
    if (canManage)
      fetch(`${apiBase}/performance/settings`, { headers })
        .then((response) => (response.ok ? response.json() : null))
        .then(setSettings);
  }, [token, canManage]);
  const openDetail = (id) =>
    fetch(`${apiBase}/performance/technicians/${id}`, { headers })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then(setDetail)
      .catch(() => setError("Não foi possível abrir a composição desta nota."));
  const saveSettings = () => {
    const numeric = Object.fromEntries(
      Object.entries(settings)
        .filter(([key]) =>
          [
            "evaluationDays",
            "minimumSample",
            "planningWeight",
            "qualityWeight",
            "documentationWeight",
            "productivityWeight",
            "reliabilityWeight",
            "recentWeight",
            "middleWeight",
            "olderWeight",
          ].includes(key),
        )
        .map(([key, value]) => [key, Number(value)]),
    );
    fetch(`${apiBase}/performance/settings`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(numeric),
    })
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok) throw new Error(result.error);
        setSettings(result);
        alert(
          "Parâmetros salvos. Eles serão usados no próximo snapshot diário.",
        );
      })
      .catch((error) =>
        setError(
          error.message === "aps_weights_must_total_100"
            ? "Os pesos de cada grupo devem somar 100%."
            : "Não foi possível salvar os parâmetros.",
        ),
      );
  };
  if (loading && !data)
    return <div className="empty">Calculando performance operacional…</div>;
  if (error && !data) return <div className="empty error-text">{error}</div>;
  const collaborators = data?.ranking || [];
  const visibleCollaborators = collaborators
    .filter(
      (item) =>
        !collaboratorId || String(item.technicianId) === String(collaboratorId),
    )
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  const topTen = [...collaborators]
    .sort((a, b) => Number(b.score) - Number(a.score))
    .slice(0, 10);
  const dimensions = [
    ["Planejamento", "planningScore", "#3474f2"],
    ["Qualidade", "qualityScore", "#8b5cf6"],
    ["Documentação", "documentationScore", "#3bad87"],
    ["Produtividade", "productivityScore", "#efa331"],
    ["Consistência operacional", "reliabilityScore", "#e65e68"],
  ];
  return (
    <section className="aps-page">
      <div className="aps-hero">
        <div>
          <span>ABOUTPLAN PERFORMANCE SCORE</span>
          <h2>Performance com contexto, justiça e transparência.</h2>
          <p>
            APS de 0 a 100, ponderado por prazo, qualidade, documentação,
            produtividade compartilhada e consistência operacional.
          </p>
        </div>
        <div>
          <strong>{data?.kpis?.average || 0}</strong>
          <span>média da equipe</span>
          <small>Atualizado em {formatDate(data?.snapshotDate)}</small>
        </div>
      </div>
      <section className="panel aps-filters">
        <label>
          Colaborador
          <select
            value={collaboratorId}
            onChange={(event) => setCollaboratorId(event.target.value)}
          >
            <option value="">Todos os colaboradores</option>
            {collaborators.map((item) => (
              <option value={item.technicianId} key={item.technicianId}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Função
          <select
            value={functionId}
            onChange={(event) => {
              setFunctionId(event.target.value);
              setCollaboratorId("");
            }}
          >
            <option value="">Todas as funções</option>
            {data?.functions?.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={() => {
            setFunctionId("");
            setCollaboratorId("");
          }}
        >
          Limpar filtros
        </button>
      </section>
      <div className="aps-kpis">
        <SummaryBox
          tone="blue"
          label="APS médio"
          value={data?.kpis?.average || 0}
          detail={`${data?.kpis?.eligible || 0} pessoas ranqueadas`}
        />
        <SummaryBox
          tone="green"
          label="Alta performance"
          value={data?.kpis?.elite || 0}
          detail="APS igual ou superior a 95"
        />
        <SummaryBox
          tone="amber"
          label="Amostra insuficiente"
          value={data?.kpis?.insufficient || 0}
          detail="Não participam do ranking"
        />
        <SummaryBox
          tone="red"
          label="Acompanhamento"
          value={data?.kpis?.attention || 0}
          detail="APS abaixo de 70"
        />
      </div>
      <section className="panel aps-top-ten">
        <div>
          <span>TOP 10</span>
          <strong>Maiores Performance Scores</strong>
          <small>Visão resumida por função.</small>
        </div>
        <ol>
          {topTen.map((item, index) => (
            <li key={item.id} onClick={() => openDetail(item.technicianId)}>
              <b>{index + 1}</b>
              <span>
                <strong>{item.name}</strong>
                <small>{item.functionName}</small>
              </span>
              <em>{item.score}</em>
            </li>
          ))}
          {!topTen.length && (
            <li className="empty">Nenhuma nota disponível.</li>
          )}
        </ol>
      </section>
      <div className="aps-analytics">
        <section className="panel">
          <PanelTitle
            title="Composição da performance"
            subtitle="Média das cinco dimensões no período"
          />
          <div className="aps-dimension-bars">
            {dimensions.map(([label, key, color]) => (
              <div key={key}>
                <span>
                  {label}
                  <b>{data?.dimensionAverages?.[key] || 0}</b>
                </span>
                <div>
                  <i
                    style={{
                      width: `${data?.dimensionAverages?.[key] || 0}%`,
                      background: color,
                    }}
                  ></i>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="panel aps-method">
          <PanelTitle
            title="O que o APS avalia"
            subtitle="Entenda as cinco dimensões do Performance Score"
          />
          <ul>
            <li>
              <strong>Planejamento</strong>
              <small>
                Cumprimento das datas previstas e antecipação saudável das
                entregas.
              </small>
            </li>
            <li>
              <strong>Qualidade</strong>
              <small>
                Conformidade técnica, ausência de retrabalho e solução adequada.
              </small>
            </li>
            <li>
              <strong>Documentação</strong>
              <small>
                Relatórios, evidências e registros entregues corretamente e no
                prazo.
              </small>
            </li>
            <li>
              <strong>Produtividade</strong>
              <small>
                Volume de entregas ponderado pela complexidade e participação da
                equipe.
              </small>
            </li>
            <li>
              <strong>Consistência operacional</strong>
              <small>
                Regularidade dos resultados e estabilidade do desempenho ao
                longo do período.
              </small>
            </li>
          </ul>
        </section>
      </div>
      <section className="panel aps-ranking">
        <div className="aps-ranking-head">
          <div>
            <span>COLABORADORES</span>
            <h3>Performance da equipe</h3>
            <p>Todos os colaboradores em ordem alfabética.</p>
          </div>
        </div>
        <div className="aps-ranking-grid">
          {visibleCollaborators.map((item) => (
            <button onClick={() => openDetail(item.technicianId)} key={item.id}>
              <div className="aps-person">
                <span>
                  {item.photoDataUrl ? (
                    <img src={item.photoDataUrl} alt="" />
                  ) : (
                    initials(item.name)
                  )}
                </span>
                <div>
                  <strong>{item.name}</strong>
                  <small>
                    {item.functionName} • {item.eligibleActivities} entregas
                  </small>
                </div>
                <em
                  className={
                    Number(item.trend) > 0
                      ? "up"
                      : Number(item.trend) < 0
                        ? "down"
                        : ""
                  }
                >
                  {Number(item.trend) > 0 ? "+" : ""}
                  {item.trend}
                </em>
              </div>
              <div className="aps-score">
                <strong>{item.score}</strong>
                <span>
                  <b>{item.classification}</b>
                  <small>
                    {item.insufficientSample
                      ? "Amostra insuficiente"
                      : `${item.rankInFunction}º de ${item.functionPeerCount}`}
                  </small>
                </span>
              </div>
              <div className="aps-mini-bars">
                {dimensions.map(([label, key, color]) => (
                  <span title={label} key={key}>
                    <i
                      style={{ width: `${item[key]}%`, background: color }}
                    ></i>
                  </span>
                ))}
              </div>
            </button>
          ))}
          {!visibleCollaborators.length && (
            <div className="empty">
              Nenhum colaborador encontrado neste período.
            </div>
          )}
        </div>
      </section>
      {canManage && settings && (
        <details className="panel aps-settings">
          <summary>Parâmetros de governança do APS</summary>
          <div>
            <label>
              Janela (dias)
              <input
                type="number"
                value={settings.evaluationDays}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    evaluationDays: event.target.value,
                  })
                }
              />
            </label>
            <label>
              Amostra mínima
              <input
                type="number"
                value={settings.minimumSample}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    minimumSample: event.target.value,
                  })
                }
              />
            </label>
            {[
              ["planningWeight", "Planejamento"],
              ["qualityWeight", "Qualidade"],
              ["documentationWeight", "Documentação"],
              ["productivityWeight", "Produtividade"],
              ["reliabilityWeight", "Consistência operacional"],
            ].map(([key, label]) => (
              <label key={key}>
                {label} (%)
                <input
                  type="number"
                  value={settings[key]}
                  onChange={(event) =>
                    setSettings({ ...settings, [key]: event.target.value })
                  }
                />
              </label>
            ))}
            <button className="primary" onClick={saveSettings}>
              Salvar parâmetros
            </button>
          </div>
        </details>
      )}
      {detail && (
        <div className="modal-backdrop">
          <section className="aps-detail">
            <div className="modal-head">
              <div>
                <span className="record-code">APS EXPLICÁVEL</span>
                <h2>{detail.current.name}</h2>
                <p>
                  {detail.current.functionName} •{" "}
                  {detail.current.eligibleActivities} entregas avaliadas
                </p>
              </div>
              <button onClick={() => setDetail(null)}>×</button>
            </div>
            <div className="aps-detail-score">
              <strong>{detail.current.score}</strong>
              <div>
                <b>{detail.current.classification}</b>
                <span>
                  {detail.current.insufficientSample
                    ? "Amostra insuficiente para ranking"
                    : `${detail.current.rankInFunction}º de ${detail.current.functionPeerCount} na função`}
                </span>
              </div>
            </div>
            <div className="aps-detail-dimensions">
              {dimensions.map(([label, key, color]) => (
                <article key={key}>
                  <span>
                    {label}
                    <b>{detail.current[key]}</b>
                  </span>
                  <div>
                    <i
                      style={{
                        width: `${detail.current[key]}%`,
                        background: color,
                      }}
                    ></i>
                  </div>
                </article>
              ))}
            </div>
            <div className="aps-insights">
              <article>
                <h4>Pontos fortes</h4>
                {detail.strengths.map((item) => (
                  <p key={item[0]}>
                    {item[0]} <b>{item[1]}</b>
                  </p>
                ))}
              </article>
              <article>
                <h4>Oportunidades</h4>
                {detail.opportunities.map((item) => (
                  <p key={item[0]}>
                    {item[0]} <b>{item[1]}</b>
                  </p>
                ))}
              </article>
            </div>
            <div className="aps-impact-list">
              <h4>Entregas que compõem a nota</h4>
              {detail.impacts.slice(0, 20).map((item) => (
                <div key={`${item.activityType}-${item.activityId}`}>
                  <span>
                    {item.activityType === "service_order"
                      ? "OS"
                      : item.activityType === "preventive"
                        ? "Preventiva"
                        : "Projeto"}{" "}
                    #{item.activityId}
                    <small>
                      {formatDate(item.occurredDate)} • Complexidade{" "}
                      {item.complexityLevel}
                    </small>
                  </span>
                  <b>
                    P {item.planningScore} • Q {item.qualityScore} • D{" "}
                    {item.documentationScore}
                  </b>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function IndicatorsPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("month");
  const [client, setClient] = useState("all");
  const [unit, setUnit] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  useEffect(() => {
    fetch(`${apiBase}/indicators`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then(setData)
      .catch(() =>
        setError("Não foi possível carregar os indicadores gerenciais."),
      );
  }, [token]);
  if (error) return <div className="empty error-text">{error}</div>;
  if (!data) return <div className="empty">Carregando indicadores…</div>;
  const allRecords = [
    ...(data.projectPerformance || []),
    ...(data.serviceOrders || []),
    ...(data.maintenanceActivities || []),
    ...(data.tickets || []),
  ];
  const clients = [
    ...new Set(allRecords.map((item) => item.clientName).filter(Boolean)),
  ].sort();
  const units = [
    ...new Set(
      allRecords
        .filter((item) => client === "all" || item.clientName === client)
        .map((item) => item.unitName)
        .filter(Boolean),
    ),
  ].sort();
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  let start = null,
    end = today;
  if (period === "month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
  }
  if (period === "30") {
    start = new Date(today);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  }
  if (period === "90") {
    start = new Date(today);
    start.setDate(start.getDate() - 89);
    start.setHours(0, 0, 0, 0);
  }
  if (period === "custom") {
    start = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    end = dateTo ? new Date(`${dateTo}T23:59:59`) : today;
  }
  if (period === "all") {
    start = null;
    end = null;
  }
  const scoped = (items, dateField, endField) =>
    items.filter((item) => {
      if (client !== "all" && item.clientName !== client) return false;
      if (unit !== "all" && item.unitName !== unit) return false;
      const from = item[dateField] ? new Date(item[dateField]) : null;
      const until =
        endField && item[endField] ? new Date(item[endField]) : from;
      if (start && until && until < start) return false;
      if (end && from && from > end) return false;
      return true;
    });
  const projects = scoped(
    data.projectPerformance || [],
    "startDate",
    "endDate",
  );
  const orders = scoped(data.serviceOrders || [], "createdAt", "finishedAt");
  const maintenance = scoped(
    (data.maintenanceActivities || []).map((item) => ({
      ...item,
      indicatorDate: ["completed", "completed_with_issue"].includes(item.status)
        ? item.completedAt || item.plannedDate
        : item.plannedDate,
    })),
    "indicatorDate",
  );
  const tickets = scoped(data.tickets || [], "createdAt");
  const solvedStatuses = ["finished", "closed"];
  const activeStatuses = [
    "new",
    "analysis",
    "scheduled",
    "in_progress",
    "paused",
    "waiting_customer",
    "waiting_material",
  ];
  const solved = orders.filter((item) => solvedStatuses.includes(item.status));
  const active = orders.filter((item) => activeStatuses.includes(item.status));
  const overdue = active.filter(
    (item) => item.scheduledAt && new Date(item.scheduledAt) < today,
  );
  const pendingReports = solved.filter((item) =>
    ["pending", "draft"].includes(item.reportStatus),
  );
  const preventiveDone = maintenance.filter((item) =>
    ["completed", "completed_with_issue"].includes(item.status),
  );
  const maintenanceIssues = maintenance.filter(
    (item) => item.status === "completed_with_issue",
  );
  const openTickets = tickets.filter((item) => !item.serviceOrderId);
  const slaRisk = openTickets.filter(
    (item) => item.resolutionDueAt && new Date(item.resolutionDueAt) < today,
  );
  const projectRisks = projects.filter(
    (item) =>
      !["completed", "closed", "cancelled"].includes(item.status) &&
      Number(item.consumedPercent) > 100,
  );
  const completionRate = orders.length
    ? Math.round((solved.length * 100) / orders.length)
    : 0;
  const preventiveRate = maintenance.length
    ? Math.round((preventiveDone.length * 100) / maintenance.length)
    : 0;
  const documentationRate = solved.length
    ? Math.round(
        ((solved.length - pendingReports.length) * 100) / solved.length,
      )
    : 100;
  const orderGroups = [
    ["Concluídas", solved.length, "linear-gradient(90deg,#63e6d4,#16a979)"],
    [
      "Em andamento",
      active.filter((item) => item.status === "in_progress").length,
      "linear-gradient(90deg,#9b8cff,#665cff)",
    ],
    [
      "Programadas",
      active.filter((item) => item.status === "scheduled").length,
      "linear-gradient(90deg,#55d8ff,#3478f6,#665cff)",
    ],
    [
      "Aguardando",
      active.filter((item) =>
        ["waiting_customer", "waiting_material", "paused"].includes(
          item.status,
        ),
      ).length,
      "linear-gradient(90deg,#ffd166,#e99b26)",
    ],
    ["Atrasadas", overdue.length, "linear-gradient(90deg,#ff8b95,#e24f5c)"],
  ];
  const groupMax = Math.max(1, ...orderGroups.map(([, value]) => value));
  const clear = () => {
    setPeriod("month");
    setClient("all");
    setUnit("all");
    setDateFrom("");
    setDateTo("");
  };
  return (
    <section className="management-indicators">
      <div className="indicator-hero">
        <div>
          <span>INTELIGÊNCIA OPERACIONAL</span>
          <h2>Painel gerencial</h2>
          <p>
            Status, riscos e oportunidades de melhoria em uma visão executiva.
          </p>
        </div>
        <div>
          <b>{formatDate(today.toISOString())}</b>
          <small>Última atualização</small>
        </div>
      </div>
      <section className="panel indicator-filters">
        <label>
          Período
          <select
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          >
            <option value="month">Mês atual</option>
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
            <option value="all">Todo o período</option>
            <option value="custom">Personalizado</option>
          </select>
        </label>
        <label>
          Cliente
          <select
            value={client}
            onChange={(event) => {
              setClient(event.target.value);
              setUnit("all");
            }}
          >
            <option value="all">Todos os clientes</option>
            {clients.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Unidade
          <select
            value={unit}
            onChange={(event) => setUnit(event.target.value)}
          >
            <option value="all">Todas as unidades</option>
            {units.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        {period === "custom" && (
          <>
            <label>
              De
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(event) => setDateFrom(event.target.value)}
              />
            </label>
            <label>
              Até
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(event) => setDateTo(event.target.value)}
              />
            </label>
          </>
        )}
        <button onClick={clear}>Limpar filtros</button>
      </section>
      <div className="indicator-kpi-grid">
        <SummaryBox
          tone="blue"
          label="Taxa de conclusão das OS"
          value={`${completionRate}%`}
          detail={`${solved.length} de ${orders.length} ordens`}
        />
        <SummaryBox
          tone="amber"
          label="OS em andamento"
          value={active.length}
          detail={`${overdue.length} atrasada(s)`}
        />
        <SummaryBox
          tone="green"
          label="Preventivas realizadas"
          value={`${preventiveRate}%`}
          detail={`${preventiveDone.length} de ${maintenance.length}`}
        />
        <SummaryBox
          tone="red"
          label="Relatórios pendentes"
          value={pendingReports.length}
          detail={`${documentationRate}% de conformidade`}
        />
        <SummaryBox
          tone="purple"
          label="Chamados aguardando OS"
          value={openTickets.length}
          detail={`${slaRisk.length} fora do SLA`}
        />
        <SummaryBox
          tone="red"
          label="Projetos acima do prazo"
          value={projectRisks.length}
          detail={`${projects.length} projeto(s) analisado(s)`}
        />
        <SummaryBox
          tone="amber"
          label="Preventivas com inconformidade"
          value={maintenanceIssues.length}
          detail={`${preventiveDone.length ? Math.round((maintenanceIssues.length * 100) / preventiveDone.length) : 0}% das realizadas`}
        />
        <SummaryBox
          tone="green"
          label="Documentação regular"
          value={`${documentationRate}%`}
          detail={`${solved.length - pendingReports.length} relatórios entregues`}
        />
      </div>
      <div className="indicator-management-grid">
        <section className="panel indicator-status-panel">
          <PanelTitle
            title="Distribuição das ordens de serviço"
            subtitle="Volume por situação no período"
          />
          <div className="indicator-status-bars">
            {orderGroups.map(([label, value, color]) => (
              <div key={label}>
                <span>
                  {label}
                  <b>{value}</b>
                </span>
                <div title={`${label}: ${value} ordem(ns)`}>
                  <i
                    style={{
                      width: `${(value / groupMax) * 100}%`,
                      background: color,
                    }}
                  ></i>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="panel indicator-documentation">
          <PanelTitle
            title="Conformidade documental"
            subtitle="Relatórios entregues após a conclusão"
          />
          <div>
            <div
              className="indicator-donut"
              style={{
                background: `conic-gradient(from 0deg,#63e6d4 0%,#16a979 ${documentationRate}%,#edf1f6 ${documentationRate}%,#edf1f6 100%)`,
              }}
              title={`${documentationRate}% de conformidade documental`}
            >
              <span>
                <b>{documentationRate}%</b>regular
              </span>
            </div>
            <ul>
              <li>
                <i className="green"></i>Entregues{" "}
                <b>{solved.length - pendingReports.length}</b>
              </li>
              <li>
                <i className="amber"></i>Pendentes{" "}
                <b>{pendingReports.length}</b>
              </li>
              <li>
                <i className="blue"></i>Total concluído <b>{solved.length}</b>
              </li>
            </ul>
          </div>
        </section>
      </div>
      <section className="panel indicator-project-panel">
        <PanelTitle
          title="Consumo de prazo dos projetos"
          subtitle="Dias trabalhados em relação aos dias estimados"
        />
        <div className="indicator-bars">
          {projects.slice(0, 15).map((item) => (
            <div
              className={Number(item.consumedPercent) > 100 ? "risk" : ""}
              key={item.id || item.name}
            >
              <span>
                <b>{item.projectNumber || item.name}</b>
                <small>
                  {item.name} • {item.clientName || "Sem cliente"}
                </small>
              </span>
              <div
                title={`${item.projectNumber || item.name}: ${item.workedDays} de ${item.estimatedDays} dias (${Math.round(Number(item.consumedPercent) || 0)}%)`}
              >
                <i
                  style={{
                    width: `${Math.min(Number(item.consumedPercent) || 0, 100)}%`,
                  }}
                ></i>
              </div>
              <b>
                {item.workedDays}/{item.estimatedDays}d
              </b>
            </div>
          ))}
          {!projects.length && (
            <div className="empty">Nenhum projeto no período selecionado.</div>
          )}
        </div>
      </section>
      <section className="indicator-insights">
        <div>
          <span>PRIORIDADES GERENCIAIS</span>
          <h3>Onde atuar primeiro</h3>
        </div>
        <article className={overdue.length ? "critical" : "ok"}>
          <Icon name={overdue.length ? "clock" : "check"} size={18} />
          <div>
            <strong>
              {overdue.length
                ? `${overdue.length} OS atrasada(s)`
                : "Agenda de OS sob controle"}
            </strong>
            <p>
              {overdue.length
                ? "Revisar responsáveis e reagendar atendimentos vencidos."
                : "Nenhuma OS programada está vencida no período."}
            </p>
          </div>
        </article>
        <article className={pendingReports.length ? "warning" : "ok"}>
          <Icon name="file" size={18} />
          <div>
            <strong>
              {pendingReports.length
                ? `${pendingReports.length} relatório(s) pendente(s)`
                : "Documentação em dia"}
            </strong>
            <p>
              {pendingReports.length
                ? "Priorizar a baixa documental para elevar a conformidade."
                : "Todas as OS concluídas possuem documentação."}
            </p>
          </div>
        </article>
        <article className={projectRisks.length ? "critical" : "ok"}>
          <Icon name="trend" size={18} />
          <div>
            <strong>
              {projectRisks.length
                ? `${projectRisks.length} projeto(s) acima do estimado`
                : "Projetos dentro do consumo previsto"}
            </strong>
            <p>
              {projectRisks.length
                ? "Analisar causas de extrapolação e impacto no planejamento."
                : "Nenhum projeto excedeu os dias estimados."}
            </p>
          </div>
        </article>
      </section>
    </section>
  );
}

function MaintenanceCalendar({ activities, onOpen, resident = false }) {
  const today = new Date().toLocaleDateString("sv-SE");
  const [selectedDay, setSelectedDay] = useState("");
  const availableMonths = [...new Set(activities.map((item) => String(item.plannedDate || "").slice(0, 7)).filter((value) => /^\d{4}-\d{2}$/.test(value)))].sort();
  const [chosenMonth, setChosenMonth] = useState("");
  const month = chosenMonth || availableMonths.find((value) => value >= today.slice(0, 7)) || availableMonths[0] || today.slice(0, 7);
  const monthDate = new Date(`${month}-01T12:00:00`);
  const firstDay = monthDate.getDay();
  const numberOfDays = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const gridLength = Math.ceil((firstDay + numberOfDays) / 7) * 7;
  const calendarDays = Array.from({ length: gridLength }, (_, index) => {
    const date = new Date(monthDate);
    date.setDate(index - firstDay + 1);
    return { key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`, day: date.getDate(), current: date.getMonth() === monthDate.getMonth() };
  });
  const monthly = activities.filter((item) => String(item.plannedDate || "").slice(0, 7) === month);
  const firstActivityDay = [...monthly].map((item) => String(item.plannedDate || "").slice(0, 10)).filter(Boolean).sort()[0];
  const activeDay = selectedDay.slice(0, 7) === month ? selectedDay : (month === today.slice(0, 7) ? today : firstActivityDay || `${month}-01`);
  const dayActivities = monthly.filter((item) => String(item.plannedDate || "").slice(0, 10) === activeDay);
  const activeWeekStart = Math.floor((Number(activeDay.slice(8, 10)) - 1) / 7) * 7;
  const weekDays = calendarDays.filter((day) => day.current).slice(activeWeekStart, activeWeekStart + 7);
  const shiftWeek = (amount) => {
    const nextDay = Math.min(numberOfDays, Math.max(1, Number(activeDay.slice(8, 10)) + amount * 7));
    setSelectedDay(`${month}-${String(nextDay).padStart(2, "0")}`);
  };
  const shift = (amount) => {
    const date = new Date(monthDate);
    date.setMonth(date.getMonth() + amount);
    setChosenMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
  };
  const tone = (item) => item.status === "completed" || item.status === "completed_with_issue" ? "done" : item.status === "waiting_area_release" ? "waiting" : item.status === "in_progress" ? "progress" : (item.effectiveDueDate || item.plannedDate)?.slice(0, 10) <= today ? "late" : "planned";
  const statusLabel = (item) => ({ done: "Concluída", waiting: "Aguardando liberação da área", progress: "Em andamento", late: "Hoje / atraso", planned: "Programada" })[tone(item)];
  return (
    <div className="portal-preventives maintenance-monthly-calendar">
      <div className="portal-preventives-toolbar"><div className="portal-preventives-month-control"><button type="button" aria-label="Mês anterior" onClick={() => shift(-1)}>‹</button><strong>{monthDate.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</strong><button type="button" aria-label="Próximo mês" onClick={() => shift(1)}>›</button></div><span>{monthly.length} atividade(s) no mês</span></div>
      {resident && <div className="resident-preventive-day-view">
        <div className="resident-preventive-day-heading"><strong>Agenda do dia</strong><span>{new Date(`${activeDay}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</span></div>
        <div className="resident-preventive-week"><button type="button" aria-label="Semana anterior" disabled={activeWeekStart === 0} onClick={() => shiftWeek(-1)}>‹</button><div className="resident-preventive-day-strip">{weekDays.map((day) => <button type="button" key={day.key} className={day.key === activeDay ? "active" : ""} onClick={() => setSelectedDay(day.key)}><small>{new Date(`${day.key}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}</small><b>{day.day}</b><i className={monthly.some((item) => String(item.plannedDate || "").slice(0, 10) === day.key) ? "has-activity" : ""} /></button>)}</div><button type="button" aria-label="Próxima semana" disabled={activeWeekStart + 7 >= numberOfDays} onClick={() => shiftWeek(1)}>›</button></div>
        <div className="resident-preventive-day-count">{dayActivities.length} atividade(s) em {new Date(`${activeDay}T12:00:00`).toLocaleDateString("pt-BR")}</div>
        {dayActivities.length ? <div className="resident-preventive-day-list">{dayActivities.map((item) => <button type="button" key={item.id} className={`resident-preventive-day-item ${tone(item)}`} onClick={() => onOpen(item)}><span><b>{item.planName || item.assetName || "Preventiva"}</b><small>{item.clientName} · {item.unitName}</small></span><em>{statusLabel(item)}</em><Icon name="chevron" size={15} /></button>)}</div> : <p className="resident-preventive-day-empty">Nenhuma preventiva neste dia.</p>}
        <div className="resident-preventive-calendar-intro"><strong>Calendário mensal</strong><span>Programação completa do mês abaixo</span></div>
      </div>}
      <div className="portal-preventives-calendar"><div className="portal-preventives-calendar-head"><div><strong>Calendário mensal</strong><span>Selecione uma atividade para ver os detalhes</span></div><div className="portal-preventives-legend"><span className="done">Concluída</span><span className="late">Hoje / atraso</span><span className="waiting">Aguardando área</span><span className="progress">Em andamento</span><span className="planned">Programada</span></div></div><div className="portal-preventives-weekdays">{["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => <span key={day}>{day}</span>)}</div><div className="portal-preventives-grid">{calendarDays.map((day) => <div key={day.key} className={`portal-preventives-day ${day.current ? "" : "outside"} ${day.key === today ? "today" : ""}`}><span className="portal-preventives-day-number">{day.day}</span>{day.current && monthly.filter((item) => String(item.plannedDate || "").slice(0, 10) === day.key).map((item) => <button type="button" key={item.id} className={`portal-preventives-event ${tone(item)}`} onClick={() => onOpen(item)} title={`${item.planName} · ${statusLabel(item)}`}><strong>{item.planName}</strong><small>{item.assetName} · {statusLabel(item)}</small></button>)}</div>)}</div></div>
    </div>
  );
}

const equipmentResults = [
  ["conforming", "Conforme"],
  ["non_conforming", "Não conforme"],
  ["not_applicable", "Não aplicável"],
  ["removed_for_maintenance", "Retirado para manutenção"],
  ["damaged", "Danificado"],
  ["replaced", "Substituído"],
];
const equipmentActions = [
  ["cleaning", "Limpeza"],
  ["focus_adjustment", "Ajuste de foco"],
  ["position_adjustment", "Ajuste de posição"],
  ["connectorization_redone", "Conectorização refeita"],
  ["communication_test", "Teste de comunicação"],
  ["alignment", "Alinhamento"],
  ["fastening", "Reaperto / fixação"],
  ["power_check", "Teste de alimentação"],
  ["cabling_check", "Inspeção do cabeamento"],
  ["configuration_adjustment", "Ajuste de configuração"],
  ["firmware_update", "Atualização de firmware"],
  ["component_replacement", "Troca de componente"],
  ["lubrication", "Lubrificação"],
  ["other", "Outra ação"],
];

function EquipmentQrScanner({ equipment, onClose, onRead }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [manual, setManual] = useState("");
  const [message, setMessage] = useState(
    "Aponte a câmera para o QR Code do equipamento.",
  );
  useEffect(() => {
    let stopped = false;
    let timer;
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia || !window.BarcodeDetector) {
        setMessage(
          "Leitura automática indisponível. Digite ou cole a TAG abaixo.",
        );
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
        if (stopped) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        const scan = async () => {
          if (stopped) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes[0]?.rawValue) {
              onRead(codes[0].rawValue);
              return;
            }
          } catch {}
          timer = setTimeout(scan, 450);
        };
        scan();
      } catch {
        setMessage(
          "Não foi possível acessar a câmera. Digite ou cole a TAG abaixo.",
        );
      }
    };
    start();
    return () => {
      stopped = true;
      clearTimeout(timer);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [onRead]);
  return (
    <div className="qr-reader-backdrop">
      <section className="qr-reader">
        <div className="modal-head">
          <div>
            <span className="record-code">VALIDAR EQUIPAMENTO</span>
            <h3>{equipment.name}</h3>
            <p>TAG esperada: {equipment.tag}</p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <video ref={videoRef} muted playsInline />
        <p>{message}</p>
        <div className="qr-manual">
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Digite ou cole a TAG"
          />
          <button className="primary" onClick={() => onRead(manual)}>
            Validar TAG
          </button>
        </div>
      </section>
    </div>
  );
}

function ChecklistModal({ activity, token, onClose, onSaved, onStatusChanged, resident = false }) {
  const completed = ["completed", "completed_with_issue"].includes(activity.status);
  const [currentStatus, setCurrentStatus] = useState(activity.status);
  const awaitingAreaRelease = currentStatus === "waiting_area_release";
  const [equipment, setEquipment] = useState([]);
  const [equipmentForms, setEquipmentForms] = useState({});
  const [dirtyEquipmentIds, setDirtyEquipmentIds] = useState([]);
  const [scanner, setScanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamIds, setTeamIds] = useState(
    (activity.team || []).map((item) => item.id),
  );
  const [saving, setSaving] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [createCorrective, setCreateCorrective] = useState(false);
  const [reportPending, setReportPending] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [rescheduleReasonOpen, setRescheduleReasonOpen] = useState(false);
  const [rescheduleOtherReason, setRescheduleOtherReason] = useState("");
  const [rescheduleServiceOrder, setRescheduleServiceOrder] = useState("");
  const [rescheduleDescription, setRescheduleDescription] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleError, setRescheduleError] = useState("");
  const [error, setError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  useEffect(() => {
    setLoading(true);
    fetch(
      `${apiBase}/maintenance/activities/${activity.id}/checklist?equipmentInspection=1`,
      { cache: "no-store", headers: { Authorization: `Bearer ${token}` } },
    )
      .then(async (response) => {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then((data) => {
        const loadedEquipment = Array.isArray(data?.equipment)
          ? data.equipment
          : [];
        setEquipment(loadedEquipment);
        setEquipmentForms(
          Object.fromEntries(
            loadedEquipment.map((item) => [
              item.id,
              {
                result: item.result || "",
                actions: item.actions || [],
                notes: item.notes || "",
                photos: item.photos || [],
                qrVerifiedAt: item.qrVerifiedAt || "",
                qrTag: "",
              },
            ]),
          ),
        );
        setDirtyEquipmentIds([]);
        setError(
          loadedEquipment.length
            ? ""
            : "Nenhum equipamento ativo foi encontrado no grupo desta preventiva.",
        );
      })
      .catch(() =>
        setError("Não foi possível carregar os equipamentos desta preventiva."),
      )
      .finally(() => setLoading(false));
  }, [activity.id, token]);
  useEffect(() => {
    fetch(`${apiBase}/technicians`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) =>
        setTeamOptions(
          data.filter(
            (item) =>
              item.status === "active" &&
              (!item.isResident ||
                (activity.team || []).some((member) => member.id === item.id)),
          ),
        ),
      )
      .catch(() => setTeamOptions([]));
  }, [token]);
  const setEquipmentForm = (id, field, value) => {
    setSavedMessage("");
    setEquipmentForms((current) => ({
      ...current,
      [id]: { ...current[id], [field]: value },
    }));
    setDirtyEquipmentIds((current) => current.includes(id) ? current : [...current, id]);
  };
  const toggleAction = (id, value) => {
    const current = equipmentForms[id]?.actions || [];
    setEquipmentForm(
      id,
      "actions",
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };
  const addPhotos = (id, files) => {
    const accepted = [...files]
      .filter(
        (file) =>
          /^image\/(jpeg|png|webp)$/.test(file.type) && file.size <= 3_500_000,
      )
      .slice(0, 3 - (equipmentForms[id]?.photos?.length || 0));
    Promise.all(
      accepted.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
          }),
      ),
    ).then((photos) =>
      setEquipmentForm(id, "photos", [
        ...(equipmentForms[id]?.photos || []),
        ...photos,
      ]),
    );
  };
  const normalizeQr = (value) => {
    try {
      const url = new URL(value);
      return (
        url.searchParams.get("equipmentTag") ||
        url.searchParams.get("tag") ||
        value
      );
    } catch {
      return String(value || "").trim();
    }
  };
  const validateQr = (value) => {
    const tag = normalizeQr(value);
    if (tag !== scanner.tag) {
      setError(`O QR Code lido não pertence a ${scanner.name}.`);
      return;
    }
    setEquipmentForm(scanner.id, "qrTag", tag);
    setEquipmentForm(scanner.id, "qrVerifiedAt", new Date().toISOString());
    setScanner(null);
    setError("");
  };
  const hasIssue = Object.values(equipmentForms).some((response) =>
    [
      "non_conforming",
      "removed_for_maintenance",
      "damaged",
      "replaced",
    ].includes(response.result),
  );
  const saveTeam = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/maintenance/activities/${activity.id}/team`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ technicianIds: teamIds }),
        },
      );
      if (!response.ok) throw new Error();
    } catch {
      setError(
        "Seu perfil não permite alterar a equipe desta preventiva ou a atualização falhou.",
      );
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    if (awaitingAreaRelease) {
      setError("Registre a liberação da área antes de concluir esta preventiva.");
      return;
    }
    if (equipment.some((item) => !equipmentForms[item.id]?.result)) {
      setError("Avalie todos os equipamentos do grupo antes de concluir.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      await Promise.all(
        equipment.map((item) =>
          fetch(
            `${apiBase}/maintenance/activities/${activity.id}/equipment/${item.id}`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify(equipmentForms[item.id]),
            },
          ).then(async (r) => {
            if (!r.ok) {
              const result = await r.json().catch(() => ({}));
              throw new Error(result.error || "equipment_save_failed");
            }
          }),
        ),
      );
      if (hasIssue && createCorrective)
        await fetch(
          `${apiBase}/maintenance/activities/${activity.id}/corrective`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              description: "Corretiva gerada a partir de item não conforme.",
              priority: "high",
              reportPending,
            }),
          },
        ).then((r) => {
          if (!r.ok) throw new Error();
        });
      else
        await fetch(`${apiBase}/maintenance/activities/${activity.id}/status`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            status: hasIssue ? "completed_with_issue" : "completed",
            reportPending,
            reportStatus: reportPending ? "pending" : "submitted",
          }),
        }).then((r) => {
          if (!r.ok) throw new Error();
        });
      onSaved();
    } catch (error) {
      setError(
        error.message === "equipment_qr_mismatch"
          ? "O QR Code informado não corresponde ao equipamento."
          : "Não foi possível salvar a inspeção preventiva.",
      );
    } finally {
      setSaving(false);
    }
  };
  const saveChanges = async () => {
    if (!dirtyEquipmentIds.length) return;
    const changed = equipment.filter((item) => dirtyEquipmentIds.includes(item.id));
    if (changed.some((item) => !equipmentForms[item.id]?.result)) {
      setError("Selecione o resultado dos equipamentos alterados antes de salvar.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      for (const item of changed) {
        const response = await fetch(`${apiBase}/maintenance/activities/${activity.id}/equipment/${item.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(equipmentForms[item.id]),
        });
        if (!response.ok) throw new Error();
        setDirtyEquipmentIds((current) => current.filter((id) => id !== item.id));
      }
      setSavedMessage("Alterações salvas. O relatório PDF já inclui os novos dados.");
    } catch {
      setError("Não foi possível salvar as alterações. Verifique sua conexão e tente novamente.");
    } finally {
      setSaving(false);
    }
  };
  const downloadReport = async () => {
    setExportingReport(true);
    setError("");
    try {
      await downloadPreventiveReportPdf({ token, activityId: activity.id, apiBase });
    } catch {
      setError("Não foi possível gerar o relatório desta preventiva.");
    } finally {
      setExportingReport(false);
    }
  };
  const close = () => {
    if (dirtyEquipmentIds.length && !window.confirm("Há alterações não salvas. Deseja fechar e descartá-las?")) return;
    onClose();
  };
  const saveReschedule = async (event) => {
    event.preventDefault();
    if (!rescheduleReason || !rescheduleDescription.trim() || !rescheduleDate ||
        (rescheduleReason === "other" && !rescheduleOtherReason.trim()) ||
        (rescheduleReason === "corrective_activity" && !rescheduleServiceOrder.trim())) {
      setRescheduleError("Preencha todos os campos obrigatórios.");
      return;
    }
    setSaving(true);
    setRescheduleError("");
    try {
      const response = await fetch(`${apiBase}/maintenance/activities/${activity.id}/reschedule`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rescheduleReason, otherReason: rescheduleReason === "other" ? rescheduleOtherReason.trim() : "", serviceOrderCode: rescheduleReason === "corrective_activity" ? rescheduleServiceOrder.trim() : "", description: rescheduleDescription.trim(), newDate: rescheduleDate }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const messages = { maintenance_reschedule_non_working_day: "Selecione uma terça, quarta, quinta ou sexta que não seja feriado.", maintenance_reschedule_service_order_invalid: "A OS informada não pertence a este cliente e unidade.", maintenance_reschedule_date_past: "Selecione uma data atual ou futura.", maintenance_reschedule_same_date: "Escolha uma data diferente da atual.", maintenance_reschedule_status_changed: "A situação da preventiva mudou. Atualize a página e tente novamente.", maintenance_reschedule_status_invalid: "Esta atividade não pode mais ser reagendada.", maintenance_reschedule_details_required: "Confira o motivo, a descrição e os campos obrigatórios." };
        throw new Error(messages[data.error] || (response.status === 401 ? "Sua sessão expirou. Entre novamente e tente reagendar." : response.status === 403 || response.status === 404 ? "Seu perfil não tem acesso a esta preventiva ou unidade." : "Não foi possível reagendar a preventiva. Tente novamente."));
      }
      setCurrentStatus("scheduled");
      setRescheduleOpen(false);
      setSavedMessage(`Preventiva reagendada para ${new Date(`${rescheduleDate}T12:00:00`).toLocaleDateString("pt-BR")}.`);
      onStatusChanged?.();
    } catch (requestError) {
      setRescheduleError(requestError.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <section className="checklist-modal">
        <div className="modal-head">
          <div>
            <span className="record-code">
              MP-{String(activity.id).padStart(4, "0")}
            </span>
            <h2>{activity.planName}</h2>
            <p>
              {activity.clientName} • {activity.assetName}
            </p>
          </div>
          <button onClick={close}>×</button>
        </div>
        {!completed && (["planned", "scheduled"].includes(currentStatus) || awaitingAreaRelease) && (
          <div className={`preventive-area-release ${awaitingAreaRelease ? "waiting" : ""}`}>
            <div><strong>{awaitingAreaRelease ? "Aguardando liberação da área" : "Precisa mudar a data?"}</strong><span>{awaitingAreaRelease ? "Registre uma nova data quando o acesso estiver definido." : "Informe o motivo e a nova data para atualizar a agenda da unidade."}</span></div>
            <button type="button" onClick={() => { setRescheduleError(""); setRescheduleOpen(true); }} disabled={saving}>Reagendar</button>
          </div>
        )}
        {rescheduleOpen && <div className="preventive-reschedule-backdrop" role="presentation"><form className="preventive-reschedule-dialog" onSubmit={saveReschedule} onClick={(event) => { if (rescheduleReasonOpen && !event.target.closest(".preventive-reason-select")) setRescheduleReasonOpen(false); }} onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); setRescheduleReasonOpen(false); } }} role="dialog" aria-modal="true" aria-labelledby="preventive-reschedule-title">
          <div className="preventive-reschedule-head"><div><span className="record-code">MP-{String(activity.id).padStart(4, "0")}</span><h3 id="preventive-reschedule-title">Reagendar preventiva</h3><p>{activity.clientName} · {activity.unitName || activity.assetName}</p></div><button type="button" onClick={() => setRescheduleOpen(false)} aria-label="Fechar">×</button></div>
          <div className="preventive-reschedule-fields">
            <div className="preventive-reason-field"><span className="field-label">Motivo <em>*</em></span><div className="preventive-reason-select"><button type="button" className="preventive-reason-trigger" aria-expanded={rescheduleReasonOpen} aria-haspopup="listbox" onClick={() => setRescheduleReasonOpen((value) => !value)}><span className={rescheduleReason ? "" : "placeholder"}>{({ corrective_activity: "Atividade corretiva na data", weather: "Eventos climáticos", area_release: "Liberação de área", other: "Outros" })[rescheduleReason] || "Selecione o motivo"}</span><svg className="preventive-reason-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg></button>{rescheduleReasonOpen && <div className="preventive-reason-options" role="listbox" aria-label="Motivo do reagendamento">{[["corrective_activity", "Atividade corretiva na data"], ["weather", "Eventos climáticos"], ["area_release", "Liberação de área"], ["other", "Outros"]].map(([value, label]) => <button type="button" role="option" aria-selected={rescheduleReason === value} className={rescheduleReason === value ? "selected" : ""} key={value} onClick={() => { setRescheduleReason(value); setRescheduleReasonOpen(false); setRescheduleError(""); }}>{label}</button>)}</div>}</div></div>
            {rescheduleReason === "corrective_activity" && <label><span className="field-label">OS relacionada <em>*</em></span><input value={rescheduleServiceOrder} onChange={(event) => setRescheduleServiceOrder(event.target.value)} placeholder="Ex.: OS-0021" required /></label>}
            {rescheduleReason === "other" && <label><span className="field-label">Qual o motivo? <em>*</em></span><input value={rescheduleOtherReason} onChange={(event) => setRescheduleOtherReason(event.target.value)} maxLength={200} required /></label>}
            <label><span className="field-label">Descrição <em>*</em></span><textarea value={rescheduleDescription} onChange={(event) => setRescheduleDescription(event.target.value)} placeholder="Explique por que a atividade precisa ser reagendada." maxLength={3000} rows={3} required /></label>
            <label><span className="field-label">Nova data da atividade <em>*</em></span><input type="date" value={rescheduleDate} onChange={(event) => setRescheduleDate(event.target.value)} required /></label>
          </div>
          {rescheduleError && <p className="preventive-reschedule-error" role="alert">{rescheduleError}</p>}
          <div className="preventive-reschedule-actions"><button type="button" onClick={() => setRescheduleOpen(false)}>Cancelar</button><button className="primary" type="submit" disabled={saving}>{saving ? "Salvando…" : "Confirmar reagendamento"}</button></div>
        </form></div>}
        <div className="checklist-progress">
          <span>Equipamentos do grupo avaliados</span>
          <b>
            {Object.values(equipmentForms).filter((r) => r.result).length} de{" "}
            {equipment.length}
          </b>
        </div>
        {!resident && <fieldset className="service-issue-selector">
          <legend>Equipe participante</legend>
          <p>O responsável principal permanece inalterado.</p>
          <div>
            {teamOptions.map((person) => (
              <label
                className={teamIds.includes(person.id) ? "selected" : ""}
                key={person.id}
              >
                <input
                  type="checkbox"
                  checked={teamIds.includes(person.id)}
                  onChange={() =>
                    setTeamIds((current) =>
                      current.includes(person.id)
                        ? current.filter((id) => id !== person.id)
                        : [...current, person.id],
                    )
                  }
                />
                <span>{person.name}</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="secondary-button team-save-button"
            disabled={saving}
            onClick={saveTeam}
          >
            <Icon name="check" size={15} />
            {saving ? "Salvando…" : "Salvar equipe"}
          </button>
        </fieldset>}
        {loading && (
          <div className="checklist-loading">
            <span></span>
            <strong>Carregando equipamentos do grupo…</strong>
          </div>
        )}
        {!loading && (
          <div className="equipment-inspections">
            {equipment.map((item, index) => {
              const form = equipmentForms[item.id] || {};
              return (
                <article
                  className={`equipment-inspection ${form.result ? "filled" : ""}`}
                  key={item.id}
                >
                  <header>
                    <span>{index + 1}</span>
                    <div>
                      <strong>{item.name}</strong>
                      <small>
                        {item.tag} •{" "}
                        {[item.assetType, item.brand, item.model]
                          .filter(Boolean)
                          .join(" • ")}
                      </small>
                      <small>
                        {[item.area, item.location].filter(Boolean).join(" — ")}
                      </small>
                    </div>
                    <button
                      className={form.qrVerifiedAt ? "qr-verified" : ""}
                      onClick={() => setScanner(item)}
                    >
                      <Icon name="scan" size={15} />
                      {form.qrVerifiedAt ? "QR validado" : "Ler QR Code"}
                    </button>
                  </header>
                  <div className="equipment-result-buttons">
                    {equipmentResults.map(([value, label]) => (
                      <button
                        className={form.result === value ? value : ""}
                        onClick={() =>
                          setEquipmentForm(item.id, "result", value)
                        }
                        key={value}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="equipment-actions">
                    <b>Ações realizadas</b>
                    <div>
                      {equipmentActions.map(([value, label]) => (
                        <label
                          className={
                            (form.actions || []).includes(value)
                              ? "selected"
                              : ""
                          }
                          key={value}
                        >
                          <input
                            type="checkbox"
                            checked={(form.actions || []).includes(value)}
                            onChange={() => toggleAction(item.id, value)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <textarea
                    placeholder="Observações sobre este equipamento…"
                    value={form.notes || ""}
                    onChange={(e) =>
                      setEquipmentForm(item.id, "notes", e.target.value)
                    }
                  />
                  <AiReviewButton
                    token={token}
                    value={form.notes || ""}
                    onApply={(value) =>
                      setEquipmentForm(item.id, "notes", value)
                    }
                    context={`observação de manutenção preventiva do equipamento ${item.name}`}
                  />
                  <div className="equipment-evidence">
                    <label>
                      <Icon name="camera" size={15} />
                      Adicionar fotos
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={(e) => addPhotos(item.id, e.target.files)}
                      />
                    </label>
                    <span>Até 3 fotos por equipamento</span>
                    <div>
                      {(form.photos || []).map((photo, photoIndex) => (
                        <figure key={photoIndex}>
                          <img
                            src={photo}
                            alt={`Evidência ${photoIndex + 1}`}
                          />
                          <button
                            onClick={() =>
                              setEquipmentForm(
                                item.id,
                                "photos",
                                form.photos.filter((_, i) => i !== photoIndex),
                              )
                            }
                          >
                            ×
                          </button>
                        </figure>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {hasIssue && !completed && (
          <label className="corrective-option">
            <input
              type="checkbox"
              checked={createCorrective}
              onChange={(e) => setCreateCorrective(e.target.checked)}
            />
            <span>
              <strong>Gerar OS corretiva</strong>
              <small>A OS ficará vinculada a esta preventiva.</small>
            </span>
          </label>
        )}
        {!completed && <label className="report-pending-option">
          <input
            type="checkbox"
            checked={reportPending}
            onChange={(e) => setReportPending(e.target.checked)}
          />
          <span>
            <strong>Manutenção realizada, relatório pendente</strong>
            <small>
              A atividade será baixada e continuará na central de pendências até
              o envio do relatório.
            </small>
          </span>
        </label>}
        {error && <div className="login-error">{error}</div>}
        {savedMessage && <p className="checklist-save-confirmation" role="status">{savedMessage}</p>}
        <div className="modal-actions">
          <button onClick={close}>{completed ? "Fechar" : "Cancelar"}</button>
          {completed && <button className="secondary-button" onClick={saveChanges} disabled={saving || !dirtyEquipmentIds.length}>{saving ? "Salvando…" : "Salvar alterações"}</button>}
          {completed ? <button className="primary" onClick={downloadReport} disabled={exportingReport || dirtyEquipmentIds.length > 0} title={dirtyEquipmentIds.length ? "Salve as alterações antes de baixar o relatório" : undefined}>
            <Icon name="download" size={16} /> {exportingReport ? "Gerando relatório…" : "Baixar relatório PDF"}
          </button> : <button className="primary" onClick={save} disabled={saving || awaitingAreaRelease}>
            {saving ? "Salvando…" : "Concluir manutenção"}
          </button>}
        </div>
      </section>
      {scanner && (
        <EquipmentQrScanner
          equipment={scanner}
          onClose={() => setScanner(null)}
          onRead={validateQr}
        />
      )}
    </div>
  );
}

function ReportsPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [client, setClient] = useState("all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [downloading, setDownloading] = useState("");
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyClient, setHistoryClient] = useState("all");
  const [historyType, setHistoryType] = useState("all");
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const load = () =>
    fetch(`${apiBase}/reports/pending-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() =>
        setError("Não foi possível carregar a central de relatórios."),
      );
  useEffect(() => {
    load();
  }, [token]);
  const reportCards = data
    ? [
        ["Relatórios de obra", data.construction, "folder"],
        ["Relatórios de OS", data.serviceOrders, "clipboard"],
        ["Relatórios de manutenção", data.maintenance, "wrench"],
      ]
    : [];
  const exportCsv = async () => {
    const response = await fetch(`${apiBase}/reports/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = "relatorios-aboutplan.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const clients = [
    ...new Set(
      (data?.allItems || []).map((item) => item.clientName).filter(Boolean),
    ),
  ].sort();
  const normalized = query.trim().toLowerCase();
  const filtered = (data?.allItems || []).filter((item) => {
    const text = [
      item.clientName,
      item.unitName,
      item.reference,
      item.orderNumber,
      item.ticketNumber,
      item.ticketTitle,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return (
      (!normalized || text.includes(normalized)) &&
      (client === "all" || item.clientName === client) &&
      (type === "all" || item.reportType === type) &&
      (status === "all" ||
        (status === "pending"
          ? ["pending", "draft"].includes(item.reportStatus)
          : ["submitted", "approved"].includes(item.reportStatus))) &&
      (!dateFrom || String(item.performedAt || "").slice(0, 10) >= dateFrom) &&
      (!dateTo || String(item.performedAt || "").slice(0, 10) <= dateTo)
    );
  });
  const pending = filtered.filter((item) =>
    ["pending", "draft"].includes(item.reportStatus),
  );
  const completed = filtered.filter((item) =>
    ["submitted", "approved"].includes(item.reportStatus),
  );
  const historyNormalized = historyQuery.trim().toLowerCase();
  const historyFiltered = completed.filter((item) => {
    const text = [
      item.clientName,
      item.reference,
      item.orderNumber,
      item.ticketNumber,
      item.ticketTitle,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return (
      (!historyNormalized || text.includes(historyNormalized)) &&
      (historyClient === "all" || item.clientName === historyClient) &&
      (historyType === "all" || item.reportType === historyType) &&
      (!historyFrom ||
        String(item.performedAt || "").slice(0, 10) >= historyFrom) &&
      (!historyTo || String(item.performedAt || "").slice(0, 10) <= historyTo)
    );
  });
  const historyPageSize = 30;
  const historyPages = Math.max(
    1,
    Math.ceil(historyFiltered.length / historyPageSize),
  );
  const historyVisible = historyFiltered.slice(
    (historyPage - 1) * historyPageSize,
    historyPage * historyPageSize,
  );
  useEffect(() => {
    setHistoryPage(1);
  }, [
    historyQuery,
    historyClient,
    historyType,
    historyFrom,
    historyTo,
    query,
    client,
    type,
    status,
    dateFrom,
    dateTo,
  ]);
  const clearFilters = () => {
    setQuery("");
    setClient("all");
    setType("all");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
  };
  const genericReportPdf = async (item) => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.setFillColor(21, 25, 26);
    pdf.rect(0, 0, 210, 30, "F");
    try {
      const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
      pdf.addImage(logo, "PNG", 15, 6, 47, 16);
    } catch {}
    pdf.setTextColor(23, 35, 60);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text(`Relatório de ${item.reportType}`, 15, 48);
    pdf.setFontSize(11);
    const rows = [
      ["Cliente", item.clientName || "Não informado"],
      ["Unidade", item.unitName || "Não informada"],
      ["Referência", item.reference || "Não informada"],
      ["Ordem de serviço", item.orderNumber || "Não se aplica"],
      [
        "Chamado de origem",
        item.ticketNumber
          ? `${item.ticketNumber}${item.ticketTitle ? ` — ${item.ticketTitle}` : ""}`
          : "Não se aplica",
      ],
      ["Realizado em", formatDate(item.performedAt)],
      ["Situação", "Relatório entregue"],
    ];
    let y = 62;
    for (const [label, value] of rows) {
      pdf.setFont("helvetica", "bold");
      pdf.text(label, 15, y);
      pdf.setFont("helvetica", "normal");
      pdf.text(pdf.splitTextToSize(String(value), 130), 62, y);
      y += 11;
    }
    if (item.summary) {
      pdf.setFont("helvetica", "bold");
      pdf.text("Resumo", 15, y + 3);
      pdf.setFont("helvetica", "normal");
      pdf.text(pdf.splitTextToSize(item.summary, 180), 15, y + 11);
    }
    await addAboutsecPdfFooters(pdf);
    pdf.save(`Relatorio_${item.reportType}_${item.id}.pdf`);
  };
  const downloadPdf = async (item) => {
    const key = `${item.reportType}-${item.id}`;
    setDownloading(key);
    setError("");
    try {
      if (item.reportType === "OS") {
        const response = await fetch(
          `${apiBase}/service-orders/${item.id}/attendance`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) throw new Error();
        const details = await response.json();
        const issueOptions = [
          ["network", "Problema de rede"],
          ["electrical", "Problema de elétrica"],
          ["software", "Problema de software"],
          ["connectorization", "Problema de conectorização"],
          ["frozen_equipment", "Equipamento travado"],
          ["damaged_equipment", "Equipamento danificado"],
          ["burned_equipment", "Equipamento queimado"],
          ["configuration_failure", "Falha de configuração"],
        ];
        await exportServiceOrderPdf(
          {
            id: item.id,
            code: item.orderNumber,
            title: item.reference,
            client: item.clientName,
            unit: item.unitName,
            owner: details.signedBy || details.assignedTo,
            signature: signatureLabel(details.signaturePolicy),
          },
          details,
          issueOptions,
        );
      } else if (/^https?:\/\//i.test(item.reportUrl || "")) {
        window.open(item.reportUrl, "_blank", "noopener,noreferrer");
      } else await genericReportPdf(item);
    } catch {
      setError("Não foi possível gerar o PDF selecionado.");
    } finally {
      setDownloading("");
    }
  };
  return (
    <>
      <img
        className="report-brand-logo"
        src="/aboutplan-horizontal-transparent.png"
        alt="AboutPlan — Gestão de Operações"
      />
      <PageHeading
        title="Central de relatórios"
        subtitle="A execução é baixada separadamente da entrega do relatório."
        button="Exportar CSV"
        onCreate={exportCsv}
      />
      <div className="report-rule">
        <Icon name="check" />
        <div>
          <strong>Realizado não significa relatório entregue</strong>
          <p>
            Dias, OS e manutenções são computados normalmente; a pendência
            documental permanece visível até o relatório ser enviado.
          </p>
        </div>
      </div>
      {error && <div className="empty error-text">{error}</div>}
      {!data && !error && <div className="empty">Carregando relatórios…</div>}
      {data && (
        <>
          <div className="report-cards">
            {reportCards.map(([title, values, icon]) => (
              <article key={title}>
                <div className="metric-icon blue">
                  <Icon name={icon} />
                </div>
                <div>
                  <span>{title}</span>
                  <strong>{values.worked || 0}</strong>
                  <small>realizados no total</small>
                </div>
                <div className="report-counts">
                  <span>
                    <b>{values.delivered || 0}</b> entregues
                  </span>
                  <span className="pending">
                    <b>{values.pending || 0}</b> pendentes
                  </span>
                </div>
              </article>
            ))}
          </div>
          <section className="panel report-library-filters">
            <div>
              <label>
                <span>Buscar cliente, OS ou chamado</span>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Cliente, OS-0001, CH-0001 ou atividade"
                />
              </label>
              <label>
                <span>Cliente</span>
                <select
                  value={client}
                  onChange={(event) => setClient(event.target.value)}
                >
                  <option value="all">Todos</option>
                  {clients.map((value) => (
                    <option value={value} key={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Tipo</span>
                <select
                  value={type}
                  onChange={(event) => setType(event.target.value)}
                >
                  <option value="all">Todos</option>
                  <option>Obra</option>
                  <option>OS</option>
                  <option>Manutenção</option>
                </select>
              </label>
              <label>
                <span>Situação</span>
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <option value="all">Todas</option>
                  <option value="pending">Pendentes</option>
                  <option value="completed">Finalizados</option>
                </select>
              </label>
              <label>
                <span>De</span>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </label>
              <label>
                <span>Até</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </label>
            </div>
            <footer>
              <span>{filtered.length} relatório(s) encontrado(s)</span>
              <button onClick={clearFilters}>Limpar filtros</button>
            </footer>
          </section>
          <section className="panel pending-reports">
            <PanelTitle
              title="Relatórios pendentes"
              subtitle="Serviços já realizados aguardando documentação"
            />
            <div className="pending-head">
              <span>Tipo</span>
              <span>Cliente / referência</span>
              <span>Nº da OS</span>
              <span>Nº do chamado</span>
              <span>Realizado em</span>
              <span>Situação</span>
              <span></span>
            </div>
            {pending.map((item) => (
              <div
                className="pending-row"
                key={`${item.reportType}-${item.id}`}
              >
                <span className="report-type">{item.reportType}</span>
                <strong>
                  <b>{item.clientName || "Cliente não informado"}</b>
                  <small>
                    {item.reference}
                    {item.ticketTitle ? ` • ${item.ticketTitle}` : ""}
                  </small>
                </strong>
                <span className="report-number">{item.orderNumber || "—"}</span>
                <span className="report-number">
                  {item.ticketNumber || "—"}
                </span>
                <span>{formatDate(item.performedAt)}</span>
                <span className="record-status waiting">
                  {item.reportStatus === "draft"
                    ? "Rascunho"
                    : "Relatório pendente"}
                </span>
                <button onClick={() => setSelected(item)}>Entregar</button>
              </div>
            ))}
            {!pending.length && (
              <div className="empty">
                Nenhum relatório pendente para os filtros selecionados.
              </div>
            )}
          </section>
          <section className="panel pending-reports completed-reports">
            <PanelTitle
              title="Relatórios finalizados"
              subtitle="Histórico completo disponível para consulta e download • 30 por página"
            />
            <div className="completed-report-filters">
              <label>
                <Icon name="search" size={14} />
                <input
                  value={historyQuery}
                  onChange={(event) => setHistoryQuery(event.target.value)}
                  placeholder="Buscar cliente, OS, chamado ou referência"
                />
              </label>
              <select
                value={historyClient}
                onChange={(event) => setHistoryClient(event.target.value)}
              >
                <option value="all">Todos os clientes</option>
                {clients.map((value) => (
                  <option value={value} key={value}>
                    {value}
                  </option>
                ))}
              </select>
              <select
                value={historyType}
                onChange={(event) => setHistoryType(event.target.value)}
              >
                <option value="all">Todos os tipos</option>
                <option>Obra</option>
                <option>OS</option>
                <option>Manutenção</option>
              </select>
              <label>
                <span>De</span>
                <input
                  type="date"
                  value={historyFrom}
                  max={historyTo || undefined}
                  onChange={(event) => setHistoryFrom(event.target.value)}
                />
              </label>
              <label>
                <span>Até</span>
                <input
                  type="date"
                  value={historyTo}
                  min={historyFrom || undefined}
                  onChange={(event) => setHistoryTo(event.target.value)}
                />
              </label>
            </div>
            <div className="pending-head">
              <span>Tipo</span>
              <span>Cliente / referência</span>
              <span>Nº da OS</span>
              <span>Nº do chamado</span>
              <span>Realizado em</span>
              <span>Situação</span>
              <span></span>
            </div>
            {historyVisible.map((item) => (
              <div
                className="pending-row"
                key={`${item.reportType}-${item.id}`}
              >
                <span className="report-type">{item.reportType}</span>
                <strong>
                  <b>{item.clientName || "Cliente não informado"}</b>
                  <small>
                    {item.reference}
                    {item.ticketTitle ? ` • ${item.ticketTitle}` : ""}
                  </small>
                </strong>
                <span className="report-number">{item.orderNumber || "—"}</span>
                <span className="report-number">
                  {item.ticketNumber || "—"}
                </span>
                <span>{formatDate(item.performedAt)}</span>
                <span className="record-status done">Finalizado</span>
                <button
                  className="report-download"
                  onClick={() => downloadPdf(item)}
                  disabled={downloading === `${item.reportType}-${item.id}`}
                >
                  <Icon name="download" size={14} />
                  {downloading === `${item.reportType}-${item.id}`
                    ? "Gerando…"
                    : "Baixar PDF"}
                </button>
              </div>
            ))}
            {!historyVisible.length && (
              <div className="empty">
                Nenhum relatório finalizado para os filtros selecionados.
              </div>
            )}
            {historyFiltered.length > 0 && (
              <footer className="report-pagination">
                <span>
                  Exibindo {(historyPage - 1) * historyPageSize + 1}–
                  {Math.min(
                    historyPage * historyPageSize,
                    historyFiltered.length,
                  )}{" "}
                  de {historyFiltered.length}
                </span>
                <div>
                  <button
                    disabled={historyPage === 1}
                    onClick={() =>
                      setHistoryPage((page) => Math.max(1, page - 1))
                    }
                  >
                    Anterior
                  </button>
                  <b>
                    Página {historyPage} de {historyPages}
                  </b>
                  <button
                    disabled={historyPage === historyPages}
                    onClick={() =>
                      setHistoryPage((page) => Math.min(historyPages, page + 1))
                    }
                  >
                    Próxima
                  </button>
                </div>
              </footer>
            )}
          </section>
        </>
      )}
      {selected && (
        <ReportDeliveryModal
          item={selected}
          token={token}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </>
  );
}

function ReportDeliveryModal({ item, token, onClose, onSaved }) {
  const [form, setForm] = useState({ reportUrl: "", summary: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const type = {
    Obra: "construction",
    OS: "service-order",
    Manutenção: "maintenance",
  }[item.reportType];
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(
        `${apiBase}/reports/${type}/${item.id}/deliver`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        },
      );
      if (!response.ok) throw new Error();
      onSaved();
    } catch {
      setError("Não foi possível entregar o relatório.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <span className="record-code">{item.reportType}</span>
            <h2>Entregar relatório</h2>
            <p>{item.reference}</p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <label>
          Link ou referência do arquivo
          <input
            value={form.reportUrl}
            onChange={(e) => setForm({ ...form, reportUrl: e.target.value })}
            placeholder="Arquivo, pasta ou URL do relatório"
          />
        </label>
        {item.reportType === "Obra" && (
          <label>
            Resumo
            <textarea
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
            />
            <AiReviewButton
              token={token}
              value={form.summary}
              onApply={(value) => setForm({ ...form, summary: value })}
              context="resumo de relatório de obra"
            />
          </label>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving}>
            {saving ? "Salvando…" : "Marcar como entregue"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MaintenancePlanModal({ token, onClose, onSaved }) {
  const [setup, setSetup] = useState({
    clients: [],
    assets: [],
    checklists: [],
  });
  const [form, setForm] = useState({
    name: "",
    clientId: "",
    assetId: "",
    checklistTemplateId: "",
    frequencyUnit: "month",
    frequencyInterval: 1,
    startDate: "",
    endDate: "",
    estimatedHours: 1,
    toleranceDays: 0,
    signaturePolicy: "informative",
    assignedTo: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    fetch(`${apiBase}/maintenance/setup`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setSetup)
      .catch(() => setError("Não foi possível carregar os cadastros."));
  }, [token]);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/maintenance/plans`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error();
      onSaved();
    } catch {
      setError("Revise os campos e tente novamente.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal plan-modal" onSubmit={submit}>
        <div className="modal-head">
          <div className="plan-modal-title">
            <span><Icon name="calendar" size={20} /></span>
            <div>
              <small>PLANEJAMENTO PREVENTIVO</small>
              <h2>Novo plano preventivo</h2>
              <p>Configure o vínculo, a recorrência e os responsáveis pela execução.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="plan-modal-body">
        <section className="plan-form-section">
          <div className="plan-form-section-title"><span>1</span><div><b>Identificação e vínculo</b><small>Defina onde o plano será aplicado.</small></div></div>
          <div className="plan-form-grid context">
          <label className="wide">
            Nome do plano*
            <input name="name" value={form.name} onChange={change} placeholder="Ex.: Preventiva mensal de CFTV" required />
          </label>
          <label>
            Cliente*
            <select
              name="clientId"
              value={form.clientId}
              onChange={change}
              required
            >
              <option value="">Selecione</option>
              {setup.clients.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Equipamento
            <select name="assetId" value={form.assetId} onChange={change}>
              <option value="">Selecione</option>
              {setup.assets
                .filter(
                  (item) =>
                    !form.clientId ||
                    String(item.clientId) === String(form.clientId),
                )
                .map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="wide">
          Checklist*
          <select
            name="checklistTemplateId"
            value={form.checklistTemplateId}
            onChange={change}
            required
          >
            <option value="">Selecione</option>
            {setup.checklists.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name} — v{item.version}
              </option>
            ))}
          </select>
        </label>
        </div>
        </section>
        <section className="plan-form-section">
          <div className="plan-form-section-title"><span>2</span><div><b>Recorrência e período</b><small>Determine a frequência e a janela de geração das atividades.</small></div></div>
        <div className="plan-form-grid recurrence">
          <label>
            Frequência*
            <select
              name="frequencyUnit"
              value={form.frequencyUnit}
              onChange={change}
            >
              <option value="week">Semanal</option>
              <option value="month">Mensal</option>
              <option value="year">Anual</option>
            </select>
          </label>
          <label>
            Intervalo*
            <input
              type="number"
              min="1"
              name="frequencyInterval"
              value={form.frequencyInterval}
              onChange={change}
            />
          </label>
          <label>
            Data inicial*
            <input
              type="date"
              name="startDate"
              value={form.startDate}
              onChange={change}
              required
            />
          </label>
          <label>
            Data final
            <input
              type="date"
              name="endDate"
              value={form.endDate}
              onChange={change}
            />
          </label>
        </div>
        </section>
        <section className="plan-form-section">
          <div className="plan-form-section-title"><span>3</span><div><b>Execução e controle</b><small>Informe esforço, tolerância e política documental.</small></div></div>
        <div className="plan-form-grid execution">
          <label>
            Duração prevista (h)
            <input
              type="number"
              min="0.5"
              step="0.5"
              name="estimatedHours"
              value={form.estimatedHours}
              onChange={change}
            />
          </label>
          <label>
            Tolerância (dias)
            <input
              type="number"
              min="0"
              name="toleranceDays"
              value={form.toleranceDays}
              onChange={change}
            />
          </label>
          <label>
            Responsável padrão
            <input
              name="assignedTo"
              value={form.assignedTo}
              onChange={change}
            />
          </label>
          <label>
            Assinatura
            <select
              name="signaturePolicy"
              value={form.signaturePolicy}
              onChange={change}
            >
              <option value="informative">Informativo — sem assinatura</option>
              <option value="acknowledgement">Ciência do cliente</option>
              <option value="acceptance">Aceite obrigatório</option>
            </select>
          </label>
        </div>
        </section>
        {error && <div className="login-error">{error}</div>}
        </div>
        <div className="modal-actions">
          <div><b>Geração automática</b><span>As atividades serão criadas conforme as datas e a frequência definidas.</span></div>
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving || !form.name || !form.clientId || !form.checklistTemplateId || !form.startDate}>
            {saving ? "Criando…" : "Criar plano e atividades"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FinancialPage({ permissions, token }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [resultSearch, setResultSearch] = useState("");
  const [resultClient, setResultClient] = useState("Todos");
  const [resultStatus, setResultStatus] = useState("Todos");
  const [settings, setSettings] = useState({
    contractValue: 0,
    serviceValue: 0,
    administrativeExpensePercent: 15,
    taxPercent: 22,
    bonusPercent: "",
  });
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/financial-results/projects`, {
        headers,
      });
      if (!response.ok) throw new Error();
      setProjects(await response.json());
    } catch {
      setMessage("Não foi possível carregar os resultados dos projetos.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (permissions.viewProjectResult) load();
  }, [token, permissions.viewProjectResult]);
  const openProject = (project) => {
    setProjectId(project.id);
    setSettings({
      contractValue:
        Number(project.contractValue) > 0 ? Number(project.contractValue) : "",
      serviceValue:
        Number(project.serviceValue) > 0 ? Number(project.serviceValue) : "",
      administrativeExpensePercent: project.administrativeExpensePercent ?? 15,
      taxPercent: project.taxPercent ?? 22,
      bonusPercent: project.bonusPercent ?? "",
    });
    setMessage("");
  };
  const resultClients = [
    ...new Set(projects.map((project) => project.clientName).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const normalizedResultSearch = resultSearch.trim().toLocaleLowerCase("pt-BR");
  const filteredResultProjects = projects.filter(
    (project) =>
      (!normalizedResultSearch ||
        [
          project.projectNumber,
          project.name,
          project.clientName,
          project.unitName,
        ].some((value) =>
          String(value || "")
            .toLocaleLowerCase("pt-BR")
            .includes(normalizedResultSearch),
        )) &&
      (resultClient === "Todos" || project.clientName === resultClient) &&
      (resultStatus === "Todos" ||
        (resultStatus === "Concluídos"
          ? project.status === "completed"
          : project.status !== "completed")),
  );
  const selected = projects.find(
    (project) => Number(project.id) === Number(projectId),
  );
  const projectValue = Number(settings.contractValue) || 0;
  const serviceValue = Number(settings.serviceValue) || 0;
  const itemsTotal = Number(selected?.itemsTotal || 0);
  const estimatedBalance = projectValue - itemsTotal - serviceValue;
  const administrativeValue =
    (projectValue * (Number(settings.administrativeExpensePercent) || 0)) / 100;
  const taxValue = (projectValue * (Number(settings.taxPercent) || 0)) / 100;
  const bonusValue =
    (projectValue * (Number(settings.bonusPercent) || 0)) / 100;
  const totalCosts =
    itemsTotal + serviceValue + administrativeValue + taxValue + bonusValue;
  const estimatedProfit = projectValue - totalCosts;
  const profitMargin = projectValue
    ? (estimatedProfit / projectValue) * 100
    : 0;
  const save = async (event) => {
    event.preventDefault();
    if (settings.contractValue === "") return;
    setSaving(true);
    setMessage("");
    const response = await fetch(
      `${apiBase}/financial-results/projects/${projectId}/settings`,
      { method: "PATCH", headers, body: JSON.stringify(settings) },
    );
    setMessage(
      response.ok
        ? "Composição financeira salva."
        : "Não foi possível salvar a composição financeira.",
    );
    if (response.ok) {
      await load();
    }
    setSaving(false);
  };
  const downloadResultSpreadsheet = () => {
    if (!selected) return;
    const materialTotal = selected.items
      .filter((item) => item.itemType !== "service")
      .reduce((sum, item) => sum + Number(item.totalValue || 0), 0);
    const serviceTotal =
      selected.items
        .filter((item) => item.itemType === "service")
        .reduce((sum, item) => sum + Number(item.totalValue || 0), 0) +
      serviceValue;
    const distribution = [
      ["Materiais e equipamentos", materialTotal],
      ["Serviços", serviceTotal],
      ["Despesas administrativas", administrativeValue],
      ["Impostos", taxValue],
      ["Premiação", bonusValue],
    ];
    const maximum = Math.max(1, ...distribution.map(([, value]) => value));
    const summary = [
      ["ABOUTPLAN — RESULTADO DO PROJETO"],
      [],
      ["Projeto", selected.name],
      ["Cliente", selected.clientName || ""],
      ["Unidade", selected.unitName || ""],
      [],
      ["INDICADORES", "VALOR"],
      ["Valor do projeto", projectValue],
      ["Materiais e equipamentos", materialTotal],
      ["Serviços", serviceTotal],
      ["Saldo após itens", estimatedBalance],
      ["Despesas administrativas", administrativeValue],
      ["Impostos", taxValue],
      ["Premiação", bonusValue],
      ["Custos totais", totalCosts],
      ["Lucro estimado", estimatedProfit],
      ["Percentual de lucro", profitMargin / 100],
      [],
      ["COMPOSIÇÃO DOS CUSTOS", "VALOR", "% DOS CUSTOS", "GRÁFICO"],
      ...distribution.map(([name, value]) => [
        name,
        value,
        totalCosts ? value / totalCosts : 0,
        "█".repeat(Math.max(1, Math.round((value / maximum) * 24))),
      ]),
    ];
    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.aoa_to_sheet(summary);
    summarySheet["!cols"] = [
      { wch: 32 },
      { wch: 18 },
      { wch: 16 },
      { wch: 28 },
    ];
    for (let row = 8; row <= 16; row++) {
      const cell = summarySheet[`B${row}`];
      if (cell) cell.z = "R$ #,##0.00";
    }
    if (summarySheet.B17) summarySheet.B17.z = "0.00%";
    for (let row = 20; row < 20 + distribution.length; row++) {
      if (summarySheet[`B${row}`]) summarySheet[`B${row}`].z = "R$ #,##0.00";
      if (summarySheet[`C${row}`]) summarySheet[`C${row}`].z = "0.00%";
    }
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Resumo e resultado");
    const itemRows = selected.items.map((item, index) => ({
      Item: index + 1,
      Tipo: item.itemType === "service" ? "Serviço" : "Material / equipamento",
      Descrição: item.item,
      Marca: item.brand || "",
      Modelo: item.model || "",
      Quantidade: Number(item.plannedQuantity || 0),
      "Valor unitário": Number(item.unitCost || 0),
      Total: Number(item.totalValue || 0),
    }));
    const itemSheet = XLSX.utils.json_to_sheet(itemRows);
    itemSheet["!cols"] = [
      { wch: 8 },
      { wch: 24 },
      { wch: 34 },
      { wch: 18 },
      { wch: 18 },
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Itens do projeto");
    const compositionSheet = XLSX.utils.json_to_sheet([
      { Parâmetro: "Valor do projeto", Percentual: "", Valor: projectValue },
      { Parâmetro: "Valor dos serviços", Percentual: "", Valor: serviceValue },
      {
        Parâmetro: "Despesas administrativas",
        Percentual: Number(settings.administrativeExpensePercent || 0) / 100,
        Valor: administrativeValue,
      },
      {
        Parâmetro: "Impostos",
        Percentual: Number(settings.taxPercent || 0) / 100,
        Valor: taxValue,
      },
      {
        Parâmetro: "Premiação",
        Percentual:
          settings.bonusPercent === ""
            ? ""
            : Number(settings.bonusPercent || 0) / 100,
        Valor: bonusValue,
      },
      { Parâmetro: "Custos totais", Percentual: "", Valor: totalCosts },
      {
        Parâmetro: "Lucro estimado",
        Percentual: profitMargin / 100,
        Valor: estimatedProfit,
      },
    ]);
    compositionSheet["!cols"] = [{ wch: 28 }, { wch: 16 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(workbook, compositionSheet, "Composição");
    XLSX.writeFile(
      workbook,
      `Resultado_${String(selected.name).replace(/[^a-z0-9_-]+/gi, "_")}.xlsx`,
    );
  };
  const downloadResultPdf = async () => {
    if (!selected) return;
    const materialTotal = selected.items
      .filter((item) => item.itemType !== "service")
      .reduce((sum, item) => sum + Number(item.totalValue || 0), 0);
    const serviceTotal =
      selected.items
        .filter((item) => item.itemType === "service")
        .reduce((sum, item) => sum + Number(item.totalValue || 0), 0) +
      serviceValue;
    const distribution = [
      ["Materiais e equipamentos", materialTotal, [73, 105, 145]],
      ["Serviços", serviceTotal, [112, 99, 137]],
      ["Despesas administrativas", administrativeValue, [181, 143, 91]],
      ["Impostos", taxValue, [174, 94, 98]],
      ["Premiação", bonusValue, [79, 137, 116]],
    ];
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const width = 297,
      height = 210;
    const reportHeader = async (title, subtitle, page) => {
      pdf.setFillColor(21, 25, 26);
      pdf.rect(0, 0, width, 29, "F");
      try {
        const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
        const properties = pdf.getImageProperties(logo);
        const ratio = properties.width / properties.height;
        const logoWidth = 61;
        pdf.addImage(
          logo,
          "PNG",
          12,
          7,
          logoWidth,
          logoWidth / ratio,
          undefined,
          "SLOW",
        );
      } catch {}
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text(title, 285, 12, { align: "right" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(201, 211, 228);
      pdf.text(subtitle, 285, 18, { align: "right" });
      pdf.text("Página " + page, 285, 23, { align: "right" });
      pdf.setFillColor(197, 119, 73);
      pdf.rect(0, 27, 297, 2, "F");
    };
    pdf.setFillColor(21, 25, 26);
    pdf.rect(0, 0, width, height, "F");
    try {
      const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
      const properties = pdf.getImageProperties(logo);
      const ratio = properties.width / properties.height;
      const logoWidth = 105;
      pdf.addImage(
        logo,
        "PNG",
        20,
        22,
        logoWidth,
        logoWidth / ratio,
        undefined,
        "SLOW",
      );
    } catch {}
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(25);
    pdf.text("RESULTADO DO PROJETO", 20, 93);
    pdf.setFontSize(18);
    pdf.text(selected.name, 20, 108);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(180, 194, 216);
    pdf.text(
      `${selected.clientName || "Sem cliente"}${selected.unitName ? `  |  ${selected.unitName}` : ""}`,
      20,
      119,
    );
    pdf.setFillColor(188, 112, 62);
    pdf.rect(20, 132, 55, 2, "F");
    pdf.setFontSize(9);
    pdf.text(
      `Relatorio gerado em ${new Date().toLocaleDateString("pt-BR")}`,
      20,
      148,
    );
    pdf.text("Documento confidencial — Diretoria e Financeiro", 20, 156);
    pdf.setFillColor(21, 25, 26);
    pdf.roundedRect(190, 55, 80, 84, 7, 7, "F");
    pdf.setTextColor(220, 231, 255);
    pdf.setFontSize(9);
    pdf.text("LUCRO ESTIMADO", 202, 75);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(23);
    pdf.text(formatCurrency(estimatedProfit), 202, 94);
    pdf.setFontSize(12);
    pdf.text(
      `${profitMargin.toFixed(2).replace(".", ",")}% de margem`,
      202,
      108,
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(220, 231, 255);
    pdf.text(`Valor do projeto: ${formatCurrency(projectValue)}`, 202, 124);
    pdf.addPage();
    pdf.setFillColor(246, 247, 249);
    pdf.rect(0, 0, width, height, "F");
    await reportHeader(
      "VISÃO EXECUTIVA",
      `${selected.name} • composição financeira estimada`,
      2,
    );
    const cards = [
      ["Valor do projeto", projectValue, [73, 105, 145]],
      ["Custos totais", totalCosts, [181, 143, 91]],
      [
        "Lucro estimado",
        estimatedProfit,
        estimatedProfit < 0 ? [164, 76, 82] : [79, 137, 116],
      ],
      [
        "Margem",
        `${profitMargin.toFixed(2).replace(".", ",")}%`,
        [112, 99, 137],
      ],
    ];
    cards.forEach(([label, value, color], index) => {
      const x = 15 + index * 68;
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(x, 34, 61, 31, 3, 3, "F");
      pdf.setFillColor(...color);
      pdf.rect(x, 34, 3, 31, "F");
      pdf.setTextColor(117, 128, 150);
      pdf.setFontSize(8);
      pdf.text(label, x + 8, 45);
      pdf.setTextColor(24, 32, 51);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.text(
        typeof value === "number" ? formatCurrency(value) : value,
        x + 8,
        57,
      );
      pdf.setFont("helvetica", "normal");
    });
    pdf.setTextColor(24, 32, 51);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("Composicao dos custos", 15, 82);
    pdf.setFont("helvetica", "normal");
    const chartMax = Math.max(1, ...distribution.map(([, value]) => value));
    distribution.forEach(([label, value, color], index) => {
      const y = 94 + index * 18;
      pdf.setTextColor(76, 88, 108);
      pdf.setFontSize(8);
      pdf.text(label, 15, y);
      pdf.setFillColor(224, 228, 234);
      pdf.roundedRect(70, y - 5, 135, 7, 2, 2, "F");
      if (value > 0) {
        pdf.setFillColor(...color);
        pdf.roundedRect(
          70,
          y - 5,
          Math.max(2, (135 * value) / chartMax),
          7,
          2,
          2,
          "F",
        );
      }
      pdf.setTextColor(24, 32, 51);
      pdf.setFont("helvetica", "bold");
      pdf.text(formatCurrency(value), 212, y);
      pdf.setFont("helvetica", "normal");
    });
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(242, 82, 40, 92, 4, 4, "F");
    pdf.setTextColor(24, 32, 51);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("LEGENDA", 249, 95);
    distribution.forEach(([label, , color], index) => {
      const y = 108 + index * 12;
      pdf.setFillColor(...color);
      pdf.circle(249, y - 2, 2, "F");
      pdf.setTextColor(91, 102, 121);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.5);
      pdf.text(label, 255, y);
    });
    const rowsPerPage = 14;
    for (
      let offset = 0;
      offset < selected.items.length;
      offset += rowsPerPage
    ) {
      pdf.addPage();
      pdf.setFillColor(247, 249, 252);
      pdf.rect(0, 0, width, height, "F");
      await reportHeader(
        "DETALHAMENTO DOS ITENS",
        `${selected.name} • itens ${offset + 1} a ${Math.min(offset + rowsPerPage, selected.items.length)} de ${selected.items.length}`,
        3 + Math.floor(offset / rowsPerPage),
      );
      const columns = [15, 28, 73, 144, 178, 208, 235, 270];
      const headers = [
        "#",
        "Tipo",
        "Descricao",
        "Marca",
        "Modelo",
        "Qtd.",
        "V. unitario",
        "Total",
      ];
      pdf.setFillColor(21, 25, 26);
      pdf.roundedRect(15, 34, 267, 12, 2, 2, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      headers.forEach((header, index) => pdf.text(header, columns[index], 42));
      selected.items
        .slice(offset, offset + rowsPerPage)
        .forEach((item, index) => {
          const y = 55 + index * 10;
          pdf.setFillColor(
            index % 2 ? 248 : 255,
            index % 2 ? 250 : 255,
            index % 2 ? 253 : 255,
          );
          pdf.rect(15, y - 6, 267, 10, "F");
          pdf.setTextColor(45, 55, 72);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(6.5);
          const values = [
            String(offset + index + 1),
            item.itemType === "service" ? "Servico" : "Material",
            String(item.item || "").slice(0, 38),
            String(item.brand || "-").slice(0, 18),
            String(item.model || "-").slice(0, 15),
            String(item.plannedQuantity || 0),
            formatCurrency(item.unitCost),
            formatCurrency(item.totalValue),
          ];
          values.forEach((value, column) =>
            pdf.text(value, columns[column], y),
          );
        });
      pdf.setTextColor(117, 128, 150);
      pdf.setFontSize(7);
      pdf.text(`Subtotal dos itens: ${formatCurrency(itemsTotal)}`, 282, 198, {
        align: "right",
      });
    }
    pdf.save(
      `Resultado_${String(selected.name).replace(/[^a-z0-9_-]+/gi, "_")}.pdf`,
    );
  };
  if (!permissions.viewProjectResult)
    return (
      <div className="restricted-result">
        <Icon name="wallet" />
        <div>
          <strong>Resultado restrito</strong>
          <p>Esta área é exclusiva da Diretoria e do setor Financeiro.</p>
        </div>
      </div>
    );
  if (!selected)
    return (
      <>
        <div className="access-banner">
          <Icon name="wallet" />
          <div>
            <strong>Resultado financeiro controlado</strong>
            <p>
              Valores, impostos, despesas, premiação, saldo e margem são
              exclusivos da Diretoria e do setor Financeiro.
            </p>
          </div>
        </div>
        {message && <div className="login-error">{message}</div>}
        <div className="cost-project-intro">
          <div>
            <h2>Resultados dos projetos</h2>
            <p>
              Consulte a composição financeira e o lucro estimado de cada obra.
            </p>
          </div>
          <span>
            {filteredResultProjects.length} de {projects.length} projeto(s)
          </span>
        </div>
        <section className="panel result-project-filters">
          <label>
            <span>Buscar</span>
            <div>
              <Icon name="search" size={16} />
              <input
                value={resultSearch}
                onChange={(event) => setResultSearch(event.target.value)}
                placeholder="Número, projeto, cliente ou unidade"
              />
            </div>
          </label>
          <label>
            <span>Cliente</span>
            <select
              value={resultClient}
              onChange={(event) => setResultClient(event.target.value)}
            >
              <option>Todos</option>
              {resultClients.map((client) => (
                <option key={client}>{client}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select
              value={resultStatus}
              onChange={(event) => setResultStatus(event.target.value)}
            >
              <option>Todos</option>
              <option>Em andamento</option>
              <option>Concluídos</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => {
              setResultSearch("");
              setResultClient("Todos");
              setResultStatus("Todos");
            }}
          >
            Limpar filtros
          </button>
        </section>
        {loading ? (
          <div className="panel empty">Carregando resultados…</div>
        ) : (
          <section className="panel result-project-list">
            <div className="result-project-head">
              <span>Número do projeto</span>
              <span>Projeto</span>
              <span>Cliente / unidade</span>
              <span>Status</span>
              <span>Valor do projeto</span>
              <span>Custos totais</span>
              <span>Lucro / margem</span>
              <span></span>
            </div>
            {filteredResultProjects.map((project) => (
              <button
                className="result-project-row"
                onClick={() => openProject(project)}
                key={project.id}
              >
                <b className="result-project-number">
                  {project.projectNumber || "Não informado"}
                </b>
                <div>
                  <i>{initials(project.clientName || project.name)}</i>
                  <span>
                    <strong>{project.name}</strong>
                    <small>
                      {project.itemCount || 0} item(ns) cadastrado(s)
                    </small>
                  </span>
                </div>
                <span>
                  <strong>{project.clientName || "Sem cliente"}</strong>
                  <small>{project.unitName || "Sem unidade"}</small>
                </span>
                <em
                  className={
                    project.status === "completed" ? "completed" : "active"
                  }
                >
                  {project.status === "completed"
                    ? "Concluído"
                    : "Em andamento"}
                </em>
                <b>{formatCurrency(project.contractValue)}</b>
                <b>{formatCurrency(project.totalCosts)}</b>
                <span
                  className={
                    Number(project.estimatedProfit) < 0 ? "negative" : ""
                  }
                >
                  <strong>{formatCurrency(project.estimatedProfit)}</strong>
                  <small>
                    {Number(project.profitMargin || 0)
                      .toFixed(2)
                      .replace(".", ",")}
                    % de margem
                  </small>
                </span>
                <Icon name="arrow" size={16} />
              </button>
            ))}
            {!filteredResultProjects.length && (
              <div className="empty">
                Nenhum projeto encontrado com os filtros selecionados.
              </div>
            )}
          </section>
        )}
      </>
    );
  return (
    <div className="project-cost-page financial-project-result">
      <div className="project-cost-toolbar">
        <button onClick={() => setProjectId(null)}>
          ‹ Voltar aos projetos
        </button>
        <div>
          <h2>{selected.name}</h2>
          <p>
            {selected.clientName}
            {selected.unitName ? ` • ${selected.unitName}` : ""}
          </p>
        </div>
        <div className="financial-result-actions">
          <div className="result-export-buttons">
            <button
              className="secondary-button spreadsheet-action compact"
              onClick={downloadResultSpreadsheet}
            >
              <span className="spreadsheet-action-icon excel">
                <Icon name="download" size={17} />
              </span>
              <span>
                <b>Exportar Excel</b>
                <small>Dados editáveis</small>
              </span>
            </button>
            <button
              className="secondary-button spreadsheet-action compact"
              onClick={downloadResultPdf}
            >
              <span className="spreadsheet-action-icon pdf">
                <Icon name="file" size={17} />
              </span>
              <span>
                <b>Exportar PDF</b>
                <small>Relatório executivo</small>
              </span>
            </button>
          </div>
          <span className="financial-restricted-label">
            <Icon name="wallet" size={15} /> Diretoria e Financeiro
          </span>
        </div>
      </div>
      {message && (
        <div
          className={message.includes("salva") ? "scope-note" : "login-error"}
        >
          {message}
        </div>
      )}
      <section className="panel project-cost-table categorized">
        <div className="project-cost-head">
          <span>Item / descrição</span>
          <span>Tipo</span>
          <span>Marca</span>
          <span>Modelo</span>
          <span>Quantidade</span>
          <span>Valor unitário</span>
          <span>Total</span>
        </div>
        {selected.items.map((item, index) => (
          <div className="project-cost-row" key={item.id}>
            <div>
              <b>{index + 1}</b>
              <strong>{item.item}</strong>
            </div>
            <span
              className={`cost-type ${item.itemType === "service" ? "service" : ""}`}
            >
              {item.itemType === "service"
                ? "Serviço"
                : "Material / equipamento"}
            </span>
            <span>{item.brand || "—"}</span>
            <span>{item.model || "—"}</span>
            <span>{item.plannedQuantity}</span>
            <span>{formatCurrency(item.unitCost)}</span>
            <strong>{formatCurrency(item.totalValue)}</strong>
          </div>
        ))}
        {!selected.items.length && (
          <div className="empty">
            Nenhum item de custo cadastrado neste projeto.
          </div>
        )}
        <div className="project-cost-subtotal">
          <span>Subtotal dos itens</span>
          <b>{formatCurrency(itemsTotal)}</b>
        </div>
      </section>
      <form className="panel project-cost-settings" onSubmit={save}>
        <div>
          <h3>Composição e resultado do projeto</h3>
          <p>Percentuais calculados sobre o valor do projeto.</p>
        </div>
        <div className="project-cost-setting-grid">
          <label>
            Valor do projeto
            <MoneyInput
              required
              value={settings.contractValue}
              onChange={(value) =>
                setSettings({ ...settings, contractValue: value })
              }
              placeholder="0,00"
            />
          </label>
          <label>
            Valor dos serviços
            <MoneyInput
              value={settings.serviceValue}
              onChange={(value) =>
                setSettings({ ...settings, serviceValue: value })
              }
              placeholder="0,00"
            />
          </label>
          <label>
            Despesas administrativas (%)
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.administrativeExpensePercent}
              onChange={(event) =>
                setSettings({
                  ...settings,
                  administrativeExpensePercent: event.target.value,
                })
              }
            />
          </label>
          <label>
            Impostos (%)
            <input
              type="number"
              min="0"
              step="0.01"
              value={settings.taxPercent}
              onChange={(event) =>
                setSettings({ ...settings, taxPercent: event.target.value })
              }
            />
          </label>
          <label>
            Premiação (%)
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Não definida"
              value={settings.bonusPercent}
              onChange={(event) =>
                setSettings({ ...settings, bonusPercent: event.target.value })
              }
            />
          </label>
          <button
            className="primary"
            disabled={saving || settings.contractValue === ""}
          >
            {saving ? "Salvando…" : "Salvar composição"}
          </button>
        </div>
        <div className="project-cost-summary financial-summary">
          <span>
            Itens cadastrados<b>{formatCurrency(itemsTotal)}</b>
          </span>
          <span>
            Valor dos serviços<b>{formatCurrency(serviceValue)}</b>
          </span>
          <span>
            Saldo após itens<b>{formatCurrency(estimatedBalance)}</b>
          </span>
          <span>
            Despesas administrativas (
            {settings.administrativeExpensePercent || 0}%)
            <b>{formatCurrency(administrativeValue)}</b>
          </span>
          <span>
            Impostos ({settings.taxPercent || 0}%)
            <b>{formatCurrency(taxValue)}</b>
          </span>
          <span>
            Premiação (
            {settings.bonusPercent === "" ? "—" : `${settings.bonusPercent}%`})
            <b>{formatCurrency(bonusValue)}</b>
          </span>
          <span className="total">
            Custos totais<b>{formatCurrency(totalCosts)}</b>
          </span>
          <span className={`result ${estimatedProfit < 0 ? "loss" : ""}`}>
            Lucro estimado<b>{formatCurrency(estimatedProfit)}</b>
            <small>{profitMargin.toFixed(2).replace(".", ",")}% de lucro</small>
          </span>
        </div>
      </form>
    </div>
  );
}

async function exportKanbanReportPdf({ board, cards, monthLabel, columns }) {
  if (!cards.length)
    return window.alert("Não há projetos no período selecionado.");
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const concludedColumnIds = new Set(
    columns
      .filter(
        (column, index) =>
          index === columns.length - 1 || /conclu[ií]d/i.test(column.name),
      )
      .map((column) => column.id),
  );
  const pending = cards.filter(
    (card) => !concludedColumnIds.has(card.columnId),
  );
  const missingInvoice = cards.filter(
    (card) => !String(card.invoiceNumber || "").trim(),
  );
  const missingOrder = cards.filter(
    (card) => !String(card.purchaseOrder || "").trim(),
  );
  const issuedAt = new Date().toLocaleString("pt-BR");
  pdf.setFillColor(21, 25, 26);
  pdf.rect(0, 0, 297, 210, "F");
  try {
    const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
    const properties = pdf.getImageProperties(logo);
    const width = 78;
    pdf.addImage(
      logo,
      "PNG",
      18,
      18,
      width,
      width / (properties.width / properties.height),
      undefined,
      "SLOW",
    );
  } catch {}
  pdf.setDrawColor(187, 161, 108);
  pdf.setLineWidth(1.2);
  pdf.line(18, 55, 279, 55);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(117, 103, 71);
  pdf.text("GESTÃO DE PROJETOS", 18, 79);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.text("Relatórios e KPIs", 18, 99);
  pdf.setFontSize(21);
  pdf.text(board.name, 18, 113);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(205, 215, 231);
  pdf.text(
    "Consolidação gerencial do fluxo Kanban, documentação comercial e faturamento dos projetos.",
    18,
    128,
    { maxWidth: 162 },
  );
  pdf.setFillColor(36, 55, 91);
  pdf.roundedRect(194, 73, 85, 70, 5, 5, "F");
  pdf.setFontSize(7);
  pdf.setTextColor(117, 103, 71);
  pdf.text("PERÍODO", 204, 86);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(17);
  pdf.setTextColor(255, 255, 255);
  pdf.text(monthLabel, 204, 99);
  pdf.setFontSize(7);
  pdf.setTextColor(117, 103, 71);
  pdf.text("PROJETOS NO RECORTE", 204, 115);
  pdf.setFontSize(20);
  pdf.setTextColor(255, 255, 255);
  pdf.text(String(cards.length), 204, 130);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(166, 184, 214);
  pdf.text(`Emitido em ${issuedAt}`, 18, 190);
  pdf.text("Documento gerencial AboutPlan", 279, 190, { align: "right" });
  pdf.addPage();
  pdf.setFillColor(21, 25, 26);
  pdf.rect(0, 0, 297, 25, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text("PAINEL EXECUTIVO DO KANBAN", 14, 15);
  pdf.setFontSize(8);
  pdf.text(`${board.name} • ${monthLabel}`, 282, 15, { align: "right" });
  const kpis = [
    ["Projetos", cards.length, [52, 116, 242]],
    ["Não concluídos", pending.length, [119, 89, 232]],
    ["Sem NF", missingInvoice.length, [239, 159, 38]],
    ["Sem pedido", missingOrder.length, [229, 82, 96]],
  ];
  kpis.forEach(([label, value, color], index) => {
    const x = 14 + index * 69;
    pdf.setFillColor(247, 249, 252);
    pdf.roundedRect(x, 34, 62, 27, 3, 3, "F");
    pdf.setFillColor(...color);
    pdf.rect(x, 34, 2.5, 27, "F");
    pdf.setTextColor(103, 116, 137);
    pdf.setFontSize(7);
    pdf.text(label.toUpperCase(), x + 8, 43);
    pdf.setTextColor(31, 44, 67);
    pdf.setFontSize(17);
    pdf.text(String(value), x + 8, 55);
  });
  const stageTotals = columns.map((column) => ({
    name: column.name,
    total: cards.filter((card) => card.columnId === column.id).length,
  }));
  const stageScaleTotal = Math.max(1, cards.length);
  pdf.setTextColor(31, 44, 67);
  pdf.setFontSize(11);
  pdf.text("Projetos por etapa", 14, 78);
  stageTotals.forEach((item, index) => {
    const y = 88 + index * 12;
    pdf.setFontSize(7);
    pdf.setTextColor(92, 106, 128);
    pdf.text(pdf.splitTextToSize(item.name, 48)[0], 14, y);
    pdf.setFillColor(232, 237, 244);
    pdf.roundedRect(64, y - 4, 92, 6, 2, 2, "F");
    if (item.total) {
      pdf.setFillColor(117, 103, 71);
      pdf.roundedRect(
        64,
        y - 4,
        (92 * item.total) / stageScaleTotal,
        6,
        2,
        2,
        "F",
      );
    }
    pdf.setTextColor(31, 44, 67);
    pdf.text(String(item.total), 160, y);
  });
  pdf.setFontSize(11);
  pdf.setTextColor(31, 44, 67);
  pdf.text("Pendências documentais", 182, 78);
  [
    ["Pedido informado", cards.length - missingOrder.length, [42, 174, 130]],
    ["Sem pedido", missingOrder.length, [229, 82, 96]],
    ["NF informada", cards.length - missingInvoice.length, [52, 116, 242]],
    ["Sem NF", missingInvoice.length, [239, 159, 38]],
  ].forEach(([label, value, color], index) => {
    const y = 91 + index * 18;
    pdf.setFillColor(...color);
    pdf.circle(186, y - 2, 2, "F");
    pdf.setFontSize(8);
    pdf.setTextColor(91, 104, 125);
    pdf.text(label, 192, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(31, 44, 67);
    pdf.text(String(value), 270, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
  });
  pdf.addPage();
  pdf.setFillColor(21, 25, 26);
  pdf.rect(0, 0, 297, 25, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text("RELAÇÃO DE PROJETOS", 14, 15);
  pdf.setFontSize(8);
  pdf.text(`${board.name} • ${monthLabel}`, 282, 15, { align: "right" });
  const drawTableHead = () => {
    pdf.setFillColor(241, 245, 250);
    pdf.rect(12, 32, 273, 12, "F");
    pdf.setTextColor(103, 116, 137);
    pdf.setFontSize(6.2);
    pdf.setFont("helvetica", "bold");
    [
      ["PROJETO", 14],
      ["CLIENTE / UNIDADE", 39],
      ["CARD", 88],
      ["ETAPA", 145],
      ["PO", 196],
      ["NF", 225],
    ].forEach(([label, x]) => pdf.text(label, x, 39));
    pdf.text(["PROJETO", "CONCLUÍDO"], 266, 37, { align: "center" });
    pdf.setFont("helvetica", "normal");
    return 52;
  };
  let y = drawTableHead();
  let page = 3;
  for (const card of cards) {
    if (y > 194) {
      pdf.addPage();
      page += 1;
      pdf.setFillColor(21, 25, 26);
      pdf.rect(0, 0, 297, 25, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text("RELAÇÃO DE PROJETOS", 14, 15);
      pdf.text(`Página ${page}`, 282, 15, { align: "right" });
      y = drawTableHead();
    }
    const column = columns.find((item) => item.id === card.columnId);
    const values = [
      card.projectNumber || "—",
      `${card.clientName || "—"} / ${card.unitName || "—"}`,
      card.title,
      column?.name || "—",
      card.purchaseOrder || "Pendente",
      card.invoiceNumber || "Pendente",
    ];
    const xs = [14, 39, 88, 145, 196, 225],
      widths = [22, 46, 53, 47, 26, 22];
    pdf.setTextColor(59, 72, 94);
    pdf.setFontSize(6.3);
    values.forEach((value, index) =>
      pdf.text(
        pdf.splitTextToSize(String(value), widths[index]).slice(0, 2),
        xs[index],
        y,
      ),
    );
    const isConcluded = concludedColumnIds.has(card.columnId);
    pdf.setFillColor(...(isConcluded ? [42, 174, 130] : [237, 241, 246]));
    pdf.roundedRect(250, y - 4, 32, 9, 2, 2, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(5.6);
    pdf.setTextColor(...(isConcluded ? [255, 255, 255] : [112, 124, 143]));
    pdf.text(isConcluded ? "CONCLUÍDO" : "EM ANDAMENTO", 266, y + 1.2, {
      align: "center",
    });
    pdf.setFont("helvetica", "normal");
    pdf.setDrawColor(230, 235, 242);
    pdf.line(12, y + 7, 285, y + 7);
    y += 13;
  }
  pdf.save(
    `Kanban_${String(board.name).replace(/[^a-z0-9]+/gi, "_")}_${monthLabel.replace(/[^a-z0-9]+/gi, "_")}.pdf`,
  );
}

function ProjectKanbanPage({ token, canManage, canConfigure }) {
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [section, setSection] = useState("board");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const boardViewportRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  const loadBoards = async (preferredId) => {
    const response = await fetch(`${apiBase}/kanban/boards`, { headers });
    if (!response.ok) throw new Error();
    const data = await response.json();
    setBoards(data);
    const candidate = String(preferredId || boardId || "");
    setBoardId(
      String(
        data.some((item) => String(item.id) === candidate)
          ? candidate
          : data[0]?.id || "",
      ),
    );
  };
  const loadBoard = async (id = boardId) => {
    if (!id) return;
    const response = await fetch(`${apiBase}/kanban/boards/${id}`, { headers });
    if (!response.ok) throw new Error();
    setBoard(await response.json());
  };
  useEffect(() => {
    loadBoards().catch(() =>
      setMessage("Não foi possível carregar os quadros."),
    );
  }, [token]);
  useEffect(() => {
    loadBoard().catch(() => setMessage("Não foi possível carregar o Kanban."));
  }, [boardId]);
  useEffect(() => {
    const syncFullscreen = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !document.fullscreenElement)
        setIsFullscreen(false);
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  const toggleFullscreen = async () => {
    if (isFullscreen) {
      if (document.fullscreenElement)
        await document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
      return;
    }
    setIsFullscreen(true);
    await boardViewportRef.current?.requestFullscreen?.().catch(() => {});
  };
  const createBoard = async () => {
    const year = window.prompt(
      "Ano do novo quadro",
      String(new Date().getFullYear() + 1),
    );
    if (!year) return;
    const name = window.prompt("Nome do quadro", `Projetos ${year}`);
    if (!name) return;
    const response = await fetch(`${apiBase}/kanban/boards`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name, year: Number(year) }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok)
      return setMessage(
        result.error === "kanban_board_year_exists"
          ? "Já existe um quadro para esse ano."
          : "Não foi possível criar o quadro.",
      );
    await loadBoards(result.id);
  };
  const deleteBoard = async () => {
    if (!board || !window.confirm(`Excluir o quadro vazio “${board.name}”?`))
      return;
    const response = await fetch(`${apiBase}/kanban/boards/${board.id}`, {
      method: "DELETE",
      headers,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok)
      return setMessage(
        result.error === "kanban_board_not_empty"
          ? `O quadro possui ${result.cards || board.cards.length} card(s). Mova os projetos antes de excluir.`
          : "Não foi possível excluir o quadro.",
      );
    setBoard(null);
    setBoardId("");
    await loadBoards();
  };
  const addColumn = async () => {
    const name = window.prompt("Nome da nova etapa");
    if (!name) return;
    await fetch(`${apiBase}/kanban/boards/${boardId}/columns`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name, color: "#3474f2" }),
    });
    loadBoard();
  };
  const editColumn = async (column) => {
    const name = window.prompt("Nome da etapa", column.name);
    if (!name || name === column.name) return;
    await fetch(`${apiBase}/kanban/columns/${column.id}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name, color: column.color }),
    });
    loadBoard();
  };
  const reorderColumn = async (columnId, direction) => {
    const ids = board.columns.map((item) => item.id);
    const index = ids.indexOf(columnId);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBoard((current) => ({
      ...current,
      columns: ids.map((id) => current.columns.find((item) => item.id === id)),
    }));
    const response = await fetch(
      `${apiBase}/kanban/boards/${boardId}/columns/reorder`,
      {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ columnIds: ids }),
      },
    );
    if (!response.ok) loadBoard();
  };
  const deleteColumn = async (column) => {
    if (!window.confirm(`Excluir a etapa vazia “${column.name}”?`)) return;
    const response = await fetch(`${apiBase}/kanban/columns/${column.id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok)
      return setMessage(
        "A etapa precisa estar vazia e o quadro deve manter ao menos uma coluna.",
      );
    loadBoard();
  };
  const moveCard = async (cardId, columnId, laneMonth, position) => {
    if (!canManage) return;
    if (!cardId) return;
    const destinationName =
      board?.columns.find((column) => column.id === columnId)?.name || "";
    const currentCard = board?.cards.find((card) => card.id === cardId);
    const originName =
      board?.columns.find((column) => column.id === currentCard?.columnId)
        ?.name || "";
    const normalizedDestination = destinationName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    const normalizedOrigin = originName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    const marksInvoiceIssued = normalizedDestination.includes("nf emitida");
    const clearsInvoiceIssued =
      !marksInvoiceIssued && normalizedOrigin.includes("nf emitida");
    const marksWorkCompleted =
      marksInvoiceIssued && normalizedDestination.includes("conclu");
    setBoard((current) => {
      const moving = current.cards.find((card) => card.id === cardId);
      if (!moving) return current;
      const remaining = current.cards.filter((card) => card.id !== cardId);
      const destination = remaining.filter(
        (card) =>
          card.columnId === columnId && Number(card.laneMonth) === laneMonth,
      );
      const insertAt =
        position === undefined
          ? destination.length
          : Math.max(0, Math.min(position, destination.length));
      destination.splice(insertAt, 0, {
        ...moving,
        columnId,
        laneMonth,
        ...(marksInvoiceIssued
          ? {
              invoiceNumber: "Sim",
              customFields: {
                ...(moving.customFields || {}),
                workCompleted: marksWorkCompleted,
              },
            }
          : clearsInvoiceIssued
            ? {
                invoiceNumber: null,
                customFields: {
                  ...(moving.customFields || {}),
                  workCompleted: false,
                },
              }
            : {}),
      });
      const other = remaining.filter(
        (card) =>
          card.columnId !== columnId || Number(card.laneMonth) !== laneMonth,
      );
      return { ...current, cards: [...other, ...destination] };
    });
    const response = await fetch(`${apiBase}/kanban/cards/${cardId}/move`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ columnId, laneMonth, position }),
    });
    if (!response.ok) {
      setMessage("Não foi possível mover o card.");
      loadBoard();
    } else await loadBoard();
  };
  const visibleCards = (columnId, laneMonth) =>
    (board?.cards || []).filter(
      (card) =>
        card.columnId === columnId &&
        Number(card.laneMonth) === laneMonth &&
        `${card.title} ${card.projectNumber || ""} ${card.clientName || ""} ${card.purchaseOrder || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
    );
  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  return (
    <div className="kanban-page">
      <div className="kanban-hero">
        <div>
          <span>GESTÃO DE PROJETOS</span>
          <h2>{board?.name || "Kanban"}</h2>
          <p>
            Acompanhe compras, entrega, instalação e faturamento em um fluxo
            configurável.
          </p>
        </div>
        <div className="kanban-hero-actions">
          <label>
            Quadro
            <select
              value={boardId}
              onChange={(event) => setBoardId(event.target.value)}
            >
              {boards.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {item.cardCount} cards
                </option>
              ))}
            </select>
          </label>
          {canConfigure && (
            <div className="kanban-board-admin-actions">
              <button className="secondary-button" onClick={createBoard}>
                <Icon name="plus" size={15} /> Novo quadro
              </button>
              <button
                className="kanban-delete-board"
                onClick={deleteBoard}
                disabled={!board}
                aria-label="Excluir quadro"
              >
                <span>×</span> Excluir
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="kanban-section-tabs">
        <button
          className={section === "board" ? "active" : ""}
          onClick={() => setSection("board")}
        >
          <Icon name="grid" size={16} /> Quadro Kanban
        </button>
        <button
          className={section === "reports" ? "active" : ""}
          onClick={() => setSection("reports")}
        >
          <Icon name="chart" size={16} /> Relatórios e KPIs
        </button>
      </div>
      {section === "board" ? (
        <>
          <div className="kanban-toolbar panel">
            <label>
              <Icon name="search" size={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar projeto, cliente, PO ou card"
              />
            </label>
            <span>{board?.cards?.length || 0} projetos neste quadro</span>
            <div className="kanban-toolbar-actions">
              <button
                className="secondary-button kanban-fullscreen-button"
                onClick={toggleFullscreen}
              >
                <Icon name="maximize" size={15} /> Tela cheia
              </button>
              {canConfigure && (
                <button
                  className="secondary-button kanban-new-stage-button"
                  onClick={addColumn}
                >
                  <Icon name="plus" size={15} /> Nova etapa
                </button>
              )}
            </div>
          </div>
          {message && <div className="empty error-text">{message}</div>}
          <div
            ref={boardViewportRef}
            className={`kanban-board-shell ${isFullscreen ? "is-fullscreen" : ""}`}
          >
            {isFullscreen && (
              <button
                className="kanban-fullscreen-exit"
                onClick={toggleFullscreen}
              >
                <Icon name="minimize" size={14} /> Sair da tela cheia
              </button>
            )}
            <div className="kanban-board-scroll">
              <div
                className="kanban-board"
                style={{ "--kanban-columns": board?.columns.length || 1 }}
              >
                <div className="kanban-month-corner">MÊS</div>
                {board?.columns.map((column) => (
                  <header
                    className="kanban-stage-header"
                    key={column.id}
                    style={{ "--kanban-color": column.color }}
                  >
                    <div>
                      <b>{column.name}</b>
                      <span>
                        {
                          (board.cards || []).filter(
                            (card) => card.columnId === column.id,
                          ).length
                        }
                      </span>
                    </div>
                    {canConfigure && (
                      <div className="kanban-column-actions">
                        <button
                          onClick={() => reorderColumn(column.id, -1)}
                          aria-label="Mover etapa para esquerda"
                        >
                          ‹
                        </button>
                        <button
                          onClick={() => reorderColumn(column.id, 1)}
                          aria-label="Mover etapa para direita"
                        >
                          ›
                        </button>
                        <button
                          onClick={() => editColumn(column)}
                          aria-label={`Editar ${column.name}`}
                        >
                          •••
                        </button>
                        <button
                          onClick={() => deleteColumn(column)}
                          aria-label={`Excluir ${column.name}`}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </header>
                ))}
                {monthNames.map((monthName, monthIndex) => {
                  const laneMonth = monthIndex + 1;
                  return (
                    <Fragment key={monthName}>
                      <div className="kanban-month-label">
                        <span>{monthName}</span>
                        <b>
                          {
                            (board?.cards || []).filter(
                              (card) => Number(card.laneMonth) === laneMonth,
                            ).length
                          }
                        </b>
                      </div>
                      {board?.columns.map((column) => {
                        const cards = visibleCards(column.id, laneMonth);
                        return (
                          <section
                            className="kanban-lane-cell"
                            key={`${laneMonth}-${column.id}`}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) =>
                              moveCard(
                                Number(
                                  event.dataTransfer.getData("text/card-id"),
                                ),
                                column.id,
                                laneMonth,
                              )
                            }
                          >
                            {cards.map((card, cardIndex) => (
                              <button
                                className={`kanban-card priority-${card.priority}`}
                                key={card.id}
                                draggable={canManage}
                                onDragStart={(event) => {
                                  event.dataTransfer.effectAllowed = "move";
                                  event.dataTransfer.setData(
                                    "text/card-id",
                                    String(card.id),
                                  );
                                }}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  moveCard(
                                    Number(
                                      event.dataTransfer.getData(
                                        "text/card-id",
                                      ),
                                    ),
                                    column.id,
                                    laneMonth,
                                    cardIndex,
                                  );
                                }}
                                onClick={() => setSelected(card)}
                              >
                                <div className="kanban-card-top">
                                  <span>
                                    {card.projectNumber
                                      ? `PROJETO ${card.projectNumber}`
                                      : "CARD"}
                                  </span>
                                  <i>
                                    {card.attachments.length
                                      ? `${card.attachments.length} anexo(s)`
                                      : ""}
                                  </i>
                                </div>
                                <h3>{card.title}</h3>
                                <p>
                                  {card.clientName || "Sem cliente"}
                                  {card.unitName ? ` · ${card.unitName}` : ""}
                                </p>
                                <dl>
                                  <div>
                                    <dt>PO</dt>
                                    <dd>{card.purchaseOrder || "—"}</dd>
                                  </div>
                                  <div>
                                    <dt>Proposta</dt>
                                    <dd>{card.proposalNumber || "—"}</dd>
                                  </div>
                                  <div>
                                    <dt>Material</dt>
                                    <dd>
                                      {card.materialAvailable ||
                                        "Não informado"}
                                    </dd>
                                  </div>
                                </dl>
                                <footer>
                                  <span>
                                    {card.dueDate
                                      ? `Prazo ${formatDate(card.dueDate)}`
                                      : "Sem prazo"}
                                  </span>
                                  <Icon name="arrow" size={14} />
                                </footer>
                              </button>
                            ))}
                            {!cards.length && (
                              <div className="kanban-lane-empty">
                                Solte aqui
                              </div>
                            )}
                          </section>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      ) : (
        <KanbanReportsPanel board={board} />
      )}
      {selected &&
        createPortal(
          <KanbanCardModal
            card={selected}
            stageName={
              board?.columns.find((column) => column.id === selected.columnId)
                ?.name || ""
            }
            token={token}
            canManage={canManage}
            onClose={() => setSelected(null)}
            onSaved={() => {
              setSelected(null);
              loadBoard();
            }}
          />,
          document.fullscreenElement ||
            (isFullscreen ? boardViewportRef.current : null) ||
            document.body,
        )}
    </div>
  );
}

function KanbanReportsPanel({ board }) {
  const [month, setMonth] = useState("all");
  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  if (!board) return <div className="empty panel">Carregando indicadores…</div>;
  const cards = board.cards.filter(
    (card) => month === "all" || Number(card.laneMonth) === Number(month),
  );
  const concludedIds = new Set(
    board.columns
      .filter(
        (column, index) =>
          index === board.columns.length - 1 ||
          /conclu[ií]d/i.test(column.name),
      )
      .map((column) => column.id),
  );
  const pending = cards.filter((card) => !concludedIds.has(card.columnId));
  const missingInvoice = cards.filter(
    (card) => !String(card.invoiceNumber || "").trim(),
  );
  const missingOrder = cards.filter(
    (card) => !String(card.purchaseOrder || "").trim(),
  );
  const documentPendingIds = new Set(
    [...missingOrder, ...missingInvoice].map((card) => card.id),
  );
  const documentComplete = cards.length - documentPendingIds.size;
  const stageTotals = board.columns.map((column) => ({
    ...column,
    total: cards.filter((card) => card.columnId === column.id).length,
  }));
  const stageScaleTotal = Math.max(1, cards.length);
  const monthlyTotals = monthNames.map((name, index) => ({
    name: name.slice(0, 3),
    total: board.cards.filter((card) => Number(card.laneMonth) === index + 1)
      .length,
  }));
  const maxMonth = Math.max(1, ...monthlyTotals.map((item) => item.total));
  const attention = cards.filter(
    (card) =>
      !concludedIds.has(card.columnId) ||
      !card.invoiceNumber ||
      !card.purchaseOrder,
  );
  const scopeLabel =
    month === "all" ? "Todos os meses" : monthNames[Number(month) - 1];
  return (
    <div className="kanban-reports">
      <section className="panel kanban-report-toolbar">
        <div>
          <span>VISÃO GERENCIAL</span>
          <h3>Relatórios e KPIs do quadro</h3>
          <p>Indicadores de andamento, pedidos e notas fiscais.</p>
        </div>
        <label>
          Período
          <select
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          >
            <option value="all">Todos os meses</option>
            {monthNames.map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="primary-button kanban-export-pdf"
          onClick={() =>
            exportKanbanReportPdf({
              board,
              cards,
              monthLabel: scopeLabel,
              columns: board.columns,
            })
          }
        >
          <span>
            <Icon name="file-download" size={16} />
          </span>{" "}
          Exportar PDF
        </button>
      </section>
      <div className="kanban-report-kpis">
        <ProjectKpi
          icon="folder"
          tone="blue"
          label="Projetos no período"
          value={cards.length}
          detail={scopeLabel}
        />
        <ProjectKpi
          icon="clock"
          tone="purple"
          label="Ainda não concluídos"
          value={pending.length}
          detail={`${cards.length ? Math.round((pending.length / cards.length) * 100) : 0}% do recorte`}
        />
        <ProjectKpi
          icon="file"
          tone="amber"
          label="Projetos sem NF"
          value={missingInvoice.length}
          detail="nota fiscal ainda não informada"
        />
        <ProjectKpi
          icon="clipboard"
          tone="red"
          label="Projetos sem pedido"
          value={missingOrder.length}
          detail="PO ainda não informado"
        />
      </div>
      <div className="kanban-report-charts">
        <section className="panel kanban-stage-chart">
          <div className="panel-title">
            <div>
              <h3>Projetos por etapa</h3>
              <p>Percentual sobre o total do recorte selecionado</p>
            </div>
          </div>
          <div>
            {stageTotals.map((item) => (
              <article key={item.id}>
                <span title={item.name}>
                  {item.name}
                  <b>{item.total}</b>
                </span>
                <div>
                  <i
                    style={{
                      width: `${(item.total / stageScaleTotal) * 100}%`,
                      background: item.color,
                    }}
                  ></i>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="panel kanban-document-chart">
          <div className="panel-title">
            <div>
              <h3>Conformidade documental</h3>
              <p>Pedido e nota fiscal dos projetos</p>
            </div>
          </div>
          <div
            className="kanban-document-donut"
            style={{
              "--document-ok": `${cards.length ? (documentComplete / cards.length) * 100 : 0}%`,
            }}
          >
            <span>
              <b>{documentComplete}</b>
              <small>completos</small>
            </span>
          </div>
          <ul>
            <li>
              <i className="green"></i>
              <span>Pedido informado</span>
              <b>{cards.length - missingOrder.length}</b>
            </li>
            <li>
              <i className="blue"></i>
              <span>NF informada</span>
              <b>{cards.length - missingInvoice.length}</b>
            </li>
            <li>
              <i className="red"></i>
              <span>Com pendência</span>
              <b>{documentPendingIds.size}</b>
            </li>
          </ul>
        </section>
      </div>
      <section className="panel kanban-month-chart">
        <div className="panel-title">
          <div>
            <h3>Volume de projetos por mês</h3>
            <p>Distribuição anual do quadro {board.name}</p>
          </div>
        </div>
        <div className="kanban-month-bars">
          {monthlyTotals.map((item) => (
            <article key={item.name}>
              <b>{item.total || ""}</b>
              <div>
                <i style={{ height: `${(item.total / maxMonth) * 100}%` }}></i>
              </div>
              <span>{item.name}</span>
            </article>
          ))}
        </div>
      </section>
      <section className="panel kanban-attention-table">
        <div className="panel-title">
          <div>
            <h3>Projetos que exigem atenção</h3>
            <p>Obras em aberto ou com documentação pendente</p>
          </div>
          <span>{attention.length} registros</span>
        </div>
        <div className="kanban-attention-head">
          <span>Projeto</span>
          <span>Cliente / unidade</span>
          <span>Etapa</span>
          <span>Pedido</span>
          <span>NF</span>
        </div>
        {attention.map((card) => (
          <div className="kanban-attention-row" key={card.id}>
            <b>
              {card.projectNumber || "—"}
              <small>{card.title}</small>
            </b>
            <span>
              {card.clientName || "—"}
              <small>{card.unitName || "Sem unidade"}</small>
            </span>
            <span>
              {board.columns.find((column) => column.id === card.columnId)
                ?.name || "—"}
            </span>
            <i className={card.purchaseOrder ? "ok" : "pending"}>
              {card.purchaseOrder || "Pendente"}
            </i>
            <i className={card.invoiceNumber ? "ok" : "pending"}>
              {card.invoiceNumber || "Pendente"}
            </i>
          </div>
        ))}
        {!attention.length && (
          <div className="empty">
            Nenhuma pendência encontrada neste período.
          </div>
        )}
      </section>
    </div>
  );
}

function KanbanCardModal({
  card,
  stageName,
  token,
  canManage,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    title: card.title || "",
    purchaseOrder: card.purchaseOrder || "",
    proposalNumber: card.proposalNumber || "",
    projectValue:
      Number(card.projectValue) > 0 ? Number(card.projectValue) : "",
    installationDate: card.installationDate || "",
    estimatedWorkDays: card.estimatedWorkDays || "",
    materialAvailable: card.materialAvailable || "Não informado",
    invoiceIssued: Boolean(String(card.invoiceNumber || "").trim()),
    workCompleted: Boolean(
      card.customFields?.workCompleted || /conclu[ií]d/i.test(stageName),
    ),
    priority: card.priority || "normal",
    dueDate: card.dueDate || "",
    notes: card.notes || "",
    customFields: card.customFields || {},
    laneMonth: Number(card.laneMonth) || 1,
  });
  const [attachments, setAttachments] = useState(card.attachments || []);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    fetch(`${apiBase}/kanban/cards/${card.id}/history`, { headers })
      .then((response) => (response.ok ? response.json() : []))
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [card.id, token]);
  const field = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));
  const save = async () => {
    setSaving(true);
    const response = await fetch(`${apiBase}/kanban/cards/${card.id}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (!response.ok) return setMessage("Não foi possível salvar o card.");
    onSaved();
  };
  const upload = async (event) => {
    const files = [...event.target.files];
    event.target.value = "";
    if (!files.length) return;
    const encoded = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({ fileName: file.name, dataUrl: reader.result });
            reader.readAsDataURL(file);
          }),
      ),
    );
    const response = await fetch(
      `${apiBase}/kanban/cards/${card.id}/attachments`,
      {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ files: encoded }),
      },
    );
    if (!response.ok)
      return setMessage(
        "Use PDF, imagem, Word ou Excel com até 6 MB por arquivo e 12 anexos por card.",
      );
    onSaved();
  };
  const openAttachment = async (attachment) => {
    const response = await fetch(
      `${apiBase}/kanban/attachments/${attachment.id}`,
      { headers },
    );
    if (!response.ok) return;
    const file = await response.json();
    const link = document.createElement("a");
    link.href = file.dataUrl;
    link.download = file.fileName;
    link.click();
  };
  const removeAttachment = async (attachment) => {
    if (!window.confirm(`Remover ${attachment.fileName}?`)) return;
    await fetch(`${apiBase}/kanban/attachments/${attachment.id}`, {
      method: "DELETE",
      headers,
    });
    setAttachments((items) =>
      items.filter((item) => item.id !== attachment.id),
    );
  };
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="kanban-card-modal">
        <div className="modal-header">
          <div>
            <span>PROJETO {card.projectNumber || "SEM NÚMERO"}</span>
            <h2>{card.title}</h2>
            <p>
              {card.clientName} · {card.unitName || "Sem unidade"}
            </p>
          </div>
          <button
            className="kanban-modal-close"
            onClick={onClose}
            aria-label="Fechar card"
          >
            ×
          </button>
        </div>
        <div className="kanban-modal-body">
          <section className="kanban-form-grid">
            <label className="wide">
              Título
              <input
                value={form.title}
                disabled={!canManage}
                onChange={(e) => field("title", e.target.value)}
              />
            </label>
            <label>
              Mês do card
              <select
                value={form.laneMonth}
                disabled={!canManage}
                onChange={(e) => field("laneMonth", Number(e.target.value))}
              >
                {[
                  "Janeiro",
                  "Fevereiro",
                  "Março",
                  "Abril",
                  "Maio",
                  "Junho",
                  "Julho",
                  "Agosto",
                  "Setembro",
                  "Outubro",
                  "Novembro",
                  "Dezembro",
                ].map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Pedido de compra (PO)
              <input
                value={form.purchaseOrder}
                disabled={!canManage}
                onChange={(e) => field("purchaseOrder", e.target.value)}
              />
            </label>
            <label>
              Proposta
              <input
                value={form.proposalNumber}
                disabled={!canManage}
                onChange={(e) => field("proposalNumber", e.target.value)}
              />
            </label>
            <label>
              Valor do projeto
              <MoneyInput
                value={form.projectValue}
                disabled={!canManage}
                onChange={(value) => field("projectValue", value)}
                placeholder="0,00"
              />
            </label>
            <label>
              Data de instalação
              <input
                type="date"
                value={form.installationDate}
                disabled={!canManage}
                onChange={(e) => field("installationDate", e.target.value)}
              />
            </label>
            <label>
              Tempo de obra (dias)
              <input
                type="number"
                min="0"
                value={form.estimatedWorkDays}
                disabled={!canManage}
                onChange={(e) => field("estimatedWorkDays", e.target.value)}
              />
            </label>
            <label>
              Material disponível
              <select
                value={form.materialAvailable}
                disabled={!canManage}
                onChange={(e) => field("materialAvailable", e.target.value)}
              >
                <option>Não informado</option>
                <option>Não</option>
                <option>Parcial</option>
                <option>Sim</option>
              </select>
            </label>
            <label>
              NF emitida
              <select
                value={form.invoiceIssued ? "sim" : "nao"}
                disabled={!canManage}
                onChange={(e) =>
                  field("invoiceIssued", e.target.value === "sim")
                }
              >
                <option value="nao">Não</option>
                <option value="sim">Sim</option>
              </select>
            </label>
            <label>
              Obra concluída
              <select
                value={form.workCompleted ? "sim" : "nao"}
                disabled={!canManage}
                onChange={(e) =>
                  field("workCompleted", e.target.value === "sim")
                }
              >
                <option value="nao">Não</option>
                <option value="sim">Sim</option>
              </select>
            </label>
            <label>
              Prioridade
              <select
                value={form.priority}
                disabled={!canManage}
                onChange={(e) => field("priority", e.target.value)}
              >
                <option value="low">Baixa</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </label>
            <label>
              Prazo
              <input
                type="date"
                value={form.dueDate}
                disabled={!canManage}
                onChange={(e) => field("dueDate", e.target.value)}
              />
            </label>
            <label className="wide">
              Observações
              <textarea
                value={form.notes}
                disabled={!canManage}
                onChange={(e) => field("notes", e.target.value)}
              />
            </label>
            <div className="kanban-custom-fields wide">
              <div>
                <span>Campos personalizados</span>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      const name = window.prompt("Nome do campo personalizado");
                      if (
                        name &&
                        !Object.prototype.hasOwnProperty.call(
                          form.customFields,
                          name,
                        )
                      )
                        field("customFields", {
                          ...form.customFields,
                          [name]: "",
                        });
                    }}
                  >
                    + Adicionar campo
                  </button>
                )}
              </div>
              {Object.entries(form.customFields)
                .filter(([name]) => name !== "workCompleted")
                .map(([name, value]) => (
                  <label key={name}>
                    {name}
                    <span>
                      <input
                        value={value}
                        disabled={!canManage}
                        onChange={(event) =>
                          field("customFields", {
                            ...form.customFields,
                            [name]: event.target.value,
                          })
                        }
                      />
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = { ...form.customFields };
                            delete next[name];
                            field("customFields", next);
                          }}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  </label>
                ))}
              {!Object.keys(form.customFields).filter(
                (name) => name !== "workCompleted",
              ).length && (
                <small>
                  Adicione informações específicas do seu processo sem alterar o
                  cadastro do projeto.
                </small>
              )}
            </div>
          </section>
          <aside className="kanban-modal-side">
            <section className="kanban-attachments">
              <div>
                <h3>Documentos</h3>
                <span>{attachments.length} de 12 arquivos</span>
              </div>
              <div className="kanban-attachment-list">
                {attachments.map((item) => (
                  <article key={item.id}>
                    <Icon name="file" size={17} />
                    <button onClick={() => openAttachment(item)}>
                      {item.fileName}
                    </button>
                    {canManage && (
                      <button
                        className="remove"
                        onClick={() => removeAttachment(item)}
                      >
                        ×
                      </button>
                    )}
                  </article>
                ))}
                {!attachments.length && (
                  <p>Nenhum pedido, nota fiscal ou documento anexado.</p>
                )}
              </div>
              {canManage && (
                <label className="secondary-button kanban-attach-button">
                  <span>
                    <Icon name="paperclip" size={15} />
                  </span>{" "}
                  Anexar documentos
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
                    onChange={upload}
                  />
                </label>
              )}
            </section>
            <section className="kanban-history">
              <div>
                <h3>Histórico do card</h3>
                <span>{history.length} eventos</span>
              </div>
              {history.map((item) => (
                <article key={item.id}>
                  <i className={item.action}></i>
                  <div>
                    <b>{item.description}</b>
                    <small>
                      {item.userName || "Sistema"} ·{" "}
                      {new Date(item.createdAt).toLocaleString("pt-BR")}
                    </small>
                  </div>
                </article>
              ))}
              {!history.length && (
                <p>As próximas alterações e movimentações aparecerão aqui.</p>
              )}
            </section>
          </aside>
        </div>
        {message && <div className="login-error">{message}</div>}
        <div className="modal-actions">
          <button onClick={onClose}>Fechar</button>
          {canManage && (
            <button className="primary" disabled={saving} onClick={save}>
              {saving ? "Salvando…" : "Salvar card"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectsPage({
  projects: items,
  permissions,
  role,
  token,
  onRefresh,
  onCreate,
}) {
  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  const [selected, setSelected] = useState(null);
  const [clientFilter, setClientFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [responsibleFilter, setResponsibleFilter] = useState("Todos");
  const [monthFilter, setMonthFilter] = useState("Todos");
  const [specificDate, setSpecificDate] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [wallOpen, setWallOpen] = useState(false);
  const openVideowall = () => {
    setWallOpen(true);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const closeVideowall = () => {
    setWallOpen(false);
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  };
  const canDeleteProject =
    role === "administrator" || permissions?.keys?.includes("projects.manage");
  const clients = [
    ...new Set(items.map((item) => item.client).filter(Boolean)),
  ].sort();
  const statuses = [
    "Execução",
    "Planejamento",
    "Pausado",
    "Concluídos",
    "Atrasados",
  ];
  const responsibles = [
    ...new Set(items.map((item) => item.responsibleName).filter(Boolean)),
  ].sort();
  const monthOptions = useMemo(() => {
    const available = new Set([currentMonth]);
    for (let offset = -12; offset <= 12; offset++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1);
      available.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
    }
    for (const item of items) {
      const start = String(item.startDate || "").slice(0, 7);
      const end = String(item.endDate || "").slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(start)) available.add(start);
      if (/^\d{4}-\d{2}$/.test(end)) available.add(end);
      if (!/^\d{4}-\d{2}$/.test(start) || !/^\d{4}-\d{2}$/.test(end) || start > end) continue;
      const cursor = new Date(`${start}-01T12:00:00Z`);
      for (let count = 0; count < 120 && cursor.toISOString().slice(0, 7) <= end; count++) {
        available.add(cursor.toISOString().slice(0, 7));
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      }
    }
    return [...available].sort((a, b) => b.localeCompare(a));
  }, [items, currentMonth]);
  const effectiveMonth = monthFilter === "Todos" ? currentMonth : monthFilter;
  const monthStart = `${effectiveMonth}-01`;
  const monthReference = new Date(`${monthStart}T12:00:00`);
  const monthEnd = `${effectiveMonth}-${String(new Date(monthReference.getFullYear(), monthReference.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
  const overlapsDate = (item, start, end) =>
    Boolean(
      item.startDate &&
      item.endDate &&
      item.startDate <= end &&
      item.endDate >= start,
    );
  const matchesProjectStatus = (item) =>
    statusFilter === "Todos" ||
    (statusFilter === "Concluídos"
      ? ["Concluído", "Encerrado"].includes(item.stage)
      : statusFilter === "Atrasados"
        ? item.isOverdue
        : item.stage === statusFilter);
  const visible = items.filter(
    (item) =>
      (clientFilter === "Todos" || item.client === clientFilter) &&
      matchesProjectStatus(item) &&
      (responsibleFilter === "Todos" ||
        item.responsibleName === responsibleFilter) &&
      (specificDate
        ? overlapsDate(item, specificDate, specificDate)
        : monthFilter === "Todos" || (!item.startDate && !item.endDate) ||
          overlapsDate(item, monthStart, monthEnd)),
  );
  const completed = visible.filter((item) =>
    ["Concluído", "Encerrado"].includes(item.stage),
  );
  const active = visible.filter(
    (item) => !["Concluído", "Encerrado"].includes(item.stage),
  );
  const overdue = visible.filter((item) => item.isOverdue).length;
  const savedDays = completed.reduce(
    (total, item) =>
      total + (Number(item.planned) || 0) - (Number(item.worked) || 0),
    0,
  );
  const averageSaved = completed.length ? savedDays / completed.length : 0;
  const savedPositive = savedDays >= 0;
  const clearFilters = () => {
    setClientFilter("Todos");
    setStatusFilter("Todos");
    setResponsibleFilter("Todos");
    setMonthFilter("Todos");
    setSpecificDate("");
  };
  const deleteProject = async (project) => {
    const confirmed = window.confirm(
      `Excluir permanentemente o projeto “${project.name}”?\n\nCronograma, equipe, diários, entrega, materiais e custos do projeto serão apagados. Ordens de serviço e marcações de ponto serão preservadas sem o vínculo com este projeto.`,
    );
    if (!confirmed) return;
    setDeletingId(project.id);
    try {
      const response = await fetch(`${apiBase}/projects/${project.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = response.status === 204 ? {} : await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(
          result.error === "project_not_found"
            ? "Este projeto já foi excluído."
            : "Não foi possível excluir o projeto.",
        );
      if (selected?.id === project.id) setSelected(null);
      await onRefresh?.();
    } catch (deleteError) {
      window.alert(deleteError.message);
    } finally {
      setDeletingId(null);
    }
  };
  return (
    <>
      <PageHeading
        title="Projetos e obras"
        subtitle={`${items.length} projetos encontrados`}
        button="Novo projeto"
        onCreate={onCreate}
      />
      <div className="projects-videowall-entry">
        <div><Icon name="maximize" size={20} /><span><strong>Painel visual de projetos</strong><small>Todos os projetos em aberto, em páginas automáticas para videowall.</small></span></div>
        <button type="button" onClick={openVideowall}><Icon name="maximize" size={16} /> Modo videowall</button>
      </div>
      <div className="project-kpis">
        <ProjectKpi
          icon="folder"
          tone="blue"
          label="Projetos em andamento"
          value={active.length}
          detail={`${visible.length} obras no período`}
        />
        <ProjectKpi
          icon="check"
          tone="purple"
          label="Projetos concluídos"
          value={completed.length}
          detail="base para cálculo de economia"
        />
        <ProjectKpi
          icon="clock"
          tone="amber"
          label="Acima do prazo estimado"
          value={overdue}
          detail="obras que exigem atenção"
        />
        <ProjectKpi
          icon="trend"
          tone={savedPositive ? "green" : "red"}
          label={savedPositive ? "Dias economizados" : "Dias excedidos"}
          value={`${Math.abs(savedDays)} dias`}
          detail={
            completed.length
              ? `${Math.abs(averageSaved).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} dias ${savedPositive ? "economizados" : "excedidos"} por obra concluída`
              : "Será calculado após concluir uma obra"
          }
        />
      </div>
      <div className="time-saving-note">
        <Icon name="trend" size={16} />
        <span>
          <b>Como é calculado:</b> dias previstos menos dias executados,
          considerando somente obras concluídas. Obras em andamento não geram
          economia antecipada.
        </span>
      </div>
      <section className="panel project-filter-bar">
        <label>
          <span>Mês</span>
          <select
            value={monthFilter}
            onChange={(event) => {
              setMonthFilter(event.target.value);
              setSpecificDate("");
            }}
          >
            <option value="Todos">Todos</option>
            {monthOptions.map((month) => {
              const label = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
              return <option value={month} key={month}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
            })}
          </select>
        </label>
        <label>
          <span>Data específica</span>
          <input
            type="date"
            value={specificDate}
            onChange={(event) => {
              setSpecificDate(event.target.value);
              if (event.target.value) setMonthFilter("Todos");
            }}
          />
        </label>
        <label>
          <span>Cliente</span>
          <select
            value={clientFilter}
            onChange={(event) => setClientFilter(event.target.value)}
          >
            <option>Todos</option>
            {clients.map((client) => (
              <option key={client}>{client}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option>Todos</option>
            {statuses.map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Responsável</span>
          <select
            value={responsibleFilter}
            onChange={(event) => setResponsibleFilter(event.target.value)}
          >
            <option>Todos</option>
            {responsibles.map((responsible) => (
              <option key={responsible}>{responsible}</option>
            ))}
          </select>
        </label>
        <div>
          <strong>
            {visible.length} de {items.length} projeto(s)
          </strong>
          <small>
            {specificDate
              ? "Filtro pela data selecionada"
              : monthFilter === "Todos"
                ? "Todos os períodos"
                : "Projetos no mês e sem data definida"}
          </small>
          <button type="button" onClick={clearFilters}>
            Limpar filtros
          </button>
        </div>
      </section>
      <div className="project-cards">
        {visible.map((p) => (
          <article
            className="project-card project-card-compact"
            key={p.id || p.name}
          >
            <div className="project-card-top">
              <span className={`client-logo ${p.tone}`}>{p.initials}</span>
              <span className={`status ${p.tone}`}>● {p.health}</span>
            </div>
            <small className="project-number">
              Projeto nº {p.projectNumber || "Não informado"}
            </small>
            <h3>{p.name}</h3>
            <p>
              {p.client} • {p.stage}
            </p>
            <small className="project-responsible-name">
              Responsável: <b>{p.responsibleName}</b>
            </small>
            {!p.startDate && !p.endDate && (
              <small className="project-responsible-name">
                Cronograma preliminar • Sem data definida
              </small>
            )}
            {p.commercialExceeded && (
              <div className="commercial-warning">
                <Icon name="clock" size={14} />
                <span>
                  Cronograma {p.commercialDifference} dia(s) acima do orçamento
                </span>
              </div>
            )}
            <div className="project-card-data-grid">
              <div>
                <span>Executado / cronograma</span>
                <b>
                  {p.worked} de {p.planned} dias
                </b>
              </div>
              <div>
                <span>{p.preliminary ? "Estudo de Implantação" : "Planejamento comercial"}</span>
                <b className={p.commercialExceeded ? "negative-time" : ""}>
                  {p.commercial ? `${p.commercial} dias` : "Não definido"}
                </b>
              </div>
              <div>
                <span>Início projetado</span>
                <b>{formatDate(p.startDate)}</b>
              </div>
              <div>
                <span>Fim projetado</span>
                <b>{formatDate(p.endDate)}</b>
              </div>
            </div>
            <div className="project-progress">
              <div title={`${p.name}: ${p.progress}% concluído`}>
                <i style={{ width: `${p.progress}%` }}></i>
              </div>
              <span>{p.progress}% concluído</span>
            </div>
            <div className="project-card-actions">
              <button className="open-record" onClick={() => setSelected(p)}>
                Abrir projeto <Icon name="arrow" size={15} />
              </button>
              {canDeleteProject && (
                <button
                  type="button"
                  className="project-delete-button"
                  disabled={deletingId === p.id}
                  onClick={() => deleteProject(p)}
                >
                  <Icon name="trash" size={14} />
                  {deletingId === p.id ? "Excluindo…" : "Excluir"}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {!visible.length && (
        <div className="empty panel">
          Nenhum projeto encontrado no período e filtros selecionados.
        </div>
      )}
      {selected && (
        <ProjectWorkModal
          project={selected}
          permissions={permissions}
          role={role}
          token={token}
          onRefresh={onRefresh}
          onClose={() => {
            setSelected(null);
            onRefresh();
          }}
          onSaved={() => {
            setSelected(null);
            onRefresh();
          }}
        />
      )}
      {wallOpen && <ProjectsVideowall projects={items} token={token} onClose={closeVideowall} />}
    </>
  );
}

function ProjectKpi({ icon, tone, label, value, detail }) {
  return (
    <article className={`project-kpi tone-${tone}`}>
      <div className={`metric-icon ${tone}`}>
        <Icon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function ProjectsPageLegacy({ projects: items, token, onRefresh, onCreate }) {
  const [selected, setSelected] = useState(null);
  const [clientFilter, setClientFilter] = useState("Todos");
  const clients = [...new Set(items.map((item) => item.client))].sort();
  const visible = items.filter(
    (item) => clientFilter === "Todos" || item.client === clientFilter,
  );
  return (
    <>
      <PageHeading
        title="Projetos e obras"
        subtitle={`${items.length} projetos encontrados`}
        button="Novo projeto"
        onCreate={onCreate}
      />
      <div className="module-filter">
        <div>
          <Icon name="building" size={16} />
          <span>Cliente</span>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option>Todos</option>
            {clients.map((client) => (
              <option key={client}>{client}</option>
            ))}
          </select>
        </div>
        <small>
          Exibindo {visible.length} de {items.length} projetos
        </small>
      </div>
      <div className="project-cards">
        {visible.map((p) => (
          <article className="project-card" key={p.name}>
            <div className="project-card-top">
              <span className={`client-logo ${p.tone}`}>{p.initials}</span>
              <span className={`status ${p.tone}`}>● {p.health}</span>
            </div>
            <h3>{p.name}</h3>
            <p>
              {p.client} • {p.stage}
            </p>
            <div className="project-stats">
              <div>
                <span>Prazo consumido</span>
                <b>
                  {p.worked} de {p.planned} dias
                </b>
              </div>
              <div>
                <span>Entrega prevista</span>
                <b>{p.end}</b>
              </div>
            </div>
            <div className="project-progress">
              <div>
                <i style={{ width: `${p.progress}%` }}></i>
              </div>
              <span>{p.progress}% concluído</span>
            </div>
            <button className="open-record" onClick={() => setSelected(p)}>
              Abrir projeto <Icon name="arrow" size={15} />
            </button>
          </article>
        ))}
      </div>
      {!visible.length && (
        <div className="empty panel">Nenhum projeto para este cliente.</div>
      )}
      {selected && (
        <ProjectWorkModal
          project={selected}
          token={token}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function dailyWorkflowLabel(status) {
  return (
    {
      draft: "Em preenchimento",
      awaiting_approval: "Aguardando aprovação",
      submitted: "Enviado",
      approved: "Aprovado",
      rejected: "Ajustes solicitados",
    }[status] ||
    status ||
    "Em preenchimento"
  );
}
function dailyWorkflowTone(status) {
  if (["submitted", "approved"].includes(status)) return "done";
  if (["awaiting_approval"].includes(status)) return "waiting";
  if (status === "rejected") return "overdue";
  return "new";
}
function dailyParticipantLabel(value) {
  const total = Number(value || 0);
  if (!total) return "Nenhum colaborador informado";
  return `${total} ${total === 1 ? "colaborador" : "colaboradores"} na atividade do dia`;
}

async function exportDailyWorkPdf(project, report) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const navy = [23, 35, 60];
  const muted = [103, 116, 139];
  const soft = [245, 247, 251];
  const line = [222, 228, 237];
  const accent = [197, 119, 73];
  const clean = (value) => String(value || "").trim();
  const safeName = (value) =>
    clean(value || "diario-de-obra")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9_-]+/gi, "_");
  const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png").catch(
    () => null,
  );
  const addLogo = (x, y, maxWidth, maxHeight) => {
    if (!logo) return;
    const properties = pdf.getImageProperties(logo);
    const ratio = properties.width / properties.height;
    let width = maxWidth;
    let height = width / ratio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }
    pdf.addImage(
      logo,
      "PNG",
      x,
      y + (maxHeight - height) / 2,
      width,
      height,
      undefined,
      "SLOW",
    );
  };
  const addHeader = (title, page) => {
    pdf.setFillColor(...navy);
    pdf.rect(0, 0, pageWidth, 25, "F");
    addLogo(margin, 4, 48, 17);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(title, pageWidth - margin, 10, { align: "right" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(`Página ${page}`, pageWidth - margin, 16, { align: "right" });
    pdf.setDrawColor(...accent);
    pdf.setLineWidth(0.7);
    pdf.line(margin, 25, pageWidth - margin, 25);
  };
  const section = (title, y) => {
    pdf.setFillColor(...soft);
    pdf.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, "F");
    pdf.setTextColor(...navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.text(title.toUpperCase(), margin + 4, y + 6);
    return y + 14;
  };
  const field = (label, value, x, y, width) => {
    pdf.setTextColor(...muted);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(label.toUpperCase(), x, y);
    pdf.setTextColor(...navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    const lines = pdf.splitTextToSize(clean(value) || "Não informado", width);
    pdf.text(lines, x, y + 5, { lineHeightFactor: 1.25 });
    return y + 5 + lines.length * 4.2;
  };
  const taskGroups = Object.values(
    (report.tasks || []).reduce((groups, task) => {
      const phase = clean(task.phaseName) || "Atividades";
      if (!groups[phase]) groups[phase] = { phase, activities: [] };
      const activity = clean(task.name);
      if (activity && !groups[phase].activities.includes(activity))
        groups[phase].activities.push(activity);
      return groups;
    }, {}),
  );

  pdf.setFillColor(...navy);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");
  addLogo(18, 20, 92, 26);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(197, 207, 223);
  pdf.setCharSpace(0.12);
  pdf.text("Powered by Aboutsec", 18, 54);
  pdf.setCharSpace(0);
  pdf.setFillColor(...accent);
  pdf.rect(18, 64, 52, 1.5, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("RELATÓRIO DIÁRIO DE OBRA", 18, 88);
  pdf.setFontSize(26);
  pdf.text(`Diário de ${formatDate(report.workDate)}`, 18, 105);
  pdf.setFontSize(16);
  const projectTitle = pdf.splitTextToSize(
    `${clean(project.projectNumber) ? `${clean(project.projectNumber)} — ` : ""}${clean(project.name) || "Projeto"}`,
    158,
  );
  pdf.text(projectTitle, 18, 121);
  const coverY = 121 + projectTitle.length * 8 + 7;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(201, 211, 228);
  pdf.text(
    `${clean(project.client) || "Cliente não informado"}${clean(project.unit) ? ` • ${clean(project.unit)}` : ""}`,
    18,
    coverY,
  );
  pdf.setFontSize(8);
  pdf.text(
    `Situação: ${dailyWorkflowLabel(report.workflowStatus)} • ${dailyParticipantLabel(report.personDays)}`,
    18,
    coverY + 29,
  );
  pdf.text(
    `Responsável pelo preenchimento: ${clean(report.authorName) || "Não informado"}`,
    18,
    coverY + 37,
  );

  pdf.addPage();
  let detailPage = 2;
  addHeader("RELATÓRIO DIÁRIO DE OBRA", detailPage);
  let y = 36;
  y = section("Identificação", y);
  const left = margin + 4;
  const right = 108;
  y =
    Math.max(
      field("Data do diário", formatDate(report.workDate), left, y, 78),
      field("Situação", dailyWorkflowLabel(report.workflowStatus), right, y, 78),
    ) + 5;
  y =
    Math.max(
      field("Cliente", project.client, left, y, 78),
      field("Unidade", project.unit, right, y, 78),
    ) + 5;
  y =
    Math.max(
      field("Projeto", project.name, left, y, 78),
      field("Número do projeto", project.projectNumber, right, y, 78),
    ) + 6;
  y = section("Equipe e atividades executadas", y);
  y =
    field(
      "Equipe participante",
      (report.technicians || []).map((item) => item.name).join(" • "),
      left,
      y,
      contentWidth - 8,
    ) + 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...muted);
  pdf.text("FASES E ATIVIDADES", left, y);
  y += 4;
  if (!taskGroups.length) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...navy);
    pdf.text("Nenhuma atividade informada", left, y + 4);
    y += 11;
  } else {
    for (const group of taskGroups) {
      const activityText = group.activities.join(" • ") || "Sem atividade informada";
      const activityLines = pdf.splitTextToSize(activityText, contentWidth - 19);
      const cardHeight = Math.max(17, 12 + activityLines.length * 4.2);
      if (y + cardHeight > 270) {
        pdf.addPage();
        detailPage++;
        addHeader("RELATÓRIO DIÁRIO DE OBRA", detailPage);
        y = section("Equipe e atividades executadas — continuação", 36);
      }
      pdf.setFillColor(...soft);
      pdf.roundedRect(margin, y, contentWidth, cardHeight, 1.7, 1.7, "F");
      pdf.setFillColor(...accent);
      pdf.roundedRect(margin, y, 1.4, cardHeight, 0.7, 0.7, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(...navy);
      pdf.text(group.phase.toUpperCase(), left, y + 6);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(...navy);
      pdf.text(activityLines, left, y + 12, { lineHeightFactor: 1.2 });
      y += cardHeight + 4;
    }
  }
  y += 2;
  y = section("Descrição do que foi executado", y);
  const summaryLines = pdf.splitTextToSize(
    clean(report.summary) || "Não informado",
    contentWidth - 8,
  );
  let summaryOffset = 0;
  while (summaryOffset < summaryLines.length) {
    const availableLines = Math.max(1, Math.floor((276 - y) / 5.2));
    const pageLines = summaryLines.slice(
      summaryOffset,
      summaryOffset + availableLines,
    );
    pdf.setTextColor(...navy);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pageLines.forEach((text, index) =>
      pdf.text(String(text), left, y + index * 5.2),
    );
    summaryOffset += pageLines.length;
    y += pageLines.length * 5.2 + 7;
    if (summaryOffset < summaryLines.length) {
      pdf.addPage();
      detailPage++;
      addHeader("RELATÓRIO DIÁRIO DE OBRA", detailPage);
      y = section("Descrição do que foi executado — continuação", 36);
    }
  }
  if (clean(report.reportUrl)) {
    if (y > 265) {
      pdf.addPage();
      detailPage++;
      addHeader("RELATÓRIO DIÁRIO DE OBRA", detailPage);
      y = 36;
    }
    y = section("Referência complementar", y);
    field("Arquivo, link ou referência", report.reportUrl, left, y, contentWidth - 8);
  }

  const photos = (report.photos || []).filter((photo) =>
    String(photo.dataUrl || "").startsWith("data:image/"),
  );
  let pageNumber = pdf.getNumberOfPages();
  for (let index = 0; index < photos.length; index++) {
    const slot = index % 4;
    if (slot === 0) {
      pdf.addPage();
      pageNumber++;
      addHeader("REGISTROS FOTOGRÁFICOS", pageNumber);
    }
    const photo = photos[index];
    const column = slot % 2;
    const row = Math.floor(slot / 2);
    const gap = 8;
    const cardWidth = (contentWidth - gap) / 2;
    const cardHeight = 112;
    const cardX = margin + column * (cardWidth + gap);
    const cardY = 35 + row * 121;
    const padding = 4;
    pdf.setFillColor(250, 251, 253);
    pdf.setDrawColor(...line);
    pdf.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, "FD");
    pdf.setTextColor(...navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.text(`REGISTRO ${index + 1}`, cardX + padding, cardY + 7);
    const photoComment = clean(photo.caption);
    if (photoComment) {
      pdf.setTextColor(...muted);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.5);
      pdf.text(
        pdf.splitTextToSize(photoComment, cardWidth - 8).slice(0, 2),
        cardX + padding,
        cardY + 12,
      );
    }
    try {
      const properties = pdf.getImageProperties(photo.dataUrl);
      const imageTop = cardY + (photoComment ? 21 : 12);
      const maxWidth = cardWidth - padding * 2;
      const maxHeight = cardHeight - (photoComment ? 25 : 16);
      const ratio = properties.width / properties.height;
      let width = maxWidth;
      let height = width / ratio;
      if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
      }
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(cardX + padding, imageTop, maxWidth, maxHeight, 1, 1, "F");
      pdf.addImage(
        photo.dataUrl,
        properties.fileType || "JPEG",
        cardX + (cardWidth - width) / 2,
        imageTop + (maxHeight - height) / 2,
        width,
        height,
        undefined,
        "FAST",
      );
    } catch {
      pdf.setTextColor(174, 94, 98);
      pdf.text("Não foi possível renderizar esta imagem.", cardX + padding, cardY + 28);
    }
  }
  await addAboutsecPdfFooters(pdf, true);
  pdf.save(
    `Diario_de_obra_${safeName(project.projectNumber || project.name)}_${report.workDate}.pdf`,
  );
}

function DailyWorkTechnicianPicker({ technicians, selectedIds, onToggle }) {
  const [showOthers, setShowOthers] = useState(false);
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");
  const projectTeam = technicians.filter((item) => item.designated);
  const occasionalSelected = technicians.filter(
    (item) => !item.designated && selectedIds.includes(item.id),
  );
  const availableOthers = technicians.filter(
    (item) =>
      !item.designated &&
      !selectedIds.includes(item.id) &&
      (!normalizedSearch ||
        `${item.name} ${item.role || ""}`
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch)),
  );
  const visible = [...projectTeam, ...occasionalSelected];
  const technicianOption = (item, occasional = false) => (
    <label
      className={`${item.designated ? "designated" : ""}${occasional ? " occasional" : ""}`}
      key={item.id}
    >
      <input
        type="checkbox"
        checked={selectedIds.includes(item.id)}
        onChange={() => onToggle(item.id)}
      />
      <span>
        <strong>{item.name}</strong>
        <small>
          {item.role || "Técnico"}
          {item.designated
            ? " • Equipe do projeto"
            : occasional
              ? " • Participante eventual"
              : ""}
        </small>
      </span>
    </label>
  );
  return (
    <div className="daily-team-picker">
      <div className="daily-technician-list">
        {visible.map((item) =>
          technicianOption(item, !item.designated),
        )}
        {!visible.length && (
          <div className="empty">Nenhum profissional vinculado ao projeto.</div>
        )}
      </div>
      <button
        type="button"
        className="daily-add-participant"
        onClick={() => setShowOthers((current) => !current)}
      >
        <Icon name={showOthers ? "chevron" : "plus"} size={15} />
        {showOthers ? "Fechar lista" : "Adicionar participante eventual"}
      </button>
      {showOthers && (
        <div className="daily-other-technicians">
          <label>
            Buscar profissional
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nome ou função"
              autoFocus
            />
          </label>
          <div className="daily-technician-list">
            {availableOthers.map((item) => technicianOption(item, true))}
            {!availableOthers.length && (
              <div className="empty">
                Nenhum outro profissional disponível neste filtro.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DailyWorkEditModal({
  form,
  setForm,
  setup,
  toggle,
  token,
  saving,
  readingPhotos,
  setReadingPhotos,
  error,
  onClose,
  onSubmit,
}) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !saving && !readingPhotos) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, readingPhotos, saving]);
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal daily-edit-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>Editar diário de obra</h2>
            <p>Revise todas as informações e inclua ou remova fotos.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar">×</button>
        </div>
        <div className="daily-edit-grid">
          <label>Data do relatório<input type="date" value={form.workDate} onChange={(event) => setForm({ ...form, workDate: event.target.value })} /></label>
          <label className="report-pending-option daily-edit-confirm">
            <input type="checkbox" checked={form.workedConfirmed} onChange={(event) => setForm({ ...form, workedConfirmed: event.target.checked })} />
            <span><strong>Confirmar trabalho nesta data</strong><small>Contabiliza 1 dia de obra com {dailyParticipantLabel(form.technicianIds.length).toLocaleLowerCase("pt-BR")}.</small></span>
          </label>
        </div>
        <fieldset className="daily-choice-field">
          <legend>Equipe participante *</legend>
          <small>A equipe do projeto fica visível. Inclua outros profissionais somente quando tiverem participado eventualmente.</small>
          <DailyWorkTechnicianPicker
            technicians={setup.technicians}
            selectedIds={form.technicianIds}
            onToggle={(id) => toggle("technicianIds", id)}
          />
        </fieldset>
        <fieldset className="daily-choice-field">
          <legend>Fases e atividades executadas *</legend>
          <div className="daily-task-list">
            {setup.phases.map((phase) => (
              <div key={phase.id}>
                <strong><i style={{ background: phase.color }}></i>{phase.name}</strong>
                {setup.tasks.filter((task) => task.phaseId === phase.id).map((task) => (
                  <label key={task.id}><input type="checkbox" checked={form.taskIds.includes(task.id)} onChange={() => toggle("taskIds", task.id)} /><span>{task.name}</span></label>
                ))}
              </div>
            ))}
          </div>
        </fieldset>
        <label>Descrição do que foi executado *
          <textarea value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} />
          <AiReviewButton token={token} value={form.summary} onApply={(value) => setForm({ ...form, summary: value })} context="descrição atualizada de diário de obra" />
        </label>
        <TicketPhotoPicker photos={form.photos} onChange={(photos) => setForm((current) => ({ ...current, photos }))} title="Fotos do diário de obra" description="Até 5 fotos JPG, PNG ou WebP de até 4 MB cada. O comentário de cada foto é opcional." disabled={saving || readingPhotos} onBusyChange={setReadingPhotos} allowComments />
        <label>Link ou referência complementar (opcional)<input value={form.reportUrl} onChange={(event) => setForm({ ...form, reportUrl: event.target.value })} placeholder="Link, pasta ou referência documental" /></label>
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" disabled={saving || readingPhotos} onClick={onClose}>Cancelar</button>
          <button type="button" disabled={saving || readingPhotos} onClick={() => onSubmit("draft")}>Salvar alterações</button>
          <button type="button" className="primary" disabled={saving || readingPhotos || !setup.tasks.length} onClick={() => onSubmit("submit")}>{saving ? "Salvando…" : "Salvar e enviar"}</button>
        </div>
      </section>
    </div>
  );
}

function DailyWorkPage({ projects, role, token, onRefresh }) {
  const today = new Date().toISOString().slice(0, 10);
  const baseForm = {
    workDate: today,
    workedConfirmed: true,
    summary: "",
    reportUrl: "",
    photos: [],
    technicianIds: [],
    taskIds: [],
  };
  const [projectId, setProjectId] = useState(projects[0]?.id || "");
  const [records, setRecords] = useState([]);
  const [setup, setSetup] = useState({
    project: {},
    technicians: [],
    phases: [],
    tasks: [],
  });
  const [form, setForm] = useState(baseForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [readingPhotos, setReadingPhotos] = useState(false);
  const [error, setError] = useState("");
  const selected = projects.find(
    (item) => String(item.id) === String(projectId),
  );
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const defaults = (data) => ({
    ...baseForm,
    technicianIds: (data?.technicians || setup.technicians)
      .filter((item) => item.designated)
      .map((item) => item.id),
  });
  const load = async () => {
    if (!projectId) {
      setRecords([]);
      setSetup({ project: {}, technicians: [], phases: [], tasks: [] });
      return;
    }
    try {
      const recordResponse = await fetch(
        `${apiBase}/projects/${projectId}/daily-work`,
        { headers },
      );
      if (!recordResponse.ok) throw new Error();
      const recordData = await recordResponse.json();
      setRecords(recordData);
      if (!selected?.canEdit) {
        setSetup({ project: {}, technicians: [], phases: [], tasks: [] });
        setError("");
        return null;
      }
      const setupResponse = await fetch(
        `${apiBase}/projects/${projectId}/daily-work/setup`,
        { headers },
      );
      if (!setupResponse.ok) throw new Error();
      const setupData = await setupResponse.json();
      setSetup(setupData);
      setError("");
      return setupData;
    } catch {
      setError("Não foi possível carregar os dados do diário.");
    }
  };
  useEffect(() => {
    setEditingId(null);
    setForm(baseForm);
    load().then((data) => data && setForm(defaults(data)));
  }, [projectId]);
  const toggle = (field, id) =>
    setForm((current) => ({
      ...current,
      [field]: current[field].includes(id)
        ? current[field].filter((item) => item !== id)
        : [...current[field], id],
    }));
  const resetEditor = () => {
    setEditingId(null);
    setForm(defaults());
    setError("");
  };
  const editRecord = (item) => {
    setEditingId(item.id);
    setForm({
      workDate: item.workDate,
      workedConfirmed: Boolean(item.workedConfirmed),
      summary: item.summary || "",
      reportUrl: item.reportUrl || "",
      photos: item.photos || [],
      technicianIds: (item.technicians || []).map(
        (technician) => technician.id,
      ),
      taskIds: (item.tasks || []).map((task) => task.id),
    });
    setError("");
  };
  const exportRecord = async (item) => {
    setExportingId(item.id);
    setError("");
    try {
      await exportDailyWorkPdf(selected, item);
    } catch {
      setError("Não foi possível gerar o PDF deste diário de obra.");
    } finally {
      setExportingId(null);
    }
  };
  const submit = async (action) => {
    if (readingPhotos || saving) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/projects/${projectId}/daily-work${editingId ? `/${editingId}` : ""}`,
        {
          method: editingId ? "PATCH" : "POST",
          headers,
          body: JSON.stringify({ ...form, action }),
        },
      );
      const data = await response.json().catch(() => ({}));
      setSaving(false);
      if (response.ok) {
        resetEditor();
        await load();
        onRefresh();
        return;
      }
      const messages = {
        invalid_daily_work_photos: "Use até 5 fotos JPG, PNG ou WebP de até 4 MB cada.",
        daily_work_report_incomplete:
          "Para enviar, informe a equipe participante, ao menos uma atividade e a descrição do dia.",
        invalid_daily_work_report: "Informe uma data válida para o relatório.",
        daily_report_date_already_exists:
          "Já existe um relatório para esta data.",
        approved_daily_report_locked:
          "Somente administrador ou gestor pode editar um relatório aprovado.",
      };
      setError(messages[data.error] || "Não foi possível salvar o diário.");
    } catch {
      setError("Não foi possível salvar o diário. Verifique a conexão e tente novamente; as fotos selecionadas foram mantidas.");
    } finally { setSaving(false); }
  };
  const updateWorkflow = async (reportId, status) => {
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${projectId}/daily-work/${reportId}/workflow`,
      { method: "PATCH", headers, body: JSON.stringify({ status }) },
    );
    setSaving(false);
    if (response.ok) load();
    else setError("Não foi possível atualizar a aprovação do relatório.");
  };
  const pending = records.filter((item) =>
    ["draft", "awaiting_approval", "rejected"].includes(item.workflowStatus),
  ).length;
  const confirmed = records.filter((item) => item.workedConfirmed).length;
  const canApprove = ["administrator", "manager"].includes(role);
  return (
    <>
      <PageHeading
        title="Diário de obra"
        subtitle="Um relatório por dia, vinculado à equipe e às atividades executadas."
      />
      <div className="daily-project-selector panel">
        <label>
          Projeto / obra
          <select
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
          >
            <option value="">Selecione</option>
            {projects.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name} — {item.client}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <div>
            <span>
              Prazo estimado<b>{selected.planned} dias</b>
            </span>
            <span>
              Dias com trabalho<b>{confirmed}</b>
            </span>
            <span>
              Em preenchimento/aprovação
              <b className={pending ? "pending" : ""}>{pending}</b>
            </span>
          </div>
        )}
      </div>
      {selected ? (
        <div className="daily-page-grid">
          {selected.canEdit && !editingId && (
            <section className="panel daily-entry-form">
              <div className="daily-form-title">
                <div>
                  <h3>
                    {editingId
                      ? "Editar relatório diário"
                      : "Novo relatório diário"}
                  </h3>
                  <small>
                    {setup.project.dailyReportApprovalRequired
                      ? "Após o envio, seguirá para aprovação."
                      : "Após o envio, ficará como enviado."}
                  </small>
                </div>
                <span className="record-status new">
                  {editingId ? "Em edição" : "Em preenchimento"}
                </span>
              </div>
              <label>
                Data do relatório
                <input
                  type="date"
                  value={form.workDate}
                  onChange={(event) =>
                    setForm({ ...form, workDate: event.target.value })
                  }
                  required
                />
              </label>
              <fieldset className="daily-choice-field">
                <legend>Equipe participante *</legend>
                <small>
                  A equipe do projeto fica visível. Inclua outros profissionais
                  somente quando tiverem participado eventualmente.
                </small>
                <DailyWorkTechnicianPicker
                  technicians={setup.technicians}
                  selectedIds={form.technicianIds}
                  onToggle={(id) => toggle("technicianIds", id)}
                />
              </fieldset>
              <fieldset className="daily-choice-field">
                <legend>Fases e atividades executadas *</legend>
                <small>
                  Selecione uma ou mais atividades relacionadas a este
                  relatório.
                </small>
                <div className="daily-task-list">
                  {setup.phases.map((phase) => (
                    <div key={phase.id}>
                      <strong>
                        <i style={{ background: phase.color }}></i>
                        {phase.name}
                      </strong>
                      {setup.tasks
                        .filter((task) => task.phaseId === phase.id)
                        .map((task) => (
                          <label key={task.id}>
                            <input
                              type="checkbox"
                              checked={form.taskIds.includes(task.id)}
                              onChange={() => toggle("taskIds", task.id)}
                            />
                            <span>{task.name}</span>
                          </label>
                        ))}
                    </div>
                  ))}
                  {!setup.tasks.length && (
                    <div className="empty">
                      Cadastre atividades no Build Plan antes de criar o diário.
                    </div>
                  )}
                </div>
              </fieldset>
              <label>
                Descrição do que foi executado *
                <textarea
                  value={form.summary}
                  onChange={(event) =>
                    setForm({ ...form, summary: event.target.value })
                  }
                  required
                  placeholder="Descreva atividades, ocorrências, avanços e observações do dia"
                />
                <AiReviewButton
                  token={token}
                  value={form.summary}
                  onApply={(value) => setForm({ ...form, summary: value })}
                  context="descrição de diário de obra"
                />
              </label>
              <TicketPhotoPicker
                key={`${projectId}-${editingId || "new"}`}
                photos={form.photos}
                onChange={(photos) => setForm((current) => ({ ...current, photos }))}
                title="Fotos do diário de obra"
                description="Até 5 fotos JPG, PNG ou WebP de até 4 MB cada. O comentário de cada foto é opcional."
                disabled={saving || readingPhotos}
                onBusyChange={setReadingPhotos}
                allowComments
              />
              <label>
                Link ou referência complementar (opcional)
                <input
                  value={form.reportUrl}
                  onChange={(event) =>
                    setForm({ ...form, reportUrl: event.target.value })
                  }
                  placeholder="Referência textual; para anexar imagens, use Adicionar fotos"
                />
              </label>
              <label className="report-pending-option">
                <input
                  type="checkbox"
                  checked={form.workedConfirmed}
                  onChange={(event) =>
                    setForm({ ...form, workedConfirmed: event.target.checked })
                  }
                />
                <span>
                  <strong>Confirmar que houve trabalho nesta data</strong>
                  <small>
                    O sistema contabilizará automaticamente 1 dia de obra com{" "}
                    {dailyParticipantLabel(form.technicianIds.length).toLocaleLowerCase("pt-BR")}.
                  </small>
                </span>
              </label>
              {error && <div className="login-error">{error}</div>}
              <div className="daily-submit-actions">
                {editingId && (
                  <button
                    className="daily-cancel-edit"
                    disabled={saving || readingPhotos}
                    onClick={resetEditor}
                  >
                    Cancelar edição
                  </button>
                )}
                <button disabled={saving || readingPhotos} onClick={() => submit("draft")}>
                  {editingId ? "Salvar alterações" : "Salvar rascunho"}
                </button>
                <button
                  className="primary"
                  disabled={saving || readingPhotos || !setup.tasks.length}
                  onClick={() => submit("submit")}
                >
                  {saving
                    ? "Salvando…"
                    : editingId
                      ? "Salvar e enviar"
                      : "Enviar relatório"}
                </button>
              </div>
            </section>
          )}
          <section className="panel daily-record-list">
            <PanelTitle
              title="Histórico da obra"
              subtitle={`${records.length} relatórios`}
            />
            {records.map((item) => (
              <article
                key={item.id}
                className={editingId === item.id ? "editing" : ""}
              >
                <time>{formatDate(item.workDate)}</time>
                <div>
                  <strong>{item.summary || "Sem descrição"}</strong>
                  <span>
                    {item.technicians
                      ?.map((technician) => technician.name)
                      .join(", ") || "Equipe não informada"}{" "}
                    • {dailyParticipantLabel(item.personDays)}
                  </span>
                  <small>
                    {item.tasks
                      ?.map((task) => `${task.phaseName}: ${task.name}`)
                      .join(" • ") || "Atividades não vinculadas"}
                  </small>
                  {item.photos?.length > 0 && (
                    <div className="ticket-photo-preview">
                      {item.photos.map((photo, index) => (
                        <figure key={index}>
                          <a href={photo.dataUrl} download={photo.fileName} aria-label={`Baixar ${photo.fileName}`}>
                            <img src={photo.dataUrl} alt={photo.fileName} loading="lazy" />
                          </a>
                          <figcaption>{photo.fileName}</figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                  <em>
                    Responsável pelo preenchimento:{" "}
                    {item.authorName || "Não informado"}
                  </em>
                </div>
                <div className="daily-record-status">
                  <span
                    className={`record-status ${dailyWorkflowTone(item.workflowStatus)}`}
                  >
                    {dailyWorkflowLabel(item.workflowStatus)}
                  </span>
                  <button
                    className="daily-export-report"
                    disabled={exportingId === item.id}
                    onClick={() => exportRecord(item)}
                  >
                    <Icon name="download" size={14} />
                    {exportingId === item.id ? "Gerando…" : "Exportar PDF"}
                  </button>
                  {selected.canEdit &&
                    (item.workflowStatus !== "approved" || canApprove) && (
                      <button
                        className="edit-report"
                        disabled={saving || readingPhotos}
                        onClick={() => editRecord(item)}
                      >
                        Editar
                      </button>
                    )}
                  {canApprove &&
                    item.workflowStatus === "awaiting_approval" && (
                      <>
                        <button
                          className="approve"
                          disabled={saving || readingPhotos}
                          onClick={() => updateWorkflow(item.id, "approved")}
                        >
                          Aprovar
                        </button>
                        <button
                          disabled={saving || readingPhotos}
                          onClick={() => updateWorkflow(item.id, "rejected")}
                        >
                          Solicitar ajustes
                        </button>
                      </>
                    )}
                </div>
              </article>
            ))}
            {!records.length && (
              <div className="empty">
                Nenhum relatório registrado para esta obra.
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="panel empty">
          Selecione um projeto para acessar o diário da obra.
        </div>
      )}
      {editingId && selected?.canEdit && (
        <DailyWorkEditModal
          form={form}
          setForm={setForm}
          setup={setup}
          toggle={toggle}
          token={token}
          saving={saving}
          readingPhotos={readingPhotos}
          setReadingPhotos={setReadingPhotos}
          error={error}
          onClose={resetEditor}
          onSubmit={submit}
        />
      )}
    </>
  );
}

function DailyWorkPageLegacy({ projects, role, token, onRefresh }) {
  const today = new Date().toISOString().slice(0, 10);
  const emptyForm = {
    workDate: today,
    workedConfirmed: true,
    summary: "",
    reportUrl: "",
    technicianIds: [],
    taskIds: [],
  };
  const [projectId, setProjectId] = useState(projects[0]?.id || "");
  const [records, setRecords] = useState([]);
  const [setup, setSetup] = useState({
    project: {},
    technicians: [],
    phases: [],
    tasks: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const selected = projects.find(
    (item) => String(item.id) === String(projectId),
  );
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const load = async () => {
    if (!projectId) {
      setRecords([]);
      setSetup({ project: {}, technicians: [], phases: [], tasks: [] });
      return;
    }
    try {
      const [recordsResponse, setupResponse] = await Promise.all([
        fetch(`${apiBase}/projects/${projectId}/daily-work`, { headers }),
        fetch(`${apiBase}/projects/${projectId}/daily-work/setup`, { headers }),
      ]);
      if (!recordsResponse.ok || !setupResponse.ok) throw new Error();
      const [recordData, setupData] = await Promise.all([
        recordsResponse.json(),
        setupResponse.json(),
      ]);
      setRecords(recordData);
      setSetup(setupData);
      setForm((current) => ({
        ...current,
        technicianIds: current.technicianIds.length
          ? current.technicianIds
          : setupData.technicians
              .filter((item) => item.designated)
              .map((item) => item.id),
      }));
      setError("");
    } catch {
      setError("Não foi possível carregar os dados do diário.");
    }
  };
  useEffect(() => {
    setForm(emptyForm);
    load();
  }, [projectId]);
  const toggle = (field, id) =>
    setForm((current) => ({
      ...current,
      [field]: current[field].includes(id)
        ? current[field].filter((item) => item !== id)
        : [...current[field], id],
    }));
  const resetEditor = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      technicianIds: setup.technicians
        .filter((item) => item.designated)
        .map((item) => item.id),
    });
    setError("");
  };
  const editRecord = (item) => {
    setEditingId(item.id);
    setForm({
      workDate: item.workDate,
      workedConfirmed: Boolean(item.workedConfirmed),
      summary: item.summary || "",
      reportUrl: item.reportUrl || "",
      technicianIds: (item.technicians || []).map(
        (technician) => technician.id,
      ),
      taskIds: (item.tasks || []).map((task) => task.id),
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async (action) => {
    setSaving(true);
    setError("");
    const response = await fetch(
      `${apiBase}/projects/${projectId}/daily-work${editingId ? `/${editingId}` : ""}`,
      {
        method: editingId ? "PATCH" : "POST",
        headers,
        body: JSON.stringify({ ...form, action }),
      },
    );
    const data = await response.json().catch(() => ({}));
    setSaving(false);
    if (response.ok) {
      resetEditor();
      await load();
      onRefresh();
      return;
    }
    setError(
      data.error === "daily_work_report_incomplete"
        ? "Para enviar, informe a equipe participante, ao menos uma atividade e a descrição do dia."
        : data.error === "invalid_daily_work_report"
          ? "Informe uma data válida para o relatório."
          : data.error === "daily_report_date_already_exists"
            ? "Já existe um relatório para esta data."
            : data.error === "approved_daily_report_locked"
              ? "Somente administrador ou gestor pode editar um relatório aprovado."
              : "Não foi possível salvar o diário.",
    );
  };
  const updateWorkflow = async (reportId, status) => {
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${projectId}/daily-work/${reportId}/workflow`,
      { method: "PATCH", headers, body: JSON.stringify({ status }) },
    );
    setSaving(false);
    if (response.ok) load();
    else setError("Não foi possível atualizar a aprovação do relatório.");
  };
  const pending = records.filter((item) =>
    ["draft", "awaiting_approval", "rejected"].includes(item.workflowStatus),
  ).length;
  const confirmed = records.filter((item) => item.workedConfirmed).length;
  const canApprove = ["administrator", "manager"].includes(role);
  return (
    <>
      <PageHeading
        title="Diário de obra"
        subtitle="Um relatório por dia, vinculado à equipe e às atividades executadas."
      />
      <div className="daily-project-selector panel">
        <label>
          Projeto / obra
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Selecione</option>
            {projects.map((item) => (
              <option value={item.id} key={item.id}>
                {item.name} — {item.client}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <div>
            <span>
              Prazo estimado<b>{selected.planned} dias</b>
            </span>
            <span>
              Dias com trabalho<b>{confirmed}</b>
            </span>
            <span>
              Em preenchimento/aprovação
              <b className={pending ? "pending" : ""}>{pending}</b>
            </span>
          </div>
        )}
      </div>
      {selected ? (
        <div className="daily-page-grid">
          <section className="panel daily-entry-form">
            <div className="daily-form-title">
              <div>
                <h3>Novo relatório diário</h3>
                <small>
                  {setup.project.dailyReportApprovalRequired
                    ? "Após o envio, seguirá para aprovação."
                    : "Após o envio, ficará como enviado."}
                </small>
              </div>
              <span className="record-status new">Em preenchimento</span>
            </div>
            <label>
              Data do relatório
              <input
                type="date"
                value={form.workDate}
                onChange={(e) => setForm({ ...form, workDate: e.target.value })}
                required
              />
            </label>
            <fieldset className="daily-choice-field">
              <legend>Equipe participante *</legend>
              <small>
                Os técnicos designados para o projeto aparecem primeiro.
              </small>
              <div className="daily-technician-list">
                {setup.technicians.map((item) => (
                  <label
                    className={item.designated ? "designated" : ""}
                    key={item.id}
                  >
                    <input
                      type="checkbox"
                      checked={form.technicianIds.includes(item.id)}
                      onChange={() => toggle("technicianIds", item.id)}
                    />
                    <span>
                      <strong>{item.name}</strong>
                      <small>
                        {item.role || "Técnico"}
                        {item.designated ? " • Designado ao projeto" : ""}
                      </small>
                    </span>
                  </label>
                ))}
                {!setup.technicians.length && (
                  <div className="empty">Nenhum técnico cadastrado.</div>
                )}
              </div>
            </fieldset>
            <fieldset className="daily-choice-field">
              <legend>Fases e atividades executadas *</legend>
              <small>
                Selecione uma ou mais atividades relacionadas a este relatório.
              </small>
              <div className="daily-task-list">
                {setup.phases.map((phase) => (
                  <div key={phase.id}>
                    <strong>
                      <i style={{ background: phase.color }}></i>
                      {phase.name}
                    </strong>
                    {setup.tasks
                      .filter((task) => task.phaseId === phase.id)
                      .map((task) => (
                        <label key={task.id}>
                          <input
                            type="checkbox"
                            checked={form.taskIds.includes(task.id)}
                            onChange={() => toggle("taskIds", task.id)}
                          />
                          <span>{task.name}</span>
                        </label>
                      ))}
                  </div>
                ))}
                {!setup.tasks.length && (
                  <div className="empty">
                    Cadastre atividades no Build Plan antes de criar o diário.
                  </div>
                )}
              </div>
            </fieldset>
            <label>
              Descrição do que foi executado *
              <textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                required
                placeholder="Descreva atividades, ocorrências, avanços e observações do dia"
              />
              <AiReviewButton
                token={token}
                value={form.summary}
                onApply={(value) => setForm({ ...form, summary: value })}
                context="descrição atualizada de diário de obra"
              />
            </label>
            <label>
              Arquivo ou referência do relatório
              <input
                value={form.reportUrl}
                onChange={(e) =>
                  setForm({ ...form, reportUrl: e.target.value })
                }
                placeholder="Link, pasta ou nome do arquivo"
              />
            </label>
            <label className="report-pending-option">
              <input
                type="checkbox"
                checked={form.workedConfirmed}
                onChange={(e) =>
                  setForm({ ...form, workedConfirmed: e.target.checked })
                }
              />
              <span>
                <strong>Confirmar que houve trabalho nesta data</strong>
                <small>
                  O sistema contabilizará automaticamente 1 dia de obra e{" "}
                  {form.technicianIds.length} homem-dia.
                </small>
              </span>
            </label>
            {error && <div className="login-error">{error}</div>}
            <div className="daily-submit-actions">
              <button disabled={saving} onClick={() => submit("draft")}>
                Salvar rascunho
              </button>
              <button
                className="primary"
                disabled={saving || !setup.tasks.length}
                onClick={() => submit("submit")}
              >
                {saving ? "Salvando…" : "Enviar relatório"}
              </button>
            </div>
          </section>
          <section className="panel daily-record-list">
            <PanelTitle
              title="Histórico da obra"
              subtitle={`${records.length} relatórios`}
            />
            {records.map((item) => (
              <article key={item.id}>
                <time>{formatDate(item.workDate)}</time>
                <div>
                  <strong>{item.summary || "Sem descrição"}</strong>
                  <span>
                    {item.technicians
                      ?.map((technician) => technician.name)
                      .join(", ") || "Equipe não informada"}{" "}
                    • {item.personDays} homem-dia
                  </span>
                  <small>
                    {item.tasks
                      ?.map((task) => `${task.phaseName}: ${task.name}`)
                      .join(" • ") || "Atividades não vinculadas"}
                  </small>
                  <em>
                    Responsável pelo preenchimento:{" "}
                    {item.authorName || "Não informado"}
                  </em>
                </div>
                <div className="daily-record-status">
                  <span
                    className={`record-status ${dailyWorkflowTone(item.workflowStatus)}`}
                  >
                    {dailyWorkflowLabel(item.workflowStatus)}
                  </span>
                  {canApprove &&
                    item.workflowStatus === "awaiting_approval" && (
                      <>
                        <button
                          className="approve"
                          disabled={saving}
                          onClick={() => updateWorkflow(item.id, "approved")}
                        >
                          Aprovar
                        </button>
                        <button
                          disabled={saving}
                          onClick={() => updateWorkflow(item.id, "rejected")}
                        >
                          Solicitar ajustes
                        </button>
                      </>
                    )}
                </div>
              </article>
            ))}
            {!records.length && (
              <div className="empty">
                Nenhum relatório registrado para esta obra.
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="panel empty">
          Selecione um projeto para acessar o diário da obra.
        </div>
      )}
    </>
  );
}
function MobileServiceOrders({ items, onSelect }) {
  const [period, setPeriod] = useState("Este mês");
  const [status, setStatus] = useState("Todas");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [client, setClient] = useState("Todos");
  const [unit, setUnit] = useState("Todas");
  const [newestFirst, setNewestFirst] = useState(true);
  const [page, setPage] = useState(1);
  const today = new Date();
  const inPeriod = (item) => {
    if (period === "Todo o período") return true;
    const value = item.scheduledAt || item.createdAt;
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    if (period === "Próximo mês") return date.getFullYear() === new Date(today.getFullYear(), today.getMonth() + 1, 1).getFullYear() && date.getMonth() === (today.getMonth() + 1) % 12;
    if (period === "Este ano") return date.getFullYear() === today.getFullYear();
    if (period === "Últimos 7 dias") return date >= new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6) && date <= today;
    return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
  };
  const stateOf = (item) => {
    if (item.reportStatus === "pending" && ["finished", "closed"].includes(item.rawStatus)) return "Relatório pendente";
    if (["finished", "closed"].includes(item.rawStatus)) return "Finalizadas";
    if (isServiceOrderOverdue(item)) return "Atrasadas";
    if (item.rawStatus === "in_progress" || item.status === "Em execução") return "Em execução";
    return "Programadas";
  };
  const periodItems = items.filter(inPeriod);
  const counts = { Todas: periodItems.length, "Em execução": 0, Atrasadas: 0, Finalizadas: 0, "Relatório pendente": 0 };
  periodItems.forEach((item) => { const state = stateOf(item); if (counts[state] !== undefined) counts[state] += 1; });
  const clients = [...new Set(items.map((item) => item.client).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const units = [...new Set(items.filter((item) => client === "Todos" || item.client === client).map((item) => item.unit).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const filtered = periodItems.filter((item) =>
    (status === "Todas" || stateOf(item) === status) &&
    (client === "Todos" || item.client === client) &&
    (unit === "Todas" || item.unit === unit) &&
    `${item.code} ${item.title} ${item.client} ${item.unit || ""}`.toLocaleLowerCase("pt-BR").includes(search.toLocaleLowerCase("pt-BR")),
  ).sort((a, b) => (newestFirst ? -1 : 1) * (new Date(a.scheduledAt || a.createdAt || 0) - new Date(b.scheduledAt || b.createdAt || 0)));
  const visible = filtered.slice((page - 1) * 20, page * 20);
  const chooseStatus = (value) => { setStatus(value); setPage(1); };
  return <div className="resident-os-mobile">
    <div className="resident-os-title"><div><h2>Ordens de serviço</h2><p>Acompanhe os atendimentos da sua unidade.</p></div><select aria-label="Período das ordens" value={period} onChange={(event) => { setPeriod(event.target.value); setPage(1); }}><option>Este mês</option><option>Últimos 7 dias</option><option>Próximo mês</option><option>Este ano</option><option>Todo o período</option></select></div>
    <div className="resident-os-kpis">{[["Todas", "clipboard", "Total"], ["Em execução", "trend", "Em execução"], ["Atrasadas", "clock", "Atrasadas"], ["Finalizadas", "check", "Finalizadas"]].map(([key, icon, label]) => <button type="button" key={key} className={`resident-os-kpi ${key === status ? "active" : ""}`} onClick={() => chooseStatus(key)}><Icon name={icon} size={17} /><strong>{counts[key]}</strong><span>{label}</span></button>)}</div>
    <div className="resident-os-toolbar"><label><Icon name="search" size={18} /><input aria-label="Buscar ordens" placeholder="Buscar OS, cliente ou local" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label><button type="button" className={showFilters ? "active" : ""} onClick={() => setShowFilters(!showFilters)}>Filtros</button><button type="button" onClick={() => setNewestFirst(!newestFirst)} aria-label={newestFirst ? "Ordenar por data mais antiga" : "Ordenar por data mais recente"}>Data {newestFirst ? "↓" : "↑"}</button></div>
    {showFilters && <div className="resident-os-extra-filters"><label>Cliente<select value={client} onChange={(event) => { setClient(event.target.value); setUnit("Todas"); setPage(1); }}><option>Todos</option>{clients.map((name) => <option key={name}>{name}</option>)}</select></label><label>Unidade<select value={unit} onChange={(event) => { setUnit(event.target.value); setPage(1); }}><option>Todas</option>{units.map((name) => <option key={name}>{name}</option>)}</select></label></div>}
    <div className="resident-os-status-tabs">{["Todas", "Em execução", "Atrasadas", "Finalizadas", "Relatório pendente"].map((key) => <button type="button" key={key} className={status === key ? "active" : ""} onClick={() => chooseStatus(key)}>{key}<b>{counts[key]}</b></button>)}</div>
    <div className="resident-os-list">{visible.map((item) => { const state = stateOf(item); return <button type="button" className={`resident-os-card ${state === "Atrasadas" ? "late" : state === "Finalizadas" ? "done" : state === "Em execução" ? "running" : "pending"}`} key={item.id} onClick={() => onSelect(item)}><span className="resident-os-card-icon"><Icon name={state === "Finalizadas" ? "check" : state === "Atrasadas" ? "clock" : "clipboard"} size={21} /></span><span className="resident-os-card-main"><strong>{item.code}</strong><span>{item.client} · {item.unit || "Unidade não informada"}</span><small>{item.title} · Corretiva</small></span><span className="resident-os-card-end"><em>{state}</em><small>{formatSchedule(item.scheduledAt || item.createdAt)}</small></span><Icon name="arrow" size={15} /></button>; })}{!visible.length && <div className="resident-os-empty">Nenhuma ordem encontrada neste filtro.</div>}</div>
    {filtered.length > 20 && <div className="resident-os-pagination"><span>{Math.min(page * 20, filtered.length)} de {filtered.length}</span><button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</button><button type="button" disabled={page * 20 >= filtered.length} onClick={() => setPage(page + 1)}>Próxima</button></div>}
  </div>;
}

function ServiceOrdersPage({
  items,
  query,
  token,
  onRefresh,
  onCreate,
  canFill = false,
  canDelete = false,
  resident = false,
  pendingOrderId = null,
  onOrderOpened,
}) {
  const [tab, setTab] = useState("Todas");
  const [clientFilter, setClientFilter] = useState("Todos");
  const [unitFilter, setUnitFilter] = useState("Todas");
  const [activityFilter, setActivityFilter] = useState("Todas");
  const [selected, setSelected] = useState(null);
  useEffect(() => { const order = items.find((item) => Number(item.id) === Number(pendingOrderId)); if (pendingOrderId && order) { setSelected(order); onOrderOpened?.(); } }, [items, pendingOrderId, onOrderOpened]);
  const sourceItems = items;
  const clients = [
    ...new Set(sourceItems.map((item) => item.client).filter(Boolean)),
  ].sort();
  const units = [
    ...new Set(
      sourceItems
        .filter(
          (item) => clientFilter === "Todos" || item.client === clientFilter,
        )
        .map((item) => item.unit)
        .filter(Boolean),
    ),
  ].sort();
  const activities = [
    ...new Set(sourceItems.map((item) => item.title).filter(Boolean)),
  ].sort();
  const group = (item) => {
    if (
      item.reportStatus === "pending" &&
      ["finished", "closed", "completed", "completed_with_issue"].includes(
        item.rawStatus,
      )
    )
      return "Relatório pendente";
    if (["Finalizada", "Encerrada", "Realizada"].includes(item.status))
      return "Finalizadas";
    if (isServiceOrderOverdue(item)) return "Atrasadas";
    if (item.status === "Em execução") return "Em execução";
    return "Todas";
  };
  const count = (name) =>
    name === "Todas"
      ? sourceItems.length
      : sourceItems.filter((item) => group(item) === name).length;
  const visible = sourceItems.filter(
    (item) =>
      `${item.code} ${item.title} ${item.client} ${item.unit} ${item.owner}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (tab === "Todas" || group(item) === tab) &&
      (clientFilter === "Todos" || item.client === clientFilter) &&
      (unitFilter === "Todas" || item.unit === unitFilter) &&
      (activityFilter === "Todas" || item.title === activityFilter),
  );
  const clear = () => {
    setTab("Todas");
    setClientFilter("Todos");
    setUnitFilter("Todas");
    setActivityFilter("Todas");
  };
  const exportVisibleOrders = () => {
    const columns = [
      "Cliente",
      "Unidade",
      "Atividade",
      "Tipo",
      "Agendamento",
      "Status",
      "Responsável",
    ];
    const rows = visible.map((item) => [
      item.client,
      item.unit || "",
      item.title,
      "Corretiva",
      formatSchedule(item.date),
      group(item) === "Relatório pendente"
        ? "Resolvida • relatório pendente"
        : isServiceOrderOverdue(item)
          ? "Atrasada"
          : item.status,
      item.owner,
    ]);
    const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [columns, ...rows]
      .map((row) => row.map(escape).join(";"))
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }),
    );
    link.download = "ordens-de-servico-corretivas.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  return (
    <>
      {resident && <MobileServiceOrders items={items} onSelect={setSelected} />}
      <div className={resident ? "resident-os-desktop" : ""}>
      <PageHeading
        title="Ordem de serviço"
        subtitle="Gestão dos atendimentos corretivos originados por chamados."
      />
      <div className="os-kpis">
        <OsKpi
          icon="clipboard"
          tone="blue"
          label="Total"
          value={sourceItems.length}
          detail="100% das ordens"
          active={tab === "Todas"}
          onClick={() => setTab("Todas")}
        />
        <OsKpi
          icon="trend"
          tone="amber"
          label="Em execução"
          value={count("Em execução")}
          detail={percentage(count("Em execução"), sourceItems.length)}
          active={tab === "Em execução"}
          onClick={() => setTab("Em execução")}
        />
        <OsKpi
          icon="clock"
          tone="red"
          label="Atrasadas"
          value={count("Atrasadas")}
          detail={percentage(count("Atrasadas"), sourceItems.length)}
          active={tab === "Atrasadas"}
          onClick={() => setTab("Atrasadas")}
        />
        <OsKpi
          icon="check"
          tone="green"
          label="Finalizadas"
          value={count("Finalizadas")}
          detail={percentage(count("Finalizadas"), sourceItems.length)}
          active={tab === "Finalizadas"}
          onClick={() => setTab("Finalizadas")}
        />
        <OsKpi
          icon="document"
          tone="amber"
          label="Relatórios pendentes"
          value={count("Relatório pendente")}
          detail={percentage(count("Relatório pendente"), sourceItems.length)}
          active={tab === "Relatório pendente"}
          onClick={() => setTab("Relatório pendente")}
        />
      </div>
      <section className="panel os-filters">
        <label>
          Cliente
          <select
            value={clientFilter}
            onChange={(e) => {
              setClientFilter(e.target.value);
              setUnitFilter("Todas");
            }}
          >
            <option>Todos</option>
            {clients.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label>
          Unidade
          <select
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <option>Todas</option>
            {units.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label>
          Atividade
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
          >
            <option>Todas</option>
            {activities.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <div>
          <span>{visible.length} ordens encontradas</span>
          <button onClick={clear}>Limpar filtros</button>
        </div>
      </section>
      <section className="panel os-table">
        <div className="os-tabs">
          <div>
            {[
              "Todas",
              "Em execução",
              "Atrasadas",
              "Finalizadas",
              "Relatório pendente",
            ].map((item) => (
              <button
                key={item}
                className={tab === item ? "active" : ""}
                onClick={() => setTab(item)}
              >
                {item}
                <b>{count(item)}</b>
              </button>
            ))}
          </div>
          <button
            className="secondary-button spreadsheet-action compact os-export-action"
            onClick={exportVisibleOrders}
          >
            <span className="spreadsheet-action-icon excel">
              <Icon name="download" size={17} />
            </span>
            <span>
              <b>Exportar lista</b>
              <small>Arquivo CSV</small>
            </span>
          </button>
        </div>
        <div className="os-head">
          <span>Cliente</span>
          <span>Unidade</span>
          <span>Atividade</span>
          <span>Tipo</span>
          <span>Agendamento</span>
          <span>Status</span>
          <span>Responsável</span>
          <span></span>
        </div>
        {visible.map((item) => {
          const overdue = isServiceOrderOverdue(item);
          return (
            <button
              className={`os-row${overdue ? " overdue" : ""}`}
              key={item.code}
              onClick={() => setSelected(item)}
            >
              <div>
                <span className="client-logo green">
                  {initials(item.client)}
                </span>
                <div>
                  <strong>{item.client}</strong>
                  <small>{item.code}</small>
                </div>
              </div>
              <span>{item.unit || "Não informada"}</span>
              <div>
                <strong>{item.title}</strong>
                <small>
                  {item.ticketId
                    ? `Origem: CH-${String(item.ticketId).padStart(4, "0")}`
                    : item.signature}
                </small>
              </div>
              <span className="os-type">
                Corretiva
              </span>
              <span>{formatSchedule(item.date)}</span>
              <span
                className={`record-status ${overdue ? "overdue" : item.reportStatus === "pending" && ["finished", "closed"].includes(item.rawStatus) ? "waiting" : statusTone(item.status)}`}
              >
                {overdue
                  ? "Atrasada"
                  : item.reportStatus === "pending" &&
                      ["finished", "closed"].includes(item.rawStatus)
                    ? "Resolvida • relatório pendente"
                    : item.status}
              </span>
              <span>{item.owner}</span>
              <Icon name="arrow" size={16} />
            </button>
          );
        })}
        {!visible.length && (
          <div className="empty">Nenhuma ordem de serviço encontrada.</div>
        )}
      </section>
      </div>
      {selected && (
        <ServiceOrderModal
          order={selected}
          token={token}
          canFill={canFill}
          canDelete={canDelete}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function OsKpi({ icon, tone, label, value, detail, active = false, onClick }) {
  return (
    <button
      type="button"
      className={`os-kpi${active ? " active" : ""}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <div className={`metric-icon ${tone}`}>
        <Icon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </button>
  );
}
function percentage(value, total) {
  return `${total ? Math.round((value / total) * 100) : 0}% do total`;
}
function isServiceOrderOverdue(item, now = new Date()) {
  if (
    !item?.date ||
    item.date === "Não programada" ||
    ["Finalizada", "Encerrada", "Realizada"].includes(item.status)
  )
    return false;
  const scheduled = new Date(item.date);
  if (Number.isNaN(scheduled.getTime())) return false;
  const scheduledDay = new Date(
    scheduled.getFullYear(),
    scheduled.getMonth(),
    scheduled.getDate(),
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return scheduledDay < today;
}
function formatSchedule(value) {
  if (!value || value === "Não programada") return "Não programada";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
}

function RecordsPage({
  type,
  title,
  subtitle,
  items,
  operationalSetup,
  resident = false,
  query,
  token,
  onRefresh,
  onCreate,
}) {
  const [filter, setFilter] = useState(
    type === "chamado" && !(typeof window.matchMedia === "function" && window.matchMedia("(max-width: 760px)").matches) ? "Aguardando OS" : "Todos",
  );
  const [clientFilter, setClientFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const residentScopeInitialized = useRef(false);
  const [selected, setSelected] = useState(null);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    if (type !== "chamado") return;
    const timer = setInterval(() => setClock(Date.now()), 60000);
    return () => clearInterval(timer);
  }, [type]);
  const awaitingOrder = (item) => !item.serviceOrderId;
  const urgentScheduling = (item) =>
    awaitingOrder(item) && ticketWaitingInfo(item, clock).urgent;
  const awaitingOrderOver24Hours = (item) => {
    if (!awaitingOrder(item)) return false;
    const waiting = ticketWaitingInfo(item, clock);
    return waiting.hours !== null && waiting.hours >= 24;
  };
  const matchesFilter = (item) =>
    filter === "Todos" ||
    (filter === "Aguardando OS" && awaitingOrder(item)) ||
    (filter === "Aguardando OS +24h" && awaitingOrderOver24Hours(item)) ||
    (filter === "OS aberta" && !awaitingOrder(item)) ||
    (filter === "Agendamento urgente" && urgentScheduling(item));
  const clientOptions = (operationalSetup?.clients || [])
    .map((client) => [String(client.id), client.name])
    .sort(([, first], [, second]) => first.localeCompare(second, "pt-BR"));
  const unitOptions = Array.from(
    new Map(
      (operationalSetup?.units || [])
        .filter((unit) => !clientFilter || String(unit.clientId) === clientFilter)
        .map((unit) => [String(unit.id), unit.name]),
    ),
  ).sort(([, first], [, second]) => first.localeCompare(second, "pt-BR"));
  useEffect(() => {
    if (type !== "chamado" || !resident || residentScopeInitialized.current) return;
    const clients = (operationalSetup?.clients || []).slice().sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    if (!clients.length) return;
    const client = clients[0];
    const firstUnit = (operationalSetup?.units || [])
      .filter((unit) => String(unit.clientId) === String(client.id))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))[0];
    setClientFilter(String(client.id));
    setUnitFilter(firstUnit ? String(firstUnit.id) : "");
    residentScopeInitialized.current = true;
  }, [operationalSetup, resident, type]);
  const scopeItems = items.filter(
    (item) =>
      `${item.code} ${item.title} ${item.client} ${item.unit || ""} ${item.openedByName || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (!clientFilter ||
        String(item.clientId || item.client) === clientFilter) &&
      (!unitFilter ||
        String(item.unitId || `${item.client}-${item.unit}`) === unitFilter),
  );
  const visible = scopeItems.filter(matchesFilter);
  const filters =
    type === "os"
      ? [
          "Todos",
          "Programada",
          "Em execução",
          "Aguardando cliente",
          "Finalizada",
        ]
      : [
          "Aguardando OS",
          "Aguardando OS +24h",
          "Agendamento urgente",
          "OS aberta",
          "Todos",
        ];
  return (
    <>
      <PageHeading
        title={title}
        subtitle={subtitle}
        button="Novo chamado"
        onCreate={onCreate}
        className={type === "chamado" ? "ticket-page-heading" : ""}
      />
      <div
        className={`record-summary ${type === "chamado" ? "ticket-summary" : ""}`}
      >
        <button
          type="button"
          className={filter === "Todos" ? "active" : ""}
          onClick={() => setFilter("Todos")}
        >
          <span>Total</span>
          <b>{scopeItems.length}</b>
        </button>
        <button
          type="button"
          className={filter === "Aguardando OS" ? "active" : ""}
          onClick={() => setFilter("Aguardando OS")}
        >
          <span>Aguardando OS</span>
          <b>{scopeItems.filter(awaitingOrder).length}</b>
        </button>
        <button
          type="button"
          className={filter === "OS aberta" ? "active" : ""}
          onClick={() => setFilter("OS aberta")}
        >
          <span>OS abertas</span>
          <b>{scopeItems.filter((item) => !awaitingOrder(item)).length}</b>
        </button>
        <button
          type="button"
          className="urgent-scheduling-card"
          data-active={filter === "Agendamento urgente"}
          onClick={() => setFilter("Agendamento urgente")}
        >
          <span>Urgentes</span>
          <b>{scopeItems.filter(urgentScheduling).length}</b>
          <small>Sem OS após 3 dias úteis</small>
        </button>
      </div>
      <section className="panel records-panel">
        {type === "chamado" && (
          <div className="ticket-scope-filters">
            <div className="ticket-filter-heading">
              <div>
                <strong>Filtrar chamados</strong>
                <span>Refine a visão por cliente e unidade.</span>
              </div>
              {(clientFilter || unitFilter) && (
                <button
                  type="button"
                  onClick={() => {
                    setClientFilter("");
                    setUnitFilter("");
                  }}
                >
                  Limpar filtros
                </button>
              )}
            </div>
            <label>
              <span>Cliente</span>
              <select
                value={clientFilter}
                onChange={(event) => {
                  setClientFilter(event.target.value);
                  setUnitFilter("");
                }}
              >
                <option value="">Todos os clientes</option>
                {clientOptions.map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Unidade</span>
              <select
                value={unitFilter}
                onChange={(event) => setUnitFilter(event.target.value)}
              >
                <option value="">Todas as unidades</option>
                {unitOptions.map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div className="filters">
          {filters.map((f) => (
            <button
              className={filter === f ? "selected" : ""}
              onClick={() => setFilter(f)}
              key={f}
            >
              {f}
              {type === "chamado" && f === "Aguardando OS +24h" && (
                <b>{scopeItems.filter(awaitingOrderOver24Hours).length}</b>
              )}
            </button>
          ))}
        </div>
        <div className="records-head ticket-record-grid">
          <span>Registro</span>
          <span>Cliente / unidade</span>
          <span>Requisitante</span>
          <span>Data de abertura</span>
          <span>Origem</span>
          <span>Situação</span>
          <span></span>
        </div>
        {visible.map((item) => {
          const waiting = ticketWaitingInfo(item, clock);
          return (
            <div
              className={`record-row ticket-record-grid ticket-wait-${waiting.tone}`}
              key={item.code}
            >
              <div>
                <b>{item.code}</b>
                <strong>{item.title}</strong>
                <small>
                  {item.priority}
                  {waiting.hours !== null
                    ? ` • aberto há ${formatWaitingHours(waiting.hours)}`
                    : ""}
                </small>
              </div>
              <span>
                {item.client}
                {item.unit ? ` • ${item.unit}` : ""}
              </span>
              <span>{item.openedByName || "Não identificado"}</span>
              <span>
                {item.createdAt
                  ? formatSchedule(item.createdAt)
                  : "Não identificada"}
              </span>
              <span>{item.channel}</span>
              <span
                className={`record-status ${item.serviceOrderId ? "done" : `ticket-${waiting.tone}`}`}
              >
                {item.serviceOrderId ? "OS aberta" : waiting.label}
              </span>
              <button onClick={() => setSelected(item)} aria-label={`Abrir chamado ${item.code}`}>
                <Icon name="arrow" size={16} />
              </button>
            </div>
          );
        })}
        {!visible.length && (
          <div className="empty">Nenhum registro neste filtro.</div>
        )}
      </section>
      {selected &&
        (type === "os" ? (
          <ServiceOrderModal
            order={selected}
            token={token}
            onClose={() => setSelected(null)}
            onSaved={() => {
              setSelected(null);
              onRefresh();
            }}
          />
        ) : (
          <TicketModal
            ticket={selected}
            token={token}
            onClose={() => setSelected(null)}
            onSaved={() => {
              setSelected(null);
              onRefresh();
            }}
          />
        ))}
    </>
  );
}

function businessHoursBetween(start, end) {
  if (!(start instanceof Date) || Number.isNaN(start.getTime()) || end <= start)
    return 0;
  let cursor = new Date(start);
  let total = 0;
  while (cursor < end) {
    const nextDay = new Date(cursor);
    nextDay.setHours(24, 0, 0, 0);
    const segmentEnd = nextDay < end ? nextDay : end;
    if (cursor.getDay() !== 0 && cursor.getDay() !== 6)
      total += segmentEnd - cursor;
    cursor = segmentEnd;
  }
  return total / 3600000;
}
function ticketWaitingInfo(item, now = Date.now()) {
  if (item.serviceOrderId)
    return { tone: "handled", label: "OS aberta", hours: null, urgent: false };
  const raw = String(item.createdAt || "");
  const normalized =
    raw && !raw.includes("T") ? `${raw.replace(" ", "T")}Z` : raw;
  const openedDate = new Date(normalized);
  const openedAt = openedDate.getTime();
  const hours = Number.isFinite(openedAt)
    ? Math.max(0, Math.floor((now - openedAt) / 3600000))
    : null;
  const businessHours = Number.isFinite(openedAt)
    ? businessHoursBetween(openedDate, new Date(now))
    : 0;
  if (businessHours >= 72)
    return {
      tone: "critical",
      label: "Agendamento urgente",
      hours,
      urgent: true,
    };
  if (hours !== null && hours >= 24)
    return {
      tone: "attention",
      label: "Aguardando OS · +24h",
      hours,
      urgent: false,
    };
  return { tone: "normal", label: "Aguardando OS", hours, urgent: false };
}
function formatWaitingHours(hours) {
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainder = hours % 24;
  return remainder ? `${days}d ${remainder}h` : `${days}d`;
}

function PageHeading({ title, subtitle, button, onCreate, className = "" }) {
  const editClient = title === "Clientes e acessos";
  const hasAction = Boolean(button || editClient);
  return (
    <div
      className={`page-heading ${hasAction ? "page-heading-action-only" : "page-heading-redundant"} ${className}`.trim()}
    >
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <div className="page-actions">
        {editClient && (
          <button
            className="secondary-button edit-client-button"
            onClick={() =>
              window.dispatchEvent(new Event("aboutplan:edit-client"))
            }
          >
            Editar selecionado
          </button>
        )}
        {button && (
          <button className="primary" onClick={onCreate}>
            <Icon name="plus" size={16} />
            {button}
          </button>
        )}
      </div>
    </div>
  );
}
function statusTone(status) {
  if (["Finalizada", "Resolvido"].includes(status)) return "done";
  if (status.includes("Aguardando")) return "waiting";
  if (["Em execução", "Em atendimento"].includes(status)) return "doing";
  return "new";
}
async function downloadPagedSchedulePdf({ project, phases, tasks, days, title }) {
  const response = await fetch("/aboutplan-logo-white-transparent.png");
  if (!response.ok) throw new Error("logo_unavailable");
  const logo = new Uint8Array(await response.arrayBuffer());
  createPagedSchedulePdf(jsPDF, { project, phases, tasks, days, title, logo })
    .save(`${title} - ${project.name}.pdf`);
}

function ProjectWorkModal({
  project,
  permissions,
  role,
  token,
  onClose,
  onSaved,
  onRefresh,
}) {
  const canBuild =
    role === "administrator" || permissions?.keys?.includes("projects.manage");
  const [tab, setTab] = useState("schedule");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [schedule, setSchedule] = useState({ phases: [], tasks: [] });
  const [projectNumber, setProjectNumber] = useState(
    project.projectNumber || "",
  );
  const [commercialDays, setCommercialDays] = useState(
    Number(project.commercial) || 0,
  );
  const [responsibleUserId, setResponsibleUserId] = useState(
    project.responsibleUserId || "",
  );
  const [effectivationStartDate, setEffectivationStartDate] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [projectTechnicianIds, setProjectTechnicianIds] = useState([]);
  const [dailyApprovalRequired, setDailyApprovalRequired] = useState(false);
  const [showPhase, setShowPhase] = useState(false);
  const [showTask, setShowTask] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [phaseForm, setPhaseForm] = useState({ name: "", color: "#3474f2" });
  const taskFormRef = useRef(null);
  useEffect(() => {
    if (showTask) taskFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showTask, editingTask]);
  const emptyTask = {
    preliminary: !project.startDate && !project.endDate,
    preliminaryStartDay: 1,
    notes: "",
    phaseId: "",
    name: "",
    startDate: "",
    endDate: "",
    durationDays: 1,
    assignedTo: "",
    technicianIds: [],
    additionalTechnicianIds: [],
    status: "planned",
    progress: 0,
    predecessorId: "",
    dependencyLagDays: 0,
    workSaturday: false,
    workSunday: false,
  };
  const [taskForm, setTaskForm] = useState(emptyTask);
  const [records, setRecords] = useState([]);
  const [daily, setDaily] = useState({
    workDate: new Date().toISOString().slice(0, 10),
    projectDays: 1,
    personDays: 1,
    workedConfirmed: true,
    reportStatus: "pending",
    summary: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const loadSchedule = () =>
    fetch(`${apiBase}/projects/${project.id}/schedule`, { headers })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data) => {
        setSchedule(data);
        setProjectNumber(data.project?.projectNumber || "");
        setCommercialDays(Number(data.project?.commercialEstimatedDays) || 0);
        setResponsibleUserId(data.project?.responsibleUserId || "");
        setProjectTechnicianIds(data.project?.technicianIds || []);
        setDailyApprovalRequired(
          Boolean(data.project?.dailyReportApprovalRequired),
        );
      })
      .catch(() => setError("Não foi possível carregar o cronograma."));
  const loadDaily = () =>
    fetch(`${apiBase}/projects/${project.id}/daily-work`, { headers })
      .then((r) => r.json())
      .then(setRecords)
      .catch(() => {});
  const refreshProjectData = async () => {
    await Promise.all([loadSchedule(), Promise.resolve(onRefresh?.())]);
  };
  useEffect(() => {
    loadSchedule();
    loadDaily();
    fetch(`${apiBase}/setup/operational`, { headers })
      .then((r) => r.json())
      .then((data) => {
        setStaffUsers(data.users || []);
        setTechnicians(data.technicians || []);
      })
      .catch(() => {});
  }, [project.id]);
  const addPhase = async (e) => {
    e.preventDefault();
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${project.id}/phases${editingPhase ? `/${editingPhase.id}` : ""}`,
      {
        method: editingPhase ? "PATCH" : "POST",
        headers,
        body: JSON.stringify({
          ...phaseForm,
          sortOrder: editingPhase?.sortOrder ?? schedule.phases.length,
        }),
      },
    );
    setSaving(false);
    if (response.ok) {
      setPhaseForm({ name: "", color: "#3474f2" });
      setEditingPhase(null);
      setShowPhase(false);
      refreshProjectData();
    }
  };
  const editPhase = (phase) => {
    setEditingPhase(phase);
    setPhaseForm({ name: phase.name, color: phase.color });
    setShowPhase(true);
    setError("");
  };
  const deletePhase = async (phase) => {
    const taskCount = schedule.tasks.filter(
      (item) => item.phaseId === phase.id,
    ).length;
    if (taskCount) {
      setError(
        `A fase "${phase.name}" possui ${taskCount} atividade(s). Mova ou exclua essas atividades antes de apagar a fase.`,
      );
      return;
    }
    if (!window.confirm(`Excluir a fase "${phase.name}"?`)) return;
    const response = await fetch(
      `${apiBase}/projects/${project.id}/phases/${phase.id}`,
      { method: "DELETE", headers },
    );
    if (response.ok) {
      setError("");
      refreshProjectData();
    } else setError("Não foi possível excluir a fase.");
  };
  const addTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...taskForm,
      phaseId: Number(taskForm.phaseId),
      predecessorId: taskForm.predecessorId
        ? Number(taskForm.predecessorId)
        : null,
      durationDays: Number(taskForm.durationDays),
      dependencyLagDays: Number(taskForm.dependencyLagDays),
      sortOrder: editingTask?.sortOrder ?? schedule.tasks.length,
    };
    delete payload.additionalTechnicianIds;
    delete payload.preliminary;
    if (taskForm.preliminary) {
      payload.preliminaryStartDay = Number(taskForm.preliminaryStartDay);
      payload.startDate = null;
      payload.endDate = null;
      payload.predecessorId = null;
    } else delete payload.preliminaryStartDay;
    const response = await fetch(
      `${apiBase}/projects/${project.id}/tasks${editingTask ? `/${editingTask.id}` : ""}`,
      {
        method: editingTask ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      const taskId = editingTask?.id || data.id;
      const participantResponse = await fetch(
        `${apiBase}/projects/${project.id}/tasks/${taskId}/additional-participants`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            technicianIds: taskForm.additionalTechnicianIds,
          }),
        },
      );
      setSaving(false);
      if (!participantResponse.ok) {
        setError(
          "Atividade salva, mas não foi possível atualizar os participantes adicionais.",
        );
        return;
      }
      setTaskForm(emptyTask);
      setEditingTask(null);
      setShowTask(false);
      refreshProjectData();
    } else {
      setSaving(false);
      const data = await response.json().catch(() => ({}));
      setError(
        data.error === "task_phase_required"
          ? "Selecione uma fase para a atividade."
          : "Não foi possível salvar a atividade.",
      );
    }
  };
  const editTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      preliminary: Boolean(relativeTaskRange(task)),
      preliminaryStartDay: relativeTaskRange(task)?.first || 1,
      notes: task.notes || "",
      phaseId: task.phaseId || "",
      name: task.name,
      startDate: task.startDate || "",
      endDate: task.endDate || "",
      durationDays: task.durationDays,
      assignedTo: task.assignedTo || "",
      technicianIds: (task.plannedTeam || []).map((item) => item.id),
      additionalTechnicianIds: (task.additionalParticipants || []).map(
        (item) => item.id,
      ),
      status: task.status,
      progress: task.progress,
      predecessorId: task.predecessorId || "",
      dependencyLagDays: task.dependencyLagDays || 0,
      workSaturday: Boolean(task.workSaturday),
      workSunday: Boolean(task.workSunday),
    });
    setShowTask(true);
    setError("");
  };
  const deleteTask = async (task) => {
    if (!window.confirm(`Excluir a atividade "${task.name}"?`)) return;
    const response = await fetch(
      `${apiBase}/projects/${project.id}/tasks/${task.id}`,
      { method: "DELETE", headers },
    );
    if (response.ok) {
      setError("");
      if (editingTask?.id === task.id) {
        setEditingTask(null);
        setTaskForm(emptyTask);
        setShowTask(false);
      }
      refreshProjectData();
      return;
    }
    const data = await response.json().catch(() => ({}));
    setError(
      data.error === "task_has_daily_reports"
        ? `A atividade "${task.name}" já possui diário de obra vinculado e não pode ser excluída.`
        : "Não foi possível excluir a atividade.",
    );
  };
  const updateProgress = async (task, progress) => {
    const response = await fetch(
      `${apiBase}/projects/${project.id}/tasks/${task.id}`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          progress: Number(progress),
          status:
            Number(progress) === 100
              ? "completed"
              : Number(progress) > 0
                ? "in_progress"
                : "planned",
        }),
      },
    );
    if (response.ok) {
      setError("");
      refreshProjectData();
    } else setError("Não foi possível atualizar o progresso da atividade.");
  };
  const saveDaily = async (e) => {
    e.preventDefault();
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${project.id}/daily-work`,
      { method: "POST", headers, body: JSON.stringify(daily) },
    );
    setSaving(false);
    if (response.ok) {
      loadDaily();
      onSaved();
    } else setError("Não foi possível salvar o diário.");
  };
  const saveCommercialDays = async () => {
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${project.id}/commercial-plan`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          commercialEstimatedDays: Number(commercialDays),
        }),
      },
    );
    setSaving(false);
    if (response.ok) {
      await refreshProjectData();
      setError("");
    } else setError("Não foi possível salvar o planejamento comercial.");
  };
  const effectivateProject = async () => {
    if (!effectivationStartDate || !responsibleUserId) {
      setError("Informe a data inicial e o responsável antes de efetivar o projeto.");
      return;
    }
    if (!window.confirm("Efetivar este estudo e converter todos os dias relativos em datas reais?")) return;
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${project.id}/effectivate`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          startDate: effectivationStartDate,
          responsibleUserId: Number(responsibleUserId),
        }),
      },
    );
    const data = await response.json().catch(() => ({}));
    setSaving(false);
    if (response.ok) {
      setError("");
      await refreshProjectData();
      return;
    }
    setError(
      data.error === "study_tasks_without_relative_days"
        ? "Existem atividades sem dias relativos válidos. Revise o estudo antes de efetivar."
        : "Não foi possível efetivar o projeto.",
    );
  };
  const saveProjectNumber = async () => {
    const normalized = projectNumber.trim();
    if (!normalized) {
      setError("Informe o número do projeto.");
      return;
    }
    setSaving(true);
    const response = await fetch(`${apiBase}/projects/${project.id}/number`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ projectNumber: normalized }),
    });
    setSaving(false);
    if (response.ok) {
      await refreshProjectData();
      setError("");
    } else
      setError(
        response.status === 403
          ? "Seu perfil não permite alterar o número do projeto."
          : "Não foi possível salvar o número do projeto.",
      );
  };
  const saveResponsible = async () => {
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${project.id}/responsible`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({ responsibleUserId: Number(responsibleUserId) }),
      },
    );
    setSaving(false);
    if (response.ok) {
      await refreshProjectData();
      setError("");
    } else setError("Não foi possível salvar o responsável do projeto.");
  };
  const toggleProjectTechnician = (id) =>
    setProjectTechnicianIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  const saveDailySettings = async () => {
    setSaving(true);
    const response = await fetch(
      `${apiBase}/projects/${project.id}/daily-report-settings`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          technicianIds: projectTechnicianIds,
          dailyReportApprovalRequired: dailyApprovalRequired,
        }),
      },
    );
    setSaving(false);
    if (response.ok) {
      await refreshProjectData();
      setError("");
    } else setError("Não foi possível salvar a equipe e o fluxo do diário.");
  };
  const projectCompleted = ["completed", "closed"].includes(
    schedule.project?.status,
  );
  const hasScheduleTasks = schedule.tasks.length > 0;
  const allTasksCompleted = schedule.tasks.every(
    (item) => Number(item.progress) >= 100,
  );
  const updateProjectStatus = async (status) => {
    const completing = status === "completed";
    if (
      !window.confirm(
        completing
          ? hasScheduleTasks
            ? "Concluir este projeto? A data de conclusão será registrada agora."
            : "Concluir este projeto sem atividades no Build Plan? Use esta opção para fornecimentos ou entregas sem execução de serviços."
          : "Reabrir este projeto e retorná-lo para execução?",
      )
    )
      return;
    setSaving(true);
    setError("");
    const response = await fetch(`${apiBase}/projects/${project.id}/status`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ status }),
    });
    const data = await response.json().catch(() => ({}));
    setSaving(false);
    if (response.ok) {
      onSaved();
      return;
    }
    setError(
      data.error === "project_tasks_incomplete"
        ? "Todas as atividades precisam estar em 100% para concluir o projeto."
        : "Não foi possível atualizar o status do projeto.",
    );
  };
  const allDates = schedule.tasks
    .flatMap((task) => [task.startDate, task.endDate])
    .filter(Boolean);
  const start = allDates.length
    ? new Date(`${allDates.sort()[0]}T12:00:00`)
    : new Date();
  const end = allDates.length
    ? new Date(`${[...allDates].sort().at(-1)}T12:00:00`)
    : new Date(start.getTime() + 86400000 * 30);
  const span = Math.max(1, Math.round((end - start) / 86400000) + 1);
  const preliminaryDays = relativeScheduleDays(schedule.tasks);
  const calendarDays = preliminaryDays || timelineDays(start, end);
  const firstTimelineDay = start.toISOString().slice(0, 10);
  const lastTimelineDay = end.toISOString().slice(0, 10);
  const taskPosition = (task) =>
    preliminaryDays
      ? relativeTaskPosition(task, 1, preliminaryDays.length)
      : timelineTaskPosition(task, firstTimelineDay, lastTimelineDay);
  const taskOverduePosition = (task) =>
    overdueTaskPosition(task, firstTimelineDay, lastTimelineDay);
  const projectedTaskDates = projectTaskDates(taskForm, schedule.tasks);
  return (
    <div className="modal-backdrop">
      <section className="project-workspace">
        <div className="print-document-brand">
          <ReportBrandLogos />
          <strong>
            {tab === "build" ? "BUILD PLAN" : "CRONOGRAMA VISUAL"}
          </strong>
        </div>
        <div className="modal-head">
          <div>
            <span className="record-code">
              {schedule.project?.projectMode === "implementation_study"
                ? "ESTUDO DE IMPLANTAÇÃO"
                : "PROJETO"}{" "}
              • {project.client}
            </span>
            <h2>{project.name}</h2>
            <p>
              Projeto nº {projectNumber || "Não informado"} • {project.unit} •{" "}
              {project.worked} de {project.planned} dias consumidos
            </p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <div className="workspace-tabs">
          <button
            className={tab === "schedule" ? "active" : ""}
            onClick={() => setTab("schedule")}
          >
            <Icon name="calendar" size={16} /> Cronograma visual
          </button>
          {canBuild && (
            <button
              className={tab === "build" ? "active" : ""}
              onClick={() => setTab("build")}
            >
              <Icon name="folder" size={16} /> Build Plan
            </button>
          )}
        </div>
        {tab === "schedule" || tab === "build" ? (
          <>
            <div className="schedule-actions">
              <div>
                <strong>{schedule.phases.length}</strong>
                <span>fases</span>
                <strong>{schedule.tasks.length}</strong>
                <span>atividades</span>
                <strong>
                  {schedule.tasks.length
                    ? Math.round(
                        schedule.tasks.reduce(
                          (sum, item) => sum + item.progress,
                          0,
                        ) / schedule.tasks.length,
                      )
                    : 0}
                  %
                </strong>
                <span>avanço médio</span>
                <div className="schedule-date-summary">
                  <small>Início do projeto</small>
                  <b>
                    {formatDate(
                      project.startDate || (allDates.length ? start.toISOString().slice(0, 10) : null),
                    )}
                  </b>
                </div>
                <div className="schedule-date-summary">
                  <small>Conclusão planejada</small>
                  <b>
                    {formatDate(
                      project.endDate || (allDates.length ? end.toISOString().slice(0, 10) : null),
                    )}
                  </b>
                </div>
              </div>
              {schedule.tasks.some(isTaskOverdue) && (
                <div className="schedule-delay-legend">
                  <i></i>
                  <span>
                    Vermelho indica dias planejados já vencidos em atividades
                    ainda não concluídas.
                  </span>
                </div>
              )}
              <div className="schedule-action-buttons">
                {canBuild &&
                  (projectCompleted ? (
                    <button
                      className="project-reopen-button"
                      disabled={saving}
                      onClick={() => updateProjectStatus("execution")}
                    >
                      Reabrir projeto
                    </button>
                  ) : (
                    <button
                      className="project-complete-button"
                      disabled={saving || !allTasksCompleted}
                      title={
                        !allTasksCompleted
                          ? "Todas as atividades devem estar em 100%"
                          : !hasScheduleTasks
                            ? "Conclusão direta para fornecimento ou entrega sem atividades"
                            : ""
                      }
                      onClick={() => updateProjectStatus("completed")}
                    >
                      <Icon name="check" size={14} /> Concluir projeto
                    </button>
                  ))}
                <button
                  className="pdf-button"
                  disabled={exportingPdf}
                  onClick={async () => {
                    setExportingPdf(true);
                    try {
                      await downloadPagedSchedulePdf({
                        project: { ...project, projectNumber }, phases: schedule.phases,
                        tasks: schedule.tasks, days: calendarDays,
                        title: tab === "build" ? "Build Plan" : "Cronograma Visual",
                      });
                    } catch {
                      setError("Não foi possível gerar o PDF. Tente novamente.");
                    } finally {
                      setExportingPdf(false);
                    }
                  }}
                >
                  <Icon name="file" size={14} /> PDF paginado
                </button>
                <button
                  className="pdf-button"
                  disabled={exportingPdf}
                  onClick={async () => {
                    setExportingPdf(true);
                    try {
                      const response = await fetch("/aboutplan-logo-white-transparent.png");
                      if (!response.ok) throw new Error("logo_unavailable");
                      const logo = new Uint8Array(await response.arrayBuffer());
                      const pdf = createSinglePageSchedulePdf(jsPDF, {
                        project: { ...project, projectNumber }, phases: schedule.phases,
                        tasks: schedule.tasks, days: calendarDays,
                        title: tab === "build" ? "Build Plan" : "Cronograma Visual", logo,
                      });
                      pdf.save(`Cronograma-${projectNumber || project.id}-folha-unica.pdf`);
                    } catch {
                      setError("Não foi possível gerar o PDF. Tente novamente.");
                    } finally {
                      setExportingPdf(false);
                    }
                  }}
                >
                  <Icon name="file" size={14} /> PDF em folha única
                </button>
                {tab === "build" && (
                  <>
                    <button
                      onClick={() => {
                        setEditingPhase(null);
                        setPhaseForm({ name: "", color: "#3474f2" });
                        setShowPhase(!showPhase);
                      }}
                    >
                      + Fase
                    </button>
                    <button
                      className="primary"
                      disabled={!schedule.phases.length}
                      title={
                        !schedule.phases.length
                          ? "Crie uma fase antes de adicionar atividades"
                          : ""
                      }
                      onClick={() => {
                        setEditingTask(null);
                        setTaskForm(emptyTask);
                        setShowTask(!showTask);
                      }}
                    >
                      + Atividade
                    </button>
                  </>
                )}
              </div>
            </div>
            {tab === "build" && (
              <>
                <div className="project-lifecycle-card">
                  <div className="project-lifecycle-summary">
                    <span className="project-lifecycle-label">Tipo do registro</span>
                    <strong className="project-lifecycle-badge">
                      <i aria-hidden="true" />
                      {schedule.project?.projectMode === "implementation_study"
                        ? "Estudo de Implantação"
                        : "Projeto efetivado"}
                    </strong>
                    <p>
                      {schedule.project?.projectMode === "implementation_study"
                        ? "Cronograma preliminar em dias corridos, ainda sem datas de execução."
                        : "Cronograma de execução definido com datas reais."}
                    </p>
                  </div>
                  {schedule.project?.projectMode === "implementation_study" ? (
                    <div className="project-effectivation-form">
                      <label>
                        Data de início do projeto
                        <input
                          type="date"
                          value={effectivationStartDate}
                          onChange={(event) =>
                            setEffectivationStartDate(event.target.value)
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className="primary"
                        disabled={saving || !effectivationStartDate || !responsibleUserId}
                        onClick={effectivateProject}
                      >
                        Efetivar projeto
                      </button>
                      <small>
                        {responsibleUserId
                          ? "Ao efetivar, os dias relativos serão convertidos em datas corridas."
                          : "Selecione o responsável abaixo para habilitar a efetivação."}
                      </small>
                    </div>
                  ) : (
                    <div className="project-lifecycle-complete">
                      <span aria-hidden="true">✓</span>
                      <div>
                        <strong>Projeto em execução</strong>
                        <small>O cronograma utiliza datas reais.</small>
                      </div>
                    </div>
                  )}
                </div>
                <div className="project-number-row">
                  <label>
                    Número do projeto
                    <input
                      value={projectNumber}
                      onChange={(event) => setProjectNumber(event.target.value)}
                      maxLength="80"
                      placeholder="Ex.: 2026-001"
                    />
                  </label>
                  <button
                    onClick={saveProjectNumber}
                    disabled={saving || !projectNumber.trim()}
                  >
                    Salvar número
                  </button>
                  <span>
                    Alteração permitida somente para perfis com gestão de
                    projetos.
                  </span>
                </div>
                <div className="project-responsible-row">
                  <label>
                    Responsável pelo projeto e pela RO
                    <select
                      value={responsibleUserId}
                      onChange={(e) => setResponsibleUserId(e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {staffUsers.map((item) => (
                        <option value={item.id} key={item.id}>
                          {item.name} — {roleLabel(item.role)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    onClick={saveResponsible}
                    disabled={saving || !responsibleUserId}
                  >
                    Salvar responsável
                  </button>
                  <span>
                    Administradores e gestores também possuem acesso à RO.
                  </span>
                </div>
                <div className="project-daily-settings">
                  <div>
                    <strong>Equipe designada para os diários</strong>
                    <small>
                      Estes técnicos aparecerão primeiro no preenchimento.
                    </small>
                    <div>
                      {technicians.map((item) => (
                        <label key={item.id}>
                          <input
                            type="checkbox"
                            checked={projectTechnicianIds.includes(item.id)}
                            onChange={() => toggleProjectTechnician(item.id)}
                          />
                          {item.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="daily-approval-setting">
                    <input
                      type="checkbox"
                      checked={dailyApprovalRequired}
                      onChange={(e) =>
                        setDailyApprovalRequired(e.target.checked)
                      }
                    />
                    <span>
                      <strong>Exigir aprovação dos relatórios diários</strong>
                      <small>
                        Relatórios enviados ficarão aguardando administrador ou
                        gestor.
                      </small>
                    </span>
                  </label>
                  <button
                    type="button"
                    className="secondary-button team-save-button"
                    onClick={saveDailySettings}
                    disabled={saving}
                  >
                    <Icon name="check" size={15} />
                    {saving ? "Salvando…" : "Salvar equipe e fluxo"}
                  </button>
                </div>
                <div className="commercial-plan-row">
                  <div>
                    <label>
                      {preliminaryDays
                        ? "Prazo do estudo de implantação (dias corridos)"
                        : "Dias projetados no orçamento"}
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={commercialDays}
                        onChange={(e) => setCommercialDays(e.target.value)}
                      />
                    </label>
                    <button onClick={saveCommercialDays} disabled={saving}>
                      Salvar limite comercial
                    </button>
                  </div>
                  <div
                    className={
                      (schedule.project?.scheduleDays || 0) > commercialDays &&
                      commercialDays > 0
                        ? "commercial-plan-status exceeded"
                        : "commercial-plan-status"
                    }
                  >
                    <strong>
                      {schedule.project?.scheduleDays || 0}{" "}
                      {preliminaryDays ? "dias corridos no estudo" : "dias úteis no cronograma"}
                    </strong>
                    <span>
                      {commercialDays > 0
                        ? (schedule.project?.scheduleDays || 0) > commercialDays
                          ? `Fora do planejado comercialmente em ${(schedule.project?.scheduleDays || 0) - commercialDays} dia(s)`
                          : `Dentro dos ${commercialDays} dias projetados no orçamento`
                        : preliminaryDays
                          ? "Informe o prazo do estudo de implantação"
                          : "Informe os dias projetados no orçamento"}
                    </span>
                  </div>
                </div>
              </>
            )}
            {tab === "build" && showPhase && (
              <form className="schedule-inline-form" onSubmit={addPhase}>
                <label>
                  Nome da fase
                  <input
                    value={phaseForm.name}
                    onChange={(e) =>
                      setPhaseForm({ ...phaseForm, name: e.target.value })
                    }
                    required
                    placeholder="Ex.: Infraestrutura"
                  />
                </label>
                <label>
                  Cor
                  <input
                    type="color"
                    value={phaseForm.color}
                    onChange={(e) =>
                      setPhaseForm({ ...phaseForm, color: e.target.value })
                    }
                  />
                </label>
                <button className="primary" disabled={saving}>
                  {editingPhase ? "Salvar alterações" : "Salvar fase"}
                </button>
              </form>
            )}
            {tab === "build" && showTask && (
              <form
                className="schedule-inline-form task-form"
                ref={taskFormRef}
                onSubmit={addTask}
              >
                <label>
                  Fase *
                  <select
                    value={taskForm.phaseId}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, phaseId: e.target.value })
                    }
                    required
                  >
                    <option value="">Selecione uma fase</option>
                    {schedule.phases.map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Atividade
                  <input
                    value={taskForm.name}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, name: e.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  Tipo de cronograma
                  <select value={taskForm.preliminary ? "preliminary" : "dated"}
                    onChange={e => setTaskForm({ ...taskForm, preliminary: e.target.value === "preliminary" })}>
                    <option value="preliminary">Preliminar — dias corridos, sem datas</option>
                    <option value="dated">Com data definida</option>
                  </select>
                </label>
                {taskForm.preliminary && (
                  <label>Dia inicial no cronograma
                    <input type="number" min="1" step="1" required
                      value={taskForm.preliminaryStartDay}
                      onChange={e => setTaskForm({ ...taskForm, preliminaryStartDay: e.target.value })} />
                  </label>
                )}
                <label>
                  Iniciar após
                  <select
                    value={taskForm.predecessorId}
                    disabled={taskForm.preliminary}
                    onChange={(e) =>
                      setTaskForm({
                        ...taskForm,
                        predecessorId: e.target.value,
                        startDate: e.target.value ? "" : taskForm.startDate,
                      })
                    }
                  >
                    <option value="">Definir data inicial</option>
                    {schedule.tasks
                      .filter((item) => item.id !== editingTask?.id)
                      .map((item) => (
                        <option value={item.id} key={item.id}>
                          {item.name} — termina {formatDate(item.endDate)}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Intervalo após anterior
                  <input
                    type="number"
                    min="0"
                    value={taskForm.dependencyLagDays}
                    onChange={(e) =>
                      setTaskForm({
                        ...taskForm,
                        dependencyLagDays: Number(e.target.value),
                      })
                    }
                    disabled={taskForm.preliminary || !taskForm.predecessorId}
                  />
                </label>
                <label>
                  Duração (dias)
                  <input
                    type="number"
                    min="1"
                    value={taskForm.durationDays}
                    onChange={(e) =>
                      setTaskForm({
                        ...taskForm,
                        durationDays: Number(e.target.value),
                      })
                    }
                    required
                  />
                </label>
                <label>
                  Data inicial
                  <input
                    type="date"
                    value={
                      taskForm.predecessorId
                        ? projectedTaskDates.startDate || ""
                        : taskForm.startDate
                    }
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, startDate: e.target.value })
                    }
                    disabled={taskForm.preliminary || Boolean(taskForm.predecessorId)}
                    required={!taskForm.preliminary && !taskForm.predecessorId}
                  />
                </label>
                <label>
                  Responsável
                  <input
                    value={taskForm.assignedTo}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, assignedTo: e.target.value })
                    }
                  />
                </label>
                <fieldset className="daily-choice-field">
                  <legend>Equipe planejada</legend>
                  <small>
                    Colaboradores previstos no cronograma desta atividade.
                  </small>
                  <div className="daily-technician-list">
                    {technicians.map((person) => (
                      <label key={person.id}>
                        <input
                          type="checkbox"
                          checked={taskForm.technicianIds.includes(person.id)}
                          onChange={() =>
                            setTaskForm((current) => ({
                              ...current,
                              technicianIds: current.technicianIds.includes(
                                person.id,
                              )
                                ? current.technicianIds.filter(
                                    (id) => id !== person.id,
                                  )
                                : [...current.technicianIds, person.id],
                            }))
                          }
                        />
                        <span>
                          <strong>{person.name}</strong>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="daily-choice-field">
                  <legend>Participantes adicionais</legend>
                  <small>
                    Quem participou da execução sem alterar a equipe
                    originalmente planejada.
                  </small>
                  <div className="daily-technician-list">
                    {technicians.map((person) => (
                      <label key={person.id}>
                        <input
                          type="checkbox"
                          checked={taskForm.additionalTechnicianIds.includes(
                            person.id,
                          )}
                          onChange={() =>
                            setTaskForm((current) => ({
                              ...current,
                              additionalTechnicianIds:
                                current.additionalTechnicianIds.includes(
                                  person.id,
                                )
                                  ? current.additionalTechnicianIds.filter(
                                      (id) => id !== person.id,
                                    )
                                  : [
                                      ...current.additionalTechnicianIds,
                                      person.id,
                                    ],
                            }))
                          }
                        />
                        <span>
                          <strong>{person.name}</strong>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className="weekend-options">
                  <span>Dias de trabalho</span>
                  <label>
                    <input
                      type="checkbox"
                      disabled={taskForm.preliminary}
                      checked={taskForm.preliminary || taskForm.workSaturday}
                      onChange={(e) =>
                        setTaskForm({
                          ...taskForm,
                          workSaturday: e.target.checked,
                        })
                      }
                    />{" "}
                    Sábado
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      disabled={taskForm.preliminary}
                      checked={taskForm.preliminary || taskForm.workSunday}
                      onChange={(e) =>
                        setTaskForm({
                          ...taskForm,
                          workSunday: e.target.checked,
                        })
                      }
                    />{" "}
                    Domingo
                  </label>
                </div>
                <div className="projected-dates">
                  <span>{taskForm.preliminary ? "Período preliminar" : "Datas projetadas"}</span>
                  {taskForm.preliminary ? (
                    <strong>Dia {taskForm.preliminaryStartDay} → Dia {Number(taskForm.preliminaryStartDay) + Number(taskForm.durationDays) - 1} · {taskForm.durationDays} dias corridos</strong>
                  ) : <strong>
                    {projectedTaskDates.startDate
                      ? formatDate(projectedTaskDates.startDate)
                      : "Defina início ou predecessora"}{" "}
                    →{" "}
                    {projectedTaskDates.endDate
                      ? formatDate(projectedTaskDates.endDate)
                      : "—"}
                  </strong>}
                </div>
                {error && <p role="alert">{error}</p>}
                <button className="primary" disabled={saving}>
                  {editingTask ? "Salvar alterações" : "Salvar atividade"}
                </button>
              </form>
            )}
            <PrintSchedulePages
              project={project}
              title={tab === "build" ? "Build Plan" : "Cronograma Visual"}
              phases={schedule.phases}
              tasks={schedule.tasks}
              days={calendarDays}
            />
            {preliminaryDays && (
              <p>Cronograma preliminar · {preliminaryDays.length} dias corridos · Início a definir</p>
            )}
            <div className="gantt">
              <div
                className="gantt-scroll-content"
                style={{
                  minWidth: `${Math.max(1000, 620 + calendarDays.length * 46)}px`,
                  "--timeline-width": `${calendarDays.length * 46}px`,
                }}
              >
                <div className="gantt-head">
                  <span>Fase / atividade</span>
                  <span>Responsável</span>
                  <span>Duração</span>
                  <span>Progresso</span>
                  <CalendarAxis days={calendarDays} />
                </div>
                {schedule.phases.map((phase) => (
                  <div className="gantt-group" key={phase.id}>
                    <div className="gantt-phase">
                      <i style={{ background: phase.color }}></i>
                      <strong>{phase.name}</strong>
                      <span>
                        {
                          schedule.tasks.filter(
                            (item) => item.phaseId === phase.id,
                          ).length
                        }{" "}
                        atividades
                      </span>
                      {tab === "build" && (
                        <>
                          <button
                            className="edit-phase"
                            onClick={() => editPhase(phase)}
                            title="Editar fase"
                          >
                            Editar
                          </button>
                          <button
                            className="delete-phase"
                            onClick={() => deletePhase(phase)}
                            title="Excluir fase"
                            aria-label={`Excluir fase ${phase.name}`}
                          >
                            Excluir
                          </button>
                        </>
                      )}
                    </div>
                    {schedule.tasks
                      .filter((item) => item.phaseId === phase.id)
                      .map((task) => (
                        <GanttTask
                          key={task.id}
                          task={task}
                          color={phase.color}
                          position={taskPosition(task)}
                          overduePosition={taskOverduePosition(task)}
                          days={calendarDays}
                          onProgress={updateProgress}
                          onEdit={tab === "build" ? editTask : null}
                          onDelete={tab === "build" ? deleteTask : null}
                        />
                      ))}
                  </div>
                ))}
                {schedule.tasks
                  .filter((item) => !item.phaseId)
                  .map((task) => (
                    <GanttTask
                      key={task.id}
                      task={task}
                      color="#8390a5"
                      position={taskPosition(task)}
                      overduePosition={taskOverduePosition(task)}
                      days={calendarDays}
                      onProgress={updateProgress}
                      onEdit={tab === "build" ? editTask : null}
                      onDelete={tab === "build" ? deleteTask : null}
                    />
                  ))}
                {!schedule.tasks.length && !showTask && (
                  <div className="empty">
                    {tab === "build"
                      ? "Crie uma fase e adicione a primeira atividade do cronograma."
                      : "O Build Plan ainda não possui atividades."}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <form className="daily-workspace" onSubmit={saveDaily}>
            <div className="work-balance">
              <div>
                <span>Estimado</span>
                <b>{project.planned} dias</b>
              </div>
              <div>
                <span>Trabalhado</span>
                <b>{project.worked} dias</b>
              </div>
              <div>
                <span>Saldo</span>
                <b>{project.planned - project.worked} dias</b>
              </div>
            </div>
            <h3>Registrar dia trabalhado</h3>
            <div className="form-grid">
              <label>
                Data
                <input
                  type="date"
                  value={daily.workDate}
                  onChange={(e) =>
                    setDaily({ ...daily, workDate: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                Dias de projeto
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={daily.projectDays}
                  onChange={(e) =>
                    setDaily({ ...daily, projectDays: Number(e.target.value) })
                  }
                />
              </label>
              <label>
                Homem-dia
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={daily.personDays}
                  onChange={(e) =>
                    setDaily({ ...daily, personDays: Number(e.target.value) })
                  }
                />
              </label>
              <label>
                Relatório
                <select
                  value={daily.reportStatus}
                  onChange={(e) =>
                    setDaily({ ...daily, reportStatus: e.target.value })
                  }
                >
                  <option value="pending">Pendente</option>
                  <option value="draft">Rascunho</option>
                  <option value="submitted">Entregue</option>
                  <option value="not_required">Não necessário</option>
                </select>
              </label>
            </div>
            <label>
              Resumo
              <textarea
                value={daily.summary}
                onChange={(e) =>
                  setDaily({ ...daily, summary: e.target.value })
                }
              />
              <AiReviewButton
                token={token}
                value={daily.summary}
                onApply={(value) => setDaily({ ...daily, summary: value })}
                context="resumo de dia trabalhado em projeto"
              />
            </label>
            <button className="primary" disabled={saving}>
              Salvar dia trabalhado
            </button>
            <div className="daily-history">
              <h3>Histórico recente</h3>
              {records.slice(0, 8).map((item) => (
                <div key={item.id}>
                  <b>{formatDate(item.workDate)}</b>
                  <span>{item.projectDays} dia(s)</span>
                  <span>
                    {item.reportStatus === "pending"
                      ? "Relatório pendente"
                      : item.reportStatus}
                  </span>
                  <small>{item.summary}</small>
                </div>
              ))}
            </div>
          </form>
        )}
        {error && <div className="login-error workspace-error">{error}</div>}
      </section>
    </div>
  );
}

function CalendarAxis({ days }) {
  return (
    <div
      className="calendar-axis"
      style={{ gridTemplateColumns: `repeat(${days.length},minmax(0,1fr))` }}
    >
      {days.map((day) => (
        <span className={day.weekend ? "weekend" : ""} key={day.iso}>
          <b>{day.label}</b>
          <small>{day.number}</small>
        </span>
      ))}
    </div>
  );
}
function TimelineBackground({ days }) {
  return (
    <div
      className="timeline-background"
      style={{ gridTemplateColumns: `repeat(${days.length},1fr)` }}
    >
      {days.map((day) => (
        <span className={day.weekend ? "weekend" : ""} key={day.iso}></span>
      ))}
    </div>
  );
}
function PrintSchedulePages({ project, title, phases, tasks, days, singlePage = false }) {
  const pageSize = singlePage ? Math.max(1, days.length) : 21;
  const pages = [];
  for (let index = 0; index < days.length; index += pageSize)
    pages.push(days.slice(index, index + pageSize));
  const dayLabel = day => day?.relativeDay ? `Dia ${day.relativeDay}` : formatDate(day?.iso);
  const bar = (task, page) => {
    if (page[0].relativeDay) return relativeTaskPosition(task, page[0].relativeDay, page.at(-1).relativeDay);
    const first = page[0].iso,
      last = page.at(-1).iso;
    if (
      !task.startDate ||
      !task.endDate ||
      task.endDate < first ||
      task.startDate > last
    )
      return null;
    const start = task.startDate < first ? first : task.startDate;
    const end = task.endDate > last ? last : task.endDate;
    return {
      left: `${(dateDays(first, start) / page.length) * 100}%`,
      width: `${((dateDays(start, end) + 1) / page.length) * 100}%`,
    };
  };
  return (
    <div className={`print-schedule-pages${singlePage ? " print-single-page" : ""}`}>
      {pages.map((page, pageIndex) => (
        <section className="print-schedule-page" key={page[0].iso}>
          <div className="print-page-brand">
            <img src="/aboutplan-logo-white-hq.png" alt="AboutPlan" />
            <div>
              <strong>{title}</strong>
              <span>{project.projectNumber} • {project.name}</span>
              <span>{project.client} → {project.unit}</span>
              {days[0]?.relativeDay && (
                <span>Cronograma preliminar • {days.length} dias corridos • Início a definir</span>
              )}
            </div>
          </div>
          <div className="print-page-meta">
            <span>
              Período desta página{" "}
              <b>
                {dayLabel(page[0])} a {dayLabel(page.at(-1))}
              </b>
            </span>
            <span>
              Planejamento completo{" "}
              <b>
                {dayLabel(days[0])} a {dayLabel(days.at(-1))}
              </b>
            </span>
            <span>
              Página{" "}
              <b>
                {pageIndex + 1} de {pages.length}
              </b>
            </span>
          </div>
          <div className="print-gantt">
            <div className="print-gantt-row print-gantt-head">
              <span>Fase / atividade</span>
              <span>Responsável</span>
              <span>Duração</span>
              <span>Progresso</span>
              <CalendarAxis days={page} />
            </div>
            {phases.map((phase) => {
              const phaseTasks = tasks.filter(
                (task) => task.phaseId === phase.id,
              );
              return (
                <div className="print-phase-group" key={phase.id}>
                  <div className="print-phase">
                    <i style={{ background: phase.color }}></i>
                    <strong>{phase.name}</strong>
                  </div>
                  {phaseTasks.map((task) => {
                    const position = bar(task, page);
                    const delayed = overdueTaskPosition(
                      task,
                      page[0].iso,
                      page.at(-1).iso,
                    );
                    return (
                      <div className="print-gantt-row print-task" key={task.id}>
                        <div>
                          <strong>{task.name}</strong>
                          <small>
                            {relativeTaskLabel(task) || `${formatDate(task.startDate)} → ${formatDate(task.endDate)}`}
                          </small>
                        </div>
                        <span>{task.assignedTo || "Não atribuído"}</span>
                        <span>{task.durationDays} dias</span>
                        <b>{task.progress}%</b>
                        <div className="gantt-track">
                          <TimelineBackground days={page} />
                          {position && (
                            <i
                              style={{
                                ...position,
                                background: phase.color,
                                color: phase.color,
                              }}
                            ></i>
                          )}
                          {delayed && (
                            <i className="overdue-bar" style={delayed}></i>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
function GanttTask({
  task,
  color,
  position,
  overduePosition,
  days,
  onProgress,
  onEdit,
  onDelete,
}) {
  return (
    <div className="gantt-task">
      <div>
        <strong>
          {task.name}
          {onEdit && (
            <span className="task-row-actions">
              <button className="edit-task" onClick={() => onEdit(task)}>
                Editar
              </button>
              {onDelete && (
                <button
                  className="delete-task"
                  onClick={() => onDelete(task)}
                  aria-label={`Excluir atividade ${task.name}`}
                >
                  Excluir
                </button>
              )}
            </span>
          )}
        </strong>
        <small>
          {relativeTaskLabel(task) || `${formatDate(task.startDate)} → ${formatDate(task.endDate)}`}
        </small>
      </div>
      <span>{task.assignedTo || "Não atribuído"}</span>
      <span>{task.durationDays} dias</span>
      <select
        value={task.progress}
        onChange={(e) => onProgress(task, e.target.value)}
      >
        <option value="0">0%</option>
        <option value="25">25%</option>
        <option value="50">50%</option>
        <option value="75">75%</option>
        <option value="100">100%</option>
      </select>
      <div className="gantt-track">
        <TimelineBackground days={days} />
        <i style={{ ...position, background: color, color }}>
          <b style={{ width: `${task.progress}%` }}></b>
        </i>
        {overduePosition && (
          <i className="overdue-bar" style={overduePosition}></i>
        )}
      </div>
    </div>
  );
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function taskPendingStart(task) {
  if (!task.startDate || Number(task.progress || 0) >= 100) return null;
  const completedDays = Math.ceil(
    (Math.max(0, Number(task.durationDays) || 1) *
      Math.max(0, Number(task.progress) || 0)) /
      100,
  );
  let date = new Date(`${task.startDate}T12:00:00Z`);
  const allowed = (value) => {
    const day = value.getUTCDay();
    return (
      (day !== 6 || Boolean(task.workSaturday)) &&
      (day !== 0 || Boolean(task.workSunday))
    );
  };
  for (let count = 0; count < completedDays; count++) {
    do {
      date.setUTCDate(date.getUTCDate() + 1);
    } while (!allowed(date));
  }
  return date.toISOString().slice(0, 10);
}
function isTaskOverdue(task) {
  const pendingStart = taskPendingStart(task);
  return Boolean(pendingStart && pendingStart < todayIso());
}
function timelineTaskPosition(task, first, last) {
  if (!task.startDate) return { left: "0%", width: "3%" };
  const span = Math.max(1, dateDays(first, last) + 1);
  const left = (Math.max(0, dateDays(first, task.startDate)) / span) * 100;
  const width = Math.max(
    2,
    ((dateDays(task.startDate, task.endDate || task.startDate) + 1) / span) *
      100,
  );
  return { left: `${left}%`, width: `${Math.min(width, 100 - left)}%` };
}
function overdueTaskPosition(task, first, last) {
  const pendingStart = taskPendingStart(task);
  if (!pendingStart || pendingStart >= todayIso()) return null;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const overdueEnd = [
    task.endDate || task.startDate,
    yesterday.toISOString().slice(0, 10),
    last,
  ].sort()[0];
  const overdueStart = pendingStart < first ? first : pendingStart;
  if (overdueEnd < overdueStart) return null;
  const span = Math.max(1, dateDays(first, last) + 1);
  return {
    left: `${(dateDays(first, overdueStart) / span) * 100}%`,
    width: `${((dateDays(overdueStart, overdueEnd) + 1) / span) * 100}%`,
  };
}
function timelineDays(start, end) {
  const labels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push({
      iso: cursor.toISOString().slice(0, 10),
      label: labels[cursor.getDay()],
      number: cursor.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      }),
      weekend: [0, 6].includes(cursor.getDay()),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}
function dateDays(start, end) {
  if (!start || !end) return 0;
  return Math.round(
    (new Date(`${end}T12:00:00`) - new Date(`${start}T12:00:00`)) / 86400000,
  );
}
function projectTaskDates(form, tasks) {
  const allowed = (date) => {
    const day = date.getUTCDay();
    return (day !== 6 || form.workSaturday) && (day !== 0 || form.workSunday);
  };
  const next = (date) => {
    const result = new Date(date);
    do {
      result.setUTCDate(result.getUTCDate() + 1);
    } while (!allowed(result));
    return result;
  };
  const add = (date, days) => {
    let result = new Date(date);
    for (let i = 0; i < days; i++) result = next(result);
    return result;
  };
  let start;
  if (form.predecessorId) {
    const predecessor = tasks.find(
      (item) => String(item.id) === String(form.predecessorId),
    );
    if (!predecessor?.endDate) return { startDate: "", endDate: "" };
    start = next(new Date(`${predecessor.endDate}T12:00:00Z`));
    start = add(start, Math.max(0, Number(form.dependencyLagDays) || 0));
  } else if (form.startDate) {
    start = new Date(`${form.startDate}T12:00:00Z`);
    while (!allowed(start)) start = next(start);
  } else return { startDate: "", endDate: "" };
  const end = add(start, Math.max(1, Number(form.durationDays) || 1) - 1);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function ProjectWorkModalLegacy({ project, token, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    workDate: today,
    projectDays: 1,
    personDays: 1,
    workedConfirmed: true,
    reportStatus: "pending",
    summary: "",
  });
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch(`${apiBase}/projects/${project.id}/daily-work`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setRecords)
      .catch(() => {});
  }, [project.id, token]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/projects/${project.id}/daily-work`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        },
      );
      if (!response.ok) throw new Error();
      onSaved();
    } catch {
      setError("Não foi possível registrar o dia trabalhado.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal work-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <span className="record-code">PROJETO</span>
            <h2>{project.name}</h2>
            <p>
              {project.client} • {project.unit}
            </p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="work-balance">
          <div>
            <span>Estimado</span>
            <b>{project.planned} dias</b>
          </div>
          <div>
            <span>Trabalhado</span>
            <b>{project.worked} dias</b>
          </div>
          <div>
            <span>Saldo</span>
            <b>{project.planned - project.worked} dias</b>
          </div>
        </div>
        <h3 className="form-section-title">Registrar dia de trabalho</h3>
        <div className="form-grid">
          <label>
            Data trabalhada
            <input
              type="date"
              value={form.workDate}
              onChange={(e) => setForm({ ...form, workDate: e.target.value })}
              required
            />
          </label>
          <label>
            Dias de projeto
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={form.projectDays}
              onChange={(e) =>
                setForm({ ...form, projectDays: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Homem-dia
            <input
              type="number"
              min="0"
              step="0.5"
              value={form.personDays}
              onChange={(e) =>
                setForm({ ...form, personDays: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Situação do relatório
            <select
              value={form.reportStatus}
              onChange={(e) =>
                setForm({ ...form, reportStatus: e.target.value })
              }
            >
              <option value="pending">Relatório pendente</option>
              <option value="draft">Rascunho</option>
              <option value="submitted">Relatório entregue</option>
              <option value="not_required">Sem relatório necessário</option>
            </select>
          </label>
        </div>
        <label>
          Resumo do trabalho
          <textarea
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
          />
          <AiReviewButton
            token={token}
            value={form.summary}
            onApply={(value) => setForm({ ...form, summary: value })}
            context="resumo de trabalho executado em projeto"
          />
        </label>
        {records.length > 0 && (
          <div className="recent-work">
            <strong>Registros recentes</strong>
            {records.slice(0, 3).map((record) => (
              <span key={record.id}>
                {formatDate(record.workDate)} • {record.projectDays} dia(s) •{" "}
                {record.reportStatus === "pending"
                  ? "Relatório pendente"
                  : "Relatório " + record.reportStatus}
              </span>
            ))}
          </div>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving}>
            {saving ? "Salvando…" : "Confirmar dia trabalhado"}
          </button>
        </div>
      </form>
    </div>
  );
}

async function exportServiceOrderPdf(order, details, issueOptions) {
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const pageWidth = 210,
    pageHeight = 297,
    margin = 16,
    contentWidth = 178;
  const navy = [23, 35, 60],
    muted = [103, 116, 139],
    line = [222, 228, 237],
    soft = [245, 247, 251],
    accent = [67, 105, 151];
  const clean = (value) => String(value || "").trim();
  const dateTime = (value) => {
    if (!value) return "Não informado";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleString("pt-BR", {
          dateStyle: "short",
          timeStyle: "short",
        });
  };
  const fileSafe = (value) =>
    String(value || "OS")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9_-]+/gi, "_");
  const addLogo = async (x, y, maxWidth, maxHeight) => {
    try {
      const logo = await imageAsDataUrl("/aboutplan-logo-white-hq.png");
      const properties = pdf.getImageProperties(logo);
      const ratio = properties.width / properties.height;
      let width = maxWidth,
        height = width / ratio;
      if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
      }
      pdf.addImage(
        logo,
        "PNG",
        x,
        y + (maxHeight - height) / 2,
        width,
        height,
        undefined,
        "SLOW",
      );
    } catch {}
  };
  const header = async (title, page) => {
    pdf.setFillColor(...navy);
    pdf.rect(0, 0, pageWidth, 25, "F");
    await addLogo(margin, 4, 48, 17);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text(title, pageWidth - margin, 10, { align: "right" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(`Página ${page}`, pageWidth - margin, 16, { align: "right" });
    pdf.setDrawColor(197, 119, 73);
    pdf.setLineWidth(0.7);
    pdf.line(margin, 25, pageWidth - margin, 25);
  };
  const sectionTitle = (title, y) => {
    pdf.setFillColor(...soft);
    pdf.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, "F");
    pdf.setTextColor(...navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(title.toUpperCase(), margin + 4, y + 6);
    return y + 14;
  };
  const field = (label, value, x, y, width) => {
    pdf.setTextColor(...muted);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(label.toUpperCase(), x, y);
    pdf.setTextColor(...navy);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    const lines = pdf.splitTextToSize(clean(value) || "Não informado", width);
    pdf.text(lines, x, y + 5, { lineHeightFactor: 1.25 });
    return y + 5 + lines.length * 4.2;
  };
  const addTextBlock = async (title, text, y, pageNumber) => {
    const fontSize = 10;
    const lineHeight = 5.2;
    const lines = pdf.splitTextToSize(
      clean(text) || "Não informado",
      contentWidth - 8,
    );
    for (let offset = 0; offset < lines.length;) {
      if (y > (offset === 0 ? 250 : 267)) {
        pdf.addPage();
        pageNumber.value++;
        await header("RELATÓRIO DE ORDEM DE SERVIÇO", pageNumber.value);
        y = 34;
      }
      if (offset === 0) y = sectionTitle(title, y);
      const available = Math.max(1, Math.floor((276 - y) / lineHeight));
      const part = lines.slice(offset, offset + available);
      pdf.setTextColor(...navy);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(fontSize);
      pdf.setCharSpace(0);
      part.forEach((lineText, lineIndex) => {
        pdf.text(String(lineText), margin + 4, y + lineIndex * lineHeight);
      });
      y += part.length * lineHeight + 6;
      offset += part.length;
    }
    return y;
  };
  pdf.setFillColor(...navy);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");
  await addLogo(18, 20, 92, 26);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("RELATÓRIO DE ATENDIMENTO", 18, 89);
  pdf.setFontSize(28);
  pdf.text(order.code || `OS-${String(order.id).padStart(4, "0")}`, 18, 106);
  pdf.setFontSize(17);
  const titleLines = pdf.splitTextToSize(
    clean(order.title || details.title),
    150,
  );
  pdf.text(titleLines, 18, 121);
  let coverY = 121 + titleLines.length * 8 + 8;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(201, 211, 228);
  pdf.text(
    `${clean(details.clientName || order.client) || "Sem cliente"}${clean(details.unitName || order.unit) ? ` • ${clean(details.unitName || order.unit)}` : ""}`,
    18,
    coverY,
  );
  pdf.setFillColor(197, 119, 73);
  pdf.rect(18, coverY + 12, 52, 2, "F");
  pdf.setFontSize(8);
  pdf.text(
    `Atendimento concluído em ${dateTime(details.attendanceEndedAt || details.finishedAt)}`,
    18,
    coverY + 29,
  );
  pdf.text("Documento operacional destinado ao cliente", 18, coverY + 37);
  pdf.addPage();
  const pageNumber = { value: 2 };
  await header("RELATÓRIO DE ORDEM DE SERVIÇO", pageNumber.value);
  let y = 36;
  y = sectionTitle("Identificação", y);
  const left = margin + 4,
    right = 108;
  y =
    Math.max(
      field("Ordem de serviço", order.code || details.id, left, y, 78),
      field("Situação", "Concluída", right, y, 78),
    ) + 4;
  y =
    Math.max(
      field("Cliente", details.clientName || order.client, left, y, 78),
      field("Unidade", details.unitName || order.unit, right, y, 78),
    ) + 6;
  y = sectionTitle("Solicitação", y);
  y =
    Math.max(
      field("Título", details.title || order.title, left, y, 78),
      field(
        "Chamado de origem",
        details.ticketId
          ? `CH-${String(details.ticketId).padStart(4, "0")}`
          : "Não identificado",
        right,
        y,
        78,
      ),
    ) + 4;
  y =
    Math.max(
      field(
        "Chamado aberto por",
        details.ticketOpenedByName ||
          order.ticketOpenedByName ||
          "Não identificado",
        left,
        y,
        78,
      ),
      field(
        "OS aberta por",
        details.serviceOrderOpenedByName ||
          order.serviceOrderOpenedByName ||
          "Não identificado",
        right,
        y,
        78,
      ),
    ) + 4;
  y =
    Math.max(
      field(
        "Solicitação registrada em",
        dateTime(details.createdAt),
        left,
        y,
        78,
      ),
      field(
        "Atendimento previsto para",
        dateTime(details.scheduledAt || order.scheduledAt),
        right,
        y,
        78,
      ),
    ) + 5;
  y = await addTextBlock(
    "Descrição da solicitação",
    details.description || order.description,
    y,
    pageNumber,
  );
  y = sectionTitle("Atendimento realizado", y);
  y =
    Math.max(
      field(
        "Técnico(s) / responsável(is)",
        details.assignedTo || order.owner,
        left,
        y,
        78,
      ),
      field(
        "Política de assinatura",
        details.signaturePolicy === "required"
          ? "Assinatura do cliente obrigatória"
          : "Relatório informativo",
        right,
        y,
        78,
      ),
    ) + 4;
  y =
    Math.max(
      field("Início", dateTime(details.attendanceStartedAt), left, y, 78),
      field("Fim", dateTime(details.attendanceEndedAt), right, y, 78),
    ) + 4;
  y =
    field(
      "Responsável pelo preenchimento",
      details.signedBy || order.owner,
      left,
      y,
      contentWidth - 8,
    ) + 4;
  if (details.customerSignerName)
    y =
      Math.max(
        field("Assinado pelo cliente", details.customerSignerName, left, y, 78),
        field(
          "Data da assinatura",
          dateTime(details.customerSignedAt),
          right,
          y,
          78,
        ),
      ) + 4;
  const issueLabels = (details.issueTypes || []).map(
    (value) => issueOptions.find((item) => item[0] === value)?.[1] || value,
  );
  if (issueLabels.length)
    y =
      field(
        "Diagnóstico registrado",
        issueLabels.join(" • "),
        left,
        y,
        contentWidth - 8,
      ) + 5;
  y = await addTextBlock(
    "Relatório e resolução",
    details.serviceReport,
    y,
    pageNumber,
  );
  const imagePhotos = (details.photos || []).filter((photo) =>
    String(photo.url || "").startsWith("data:image/"),
  );
  if (imagePhotos.length) {
    for (let index = 0; index < imagePhotos.length; index++) {
      const slot = index % 4;
      if (slot === 0) {
        pdf.addPage();
        pageNumber.value++;
        await header("EVIDÊNCIAS FOTOGRÁFICAS", pageNumber.value);
      }
      const photo = imagePhotos[index];
      const column = slot % 2;
      const row = Math.floor(slot / 2);
      const gap = 8;
      const cardWidth = (contentWidth - gap) / 2;
      const cardHeight = 112;
      const cardX = margin + column * (cardWidth + gap);
      const cardY = 35 + row * 121;
      const cardPadding = 4;
      pdf.setFillColor(250, 251, 253);
      pdf.setDrawColor(...line);
      pdf.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, "FD");
      pdf.setTextColor(...navy);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.text(
        photo.type === "before"
          ? "REGISTRO RECEBIDO NO CHAMADO"
          : "REGISTRO DO ATENDIMENTO",
        cardX + cardPadding,
        cardY + 7,
      );
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.5);
      pdf.setTextColor(...muted);
      const caption = pdf.splitTextToSize(
        clean(photo.caption) || `Evidência ${index + 1}`,
        cardWidth - cardPadding * 2,
      );
      pdf.text(caption.slice(0, 3), cardX + cardPadding, cardY + 97, { lineHeightFactor: 1.15 });
      try {
        const properties = pdf.getImageProperties(photo.url);
        const imageTop = cardY + 12;
        const maxWidth = cardWidth - cardPadding * 2,
          maxHeight = 76;
        const ratio = properties.width / properties.height;
        let width = maxWidth,
          height = width / ratio;
        if (height > maxHeight) {
          height = maxHeight;
          width = height * ratio;
        }
        const x = cardX + (cardWidth - width) / 2;
        const imageY = imageTop + (maxHeight - height) / 2;
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(...line);
        pdf.roundedRect(
          cardX + cardPadding,
          imageTop,
          maxWidth,
          maxHeight,
          1,
          1,
          "FD",
        );
        pdf.addImage(
          photo.url,
          properties.fileType || "JPEG",
          x,
          imageY,
          width,
          height,
          undefined,
          "FAST",
        );
      } catch {
        pdf.setTextColor(174, 94, 98);
        pdf.text(
          "Não foi possível renderizar esta imagem.",
          cardX + cardPadding,
          cardY + 28,
        );
      }
    }
  }
  await addAboutsecPdfFooters(pdf, true);
  pdf.save(`Relatorio_${fileSafe(order.code || `OS_${order.id}`)}.pdf`);
}

function TeamMultiSelect({
  options = [],
  selected = [],
  onChange,
  disabled = false,
  excludeName = "",
}) {
  const [search, setSearch] = useState("");
  const normalized = search.trim().toLowerCase();
  const available = options.filter((person) => person.name !== excludeName);
  const visible = available.filter((person) =>
    `${person.name} ${person.functionName || person.role || ""}`
      .toLowerCase()
      .includes(normalized),
  );
  const chosen = available.filter((person) => selected.includes(person.id));
  const toggle = (id) =>
    onChange(
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );
  return (
    <div className="team-multi-select">
      <div className="team-selection-summary">
        <span>
          <b>{chosen.length}</b> técnico(s) selecionado(s)
        </span>
        {chosen.length > 0 && (
          <div>
            {chosen.map((person) => (
              <button
                type="button"
                key={person.id}
                onClick={() => toggle(person.id)}
                disabled={disabled}
              >
                {person.name}
                <i>×</i>
              </button>
            ))}
          </div>
        )}
      </div>
      <label className="team-search">
        <Icon name="search" size={15} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar técnico por nome ou função"
          disabled={disabled}
        />
      </label>
      <div className="team-option-list">
        {visible.map((person) => (
          <label
            className={selected.includes(person.id) ? "selected" : ""}
            key={person.id}
          >
            <input
              type="checkbox"
              checked={selected.includes(person.id)}
              onChange={() => toggle(person.id)}
              disabled={disabled}
            />
            <span>
              <strong>{person.name}</strong>
              <small>
                {person.functionName || person.role || "Colaborador"}
              </small>
            </span>
          </label>
        ))}
        {!visible.length && (
          <p>
            {normalized
              ? "Nenhum técnico encontrado."
              : "Nenhum outro técnico ativo disponível."}
          </p>
        )}
      </div>
    </div>
  );
}

function ServiceOrderModal({ order, token, onClose, onSaved, canFill = true, canDelete = false }) {
  const issueOptions = [
    ["network", "Problema de rede"],
    ["electrical", "Problema de elétrica"],
    ["software", "Problema de software"],
    ["connectorization", "Problema de conectorização"],
    ["frozen_equipment", "Equipamento travado"],
    ["damaged_equipment", "Equipamento danificado"],
    ["burned_equipment", "Equipamento queimado"],
    ["configuration_failure", "Falha de configuração"],
  ];
  const scheduled = String(order.scheduledAt || "").slice(0, 16);
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState({
    issueTypes: [],
    attendanceStartedAt: scheduled,
    attendanceEndedAt: "",
    serviceReport: "",
    internalNotes: "",
    signedBy: order.owner === "Não atribuído" ? "" : order.owner,
  });
  const [attachments, setAttachments] = useState([]);
  const [photoCaptions, setPhotoCaptions] = useState({});
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamIds, setTeamIds] = useState([]);
  const [planning, setPlanning] = useState({
    scheduledAt: scheduled,
    assignedTechnicianId: "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const deleteOrder = async () => {
    if (!window.confirm(`Excluir definitivamente a OS-${String(order.id).padStart(4, "0")}? O chamado vinculado será reaberto e o relatório e as fotos desta OS serão removidos.`)) return;
    setDeleting(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/service-orders/${order.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("delete_failed");
      onSaved();
    } catch {
      setError("Não foi possível excluir a ordem de serviço.");
      setDeleting(false);
    }
  };
  useEffect(() => {
    Promise.all([
      fetch(`${apiBase}/service-orders/${order.id}/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${apiBase}/technicians`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(async ([response, peopleResponse]) => {
        if (!response.ok) throw new Error();
        const data = await response.json();
        setDetails(data);
        setPhotoCaptions(Object.fromEntries((data.photos || []).map((photo) => [photo.id, photo.caption || ""])));
        setTeamIds((data.team || []).map((item) => item.id));
        let availableTechnicians = [];
        if (peopleResponse.ok) {
          availableTechnicians = (await peopleResponse.json()).filter(
              (item) =>
                item.status === "active" &&
                (!item.isResident ||
                  Number(item.id) === Number(data.assignedTechnicianId) ||
                  item.name === data.assignedTo ||
                  (data.team || []).some((member) => member.id === item.id)),
            );
          setTeamOptions(availableTechnicians);
        }
        const assignedTechnicianId = data.assignedTechnicianId || availableTechnicians.find((item) => item.name === data.assignedTo)?.id || "";
        setPlanning({
          scheduledAt: String(data.scheduledAt || "").slice(0, 16),
          assignedTechnicianId: String(assignedTechnicianId),
        });
        setForm({
          issueTypes: data.issueTypes || [],
          attendanceStartedAt: String(
            data.attendanceStartedAt || data.scheduledAt || "",
          ).slice(0, 16),
          attendanceEndedAt: String(data.attendanceEndedAt || "").slice(0, 16),
          serviceReport: data.serviceReport || "",
          internalNotes: data.internalNotes || "",
          signedBy: data.signedBy || data.assignedTo || "",
        });
      })
      .catch(() =>
        setError("Não foi possível carregar os dados completos da OS."),
      )
      .finally(() => setLoading(false));
  }, [order.id, token]);
  const locked = ["finished", "closed"].includes(
    details?.status || order.rawStatus,
  );
  const canFillReport = details?.canFillAttendance === true;
  const readOnly = !canFillReport || (locked && !editing);
  const toggleIssue = (value) =>
    setForm((current) => ({
      ...current,
      issueTypes: current.issueTypes.includes(value)
        ? current.issueTypes.filter((item) => item !== value)
        : [...current.issueTypes, value],
    }));
  const updateAttendancePart = (field, part, value) => {
    if (!value) return;
    setForm((current) => {
      const existing = String(current[field] || "");
      const now = new Date();
      const fallbackDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const fallbackTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const date =
        part === "date" ? value : existing.slice(0, 10) || fallbackDate;
      const time =
        part === "time" ? value : existing.slice(11, 16) || fallbackTime;
      return { ...current, [field]: `${date}T${time}` };
    });
  };
  const openNativePicker = (event) => {
    if (!readOnly && typeof event.currentTarget.showPicker === "function")
      event.currentTarget.showPicker();
  };
  const updatePlanningPart = (part, value) => {
    if (!value) return;
    setPlanning((current) => {
      const existing = String(current.scheduledAt || "");
      const date = part === "date" ? value : existing.slice(0, 10);
      const time = part === "time" ? value : existing.slice(11, 16);
      return { ...current, scheduledAt: `${date}T${time}` };
    });
  };
  const addAttachments = async (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (attachments.length + files.length > 10) {
      setError("Selecione no máximo 10 novos anexos por vez.");
      return;
    }
    try {
      const converted = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) => {
              if (
                ![
                  "image/jpeg",
                  "image/png",
                  "image/webp",
                  "application/pdf",
                ].includes(file.type) ||
                file.size > 5_500_000
              )
                return reject();
              const reader = new FileReader();
              reader.onload = () =>
                resolve({
                  fileName: file.name,
                  dataUrl: reader.result,
                  type: file.type,
                  caption: "",
                });
              reader.onerror = reject;
              reader.readAsDataURL(file);
            }),
        ),
      );
      setAttachments((current) => [...current, ...converted]);
      setError("");
    } catch {
      setError("Use imagens JPG, PNG ou WebP e PDF de até 5 MB.");
    }
  };
  const save = async (action) => {
    if (
      ["resolve_pending", "submit_report"].includes(action) &&
      (!form.attendanceStartedAt || !form.attendanceEndedAt)
    ) {
      setError("Informe o início e o fim do atendimento para concluir a OS.");
      return;
    }
    if (
      action === "submit_report" &&
      (!form.serviceReport.trim() || !form.signedBy.trim())
    ) {
      setError(
        "Para enviar o relatório, preencha o relatório de atendimento e o responsável.",
      );
      return;
    }
    if (
      form.attendanceStartedAt &&
      form.attendanceEndedAt &&
      new Date(form.attendanceEndedAt) < new Date(form.attendanceStartedAt)
    ) {
      setError("O fim do atendimento não pode ser anterior ao início.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/service-orders/${order.id}/attendance`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...form, attachments, photoCaptions, action }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      onSaved();
    } catch (saveError) {
      setError(
        saveError.message === "service_order_report_responsible_only"
          ? "Somente o responsável principal pode preencher o relatório desta OS."
          : saveError.message === "attendance_times_required"
            ? "Informe o início e o fim do atendimento."
            : saveError.message === "attendance_report_required"
              ? "Preencha o relatório e o responsável antes de enviar."
              : "Não foi possível salvar o atendimento.",
      );
    } finally {
      setSaving(false);
    }
  };
  const downloadPdf = async () => {
    if (!details || !locked) return;
    setGeneratingPdf(true);
    setError("");
    try {
      await exportServiceOrderPdf(order, details, issueOptions);
    } catch {
      setError("Não foi possível gerar o PDF da ordem de serviço.");
    } finally {
      setGeneratingPdf(false);
    }
  };
  const saveTeam = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/service-orders/${order.id}/team`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ technicianIds: teamIds }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      setDetails((current) => ({ ...current, team: data.team || [] }));
    } catch {
      setError("Não foi possível atualizar a equipe desta OS.");
    } finally {
      setSaving(false);
    }
  };
  const savePlanning = async () => {
    if (!planning.scheduledAt || !planning.assignedTechnicianId) {
      setError("Informe a data, o horário e o responsável principal.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/service-orders/${order.id}/planning`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(planning),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      setPlanning({
        scheduledAt: String(data.scheduledAt || "").slice(0, 16),
        assignedTechnicianId: String(data.assignedTechnicianId || ""),
      });
      setDetails((current) => ({
        ...current,
        scheduledAt: data.scheduledAt,
        assignedTechnicianId: data.assignedTechnicianId,
        assignedTo: data.assignedTo,
        team: (current.team || []).filter(
          (member) => Number(member.id) !== Number(data.assignedTechnicianId),
        ),
      }));
      setTeamIds((current) =>
        current.filter(
          (id) => Number(id) !== Number(data.assignedTechnicianId),
        ),
      );
      setForm((current) => ({
        ...current,
        signedBy: current.signedBy || data.assignedTo,
      }));
    } catch (planningError) {
      setError(
        planningError.message === "resident_technician_unit_mismatch"
          ? "O técnico residente só pode ser responsável por atividades da própria unidade."
          : "Não foi possível atualizar a programação da OS.",
      );
    } finally {
      setSaving(false);
    }
  };
  const storedPhotos = details?.photos || [];
  const ticketPhotos = storedPhotos.filter((photo) => photo.type === "before");
  const attendancePhotos = storedPhotos.filter(
    (photo) => photo.type !== "before",
  );
  return (
    <div className="modal-backdrop">
      <section className="modal operation-modal service-attendance-modal">
        <div className="modal-head">
          <div>
            <span className="record-code">{order.code}</span>
            <h2>{order.title}</h2>
            <p>
              {order.client} • {order.unit}
            </p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        <div className="operation-info">
          <div>
            <span>Status</span>
            <b>
              {locked
                ? details?.reportStatus === "pending"
                  ? "Resolvida • relatório pendente"
                  : "Atendida e enviada"
                : order.status}
            </b>
          </div>
          <div>
            <span>Responsável</span>
            <b>{details?.assignedTo || order.owner}</b>
          </div>
          <div>
            <span>Assinatura</span>
            <b>{order.signature}</b>
          </div>
          <div>
            <span>Chamado aberto por</span>
            <b>
              {details?.ticketOpenedByName ||
                order.ticketOpenedByName ||
                "Não identificado"}
            </b>
          </div>
        </div>
        {loading ? (
          <div className="empty">Carregando atendimento…</div>
        ) : (
          <>
            {!canFillReport && (
              <div className="scope-note">
                <Icon name="users" />
                <span>
                  Você participa desta OS e pode acompanhar o atendimento. O
                  preenchimento e a conclusão do relatório são exclusivos do
                  responsável principal.
                </span>
              </div>
            )}
            {details?.canManagePlanning && (
              <div className="service-planning-manager" role="group" aria-labelledby="service-planning-heading">
                <h3 id="service-planning-heading">Gestão do atendimento</h3>
                <p>
                  Reprograme a OS e altere o responsável principal. Os demais
                  participantes continuam definidos na equipe abaixo.
                </p>
                <div className="service-planning-fields">
                  <label>
                    Data do atendimento
                    <span className="service-native-datetime-control"><input
                      type="date"
                      value={planning.scheduledAt.slice(0, 10)}
                      onClick={(event) => event.currentTarget.showPicker?.()}
                      onChange={(event) =>
                        updatePlanningPart("date", event.target.value)
                      }
                    /></span>
                  </label>
                  <label>
                    Horário
                    <span className="service-native-datetime-control"><input
                      type="time"
                      step="60"
                      value={planning.scheduledAt.slice(11, 16)}
                      onClick={(event) => event.currentTarget.showPicker?.()}
                      onChange={(event) =>
                        updatePlanningPart("time", event.target.value)
                      }
                    /></span>
                  </label>
                  <label>
                    Responsável principal
                    <select
                      value={planning.assignedTechnicianId}
                      onChange={(event) =>
                        setPlanning({
                          ...planning,
                          assignedTechnicianId: event.target.value,
                        })
                      }
                    >
                      <option value="">Selecione</option>
                      {teamOptions.map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.name}
                          {person.functionName
                            ? ` • ${person.functionName}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="secondary-button team-save-button planning-save-button"
                    onClick={savePlanning}
                    disabled={saving}
                  >
                    <Icon name="check" size={15} />
                    {saving ? "Salvando…" : "Salvar programação"}
                  </button>
                </div>
              </div>
            )}
            {order.description && (
              <div className="service-order-origin">
                <span>Solicitação recebida</span>
                <p>{order.description}</p>
              </div>
            )}
            <fieldset className="service-issue-selector" disabled={readOnly}>
              <legend>Diagnóstico do atendimento (opcional)</legend>
              <p>
                Quando aplicável, selecione um ou mais itens identificados
                durante o atendimento.
              </p>
              <div>
                {issueOptions.map(([value, label]) => (
                  <label
                    className={
                      form.issueTypes.includes(value) ? "selected" : ""
                    }
                    key={value}
                  >
                    <input
                      type="checkbox"
                      checked={form.issueTypes.includes(value)}
                      onChange={() => toggleIssue(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="service-attendance-times">
              <div className="service-attendance-period">
                <strong>Início do atendimento*</strong>
                <div>
                  <label>
                    Data
                    <span className="service-native-datetime-control"><input
                      type="date"
                      value={form.attendanceStartedAt.slice(0, 10)}
                      onClick={openNativePicker}
                      onChange={(event) =>
                        updateAttendancePart(
                          "attendanceStartedAt",
                          "date",
                          event.target.value,
                        )
                      }
                      disabled={readOnly}
                    /></span>
                  </label>
                  <label>
                    Horário
                    <span className="service-native-datetime-control"><input
                      type="time"
                      step="60"
                      value={form.attendanceStartedAt.slice(11, 16)}
                      onClick={openNativePicker}
                      onChange={(event) =>
                        updateAttendancePart(
                          "attendanceStartedAt",
                          "time",
                          event.target.value,
                        )
                      }
                      disabled={readOnly}
                    /></span>
                  </label>
                </div>
              </div>
              <div className="service-attendance-period">
                <strong>Fim do atendimento*</strong>
                <div>
                  <label>
                    Data
                    <span className="service-native-datetime-control"><input
                      type="date"
                      value={form.attendanceEndedAt.slice(0, 10)}
                      onClick={openNativePicker}
                      onChange={(event) =>
                        updateAttendancePart(
                          "attendanceEndedAt",
                          "date",
                          event.target.value,
                        )
                      }
                      disabled={readOnly}
                    /></span>
                  </label>
                  <label>
                    Horário
                    <span className="service-native-datetime-control"><input
                      type="time"
                      step="60"
                      value={form.attendanceEndedAt.slice(11, 16)}
                      onClick={openNativePicker}
                      onChange={(event) =>
                        updateAttendancePart(
                          "attendanceEndedAt",
                          "time",
                          event.target.value,
                        )
                      }
                      disabled={readOnly}
                    /></span>
                  </label>
                </div>
              </div>
            </div>
            <label className="service-text-field">
              Relatório de atendimento
              {details?.reportStatus === "pending" ? " — pendente" : "*"}
              <textarea
                rows="7"
                placeholder="Descreva o diagnóstico, os testes realizados, a solução aplicada e a condição final dos equipamentos."
                value={form.serviceReport}
                onChange={(event) =>
                  setForm({ ...form, serviceReport: event.target.value })
                }
                disabled={readOnly}
              />
              <AiReviewButton
                token={token}
                value={form.serviceReport}
                onApply={(value) => setForm({ ...form, serviceReport: value })}
                context="relatório técnico de ordem de serviço"
                disabled={readOnly}
              />
            </label>
            <label className="service-text-field internal">
              Observações internas (não aparecem ao cliente)
              <textarea
                rows="4"
                placeholder="Registre informações internas, recomendações ou pendências da equipe."
                value={form.internalNotes}
                onChange={(event) =>
                  setForm({ ...form, internalNotes: event.target.value })
                }
                disabled={readOnly}
              />
              <AiReviewButton
                token={token}
                value={form.internalNotes}
                onApply={(value) => setForm({ ...form, internalNotes: value })}
                context="observação interna de ordem de serviço"
                disabled={readOnly}
              />
            </label>
            <fieldset
              className="service-issue-selector service-team-selector"
              disabled={!details?.canManageTeam}
            >
              <legend>Outros técnicos participantes</legend>
              <p>
                Selecione os demais técnicos que participaram desta OS. O
                responsável principal não será alterado.
              </p>
              <TeamMultiSelect
                options={teamOptions}
                selected={teamIds}
                onChange={setTeamIds}
                disabled={!details?.canManageTeam}
                excludeName={details?.assignedTo}
              />
              {details?.canManageTeam && (
                <button
                  type="button"
                  className="secondary-button team-save-button"
                  onClick={saveTeam}
                  disabled={saving}
                >
                  <Icon name="check" size={15} />
                  {saving ? "Salvando…" : "Salvar outros técnicos"}
                </button>
              )}
              {!details?.canManageTeam && (
                <small>
                  Seu perfil pode visualizar a equipe, mas não alterá-la.
                </small>
              )}
            </fieldset>
            <label className="service-signed-field">
              Responsável pelo preenchimento / assinado por*
              <input
                value={form.signedBy}
                onChange={(event) =>
                  setForm({ ...form, signedBy: event.target.value })
                }
                placeholder="Nome completo"
                disabled={readOnly}
              />
            </label>
            {ticketPhotos.length > 0 && (
              <section className="service-evidence">
                <div>
                  <strong>Fotos recebidas no chamado</strong>
                  <small>Referência original do problema.</small>
                </div>
                <div>
                  {ticketPhotos.map((photo) => (
                    <article className="service-photo-card" key={photo.id}>
                      <a href={photo.url} target="_blank" rel="noreferrer"><img src={photo.url} alt={photoCaptions[photo.id] || "Foto do chamado"} /></a>
                      {readOnly ? <span>{photoCaptions[photo.id] || "Foto do chamado"}</span> : <textarea aria-label="Descrição da foto" placeholder="Descreva esta foto" maxLength={300} value={photoCaptions[photo.id] || ""} onChange={(event) => setPhotoCaptions((current) => ({ ...current, [photo.id]: event.target.value }))} />}
                    </article>
                  ))}
                </div>
              </section>
            )}
            <section className="service-attachments">
              <div>
                <strong>Anexos do atendimento</strong>
                <small>
                  Fotos da execução e documentos técnicos. Até 10 arquivos JPG,
                  PNG, WebP ou PDF.
                </small>
              </div>
              {!readOnly && (
                <label className="secondary-button">
                  <Icon name="plus" size={16} />
                  Adicionar anexos
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={addAttachments}
                  />
                </label>
              )}
              <div className="service-attachment-list">
                {attendancePhotos.map((photo) => (
                  <article className="service-photo-card" key={photo.id}>
                    <a href={photo.url} target="_blank" rel="noreferrer">{photo.url.startsWith("data:image/") ? <img src={photo.url} alt={photoCaptions[photo.id] || "Evidência do atendimento"} /> : <Icon name="file" size={20} />}</a>
                    {readOnly ? <span>{photoCaptions[photo.id] || "Evidência do atendimento"}</span> : <textarea aria-label="Descrição da foto" placeholder="Descreva esta foto" maxLength={300} value={photoCaptions[photo.id] || ""} onChange={(event) => setPhotoCaptions((current) => ({ ...current, [photo.id]: event.target.value }))} />}
                  </article>
                ))}
                {attachments.map((attachment, index) => (
                  <article key={`${attachment.fileName}-${index}`}>
                    {attachment.type.startsWith("image/") ? (
                      <img src={attachment.dataUrl} alt={attachment.fileName} />
                    ) : (
                      <Icon name="file" size={20} />
                    )}
                    <textarea aria-label="Descrição da nova foto" placeholder="Descreva esta foto" maxLength={300} value={attachment.caption || ""} onChange={(event) => setAttachments((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, caption: event.target.value } : item))} />
                    <button
                      type="button"
                      onClick={() =>
                        setAttachments((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                    >
                      ×
                    </button>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          {canDelete && <button type="button" className="service-order-delete-action" disabled={saving || deleting || loading || generatingPdf} onClick={deleteOrder}>
            {deleting ? "Excluindo…" : "Excluir OS"}
          </button>}
          <button onClick={editing ? () => setEditing(false) : onClose}>
            {editing ? "Cancelar edição" : readOnly ? "Fechar" : "Cancelar"}
          </button>
          {locked && details?.reportStatus !== "pending" && (
            <button
              className="secondary-button spreadsheet-action compact service-order-pdf-action"
              disabled={generatingPdf || loading || !details}
              onClick={downloadPdf}
            >
              <span className="spreadsheet-action-icon pdf">
                <Icon name="file" size={17} />
              </span>
              <span>
                <b>{generatingPdf ? "Gerando PDF…" : "Exportar PDF"}</b>
                <small>Relatório da OS</small>
              </span>
            </button>
          )}
          {locked && canFillReport && !editing && (
            <button
              className="secondary-button service-order-edit-action"
              onClick={() => setEditing(true)}
              disabled={loading}
            >
              <Icon name="edit" size={16} />
              Editar OS
            </button>
          )}
          {!readOnly &&
            (locked ? (
              <button
                className="primary"
                onClick={() => save("submit_report")}
                disabled={saving || loading}
              >
                {saving
                  ? "Enviando…"
                  : details?.reportStatus === "pending"
                    ? "Concluir e enviar relatório"
                    : "Salvar alterações"}
              </button>
            ) : (
              <>
                <button
                  onClick={() => save("draft")}
                  disabled={saving || loading}
                >
                  {saving ? "Salvando…" : "Salvar rascunho"}
                </button>
                <button
                  className="secondary-button resolve-pending-action"
                  onClick={() => save("resolve_pending")}
                  disabled={saving || loading}
                >
                  {saving ? "Salvando…" : "Resolver • relatório pendente"}
                </button>
                <button
                  className="primary"
                  onClick={() => save("submit_report")}
                  disabled={saving || loading}
                >
                  {saving ? "Enviando…" : "Finalizar e enviar"}
                </button>
              </>
            ))}
        </div>
      </section>
    </div>
  );
}

function TicketModal({ ticket, token, onClose, onSaved }) {
  const [assignedTo, setAssignedTo] = useState("");
  const [primaryTechnicianId, setPrimaryTechnicianId] = useState(null);
  const [teamIds, setTeamIds] = useState([]);
  const [scheduledAt, setScheduledAt] = useState("");
  const [setup, setSetup] = useState({
    clients: [],
    units: [],
    technicians: [],
    residentTechnicians: [],
  });
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    clientId: ticket.clientId || "",
    unitId: ticket.unitId || "",
    title: ticket.title || "",
    description: ticket.description || "",
    priority: ticket.rawPriority || "medium",
    photos: ticket.photos || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([
      fetch(`${apiBase}/setup/operational`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${apiBase}/tickets/${ticket.id}/audit-log`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(async ([setupResponse, logResponse]) => {
        if (setupResponse.ok) setSetup(await setupResponse.json());
        if (logResponse.ok) setLogs(await logResponse.json());
      })
      .catch(() =>
        setError("Não foi possível carregar todos os dados do chamado."),
      );
  }, [token, ticket.id]);
  const collaborators = setup.technicians || [];
  const residentTechnicians = useMemo(
    () =>
      (setup.residentTechnicians || []).filter(
        (item) => String(item.unitId) === String(ticket.unitId),
      ),
    [setup.residentTechnicians, ticket.unitId],
  );
  useEffect(() => {
    if (!residentTechnicians.length) return;
    setPrimaryTechnicianId((current) =>
      residentTechnicians.some((item) => Number(item.id) === Number(current))
        ? current
        : residentTechnicians[0].id,
    );
    setAssignedTo((current) =>
      residentTechnicians.some((item) => item.name === current)
        ? current
        : residentTechnicians[0].name,
    );
    setTeamIds(residentTechnicians.map((item) => item.id));
  }, [residentTechnicians]);
  const units = (setup.units || []).filter(
    (unit) => String(unit.clientId) === String(form.clientId),
  );
  const openServiceOrder = async () => {
    if (!scheduledAt) {
      setError("Informe obrigatoriamente a data e o horário do atendimento.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/tickets/${ticket.id}/service-order`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assignedTo,
            assignedTechnicianId: primaryTechnicianId,
            technicianIds: teamIds,
            scheduledAt,
            signaturePolicy: "informative",
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      onSaved();
    } catch (error) {
      setError(
        error.message === "ticket_already_has_service_order"
          ? "Este chamado já possui uma OS aberta."
          : error.message === "invalid_scheduled_at"
            ? "Informe uma data e um horário de atendimento válidos."
            : "Não foi possível abrir a OS deste chamado.",
      );
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/tickets/${ticket.id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          clientId: Number(form.clientId),
          unitId: Number(form.unitId),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      onSaved();
    } catch (saveError) {
      setError(
        saveError.message === "ticket_locked_by_service_order"
          ? "Este chamado está bloqueado porque já possui uma OS."
          : "Não foi possível salvar as alterações.",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (
      !window.confirm(
        `Excluir definitivamente ${ticket.code}? A ação ficará registrada no log.`,
      )
    )
      return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/tickets/${ticket.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error);
      onSaved();
    } catch (removeError) {
      setError(
        removeError.message === "ticket_locked_by_service_order"
          ? "Chamados com OS aberta não podem ser excluídos."
          : "Não foi possível excluir o chamado.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop ticket-operation-backdrop">
      <section className="modal operation-modal ticket-operation-modal">
        <div className="modal-head">
          <div>
            <span className="record-code">{ticket.code}</span>
            <h2>{ticket.title}</h2>
            <p>
              {ticket.client} • {ticket.unit}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar chamado">×</button>
        </div>
        <div className="operation-info">
          <div>
            <span>Situação</span>
            <b>{ticket.serviceOrderId ? "OS aberta" : "Aguardando OS"}</b>
          </div>
          <div>
            <span>Prioridade</span>
            <b>{ticket.priority}</b>
          </div>
          <div>
            <span>Origem</span>
            <b>{ticket.channel}</b>
          </div>
          <div>
            <span>Aberto por</span>
            <b>{ticket.openedByName || "Não identificado"}</b>
          </div>
        </div>
        {editing ? (
          <div className="ticket-edit-form">
            <div className="form-grid">
              <label>
                Cliente
                <select
                  value={form.clientId}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      clientId: event.target.value,
                      unitId: "",
                    })
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {(setup.clients || []).map((client) => (
                    <option value={client.id} key={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Unidade
                <select
                  value={form.unitId}
                  onChange={(event) =>
                    setForm({ ...form, unitId: event.target.value })
                  }
                  required
                >
                  <option value="">Selecione</option>
                  {units.map((unit) => (
                    <option value={unit.id} key={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Título
              <input
                value={form.title}
                onChange={(event) =>
                  setForm({ ...form, title: event.target.value })
                }
              />
            </label>
            <label>
              Prioridade
              <select
                value={form.priority}
                onChange={(event) =>
                  setForm({ ...form, priority: event.target.value })
                }
              >
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
                <option value="critical">Crítica</option>
              </select>
            </label>
            <label>
              Descrição
              <textarea
                value={form.description}
                onChange={(event) =>
                  setForm({ ...form, description: event.target.value })
                }
              />
              <AiReviewButton
                token={token}
                value={form.description}
                onApply={(value) => setForm({ ...form, description: value })}
                context="descrição de chamado técnico"
              />
            </label>
            <TicketPhotoPicker
              photos={form.photos}
              onChange={(photos) =>
                setForm((current) => ({ ...current, photos }))
              }
            />
            <div className="ticket-edit-actions">
              <button type="button" onClick={() => setEditing(false)}>
                Cancelar edição
              </button>
              <button
                type="button"
                className="primary"
                onClick={save}
                disabled={saving}
              >
                {saving ? "Salvando…" : "Salvar alterações"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {ticket.description && (
              <div className="ticket-detail-description">
                <span>Descrição</span>
                <p>{ticket.description}</p>
              </div>
            )}
            {ticket.photos?.length > 0 && (
              <div className="ticket-detail-photos">
                {ticket.photos.map((photo) => (
                  <a
                    href={photo.dataUrl}
                    target="_blank"
                    rel="noreferrer"
                    key={photo.id}
                  >
                    <img
                      src={photo.dataUrl}
                      alt={photo.fileName || "Foto do chamado"}
                    />
                    <span>{photo.fileName || "Foto do chamado"}</span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
        {logs.length > 0 && (
          <div className="ticket-audit">
            <strong>Histórico do chamado</strong>
            {logs.map((log) => (
              <div key={log.id}>
                <span>{log.actorName}</span>
                <p>{log.summary}</p>
                <time>{formatSchedule(log.createdAt)}</time>
              </div>
            ))}
          </div>
        )}
        {ticket.serviceOrderId ? (
          <div className="scope-note">
            <Icon name="check" />
            <span>
              OS-{String(ticket.serviceOrderId).padStart(4, "0")} aberta. O
              atendimento agora é gerenciado exclusivamente em Ordem de serviço.
            </span>
          </div>
        ) : (
          <div
            className={`create-order-box ${residentTechnicians.length ? "resident-team-order" : ""}`}
          >
            {residentTechnicians.length ? (
              <div className="resident-order-assignment">
                <header>
                  <span>
                    <Icon name="users" size={18} />
                  </span>
                  <div>
                    <small>ATRIBUIÇÃO AUTOMÁTICA</small>
                    <strong>Equipe residente da unidade</strong>
                    <b className="resident-order-team-count">
                      {residentTechnicians.length}{" "}
                      {residentTechnicians.length === 1 ? "técnico" : "técnicos"}
                    </b>
                    <p>
                      Profissionais vinculados ao atendimento deste chamado.
                    </p>
                  </div>
                </header>
                <div className="resident-order-team-list">
                  {residentTechnicians.map((technician) => {
                    const isPrimary =
                      Number(primaryTechnicianId) === Number(technician.id);
                    return (
                      <button
                        type="button"
                        className={isPrimary ? "selected" : ""}
                        key={technician.id}
                        onClick={() => {
                          setPrimaryTechnicianId(technician.id);
                          setAssignedTo(technician.name);
                        }}
                        aria-pressed={isPrimary}
                      >
                        <span>{initials(technician.name)}</span>
                        <div>
                          <strong>{technician.name}</strong>
                          <small>
                            {isPrimary
                              ? "Responsável principal pela OS"
                              : "Clique para definir como responsável"}
                          </small>
                        </div>
                        {isPrimary && <em>Principal</em>}
                      </button>
                    );
                  })}
                </div>
                <footer>
                  <span>
                    <Icon name="check" size={14} /> Equipe confirmada
                  </span>
                  <p>
                    Todos os profissionais acima serão vinculados à ordem de
                    serviço quando ela for aberta.
                  </p>
                </footer>
              </div>
            ) : (
              <label>
                Responsável pela OS
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                >
                  <option value="">Não atribuído</option>
                  {collaborators.map((item) => (
                    <option value={item.name} key={item.id}>
                      {item.name} — {item.role || "Colaborador"}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Data e horário do atendimento*
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                required
              />
              <small>
                Também pode ser uma data retroativa quando o atendimento
                emergencial já ocorreu.
              </small>
            </label>
            <button
              className="primary create-order-submit"
              onClick={openServiceOrder}
              disabled={saving || !scheduledAt}
            >
              <span><Icon name="plus" size={16} /></span>
              <span><b>{saving ? "Abrindo…" : "Abrir OS corretiva"}</b><small>Iniciar atendimento técnico</small></span>
            </button>
            {!residentTechnicians.length && (
              <fieldset className="create-order-team">
                <legend>Outros técnicos participantes</legend>
                <small>
                  Selecione todos que participarão desta OS além do responsável
                  principal.
                </small>
                <TeamMultiSelect
                  options={collaborators}
                  selected={teamIds}
                  onChange={setTeamIds}
                  excludeName={assignedTo}
                />
              </fieldset>
            )}
          </div>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions ticket-modal-actions">
          {!ticket.serviceOrderId && !editing && (
            <>
              <button
                type="button"
                className="ticket-delete-button"
                onClick={remove}
                disabled={saving}
              >
                <Icon name="trash" size={15} /> Excluir chamado
              </button>
              <button type="button" className="ticket-edit-button" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} /> Editar chamado
              </button>
            </>
          )}
          <button className="ticket-close-button" onClick={onClose}>Fechar</button>
        </div>
      </section>
    </div>
  );
}

function TicketPhotoPicker({ photos, onChange, title = "Fotos do problema", description = "Até 5 imagens JPG, PNG ou WebP. Elas acompanharão a futura OS.", disabled = false, onBusyChange, allowComments = false, maxPhotos = 5, hideFileNames = false }) {
  const [error, setError] = useState("");
  const [reading, setReading] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const addFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    setError("");
    if (photos.length + files.length > maxPhotos) {
      setError(`Adicione no máximo ${maxPhotos} fotos.`);
      return;
    }
    if (
      files.some(
        (file) =>
          !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
          file.size > 4 * 1024 * 1024,
      )
    ) {
      setError("Use JPG, PNG ou WebP com até 4 MB por foto.");
      return;
    }
    setReading(true);
    onBusyChange?.(true);
    try {
      const added = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () =>
                resolve({
                  fileName: file.name,
                  mimeType: file.type,
                  dataUrl: reader.result,
                });
              reader.onerror = reject;
              reader.readAsDataURL(file);
            }),
        ),
      );
      if (mounted.current) onChange([...photos, ...added]);
    } catch {
      if (mounted.current) setError("Não foi possível ler a foto. Selecione o arquivo novamente.");
    } finally {
      if (mounted.current) setReading(false);
      onBusyChange?.(false);
    }
  };
  return (
    <div className={`ticket-photo-picker${allowComments ? " with-comments" : ""}`}>
      <div>
        <strong>{title}</strong>
        <small>
          {description}
        </small>
      </div>
      <label className="ticket-photo-add">
        <Icon name="camera-plus" size={18} /> <span>Adicionar fotos</span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          disabled={disabled || reading}
          onChange={addFiles}
        />
      </label>
      {reading && <small role="status">Preparando fotos…</small>}
      {photos.length > 0 && (
        <div className="ticket-photo-preview">
          {photos.map((photo, index) => (
            <figure key={`${photo.fileName}-${index}`}>
              <img
                src={photo.dataUrl}
                alt={hideFileNames ? `Registro fotográfico ${index + 1}` : photo.fileName}
              />
              <button
                type="button"
                disabled={disabled || reading}
                onClick={() =>
                  onChange(
                    photos.filter((_, photoIndex) => photoIndex !== index),
                  )
                }
                aria-label={hideFileNames ? `Remover registro ${index + 1}` : `Remover ${photo.fileName}`}
              >
                ×
              </button>
              {allowComments ? (
                <label className="ticket-photo-comment">
                  Comentário opcional
                  <input
                    value={photo.caption || ""}
                    maxLength={300}
                    disabled={disabled || reading}
                    onChange={(event) =>
                      onChange(
                        photos.map((item, photoIndex) =>
                          photoIndex === index
                            ? { ...item, caption: event.target.value }
                            : item,
                        ),
                      )
                    }
                    placeholder="Descreva o registro, local ou atividade"
                  />
                </label>
              ) : !hideFileNames ? (
                <figcaption>{photo.fileName}</figcaption>
              ) : null}
            </figure>
          ))}
        </div>
      )}
      {error && <small className="ticket-photo-error">{error}</small>}
    </div>
  );
}

function CreateModal({ type, setup: initialSetup, resident = false, token, onClose, onSave }) {
  const isTicket = type === "chamado";
  const isProject = type === "projeto";
  const [form, setForm] = useState({
    title: "",
    description: "",
    photos: [],
    projectNumber: "",
    projectMode: "project",
    clientId: "",
    unitId: "",
    projectId: "",
    responsibleUserId: "",
    technicianIds: [],
    dailyReportApprovalRequired: false,
    owner: "",
    priority: "medium",
    signaturePolicy: "informative",
    estimatedDays: 0,
    startDate: "",
    endDate: "",
  });
  const [setup, setSetup] = useState(initialSetup);
  useEffect(() => {
    if (!resident || !isTicket) return;
    const client = (setup?.clients || []).slice().sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))[0];
    if (!client) return;
    const unit = (setup?.units || []).filter((item) => String(item.clientId) === String(client.id)).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))[0];
    setForm((current) => current.clientId ? current : { ...current, clientId: String(client.id), unitId: unit ? String(unit.id) : "" });
  }, [setup, resident, isTicket]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch(`${apiBase}/setup/operational`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then(setSetup)
      .catch(() => setError("Não foi possível atualizar clientes e unidades."));
  }, [token]);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const toggleTechnician = (id) =>
    setForm((current) => ({
      ...current,
      technicianIds: current.technicianIds.includes(id)
        ? current.technicianIds.filter((item) => item !== id)
        : [...current.technicianIds, id],
    }));
  const units = setup.units.filter(
    (unit) => String(unit.clientId) === String(form.clientId),
  );
  const availableProjects = setup.projects.filter(
    (project) =>
      String(project.clientId) === String(form.clientId) &&
      String(project.unitId) === String(form.unitId),
  );
  const availableTechnicians = (setup.technicians || []).filter(
    (item) => !item.residentUnitIds,
  );
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = isProject
      ? {
          projectNumber: form.projectNumber.trim(),
          name: form.title,
          projectMode: form.projectMode,
          clientId: Number(form.clientId),
          unitId: Number(form.unitId),
          responsibleUserId: form.responsibleUserId
            ? Number(form.responsibleUserId)
            : null,
          technicianIds: form.technicianIds,
          dailyReportApprovalRequired: form.dailyReportApprovalRequired,
          estimatedDays: Number(form.estimatedDays),
          startDate:
            form.projectMode === "implementation_study"
              ? null
              : form.startDate || null,
          endDate:
            form.projectMode === "implementation_study"
              ? null
              : form.endDate || null,
          status: "planning",
        }
      : isTicket
        ? {
            title: form.title,
            description: form.description,
            photos: form.photos,
            clientId: Number(form.clientId),
            unitId: Number(form.unitId),
            priority: form.priority,
            status: "open",
            source: "internal",
          }
        : {
            title: form.title,
            clientId: Number(form.clientId),
            unitId: Number(form.unitId),
            projectId: form.projectId ? Number(form.projectId) : null,
            priority: form.priority,
            status: "new",
            assignedTechnicianId: form.owner ? Number(form.owner) : null,
            signaturePolicy: form.signaturePolicy,
          };
    try {
      await onSave(payload);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form className="modal create-record-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>
              {isProject
                ? "Novo projeto"
                : isTicket
                  ? "Novo chamado"
                  : "Nova ordem de serviço"}
            </h2>
            <p>O registro será salvo no banco de dados.</p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        {isProject ? (
          <div className="form-grid">
            <label>
              Tipo do registro
              <select name="projectMode" value={form.projectMode} onChange={change}>
                <option value="project">Projeto efetivado</option>
                <option value="implementation_study">Estudo de Implantação</option>
              </select>
            </label>
            <label>
              Projeto número
              <input
                autoFocus
                name="projectNumber"
                value={form.projectNumber}
                onChange={change}
                placeholder="Ex.: 2026-001"
                required
              />
            </label>
            <label>
              Nome do projeto
              <input
                name="title"
                value={form.title}
                onChange={change}
                placeholder="Ex.: Implantação CFTV — Unidade SP"
                required
              />
            </label>
          </div>
        ) : (
          <label>
            Título
            <input
              autoFocus
              name="title"
              value={form.title}
              onChange={change}
              placeholder="Descreva brevemente a solicitação"
              required
            />
          </label>
        )}
        <div className="form-grid">
          <label>
            Cliente
            <select
              name="clientId"
              value={form.clientId}
              onChange={(e) =>
                setForm({
                  ...form,
                  clientId: e.target.value,
                  unitId: "",
                  projectId: "",
                })
              }
              required
            >
              <option value="">Selecione</option>
              {setup.clients.map((client) => (
                <option value={client.id} key={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Unidade
            <select
              name="unitId"
              value={form.unitId}
              onChange={change}
              required
            >
              <option value="">Selecione</option>
              {units.map((unit) => (
                <option value={unit.id} key={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {isTicket && (
          <>
            <label className="ticket-description-field">
              Descrição
              <textarea
                name="description"
                value={form.description}
                onChange={change}
                placeholder="Descreva detalhadamente o problema, quando começou e onde ocorre"
                required
              />
              <AiReviewButton
                token={token}
                value={form.description}
                onApply={(value) =>
                  setForm((current) => ({ ...current, description: value }))
                }
                context="descrição de chamado técnico"
              />
            </label>
            <TicketPhotoPicker
              photos={form.photos}
              onChange={(photos) =>
                setForm((current) => ({ ...current, photos }))
              }
            />
          </>
        )}
        {isProject ? (
          <div className="form-grid">
            {form.projectMode === "project" && (
              <>
                <label>
                  Data inicial
                  <input
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={change}
                    required
                  />
                </label>
                <label>
                  Entrega prevista
                  <input
                    type="date"
                    name="endDate"
                    value={form.endDate}
                    onChange={change}
                    required
                  />
                </label>
              </>
            )}
            <label>
              {form.projectMode === "implementation_study"
                ? "Prazo do estudo (dias corridos)"
                : "Dias estimados"}
              <input
                type="number"
                min="0"
                name="estimatedDays"
                value={form.estimatedDays}
                onChange={change}
              />
            </label>
            <label>
              Responsável pelo projeto
              <select
                name="responsibleUserId"
                value={form.responsibleUserId}
                onChange={change}
                required={form.projectMode === "project"}
              >
                <option value="">Selecione</option>
                {(setup.users || []).map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name} — {roleLabel(item.role)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <>
            <div className="form-grid">
              {!isTicket && (
                <label>
                  Projeto
                  <select
                    name="projectId"
                    value={form.projectId}
                    onChange={change}
                  >
                    <option value="">Sem projeto</option>
                    {availableProjects.map((project) => (
                      <option value={project.id} key={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                {isTicket ? "Prioridade" : "Responsável"}
                {isTicket ? (
                  <select
                    name="priority"
                    value={form.priority}
                    onChange={change}
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                ) : (
                  <select name="owner" value={form.owner} onChange={change}>
                    <option value="">Não atribuído</option>
                    {availableTechnicians.map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.name} — {item.role || "Colaborador"}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            </div>
            {!isTicket && (
              <div className="form-grid">
                <label>
                  Prioridade
                  <select
                    name="priority"
                    value={form.priority}
                    onChange={change}
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </label>
                <label>
                  Assinatura
                  <select
                    name="signaturePolicy"
                    value={form.signaturePolicy}
                    onChange={change}
                  >
                    <option value="informative">Informativo</option>
                    <option value="acknowledgement">Solicitar ciência</option>
                    <option value="acceptance">Aceite obrigatório</option>
                  </select>
                </label>
              </div>
            )}
          </>
        )}
        {isProject && (
          <div className="new-project-daily-settings">
            <fieldset>
              <legend>Técnicos designados para o projeto</legend>
              <div>
                {availableTechnicians.map((item) => (
                  <label key={item.id}>
                    <input
                      type="checkbox"
                      checked={form.technicianIds.includes(item.id)}
                      onChange={() => toggleTechnician(item.id)}
                    />
                    <span>
                      <strong>{item.name}</strong>
                      <small>{item.role || "Técnico"}</small>
                    </span>
                  </label>
                ))}
                {!availableTechnicians.length && (
                  <small>Nenhum técnico disponível para este cliente.</small>
                )}
              </div>
            </fieldset>
            <label className="daily-approval-setting">
              <input
                type="checkbox"
                checked={form.dailyReportApprovalRequired}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dailyReportApprovalRequired: e.target.checked,
                  })
                }
              />
              <span>
                <strong>Exigir aprovação dos relatórios diários</strong>
                <small>
                  Ao enviar, o relatório aguardará aprovação de administrador ou
                  gestor.
                </small>
              </span>
            </label>
          </div>
        )}
        <div className="scope-note">
          <Icon name="check" size={16} />
          <span>Cliente e unidade serão validados no servidor.</span>
        </div>
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" type="submit" disabled={saving}>
            {saving ? "Salvando…" : "Criar registro"}
          </button>
        </div>
      </form>
    </div>
  );
}

const internalCategoryOptions = [
  ["director", "Diretoria"],
  ["finance", "Financeiro"],
  ["administration", "Administração"],
  ["procurement", "Suprimentos"],
];
const internalCategoryLabel = (value) =>
  internalCategoryOptions.find((item) => item[0] === value)?.[1] ||
  "Administração";
function SettingsTabs({ tab, setTab }) {
  const tabs = [
    ["users", "users", "Usuários administrativos"],
    ["functions", "clipboard", "Funções operacionais"],
    ["profiles", "wrench", "Perfis operacionais"],
    ["notifications", "bell", "Notificações"],
  ];
  return (
    <div className="settings-tabs" role="tablist">
      {tabs.map(([value, icon, label]) => (
        <button
          type="button"
          role="tab"
          aria-selected={tab === value}
          className={tab === value ? "active" : ""}
          onClick={() => setTab(value)}
          key={value}
        >
          <Icon name={icon} size={16} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

function SettingsPage({ token, currentUserId, role }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };
  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/settings/users`, { headers });
      if (!response.ok)
        throw new Error(
          response.status === 403
            ? "Apenas administradores podem gerenciar usuários internos."
            : "Não foi possível carregar os usuários.",
        );
      setUsers(await response.json());
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadUsers();
  }, []);
  if (role !== "administrator")
    return (
      <div className="coming-soon">
        <div className="metric-icon red">
          <Icon name="users" />
        </div>
        <h2>Acesso restrito</h2>
        <p>A gestão de usuários internos é exclusiva de administradores.</p>
      </div>
    );
  if (tab === "notifications")
    return (
      <QuoteNotificationSettings token={token} tab={tab} setTab={setTab} />
    );
  if (tab !== "users")
    return <SettingsAccessManager token={token} tab={tab} setTab={setTab} />;
  const visible = users.filter((user) => {
    const term = query.toLowerCase();
    return (
      (!term || `${user.name} ${user.email}`.toLowerCase().includes(term)) &&
      (roleFilter === "all" || user.internalCategory === roleFilter) &&
      (statusFilter === "all" || String(user.active) === statusFilter)
    );
  });
  const totalActive = users.filter((user) => user.active).length;
  return (
    <div className="settings-page">
      <SettingsTabs tab={tab} setTab={setTab} />
      <div className="settings-scope-note">
        <Icon name="building" size={17} />
        <div>
          <strong>
            Usuários administrativos são independentes dos colaboradores
          </strong>
          <span>
            Funções e perfis operacionais pertencem a técnicos e supervisores.
            Aqui, cada usuário recebe permissões administrativas individuais.
          </span>
        </div>
      </div>
      {error && <div className="login-error">{error}</div>}
      <div className="settings-metrics">
        <article className="panel">
          <span>Usuários administrativos</span>
          <b>{users.length}</b>
        </article>
        <article className="panel">
          <span>Ativos</span>
          <b>{totalActive}</b>
        </article>
        <article className="panel">
          <span>Diretoria</span>
          <b>
            {
              users.filter(
                (user) => user.internalCategory === "director" && user.active,
              ).length
            }
          </b>
        </article>
        <article className="panel">
          <span>Financeiro</span>
          <b>
            {
              users.filter(
                (user) => user.internalCategory === "finance" && user.active,
              ).length
            }
          </b>
        </article>
      </div>
      <section className="panel settings-users-panel">
        <div className="settings-users-toolbar">
          <div>
            <h3>Usuários administrativos</h3>
            <p>
              Diretoria, Financeiro, Administração e Suprimentos, com acessos
              individuais.
            </p>
          </div>
          <button
            className="primary"
            onClick={() =>
              setEditor({
                name: "",
                email: "",
                internalCategory: "administration",
                active: true,
                permissions: [],
              })
            }
          >
            <Icon name="plus" size={16} /> Novo usuário
          </button>
        </div>
        <div className="settings-filters">
          <label>
            <Icon name="search" size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nome ou e-mail"
            />
          </label>
          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
          >
            <option value="all">Todas as áreas</option>
            {internalCategoryOptions.map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">Todos os status</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
        <div className="settings-user-table">
          <div className="settings-user-head">
            <span>Usuário</span>
            <span>Área interna</span>
            <span>Acessos concedidos</span>
            <span>Status</span>
            <span></span>
          </div>
          {loading ? (
            <div className="empty">Carregando usuários…</div>
          ) : (
            visible.map((user) => (
              <div className="settings-user-row" key={user.id}>
                <div>
                  <i>{initials(user.name)}</i>
                  <span>
                    <strong>
                      {user.name}
                      {Number(user.id) === Number(currentUserId) && (
                        <small> Você</small>
                      )}
                    </strong>
                    <small>{user.email}</small>
                  </span>
                </div>
                <span>{internalCategoryLabel(user.internalCategory)}</span>
                <span className="settings-permissions">
                  {user.internalCategory === "director" ? (
                    <em>Acesso total obrigatório</em>
                  ) : (
                    <em>{user.permissions.length} permissão(ões)</em>
                  )}
                </span>
                <span
                  className={`settings-status ${user.active ? "active" : "inactive"}`}
                >
                  {user.active ? "Ativo" : "Inativo"}
                </span>
                <button onClick={() => setEditor(user)}>Editar</button>
              </div>
            ))
          )}
          {!loading && !visible.length && (
            <div className="empty">
              Nenhum usuário administrativo encontrado.
            </div>
          )}
        </div>
      </section>
      {editor && (
        <InternalUserModal
          token={token}
          user={editor}
          currentUserId={currentUserId}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            loadUsers();
          }}
        />
      )}
    </div>
  );
}

function QuoteNotificationSettings({ token, tab, setTab }) {
  const [settings, setSettings] = useState({
    notificationEmail: "",
    smtpConfigured: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  useEffect(() => {
    fetch(`${apiBase}/settings/quote-notifications`, { headers })
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) =>
        setSettings({
          notificationEmail: data.notificationEmail || "",
          smtpConfigured: Boolean(data.smtpConfigured),
        }),
      )
      .catch(() => setMessage("Não foi possível carregar a configuração."))
      .finally(() => setLoading(false));
  }, [token]);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(`${apiBase}/settings/quote-notifications`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          notificationEmail: settings.notificationEmail,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "save_failed");
      setSettings({
        notificationEmail: data.notificationEmail || "",
        smtpConfigured: Boolean(data.smtpConfigured),
      });
      setMessage("Configuração salva com sucesso.");
    } catch (error) {
      setMessage(
        error.message === "invalid_notification_email"
          ? "Informe um endereço de e-mail válido."
          : "Não foi possível salvar a configuração.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="settings-page">
      <SettingsTabs tab={tab} setTab={setTab} />
      <section className="panel quote-notification-settings">
        <div className="quote-notification-heading">
          <span className="metric-icon blue">
            <Icon name="bell" size={20} />
          </span>
          <div>
            <h2>Notificações de orçamentos</h2>
            <p>
              Defina quem recebe um aviso quando o cliente envia uma nova
              solicitação pelo portal.
            </p>
          </div>
        </div>
        {loading ? (
          <div className="empty">Carregando configuração…</div>
        ) : (
          <form onSubmit={save}>
            <label>
              E-mail comercial principal
              <input
                type="email"
                value={settings.notificationEmail}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    notificationEmail: event.target.value,
                  })
                }
                placeholder="comercial@empresa.com.br"
              />
              <small>
                Deixe vazio para desativar o aviso de novas solicitações.
              </small>
            </label>
            <div
              className={`email-service-status ${settings.smtpConfigured ? "ready" : "pending"}`}
            >
              <Icon
                name={settings.smtpConfigured ? "check" : "alert"}
                size={16}
              />
              <span>
                <strong>
                  {settings.smtpConfigured
                    ? "Serviço de e-mail conectado"
                    : "Serviço de e-mail ainda não conectado"}
                </strong>
                <small>
                  {settings.smtpConfigured
                    ? "Novas solicitações serão notificadas automaticamente."
                    : "O destinatário pode ser salvo, mas o servidor precisa das credenciais SMTP para realizar os envios."}
                </small>
              </span>
            </div>
            {message && (
              <div
                className={
                  message.includes("sucesso") ? "scope-note" : "login-error"
                }
              >
                {message}
              </div>
            )}
            <button className="primary" disabled={saving}>
              {saving ? "Salvando…" : "Salvar notificação"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

function InternalUserModal({ token, user, currentUserId, onClose, onSaved }) {
  const editing = Boolean(user.id);
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    internalCategory: user.internalCategory || "administration",
    active: user.active !== false,
    password: "",
    accessProfileId: user.accessProfileId ? String(user.accessProfileId) : "",
  });
  const [catalog, setCatalog] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [permissionStates, setPermissionStates] = useState({});
  const [permissionScopes, setPermissionScopes] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${apiBase}/settings/access-catalog`, { headers }),
      fetch(`${apiBase}/settings/access-profiles`, { headers }),
      fetch(`${apiBase}/settings/access-scopes`, { headers }),
      ...(editing
        ? [fetch(`${apiBase}/settings/users/${user.id}/access`, { headers })]
        : []),
    ])
      .then(async (responses) => {
        if (!responses.every((response) => response.ok)) throw new Error();
        const [nextCatalog, nextProfiles, nextScopes, access] =
          await Promise.all(responses.map((response) => response.json()));
        setCatalog(nextCatalog);
        setProfiles(nextProfiles.filter((item) => item.active));
        setScopes(nextScopes);
        if (access) {
          setForm((current) => ({
            ...current,
            accessProfileId: access.accessProfileId
              ? String(access.accessProfileId)
              : "",
          }));
          setPermissionStates(
            Object.fromEntries([
              ...(access.addedPermissions || []).map((key) => [key, "allow"]),
              ...(access.removedPermissions || []).map((key) => [key, "deny"]),
            ]),
          );
          setPermissionScopes(
            Object.fromEntries(
              (access.accessScopes || []).map((item) => [
                item.permission,
                item.scopeType,
              ]),
            ),
          );
        } else if (nextProfiles[0]) {
          const defaultProfile =
            nextProfiles.find(
              (item) => item.systemKey === "administrative_management",
            ) || nextProfiles[0];
          setForm((current) => ({
            ...current,
            accessProfileId: String(defaultProfile.id),
          }));
        }
      })
      .catch(() => setError("Não foi possível carregar a política de acesso."));
  }, [token, editing, user.id]);
  const selectedProfile = profiles.find(
    (item) => Number(item.id) === Number(form.accessProfileId),
  );
  const inherited = new Set(selectedProfile?.permissions || []);
  const isDirector = selectedProfile?.systemKey === "director";
  const stateFor = (key) => permissionStates[key] || "inherit";
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/settings/users${editing ? `/${user.id}` : ""}`,
        {
          method: editing ? "PATCH" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...form,
            accessProfileId: Number(form.accessProfileId) || null,
            permissionOverrides: Object.entries(permissionStates)
              .filter(([, effect]) => effect !== "inherit")
              .map(([permission, effect]) => ({ permission, effect })),
            accessScopes: Object.entries(permissionScopes)
              .filter(([, scopeType]) => scopeType)
              .map(([permission, scopeType]) => ({
                permission,
                scopeType,
                scopeId: 0,
              })),
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const messages = {
          invalid_user_identity: "Informe nome e um e-mail válido.",
          password_too_short: "A senha deve ter pelo menos 8 caracteres.",
          invalid_internal_category: "Área interna inválida.",
          email_already_registered: "Este e-mail já está cadastrado.",
          cannot_deactivate_current_user:
            "Você não pode inativar o próprio usuário.",
          last_active_administrator:
            "É necessário manter ao menos um usuário ativo da Diretoria.",
          invalid_access_profile: "Selecione um perfil de acesso válido.",
        };
        throw new Error(
          messages[data.error] || "Não foi possível salvar o usuário.",
        );
      }
      onSaved();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };
  const modules = [...new Set(catalog.map((item) => item.module))];
  const restoreDefaults = async () => {
    if (
      !editing ||
      !window.confirm(
        "Restaurar todas as permissões e escopos para o padrão do perfil selecionado?",
      )
    )
      return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/settings/users/${user.id}/access/restore-defaults`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error();
      setPermissionStates({});
      setPermissionScopes({});
    } catch {
      setError("Não foi possível restaurar o acesso padrão.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal internal-user-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>
              {editing
                ? "Editar usuário administrativo"
                : "Novo usuário administrativo"}
            </h2>
            <p>
              Perfil padrão, exceções individuais e escopo operacional efetivo.
            </p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="form-grid">
          <label>
            Nome completo*
            <input
              required
              value={form.name}
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
            />
          </label>
          <label>
            E-mail corporativo*
            <input
              required
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
            />
          </label>
          <label>
            Área interna*
            <select
              value={form.internalCategory}
              onChange={(event) =>
                setForm({ ...form, internalCategory: event.target.value })
              }
            >
              {internalCategoryOptions.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Perfil de acesso*
            <select
              required
              value={form.accessProfileId}
              onChange={(event) => {
                setForm({ ...form, accessProfileId: event.target.value });
                setPermissionStates({});
                setPermissionScopes({});
              }}
            >
              <option value="">Selecione</option>
              {profiles.map((profile) => (
                <option value={profile.id} key={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {editing ? "Nova senha (opcional)" : "Senha inicial*"}
            <input
              required={!editing}
              type="password"
              minLength="8"
              value={form.password}
              onChange={(event) =>
                setForm({ ...form, password: event.target.value })
              }
              placeholder={
                editing
                  ? "Mantenha vazio para não alterar"
                  : "Mínimo de 8 caracteres"
              }
            />
          </label>
        </div>
        {isDirector && (
          <div className="director-access-note">
            <Icon name="check" size={17} />
            <span>
              <strong>Diretoria possui acesso total obrigatório</strong>
              <small>
                Essa regra é aplicada também pelo servidor e não pode ser
                desmarcada nesta tela.
              </small>
            </span>
          </div>
        )}
        <div className="access-policy-summary">
          <div>
            <strong>{selectedProfile?.name || "Perfil não selecionado"}</strong>
            <small>
              {selectedProfile?.description ||
                "Selecione o perfil base do usuário."}
            </small>
          </div>
          {editing && (
            <button type="button" onClick={restoreDefaults} disabled={saving}>
              Restaurar padrão do perfil
            </button>
          )}
        </div>
        <div className="internal-permission-matrix modern-access-matrix">
          {modules.map((module) => (
            <details key={module} open={module === modules[0]}>
              <summary>{module}</summary>
              {catalog
                .filter((item) => item.module === module)
                .map((item) => {
                  const reserved = [
                    "settings.manage",
                    "kanban.configure",
                  ].includes(item.key);
                  const state = stateFor(item.key);
                  const effective =
                    state === "allow" ||
                    (state === "inherit" && inherited.has(item.key));
                  return (
                    <div className="access-permission-row" key={item.key}>
                      <span>
                        <strong>{item.description}</strong>
                        <small>{item.key}</small>
                      </span>
                      <span className={`access-source-badge ${state}`}>
                        {state === "allow"
                          ? "Adição manual"
                          : state === "deny"
                            ? "Remoção manual"
                            : inherited.has(item.key)
                              ? "Herdada do perfil"
                              : "Não concedida"}
                      </span>
                      <select
                        aria-label={`Regra de ${item.description}`}
                        value={state}
                        disabled={isDirector || reserved}
                        onChange={(event) =>
                          setPermissionStates((current) => ({
                            ...current,
                            [item.key]: event.target.value,
                          }))
                        }
                      >
                        <option value="inherit">Usar perfil</option>
                        <option value="allow">Permitir</option>
                        <option value="deny">Negar</option>
                      </select>
                      <select
                        aria-label={`Escopo de ${item.description}`}
                        value={permissionScopes[item.key] || ""}
                        disabled={!effective}
                        onChange={(event) =>
                          setPermissionScopes((current) => ({
                            ...current,
                            [item.key]: event.target.value,
                          }))
                        }
                      >
                        <option value="">Escopo do perfil</option>
                        {scopes.map((scope) => (
                          <option value={scope.key} key={scope.key}>
                            {scope.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
            </details>
          ))}
        </div>
        {editing && (
          <label className="internal-active-toggle">
            <input
              type="checkbox"
              checked={form.active}
              disabled={Number(user.id) === Number(currentUserId)}
              onChange={(event) =>
                setForm({ ...form, active: event.target.checked })
              }
            />
            <span>
              <strong>Usuário ativo</strong>
              <small>
                {Number(user.id) === Number(currentUserId)
                  ? "O usuário atual não pode ser inativado."
                  : "Usuários inativos não conseguem acessar o sistema."}
              </small>
            </span>
          </label>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" disabled={saving}>
            {saving ? "Salvando…" : "Salvar usuário"}
          </button>
        </div>
      </form>
    </div>
  );
}

const scopeLabels = {
  own: "Próprios",
  linked: "Atividades vinculadas",
  team: "Equipe supervisionada",
  unit: "Unidade autorizada",
  client: "Cliente autorizado",
  global: "Global",
};

function SettingsAccessManager({ token, tab, setTab }) {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const endpoint = tab === "profiles" ? "access-profiles" : "job-functions";
  const blank =
    tab === "profiles"
      ? {
          name: "",
          description: "",
          defaultScope: "global",
          permissions: [],
          active: true,
        }
      : { name: "", active: true };
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const responses = await Promise.all([
        fetch(`${apiBase}/settings/${endpoint}`, { headers }),
        ...(tab === "profiles"
          ? [fetch(`${apiBase}/settings/access-catalog`, { headers })]
          : []),
      ]);
      if (!responses.every((item) => item.ok)) throw new Error();
      const receivedItems = await responses[0].json();
      const normalizedItems = Array.isArray(receivedItems)
        ? receivedItems.map((item) => ({
            ...item,
            permissions: Array.isArray(item?.permissions)
              ? item.permissions
              : [],
          }))
        : [];
      setItems(normalizedItems);
      if (responses[1]) {
        const receivedCatalog = await responses[1].json();
        setCatalog(
          Array.isArray(receivedCatalog)
            ? receivedCatalog
            : Array.isArray(receivedCatalog?.permissions)
              ? receivedCatalog.permissions
              : [],
        );
      } else {
        setCatalog([]);
      }
    } catch {
      setItems([]);
      setCatalog([]);
      setError("Não foi possível carregar as configurações de acesso.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setEditing(null);
    load();
  }, [tab]);
  const form = editing
    ? {
        ...editing,
        permissions: Array.isArray(editing.permissions)
          ? editing.permissions
          : [],
      }
    : blank;
  const update = (field, value) => setEditing({ ...form, [field]: value });
  const toggle = (key) =>
    update(
      "permissions",
      (form.permissions || []).includes(key)
        ? form.permissions.filter((item) => item !== key)
        : [...(form.permissions || []), key],
    );
  const save = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const response = await fetch(
        `${apiBase}/settings/${endpoint}${form.id ? `/${form.id}` : ""}`,
        {
          method: form.id ? "PATCH" : "POST",
          headers,
          body: JSON.stringify(form),
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "save_failed");
      }
      setEditing(null);
      await load();
    } catch (saveError) {
      setError(
        saveError.message === "profile_name_in_use"
          ? "Já existe um perfil com esse nome."
          : "Não foi possível salvar o perfil. Tente novamente.",
      );
    }
  };
  const remove = async (item) => {
    if (!window.confirm(`Excluir ${item.name}?`)) return;
    const response = await fetch(`${apiBase}/settings/${endpoint}/${item.id}`, {
      method: "DELETE",
      headers,
    });
    if (response.ok) load();
    else
      setError(
        tab === "profiles"
          ? "Perfil em uso: altere os colaboradores antes de excluir."
          : "Função em uso: altere os colaboradores antes de excluir.",
      );
  };
  return (
    <div className="settings-page">
      <SettingsTabs tab={tab} setTab={setTab} />
      {error && <div className="login-error">{error}</div>}
      {tab === "profiles" && (
        <section className="panel access-manager-intro">
          <div>
            <span>CONTROLE DE ACESSO</span>
            <h2>Perfis operacionais</h2>
            <p>
              Defina as permissões herdadas e o alcance padrão de cada perfil.
              Exceções individuais continuam sendo configuradas no cadastro do
              usuário.
            </p>
          </div>
          <div className="access-manager-summary">
            <strong>{items.length}</strong>
            <small>perfis ativos e protegidos</small>
          </div>
        </section>
      )}
      <div
        className={`collaborator-layout${tab === "profiles" ? " access-profile-layout" : ""}`}
      >
        <form className="panel inline-form collaborator-form" onSubmit={save}>
          <h3>
            {form.id ? "Editar" : "Adicionar"}{" "}
            {tab === "profiles" ? "perfil de acesso" : "função"}
          </h3>
          <label>
            Nome*
            <input
              required
              value={form.name}
              onChange={(event) => update("name", event.target.value)}
            />
          </label>
          {tab === "profiles" && (
            <>
              <label>
                Descrição
                <textarea
                  value={form.description || ""}
                  onChange={(event) =>
                    update("description", event.target.value)
                  }
                />
              </label>
              <label>
                Escopo padrão
                <select
                  value={form.defaultScope || "global"}
                  onChange={(event) =>
                    update("defaultScope", event.target.value)
                  }
                >
                  <option value="own">Próprios</option>
                  <option value="linked">Atividades vinculadas</option>
                  <option value="team">Equipe supervisionada</option>
                  <option value="unit">Unidade autorizada</option>
                  <option value="client">Cliente autorizado</option>
                  <option value="global">Global</option>
                </select>
              </label>
              <div className="profile-permission-matrix">
                {[...new Set(catalog.map((item) => item.module))].map(
                  (module) => (
                    <fieldset key={module}>
                      <legend>{module}</legend>
                      {catalog
                        .filter((item) => item.module === module)
                        .map((item) => (
                          <label key={item.key}>
                            <input
                              type="checkbox"
                              checked={(form.permissions || []).includes(
                                item.key,
                              )}
                              onChange={() => toggle(item.key)}
                            />
                            <span>
                              <strong>{item.description}</strong>
                              <small>{item.key}</small>
                            </span>
                          </label>
                        ))}
                    </fieldset>
                  ),
                )}
              </div>
            </>
          )}
          <div className="modal-actions">
            {form.id && (
              <button type="button" onClick={() => setEditing(null)}>
                Cancelar
              </button>
            )}
            <button className="primary">Salvar</button>
          </div>
        </form>
        <section className="panel collaborator-list">
          <PanelTitle
            title={
              tab === "profiles" ? "Perfis cadastrados" : "Funções cadastradas"
            }
            subtitle={`${items.length} no total`}
          />
          {loading && <div className="empty">Carregando cadastros…</div>}
          {items.map((item) => (
            <article className="collaborator-row" key={item.id}>
              <span className="avatar">{initials(item.name)}</span>
              <div>
                <strong>{item.name}</strong>
                <small>
                  {tab === "profiles"
                    ? `${(item.permissions || []).length} permissões selecionadas`
                    : item.active
                      ? "Disponível"
                      : "Inativa"}
                </small>
                {tab === "profiles" && (
                  <em>
                    Escopo padrão: {scopeLabels[item.defaultScope] || "Global"}
                    {item.protected ? " • Perfil padrão do sistema" : ""}
                  </em>
                )}
              </div>
              <div className="collaborator-row-actions">
                <button onClick={() => setEditing({ ...item })}>Editar</button>
                {!item.protected && (
                  <button className="danger" onClick={() => remove(item)}>
                    Inativar
                  </button>
                )}
              </div>
            </article>
          ))}
          {!loading && !items.length && (
            <div className="empty">Nenhum cadastro encontrado.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function ComingSoon({ title }) {
  return (
    <div className="coming-soon">
      <div className="metric-icon blue">
        <Icon name="folder" />
      </div>
      <h2>{title}</h2>
      <p>Este módulo está previsto na próxima entrega do MVP.</p>
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onLogin(credentials);
    } catch (loginError) {
      setError(
        loginError.message === "Failed to fetch"
          ? "Não foi possível conectar à API. Confirme se o backend está iniciado."
          : loginError.message,
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="login-page">
      <section className="login-aside">
        <div className="login-brand">
          <img
            src="/aboutplan-logo-white-transparent.png"
            alt="AboutPlan — Gestão de Operações"
          />
          <div className="login-powered-by" aria-label="Powered by AboutSec">
            <span>Powered by</span>
            <strong>AboutSec</strong>
          </div>
        </div>
        <div>
          <span className="login-eyebrow">OPERAÇÃO SOB CONTROLE</span>
          <h1>Obras, equipes e atendimentos em uma visão clara.</h1>
          <p>
            Planeje os dias, acompanhe a execução e mantenha cada informação no
            nível certo de acesso.
          </p>
        </div>
        <small>Gestão de projetos e serviços</small>
      </section>
      <main className="login-main">
        <div className="login-mobile-brand" aria-label="AboutPlan, powered by AboutSec">
          <img src="/aboutplan-logo-white-transparent.png" alt="AboutPlan — Gestão Inteligente" />
          <span>Powered by <strong>AboutSec</strong></span>
        </div>
        <form className="login-card" onSubmit={submit}>
          <div className="login-icon">
            <Icon name="users" size={22} />
          </div>
          <h2>Acesse sua conta</h2>
          <p>Entre com seu usuário corporativo.</p>
          <label>
            Usuário ou e-mail
            <input
              type="text"
              value={credentials.email}
              onChange={(e) =>
                setCredentials({ ...credentials, email: e.target.value })
              }
              required
            />
          </label>
          <label>
            Senha
            <input
              type="password"
              value={credentials.password}
              onChange={(e) =>
                setCredentials({ ...credentials, password: e.target.value })
              }
              required
            />
          </label>
          {error && <div className="login-error">{error}</div>}
          <button className="primary login-submit" disabled={loading}>
            {loading ? "Entrando…" : "Entrar"}
          </button>
        </form>
      </main>
    </div>
  );
}

function CustomerPortal({ token, user, onLogout, theme, onToggleTheme }) {
  const allUnits = { id: "all", name: "Todas as unidades" };
  const [context, setContext] = useState(null);
  const [error, setError] = useState("");
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [portalView, setPortalView] = useState("overview");
  const [overviewPeriod, setOverviewPeriod] = useState("month");
  const [overviewDateFrom, setOverviewDateFrom] = useState(() => { const date = new Date(); date.setDate(date.getDate() - 6); return date.toISOString().slice(0, 10); });
  const [overviewDateTo, setOverviewDateTo] = useState(todayIso());
  const [portalData, setPortalData] = useState([]);
  const [overviewData, setOverviewData] = useState({ tickets: [], orders: [], preventives: [] });
  const [projectSchedule, setProjectSchedule] = useState(null);
  useEffect(() => {
    fetch(`${apiBase}/portal/context`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        setContext(data);
        setSelectedUnit(data.units.length ? allUnits : null);
      })
      .catch(() => setError("Seu acesso ao portal não está disponível."));
  }, [token]);
  const loadPortalRows = async (endpoint, unit = selectedUnit) => {
    const headers = { Authorization: `Bearer ${token}` };
    const needsUnit = endpoint === "maintenance" || endpoint === "quotes";
    const units = unit?.id === "all" && needsUnit ? context.units : [unit];
    const responses = await Promise.all(units.map((item) =>
      fetch(`${apiBase}/portal/${endpoint}${item?.id === "all" ? "" : `?unitId=${item.id}`}`, { headers })
        .then(async (response) => response.ok ? await response.json() : [])));
    return responses.flat();
  };
  useEffect(() => {
    if (!selectedUnit || !context) return;
    setOverviewData({ tickets: [], orders: [], preventives: [] });
    const requests = [];
    if (context.permissions.includes("open_tickets"))
      requests.push(loadPortalRows("tickets").then((tickets) => ({ tickets })));
    if (context.permissions.includes("view_service_orders"))
      requests.push(loadPortalRows("service-orders").then((orders) => ({ orders })));
    if (context.permissions.includes("view_maintenance"))
      requests.push(loadPortalRows("maintenance").then((preventives) => ({ preventives })));
    let active = true;
    Promise.all(requests)
      .then((parts) => {
        if (active) setOverviewData(
          parts.reduce((result, part) => ({ ...result, ...part }), {
            tickets: [],
            orders: [],
            preventives: [],
          }),
        );
      })
      .catch(() => { if (active) setOverviewData({ tickets: [], orders: [], preventives: [] }); });
    return () => { active = false; };
  }, [selectedUnit, context, token]);
  const openPortalView = async (permission, targetUnit = selectedUnit) => {
    if (!targetUnit) return;
    const endpoints = {
      open_tickets: "tickets",
      view_service_orders: "service-orders",
      view_projects: "projects",
      request_quotes: "quotes",
      view_maintenance: "maintenance",
    };
    const endpoint = endpoints[permission];
    if (!endpoint) return;
    if (targetUnit.id !== selectedUnit?.id) setSelectedUnit(targetUnit);
    setPortalView(permission);
    setPortalData(await loadPortalRows(endpoint, targetUnit));
  };
  const openProjectSchedule = async (project) => {
    const response = await fetch(
      `${apiBase}/portal/projects/${project.id}/schedule`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (response.ok) setProjectSchedule(await response.json());
    else setError("Cronograma não disponível para este projeto.");
  };
  return (
    <div className="portal-shell">
      <header className="portal-header">
        <div className="brand">
          <img
            className="brand-icon"
            src="/aboutplan-icon-transparent.png"
            alt="AboutPlan"
          />
          <div>
            <strong>AboutPlan</strong>
            <span>Portal do cliente</span>
          </div>
        </div>
        <div className="portal-header-actions">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
        <button onClick={onLogout}>
          <span className="avatar">{initials(user.name)}</span>
          <div>
            <strong>{user.name}</strong>
            <small>Sair</small>
          </div>
        </button>
        </div>
      </header>
      <main className="portal-main">
        {error && <div className="empty error-text">{error}</div>}
        {!context && !error && (
          <div className="empty">Carregando seu portal…</div>
        )}
        {context && (
          <>
            <div className="portal-welcome">
              <div>
                <span>PORTAL DO CLIENTE</span>
                <h1>Olá, {context.name.split(" ")[0]}</h1>
                <p>
                  {context.clientName} • {context.jobTitle || "Responsável"}
                </p>
              </div>
              <div className="portal-top-controls">
                <label>
                  Unidade
                  <select
                    value={selectedUnit?.id ?? ""}
                    onChange={(e) => {
                      setSelectedUnit(
                        e.target.value === "all" ? allUnits : context.units.find(
                          (unit) => unit.id === Number(e.target.value),
                        ),
                      );
                      setPortalView("overview");
                    }}
                  >
                    <option value="all">Todas as unidades</option>
                    {context.units.map((unit) => (
                      <option value={unit.id} key={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                  </select>
                </label>
                {portalView === "overview" && <PortalOverviewFilters period={overviewPeriod} setPeriod={setOverviewPeriod} dateFrom={overviewDateFrom} setDateFrom={setOverviewDateFrom} dateTo={overviewDateTo} setDateTo={setOverviewDateTo} />}
              </div>
            </div>
            <div
              className="customer-portal-tabs"
              role="navigation"
              aria-label="Áreas do portal"
            >
              <button
                className={portalView === "overview" ? "active" : ""}
                onClick={() => setPortalView("overview")}
              >
                <Icon name="trend" size={15} />
                <span>
                  Visão geral<small>Indicadores da unidade</small>
                </span>
              </button>
              {[
                ["open_tickets", "headset", "Chamados", "Abrir e acompanhar"],
                [
                  "view_service_orders",
                  "clipboard",
                  "Ordens de serviço",
                  "Atendimentos técnicos",
                ],
                ["view_projects", "folder", "Projetos", "Cronogramas e avanço"],
                ["view_maintenance", "calendar", "Preventivas", "Agenda e relatórios"],
                [
                  "request_quotes",
                  "file",
                  "Orçamentos",
                  "Solicitar e acompanhar",
                ],
              ]
                .filter(([permission]) =>
                  context.permissions.includes(permission),
                )
                .map(([permission, icon, label, description]) => (
                  <button
                    className={portalView === permission ? "active" : ""}
                    key={permission}
                    onClick={() => openPortalView(permission)}
                  >
                    <Icon name={icon} size={15} />
                    <span>
                      {label}
                      <small>{description}</small>
                    </span>
                  </button>
                ))}
            </div>
            {portalView === "overview" ? (
              <PortalOverview
                tickets={overviewData.tickets}
                orders={overviewData.orders}
                preventives={overviewData.preventives || []}
                canViewPreventives={context.permissions.includes("view_maintenance")}
                period={overviewPeriod}
                dateFrom={overviewDateFrom}
                dateTo={overviewDateTo}
              />
            ) : portalView === "open_tickets" ? (
              <PortalTickets
                clientName={context.clientName}
                unit={selectedUnit}
                units={context.units}
                items={portalData}
                token={token}
                onRefresh={() => openPortalView("open_tickets")}
              />
            ) : portalView === "view_service_orders" ? (
              <ProfessionalPortalServiceOrders
                items={portalData}
                token={token}
                unit={selectedUnit}
              />
            ) : portalView === "view_projects" ? (
              <ProfessionalPortalProjects
                items={portalData}
                unit={selectedUnit}
                onOpen={openProjectSchedule}
              />
            ) : portalView === "view_maintenance" ? (
              <PortalPreventives items={portalData} unit={selectedUnit} units={context.units} clientName={context.clientName} token={token} canViewAssets={context.permissions.includes("view_assets")} />
            ) : portalView === "request_quotes" ? (
              <PortalQuotes
                clientName={context.clientName}
                contactId={context.id}
                unit={selectedUnit}
                units={context.units}
                items={portalData}
                token={token}
                onRefresh={() => openPortalView("request_quotes")}
              />
            ) : null}
            {projectSchedule && (
              <CustomerScheduleModal
                data={projectSchedule}
                onClose={() => setProjectSchedule(null)}
              />
            )}
            {!context.units.length && (
              <div className="restricted-result">
                <Icon name="building" />
                <div>
                  <strong>Nenhuma unidade liberada</strong>
                  <p>
                    Solicite ao administrador do contrato a liberação de uma
                    unidade.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export function PortalQuotes({ clientName, contactId, unit, units, items, token, onRefresh }) {
  const [open, setOpen] = useState(false),
    [saving, setSaving] = useState(false),
    [readingPhotos, setReadingPhotos] = useState(false),
    [readingAttachments, setReadingAttachments] = useState(false),
    [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [kpiPeriod, setKpiPeriod] = useState("month");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quotePage, setQuotePage] = useState(1);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [form, setForm] = useState({
    unitId: "",
    title: "",
    location: "",
    category: "Projeto",
    description: "",
    photos: [],
    attachments: [],
  });
  const openQuote = async (item) => {
    try {
      const response = await fetch(`${apiBase}/portal/quotes/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("quote_not_found");
      setSelectedQuote(await response.json());
    } catch {
      setError("Não foi possível abrir a solicitação.");
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (readingPhotos || readingAttachments) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${apiBase}/portal/quotes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error("invalid_quote_request");
      setOpen(false);
      const submittedUnitId = form.unitId;
      setForm({unitId:"",title:"",location:"",category:"Projeto",description:"",photos:[],attachments:[]});
      await onRefresh(submittedUnitId);
    } catch {
      setError("Não foi possível enviar a solicitação. Confira os campos e anexos.");
    } finally {
      setSaving(false);
    }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancelar esta solicitação de orçamento?")) return;
    const response = await fetch(`${apiBase}/portal/quotes/${id}/cancel`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      setSelectedQuote(null);
      onRefresh();
    } else setError("Esta solicitação não pode mais ser cancelada.");
  };
  const closedStatuses = ["sent", "accepted", "declined", "cancelled"];
  const kpis = quoteKpis(items, kpiPeriod);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdueItems = items.filter((item) =>
    !closedStatuses.includes(item.status) &&
    item.promisedProposalDate &&
    new Date(`${item.promisedProposalDate}T00:00:00`) < today,
  );
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const legacyQuoteCategories = [...new Set(items.map((item) => item.category).filter(Boolean))]
    .filter((category) => !quoteCategoryOptions.includes(category));
  const filteredItems = items.filter((item) => {
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    const matchesCategory =
      categoryFilter === "all" ||
      (item.category || "Outros") === categoryFilter;
    const matchesQuery =
      !normalizedQuery ||
      [
        item.title,
        item.location,
        item.category,
        `ORC-${String(item.id).padStart(5, "0")}`,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLocaleLowerCase("pt-BR").includes(normalizedQuery),
        );
    return matchesStatus && matchesCategory && matchesQuery;
  });
  const quotesPerPage = 8;
  const quotePageCount = Math.max(
    1,
    Math.ceil(filteredItems.length / quotesPerPage),
  );
  const safeQuotePage = Math.min(quotePage, quotePageCount);
  const visibleQuotes = filteredItems.slice(
    (safeQuotePage - 1) * quotesPerPage,
    safeQuotePage * quotesPerPage,
  );
  useEffect(() => {
    setQuotePage(1);
  }, [query, statusFilter, categoryFilter, unit?.id]);
  const statusProgress = {
    new: 10,
    under_review: 25,
    inspection_pending: 35,
    inspection_scheduled: 45,
    waiting_distributor: 58,
    preparing: 72,
    sent: 90,
    accepted: 100,
    declined: 100,
    cancelled: 100,
  };
  return (
    <section className="portal-quotes">
      <div className="portal-section-head portal-quote-hero">
        <div>
          <span>ORÇAMENTOS</span>
          <h2>{unit?.id === "all" ? "Solicitações de todas as unidades" : `Solicitações de ${unit?.name}`}</h2>
          <p>Peça uma proposta e acompanhe cada etapa até o envio.</p>
        </div>
        <button className="primary" onClick={() => { setForm((current) => ({ ...current, unitId: unit?.id === "all" ? "" : String(unit?.id || "") })); setOpen(true); }}>
          <Icon name="plus" />
          Nova solicitação
          <Icon name="chevron" size={15} />
        </button>
      </div>
      {error && <div className="login-error">{error}</div>}
      <div className="portal-quote-kpi-controls">
        <label htmlFor="portal-quote-kpi-period">Período de Total e Propostas recebidas</label>
        <select id="portal-quote-kpi-period" value={kpiPeriod} onChange={(event) => setKpiPeriod(event.target.value)}>
          <option value="day">Dia</option>
          <option value="week">Semana</option>
          <option value="month">Mês</option>
          <option value="year">Ano</option>
        </select>
      </div>
      <div className="portal-quote-kpis" aria-label="Resumo dos orçamentos">
        <article className="total">
          <i>
            <Icon name="clipboard" />
          </i>
          <div>
            <span>Total</span>
            <strong>{kpis.total}</strong>
            <small>solicitações no período</small>
          </div>
        </article>
        <article className="active">
          <i>
            <Icon name="clock" />
          </i>
          <div>
            <span>Em andamento</span>
            <strong>{kpis.inProgress}</strong>
            <small>ainda sem proposta enviada • todos os períodos</small>
          </div>
        </article>
        <article className="sent">
          <i>
            <Icon name="check" />
          </i>
          <div>
            <span>Propostas recebidas</span>
            <strong>{kpis.received}</strong>
            <small>enviadas no período</small>
          </div>
        </article>
      </div>
      <div className="portal-quote-toolbar">
        <label className="portal-quote-search">
          <Icon name="search" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar solicitação, categoria, local ou número"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Limpar busca"
            >
              ×
            </button>
          )}
        </label>
        <label>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">Todos os status</option>
            {quoteStatusOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {value === "new" ? "Em andamento" : label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Categoria</span>
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            <option value="all">Todas as categorias</option>
            {quoteCategoryOptions.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
            {legacyQuoteCategories.length > 0 && <optgroup label="Categorias anteriores">
              {legacyQuoteCategories.map((category) => <option key={category} value={category}>{category}</option>)}
            </optgroup>}
          </select>
        </label>
      </div>
      <div className="portal-quote-list-shell">
        <div
          className={`portal-quote-table-scroll ${actionMenuId ? "menu-open" : ""}`}
        >
          <table className="portal-quote-table">
            <thead>
              <tr>
                <th>Nº solicitação</th>
                <th>Título</th>
                <th>Unidade / local</th>
                <th>Categoria</th>
                <th>Status</th>
                <th>Prazo AboutPlan</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {visibleQuotes.map((item) => {
                const isOverdue = overdueItems.some(
                  (quote) => quote.id === item.id,
                );
                return (
                  <tr key={item.id} className={isOverdue ? "overdue" : ""}>
                    <td>
                      <button
                        className="portal-quote-number"
                        onClick={() => openQuote(item)}
                      >
                        ORC-{String(item.id).padStart(5, "0")}
                      </button>
                    </td>
                    <td>
                      <strong>{item.title}</strong>
                    </td>
                    <td>
                      <span className="portal-quote-location">
                        <Icon name="building" size={16} />
                        <span>
                          <strong>{item.unitName || units.find((candidate) => candidate.id === item.unitId)?.name || unit?.name}</strong>
                          <small>{item.location}</small>
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className="portal-quote-category">
                        {item.category || "Outros"}
                      </span>
                    </td>
                    <td>
                      <i
                        className={`status-pill ${quoteStatusTone(item.status)}`}
                      >
                        <b />
                        {item.status === "new"
                          ? "Em andamento"
                          : quoteStatusLabel(item.status)}
                      </i>
                    </td>
                    <td>
                      {isOverdue ? (
                        <span className="portal-quote-overdue">Vencido</span>
                      ) : (
                        quoteDisplayDate(item.promisedProposalDate)
                      )}
                    </td>
                    <td className="portal-quote-row-actions">
                      <button
                        className="portal-quote-view"
                        onClick={() => openQuote(item)}
                        aria-label="Ver solicitação"
                      >
                        <Icon name="eye" />
                      </button>
                      <div className="portal-quote-more-wrap">
                        <button
                          className="portal-quote-more"
                          onClick={() =>
                            setActionMenuId(
                              actionMenuId === item.id ? null : item.id,
                            )
                          }
                          aria-label="Mais ações"
                        >
                          <Icon name="more" />
                        </button>
                        {actionMenuId === item.id && (
                          <div className="portal-quote-more-menu">
                            <button
                              onClick={() => {
                                openQuote(item);
                                setActionMenuId(null);
                              }}
                            >
                              Ver solicitação
                            </button>
                            {Number(item.requestedByContactId) === Number(contactId) && !closedStatuses.includes(item.status) && (
                              <button
                                className="text-danger"
                                onClick={() => cancel(item.id)}
                              >
                                Cancelar solicitação
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredItems.length && (
                <tr>
                  <td colSpan="7">
                    <div className="empty portal-quote-table-empty">
                      {items.length
                        ? "Nenhuma solicitação corresponde aos filtros."
                        : "Nenhuma solicitação de orçamento nesta unidade."}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="portal-quote-pagination">
          <span>
            Exibindo{" "}
            {filteredItems.length ? (safeQuotePage - 1) * quotesPerPage + 1 : 0}
            –{Math.min(safeQuotePage * quotesPerPage, filteredItems.length)} de{" "}
            {filteredItems.length} solicitações
          </span>
          <div>
            <button
              disabled={safeQuotePage === 1}
              onClick={() => setQuotePage((page) => Math.max(1, page - 1))}
            >
              ‹
            </button>
            <strong>{safeQuotePage}</strong>
            <button
              disabled={safeQuotePage === quotePageCount}
              onClick={() =>
                setQuotePage((page) => Math.min(quotePageCount, page + 1))
              }
            >
              ›
            </button>
          </div>
        </div>
      </div>
      {selectedQuote && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setSelectedQuote(null)
          }
        >
          <section className="modal portal-quote-detail-modal">
            <div className="modal-head portal-quote-detail-head">
              <div>
                <span>ORC-{String(selectedQuote.id).padStart(5, "0")}</span>
                <h2>{selectedQuote.title}</h2>
                <p>
                  {clientName} • {units.find((item) => String(item.id) === String(form.unitId))?.name || unit?.name}
                </p>
              </div>
              <button type="button" onClick={() => setSelectedQuote(null)}>
                ×
              </button>
            </div>
            <div className="portal-quote-detail-status">
              <div>
                <small>Status atual</small>
                <strong>{quoteStatusLabel(selectedQuote.status)}</strong>
              </div>
              <i
                className={`status-pill ${quoteStatusTone(selectedQuote.status)}`}
              >
                {statusProgress[selectedQuote.status] || 0}% do fluxo
              </i>
            </div>
            <div className="portal-quote-detail-grid">
              <div>
                <small>Categoria</small>
                <strong>{selectedQuote.category || "Outros"}</strong>
              </div>
              <div>
                <small>Local</small>
                <strong>{selectedQuote.location}</strong>
              </div>
              <div>
                <small>Solicitado em</small>
                <strong>
                  {new Date(selectedQuote.createdAt).toLocaleDateString(
                    "pt-BR",
                  )}
                </strong>
              </div>
              <div>
                <small>Prazo informado</small>
                <strong>
                  {quoteDisplayDate(selectedQuote.promisedProposalDate)}
                </strong>
              </div>
              <div>
                <small>Vistoria</small>
                <strong>
                  {selectedQuote.inspectionScheduledAt
                    ? new Date(
                        selectedQuote.inspectionScheduledAt,
                      ).toLocaleString("pt-BR")
                    : "Não agendada"}
                </strong>
              </div>
            </div>
            <div className="portal-quote-description">
              <small>Descrição da solicitação</small>
              <p>{selectedQuote.description}</p>
            </div>
            <QuotePhotoGallery photos={selectedQuote.photos} />
            <QuoteAttachments files={selectedQuote.attachments} />
            <div className="portal-quote-detail-actions">
              {selectedQuote.proposalDataUrl && (
                <a
                  className="portal-quote-download"
                  href={selectedQuote.proposalDataUrl}
                  download={selectedQuote.proposalFileName || "proposta.pdf"}
                >
                  <Icon name="download" />
                  Baixar proposta
                </a>
              )}
              {Number(selectedQuote.requestedByContactId) === Number(contactId) && !closedStatuses.includes(selectedQuote.status) && (
                <button
                  className="text-danger"
                  onClick={() => cancel(selectedQuote.id)}
                >
                  Cancelar solicitação
                </button>
              )}
              <button type="button" onClick={() => setSelectedQuote(null)}>
                Fechar
              </button>
            </div>
          </section>
        </div>
      )}
      {open && (
        <div
          className="modal-backdrop"
          onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <form className="modal portal-quote-modal" onSubmit={submit}>
            <div className="modal-head">
              <div>
                <span>NOVO ORÇAMENTO</span>
                <h2>Solicitar proposta</h2>
                <p>
                  {clientName} • {unit?.name}
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)}>
                ×
              </button>
            </div>
            <div className="form-grid">
              <label className="wide">
                Título da solicitação
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex.: Ampliação do sistema de CFTV"
                />
              </label>
              <label>
                Unidade
                <select required value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })}>
                  <option value="">Selecione a unidade</option>
                  {units.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
              <label>
                Local do serviço ou entrega
                <input
                  required
                  value={form.location}
                  onChange={(e) =>
                    setForm({ ...form, location: e.target.value })
                  }
                  placeholder="Ex.: Galpão 2, doca de recebimento"
                />
              </label>
              <label>
                Categoria
                <select
                  required
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                >
                  {quoteCategoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide quote-description-field">
                Descrição da solicitação
                <textarea
                  required
                  rows={6}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Informe escopo, quantidade, condições do local e demais detalhes."
                />
              </label>
            </div>
            <TicketPhotoPicker photos={form.photos} onChange={(photos) => setForm((current) => ({...current,photos}))} title="Fotos da solicitação" description="Até 5 fotos JPG, PNG ou WebP de até 4 MB cada." disabled={saving || readingPhotos} onBusyChange={setReadingPhotos} hideFileNames />
            <QuoteAttachments files={form.attachments} onChange={(attachments) => setForm((current) => ({...current,attachments}))} disabled={saving || readingAttachments} onBusyChange={setReadingAttachments} />
            {error && <div className="login-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setOpen(false)}>
                Cancelar
              </button>
              <button className="primary" disabled={saving || readingPhotos || readingAttachments}>
                {saving ? "Enviando…" : "Enviar solicitação"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

function PortalOverviewFilters({ period, setPeriod, dateFrom, setDateFrom, dateTo, setDateTo }) {
  return (
        <div className="portal-period-filter" aria-label="Período dos indicadores">
          <div>
            {[
              ["today", "Diário"],
              ["week", "Semanal"],
              ["month", "Mensal"],
              ["year", "Anual"],
              ["date", "Por data"],
            ].map(([value, label]) => (
              <button
                type="button"
                className={period === value ? "active" : ""}
                key={value}
                aria-pressed={period === value}
                onClick={() => setPeriod(value)}
              >
                {label}
              </button>
            ))}
          </div>
          {period === "date" && (
            <div className="portal-custom-range">
              <label>
                <span>Data inicial</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDateFrom(value);
                    if (value && dateTo && value > dateTo) setDateTo(value);
                  }}
                />
              </label>
              <i>até</i>
              <label>
                <span>Data final</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(event) => {
                    const value = event.target.value;
                    setDateTo(value);
                    if (value && dateFrom && value < dateFrom) setDateFrom(value);
                  }}
                />
              </label>
            </div>
          )}
        </div>
  );
}

function PortalComparisonChart({ eyebrow, title, subtitle, buckets, first, second, firstLabel, secondLabel, tone, emptyDetail }) {
  const maximum = Math.max(2, ...buckets.flatMap((bucket) => [bucket[first], bucket[second]]));
  const hasData = buckets.some((bucket) => bucket[first] || bucket[second]);
  const step = 480 / Math.max(1, buckets.length);
  const barWidth = Math.min(24, step * 0.24);
  const firstGradient = `portal-${tone}-first`;
  const secondGradient = `portal-${tone}-second`;
  return <article className={`panel portal-comparison-card portal-comparison-${tone}`}>
    <div className="portal-comparison-head"><div><small>{eyebrow}</small><h3>{title}</h3><p>{subtitle}</p></div><span>{buckets.reduce((sum, bucket) => sum + bucket[first] + bucket[second], 0)} registros no período</span></div>
    <div className="portal-comparison-legend"><span className="first"><i />{firstLabel}</span><span className="second"><i />{secondLabel}</span></div>
    <div className="portal-comparison-plot">{hasData ? <svg viewBox="0 0 540 225" role="img" aria-label={`${title}: ${buckets.map((bucket) => `${bucket.label}: ${bucket[first]} ${firstLabel}, ${bucket[second]} ${secondLabel}`).join('; ')}`}>
      <defs><linearGradient id={firstGradient} x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor={tone === "preventive" ? "#7794a9" : "#a87a76"}/><stop offset="100%" stopColor={tone === "preventive" ? "#a6bac7" : "#c49a94"}/></linearGradient><linearGradient id={secondGradient} x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stopColor="#719b82"/><stop offset="100%" stopColor="#a2c3ac"/></linearGradient></defs>
      {[0, 1, 2].map((tick) => { const y = 176 - tick * 70; return <g key={tick}><line x1="34" x2="525" y1={y} y2={y} className="portal-comparison-grid"/><text x="27" y={y + 4} textAnchor="end" className="portal-comparison-axis">{Math.round(maximum * tick / 2)}</text></g>; })}
      {buckets.map((bucket, index) => { const x = 34 + step * (index + 0.5); const firstHeight = bucket[first] / maximum * 140; const secondHeight = bucket[second] / maximum * 140; return <g key={bucket.label}><rect fill={`url(#${firstGradient})`} x={x - barWidth - 2} y={176 - firstHeight} width={barWidth} height={firstHeight} rx="4"><title>{bucket.label}: {bucket[first]} {firstLabel}</title></rect><rect fill={`url(#${secondGradient})`} x={x + 2} y={176 - secondHeight} width={barWidth} height={secondHeight} rx="4"><title>{bucket.label}: {bucket[second]} {secondLabel}</title></rect><text x={x} y="199" textAnchor="middle" className="portal-comparison-axis">{bucket.label}</text></g>; })}
    </svg> : <div className="portal-comparison-empty-state"><span>0 no período</span><strong>Nenhuma atividade nesta seleção</strong><p>{emptyDetail || "Não houve movimentação no período selecionado."}</p></div>}</div>
  </article>;
}

function PortalOverview({ tickets, orders, preventives = [], canViewPreventives, period, dateFrom, dateTo }) {
  const dateKey = (value) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return String(value);
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? ""
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };
  const today = todayIso();
  const days = [];
  if (period === "today") days.push(today);
  else if (period === "date") {
    const safeFrom =
      dateFrom && dateTo && dateFrom > dateTo ? dateTo : dateFrom;
    const safeTo = dateFrom && dateTo && dateFrom > dateTo ? dateFrom : dateTo;
    if (safeFrom && safeTo) {
      const cursor = new Date(`${safeFrom}T12:00:00`),
        end = new Date(`${safeTo}T12:00:00`);
      while (cursor <= end && days.length < 366) {
        days.push(dateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  } else if (period === "week") {
    for (let offset = 6; offset >= 0; offset--) {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() - offset);
      days.push(dateKey(date));
    }
  } else if (period === "year") {
    const year = new Date().getFullYear();
    for (let month = 1; month <= 12; month++)
      days.push(`${year}-${String(month).padStart(2, "0")}`);
  } else {
    const date = new Date();
    const year = date.getFullYear(),
      month = date.getMonth();
    for (let day = 1; day <= new Date(year, month + 1, 0).getDate(); day++)
      days.push(
        `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      );
  }
  const periodDescription = period === "today"
    ? "Hoje"
    : period === "week"
      ? "Últimos 7 dias"
      : period === "month"
        ? "Este mês"
        : period === "year"
          ? `Ano de ${new Date().getFullYear()}`
        : dateFrom && dateTo
          ? `${dateFrom.split("-").reverse().join("/")} a ${dateTo.split("-").reverse().join("/")}`
          : "Selecione as datas";
  const solved = orders.filter((order) =>
    ["finished", "closed"].includes(order.status),
  );
  const pendingTickets = tickets.filter((ticket) => !ticket.serviceOrderId);
  const series = days.map((day) => ({
    day,
    tickets: tickets.filter((item) => period === "year" ? dateKey(item.createdAt).startsWith(day) : dateKey(item.createdAt) === day).length,
    orders: solved.filter(
      (item) => period === "year"
        ? dateKey(item.finishedAt || item.attendanceEndedAt).startsWith(day)
        : dateKey(item.finishedAt || item.attendanceEndedAt) === day,
    ).length,
  }));
  const ticketTotal = series.reduce((sum, item) => sum + item.tickets, 0),
    orderTotal = series.reduce((sum, item) => sum + item.orders, 0),
    maximum = Math.max(
      1,
      ...series.flatMap((item) => [item.tickets, item.orders]),
    );
  const resolutionRate = Math.min(
    100,
    Math.round((orderTotal / Math.max(1, ticketTotal || orderTotal)) * 100),
  );
  const chartBuckets = [];
  const chunkSize = period === "year" || period === "today" || period === "week" ? 1 : 7;
  for (let index = 0; index < series.length; index += chunkSize) {
    const segment = series.slice(index, index + chunkSize);
    const first = segment[0]?.day || "";
    const last = segment[segment.length - 1]?.day || "";
    chartBuckets.push({
      label: period === "year" ? new Intl.DateTimeFormat("pt-BR", { month: "short", timeZone: "UTC" }).format(new Date(`${first}-01T12:00:00Z`)) : period === "today" ? "Hoje" : period === "week" ? first.slice(8, 10) : `${first.slice(8, 10)}–${last.slice(8, 10)}`,
      first, last,
      tickets: segment.reduce((sum, item) => sum + item.tickets, 0),
      orders: segment.reduce((sum, item) => sum + item.orders, 0),
      planned: 0,
      realized: 0,
    });
  }
  if (!chartBuckets.length) chartBuckets.push({ label: "Período", tickets: 0, orders: 0, planned: 0, realized: 0 });
  const bucketFor = (value) => {
    const key = dateKey(value);
    const normalized = period === "year" ? key.slice(0, 7) : key;
    return chartBuckets.find((bucket) => normalized >= bucket.first && normalized <= bucket.last);
  };
  if (canViewPreventives) preventives.filter((item) => !["cancelled", "waiting_area_release"].includes(item.status)).forEach((item) => {
    const plannedBucket = bucketFor(item.plannedDate);
    if (plannedBucket) plannedBucket.planned += 1;
    if (["completed", "completed_with_issue"].includes(item.status)) {
      const realizedBucket = bucketFor(item.completedAt);
      if (realizedBucket) realizedBucket.realized += 1;
    }
  });
  const activeOrders = orders.filter(
    (order) => !["finished", "closed"].includes(order.status),
  ).length;
  const portfolioGroups = [
    {
      label: "Chamados pendentes",
      value: pendingTickets.length,
      color: "#f59e42",
    },
    { label: "OS em andamento", value: activeOrders, color: "#7357e6" },
    { label: "OS solucionadas", value: solved.length, color: "#20ae7b" },
  ];
  const portfolioTotal = portfolioGroups.reduce(
    (sum, item) => sum + item.value,
    0,
  );
  let portfolioSweep = 0;
  const portfolioGradient = portfolioGroups
    .map((item) => {
      const start = portfolioSweep;
      portfolioSweep += (item.value / Math.max(1, portfolioTotal)) * 360;
      return `${item.color} ${start}deg ${portfolioSweep}deg`;
    })
    .join(",");
  const preventiveGroups = [
    { key: "done", label: "Concluídas", color: "#75aa8e", value: 0 },
    { key: "progress", label: "Em execução", color: "#9a8bb6", value: 0 },
    { key: "due", label: "Hoje / atrasadas", color: "#c88d86", value: 0 },
    { key: "waiting", label: "Aguardando área", color: "#b4a47e", value: 0 },
    { key: "planned", label: "Programadas", color: "#839fba", value: 0 },
  ];
  const firstDay = days[0] || "";
  const lastDay = days[days.length - 1] || "";
  const preventivePeriodStart = period === "year" ? `${firstDay}-01` : firstDay;
  const preventivePeriodEnd = period === "year" ? `${lastDay}-31` : lastDay;
  preventives.filter((item) => {
    const plannedDay = String(item.plannedDate || "").slice(0, 10);
    return plannedDay && plannedDay >= preventivePeriodStart && plannedDay <= preventivePeriodEnd;
  }).forEach((item) => {
    const group = ["completed", "completed_with_issue"].includes(item.status)
      ? "done"
      : item.status === "waiting_area_release"
        ? "waiting"
      : item.status === "in_progress"
        ? "progress"
        : String(item.effectiveDueDate || item.plannedDate || "").slice(0, 10) <= today
          ? "due"
          : "planned";
    preventiveGroups.find((entry) => entry.key === group).value += 1;
  });
  const preventiveTotal = preventiveGroups.reduce((sum, item) => sum + item.value, 0);
  let preventiveSweep = 0;
  const preventiveGradient = preventiveGroups.filter((item) => item.value).map((item) => {
    const start = preventiveSweep;
    preventiveSweep += (item.value / preventiveTotal) * 360;
    return `${item.color} ${start}deg ${preventiveSweep}deg`;
  }).join(", ");
  const preventiveEligible = preventiveTotal - (preventiveGroups.find((item) => item.key === "waiting")?.value || 0);
  const preventiveRate = preventiveEligible ? Math.round((preventiveGroups[0].value / preventiveEligible) * 100) : 0;
  const upcomingPreventives = preventives.filter((item) =>
    item.status !== "cancelled" &&
    !["completed", "completed_with_issue"].includes(item.status) &&
    String(item.effectiveDueDate || item.plannedDate || "").slice(0, 10) >= today,
  );
  const nextPreventiveDate = upcomingPreventives.map((item) => String(item.effectiveDueDate || item.plannedDate).slice(0, 10)).sort()[0];
  const priorityGroups = [
    { key: "critical", label: "Crítica", color: "#e94f64" },
    { key: "high", label: "Alta", color: "#f59e42" },
    { key: "medium", label: "Média", color: "#3478f6" },
    { key: "low", label: "Baixa", color: "#20ae7b" },
  ].map((group) => ({
    ...group,
    value: tickets.filter((item) => item.priority === group.key).length,
  }));
  const priorityMaximum = Math.max(
    1,
    ...priorityGroups.map((item) => item.value),
  );
  return (
    <section className="portal-overview">
      <div className="portal-kpis">
        <article className="tone-amber">
          <div className="metric-icon amber">
            <Icon name="headset" size={18} />
          </div>
          <div>
            <span>Chamados recebidos</span>
            <b>{ticketTotal}</b>
            <small>No período selecionado</small>
          </div>
        </article>
        <article className="tone-green">
          <div className="metric-icon green">
            <Icon name="check" size={18} />
          </div>
          <div>
            <span>OS solucionadas</span>
            <b>{orderTotal}</b>
            <small>No período selecionado</small>
          </div>
        </article>
        <article className="tone-blue">
          <div className="metric-icon blue">
            <Icon name="trend" size={18} />
          </div>
          <div>
            <span>Índice de resolução</span>
            <b>{resolutionRate}%</b>
            <small>Soluções em relação às entradas</small>
          </div>
        </article>
      </div>
      <div className={`portal-overview-visual-grid ${canViewPreventives ? "with-preventives" : "without-preventives"}`}>
      <div className="portal-comparison-grid-layout">
        <PortalComparisonChart eyebrow="EVOLUÇÃO OPERACIONAL" title="Chamados e atendimentos" subtitle="Entradas e OS solucionadas" buckets={chartBuckets} first="tickets" second="orders" firstLabel="Chamados" secondLabel="OS solucionadas" tone="service" />
        {canViewPreventives && <PortalComparisonChart eyebrow="MANUTENÇÃO PREVENTIVA" title="Programação e execução" subtitle="Visitas planejadas e realizadas" buckets={chartBuckets} first="planned" second="realized" firstLabel="Programadas" secondLabel="Realizadas" tone="preventive" emptyDetail={upcomingPreventives.length ? `${upcomingPreventives.length} preventivas programadas na agenda da unidade. Próxima em ${formatDate(nextPreventiveDate)}.` : "Não há próximas preventivas programadas nesta unidade."} />}
      </div>
      <div className="portal-bi-layout portal-overview-insights-layout">
        <div className="portal-bi-insights">
          {canViewPreventives ? <article className="panel portal-bi-preventives-card">
            <header>
              <small>MANUTENÇÃO PREVENTIVA</small>
              <strong>Atividades da unidade</strong>
              <span>Distribuição por situação · {periodDescription.toLowerCase()}</span>
            </header>
            <div className="portal-bi-preventives-main">
              <div className="portal-bi-preventives-ring" style={{ background: preventiveTotal ? `conic-gradient(${preventiveGradient})` : "#dce4e6" }} role="img" aria-label={`${preventiveTotal} atividades preventivas; ${preventiveGroups.map((item) => `${item.value} ${item.label.toLowerCase()}`).join(", ")}`}>
                <span><b>{preventiveTotal}</b><small>atividades</small></span>
              </div>
              <div className="portal-bi-preventives-legend">
                {preventiveGroups.map((item) => <div key={item.key}>
                  <span><i style={{ backgroundColor: item.color }} />{item.label}<b>{item.value}</b></span>
                  <em><i style={{ width: `${preventiveTotal ? (item.value / preventiveTotal) * 100 : 0}%`, backgroundColor: item.color }} /></em>
                </div>)}
              </div>
            </div>
            <div className="portal-bi-preventives-foot"><span>Execução concluída</span><strong>{preventiveRate}%</strong></div>
            {preventiveTotal === 0 && <div className="portal-preventive-empty-context">
              <strong>Nenhuma preventiva em {periodDescription.toLowerCase()}.</strong>
              {upcomingPreventives.length > 0
                ? <span>Na agenda da unidade: {upcomingPreventives.length} programada(s). Próxima em {formatDate(nextPreventiveDate)}.</span>
                : <span>Não há próximas preventivas programadas nesta unidade.</span>}
            </div>}
          </article> : <article className="panel portal-bi-donut-card">
            <header><small>COMPOSIÇÃO</small><strong>Carteira da unidade</strong><span>Distribuição geral dos atendimentos</span></header>
            <div><div className="portal-bi-donut" style={{ background: portfolioTotal ? `conic-gradient(${portfolioGradient})` : "#e8edf5" }}><span><b>{portfolioTotal}</b>registros</span></div><div className="portal-bi-legend">{portfolioGroups.map((item) => <span key={item.label}><i style={{ background: item.color }}></i>{item.label}<b>{item.value}</b></span>)}</div></div>
          </article>}
          <article className="panel portal-bi-priority-card">
            <header>
              <small>CRITICIDADE</small>
              <strong>Chamados por prioridade</strong>
              <span>Demandas atualmente abertas</span>
            </header>
            <div className="portal-bi-priority-bars">
              {priorityGroups.map((item) => (
                <div key={item.key}>
                  <span>
                    {item.label}
                    <b>{item.value}</b>
                  </span>
                  <i>
                    <em
                      style={{
                        width: `${(item.value / priorityMaximum) * 100}%`,
                        background: item.color,
                      }}
                    ></em>
                  </i>
                </div>
              ))}
            </div>
          </article>
        </div>
      </div>
      </div>
    </section>
  );
}

function PortalProjects({ items, unit, onOpen }) {
  return (
    <section className="portal-data panel">
      <PanelTitle title="Projetos da unidade" subtitle={unit?.name} />
      {items.map((item) => (
        <button
          className="portal-data-row portal-project-row"
          key={item.id}
          onClick={() => onOpen(item)}
        >
          <div className="metric-icon blue">
            <Icon name="folder" size={16} />
          </div>
          <div>
            <strong>{item.name}</strong>
            <small>
              {formatDate(item.startDate)} → {formatDate(item.endDate)} • Abrir
              cronograma visual
            </small>
          </div>
          <span
            className={`record-status ${["completed", "closed"].includes(item.status) ? "done" : "doing"}`}
          >
            {projectStatusLabel(item.status)}
          </span>
        </button>
      ))}
      {!items.length && (
        <div className="empty">Nenhum projeto disponível nesta unidade.</div>
      )}
    </section>
  );
}

function ProfessionalPortalProjects({ items, unit, onOpen }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const completed = items.filter((item) =>
    ["completed", "closed"].includes(item.status),
  );
  const running = items.filter((item) => item.status === "execution");
  const planning = items.filter((item) => item.status === "planning");
  const average = items.length
    ? Math.round(
        items.reduce(
          (sum, item) => sum + Number(item.scheduleProgress || 0),
          0,
        ) / items.length,
      )
    : 0;
  const filtered = items.filter(
    (item) =>
      (!query ||
        `${item.projectNumber || ""} ${item.name}`
          .toLowerCase()
          .includes(query.toLowerCase())) &&
      (status === "all" || item.status === status),
  );
  const ranked = [...items]
    .sort(
      (a, b) =>
        Number(b.scheduleProgress || 0) - Number(a.scheduleProgress || 0),
    )
    .slice(0, 6);
  return (
    <section className="portal-project-workspace">
      <div className="portal-project-hero">
        <div>
          <span>CARTEIRA DE PROJETOS</span>
          <h2>Projetos e obras</h2>
          <p>
            Acompanhe marcos, cronogramas e evolução das entregas da unidade.
          </p>
        </div>
        <div>
          <Icon name="building" size={17} />
          <span>
            Unidade selecionada<strong>{unit?.name}</strong>
          </span>
        </div>
      </div>
      <div className="portal-project-kpis">
        <article>
          <div className="metric-icon blue">
            <Icon name="folder" size={18} />
          </div>
          <span>
            Total de projetos<b>{items.length}</b>
            <small>Carteira da unidade</small>
          </span>
        </article>
        <article>
          <div className="metric-icon purple">
            <Icon name="trend" size={18} />
          </div>
          <span>
            Em execução<b>{running.length}</b>
            <small>Obras ativas</small>
          </span>
        </article>
        <article>
          <div className="metric-icon amber">
            <Icon name="clock" size={18} />
          </div>
          <span>
            Em planejamento<b>{planning.length}</b>
            <small>Preparação e mobilização</small>
          </span>
        </article>
        <article>
          <div className="metric-icon green">
            <Icon name="check" size={18} />
          </div>
          <span>
            Avanço médio<b>{average}%</b>
            <small>{completed.length} concluído(s)</small>
          </span>
        </article>
      </div>
      <article className="panel portal-project-chart">
        <div className="portal-project-chart-head">
          <div>
            <strong>Avanço dos projetos</strong>
            <span>Percentual médio das atividades do cronograma</span>
          </div>
          <small>Barras azuis indicam evolução executada</small>
        </div>
        <div>
          {ranked.map((item) => (
            <button key={item.id} onClick={() => onOpen(item)}>
              <span>
                <strong>
                  {item.projectNumber || "Sem número"} • {item.name}
                </strong>
                <small>{item.taskCount || 0} atividade(s)</small>
              </span>
              <div>
                <i
                  style={{
                    width: `${Math.min(100, Math.max(0, Number(item.scheduleProgress) || 0))}%`,
                  }}
                ></i>
              </div>
              <b>{Number(item.scheduleProgress || 0)}%</b>
            </button>
          ))}
          {!ranked.length && (
            <div className="empty">
              Nenhum projeto disponível para o gráfico.
            </div>
          )}
        </div>
      </article>
      <section className="panel portal-project-list">
        <div className="portal-project-list-head">
          <div>
            <span>ACOMPANHAMENTO</span>
            <h3>Carteira da unidade</h3>
            <p>
              {filtered.length} de {items.length} projetos exibidos
            </p>
          </div>
          <div>
            <label>
              <Icon name="search" size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Número ou nome do projeto"
              />
            </label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="planning">Planejamento</option>
              <option value="execution">Execução</option>
              <option value="paused">Pausado</option>
              <option value="completed">Concluído</option>
              <option value="closed">Encerrado</option>
            </select>
          </div>
        </div>
        <div className="portal-project-grid">
          {filtered.map((item) => (
            <button key={item.id} onClick={() => onOpen(item)}>
              <div className="portal-project-card-top">
                <span>{item.projectNumber || "Projeto sem número"}</span>
                <em
                  className={`record-status ${["completed", "closed"].includes(item.status) ? "done" : "doing"}`}
                >
                  {projectStatusLabel(item.status)}
                </em>
              </div>
              <h4>{item.name}</h4>
              <p>
                {formatDate(item.startDate)} → {formatDate(item.endDate)}
              </p>
              <div className="portal-project-card-progress">
                <span>
                  <b>{Number(item.scheduleProgress || 0)}%</b> concluído
                </span>
                <div>
                  <i
                    style={{
                      width: `${Math.min(100, Math.max(0, Number(item.scheduleProgress) || 0))}%`,
                    }}
                  ></i>
                </div>
              </div>
              <footer>
                <span>{item.taskCount || 0} atividades</span>
                <strong>
                  Abrir cronograma <Icon name="arrow" size={13} />
                </strong>
              </footer>
            </button>
          ))}
          {!filtered.length && (
            <div className="empty">
              Nenhum projeto encontrado para os filtros selecionados.
            </div>
          )}
        </div>
      </section>
    </section>
  );
}

function ProfessionalPortalServiceOrders({ items, token, unit }) {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [period, setPeriod] = useState("all");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [unit?.id]);
  const now = new Date();
  const startToday = new Date();
  startToday.setHours(0, 0, 0, 0);
  const startWeek = new Date(startToday);
  startWeek.setDate(startWeek.getDate() - 6);
  const startMonth = new Date(
    startToday.getFullYear(),
    startToday.getMonth(),
    1,
  );
  const solvedStatuses = ["finished", "closed"];
  const activeStatuses = [
    "new",
    "analysis",
    "scheduled",
    "in_progress",
    "waiting_customer",
    "waiting_material",
  ];
  const filtered = items.filter((item) => {
    const text = [
      item.code,
      item.title,
      item.signedBy,
      item.serviceOrderOpenedByName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (query && !text.includes(query.toLowerCase())) return false;
    if (status !== "all" && item.status !== status) return false;
    const reference = new Date(item.scheduledAt || item.createdAt);
    if (period === "today" && reference < startToday) return false;
    if (period === "week" && reference < startWeek) return false;
    if (period === "month" && reference < startMonth) return false;
    if (period === "custom") {
      const day = item.scheduledAt
        ? String(item.scheduledAt).slice(0, 10)
        : portalTicketDayKey(item.createdAt);
      if (!day || (rangeStart && day < rangeStart) || (rangeEnd && day > rangeEnd)) return false;
    }
    return true;
  });
  const pageSize = 20;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const visibleOrders = filtered.slice(pageStart, pageStart + pageSize);
  const solved = items.filter((item) => solvedStatuses.includes(item.status));
  const active = items.filter((item) => activeStatuses.includes(item.status));
  const scheduled = items.filter((item) => item.status === "scheduled");
  const inProgress = items.filter((item) => item.status === "in_progress");
  const otherActive = items.filter(
    (item) =>
      !solvedStatuses.includes(item.status) &&
      !["scheduled", "in_progress"].includes(item.status),
  );
  const lastDays = [];
  for (let offset = 6; offset >= 0; offset--) {
    const date = new Date(`${portalTodayKey()}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() - offset);
    const key = date.toISOString().slice(0, 10);
    lastDays.push({
      key,
      label: date
        .toLocaleDateString("pt-BR", { weekday: "short", timeZone: "UTC" })
        .replace(".", ""),
      ...portalOsVolumeForDay(items, key),
    });
  }
  const maxDay = Math.max(1, ...lastDays.flatMap((day) => [day.requested, day.solved]));
  const statusGroups = [
    { label: "Finalizadas", count: solved.length, color: "#87ad9a", light: "#afc8b9", dark: "#719d89" },
    { label: "Programadas", count: scheduled.length, color: "#8ca5b8", light: "#afc1cb", dark: "#718fa5" },
    { label: "Em execução", count: inProgress.length, color: "#a69ab5", light: "#c4b9cc", dark: "#8f81a1" },
    ...(otherActive.length
      ? [{ label: "Em andamento", count: otherActive.length, color: "#b7a179", light: "#d0c09d", dark: "#a58e68" }]
      : []),
  ];
  const total = Math.max(1, items.length);
  let sweep = 0;
  const gradient = statusGroups
    .filter((group) => group.count > 0)
    .map((group) => {
      const start = sweep;
      sweep += (group.count / total) * 360;
      const midpoint = start + (sweep - start) / 2;
      return `${group.light} ${start}deg, ${group.color} ${midpoint}deg, ${group.dark} ${sweep}deg`;
    })
    .join(",");
  const statuses = [
    ...new Set(items.map((item) => item.status).filter(Boolean)),
  ];
  return (
    <section className="portal-os-workspace">
      <div className="portal-os-hero">
        <div>
          <span>CENTRAL DE ATENDIMENTOS</span>
          <h2>Ordens de serviço</h2>
          <p>
            Acompanhe o ciclo completo dos atendimentos técnicos da sua unidade.
          </p>
        </div>
        <div>
          <Icon name="building" size={17} />
          <span>
            Unidade selecionada<strong>{unit?.name}</strong>
          </span>
        </div>
      </div>
      <div className="portal-os-kpis">
        <article>
          <div className="metric-icon blue">
            <Icon name="clipboard" size={18} />
          </div>
          <span>
            Total de OS<b>{items.length}</b>
            <small>Histórico da unidade</small>
          </span>
        </article>
        <article>
          <div className="metric-icon amber">
            <Icon name="clock" size={18} />
          </div>
          <span>
            Em andamento<b>{active.length}</b>
            <small>Atendimentos ativos</small>
          </span>
        </article>
        <article>
          <div className="metric-icon purple">
            <Icon name="wrench" size={18} />
          </div>
          <span>
            Em execução<b>{inProgress.length}</b>
            <small>Equipe em atendimento</small>
          </span>
        </article>
        <article>
          <div className="metric-icon green">
            <Icon name="check" size={18} />
          </div>
          <span>
            Solucionadas<b>{solved.length}</b>
            <small>Finalizadas ou encerradas</small>
          </span>
        </article>
      </div>
      <div className="portal-os-charts">
        <article className="panel portal-os-volume">
          <div className="portal-os-chart-head">
            <div>
              <strong>Ordens de serviço nos últimos sete dias</strong>
              <span>Solicitadas e solucionadas por dia</span>
            </div>
            <div className="portal-os-volume-legend" aria-label="Legenda do gráfico">
              <span><i className="requested" />Solicitadas</span>
              <span><i className="solved" />Solucionadas</span>
            </div>
          </div>
          <div className="portal-os-bars">
            {lastDays.map((day) => (
              <div className="portal-os-day" key={day.key}>
                <div className="portal-os-day-bars">
                  {[["requested", "Solicitadas"], ["solved", "Solucionadas"]].map(([series, label]) => (
                    <span className={`portal-os-day-bar ${series}`} key={series} title={`${label}: ${day[series]} em ${day.key}`} aria-label={`${label}: ${day[series]} em ${day.key}`} style={{ "--bar-height": `${day[series] ? Math.max(8, (day[series] / maxDay) * 100) : 2}%` }}>
                      {day[series] > 0 && <b>{day[series]}</b>}
                      <i />
                    </span>
                  ))}
                </div>
                <small>{day.label}</small>
              </div>
            ))}
          </div>
        </article>
        <article className="panel portal-os-status-chart">
          <div className="portal-os-chart-head">
            <div>
              <strong>Situação das ordens</strong>
              <span>Distribuição do histórico</span>
            </div>
          </div>
          <div className="portal-os-status-body">
            <div
              className="portal-os-donut"
              style={{
                background: items.length
                  ? `conic-gradient(${gradient})`
                  : "#e9edf3",
              }}
            >
              <span>
                <b>{items.length}</b>OS
              </span>
            </div>
            <div>
              {statusGroups.map((group) => (
                <span key={group.label}>
                  <i style={{ background: `linear-gradient(135deg, ${group.light}, ${group.dark})` }}></i>
                  {group.label}
                  <b>{group.count}</b>
                </span>
              ))}
            </div>
          </div>
        </article>
      </div>
      <section className="panel portal-os-list">
        <div className="portal-os-list-head">
          <div>
            <span>ACOMPANHAMENTO</span>
            <h3>Histórico de atendimentos</h3>
            <p>
              {filtered.length ? `${pageStart + 1}–${pageStart + visibleOrders.length} de ${filtered.length}` : "0"} ordens exibidas{filtered.length !== items.length ? ` · ${items.length} no histórico` : ""}
            </p>
          </div>
          <div>
            <label>
              <Icon name="search" size={14} />
              <input
                value={query}
                onChange={(event) => { setQuery(event.target.value); setPage(1); }}
                placeholder="Número da OS ou título do chamado"
              />
            </label>
            <select
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(1); }}
            >
              <option value="all">Todos os status</option>
              {statuses.map((value) => (
                <option value={value} key={value}>
                  {orderStatusLabel(value)}
                </option>
              ))}
            </select>
            <select
              value={period}
              onChange={(event) => { setPeriod(event.target.value); setPage(1); }}
            >
              <option value="all">Todo o período</option>
              <option value="today">Hoje</option>
              <option value="week">Últimos 7 dias</option>
              <option value="month">Mês atual</option>
              <option value="custom">Por data</option>
            </select>
          </div>
        </div>
        {period === "custom" && (
          <div className="portal-os-date-range" role="group" aria-label="Intervalo de datas dos atendimentos">
            <label>Data inicial<input type="date" value={rangeStart} onChange={(event) => { const value = event.target.value; setRangeStart(value); if (rangeEnd && value > rangeEnd) setRangeEnd(value); setPage(1); }} /></label>
            <span aria-hidden="true">até</span>
            <label>Data final<input type="date" value={rangeEnd} onChange={(event) => { const value = event.target.value; setRangeEnd(value); if (rangeStart && value < rangeStart) setRangeStart(value); setPage(1); }} /></label>
          </div>
        )}
        <div className="portal-os-table-head">
          <span>Nº da OS / título do chamado</span>
          <span>Atendimento</span>
          <span>Responsável</span>
          <span>Situação</span>
          <span></span>
        </div>
        {visibleOrders.map((item) => (
          <button
            type="button"
            className="portal-os-row"
            key={item.id}
            onClick={() => setSelected(item)}
          >
            <div>
              <i
                className={`os-status-mark ${solvedStatuses.includes(item.status) ? "solved" : "active"}`}
              ></i>
              <span>
                <strong className="portal-os-code">{item.code}</strong>
                <b className="portal-os-title">
                  {item.title || "Chamado sem título"}
                </b>
                <small>
                  {item.description || "Atendimento técnico da unidade"}
                </small>
              </span>
            </div>
            <time>{formatSchedule(item.scheduledAt)}</time>
            <span>{item.signedBy || "A definir"}</span>
            <em
              className={`record-status ${solvedStatuses.includes(item.status) ? "done" : "doing"}`}
            >
              {orderStatusLabel(item.status)}
            </em>
            <Icon name="arrow" size={15} />
          </button>
        ))}
        {!filtered.length && (
          <div className="empty">
            Nenhuma ordem de serviço encontrada para os filtros selecionados.
          </div>
        )}
        {pageCount > 1 && (
          <nav className="portal-os-pagination" aria-label="Páginas do histórico de atendimentos">
            <span>Página {currentPage} de {pageCount}</span>
            <div>
              <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Anterior</button>
              <button type="button" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>Próxima</button>
            </div>
          </nav>
        )}
      </section>
      {selected && (
        <CustomerServiceOrderModal
          order={selected}
          token={token}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

function PortalServiceOrders({ items, token }) {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [period, setPeriod] = useState("all");
  const normalizedQuery = query.trim().toLowerCase();
  const statusOptions = [
    ...new Set(items.map((item) => item.status).filter(Boolean)),
  ];
  const now = new Date();
  const filteredItems = items.filter((item) => {
    const text = [
      item.code,
      item.title,
      item.responsibleName,
      item.assigneeName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (normalizedQuery && !text.includes(normalizedQuery)) return false;
    if (status !== "all" && item.status !== status) return false;
    if (period !== "all") {
      const reference = new Date(item.scheduledAt || item.createdAt);
      if (Number.isNaN(reference.getTime())) return false;
      if (period === "today" && reference.toDateString() !== now.toDateString())
        return false;
      if (period === "week") {
        const start = new Date(now);
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - 6);
        if (reference < start || reference > now) return false;
      }
      if (
        period === "month" &&
        (reference.getMonth() !== now.getMonth() ||
          reference.getFullYear() !== now.getFullYear())
      )
        return false;
    }
    return true;
  });
  const clearFilters = () => {
    setQuery("");
    setStatus("all");
    setPeriod("all");
  };
  return (
    <section className="portal-data panel">
      <PanelTitle
        title="Ordens de serviço da unidade"
        subtitle="Acompanhamento dos atendimentos"
      />
      <div className="filters-card" style={{ marginBottom: 18 }}>
        <div className="filters-grid">
          <label className="field">
            <span>Buscar OS</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Número, atividade ou responsável"
            />
          </label>
          <label className="field">
            <span>Status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">Todos</option>
              {statusOptions.map((value) => (
                <option key={value} value={value}>
                  {orderStatusLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Período</span>
            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="all">Todo o período</option>
              <option value="today">Hoje</option>
              <option value="week">Últimos 7 dias</option>
              <option value="month">Mês atual</option>
            </select>
          </label>
        </div>
        <div className="filter-summary">
          <span>
            Exibindo {filteredItems.length} de {items.length} OS
          </span>
          {(query || status !== "all" || period !== "all") && (
            <button
              type="button"
              className="link-button"
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>
      {filteredItems.map((item) => (
        <button
          type="button"
          className="portal-data-row portal-project-row"
          key={item.id}
          onClick={() => setSelected(item)}
        >
          <div className="metric-icon blue">
            <Icon name="clipboard" size={16} />
          </div>
          <div>
            <strong>
              {item.code} • {item.title}
            </strong>
            <small>
              {formatSchedule(item.scheduledAt)} •{" "}
              {orderStatusLabel(item.status)}
            </small>
          </div>
          <span
            className={`record-status ${["finished", "closed"].includes(item.status) ? "done" : "doing"}`}
          >
            {orderStatusLabel(item.status)}
          </span>
        </button>
      ))}
      {!filteredItems.length && (
        <div className="empty">
          Nenhuma ordem de serviço encontrada para os filtros selecionados.
        </div>
      )}
      {selected && (
        <CustomerServiceOrderModal
          order={selected}
          token={token}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

function CustomerServiceOrderModal({ order, token, onClose }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  useEffect(() => {
    fetch(`${apiBase}/portal/service-orders/${order.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        setDetails(await response.json());
      })
      .catch(() =>
        setError("Não foi possível carregar esta ordem de serviço."),
      );
  }, [order.id, token]);
  const completed = ["finished", "closed"].includes(
    details?.status || order.status,
  );
  const issueOptions = [
    ["network", "Problema de rede"],
    ["electrical", "Problema de elétrica"],
    ["software", "Problema de software"],
    ["connectorization", "Problema de conectorização"],
    ["frozen_equipment", "Equipamento travado"],
    ["damaged_equipment", "Equipamento danificado"],
    ["burned_equipment", "Equipamento queimado"],
    ["configuration_failure", "Falha de configuração"],
  ];
  const download = async () => {
    setGenerating(true);
    try {
      await exportServiceOrderPdf(
        {
          code: order.code,
          title: order.title,
          client: details.clientName,
          unit: details.unitName,
          description: details.description,
          owner: details.signedBy || "Equipe técnica",
          signature: signatureLabel(details.signaturePolicy),
        },
        details,
        issueOptions,
      );
    } catch {
      setError("Não foi possível gerar o PDF.");
    } finally {
      setGenerating(false);
    }
  };
  return (
    <div className="modal-backdrop">
      <section className="modal operation-modal service-attendance-modal">
        <div className="modal-head">
          <div>
            <span className="record-code">{order.code}</span>
            <h2>{order.title}</h2>
            <p>
              {details?.clientName || ""} • {details?.unitName || ""}
            </p>
          </div>
          <button onClick={onClose}>×</button>
        </div>
        {details && (
          <>
            <div className="operation-info">
              <div>
                <span>Status</span>
                <b>{orderStatusLabel(details.status)}</b>
              </div>
              <div>
                <span>Atendimento</span>
                <b>{formatSchedule(details.scheduledAt)}</b>
              </div>
              <div>
                <span>Responsável</span>
                <b>{details.signedBy || "A definir"}</b>
              </div>
            </div>
            {details.description && (
              <div className="service-order-origin">
                <span>Solicitação recebida</span>
                <p>{details.description}</p>
              </div>
            )}
            {details.serviceReport && (
              <div className="service-order-origin">
                <span>Relatório do atendimento</span>
                <p>{details.serviceReport}</p>
              </div>
            )}
            <div className="service-attendance-times">
              <label>
                Início
                <input
                  value={formatSchedule(details.attendanceStartedAt)}
                  readOnly
                />
              </label>
              <label>
                Fim
                <input
                  value={formatSchedule(details.attendanceEndedAt)}
                  readOnly
                />
              </label>
            </div>
            {details.photos?.length > 0 && (
              <section className="service-evidence">
                <div>
                  <strong>Fotos e evidências</strong>
                </div>
                <div>
                  {details.photos.map((photo) => (
                    <a
                      key={photo.id}
                      href={photo.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src={photo.url} alt={photo.caption || "Evidência"} />
                      <span>{photo.caption || "Evidência"}</span>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
        {error && <div className="login-error">{error}</div>}
        <div className="modal-actions">
          <button onClick={onClose}>Fechar</button>
          {completed && details && (
            <button
              className="pdf-button"
              onClick={download}
              disabled={generating}
            >
              <Icon name="file" size={15} />
              {generating ? "Gerando PDF…" : "Exportar PDF"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function PortalTickets({ clientName, unit, units, items, token, onRefresh }) {
  const [form, setForm] = useState({
    unitId: "",
    title: "",
    description: "",
    priority: "medium",
    photos: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [priority, setPriority] = useState("all");
  const [period, setPeriod] = useState("all");
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketDetailLoading, setTicketDetailLoading] = useState(false);
  const [editingTicket, setEditingTicket] = useState(false);
  const [savingTicketUpdate, setSavingTicketUpdate] = useState(false);
  const [ticketUpdateForm, setTicketUpdateForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    updateNote: "",
  });
  const openItems = items.filter((item) => !item.serviceOrderId);
  const scheduledItems = items.filter((item) => item.serviceOrderId);
  const today = portalTodayKey();
  const weekStart = new Date(`${today}T12:00:00Z`);
  weekStart.setUTCDate(weekStart.getUTCDate() - 6);
  const weekStartKey = weekStart.toISOString().slice(0, 10);
  const monthStartKey = `${today.slice(0, 7)}-01`;
  const filteredItems = openItems.filter((item) => {
    if (
      query &&
      !`${item.id} ${item.title} ${item.description || ""}`
        .toLowerCase()
        .includes(query.toLowerCase())
    )
      return false;
    if (priority !== "all" && item.priority !== priority) return false;
    const created = portalTicketDayKey(item.createdAt);
    if (period === "today" && created !== today) return false;
    if (period === "week" && created < weekStartKey) return false;
    if (period === "month" && created < monthStartKey) return false;
    return true;
  });
  const criticalCount = items.filter((item) =>
    ["high", "critical"].includes(item.priority),
  ).length;
  const priorityCounts = ["low", "medium", "high", "critical"].map((value) => ({
    value,
    count: items.filter((item) => item.priority === value).length,
  }));
  const priorityTotal = Math.max(1, items.length);
  let sweep = 0;
  const colors = {
    low: "#63b494",
    medium: "#4d7fe5",
    high: "#efa348",
    critical: "#dc5c68",
  };
  const gradient = priorityCounts
    .map((item) => {
      const start = sweep;
      sweep += (item.count / priorityTotal) * 360;
      return `${colors[item.value]} ${start}deg ${sweep}deg`;
    })
    .join(",");
  const lastDays = [];
  for (let offset = 6; offset >= 0; offset--) {
    const date = new Date(`${today}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() - offset);
    const key = date.toISOString().slice(0, 10);
    lastDays.push({
      key,
      label: date
        .toLocaleDateString("pt-BR", { weekday: "short", timeZone: "UTC" })
        .replace(".", ""),
      count: items.filter((item) => portalTicketDayKey(item.createdAt) === key).length,
    });
  }
  const dailyMax = Math.max(1, ...lastDays.map((item) => item.count));
  const submit = async (event) => {
    event.preventDefault();
    const unitId = unit?.id === "all" ? Number(form.unitId) : unit?.id;
    if (!units.some((item) => item.id === unitId)) {
      setError("Selecione a unidade do chamado.");
      return;
    }
    setSaving(true);
    setError("");
    const response = await fetch(`${apiBase}/tickets`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...form, unitId }),
    });
    setSaving(false);
    if (response.ok) {
      setForm({ unitId: "", title: "", description: "", priority: "medium", photos: [] });
      onRefresh();
    } else
      setError(
        "Não foi possível abrir o chamado. Confira os campos e as fotos.",
      );
  };
  const openTicketDetail = async (item) => {
    setTicketDetailLoading(true);
    setError("");
    const response = await fetch(`${apiBase}/portal/tickets/${item.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const detail = await response.json();
      setTicketDetail(detail);
      setTicketUpdateForm({
        title: detail.ticket.title || "",
        description: detail.ticket.description || "",
        priority: detail.ticket.priority || "medium",
        updateNote: "",
      });
      setEditingTicket(false);
    } else setError("Não foi possível abrir os detalhes do chamado.");
    setTicketDetailLoading(false);
  };
  const saveTicketUpdate = async (event) => {
    event.preventDefault();
    if (!ticketDetail?.ticket?.id) return;
    setSavingTicketUpdate(true);
    setError("");
    const response = await fetch(
      `${apiBase}/portal/tickets/${ticketDetail.ticket.id}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ticketUpdateForm),
      },
    );
    if (response.ok) {
      const detail = await response.json();
      setTicketDetail(detail);
      setTicketUpdateForm((current) => ({ ...current, updateNote: "" }));
      setEditingTicket(false);
      await onRefresh();
    } else {
      const result = await response.json().catch(() => ({}));
      setError(
        result.error === "ticket_locked_by_service_order"
          ? "Este chamado já foi convertido em OS e não pode mais ser alterado."
          : "Não foi possível atualizar o chamado.",
      );
    }
    setSavingTicketUpdate(false);
  };
  return (
    <section className="portal-ticket-workspace">
      <div className="portal-ticket-hero">
        <div>
          <span>CENTRAL DE SOLICITAÇÕES</span>
          <h2>Chamados da unidade</h2>
          <p>
            Abra solicitações, acompanhe as pendências e consulte os indicadores
            históricos da unidade.
          </p>
        </div>
        <div>
          <Icon name="building" size={17} />
          <span>
            Unidade selecionada<strong>{unit?.name}</strong>
          </span>
        </div>
      </div>
      <div className="portal-ticket-kpis">
        <article>
          <div className="metric-icon blue">
            <Icon name="headset" size={18} />
          </div>
          <span>
            Total de chamados<b>{items.length}</b>
            <small>Histórico da unidade</small>
          </span>
        </article>
        <article>
          <div className="metric-icon amber">
            <Icon name="clock" size={18} />
          </div>
          <span>
            Chamados pendentes<b>{openItems.length}</b>
            <small>Aguardando abertura de OS</small>
          </span>
        </article>
        <article>
          <div className="metric-icon green">
            <Icon name="check" size={18} />
          </div>
          <span>
            Encaminhados para OS<b>{scheduledItems.length}</b>
            <small>Com ordem de serviço vinculada</small>
          </span>
        </article>
        <article>
          <div className="metric-icon red">
            <Icon name="trend" size={18} />
          </div>
          <span>
            Alta prioridade<b>{criticalCount}</b>
            <small>Alta ou crítica no histórico</small>
          </span>
        </article>
      </div>
      <div className="portal-ticket-layout">
        <form className="portal-ticket-create panel" onSubmit={submit}>
          <div className="portal-ticket-section-head">
            <div>
              <span>NOVA SOLICITAÇÃO</span>
              <h3>Como podemos ajudar?</h3>
              <p>
                Quanto mais detalhes, mais rápido conseguimos direcionar o
                atendimento.
              </p>
            </div>
            <div className="metric-icon blue">
              <Icon name="headset" size={18} />
            </div>
          </div>
          <div className="portal-ticket-context">
            <span>
              <Icon name="users" size={13} />
              Cliente <b>{clientName}</b>
            </span>
            <span>
              <Icon name="building" size={13} />
              Unidade <b>{unit?.id === "all" ? "Escolha abaixo" : unit?.name}</b>
            </span>
          </div>
          {unit?.id === "all" && <label>
            Unidade do chamado
            <select value={form.unitId} onChange={(event) => setForm({ ...form, unitId: event.target.value })} required>
              <option value="">Selecione a unidade</option>
              {units.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>}
          <label>
            Título da solicitação
            <input
              value={form.title}
              onChange={(event) =>
                setForm({ ...form, title: event.target.value })
              }
              placeholder="Ex.: Câmera da entrada sem imagem"
              required
            />
          </label>
          <label>
            Prioridade
            <select
              value={form.priority}
              onChange={(event) =>
                setForm({ ...form, priority: event.target.value })
              }
            >
              <option value="low">Baixa — pode aguardar</option>
              <option value="medium">Média — impacto moderado</option>
              <option value="high">Alta — operação afetada</option>
              <option value="critical">Crítica — operação interrompida</option>
            </select>
          </label>
          <label>
            Descrição detalhada
            <textarea
              value={form.description}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              placeholder="Informe o local, quando começou, equipamentos envolvidos e o impacto na operação."
              required
            />
          </label>
          <TicketPhotoPicker
            photos={form.photos}
            onChange={(photos) =>
              setForm((current) => ({ ...current, photos }))
            }
          />
          {error && <div className="login-error">{error}</div>}
          <button className="primary portal-ticket-submit" disabled={saving}>
            <Icon name="arrow" size={15} />
            {saving ? "Enviando solicitação…" : "Enviar chamado"}
          </button>
          <small className="portal-ticket-security">
            <Icon name="check" size={13} />
            Sua solicitação ficará vinculada exclusivamente a esta unidade.
          </small>
        </form>
        <aside className="portal-ticket-insights">
          <article className="panel portal-priority-chart">
            <div className="portal-insight-head">
              <div>
                <strong>Perfil das solicitações</strong>
                <span>Distribuição por prioridade</span>
              </div>
              <small>{items.length} no histórico</small>
            </div>
            <div className="portal-priority-body">
              <div
                className="portal-donut"
                style={{
                  background: items.length
                    ? `conic-gradient(${gradient})`
                    : "#e9edf3",
                }}
              >
                <span>
                  <b>{items.length}</b>total
                </span>
              </div>
              <div>
                {priorityCounts.map((item) => (
                  <span key={item.value}>
                    <i style={{ background: colors[item.value] }}></i>
                    {priorityLabel(item.value)}
                    <b>{item.count}</b>
                  </span>
                ))}
              </div>
            </div>
          </article>
          <article className="panel portal-week-chart">
            <div className="portal-insight-head">
              <div>
                <strong>Últimos sete dias</strong>
                <span>Todos os novos chamados por dia</span>
              </div>
              <Icon name="trend" size={16} />
            </div>
            <div>
              <div className="portal-week-y-axis" aria-hidden="true">
                <span>{dailyMax}</span>
                <span>{Math.ceil(dailyMax / 2)}</span>
                <span>0</span>
              </div>
              {lastDays.map((item) => (
                <span key={item.key}>
                  <i
                    style={{
                      height: `${Math.max(item.count ? 10 : 2, (item.count / dailyMax) * 100)}%`,
                    }}
                    title={`${item.count} chamado(s)`}
                  ></i>
                  <small>{item.label}</small>
                  <b>{item.count || ""}</b>
                </span>
              ))}
            </div>
          </article>
          <div className="portal-ticket-help">
            <Icon name="check" size={17} />
            <div>
              <strong>Fluxo transparente</strong>
              <p>
                Os indicadores preservam todo o histórico. Abaixo ficam
                separados apenas os chamados que ainda aguardam uma OS.
              </p>
            </div>
          </div>
        </aside>
      </div>
      <section className="panel portal-ticket-list">
        <div className="portal-ticket-list-head">
          <div>
            <span>ACOMPANHAMENTO</span>
            <h3>Chamados aguardando OS</h3>
            <p>
              {filteredItems.length} de {openItems.length} solicitações exibidas
            </p>
          </div>
          <div>
            <label>
              <Icon name="search" size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar chamado"
              />
            </label>
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            >
              <option value="all">Todas as prioridades</option>
              <option value="critical">Crítica</option>
              <option value="high">Alta</option>
              <option value="medium">Média</option>
              <option value="low">Baixa</option>
            </select>
            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="all">Todo o período</option>
              <option value="today">Hoje</option>
              <option value="week">Últimos 7 dias</option>
              <option value="month">Mês atual</option>
            </select>
          </div>
        </div>
        <div className="portal-ticket-table-head">
          <span>Chamado</span>
          <span>Prioridade</span>
          <span>Abertura</span>
          <span>Evidências</span>
          <span>Situação</span>
        </div>
        {filteredItems.map((item) => (
          <article
            className="portal-ticket-row"
            key={item.id}
            role="button"
            tabIndex="0"
            onClick={() => openTicketDetail(item)}
            onKeyDown={(event) =>
              (event.key === "Enter" || event.key === " ") &&
              openTicketDetail(item)
            }
          >
            <div>
              <i className={`priority-dot ${item.priority}`}></i>
              <span>
                <strong>
                  CH-{String(item.id).padStart(4, "0")} • {item.title}
                </strong>
                <small>
                  {item.description || "Sem descrição complementar"}
                </small>
              </span>
            </div>
            <em className={`portal-priority ${item.priority}`}>
              {priorityLabel(item.priority)}
            </em>
            <time>{formatSchedule(item.createdAt)}</time>
            <span>{item.photos?.length || 0} foto(s)</span>
            <b>Aguardando OS</b>
          </article>
        ))}
        {!filteredItems.length && (
          <div className="empty">
            Nenhum chamado encontrado para os filtros selecionados.
          </div>
        )}
      </section>
      {(ticketDetailLoading || ticketDetail) && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !savingTicketUpdate &&
            setTicketDetail(null)
          }
        >
          <section className="modal portal-ticket-detail-modal">
            {ticketDetailLoading && !ticketDetail ? (
              <div className="portal-ticket-detail-loading">
                Carregando chamado…
              </div>
            ) : (
              <>
                <div className="modal-head portal-ticket-detail-head">
                  <div>
                    <span>{ticketDetail.ticket.code}</span>
                    <h2>{ticketDetail.ticket.title}</h2>
                    <p>
                      {ticketDetail.ticket.clientName} •{" "}
                      {ticketDetail.ticket.unitName}
                    </p>
                  </div>
                  <button type="button" onClick={() => setTicketDetail(null)}>
                    ×
                  </button>
                </div>
                <div className="portal-ticket-detail-body">
                  <div className="portal-ticket-detail-main">
                    <div className="portal-ticket-detail-summary">
                      <div>
                        <small>Situação</small>
                        <strong>Aguardando OS</strong>
                      </div>
                      <div>
                        <small>Prioridade</small>
                        <em
                          className={`portal-priority ${ticketDetail.ticket.priority}`}
                        >
                          {priorityLabel(ticketDetail.ticket.priority)}
                        </em>
                      </div>
                      <div>
                        <small>Aberto em</small>
                        <strong>
                          {formatSchedule(ticketDetail.ticket.createdAt)}
                        </strong>
                      </div>
                      <div>
                        <small>Solicitante</small>
                        <strong>
                          {ticketDetail.ticket.openedByName || "Cliente"}
                        </strong>
                      </div>
                    </div>
                    <div className="portal-ticket-detail-description">
                      <small>Descrição da solicitação</small>
                      <p>{ticketDetail.ticket.description}</p>
                    </div>
                    {!!ticketDetail.photos?.length && (
                      <div className="portal-ticket-detail-photos">
                        <small>Evidências anexadas</small>
                        <div>
                          {ticketDetail.photos.map((photo) => (
                            <a
                              key={photo.id}
                              href={photo.dataUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={photo.dataUrl}
                                alt={photo.fileName || "Evidência do chamado"}
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="portal-ticket-audit">
                      <div>
                        <small>Histórico de atualizações</small>
                        <span>
                          {ticketDetail.history?.length || 0} registro(s)
                        </span>
                      </div>
                      {ticketDetail.history?.map((event) => (
                        <article key={event.id}>
                          <i />
                          <div>
                            <strong>{event.summary}</strong>
                            <small>
                              {event.actorName || "Sistema"} •{" "}
                              {new Date(event.createdAt).toLocaleString(
                                "pt-BR",
                              )}
                            </small>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                  <aside className="portal-ticket-update-panel">
                    <div>
                      <span>ATUALIZAÇÃO</span>
                      <h3>Revisar chamado</h3>
                      <p>
                        Altere as informações enquanto a OS ainda não foi
                        criada.
                      </p>
                    </div>
                    {!editingTicket ? (
                      <button
                        className="primary"
                        onClick={() => setEditingTicket(true)}
                      >
                        <Icon name="edit" size={16} /> Atualizar chamado
                      </button>
                    ) : (
                      <form onSubmit={saveTicketUpdate}>
                        <label>
                          Título
                          <input
                            required
                            value={ticketUpdateForm.title}
                            onChange={(event) =>
                              setTicketUpdateForm({
                                ...ticketUpdateForm,
                                title: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          Prioridade
                          <select
                            value={ticketUpdateForm.priority}
                            onChange={(event) =>
                              setTicketUpdateForm({
                                ...ticketUpdateForm,
                                priority: event.target.value,
                              })
                            }
                          >
                            <option value="low">Baixa</option>
                            <option value="medium">Média</option>
                            <option value="high">Alta</option>
                            <option value="critical">Crítica</option>
                          </select>
                        </label>
                        <label>
                          Descrição
                          <textarea
                            required
                            value={ticketUpdateForm.description}
                            onChange={(event) =>
                              setTicketUpdateForm({
                                ...ticketUpdateForm,
                                description: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label>
                          Motivo ou complemento da atualização
                          <textarea
                            maxLength="1000"
                            value={ticketUpdateForm.updateNote}
                            onChange={(event) =>
                              setTicketUpdateForm({
                                ...ticketUpdateForm,
                                updateNote: event.target.value,
                              })
                            }
                            placeholder="Ex.: problema passou a afetar também a portaria lateral."
                          />
                        </label>
                        {error && <div className="login-error">{error}</div>}
                        <div>
                          <button
                            type="button"
                            onClick={() => setEditingTicket(false)}
                          >
                            Cancelar
                          </button>
                          <button
                            className="primary"
                            disabled={savingTicketUpdate}
                          >
                            {savingTicketUpdate
                              ? "Salvando…"
                              : "Salvar atualização"}
                          </button>
                        </div>
                      </form>
                    )}
                  </aside>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </section>
  );
}

function CustomerScheduleModal({ data, onClose }) {
  const dates = data.tasks
    .flatMap((item) => [item.startDate, item.endDate])
    .filter(Boolean)
    .sort();
  const start = dates.length ? new Date(`${dates[0]}T12:00:00Z`) : new Date();
  const end = dates.length
    ? new Date(`${dates.at(-1)}T12:00:00Z`)
    : new Date(start.getTime() + 30 * 86400000);
  const firstDay = start.toISOString().slice(0, 10);
  const lastDay = end.toISOString().slice(0, 10);
  const span = Math.max(1, dateDays(firstDay, lastDay) + 1);
  const calendarDays = timelineDays(start, end);
  const position = (task) => {
    if (!task.startDate || !task.endDate) return { left: "0%", width: "2%" };
    const left = (Math.max(0, dateDays(firstDay, task.startDate)) / span) * 100;
    const width = Math.max(
      2,
      ((dateDays(task.startDate, task.endDate) + 1) / span) * 100,
    );
    return { left: `${left}%`, width: `${Math.min(width, 100 - left)}%` };
  };
  const overduePosition = (task) =>
    overdueTaskPosition(task, firstDay, lastDay);
  const average = data.tasks.length
    ? Math.round(
        data.tasks.reduce((sum, item) => sum + Number(item.progress || 0), 0) /
          data.tasks.length,
      )
    : 0;
  return (
    <div className="modal-backdrop">
      <section className="customer-schedule">
        <div className="print-document-brand">
          <ReportBrandLogos />
          <strong>CRONOGRAMA VISUAL</strong>
        </div>
        <div className="modal-head">
          <div>
            <span className="record-code">
              CRONOGRAMA VISUAL • SOMENTE LEITURA
            </span>
            <h2>{data.project.name}</h2>
            <p>{data.project.unitName} • Atualização informativa da execução</p>
          </div>
          <div className="modal-head-actions">
            <button
              className="pdf-button"
              onClick={() => downloadPagedSchedulePdf({
                project: data.project, phases: data.phases, tasks: data.tasks,
                days: calendarDays, title: "Cronograma Visual",
              }).catch(() => window.alert("Não foi possível gerar o PDF. Tente novamente."))}
            >
              <Icon name="file" size={14} /> Baixar PDF
            </button>
            <button onClick={onClose}>×</button>
          </div>
        </div>
        <div className="customer-schedule-summary">
          <span>
            Fases <b>{data.phases.length}</b>
          </span>
          <span>
            Atividades <b>{data.tasks.length}</b>
          </span>
          <span>
            Avanço médio <b>{average}%</b>
          </span>
          <span>
            Início do projeto{" "}
            <b>{formatDate(data.project.startDate || firstDay)}</b>
          </span>
          <span>
            Conclusão planejada{" "}
            <b>{formatDate(data.project.endDate || lastDay)}</b>
          </span>
        </div>
        {data.tasks.some(isTaskOverdue) && (
          <div className="schedule-delay-legend customer-delay">
            <i></i>
            <span>Vermelho indica dias planejados já vencidos.</span>
          </div>
        )}
        <PrintSchedulePages
          project={data.project}
          title="Cronograma Visual"
          phases={data.phases}
          tasks={data.tasks}
          days={calendarDays}
        />
        <div className="customer-gantt">
          <div
            className="gantt-scroll-content customer-scroll-content"
            style={{
              minWidth: `${Math.max(850, 500 + calendarDays.length * 46)}px`,
              "--timeline-width": `${calendarDays.length * 46}px`,
            }}
          >
            <div className="customer-gantt-head">
              <span>Fase / atividade</span>
              <span>Responsável</span>
              <CalendarAxis days={calendarDays} />
              <span>Conclusão</span>
            </div>
            {data.phases.map((phase) => (
              <div key={phase.id}>
                <div className="customer-phase">
                  <i style={{ background: phase.color }}></i>
                  <strong>{phase.name}</strong>
                </div>
                {data.tasks
                  .filter((task) => task.phaseId === phase.id)
                  .map((task) => (
                    <div className="customer-task" key={task.id}>
                      <div>
                        <strong>{task.name}</strong>
                        <small>
                          {formatDate(task.startDate)} →{" "}
                          {formatDate(task.endDate)}
                        </small>
                      </div>
                      <span>{task.assignedTo || "Equipe não informada"}</span>
                      <div className="gantt-track">
                        <TimelineBackground days={calendarDays} />
                        <i
                          style={{
                            ...position(task),
                            background: phase.color,
                            color: phase.color,
                          }}
                        ></i>
                        {overduePosition(task) && (
                          <i
                            className="overdue-bar"
                            style={overduePosition(task)}
                          ></i>
                        )}
                      </div>
                      <b>{task.progress}%</b>
                    </div>
                  ))}
              </div>
            ))}
            {!data.tasks.length && (
              <div className="empty">
                O cronograma ainda não possui atividades publicadas.
              </div>
            )}
          </div>
        </div>
        <div className="customer-readonly-note">
          <Icon name="check" size={16} />
          <span>
            Este cronograma é somente para consulta. Alterações são realizadas
            pela equipe responsável pelo projeto.
          </span>
        </div>
      </section>
    </div>
  );
}

function initials(name) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}
function roleLabel(role) {
  return (
    {
      administrator: "Administrador",
      manager: "Gestor",
      analyst: "Analista",
      supervisor: "Supervisor",
      technician: "Técnico",
      customer: "Cliente",
    }[role] || role
  );
}
function projectStatusLabel(status) {
  return (
    {
      planning: "Planejamento",
      execution: "Execução",
      paused: "Pausado",
      completed: "Concluído",
      closed: "Encerrado",
    }[status] || status
  );
}
function orderStatusLabel(status) {
  return (
    {
      new: "Nova",
      analysis: "Em análise",
      scheduled: "Programada",
      in_progress: "Em execução",
      waiting_customer: "Aguardando cliente",
      waiting_material: "Aguardando material",
      finished: "Finalizada",
      closed: "Encerrada",
    }[status] || status
  );
}
function ticketStatusLabel(status) {
  return (
    {
      open: "Aguardando OS",
      triage: "Aguardando OS",
      in_progress: "OS aberta",
      service_order_opened: "OS aberta",
      resolved: "OS aberta",
      closed: "OS aberta",
    }[status] || status
  );
}
function priorityLabel(priority) {
  return (
    { low: "Baixa", medium: "Média", high: "Alta", critical: "Crítica" }[
      priority
    ] || priority
  );
}
function signatureLabel(policy) {
  return (
    {
      informative: "Informativo",
      acknowledgement: "Solicitar ciência",
      acceptance: "Aceite obrigatório",
    }[policy] || policy
  );
}
function sourceLabel(source) {
  return (
    {
      internal: "Interno",
      portal: "Portal do cliente",
      email: "E-mail",
      api: "API",
    }[source] || source
  );
}
function MoneyInput({
  value,
  onChange,
  required = false,
  disabled = false,
  placeholder = "0,00",
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState("");
  const display =
    value === ""
      ? ""
      : new Intl.NumberFormat("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value) || 0);
  useEffect(() => {
    if (!focused) setText(display);
  }, [value, focused, display]);
  const parse = (raw) => {
    let normalized = String(raw)
      .replace(/R\$|\s/g, "")
      .replace(/[^0-9,.-]/g, "");
    if (normalized.includes(","))
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    else if (/^\d{1,3}(\.\d{3})+$/.test(normalized))
      normalized = normalized.replace(/\./g, "");
    const number = Number(normalized);
    return Number.isFinite(number) && number >= 0 ? number : "";
  };
  return (
    <div className="money-input">
      <span>R$</span>
      <input
        required={required}
        disabled={disabled}
        inputMode="decimal"
        value={text}
        placeholder={placeholder}
        onFocus={(event) => {
          setFocused(true);
          setText(value === "" ? "" : String(value).replace(".", ","));
          setTimeout(() => event.target.select(), 0);
        }}
        onChange={(event) => {
          setText(event.target.value);
          onChange(parse(event.target.value));
        }}
        onBlur={() => {
          setFocused(false);
          setText(
            value === ""
              ? ""
              : new Intl.NumberFormat("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(Number(value) || 0),
          );
        }}
      />
    </div>
  );
}
function formatCurrency(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value) || 0);
}
function shortChartDate(value) {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? ""
    : date
        .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
        .replace(".", "");
}
function formatDocument(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    : value || "";
}

export default App;
